import { z } from 'zod';

// src/branding.ts
var APP_DISPLAY_NAME = "Mitarbeiterportal";
var HELPDESK_DISPLAY_NAME = "Helpdesk";
var CONNECTOR_DISPLAY_NAME = "Mitarbeiterportal Connector";
var TICKET_EMAIL_BRAND = HELPDESK_DISPLAY_NAME;
function ticketMailSubjectTag(ticketNumber) {
  return `[${TICKET_EMAIL_BRAND} #${ticketNumber}]`;
}
var RoleCode = {
  END_USER: "END_USER",
  IT_AGENT: "IT_AGENT",
  IT_LEAD: "IT_LEAD",
  IT_ADMIN: "IT_ADMIN",
  /** Intranet editorial staff: news, library, categories, contacts, links. */
  INTRANET_EDITOR: "INTRANET_EDITOR",
  /** HR: all leave.* permissions. Not IT_ADMIN. */
  LEAVE_ADMIN: "LEAVE_ADMIN",
  /** Fleet desk: fleet.* permissions except E-Management. Not IT_ADMIN. */
  FLEET_ADMIN: "FLEET_ADMIN",
  /** Own leave requests, balance and team calendar. */
  LEAVE_USER: "LEAVE_USER",
  /** Own vehicles, pool booking, mileage, damage and own compliance. */
  FLEET_USER: "FLEET_USER",
  /** Wartungsprotokolle: all maintenance.* permissions. Not IT_ADMIN. */
  MAINTENANCE_ADMIN: "MAINTENANCE_ADMIN",
  /** Assigned work orders and own protocol editing. */
  MAINTENANCE_TECHNICIAN: "MAINTENANCE_TECHNICIAN",
  /** Eintritt und Austritt: all onboarding.* permissions. Not IT_ADMIN. */
  ONBOARDING_ADMIN: "ONBOARDING_ADMIN",
  /** Own onboarding tasks and subject task list. */
  ONBOARDING_USER: "ONBOARDING_USER",
  /**
   * Installation operator. Catalog only until T1 seeds it on the home tenant.
   * `assignToNewUsers` stays false: it is not in the startup provision set.
   */
  PLATFORM_OPERATOR: "PLATFORM_OPERATOR"
};
var roleCodeSchema = z.enum(RoleCode);
var DIRECTORY_ROLE_CODES = [
  RoleCode.END_USER,
  RoleCode.IT_AGENT,
  RoleCode.IT_LEAD,
  RoleCode.IT_ADMIN
];
var DIRECTORY_ROLE_CODE_SET = new Set(DIRECTORY_ROLE_CODES);
function isDirectoryRoleCode(code) {
  return DIRECTORY_ROLE_CODE_SET.has(code);
}
function roleSeededAtStartup(code) {
  return code !== RoleCode.PLATFORM_OPERATOR;
}
var ROLE_LABELS = {
  END_USER: "Endnutzer",
  IT_AGENT: "IT-Agent",
  IT_LEAD: "IT-Teamleiter",
  IT_ADMIN: "IT-Admin",
  INTRANET_EDITOR: "Intranet-Redaktion",
  LEAVE_ADMIN: "Urlaubs-Admin",
  FLEET_ADMIN: "Fuhrpark-Admin",
  LEAVE_USER: "Urlaub Mitarbeitende",
  FLEET_USER: "Fuhrpark Mitarbeitende",
  MAINTENANCE_ADMIN: "Wartung-Admin",
  MAINTENANCE_TECHNICIAN: "Wartung Monteur",
  ONBOARDING_ADMIN: "Eintritt-Admin",
  ONBOARDING_USER: "Eintritt Mitarbeitende",
  PLATFORM_OPERATOR: "Betreiber"
};
var PermissionNamespace = {
  PLATFORM: "platform",
  HELPDESK: "helpdesk",
  INTRANET: "intranet",
  LEAVE: "leave",
  FLEET: "fleet",
  MAINTENANCE: "maintenance",
  ONBOARDING: "onboarding"
};
var RESERVED_PERMISSION_NAMESPACES = ["tools"];
var PermissionCode = {
  TICKETS_CREATE: "helpdesk.tickets:create",
  TICKETS_READ: "helpdesk.tickets:read",
  /** Agent inbox. Also gates the ticket AI helper (`POST/GET …/ai-assist`); END_USER does not have this. */
  TICKETS_READ_ALL: "helpdesk.tickets:read:all",
  TICKETS_UPDATE: "helpdesk.tickets:update",
  TICKETS_ASSIGN: "helpdesk.tickets:assign",
  TICKETS_COMMENT: "helpdesk.tickets:comment",
  TICKETS_NOTE_INTERNAL: "helpdesk.tickets:note_internal",
  TICKETS_CLOSE: "helpdesk.tickets:close",
  TICKETS_REOPEN: "helpdesk.tickets:reopen",
  /** Create / edit / deactivate waiting queues (Kanban columns). */
  QUEUES_MANAGE: "helpdesk.queues:manage",
  KB_READ: "helpdesk.kb:read",
  KB_WRITE: "helpdesk.kb:write",
  KB_PUBLISH: "helpdesk.kb:publish",
  ASSETS_READ: "helpdesk.assets:read",
  ASSETS_WRITE: "helpdesk.assets:write",
  ASSETS_DELETE: "helpdesk.assets:delete",
  /** Central user base: list/lookups — also what products use for assignee pickers. */
  USERS_READ: "platform.users:read",
  /** Central user admin: edit users, departments, teams, memberships. */
  USERS_MANAGE: "platform.users:manage",
  USERS_ASSIGN_ROLE: "platform.users:assign_role",
  /** Company-wide product switches (`/verwaltung/produkte`). */
  PRODUCTS_MANAGE: "platform.products:manage",
  /** Create and suspend tenants. PLATFORM_OPERATOR only, never IT_ADMIN. */
  TENANTS_MANAGE: "platform.tenants:manage",
  /** Directory sync status and „Jetzt synchronisieren“ (`/verwaltung/verzeichnis`). */
  DIRECTORY_MANAGE: "platform.directory:manage",
  /** Company AI connection (`/verwaltung/ki`). Granted only to IT_ADMIN. */
  AI_MANAGE: "platform.ai:manage",
  /** Company mail connection (`/verwaltung/e-mail`). Granted only to IT_ADMIN. */
  MAIL_MANAGE: "platform.mail:manage",
  /** Address form and person-name shape (`/verwaltung/anrede`). On IT_ADMIN, not on PLATFORM_OPERATOR. */
  VOICE_MANAGE: "platform.voice:manage",
  /** Tenant font and root size (`/verwaltung/darstellung`). On IT_ADMIN, not on PLATFORM_OPERATOR. */
  TYPOGRAPHY_MANAGE: "platform.typography:manage",
  MASTER_DATA_READ: "platform.master_data:read",
  MASTER_DATA_MANAGE: "platform.master_data:manage",
  /** Full IBAN and tax values. Not granted to IT_ADMIN by the role formula. */
  MASTER_DATA_SENSITIVE_READ: "platform.master_data_sensitive:read",
  MASTER_DATA_SENSITIVE_MANAGE: "platform.master_data_sensitive:manage",
  /** CSV preview and commit. Not granted to IT_ADMIN by the role formula. */
  MASTER_DATA_IMPORT: "platform.master_data:import",
  /** CSV export. Not granted to IT_ADMIN by the role formula. */
  MASTER_DATA_EXPORT: "platform.master_data:export",
  ARTICLES_READ: "platform.articles:read",
  ARTICLES_MANAGE: "platform.articles:manage",
  /** Purchase prices. Not granted to IT_ADMIN by the role formula. */
  ARTICLE_PRICES_READ: "platform.article_prices:read",
  ARTICLE_PRICES_MANAGE: "platform.article_prices:manage",
  /** Article files (image, datasheet). Granted to IT_ADMIN; not bank data. */
  ARTICLE_DOCUMENTS_MANAGE: "platform.article_documents:manage",
  SLA_READ: "helpdesk.sla:read",
  SLA_MANAGE: "helpdesk.sla:manage",
  APPROVALS_APPROVE: "helpdesk.approvals:approve",
  APPROVALS_MANAGE: "helpdesk.approvals:manage",
  AUDIT_READ: "platform.audit:read",
  AUTOMATION_READ: "helpdesk.automation:read",
  AUTOMATION_MANAGE: "helpdesk.automation:manage",
  INTEGRATIONS_MANAGE: "helpdesk.integrations:manage",
  REPORTS_READ: "helpdesk.reports:read",
  /** Helpdesk admin settings: ticket catalog, system modules, mail workspace. */
  SETTINGS_MANAGE: "helpdesk.settings:manage",
  CSAT_SUBMIT: "helpdesk.csat:submit",
  CSAT_READ: "helpdesk.csat:read",
  /** End user: request a remote-support session on own ticket, pair a connector. */
  REMOTE_SUPPORT_REQUEST: "helpdesk.remote_support:request",
  /** Agent: request a connect-launch nonce for an active session on a ticket assigned to them. */
  REMOTE_SUPPORT_CONNECT: "helpdesk.remote_support:connect",
  /** Admin: list / revoke connector devices, see all sessions. */
  REMOTE_SUPPORT_MANAGE: "helpdesk.remote_support:manage",
  /** Intranet: read published content; also the product gate on `/start`. */
  INTRANET_CONTENT_READ: "intranet.content:read",
  /** Intranet editors: drafts, edit, publish. */
  INTRANET_CONTENT_WRITE: "intranet.content:write",
  /** Intranet admin: delete, force audience, reports. */
  INTRANET_CONTENT_MANAGE: "intranet.content:manage",
  INTRANET_FEEDBACK_CREATE: "intranet.feedback:create",
  INTRANET_ACKNOWLEDGEMENTS_WRITE: "intranet.acknowledgements:write",
  INTRANET_ACKNOWLEDGEMENTS_READ: "intranet.acknowledgements:read",
  INTRANET_CATEGORIES_MANAGE: "intranet.categories:manage",
  INTRANET_CONTACTS_MANAGE: "intranet.contacts:manage",
  INTRANET_LINKS_MANAGE: "intranet.links:manage",
  /** Intranet KI chat. Assigned in ROLE_PERMISSIONS only to IT_ADMIN; other roles get it from Berechtigungsgruppen. */
  INTRANET_AI_USE: "intranet.ai:use",
  /** Own leave requests: create, submit, cancel. */
  LEAVE_REQUESTS_CREATE: "leave.requests:create",
  /** Own vacation balance. */
  LEAVE_BALANCE_READ: "leave.balance:read",
  /** Department absence calendar. */
  LEAVE_CALENDAR_READ: "leave.calendar:read",
  /** Decide any request, including a negative balance. HR only — not IT_ADMIN. */
  LEAVE_REQUESTS_DECIDE_ALL: "leave.requests:decide_all",
  /** Vacation adjustments and work-week patterns. */
  LEAVE_BALANCES_ADJUST: "leave.balances:adjust",
  /** Book overtime hours. */
  LEAVE_OVERTIME_WRITE: "leave.overtime:write",
  /** Policy, holidays, special-leave catalog, staffing minimums. */
  LEAVE_POLICY_MANAGE: "leave.policy:manage",
  /** CSV and Excel exports. */
  LEAVE_REPORTS_EXPORT: "leave.reports:export",
  /** Own assigned vehicles, own reports, driver-visible documents. */
  FLEET_VEHICLES_READ_OWN: "fleet.vehicles:read_own",
  /** Book a pool vehicle for yourself. */
  FLEET_POOL_BOOK: "fleet.pool:book",
  /** Record mileage for a vehicle in your current assignment. */
  FLEET_MILEAGE_WRITE: "fleet.mileage:write",
  /** Report damage or a defect for a vehicle in your current assignment. */
  FLEET_DAMAGE_CREATE: "fleet.damage:create",
  /** Own license check and instruction status. */
  FLEET_COMPLIANCE_READ_OWN: "fleet.compliance:read_own",
  /** All vehicles, deadlines, release, inspections. */
  FLEET_VEHICLES_MANAGE: "fleet.vehicles:manage",
  /** Assign drivers, book for others, compliance override. */
  FLEET_ASSIGNMENTS_MANAGE: "fleet.assignments:manage",
  /** License checks, instructions, license photo. */
  FLEET_COMPLIANCE_MANAGE: "fleet.compliance:manage",
  /** Workshop visits. */
  FLEET_WORKSHOP_MANAGE: "fleet.workshop:manage",
  /** Documents except the license photo. */
  FLEET_DOCUMENTS_MANAGE: "fleet.documents:manage",
  /** Correct an odometer reading downward. */
  FLEET_ODOMETER_CORRECT: "fleet.odometer:correct",
  /** Fleet policy. */
  FLEET_POLICY_MANAGE: "fleet.policy:manage",
  /** Telematics positions, trips, issues, connections (no token). */
  FLEET_TELEMATICS_READ: "fleet.telematics:read",
  /** Telematics connections, CSV import, vehicle telematics ref. */
  FLEET_TELEMATICS_MANAGE: "fleet.telematics:manage",
  /** Load groups and charging profiles. Not included in the fleet-admin bundle. */
  FLEET_ENERGY_MANAGE: "fleet.energy:manage",
  /** Own trips that overlap a current assignment. */
  FLEET_TRIPS_READ_OWN: "fleet.trips:read_own",
  /** Classify own trips (business / private). */
  FLEET_TRIPS_CLASSIFY_OWN: "fleet.trips:classify_own",
  /** Fuel cards (read-only), transactions, fuel cost share. */
  FLEET_FUEL_READ: "fleet.fuel:read",
  /** Cards, assignments, import, resolve exceptions. */
  FLEET_FUEL_MANAGE: "fleet.fuel:manage",
  /** Own assigned maintenance work orders. */
  MAINTENANCE_ORDERS_READ_OWN: "maintenance.orders:read_own",
  /** Fill and sign the protocol on an assigned work order. */
  MAINTENANCE_PROTOCOLS_WRITE_OWN: "maintenance.protocols:write_own",
  /** Plan, assign, reassign and cancel work orders. */
  MAINTENANCE_ORDERS_MANAGE: "maintenance.orders:manage",
  /** Read all protocols and completed documents. */
  MAINTENANCE_PROTOCOLS_READ_ALL: "maintenance.protocols:read_all",
  /** Checklist templates and versions. */
  MAINTENANCE_TEMPLATES_MANAGE: "maintenance.templates:manage",
  /** Customers, projects and installations. */
  MAINTENANCE_MASTER_DATA_MANAGE: "maintenance.master_data:manage",
  /** Download completed protocol PDFs and exports. */
  MAINTENANCE_PROTOCOLS_EXPORT: "maintenance.protocols:export",
  /** Own onboarding / offboarding tasks and subject visibility. */
  ONBOARDING_TASKS_READ_OWN: "onboarding.tasks:read_own",
  /** Complete, claim and fill own onboarding tasks. */
  ONBOARDING_TASKS_COMPLETE_OWN: "onboarding.tasks:complete_own",
  /** Read all onboarding cases in the tenant (desk). */
  ONBOARDING_CASES_READ: "onboarding.cases:read",
  /** Create, start, cancel, reassign and skip required tasks. */
  ONBOARDING_CASES_MANAGE: "onboarding.cases:manage",
  /** Reopen completed or cancelled cases. */
  ONBOARDING_CASES_REOPEN: "onboarding.cases:reopen",
  /** Manage onboarding templates and versions. */
  ONBOARDING_TEMPLATES_MANAGE: "onboarding.templates:manage"
};
var permissionCodeSchema = z.enum(PermissionCode);
var CANONICAL_CODES = new Set(Object.values(PermissionCode));
function stripNamespace(code) {
  return code.slice(code.indexOf(".") + 1);
}
var RENAMED_PERMISSION_CODES = {
  "users:write": PermissionCode.USERS_MANAGE,
  "platform.users:write": PermissionCode.USERS_MANAGE
};
var LEGACY_PERMISSION_ALIASES = Object.freeze({
  ...Object.fromEntries(Object.values(PermissionCode).map((code) => [stripNamespace(code), code])),
  ...RENAMED_PERMISSION_CODES
});
function canonicalPermissionCode(code) {
  if (CANONICAL_CODES.has(code)) {
    return code;
  }
  return LEGACY_PERMISSION_ALIASES[code] ?? null;
}
function canonicalPermissionCodes(codes) {
  const out = /* @__PURE__ */ new Set();
  for (const code of codes) {
    const canonical = canonicalPermissionCode(code);
    if (canonical) {
      out.add(canonical);
    }
  }
  return [...out];
}
function permissionNamespace(code) {
  return code.slice(0, code.indexOf("."));
}
var PERMISSION_LABELS = {
  [PermissionCode.TICKETS_CREATE]: "Tickets erstellen",
  [PermissionCode.TICKETS_READ]: "Eigene Tickets lesen",
  [PermissionCode.TICKETS_READ_ALL]: "Alle Tickets lesen",
  [PermissionCode.TICKETS_UPDATE]: "Tickets bearbeiten",
  [PermissionCode.TICKETS_ASSIGN]: "Tickets zuweisen",
  [PermissionCode.TICKETS_COMMENT]: "Tickets kommentieren",
  [PermissionCode.TICKETS_NOTE_INTERNAL]: "Interne Notizen schreiben",
  [PermissionCode.TICKETS_CLOSE]: "Tickets schlie\xDFen",
  [PermissionCode.TICKETS_REOPEN]: "Tickets wiederer\xF6ffnen",
  [PermissionCode.QUEUES_MANAGE]: "Warteschlangen verwalten",
  [PermissionCode.KB_READ]: "Wissensdatenbank lesen",
  [PermissionCode.KB_WRITE]: "Wissensdatenbank schreiben",
  [PermissionCode.KB_PUBLISH]: "Artikel ver\xF6ffentlichen",
  [PermissionCode.ASSETS_READ]: "Assets lesen",
  [PermissionCode.ASSETS_WRITE]: "Assets schreiben",
  [PermissionCode.ASSETS_DELETE]: "Assets l\xF6schen",
  [PermissionCode.USERS_READ]: "Benutzer lesen",
  [PermissionCode.USERS_MANAGE]: "Benutzer verwalten",
  [PermissionCode.USERS_ASSIGN_ROLE]: "Rollen zuweisen",
  [PermissionCode.PRODUCTS_MANAGE]: "Produkte an- und abschalten",
  [PermissionCode.TENANTS_MANAGE]: "Mandanten verwalten",
  [PermissionCode.DIRECTORY_MANAGE]: "Verzeichnis-Sync verwalten",
  [PermissionCode.AI_MANAGE]: "KI-Anschluss verwalten",
  [PermissionCode.MAIL_MANAGE]: "E-Mail-Verbindung verwalten",
  [PermissionCode.VOICE_MANAGE]: "Anrede und Namen einstellen",
  [PermissionCode.TYPOGRAPHY_MANAGE]: "Schrift und Schriftgr\xF6\xDFe einstellen",
  [PermissionCode.MASTER_DATA_READ]: "Stammdaten lesen",
  [PermissionCode.MASTER_DATA_MANAGE]: "Stammdaten pflegen",
  [PermissionCode.MASTER_DATA_SENSITIVE_READ]: "Stammdaten Bank und Steuer lesen",
  [PermissionCode.MASTER_DATA_SENSITIVE_MANAGE]: "Stammdaten Bank und Steuer pflegen",
  [PermissionCode.MASTER_DATA_IMPORT]: "Stammdaten importieren",
  [PermissionCode.MASTER_DATA_EXPORT]: "Stammdaten exportieren",
  [PermissionCode.ARTICLES_READ]: "Artikel lesen",
  [PermissionCode.ARTICLES_MANAGE]: "Artikel pflegen",
  [PermissionCode.ARTICLE_PRICES_READ]: "Artikelpreise lesen",
  [PermissionCode.ARTICLE_PRICES_MANAGE]: "Artikelpreise pflegen",
  [PermissionCode.ARTICLE_DOCUMENTS_MANAGE]: "Artikeldateien pflegen",
  [PermissionCode.SLA_READ]: "SLA einsehen",
  [PermissionCode.SLA_MANAGE]: "SLA verwalten",
  [PermissionCode.APPROVALS_APPROVE]: "Freigaben erteilen",
  [PermissionCode.APPROVALS_MANAGE]: "Freigabe-Workflows verwalten",
  [PermissionCode.AUDIT_READ]: "Audit-Log lesen",
  [PermissionCode.AUTOMATION_READ]: "Automatisierung lesen",
  [PermissionCode.AUTOMATION_MANAGE]: "Automatisierung verwalten",
  [PermissionCode.INTEGRATIONS_MANAGE]: "Integrationen verwalten",
  [PermissionCode.REPORTS_READ]: "Berichte lesen",
  [PermissionCode.SETTINGS_MANAGE]: "Systemeinstellungen",
  [PermissionCode.CSAT_SUBMIT]: "Zufriedenheit bewerten",
  [PermissionCode.CSAT_READ]: "CSAT-Ergebnisse lesen",
  [PermissionCode.REMOTE_SUPPORT_REQUEST]: "Remote-Support starten",
  [PermissionCode.REMOTE_SUPPORT_CONNECT]: "Mit RustDesk verbinden",
  [PermissionCode.REMOTE_SUPPORT_MANAGE]: "Remote-Support verwalten",
  [PermissionCode.INTRANET_CONTENT_READ]: "Intranet lesen",
  [PermissionCode.INTRANET_CONTENT_WRITE]: "Intranet-Beitr\xE4ge schreiben",
  [PermissionCode.INTRANET_CONTENT_MANAGE]: "Intranet-Beitr\xE4ge verwalten",
  [PermissionCode.INTRANET_FEEDBACK_CREATE]: "Intranet kommentieren und bewerten",
  [PermissionCode.INTRANET_ACKNOWLEDGEMENTS_WRITE]: "Lesebest\xE4tigung abgeben",
  [PermissionCode.INTRANET_ACKNOWLEDGEMENTS_READ]: "Lesebest\xE4tigungen auswerten",
  [PermissionCode.INTRANET_CATEGORIES_MANAGE]: "Intranet-Channels verwalten",
  [PermissionCode.INTRANET_CONTACTS_MANAGE]: "Ansprechpartner verwalten",
  [PermissionCode.INTRANET_LINKS_MANAGE]: "Intranet-Links verwalten",
  [PermissionCode.INTRANET_AI_USE]: "Intranet-KI benutzen",
  [PermissionCode.LEAVE_REQUESTS_CREATE]: "Urlaub beantragen",
  [PermissionCode.LEAVE_BALANCE_READ]: "Eigenen Urlaubssaldo lesen",
  [PermissionCode.LEAVE_CALENDAR_READ]: "Abwesenheitskalender lesen",
  [PermissionCode.LEAVE_REQUESTS_DECIDE_ALL]: "Alle Urlaubsantr\xE4ge entscheiden",
  [PermissionCode.LEAVE_BALANCES_ADJUST]: "Urlaubssalden korrigieren",
  [PermissionCode.LEAVE_OVERTIME_WRITE]: "\xDCberstunden buchen",
  [PermissionCode.LEAVE_POLICY_MANAGE]: "Urlaubsrichtlinie verwalten",
  [PermissionCode.LEAVE_REPORTS_EXPORT]: "Urlaub exportieren",
  [PermissionCode.FLEET_VEHICLES_READ_OWN]: "Eigene Fahrzeuge lesen",
  [PermissionCode.FLEET_POOL_BOOK]: "Poolfahrzeug buchen",
  [PermissionCode.FLEET_MILEAGE_WRITE]: "Kilometerstand melden",
  [PermissionCode.FLEET_DAMAGE_CREATE]: "Schaden oder Mangel melden",
  [PermissionCode.FLEET_COMPLIANCE_READ_OWN]: "Eigene F\xFChrerscheinkontrolle lesen",
  [PermissionCode.FLEET_VEHICLES_MANAGE]: "Fahrzeuge verwalten",
  [PermissionCode.FLEET_ASSIGNMENTS_MANAGE]: "Fahrer zuordnen",
  [PermissionCode.FLEET_COMPLIANCE_MANAGE]: "F\xFChrerschein und Unterweisung verwalten",
  [PermissionCode.FLEET_WORKSHOP_MANAGE]: "Werkstattbesuche erfassen",
  [PermissionCode.FLEET_DOCUMENTS_MANAGE]: "Fuhrpark-Dokumente verwalten",
  [PermissionCode.FLEET_ODOMETER_CORRECT]: "Kilometerstand korrigieren",
  [PermissionCode.FLEET_POLICY_MANAGE]: "Fuhrpark-Richtlinie verwalten",
  [PermissionCode.FLEET_TELEMATICS_READ]: "Telematik lesen",
  [PermissionCode.FLEET_TELEMATICS_MANAGE]: "Telematik verwalten",
  [PermissionCode.FLEET_ENERGY_MANAGE]: "E-Management",
  [PermissionCode.FLEET_TRIPS_READ_OWN]: "Eigene Fahrten lesen",
  [PermissionCode.FLEET_TRIPS_CLASSIFY_OWN]: "Eigene Fahrten klassifizieren",
  [PermissionCode.FLEET_FUEL_READ]: "Tankkarten und Kraftstoffkosten lesen",
  [PermissionCode.FLEET_FUEL_MANAGE]: "Tankkarten verwalten und importieren",
  [PermissionCode.MAINTENANCE_ORDERS_READ_OWN]: "Eigene Wartungsauftr\xE4ge lesen",
  [PermissionCode.MAINTENANCE_PROTOCOLS_WRITE_OWN]: "Eigenes Wartungsprotokoll bearbeiten",
  [PermissionCode.MAINTENANCE_ORDERS_MANAGE]: "Wartungsauftr\xE4ge planen und zuweisen",
  [PermissionCode.MAINTENANCE_PROTOCOLS_READ_ALL]: "Alle Wartungsprotokolle lesen",
  [PermissionCode.MAINTENANCE_TEMPLATES_MANAGE]: "Wartungsvorlagen verwalten",
  [PermissionCode.MAINTENANCE_MASTER_DATA_MANAGE]: "Kunden, Projekte und Anlagen verwalten",
  [PermissionCode.MAINTENANCE_PROTOCOLS_EXPORT]: "Wartungsprotokolle exportieren",
  [PermissionCode.ONBOARDING_TASKS_READ_OWN]: "Eigene Aufgaben lesen",
  [PermissionCode.ONBOARDING_TASKS_COMPLETE_OWN]: "Eigene Aufgaben bearbeiten",
  [PermissionCode.ONBOARDING_CASES_READ]: "Alle Vorg\xE4nge lesen",
  [PermissionCode.ONBOARDING_CASES_MANAGE]: "Vorg\xE4nge anlegen, starten, abbrechen, Daten und Zuordnung \xE4ndern, Pflichtaufgaben \xFCberspringen",
  [PermissionCode.ONBOARDING_CASES_REOPEN]: "Abgeschlossene oder abgebrochene Vorg\xE4nge wieder \xF6ffnen",
  [PermissionCode.ONBOARDING_TEMPLATES_MANAGE]: "Vorlagen verwalten"
};
var HELPDESK_EMPLOYEE_PERMISSIONS = [
  PermissionCode.TICKETS_CREATE,
  PermissionCode.TICKETS_READ,
  PermissionCode.TICKETS_COMMENT,
  PermissionCode.TICKETS_REOPEN,
  PermissionCode.KB_READ,
  PermissionCode.CSAT_SUBMIT,
  PermissionCode.REMOTE_SUPPORT_REQUEST
];
var INTRANET_READER_PERMISSIONS = [
  PermissionCode.INTRANET_CONTENT_READ,
  PermissionCode.INTRANET_FEEDBACK_CREATE,
  PermissionCode.INTRANET_ACKNOWLEDGEMENTS_WRITE
];
var LEAVE_EMPLOYEE_PERMISSIONS = [
  PermissionCode.LEAVE_REQUESTS_CREATE,
  PermissionCode.LEAVE_BALANCE_READ,
  PermissionCode.LEAVE_CALENDAR_READ
];
var LEAVE_ADMIN_PERMISSIONS = Object.values(PermissionCode).filter(
  (code) => permissionNamespace(code) === PermissionNamespace.LEAVE
);
var FLEET_EMPLOYEE_PERMISSIONS = [
  PermissionCode.FLEET_VEHICLES_READ_OWN,
  PermissionCode.FLEET_POOL_BOOK,
  PermissionCode.FLEET_MILEAGE_WRITE,
  PermissionCode.FLEET_DAMAGE_CREATE,
  PermissionCode.FLEET_COMPLIANCE_READ_OWN,
  PermissionCode.FLEET_TRIPS_READ_OWN,
  PermissionCode.FLEET_TRIPS_CLASSIFY_OWN
];
var FLEET_ADMIN_PERMISSIONS = Object.values(PermissionCode).filter(
  (code) => permissionNamespace(code) === PermissionNamespace.FLEET && code !== PermissionCode.FLEET_ENERGY_MANAGE
);
var MAINTENANCE_TECHNICIAN_PERMISSIONS = [
  PermissionCode.MAINTENANCE_ORDERS_READ_OWN,
  PermissionCode.MAINTENANCE_PROTOCOLS_WRITE_OWN
];
var MAINTENANCE_ADMIN_PERMISSIONS = Object.values(
  PermissionCode
).filter((code) => permissionNamespace(code) === PermissionNamespace.MAINTENANCE);
var ONBOARDING_EMPLOYEE_PERMISSIONS = [
  PermissionCode.ONBOARDING_TASKS_READ_OWN,
  PermissionCode.ONBOARDING_TASKS_COMPLETE_OWN
];
var ONBOARDING_ADMIN_PERMISSIONS = Object.values(
  PermissionCode
).filter((code) => permissionNamespace(code) === PermissionNamespace.ONBOARDING);
var PERMISSION_GROUP = Object.fromEntries(
  Object.values(PermissionCode).map((code) => {
    const resource = stripNamespace(code);
    return [code, resource.slice(0, resource.indexOf(":"))];
  })
);
function employeePermissionsForNamespace(namespace) {
  switch (namespace) {
    case PermissionNamespace.HELPDESK:
      return HELPDESK_EMPLOYEE_PERMISSIONS;
    case PermissionNamespace.INTRANET:
      return INTRANET_READER_PERMISSIONS;
    case PermissionNamespace.LEAVE:
      return LEAVE_EMPLOYEE_PERMISSIONS;
    case PermissionNamespace.FLEET:
      return FLEET_EMPLOYEE_PERMISSIONS;
    case PermissionNamespace.MAINTENANCE:
      return MAINTENANCE_TECHNICIAN_PERMISSIONS;
    case PermissionNamespace.ONBOARDING:
      return ONBOARDING_EMPLOYEE_PERMISSIONS;
    default:
      return [];
  }
}
var IT_ADMIN_EXCLUDED_PERMISSIONS = [
  PermissionCode.TENANTS_MANAGE,
  PermissionCode.MASTER_DATA_SENSITIVE_READ,
  PermissionCode.MASTER_DATA_SENSITIVE_MANAGE,
  PermissionCode.MASTER_DATA_IMPORT,
  PermissionCode.MASTER_DATA_EXPORT,
  PermissionCode.ARTICLE_PRICES_READ,
  PermissionCode.ARTICLE_PRICES_MANAGE
];
var ROLE_PERMISSIONS = {
  END_USER: [...HELPDESK_EMPLOYEE_PERMISSIONS, ...INTRANET_READER_PERMISSIONS],
  IT_AGENT: [
    PermissionCode.TICKETS_CREATE,
    PermissionCode.TICKETS_READ,
    PermissionCode.TICKETS_READ_ALL,
    PermissionCode.TICKETS_UPDATE,
    PermissionCode.TICKETS_ASSIGN,
    PermissionCode.TICKETS_COMMENT,
    PermissionCode.TICKETS_NOTE_INTERNAL,
    PermissionCode.TICKETS_CLOSE,
    PermissionCode.TICKETS_REOPEN,
    PermissionCode.KB_READ,
    PermissionCode.KB_WRITE,
    PermissionCode.ASSETS_READ,
    PermissionCode.ASSETS_WRITE,
    PermissionCode.USERS_READ,
    PermissionCode.SLA_READ,
    PermissionCode.APPROVALS_APPROVE,
    PermissionCode.CSAT_SUBMIT,
    PermissionCode.CSAT_READ,
    PermissionCode.REMOTE_SUPPORT_REQUEST,
    PermissionCode.REMOTE_SUPPORT_CONNECT,
    ...INTRANET_READER_PERMISSIONS
  ],
  IT_LEAD: [
    PermissionCode.TICKETS_CREATE,
    PermissionCode.TICKETS_READ,
    PermissionCode.TICKETS_READ_ALL,
    PermissionCode.TICKETS_UPDATE,
    PermissionCode.TICKETS_ASSIGN,
    PermissionCode.TICKETS_COMMENT,
    PermissionCode.TICKETS_NOTE_INTERNAL,
    PermissionCode.TICKETS_CLOSE,
    PermissionCode.TICKETS_REOPEN,
    PermissionCode.QUEUES_MANAGE,
    PermissionCode.KB_READ,
    PermissionCode.KB_WRITE,
    PermissionCode.KB_PUBLISH,
    PermissionCode.ASSETS_READ,
    PermissionCode.ASSETS_WRITE,
    PermissionCode.USERS_READ,
    PermissionCode.USERS_MANAGE,
    PermissionCode.SLA_READ,
    PermissionCode.SLA_MANAGE,
    PermissionCode.APPROVALS_APPROVE,
    PermissionCode.APPROVALS_MANAGE,
    PermissionCode.AUTOMATION_READ,
    PermissionCode.REPORTS_READ,
    PermissionCode.CSAT_SUBMIT,
    PermissionCode.CSAT_READ,
    PermissionCode.REMOTE_SUPPORT_REQUEST,
    PermissionCode.REMOTE_SUPPORT_CONNECT,
    ...INTRANET_READER_PERMISSIONS
  ],
  /**
   * Every platform, helpdesk and intranet code except tenant administration.
   * Leave, fleet, maintenance and onboarding self-service stay on LEAVE_USER /
   * FLEET_USER / MAINTENANCE_TECHNICIAN / ONBOARDING_USER.
   * HR, fleet desk, maintenance desk and onboarding desk rights stay on
   * LEAVE_ADMIN / FLEET_ADMIN / MAINTENANCE_ADMIN / ONBOARDING_ADMIN.
   * `platform.tenants:manage` stays on PLATFORM_OPERATOR.
   */
  IT_ADMIN: Object.values(PermissionCode).filter((code) => {
    if (IT_ADMIN_EXCLUDED_PERMISSIONS.includes(code)) {
      return false;
    }
    const namespace = permissionNamespace(code);
    return namespace !== PermissionNamespace.LEAVE && namespace !== PermissionNamespace.FLEET && namespace !== PermissionNamespace.MAINTENANCE && namespace !== PermissionNamespace.ONBOARDING;
  }),
  /** Operator of the installation home. Not granted to new users. */
  PLATFORM_OPERATOR: [PermissionCode.TENANTS_MANAGE],
  INTRANET_EDITOR: [
    ...INTRANET_READER_PERMISSIONS,
    PermissionCode.INTRANET_CONTENT_WRITE,
    PermissionCode.INTRANET_ACKNOWLEDGEMENTS_READ,
    PermissionCode.INTRANET_CATEGORIES_MANAGE,
    PermissionCode.INTRANET_CONTACTS_MANAGE,
    PermissionCode.INTRANET_LINKS_MANAGE
  ],
  LEAVE_ADMIN: LEAVE_ADMIN_PERMISSIONS,
  FLEET_ADMIN: FLEET_ADMIN_PERMISSIONS,
  LEAVE_USER: LEAVE_EMPLOYEE_PERMISSIONS,
  FLEET_USER: FLEET_EMPLOYEE_PERMISSIONS,
  MAINTENANCE_ADMIN: MAINTENANCE_ADMIN_PERMISSIONS,
  MAINTENANCE_TECHNICIAN: MAINTENANCE_TECHNICIAN_PERMISSIONS,
  ONBOARDING_ADMIN: ONBOARDING_ADMIN_PERMISSIONS,
  ONBOARDING_USER: ONBOARDING_EMPLOYEE_PERMISSIONS
};
function rolePermissionCeiling(input) {
  const next = new Set(input.permissions);
  const actorIsOperator = input.actorPermissions.includes(PermissionCode.TENANTS_MANAGE);
  if (next.has(PermissionCode.TENANTS_MANAGE) && !actorIsOperator) {
    return "OPERATOR_PERMISSION";
  }
  for (const code of IT_ADMIN_EXCLUDED_PERMISSIONS) {
    if (code === PermissionCode.TENANTS_MANAGE || !next.has(code)) {
      continue;
    }
    if (!actorIsOperator && !input.actorPermissions.includes(code)) {
      return "IT_ADMIN_EXCLUSION";
    }
  }
  if (input.roleCode === RoleCode.END_USER) {
    const allowed = new Set(ROLE_PERMISSIONS.END_USER);
    for (const code of next) {
      if (!allowed.has(code)) {
        return "END_USER_BASELINE";
      }
    }
  }
  if (input.roleCode === RoleCode.IT_ADMIN) {
    for (const code of IT_ADMIN_EXCLUDED_PERMISSIONS) {
      if (next.has(code)) {
        return "IT_ADMIN_EXCLUSION";
      }
    }
  }
  return null;
}
function roleAssignmentCeiling(input) {
  const actorIsOperator = input.actorPermissions.includes(PermissionCode.TENANTS_MANAGE);
  for (const role of input.addedRoles) {
    const permissions = role.permissions ?? [];
    const operator = role.code === RoleCode.PLATFORM_OPERATOR || permissions.includes(PermissionCode.TENANTS_MANAGE);
    if (operator && !actorIsOperator) {
      return "OPERATOR_PERMISSION";
    }
    if (!actorIsOperator) {
      for (const code of permissions) {
        if (!input.actorPermissions.includes(code)) {
          return "ROLE_ESCALATION";
        }
      }
    }
  }
  return null;
}

// src/version.ts
var APP_VERSION = "1.0.0-beta.1";

// src/products.ts
var ProductCode = {
  HELPDESK: "helpdesk",
  LEAVE: "leave",
  FLEET: "fleet",
  MAINTENANCE: "maintenance",
  ONBOARDING: "onboarding",
  TOOLS: "tools",
  INTRANET: "intranet"
};
var productCodeSchema = z.enum(ProductCode);
var PRODUCT_CODES = Object.values(ProductCode);
var ProductStatus = {
  LIVE: "live",
  PLANNED: "planned"
};
var productStatusSchema = z.enum(ProductStatus);
var productAdminLinkSchema = z.object({
  id: z.string().min(1),
  to: z.string().min(1),
  label: z.string().min(1),
  description: z.string().min(1),
  permission: permissionCodeSchema
});
var PRODUCT_REQUIRES_PLATFORM = ">=1.0.0-beta.1";
var PRODUCT_META = {
  helpdesk: {
    code: ProductCode.HELPDESK,
    labelDe: "Helpdesk",
    descriptionDe: "IT-Tickets, Wissen und Self-Service.",
    pathPrefix: "/helpdesk",
    selfServicePath: "/portal",
    staffPermission: PermissionCode.TICKETS_READ_ALL,
    order: 10,
    status: ProductStatus.LIVE,
    version: "1.8.0",
    requiresPlatform: PRODUCT_REQUIRES_PLATFORM,
    requires: [],
    admin: [
      {
        id: "helpdesk-funktionsumfang",
        to: "/helpdesk/einstellungen/system",
        label: "Funktionsumfang",
        description: "Bereiche des Helpdesks ein- und ausschalten.",
        permission: PermissionCode.SETTINGS_MANAGE
      },
      {
        id: "helpdesk-katalog",
        to: "/helpdesk/einstellungen/kategorien",
        label: "Katalog",
        description: "Kategorien und Ticketarten.",
        permission: PermissionCode.SETTINGS_MANAGE
      }
    ],
    access: { kind: "namespace", namespace: PermissionNamespace.HELPDESK }
  },
  leave: {
    code: ProductCode.LEAVE,
    labelDe: "Urlaubsverwaltung",
    descriptionDe: "Abwesenheiten und Genehmigungen.",
    pathPrefix: "/leave",
    order: 20,
    status: ProductStatus.LIVE,
    version: "1.3.0",
    requiresPlatform: PRODUCT_REQUIRES_PLATFORM,
    requires: [],
    admin: [
      {
        id: "leave-verwaltung",
        to: "/leave/verwaltung",
        label: "Verwaltung",
        description: "Richtlinie, Feiertage und Abwesenheitsarten.",
        permission: PermissionCode.LEAVE_POLICY_MANAGE
      }
    ],
    access: { kind: "namespace", namespace: PermissionNamespace.LEAVE }
  },
  fleet: {
    code: ProductCode.FLEET,
    labelDe: "Fuhrparkmanagement",
    descriptionDe: "Fahrzeuge, Fristen und F\xFChrerscheinkontrolle.",
    pathPrefix: "/fleet",
    order: 30,
    status: ProductStatus.LIVE,
    version: "1.2.0",
    requiresPlatform: PRODUCT_REQUIRES_PLATFORM,
    requires: [],
    admin: [
      {
        id: "fleet-verwaltung",
        to: "/fleet/verwaltung",
        label: "Verwaltung",
        description: "Intervalle f\xFCr Kontrolle, Unterweisung und Erinnerungen.",
        permission: PermissionCode.FLEET_POLICY_MANAGE
      }
    ],
    access: { kind: "namespace", namespace: PermissionNamespace.FLEET }
  },
  maintenance: {
    code: ProductCode.MAINTENANCE,
    labelDe: "Wartungsprotokolle",
    descriptionDe: "Wartungen, Checklisten und Anlagenhistorie.",
    pathPrefix: "/maintenance",
    order: 35,
    status: ProductStatus.LIVE,
    version: "1.0.0",
    requiresPlatform: PRODUCT_REQUIRES_PLATFORM,
    requires: [],
    admin: [
      {
        id: "maintenance-vorlagen",
        to: "/maintenance/vorlagen",
        label: "Vorlagen",
        description: "Versionierte Wartungsprotokolle und PDF-Hintergr\xFCnde.",
        permission: PermissionCode.MAINTENANCE_TEMPLATES_MANAGE
      }
    ],
    access: { kind: "namespace", namespace: PermissionNamespace.MAINTENANCE }
  },
  onboarding: {
    code: ProductCode.ONBOARDING,
    labelDe: "Eintritt und Austritt",
    descriptionDe: "Vorlagen, Aufgaben und Fristen f\xFCr Eintritte und Austritte.",
    pathPrefix: "/onboarding",
    order: 36,
    /**
     * Not the runtime switch: session and ProductGuard use ModuleInstallation.
     * Explicit false keeps empty-map helpers and the E1 backfill from treating
     * a never-installed row as company-enabled.
     */
    defaultEnabled: false,
    status: ProductStatus.LIVE,
    version: "1.0.0",
    requiresPlatform: PRODUCT_REQUIRES_PLATFORM,
    requires: [],
    admin: [],
    access: { kind: "namespace", namespace: PermissionNamespace.ONBOARDING }
  },
  tools: {
    code: ProductCode.TOOLS,
    labelDe: "Werkzeugeverwaltung",
    descriptionDe: "Ausleihe und Bestand.",
    pathPrefix: "/tools",
    order: 40,
    status: ProductStatus.PLANNED,
    requiresPlatform: PRODUCT_REQUIRES_PLATFORM,
    requires: [],
    admin: [],
    access: { kind: "permissions", anyOf: [] }
  },
  intranet: {
    code: ProductCode.INTRANET,
    labelDe: "Intranet",
    descriptionDe: "Unternehmensnachrichten, Dokumente, Formulare und Ansprechpartner.",
    pathPrefix: "/intranet",
    staffPermission: PermissionCode.INTRANET_CONTENT_READ,
    order: 5,
    status: ProductStatus.LIVE,
    version: "1.4.0",
    requiresPlatform: PRODUCT_REQUIRES_PLATFORM,
    requires: [],
    admin: [
      {
        id: "intranet-erscheinungsbild",
        to: "/intranet/redaktion/erscheinungsbild",
        label: "Erscheinungsbild",
        description: "Titel und Hintergrundbild der Startseite.",
        permission: PermissionCode.INTRANET_CONTENT_WRITE
      },
      {
        id: "intranet-einstellungen",
        to: "/intranet/redaktion/einstellungen",
        label: "Einstellungen",
        description: "Grenzen f\xFCr Titelbilder und f\xFCr Bilder in Beitr\xE4gen.",
        permission: PermissionCode.INTRANET_CONTENT_WRITE
      }
    ],
    access: { kind: "namespace", namespace: PermissionNamespace.INTRANET }
  }
};
var PRODUCT_META_LIST = PRODUCT_CODES.map(
  (code) => PRODUCT_META[code]
).sort((a, b) => a.order - b.order);
function installedProductAdminGroups(input) {
  const installed = new Set(input.installedProducts);
  const permissions = new Set(input.permissions);
  const groups = [];
  for (const meta of PRODUCT_META_LIST) {
    if (meta.status !== ProductStatus.LIVE || !installed.has(meta.code)) {
      continue;
    }
    const links = meta.admin.filter((link) => permissions.has(link.permission)).map(({ id, to, label, description }) => ({ id, to, label, description }));
    if (links.length === 0) {
      continue;
    }
    groups.push({ code: meta.code, heading: meta.labelDe, links });
  }
  return groups;
}
var sessionProductsSchema = z.array(productCodeSchema);
function isProductEnabledInMap(map, code) {
  const meta = PRODUCT_META[code];
  if (meta.status !== ProductStatus.LIVE) {
    return false;
  }
  if (!map || !(code in map)) {
    return meta.defaultEnabled ?? true;
  }
  return map[code] === true;
}
function productEntry(code, permissions) {
  const meta = PRODUCT_META[code];
  if (meta.selfServicePath && meta.staffPermission && !permissions.includes(meta.staffPermission)) {
    return { path: meta.selfServicePath, selfService: true };
  }
  return { path: meta.pathPrefix, selfService: false };
}
var productSettingItemSchema = z.object({
  code: productCodeSchema,
  labelDe: z.string(),
  descriptionDe: z.string(),
  status: productStatusSchema,
  enabled: z.boolean(),
  /** Only live products have a switch; planned ones show „Demnächst“. */
  toggleable: z.boolean()
});
var productSettingsResponseSchema = z.object({
  items: z.array(productSettingItemSchema),
  updatedAt: z.iso.datetime().nullable()
});
var updateProductSettingRequestSchema = z.strictObject({
  enabled: z.boolean()
});
function enabledLiveProductCodes(map) {
  return PRODUCT_CODES.filter((code) => isProductEnabledInMap(map, code));
}
function hasAnyPermission(permissions, required) {
  if (required.length === 0) {
    return false;
  }
  const set = new Set(permissions);
  return required.some((code) => set.has(code));
}
function hasProductAccess(permissions, access) {
  if (access.kind === "namespace") {
    return permissions.some((code) => permissionNamespace(code) === access.namespace);
  }
  return hasAnyPermission(permissions, access.anyOf);
}
function userQualifiesForProduct(input) {
  return hasProductAccess(input.permissions, PRODUCT_META[input.code].access);
}
function userCanAccessProduct(input) {
  const meta = PRODUCT_META[input.code];
  if (meta.status !== ProductStatus.LIVE) {
    return false;
  }
  if (!isProductEnabledInMap(input.enabledProducts, input.code)) {
    return false;
  }
  return userQualifiesForProduct(input);
}
function resolveSessionProducts(input) {
  return PRODUCT_META_LIST.filter(
    (meta) => userCanAccessProduct({
      code: meta.code,
      permissions: input.permissions,
      enabledProducts: input.enabledProducts
    })
  ).map((meta) => meta.code);
}
var MODULE_VERSION_PATTERN = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?$/;
var moduleVersionSchema = z.string().regex(MODULE_VERSION_PATTERN);
function parseModuleVersion(value) {
  const match = MODULE_VERSION_PATTERN.exec(value);
  if (!match) {
    return null;
  }
  const major = Number(match[1]);
  const minor = Number(match[2]);
  const patch = Number(match[3]);
  if (!Number.isSafeInteger(major) || !Number.isSafeInteger(minor) || !Number.isSafeInteger(patch)) {
    return null;
  }
  return {
    major,
    minor,
    patch,
    prerelease: match[4] ? match[4].split(".") : []
  };
}
function compareIdentifier(left, right) {
  const numeric = /^(?:0|[1-9]\d*)$/;
  const leftNumeric = numeric.test(left);
  const rightNumeric = numeric.test(right);
  if (leftNumeric && rightNumeric) {
    if (left.length !== right.length) {
      return left.length < right.length ? -1 : 1;
    }
    if (left === right) {
      return 0;
    }
    return left < right ? -1 : 1;
  }
  if (leftNumeric !== rightNumeric) {
    return leftNumeric ? -1 : 1;
  }
  if (left === right) {
    return 0;
  }
  return left < right ? -1 : 1;
}
function compareModuleVersion(left, right) {
  if (left.major !== right.major) {
    return left.major < right.major ? -1 : 1;
  }
  if (left.minor !== right.minor) {
    return left.minor < right.minor ? -1 : 1;
  }
  if (left.patch !== right.patch) {
    return left.patch < right.patch ? -1 : 1;
  }
  if (left.prerelease.length === 0 && right.prerelease.length === 0) {
    return 0;
  }
  if (left.prerelease.length === 0) {
    return 1;
  }
  if (right.prerelease.length === 0) {
    return -1;
  }
  const length = Math.min(left.prerelease.length, right.prerelease.length);
  for (let index = 0; index < length; index += 1) {
    const leftId = left.prerelease[index];
    const rightId = right.prerelease[index];
    if (leftId === void 0 || rightId === void 0) {
      break;
    }
    const delta = compareIdentifier(leftId, rightId);
    if (delta !== 0) {
      return delta;
    }
  }
  if (left.prerelease.length === right.prerelease.length) {
    return 0;
  }
  return left.prerelease.length < right.prerelease.length ? -1 : 1;
}
function moduleVersionIsNewer(installed, manifest) {
  const left = parseModuleVersion(installed);
  const right = parseModuleVersion(manifest);
  if (!left || !right) {
    return false;
  }
  return compareModuleVersion(right, left) > 0;
}
var PLATFORM_RANGE_PATTERN = /^(>=|>|<=|<|=)\s*(\S+)$/;
function platformSatisfies(requiresPlatform, appVersion) {
  const match = PLATFORM_RANGE_PATTERN.exec(requiresPlatform.trim());
  if (!match) {
    return false;
  }
  const operator = match[1];
  const required = parseModuleVersion(match[2] ?? "");
  const actual = parseModuleVersion(appVersion);
  if (!operator || !required || !actual) {
    return false;
  }
  const delta = compareModuleVersion(actual, required);
  if (operator === ">=") {
    return delta >= 0;
  }
  if (operator === ">") {
    return delta > 0;
  }
  if (operator === "<=") {
    return delta <= 0;
  }
  if (operator === "<") {
    return delta < 0;
  }
  return operator === "=" && delta === 0;
}
var ModuleInstallationStatus = {
  INSTALLED: "installed",
  REMOVED: "removed"
};
var moduleInstallationStatusSchema = z.enum(ModuleInstallationStatus);
function moduleInstallationsFromProductFlagMap(map) {
  const rows = [];
  for (const code of PRODUCT_CODES) {
    const meta = PRODUCT_META[code];
    if (meta.status !== ProductStatus.LIVE || !isProductEnabledInMap(map, code)) {
      continue;
    }
    rows.push({
      moduleCode: code,
      status: ModuleInstallationStatus.INSTALLED,
      installedVersion: meta.version
    });
  }
  return rows;
}
function moduleInstallationsForTenantBackfill(input) {
  if (input.setupCompletedAt == null || input.setupCompletedAt === "") {
    return [];
  }
  return moduleInstallationsFromProductFlagMap(input.products);
}
var moduleInstallationSnapshotSchema = z.object({
  code: productCodeSchema,
  status: moduleInstallationStatusSchema,
  installedVersion: moduleVersionSchema
});
var ModuleStoreState = {
  NEVER: "never",
  INSTALLED: "installed",
  REMOVED: "removed",
  PLANNED: "planned"
};
var moduleStoreStateSchema = z.enum(ModuleStoreState);
var moduleStoreCardSchema = z.object({
  code: productCodeSchema,
  labelDe: z.string(),
  descriptionDe: z.string(),
  state: moduleStoreStateSchema,
  /** Manifest version. Null while the module is planned. */
  version: moduleVersionSchema.nullable(),
  /** Version on the installation row. Null when the module was never installed. */
  installedVersion: moduleVersionSchema.nullable(),
  installable: z.boolean(),
  requiresPlatform: z.string(),
  requires: z.array(productCodeSchema)
}).superRefine((card, ctx) => {
  const meta = PRODUCT_META[card.code];
  if (meta.status !== ProductStatus.LIVE) {
    if (card.state !== ModuleStoreState.PLANNED) {
      ctx.addIssue({
        code: "custom",
        path: ["state"],
        message: "Geplante Module bleiben geplant."
      });
    }
    if (card.installable) {
      ctx.addIssue({
        code: "custom",
        path: ["installable"],
        message: "Geplante Module sind nicht installierbar."
      });
    }
    if (card.version !== null) {
      ctx.addIssue({
        code: "custom",
        path: ["version"],
        message: "Geplante Module haben keine Version."
      });
    }
    if (card.installedVersion !== null) {
      ctx.addIssue({
        code: "custom",
        path: ["installedVersion"],
        message: "Geplante Module haben keine Installation."
      });
    }
    return;
  }
  if (card.version !== meta.version) {
    ctx.addIssue({
      code: "custom",
      path: ["version"],
      message: "Die Katalogversion kommt aus dem Manifest."
    });
  }
  if (card.state === ModuleStoreState.PLANNED) {
    ctx.addIssue({
      code: "custom",
      path: ["state"],
      message: "Nur geplante Module haben den Zustand geplant."
    });
  }
  if (card.state === ModuleStoreState.NEVER && card.installedVersion !== null) {
    ctx.addIssue({
      code: "custom",
      path: ["installedVersion"],
      message: "Nie installierte Module haben keine installierte Version."
    });
  }
  if ((card.state === ModuleStoreState.INSTALLED || card.state === ModuleStoreState.REMOVED) && card.installedVersion === null) {
    ctx.addIssue({
      code: "custom",
      path: ["installedVersion"],
      message: "Installierte oder entfernte Module nennen die installierte Version."
    });
  }
});
var moduleStoreCatalogSchema = z.array(moduleStoreCardSchema);
var completeSetupRequestSchema = z.strictObject({
  codes: z.array(productCodeSchema)
});
var moduleStoreResponseSchema = z.object({
  items: moduleStoreCatalogSchema
});
var moduleInstallResultSchema = z.object({
  /** Codes from the request that were written (or already installed). */
  installed: z.array(productCodeSchema),
  /** Dependencies installed in the same call that were not in the request. */
  alsoInstalled: z.array(productCodeSchema),
  items: moduleStoreCatalogSchema
});
var moduleRemoveResultSchema = z.object({
  code: productCodeSchema,
  items: moduleStoreCatalogSchema
});
var moduleUpgradeResultSchema = z.object({
  code: productCodeSchema,
  installedVersion: moduleVersionSchema,
  items: moduleStoreCatalogSchema
});
var moduleCompleteSetupResultSchema = z.object({
  setupCompletedAt: z.string().datetime(),
  installed: z.array(productCodeSchema),
  alsoInstalled: z.array(productCodeSchema),
  items: moduleStoreCatalogSchema
});
function collectModuleInstallPlan(input) {
  const requiresOf = input.requiresOf ?? ((code) => PRODUCT_META[code].requires);
  const requested = [...new Set(input.codes)];
  const requestedSet = new Set(requested);
  const seen = /* @__PURE__ */ new Set();
  const expanded = [];
  const visit = (code) => {
    if (seen.has(code)) {
      return;
    }
    seen.add(code);
    for (const dep of requiresOf(code)) {
      visit(dep);
    }
    expanded.push(code);
  };
  for (const code of requested) {
    visit(code);
  }
  const toInstall = expanded.filter((code) => !input.installed.has(code));
  const alsoInstalled = toInstall.filter((code) => !requestedSet.has(code));
  return { toInstall, alsoInstalled };
}
function storeState(meta, row) {
  if (meta.status !== ProductStatus.LIVE) {
    return ModuleStoreState.PLANNED;
  }
  if (!row) {
    return ModuleStoreState.NEVER;
  }
  if (row.status === ModuleInstallationStatus.REMOVED) {
    return ModuleStoreState.REMOVED;
  }
  return ModuleStoreState.INSTALLED;
}
function storeInstallable(meta, state, appVersion) {
  if (meta.status !== ProductStatus.LIVE) {
    return false;
  }
  if (state === ModuleStoreState.INSTALLED) {
    return false;
  }
  return platformSatisfies(meta.requiresPlatform, appVersion);
}
function buildModuleStoreCatalog(installations = [], appVersion = APP_VERSION) {
  const byCode = /* @__PURE__ */ new Map();
  for (const row of installations) {
    if (PRODUCT_META[row.code].status === ProductStatus.LIVE) {
      byCode.set(row.code, row);
    }
  }
  return PRODUCT_META_LIST.map((meta) => {
    const row = byCode.get(meta.code);
    const state = storeState(meta, row);
    return {
      code: meta.code,
      labelDe: meta.labelDe,
      descriptionDe: meta.descriptionDe,
      state,
      version: meta.status === ProductStatus.LIVE ? meta.version : null,
      installedVersion: row && meta.status === ProductStatus.LIVE ? row.installedVersion : null,
      installable: storeInstallable(meta, state, appVersion),
      requiresPlatform: meta.requiresPlatform,
      requires: [...meta.requires]
    };
  });
}
var paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  sortBy: z.string().min(1).max(64).optional(),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var paginatedSchema = (item) => z.object({
  items: z.array(item),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1)
});
var apiErrorSchema = z.object({
  statusCode: z.number().int(),
  code: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
  timestamp: z.iso.datetime(),
  path: z.string().optional()
});
var healthStatusSchema = z.object({
  status: z.enum(["ok", "error", "shutting_down"]),
  info: z.record(z.string(), z.unknown()).optional(),
  error: z.record(z.string(), z.unknown()).optional(),
  details: z.record(z.string(), z.unknown()).optional()
});

// src/timezone.ts
var BERLIN_TIME_ZONE = "Europe/Berlin";
function berlinCalendarYear(now = /* @__PURE__ */ new Date()) {
  return zonedDateTimeParts(now, BERLIN_TIME_ZONE).year;
}
function pad(value) {
  return String(value).padStart(2, "0");
}
function zonedDateTimeParts(date, timeZone) {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
  });
  const map = {};
  for (const part of fmt.formatToParts(date)) {
    if (part.type !== "literal") {
      map[part.type] = part.value;
    }
  }
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second)
  };
}
function fromZonedTime(timeZone, year, month, day, hour, minute, second = 0) {
  const guess = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  const parts = zonedDateTimeParts(guess, timeZone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  const wanted = Date.UTC(year, month - 1, day, hour, minute, second);
  return new Date(guess.getTime() + (wanted - asUtc));
}
function zonedDayRange(timeZone, year, month, day) {
  const start = fromZonedTime(timeZone, year, month, day, 0, 0, 0);
  const nextUtc = new Date(Date.UTC(year, month - 1, day + 1));
  const end = fromZonedTime(
    timeZone,
    nextUtc.getUTCFullYear(),
    nextUtc.getUTCMonth() + 1,
    nextUtc.getUTCDate(),
    0,
    0,
    0
  );
  return { start, end };
}
function windowOverlapsRange(windowStart, windowEnd, rangeStart, rangeEnd) {
  const start = new Date(windowStart).getTime();
  const end = new Date(windowEnd).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return false;
  }
  return start < rangeEnd.getTime() && end > rangeStart.getTime();
}
function windowOverlapsZonedDay(windowStart, windowEnd, timeZone, year, month, day) {
  const { start, end } = zonedDayRange(timeZone, year, month, day);
  return windowOverlapsRange(windowStart, windowEnd, start, end);
}
function toZonedDatetimeLocalValue(iso, timeZone) {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const parts = zonedDateTimeParts(date, timeZone);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
}
function fromZonedDatetimeLocalValue(value, timeZone) {
  const trimmed = value.trim();
  if (!trimmed) {
    return void 0;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(trimmed);
  if (!match) {
    return void 0;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6] ?? "0");
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59 || second > 59) {
    return void 0;
  }
  return fromZonedTime(timeZone, year, month, day, hour, minute, second).toISOString();
}

// src/ticket.ts
var TicketType = {
  INCIDENT: "INCIDENT",
  SERVICE_REQUEST: "SERVICE_REQUEST",
  PROBLEM: "PROBLEM",
  CHANGE: "CHANGE"
};
var ticketTypeSchema = z.enum(TicketType);
var TicketStatus = {
  NEW: "NEW",
  IN_PROGRESS: "IN_PROGRESS",
  WAITING_FOR_CUSTOMER: "WAITING_FOR_CUSTOMER",
  WAITING_FOR_APPROVAL: "WAITING_FOR_APPROVAL",
  RESOLVED: "RESOLVED",
  CLOSED: "CLOSED",
  REOPENED: "REOPENED"
};
var ticketStatusSchema = z.enum(TicketStatus);
var TicketPriority = {
  LOW: "LOW",
  MEDIUM: "MEDIUM",
  HIGH: "HIGH",
  CRITICAL: "CRITICAL"
};
var ticketPrioritySchema = z.enum(TicketPriority);
var TICKET_TYPE_LABELS = {
  INCIDENT: "St\xF6rung",
  SERVICE_REQUEST: "Serviceanfrage",
  PROBLEM: "Problem",
  CHANGE: "\xC4nderung"
};
var TICKET_STATUS_LABELS = {
  NEW: "Neu",
  IN_PROGRESS: "In Bearbeitung",
  WAITING_FOR_CUSTOMER: "Wartet auf Kunde",
  WAITING_FOR_APPROVAL: "Wartet auf Freigabe",
  RESOLVED: "Gel\xF6st",
  CLOSED: "Geschlossen",
  REOPENED: "Wiederer\xF6ffnet"
};
var TICKET_PRIORITY_LABELS = {
  LOW: "Niedrig",
  MEDIUM: "Mittel",
  HIGH: "Hoch",
  CRITICAL: "Kritisch"
};
var TicketSource = {
  PORTAL: "PORTAL",
  EMAIL: "EMAIL",
  PHONE: "PHONE",
  AGENT: "AGENT",
  CHATBOT: "CHATBOT",
  API: "API"
};
var ticketSourceSchema = z.enum(TicketSource);
var TICKET_SOURCE_LABELS = {
  PORTAL: "Self-Service-Portal",
  EMAIL: "E-Mail",
  PHONE: "Telefon",
  AGENT: "IT-Agent",
  CHATBOT: "Chatbot",
  API: "API"
};
var TicketLinkKind = {
  RELATED: "RELATED",
  DUPLICATE: "DUPLICATE",
  CAUSED_BY: "CAUSED_BY",
  CHILD_OF: "CHILD_OF",
  /** Lead (`from`) → bundled ticket (`to`). */
  MAJOR_INCIDENT: "MAJOR_INCIDENT",
  INCIDENT_OF_PROBLEM: "INCIDENT_OF_PROBLEM",
  PROBLEM_OF_CHANGE: "PROBLEM_OF_CHANGE",
  /** New ticket (`from`) that continues a closed ticket (`to`) after the reopen window. */
  FOLLOW_UP: "FOLLOW_UP"
};
var ticketLinkKindSchema = z.enum(TicketLinkKind);
var TICKET_LINK_KIND_LABELS = {
  RELATED: "Verwandt",
  DUPLICATE: "Duplikat",
  CAUSED_BY: "Verursacht durch",
  CHILD_OF: "Untergeordnet",
  MAJOR_INCIDENT: "Major Incident",
  INCIDENT_OF_PROBLEM: "Incident zu Problem",
  PROBLEM_OF_CHANGE: "Problem zu Change",
  FOLLOW_UP: "Folgeticket"
};
var IncidentSeverity = {
  MINOR: "MINOR",
  MAJOR: "MAJOR"
};
var incidentSeveritySchema = z.enum(IncidentSeverity);
var INCIDENT_SEVERITY_LABELS = {
  MINOR: "Minor",
  MAJOR: "Major"
};
var TICKET_PRIORITY_RANK = {
  LOW: 0,
  MEDIUM: 1,
  HIGH: 2,
  CRITICAL: 3
};
var IMPACT_URGENCY_PRIORITY = {
  HIGH: { LOW: "MEDIUM", MEDIUM: "HIGH", HIGH: "CRITICAL" },
  MEDIUM: { LOW: "LOW", MEDIUM: "MEDIUM", HIGH: "HIGH" },
  LOW: { LOW: "LOW", MEDIUM: "LOW", HIGH: "MEDIUM" }
};
function priorityFromImpactUrgency(impact, urgency) {
  return IMPACT_URGENCY_PRIORITY[impact][urgency];
}
function resolveIncidentPriority(input) {
  let priority = priorityFromImpactUrgency(input.impact, input.urgency);
  const isMajor = input.severity === IncidentSeverity.MAJOR;
  if (isMajor && TICKET_PRIORITY_RANK[priority] < TICKET_PRIORITY_RANK.HIGH) {
    priority = TicketPriority.HIGH;
  }
  return { priority, isMajor };
}
var ImpactLevel = {
  LOW: "LOW",
  MEDIUM: "MEDIUM",
  HIGH: "HIGH"
};
var impactLevelSchema = z.enum(ImpactLevel);
var UrgencyLevel = ImpactLevel;
var urgencyLevelSchema = impactLevelSchema;
var IMPACT_LEVEL_LABELS = {
  LOW: "Niedrig",
  MEDIUM: "Mittel",
  HIGH: "Hoch"
};
var URGENCY_LEVEL_LABELS = IMPACT_LEVEL_LABELS;
var ProblemLifecycle = {
  IDENTIFIED: "IDENTIFIED",
  ANALYSIS: "ANALYSIS",
  WORKAROUND_AVAILABLE: "WORKAROUND_AVAILABLE",
  RESOLVED: "RESOLVED"
};
var problemLifecycleSchema = z.enum(ProblemLifecycle);
var PROBLEM_LIFECYCLE_LABELS = {
  IDENTIFIED: "Identifiziert",
  ANALYSIS: "Analyse",
  WORKAROUND_AVAILABLE: "Workaround verf\xFCgbar",
  RESOLVED: "Gel\xF6st"
};
var PROBLEM_LIFECYCLE_TRANSITIONS = {
  IDENTIFIED: [ProblemLifecycle.ANALYSIS],
  ANALYSIS: [ProblemLifecycle.WORKAROUND_AVAILABLE, ProblemLifecycle.IDENTIFIED],
  WORKAROUND_AVAILABLE: [ProblemLifecycle.RESOLVED, ProblemLifecycle.ANALYSIS],
  RESOLVED: [ProblemLifecycle.ANALYSIS]
};
function allowedProblemLifecycleTransitions(from) {
  return PROBLEM_LIFECYCLE_TRANSITIONS[from];
}
function canTransitionProblemLifecycle(from, to) {
  return from === to || PROBLEM_LIFECYCLE_TRANSITIONS[from].includes(to);
}
var EMPTY_PROBLEM_RCA = {
  summary: "",
  trigger: "",
  contributingFactors: "",
  evidence: "",
  preventiveActions: ""
};
var problemRcaSchema = z.object({
  summary: z.string().trim().max(2e3),
  trigger: z.string().trim().max(5e3),
  contributingFactors: z.string().trim().max(5e3),
  evidence: z.string().trim().max(5e3),
  preventiveActions: z.string().trim().max(5e3)
});
function parseProblemRca(value) {
  if (!value?.trim()) {
    return { ...EMPTY_PROBLEM_RCA };
  }
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const result = problemRcaSchema.safeParse({ ...EMPTY_PROBLEM_RCA, ...parsed });
      if (result.success) {
        return result.data;
      }
    }
  } catch {
  }
  return { ...EMPTY_PROBLEM_RCA, summary: value.trim().slice(0, 2e3) };
}
function serializeProblemRca(rca) {
  const normalized = problemRcaSchema.parse(rca);
  const empty = !normalized.summary && !normalized.trigger && !normalized.contributingFactors && !normalized.evidence && !normalized.preventiveActions;
  return empty ? null : JSON.stringify(normalized);
}
var ChangeRisk = {
  LOW: "LOW",
  MEDIUM: "MEDIUM",
  HIGH: "HIGH"
};
var changeRiskSchema = z.enum(ChangeRisk);
var CHANGE_RISK_LABELS = {
  LOW: "Niedrig",
  MEDIUM: "Mittel",
  HIGH: "Hoch"
};
var CHANGE_CALENDAR_TIMEZONE = "Europe/Berlin";
var CHANGE_AFFECTED_SERVICES_MAX = 50;
var CHANGE_AFFECTED_SERVICE_MAX_LENGTH = 200;
function changeBerlinDayRange(year, month, day) {
  return zonedDayRange(CHANGE_CALENDAR_TIMEZONE, year, month, day);
}
function changeWindowOverlapsBerlinDay(windowStart, windowEnd, year, month, day) {
  return windowOverlapsZonedDay(windowStart, windowEnd, CHANGE_CALENDAR_TIMEZONE, year, month, day);
}
function toChangeDatetimeLocalValue(iso) {
  return toZonedDatetimeLocalValue(iso, CHANGE_CALENDAR_TIMEZONE);
}
function fromChangeDatetimeLocalValue(value) {
  return fromZonedDatetimeLocalValue(value, CHANGE_CALENDAR_TIMEZONE);
}
var MS_PER_DAY = 24 * 60 * 60 * 1e3;
function shouldOpenChangePanel(change, now = /* @__PURE__ */ new Date()) {
  if (!change.approved) {
    return true;
  }
  const start = change.scheduledStart ? new Date(change.scheduledStart).getTime() : NaN;
  const end = change.scheduledEnd ? new Date(change.scheduledEnd).getTime() : NaN;
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return false;
  }
  const horizonEnd = now.getTime() + 7 * MS_PER_DAY;
  return start < horizonEnd && end > now.getTime();
}
function parseAffectedServices(value) {
  const raw = typeof value === "string" ? value.split(/[\n,;]+/) : [...value ?? []];
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const item of raw) {
    const trimmed = item.trim().slice(0, CHANGE_AFFECTED_SERVICE_MAX_LENGTH);
    if (!trimmed) {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(trimmed);
    if (out.length >= CHANGE_AFFECTED_SERVICES_MAX) {
      break;
    }
  }
  return out;
}
function isValidChangeWindow(start, end) {
  if (!start && !end) {
    return true;
  }
  if (!start || !end) {
    return false;
  }
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  return Number.isFinite(startMs) && Number.isFinite(endMs) && endMs >= startMs;
}
var TICKET_STATUS_TRANSITIONS = {
  NEW: [
    TicketStatus.IN_PROGRESS,
    TicketStatus.WAITING_FOR_CUSTOMER,
    TicketStatus.RESOLVED,
    TicketStatus.CLOSED
  ],
  IN_PROGRESS: [TicketStatus.WAITING_FOR_CUSTOMER, TicketStatus.RESOLVED, TicketStatus.CLOSED],
  WAITING_FOR_CUSTOMER: [TicketStatus.IN_PROGRESS, TicketStatus.RESOLVED, TicketStatus.CLOSED],
  WAITING_FOR_APPROVAL: [TicketStatus.CLOSED],
  RESOLVED: [TicketStatus.CLOSED, TicketStatus.REOPENED],
  CLOSED: [TicketStatus.REOPENED],
  REOPENED: [
    TicketStatus.IN_PROGRESS,
    TicketStatus.WAITING_FOR_CUSTOMER,
    TicketStatus.RESOLVED,
    TicketStatus.CLOSED
  ]
};
function allowedTicketTransitions(from) {
  return TICKET_STATUS_TRANSITIONS[from];
}
function canTransitionTicketStatus(from, to) {
  return TICKET_STATUS_TRANSITIONS[from].includes(to);
}
function ticketStatusActionLabel(from, to) {
  if (to === TicketStatus.CLOSED && from !== TicketStatus.RESOLVED) {
    return "Gel\xF6st & geschlossen";
  }
  return TICKET_STATUS_LABELS[to];
}
function permissionForTicketStatusChange(to) {
  if (to === TicketStatus.CLOSED) {
    return PermissionCode.TICKETS_CLOSE;
  }
  if (to === TicketStatus.REOPENED) {
    return PermissionCode.TICKETS_REOPEN;
  }
  return PermissionCode.TICKETS_UPDATE;
}
var TicketAuditAction = {
  CREATED: "TICKET_CREATED",
  UPDATED: "TICKET_UPDATED",
  ASSIGNED: "TICKET_ASSIGNED",
  STATUS_CHANGED: "TICKET_STATUS_CHANGED",
  COMMENTED: "TICKET_COMMENTED",
  ATTACHMENT_ADDED: "TICKET_ATTACHMENT_ADDED",
  SLA_ESCALATED: "TICKET_SLA_ESCALATED",
  APPROVAL_STARTED: "TICKET_APPROVAL_STARTED",
  APPROVAL_DECIDED: "TICKET_APPROVAL_DECIDED",
  AI_CLASSIFIED: "TICKET_AI_CLASSIFIED",
  AI_ASSIST: "TICKET_AI_ASSIST",
  INCIDENT_UPDATED: "TICKET_INCIDENT_UPDATED",
  INCIDENT_LINKED: "TICKET_INCIDENT_LINKED",
  INCIDENT_UNLINKED: "TICKET_INCIDENT_UNLINKED",
  INCIDENT_BROADCAST: "TICKET_INCIDENT_BROADCAST",
  PROBLEM_UPDATED: "TICKET_PROBLEM_UPDATED",
  PROBLEM_LINKED: "TICKET_PROBLEM_LINKED",
  PROBLEM_UNLINKED: "TICKET_PROBLEM_UNLINKED",
  PROBLEM_KB_DRAFT: "TICKET_PROBLEM_KB_DRAFT",
  CHANGE_UPDATED: "TICKET_CHANGE_UPDATED",
  AUTOMATION_APPLIED: "TICKET_AUTOMATION_APPLIED",
  REMOTE_SESSION_REQUESTED: "TICKET_REMOTE_SESSION_REQUESTED",
  REMOTE_SESSION_ACTIVATED: "TICKET_REMOTE_SESSION_ACTIVATED",
  REMOTE_SESSION_DENIED: "TICKET_REMOTE_SESSION_DENIED",
  REMOTE_SESSION_LAUNCH_REQUESTED: "TICKET_REMOTE_SESSION_LAUNCH_REQUESTED",
  REMOTE_SESSION_CREDENTIALS_ACCESSED: "TICKET_REMOTE_SESSION_CREDENTIALS_ACCESSED",
  REMOTE_SESSION_CREDENTIALS_REJECTED: "TICKET_REMOTE_SESSION_CREDENTIALS_REJECTED",
  REMOTE_SESSION_ENDED: "TICKET_REMOTE_SESSION_ENDED",
  REMOTE_SESSION_EXPIRED: "TICKET_REMOTE_SESSION_EXPIRED",
  CSAT_SENT: "TICKET_CSAT_SENT",
  CSAT_SUBMITTED: "TICKET_CSAT_SUBMITTED"
};
var TICKET_AUDIT_ACTION_LABELS = {
  TICKET_CREATED: "Ticket erstellt",
  TICKET_UPDATED: "Ticket ge\xE4ndert",
  TICKET_ASSIGNED: "Zuweisung",
  TICKET_STATUS_CHANGED: "Status ge\xE4ndert",
  TICKET_COMMENTED: "Kommentar",
  TICKET_ATTACHMENT_ADDED: "Anhang hinzugef\xFCgt",
  TICKET_SLA_ESCALATED: "SLA-Eskalation",
  TICKET_APPROVAL_STARTED: "Freigabe gestartet",
  TICKET_APPROVAL_DECIDED: "Freigabeentscheidung",
  TICKET_AI_CLASSIFIED: "KI-Kategorisierung",
  TICKET_AI_ASSIST: "KI-Ticket-Hilfe",
  TICKET_INCIDENT_UPDATED: "Incident aktualisiert",
  TICKET_INCIDENT_LINKED: "Major-Incident-Verkn\xFCpfung",
  TICKET_INCIDENT_UNLINKED: "Major-Incident-Verkn\xFCpfung gel\xF6st",
  TICKET_INCIDENT_BROADCAST: "Major-Incident-Mitteilung",
  TICKET_PROBLEM_UPDATED: "Problem aktualisiert",
  TICKET_PROBLEM_LINKED: "Incident mit Problem verkn\xFCpft",
  TICKET_PROBLEM_UNLINKED: "Incident vom Problem gel\xF6st",
  TICKET_PROBLEM_KB_DRAFT: "KB-Entwurf aus Problem",
  TICKET_CHANGE_UPDATED: "Change aktualisiert",
  TICKET_AUTOMATION_APPLIED: "Automatisierungsregel",
  TICKET_REMOTE_SESSION_REQUESTED: "Remote-Support angefragt",
  TICKET_REMOTE_SESSION_ACTIVATED: "Remote-Support freigegeben",
  TICKET_REMOTE_SESSION_DENIED: "Remote-Support abgelehnt",
  TICKET_REMOTE_SESSION_LAUNCH_REQUESTED: "Remote-Verbindung angefordert",
  TICKET_REMOTE_SESSION_CREDENTIALS_ACCESSED: "Remote-Zugangsdaten abgerufen",
  TICKET_REMOTE_SESSION_CREDENTIALS_REJECTED: "Remote-Zugangsdaten verweigert",
  TICKET_REMOTE_SESSION_ENDED: "Remote-Support beendet",
  TICKET_REMOTE_SESSION_EXPIRED: "Remote-Support abgelaufen",
  TICKET_CSAT_SENT: "CSAT-Umfrage versendet",
  TICKET_CSAT_SUBMITTED: "CSAT-Bewertung abgegeben"
};
function plainTextFromHtml(html) {
  return html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, " ").replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, " ").replace(/<br\s*\/?>/gi, " ").replace(/<\/(p|div|li|h[1-6]|tr)>/gi, " ").replace(/<[^>]+>/g, " ").replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&").replace(/&lt;/gi, "<").replace(/&gt;/gi, ">").replace(/&quot;/gi, '"').replace(/&#39;/g, "'").replace(/\s+/g, " ").trim();
}
var TICKET_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
var TICKET_ATTACHMENT_MAX_FILES = 5;
var TICKET_ATTACHMENT_ALLOWED_MIME = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "text/plain",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/zip"
];
var AttachmentScanStatus = {
  PENDING: "PENDING",
  CLEAN: "CLEAN",
  BLOCKED: "BLOCKED",
  SKIPPED: "SKIPPED"
};
var attachmentScanStatusSchema = z.enum(AttachmentScanStatus);
var ATTACHMENT_SCAN_STATUS_LABELS = {
  PENDING: "Wird gepr\xFCft",
  CLEAN: "Gepr\xFCft",
  BLOCKED: "Blockiert",
  SKIPPED: "Scan \xFCbersprungen"
};
function isPreviewableAttachment(mimeType) {
  return mimeType.startsWith("image/") || mimeType === "application/pdf";
}

// src/approval.ts
var approvalPersonSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string().email(),
  profilePhotoUrl: z.string().nullable().optional()
});
var ApprovalMode = {
  SEQUENTIAL: "SEQUENTIAL",
  PARALLEL: "PARALLEL"
};
var approvalModeSchema = z.enum(ApprovalMode);
var APPROVAL_MODE_LABELS = {
  SEQUENTIAL: "Sequenziell",
  PARALLEL: "Parallel"
};
var ApprovalState = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  CANCELLED: "CANCELLED"
};
var approvalStateSchema = z.enum(ApprovalState);
var APPROVAL_STATE_LABELS = {
  PENDING: "Ausstehend",
  APPROVED: "Freigegeben",
  REJECTED: "Abgelehnt",
  CANCELLED: "Abgebrochen"
};
var ApprovalDecisionValue = {
  APPROVED: "APPROVED",
  REJECTED: "REJECTED"
};
var approvalDecisionValueSchema = z.enum(ApprovalDecisionValue);
var APPROVAL_DECISION_LABELS = {
  APPROVED: "Freigeben",
  REJECTED: "Ablehnen"
};
var PRIORITY_RANK = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
  CRITICAL: 4
};
var optionalNullableUuid = z.union([z.string().uuid(), z.null()]).optional();
var approvalStepApproverSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string().email(),
  sortOrder: z.number().int(),
  profilePhotoUrl: z.string().nullable().optional()
});
var approvalStepSchema = z.object({
  id: z.string().uuid(),
  position: z.number().int().nonnegative(),
  name: z.string(),
  mode: approvalModeSchema,
  roleId: z.string().uuid().nullable(),
  roleName: z.string().nullable(),
  minApprovals: z.number().int().positive(),
  approvers: z.array(approvalStepApproverSchema)
});
var upsertApprovalStepRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  mode: approvalModeSchema.default(ApprovalMode.SEQUENTIAL),
  roleId: optionalNullableUuid,
  minApprovals: z.number().int().min(1).max(50).default(1),
  approverIds: z.array(z.string().uuid()).default([])
}).refine((value) => Boolean(value.roleId) || value.approverIds.length > 0, {
  message: "Jede Stufe braucht mindestens eine Person oder eine Rolle."
});
var approvalWorkflowSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean(),
  ticketType: ticketTypeSchema.nullable(),
  categoryId: z.string().uuid().nullable(),
  categoryName: z.string().nullable(),
  minPriority: ticketPrioritySchema.nullable(),
  minValueCents: z.number().int().nonnegative().nullable(),
  steps: z.array(approvalStepSchema),
  updatedAt: z.iso.datetime()
});
var upsertApprovalWorkflowRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  isActive: z.boolean().optional(),
  ticketType: ticketTypeSchema.nullable().optional(),
  categoryId: optionalNullableUuid,
  minPriority: ticketPrioritySchema.nullable().optional(),
  minValueCents: z.number().int().min(0).max(1e8).nullable().optional(),
  steps: z.array(upsertApprovalStepRequestSchema).min(1).optional()
}).refine(
  (value) => Boolean(value.ticketType) || Boolean(value.categoryId) || Boolean(value.minPriority) || value.minValueCents != null && value.minValueCents > 0,
  {
    message: "helpdesk.approval.rule.min"
  }
);
var approvalWorkflowListResponseSchema = z.object({
  items: z.array(approvalWorkflowSchema)
});
var approvalLookupsSchema = z.object({
  categories: z.array(
    z.object({ id: z.string().uuid(), name: z.string(), parentId: z.string().uuid().nullable() })
  ),
  roles: z.array(z.object({ id: z.string().uuid(), code: z.string(), name: z.string() })),
  users: z.array(approvalPersonSchema)
});
var ticketApprovalDecisionSchema = z.object({
  id: z.string().uuid(),
  decision: approvalDecisionValueSchema,
  comment: z.string().nullable(),
  decidedAt: z.iso.datetime(),
  approver: approvalPersonSchema
});
var ticketApprovalStepSchema = z.object({
  id: z.string().uuid(),
  position: z.number().int().nonnegative(),
  name: z.string(),
  mode: approvalModeSchema,
  minApprovals: z.number().int().positive(),
  roleName: z.string().nullable(),
  isCurrent: z.boolean(),
  status: approvalStateSchema,
  pendingApprovers: z.array(approvalPersonSchema),
  decisions: z.array(ticketApprovalDecisionSchema)
});
var ticketApprovalSchema = z.object({
  requestId: z.string().uuid(),
  workflowId: z.string().uuid(),
  workflowName: z.string(),
  status: approvalStateSchema,
  stepPosition: z.number().int().nonnegative(),
  canDecide: z.boolean(),
  steps: z.array(ticketApprovalStepSchema)
});
var decideApprovalRequestSchema = z.object({
  decision: approvalDecisionValueSchema,
  comment: z.string().trim().max(2e3).optional()
});
var approvalActionPreviewSchema = z.object({
  tokenValid: z.boolean(),
  alreadyUsed: z.boolean(),
  expired: z.boolean(),
  requestStatus: approvalStateSchema.nullable(),
  ticketNumber: z.number().int().positive().nullable(),
  ticketSubject: z.string().nullable(),
  workflowName: z.string().nullable(),
  stepName: z.string().nullable(),
  approverDisplayName: z.string().nullable()
});
var approvalActionResponseSchema = z.object({
  ok: z.literal(true),
  decision: approvalDecisionValueSchema,
  requestStatus: approvalStateSchema,
  ticketNumber: z.number().int().positive()
});
function eurosToCents(value) {
  if (!value || value.trim() === "") {
    return void 0;
  }
  const euros = Number(value.trim().replace(",", "."));
  if (!Number.isFinite(euros) || euros < 0) {
    return void 0;
  }
  return Math.round(euros * 100);
}
function centsToEuroInput(cents2) {
  if (cents2 == null) {
    return "";
  }
  return (cents2 / 100).toFixed(2).replace(".", ",");
}
function matchApprovalWorkflow(workflows, ticket, categoryIds) {
  let best;
  let bestScore = -1;
  for (const workflow of workflows) {
    if (workflow.isActive === false) {
      continue;
    }
    if (workflow.ticketType && workflow.ticketType !== ticket.type) {
      continue;
    }
    if (workflow.categoryId && !categoryIds.has(workflow.categoryId)) {
      continue;
    }
    if (workflow.minPriority) {
      const needed = PRIORITY_RANK[workflow.minPriority];
      const actual = PRIORITY_RANK[ticket.priority];
      if (!needed || !actual || actual < needed) {
        continue;
      }
    }
    if (workflow.minValueCents != null && workflow.minValueCents > 0) {
      if ((ticket.estimatedValueCents ?? 0) < workflow.minValueCents) {
        continue;
      }
    }
    const hasRule = Boolean(workflow.ticketType) || Boolean(workflow.categoryId) || Boolean(workflow.minPriority) || workflow.minValueCents != null && workflow.minValueCents > 0;
    if (!hasRule) {
      continue;
    }
    let score = 0;
    if (workflow.ticketType) score += 8;
    if (workflow.categoryId) score += 16;
    if (workflow.minPriority) score += 4;
    if (workflow.minValueCents != null && workflow.minValueCents > 0) score += 2;
    if (score > bestScore) {
      best = workflow;
      bestScore = score;
    }
  }
  return best;
}
function currentSequentialApproverId(namedApprovers, decisions) {
  const ordered = [...namedApprovers].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.userId.localeCompare(b.userId)
  );
  for (const approver of ordered) {
    const existing = decisions.find((item) => item.approverId === approver.userId);
    if (!existing) {
      return approver.userId;
    }
    if (existing.decision === ApprovalDecisionValue.REJECTED) {
      return null;
    }
  }
  return null;
}
function canUserDecideStep(input) {
  if (input.decisions.some((item) => item.approverId === input.actorId)) {
    return false;
  }
  const namedIds = new Set(input.namedApprovers.map((item) => item.userId));
  const roleIds = new Set(input.roleMemberIds);
  const eligible = namedIds.has(input.actorId) || roleIds.has(input.actorId);
  if (!eligible) {
    return false;
  }
  if (input.mode === ApprovalMode.PARALLEL || input.namedApprovers.length === 0) {
    return true;
  }
  if (namedIds.has(input.actorId)) {
    return currentSequentialApproverId(input.namedApprovers, input.decisions) === input.actorId;
  }
  return roleIds.has(input.actorId);
}
function stepOutcome(input) {
  if (input.decisions.some((item) => item.decision === ApprovalDecisionValue.REJECTED)) {
    return ApprovalState.REJECTED;
  }
  const approved = input.decisions.filter(
    (item) => item.decision === ApprovalDecisionValue.APPROVED
  ).length;
  if (approved >= input.minApprovals) {
    return ApprovalState.APPROVED;
  }
  const namedPending = input.namedApprovers.some(
    (approver) => !input.decisions.some((item) => item.approverId === approver.userId)
  );
  if (!namedPending && approved >= input.minApprovals) {
    return ApprovalState.APPROVED;
  }
  return ApprovalState.PENDING;
}
var ChangeCollisionReason = {
  SERVICE: "service",
  ASSET: "asset",
  WINDOW: "window"
};
var changeCollisionReasonSchema = z.enum(ChangeCollisionReason);
var ChangeCollisionSeverity = {
  WARNING: "warning",
  STRONG: "strong"
};
var changeCollisionSeveritySchema = z.enum(ChangeCollisionSeverity);
var changeCollisionWarningSchema = z.object({
  ticketId: z.string().uuid(),
  number: z.number().int().positive(),
  subject: z.string(),
  risk: changeRiskSchema,
  reason: changeCollisionReasonSchema,
  severity: changeCollisionSeveritySchema,
  message: z.string(),
  sharedServices: z.array(z.string()).default([])
});
function windowsOverlap(aStart, aEnd, bStart, bEnd) {
  const a0 = new Date(aStart).getTime();
  const a1 = new Date(aEnd).getTime();
  const b0 = new Date(bStart).getTime();
  const b1 = new Date(bEnd).getTime();
  if (![a0, a1, b0, b1].every(Number.isFinite)) {
    return false;
  }
  return a0 < b1 && a1 > b0;
}
function normalizeServiceKey(value) {
  return value.trim().toLowerCase();
}
function sharedAffectedServices(a, b) {
  const other = new Set(b.map(normalizeServiceKey).filter(Boolean));
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const item of a) {
    const key = normalizeServiceKey(item);
    if (!key || !other.has(key) || seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(item.trim());
  }
  return out;
}
function sharedAssetIds(a, b) {
  const other = new Set(b);
  return [...new Set(a.filter((id) => other.has(id)))];
}
function buildChangeCollisionWarnings(self, others, options = {}) {
  const limit = options.limit ?? 8;
  const preferred = [];
  const fallback = [];
  for (const other of others) {
    if (!windowsOverlap(
      self.scheduledStart,
      self.scheduledEnd,
      other.scheduledStart,
      other.scheduledEnd
    )) {
      continue;
    }
    const services = sharedAffectedServices(self.affectedServices, other.affectedServices);
    const assets = sharedAssetIds(self.assetIds, other.assetIds);
    if (services.length > 0) {
      const strong = self.risk === "HIGH" || other.risk === "HIGH";
      preferred.push({
        ticketId: other.ticketId,
        number: other.number,
        subject: other.subject,
        risk: other.risk,
        reason: ChangeCollisionReason.SERVICE,
        severity: strong ? ChangeCollisionSeverity.STRONG : ChangeCollisionSeverity.WARNING,
        sharedServices: services,
        message: strong ? `Stark: hohes Risiko und gleicher Service wie #${other.number} \u201E${other.subject}\u201C (${services.join(", ")}).` : `\xDCberlappt mit #${other.number} \u201E${other.subject}\u201C (gleicher Service: ${services.join(", ")}).`
      });
      continue;
    }
    if (assets.length > 0) {
      preferred.push({
        ticketId: other.ticketId,
        number: other.number,
        subject: other.subject,
        risk: other.risk,
        reason: ChangeCollisionReason.ASSET,
        severity: ChangeCollisionSeverity.WARNING,
        sharedServices: [],
        message: `\xDCberlappt mit #${other.number} \u201E${other.subject}\u201C (gemeinsames Asset).`
      });
      continue;
    }
    fallback.push({
      ticketId: other.ticketId,
      number: other.number,
      subject: other.subject,
      risk: other.risk,
      reason: ChangeCollisionReason.WINDOW,
      severity: ChangeCollisionSeverity.WARNING,
      sharedServices: [],
      message: `\xDCberlappt zeitlich mit #${other.number} \u201E${other.subject}\u201C.`
    });
  }
  const ranked = preferred.length > 0 ? preferred : fallback;
  return ranked.slice(0, limit);
}
var EscalationTrigger = {
  FIRST_RESPONSE_WARNING: "FIRST_RESPONSE_WARNING",
  FIRST_RESPONSE_BREACH: "FIRST_RESPONSE_BREACH",
  RESOLUTION_WARNING: "RESOLUTION_WARNING",
  RESOLUTION_BREACH: "RESOLUTION_BREACH"
};
var escalationTriggerSchema = z.enum(EscalationTrigger);
var ESCALATION_TRIGGER_LABELS = {
  FIRST_RESPONSE_WARNING: "Warnung erste Reaktion",
  FIRST_RESPONSE_BREACH: "Verletzung erste Reaktion",
  RESOLUTION_WARNING: "Warnung L\xF6sungszeit",
  RESOLUTION_BREACH: "Verletzung L\xF6sungszeit"
};
var hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
var businessIntervalSchema = z.object({
  start: hhmm,
  end: hhmm
});
var weeklyHoursSchema = z.object({
  "1": z.array(businessIntervalSchema),
  "2": z.array(businessIntervalSchema),
  "3": z.array(businessIntervalSchema),
  "4": z.array(businessIntervalSchema),
  "5": z.array(businessIntervalSchema),
  "6": z.array(businessIntervalSchema),
  "7": z.array(businessIntervalSchema)
}).refine(
  (hours) => Object.values(hours).every(
    (intervals) => intervals.every((interval) => interval.start < interval.end)
  ),
  { message: "Jede Zeitspanne muss eine Startzeit vor der Endzeit haben." }
);
var WEEKDAY_LABELS = {
  "1": "Montag",
  "2": "Dienstag",
  "3": "Mittwoch",
  "4": "Donnerstag",
  "5": "Freitag",
  "6": "Samstag",
  "7": "Sonntag"
};
var calendarHolidaySchema = z.object({
  id: z.string().uuid(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  name: z.string()
});
var businessCalendarSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  timezone: z.string(),
  weeklyHours: weeklyHoursSchema,
  holidays: z.array(calendarHolidaySchema),
  updatedAt: z.iso.datetime()
});
var businessCalendarListResponseSchema = z.object({
  items: z.array(businessCalendarSchema)
});
var okResponseSchema = z.object({ ok: z.literal(true) });
var updateBusinessCalendarRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  timezone: z.string().trim().min(1).max(80).optional(),
  weeklyHours: weeklyHoursSchema.optional()
});
var createCalendarHolidayRequestSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  name: z.string().trim().min(1).max(120)
});
var slaEscalationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  trigger: escalationTriggerSchema,
  offsetMinutes: z.number().int(),
  notifyRoleId: z.string().uuid().nullable(),
  notifyRoleName: z.string().nullable(),
  raisePriorityTo: ticketPrioritySchema.nullable(),
  reassignQueueId: z.string().uuid().nullable(),
  reassignQueueName: z.string().nullable(),
  isActive: z.boolean()
});
var upsertSlaEscalationRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  trigger: escalationTriggerSchema,
  offsetMinutes: z.number().int().min(0).max(10080),
  notifyRoleId: z.string().uuid().nullable().optional(),
  raisePriorityTo: ticketPrioritySchema.nullable().optional(),
  reassignQueueId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().optional()
});
var slaPolicySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  ticketType: ticketTypeSchema.nullable(),
  priority: ticketPrioritySchema.nullable(),
  categoryId: z.string().uuid().nullable(),
  categoryName: z.string().nullable(),
  queueId: z.string().uuid().nullable(),
  queueName: z.string().nullable(),
  calendarId: z.string().uuid(),
  calendarName: z.string(),
  firstResponseMinutes: z.number().int().positive(),
  resolutionMinutes: z.number().int().positive(),
  escalateToRoleId: z.string().uuid().nullable(),
  escalateToUserId: z.string().uuid().nullable(),
  escalations: z.array(slaEscalationSchema),
  updatedAt: z.iso.datetime()
});
var upsertSlaPolicyRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  isDefault: z.boolean().optional(),
  isActive: z.boolean().optional(),
  ticketType: ticketTypeSchema.nullable().optional(),
  priority: ticketPrioritySchema.nullable().optional(),
  categoryId: z.string().uuid().nullable().optional(),
  queueId: z.string().uuid().nullable().optional(),
  calendarId: z.string().uuid(),
  firstResponseMinutes: z.number().int().min(1).max(1e5),
  resolutionMinutes: z.number().int().min(1).max(5e5),
  escalateToRoleId: z.string().uuid().nullable().optional(),
  escalateToUserId: z.string().uuid().nullable().optional()
});
var slaPolicyListResponseSchema = z.object({
  items: z.array(slaPolicySchema)
});
var slaLookupsSchema = z.object({
  calendars: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  categories: z.array(
    z.object({ id: z.string().uuid(), name: z.string(), parentId: z.string().uuid().nullable() })
  ),
  queues: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  roles: z.array(z.object({ id: z.string().uuid(), code: z.string(), name: z.string() }))
});
var ticketSlaSchema = z.object({
  policyId: z.string().uuid(),
  policyName: z.string(),
  firstResponseDueAt: z.iso.datetime().nullable(),
  resolutionDueAt: z.iso.datetime().nullable(),
  paused: z.boolean(),
  pausedAt: z.iso.datetime().nullable(),
  firstResponseBreached: z.boolean(),
  resolutionBreached: z.boolean(),
  firstResponseMinutes: z.number().int(),
  resolutionMinutes: z.number().int()
});
var SLA_DUE_SOON_MINUTES = 30;
var TicketSlaRisk = {
  /** Breached or due within {@link SLA_DUE_SOON_MINUTES} (active clock only for due-soon). */
  AT_RISK: "at_risk",
  BREACHED: "breached",
  DUE_SOON: "due_soon"
};
var ticketSlaRiskSchema = z.enum(TicketSlaRisk);
var OLLAMA_CLASSIFY_CONFIDENCE_DEFAULT = 0.8;
var TicketAiDecision = {
  PENDING: "PENDING",
  APPLIED: "APPLIED",
  REJECTED: "REJECTED",
  MATCHED: "MATCHED"
};
var ticketAiDecisionSchema = z.enum(TicketAiDecision);
var ticketAiFieldSchema = z.enum(["category", "priority"]);
var ticketAiSuggestionActionSchema = z.object({
  fields: z.array(ticketAiFieldSchema).min(1).max(2).optional()
});
var ticketAiCategoryRefSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  parentId: z.string().uuid().nullable(),
  parentName: z.string().nullable()
});
var ticketAiSuggestionSchema = z.object({
  id: z.string().uuid(),
  category: ticketAiCategoryRefSchema.nullable(),
  priority: ticketPrioritySchema.nullable(),
  categoryConfidence: z.number().min(0).max(1),
  priorityConfidence: z.number().min(0).max(1),
  rationale: z.string().nullable(),
  pendingCategory: z.boolean(),
  pendingPriority: z.boolean(),
  categoryDecision: ticketAiDecisionSchema,
  priorityDecision: ticketAiDecisionSchema
});
var ticketAiStateSchema = z.object({
  classified: z.boolean(),
  categorySet: z.boolean(),
  prioritySet: z.boolean(),
  confidence: z.number().min(0).max(1).nullable(),
  suggestion: ticketAiSuggestionSchema.nullable()
});
var EMPTY_TICKET_AI_STATE = {
  classified: false,
  categorySet: false,
  prioritySet: false,
  confidence: null,
  suggestion: null
};
var classifyModelJsonSchema = z.object({
  categoryId: z.string().uuid().optional(),
  categorySlug: z.string().optional(),
  priority: ticketPrioritySchema.optional(),
  categoryConfidence: z.coerce.number().min(0).max(1).optional(),
  priorityConfidence: z.coerce.number().min(0).max(1).optional(),
  confidence: z.coerce.number().min(0).max(1).optional(),
  rationale: z.string().optional()
});
function extractJsonObject(raw) {
  const trimmed = raw.trim();
  const fenced = /```(?:json)?\s*([\s\S]*?)```/i.exec(trimmed);
  const candidate = fenced?.[1]?.trim() ?? trimmed;
  const start = candidate.indexOf("{");
  const end = candidate.lastIndexOf("}");
  if (start < 0 || end <= start) {
    return null;
  }
  try {
    return JSON.parse(candidate.slice(start, end + 1));
  } catch {
    return null;
  }
}
function parseTicketClassifyModelJson(raw) {
  const parsed = classifyModelJsonSchema.safeParse(extractJsonObject(raw));
  return parsed.success ? parsed.data : null;
}
function clampConfidence(value, fallback = 0) {
  if (value === void 0 || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.min(1, Math.max(0, value));
}
function isHighClassifyConfidence(score, threshold) {
  return score >= threshold;
}
function allowedPriorityCodes() {
  return [...ticketPrioritySchema.options];
}
var TicketCatalogSource = {
  USER: "USER",
  KEYWORD: "KEYWORD",
  AI: "AI",
  DEFAULT: "DEFAULT"
};
var ticketCatalogSourceSchema = z.enum(TicketCatalogSource);
var TICKET_CATALOG_SOURCE_LABELS = {
  USER: "Nutzerwahl",
  KEYWORD: "Stichwort",
  AI: "KI",
  DEFAULT: "Standard"
};
var TicketCatalogKeywordDecision = {
  KEYWORD: "KEYWORD",
  AMBIGUOUS: "AMBIGUOUS",
  NONE: "NONE"
};
var ticketCatalogKeywordDecisionSchema = z.enum(TicketCatalogKeywordDecision);
var TicketCatalogPreviewDecision = {
  KEYWORD: "KEYWORD",
  DEFAULT: "DEFAULT",
  AMBIGUOUS: "AMBIGUOUS",
  NONE: "NONE"
};
var ticketCatalogPreviewDecisionSchema = z.enum(TicketCatalogPreviewDecision);
var TicketCatalogKeywordKind = {
  CATEGORY: "category",
  TYPE: "type"
};
var ticketCatalogKeywordKindSchema = z.enum(TicketCatalogKeywordKind);
var CATALOG_KEYWORD_MIN_LENGTH = 2;
var CATALOG_KEYWORD_MAX_LENGTH = 80;
var CATALOG_KEYWORD_MAX_PER_ENTRY = 40;
var CATALOG_KEYWORD_AMBIGUOUS_MARGIN = 10;
var SYSTEM_TYPE_DEFINITION_IDS = {
  SERVICE_REQUEST: "00000000-0000-4000-8000-000000000071",
  INCIDENT: "00000000-0000-4000-8000-000000000072",
  PROBLEM: "00000000-0000-4000-8000-000000000073",
  CHANGE: "00000000-0000-4000-8000-000000000074"
};
var TICKET_CATALOG_SETTINGS_ID = "default";
var CATALOG_KEYWORD_STOPWORDS = [
  "der",
  "die",
  "das",
  "und",
  "oder",
  "bitte",
  "ticket",
  "problem",
  "hallo",
  "it"
];
var STOPWORD_SET = new Set(CATALOG_KEYWORD_STOPWORDS);
var ticketCatalogSlugSchema = z.string().trim().min(2).max(80).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug: nur Kleinbuchstaben, Ziffern und Bindestriche.");
var emptyToNull = (value) => value === "" || value === void 0 ? null : value;
function catalogKeywordListFromUnknown(value) {
  const rows = Array.isArray(value) ? value : typeof value === "string" ? value.split(/\n/) : value == null ? [] : [value];
  const seen = /* @__PURE__ */ new Set();
  const keywords = [];
  for (const row of rows) {
    if (typeof row !== "string") {
      continue;
    }
    const trimmed = row.trim();
    if (trimmed.length < CATALOG_KEYWORD_MIN_LENGTH || trimmed.length > CATALOG_KEYWORD_MAX_LENGTH) {
      continue;
    }
    if (isSingleTokenStopword(trimmed)) {
      continue;
    }
    const key = trimmed.toLocaleLowerCase("de-DE");
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    keywords.push(trimmed);
    if (keywords.length >= CATALOG_KEYWORD_MAX_PER_ENTRY) {
      break;
    }
  }
  return keywords;
}
var ticketCatalogKeywordListSchema = z.preprocess(
  catalogKeywordListFromUnknown,
  z.array(z.string().min(CATALOG_KEYWORD_MIN_LENGTH).max(CATALOG_KEYWORD_MAX_LENGTH)).max(CATALOG_KEYWORD_MAX_PER_ENTRY)
);
var optionalNullableUuid2 = z.preprocess(
  emptyToNull,
  z.union([z.string().uuid(), z.null()]).optional()
);
var ticketTypeDefinitionSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  itilType: ticketTypeSchema,
  isSystem: z.boolean(),
  isActive: z.boolean(),
  portalVisible: z.boolean(),
  sortOrder: z.number().int(),
  keywords: z.array(z.string()),
  createdAt: z.iso.datetime().optional(),
  updatedAt: z.iso.datetime().optional()
});
var ticketCatalogSettingsSchema = z.object({
  portalCategoryPickerEnabled: z.boolean(),
  portalTypePickerEnabled: z.boolean(),
  defaultPortalCategoryId: z.string().uuid().nullable(),
  defaultPortalTypeDefinitionId: z.string().uuid().nullable()
});
var ticketCategoryAdminSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  parentId: z.string().uuid().nullable(),
  defaultQueueId: z.string().uuid().nullable(),
  defaultAssigneeId: z.string().uuid().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  portalVisible: z.boolean(),
  description: z.string().nullable(),
  keywords: z.array(z.string()),
  createdAt: z.iso.datetime().optional(),
  updatedAt: z.iso.datetime().optional()
});
var ticketCatalogAdminResponseSchema = z.object({
  settings: ticketCatalogSettingsSchema,
  categories: z.array(ticketCategoryAdminSchema),
  typeDefinitions: z.array(ticketTypeDefinitionSchema)
});
var DEFAULT_TICKET_CATALOG_SETTINGS = {
  portalCategoryPickerEnabled: true,
  portalTypePickerEnabled: true,
  defaultPortalCategoryId: null,
  defaultPortalTypeDefinitionId: null
};
var createTicketCategoryRequestSchema = z.object({
  name: z.string().trim().min(2).max(80),
  slug: ticketCatalogSlugSchema.optional(),
  parentId: optionalNullableUuid2,
  defaultQueueId: optionalNullableUuid2,
  defaultAssigneeId: optionalNullableUuid2,
  sortOrder: z.number().int().min(0).max(1e4).optional(),
  isActive: z.boolean().optional(),
  portalVisible: z.boolean().optional(),
  description: z.preprocess(emptyToNull, z.string().trim().max(500).nullable().optional()),
  keywords: ticketCatalogKeywordListSchema.optional()
});
var updateTicketCategoryRequestSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  parentId: optionalNullableUuid2,
  defaultQueueId: optionalNullableUuid2,
  defaultAssigneeId: optionalNullableUuid2,
  sortOrder: z.number().int().min(0).max(1e4).optional(),
  isActive: z.boolean().optional(),
  portalVisible: z.boolean().optional(),
  description: z.preprocess(emptyToNull, z.string().trim().max(500).nullable().optional()),
  keywords: ticketCatalogKeywordListSchema.optional()
});
var createTicketTypeDefinitionRequestSchema = z.object({
  name: z.string().trim().min(2).max(80),
  slug: ticketCatalogSlugSchema.optional(),
  description: z.preprocess(emptyToNull, z.string().trim().max(500).nullable().optional()),
  portalVisible: z.boolean().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(1e4).optional(),
  keywords: ticketCatalogKeywordListSchema.optional()
});
var updateTicketTypeDefinitionRequestSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  description: z.preprocess(emptyToNull, z.string().trim().max(500).nullable().optional()),
  portalVisible: z.boolean().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(1e4).optional(),
  keywords: ticketCatalogKeywordListSchema.optional()
});
var updateTicketCatalogSettingsRequestSchema = z.object({
  portalCategoryPickerEnabled: z.boolean().optional(),
  portalTypePickerEnabled: z.boolean().optional(),
  defaultPortalCategoryId: optionalNullableUuid2,
  defaultPortalTypeDefinitionId: optionalNullableUuid2
});
var classifyPreviewRequestSchema = z.object({
  subject: z.string().max(200).default(""),
  description: z.string().max(2e4).default("")
});
var classifyPreviewResponseSchema = z.object({
  categoryId: z.string().uuid().nullable(),
  categoryName: z.string().nullable(),
  categoryDecision: ticketCatalogPreviewDecisionSchema,
  typeDefinitionId: z.string().uuid().nullable(),
  typeName: z.string().nullable(),
  typeDecision: ticketCatalogPreviewDecisionSchema,
  matchedKeywords: z.array(z.string())
});
var catalogKeywordEntrySchema = z.object({
  id: z.string().min(1),
  keywords: z.array(z.string()),
  kind: ticketCatalogKeywordKindSchema,
  weight: z.number().finite().optional()
});
function combineCatalogMatchText(subject, description) {
  return `${subject} ${plainTextFromHtml(description)}`.replace(/\s+/g, " ").trim();
}
function foldGerman(value) {
  return value.replaceAll("\xE4", "ae").replaceAll("\xF6", "oe").replaceAll("\xFC", "ue").replaceAll("\xDF", "ss");
}
function tokenizeCatalogText(value) {
  const nfc = plainTextFromHtml(value).normalize("NFC").toLocaleLowerCase("de-DE");
  const stripped = nfc.replace(/[^\p{L}\p{N}-]+/gu, " ").replace(/\s+/g, " ").trim();
  if (!stripped) {
    return { raw: [], folded: [] };
  }
  const raw = stripped.split(" ");
  return { raw, folded: raw.map(foldGerman) };
}
function containsPhrase(haystack, needle) {
  if (needle.length === 0 || haystack.length < needle.length) {
    return false;
  }
  for (let start = 0; start <= haystack.length - needle.length; start += 1) {
    let ok = true;
    for (let offset = 0; offset < needle.length; offset += 1) {
      if (haystack[start + offset] !== needle[offset]) {
        ok = false;
        break;
      }
    }
    if (ok) {
      return true;
    }
  }
  return false;
}
function isSingleTokenStopword(value) {
  const tokens = tokenizeCatalogText(value);
  return tokens.raw.length === 1 && (STOPWORD_SET.has(tokens.raw[0] ?? "") || STOPWORD_SET.has(tokens.folded[0] ?? ""));
}
function prepareKeyword(rawKeyword) {
  const original = rawKeyword.trim();
  if (original.length < CATALOG_KEYWORD_MIN_LENGTH || original.length > CATALOG_KEYWORD_MAX_LENGTH) {
    return null;
  }
  const tokens = tokenizeCatalogText(original);
  if (tokens.raw.length === 0 || isSingleTokenStopword(original)) {
    return null;
  }
  return { original, raw: tokens.raw, folded: tokens.folded };
}
function scoreKeywordHit(tokenCount) {
  return tokenCount * 10 + (tokenCount > 1 ? 3 : 0);
}
function matchCatalogKeywords(text, entries) {
  const empty = {
    decision: TicketCatalogKeywordDecision.NONE,
    id: null,
    kind: null,
    score: 0,
    matchedKeywords: [],
    runnerUpId: null,
    runnerUpScore: 0
  };
  const haystack = tokenizeCatalogText(text);
  if (haystack.raw.length === 0 || entries.length === 0) {
    return empty;
  }
  const scored = [];
  for (const entry of entries) {
    const prepared = (entry.keywords ?? []).slice(0, CATALOG_KEYWORD_MAX_PER_ENTRY).map(prepareKeyword).filter((item) => item != null);
    const seenFolded = /* @__PURE__ */ new Set();
    let score = 0;
    const matchedKeywords = [];
    for (const keyword of prepared) {
      const foldKey = keyword.folded.join(" ");
      if (seenFolded.has(foldKey)) {
        continue;
      }
      seenFolded.add(foldKey);
      const hit = containsPhrase(haystack.raw, keyword.raw) || containsPhrase(haystack.folded, keyword.folded);
      if (!hit) {
        continue;
      }
      score += scoreKeywordHit(keyword.raw.length);
      matchedKeywords.push(keyword.original);
    }
    if (matchedKeywords.length === 0 && !(entry.weight && entry.weight !== 0)) {
      continue;
    }
    if (matchedKeywords.length === 0) {
      continue;
    }
    score += entry.weight ?? 0;
    scored.push({ id: entry.id, kind: entry.kind, score, matchedKeywords });
  }
  scored.sort((a, b) => b.score - a.score || a.id.localeCompare(b.id));
  const winner = scored[0];
  if (!winner || winner.score <= 0) {
    return empty;
  }
  const runnerUp = scored[1];
  const runnerUpScore = runnerUp?.score ?? 0;
  const tied = runnerUp != null && runnerUp.id !== winner.id && (runnerUpScore === winner.score || winner.score - runnerUpScore < CATALOG_KEYWORD_AMBIGUOUS_MARGIN);
  if (tied) {
    return {
      decision: TicketCatalogKeywordDecision.AMBIGUOUS,
      id: null,
      kind: null,
      score: winner.score,
      matchedKeywords: [],
      runnerUpId: runnerUp.id,
      runnerUpScore
    };
  }
  return {
    decision: TicketCatalogKeywordDecision.KEYWORD,
    id: winner.id,
    kind: winner.kind,
    score: winner.score,
    matchedKeywords: winner.matchedKeywords,
    runnerUpId: runnerUp?.id ?? null,
    runnerUpScore
  };
}

// src/ticket-api.ts
var emptyToUndefined = (value) => value === "" || value === "all" ? void 0 : value;
function optionalMultiValues(item, max = 50) {
  return z.preprocess((value) => {
    if (value === "" || value === void 0 || value === null || value === "all") {
      return void 0;
    }
    const raw = Array.isArray(value) ? value : [value];
    const parts = raw.flatMap((entry) => {
      if (typeof entry !== "string") {
        return [entry];
      }
      return entry.split(",").map((part) => part.trim());
    });
    const cleaned = [
      ...new Set(
        parts.filter(
          (entry) => entry !== "" && entry !== "all" && entry !== void 0 && entry !== null
        )
      )
    ];
    return cleaned.length > 0 ? cleaned : void 0;
  }, z.array(item).min(1).max(max).optional());
}
var optionalNullableUuid3 = z.union([z.string().uuid(), z.null()]).optional();
var optionalIsoDatetime = z.preprocess(
  (value) => value === "" || value === null ? void 0 : value,
  z.iso.datetime().optional()
);
var affectedServicesInputSchema = z.preprocess(
  (value) => {
    if (value === void 0) {
      return void 0;
    }
    return parseAffectedServices(value);
  },
  z.array(z.string().trim().min(1).max(CHANGE_AFFECTED_SERVICE_MAX_LENGTH)).max(CHANGE_AFFECTED_SERVICES_MAX).optional()
);
var ticketPersonSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string().email(),
  /** Servable profile photo, or null when the person has none. */
  profilePhotoUrl: z.string().nullable().optional()
});
var ticketCategoryRefSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  parentId: z.string().uuid().nullable(),
  parentName: z.string().nullable()
});
var ticketQueueRefSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string()
});
var ticketListItemSchema = z.object({
  id: z.string().uuid(),
  number: z.number().int().positive(),
  type: ticketTypeSchema,
  status: ticketStatusSchema,
  priority: ticketPrioritySchema,
  subject: z.string(),
  requester: ticketPersonSchema,
  assignee: ticketPersonSchema.nullable(),
  category: ticketCategoryRefSchema.nullable(),
  queue: ticketQueueRefSchema.nullable(),
  approvalStatus: approvalStateSchema.nullable(),
  isMajor: z.boolean(),
  problemLifecycle: problemLifecycleSchema.nullable(),
  changeRisk: changeRiskSchema.nullable(),
  changeScheduledStart: z.iso.datetime().nullable(),
  changeScheduledEnd: z.iso.datetime().nullable(),
  /** Free-form labels, mostly set by automation rules (Phase 21). */
  tags: z.array(z.string()),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var ticketIncidentRefSchema = z.object({
  id: z.string().uuid(),
  number: z.number().int().positive(),
  subject: z.string(),
  status: ticketStatusSchema,
  requester: ticketPersonSchema.nullable()
});
var ticketIncidentStateSchema = z.object({
  severity: incidentSeveritySchema,
  impact: impactLevelSchema,
  urgency: urgencyLevelSchema,
  isMajor: z.boolean(),
  broadcastNote: z.string().nullable(),
  lead: ticketIncidentRefSchema.nullable(),
  children: z.array(ticketIncidentRefSchema),
  problem: ticketIncidentRefSchema.nullable()
});
var ticketProblemKbProposalSchema = z.object({
  id: z.string().uuid(),
  proposedTitle: z.string(),
  status: z.string()
});
var ticketProblemStateSchema = z.object({
  lifecycle: problemLifecycleSchema,
  allowedLifecycles: z.array(problemLifecycleSchema),
  rca: problemRcaSchema,
  workaround: z.string().nullable(),
  resolution: z.string().nullable(),
  knownError: z.boolean(),
  incidents: z.array(ticketIncidentRefSchema),
  kbProposal: ticketProblemKbProposalSchema.nullable()
});
var ticketChangeAssetSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  inventoryTag: z.string().nullable()
});
var ticketChangeStateSchema = z.object({
  risk: changeRiskSchema,
  rollbackPlan: z.string().nullable(),
  scheduledStart: z.iso.datetime().nullable(),
  scheduledEnd: z.iso.datetime().nullable(),
  affectedServices: z.array(z.string()),
  implementationNotes: z.string().nullable(),
  assets: z.array(ticketChangeAssetSchema),
  approvalRequired: z.boolean(),
  approved: z.boolean()
});
var ticketLifecycleRefSchema = z.object({
  id: z.string().uuid(),
  number: z.number().int().positive(),
  subject: z.string(),
  status: ticketStatusSchema
});
var ticketLifecycleSchema = z.object({
  autoCloseDays: z.number().int().positive(),
  reopenWindowDays: z.number().int().positive(),
  autoCloseAt: z.iso.datetime().nullable(),
  autoCloseReminderAt: z.iso.datetime().nullable(),
  reopenUntil: z.iso.datetime().nullable(),
  canReopen: z.boolean(),
  followUpSuggested: z.boolean(),
  followUpOf: ticketLifecycleRefSchema.nullable(),
  followUps: z.array(ticketLifecycleRefSchema)
});
var ticketDetailSchema = ticketListItemSchema.extend({
  description: z.string(),
  source: ticketSourceSchema,
  requesterId: z.string().uuid(),
  createdBy: ticketPersonSchema,
  closedBy: ticketPersonSchema.nullable(),
  slaPolicyId: z.string().uuid().nullable(),
  firstResponseAt: z.iso.datetime().nullable(),
  resolvedAt: z.iso.datetime().nullable(),
  closedAt: z.iso.datetime().nullable(),
  reopenedAt: z.iso.datetime().nullable(),
  estimatedValueCents: z.number().int().nonnegative().nullable(),
  allowedTransitions: z.array(ticketStatusSchema),
  sla: ticketSlaSchema.nullable(),
  approval: ticketApprovalSchema.nullable(),
  ai: ticketAiStateSchema,
  incident: ticketIncidentStateSchema.nullable(),
  problem: ticketProblemStateSchema.nullable(),
  change: ticketChangeStateSchema.nullable(),
  lifecycle: ticketLifecycleSchema,
  /** Soft collision hints after create/update of a Change (optional). */
  changeWarnings: z.array(changeCollisionWarningSchema).optional()
});
var ticketListSortBySchema = z.enum([
  "number",
  "createdAt",
  "updatedAt",
  "priority",
  "status",
  "subject",
  /** Soonest SLA due date / breached first when sortOrder=asc (urgency). */
  "slaDueAt"
]);
var ticketListQuerySchema = paginationQuerySchema.extend({
  q: z.preprocess(emptyToUndefined, z.string().trim().max(200).optional()),
  status: optionalMultiValues(ticketStatusSchema),
  priority: optionalMultiValues(ticketPrioritySchema),
  type: optionalMultiValues(ticketTypeSchema),
  categoryId: optionalMultiValues(z.string().uuid()),
  queueId: optionalMultiValues(z.string().uuid()),
  assigneeId: optionalMultiValues(z.string().uuid()),
  requesterId: z.preprocess(emptyToUndefined, z.string().uuid().optional()),
  unassigned: z.preprocess((value) => {
    if (value === "" || value === void 0 || value === "all") {
      return void 0;
    }
    if (value === true || value === "true" || value === "1") {
      return true;
    }
    if (value === false || value === "false" || value === "0") {
      return false;
    }
    return value;
  }, z.boolean().optional()),
  createdFrom: z.preprocess(
    emptyToUndefined,
    z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional()
  ),
  createdTo: z.preprocess(
    emptyToUndefined,
    z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional()
  ),
  isMajor: z.preprocess((value) => {
    if (value === "" || value === void 0 || value === "all") {
      return void 0;
    }
    if (value === true || value === "true" || value === "1") {
      return true;
    }
    if (value === false || value === "false" || value === "0") {
      return false;
    }
    return value;
  }, z.boolean().optional()),
  problemLifecycle: optionalMultiValues(problemLifecycleSchema),
  /** Filter CHANGE tickets by whether a planned window is set (both start and end). */
  changeWindow: z.preprocess(emptyToUndefined, z.enum(["none", "set"]).optional()),
  /** Filter by Change risk (only meaningful with type=CHANGE). */
  changeRisk: optionalMultiValues(changeRiskSchema),
  /**
   * Filter by CAB approval state for the ticket's approval request.
   * `approved` / `pending` / `rejected` map to ApprovalState.
   */
  changeApproval: z.preprocess(
    emptyToUndefined,
    z.enum(["approved", "pending", "rejected"]).optional()
  ),
  /**
   * Filter by SLA urgency on the ticket's `Sla` row.
   * `at_risk` = breached or due within SLA_DUE_SOON_MINUTES (unpaused).
   */
  slaRisk: z.preprocess(emptyToUndefined, ticketSlaRiskSchema.optional()),
  sortBy: ticketListSortBySchema.default("createdAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var ticketListResponseSchema = paginatedSchema(ticketListItemSchema);
var createTicketRequestSchema = z.object({
  type: ticketTypeSchema.optional(),
  typeDefinitionId: z.string().uuid().optional(),
  subject: z.string().trim().min(3).max(200),
  description: z.string().max(2e4).refine((value) => plainTextFromHtml(value).length >= 1, {
    message: "helpdesk.ticket.describe.required"
  }),
  priority: ticketPrioritySchema.default("MEDIUM"),
  categoryId: z.string().uuid().optional(),
  requesterId: z.string().uuid().optional(),
  assigneeId: z.string().uuid().optional(),
  queueId: z.string().uuid().optional(),
  source: ticketSourceSchema.optional(),
  estimatedValueCents: z.number().int().nonnegative().max(1e8).optional(),
  followUpOfTicketId: z.string().uuid().optional(),
  impact: impactLevelSchema.optional(),
  urgency: urgencyLevelSchema.optional(),
  severity: incidentSeveritySchema.optional(),
  risk: changeRiskSchema.optional(),
  rollbackPlan: z.string().max(2e4).optional(),
  scheduledStart: optionalIsoDatetime,
  scheduledEnd: optionalIsoDatetime,
  affectedServices: affectedServicesInputSchema
}).superRefine((value, ctx) => {
  if (value.type !== "CHANGE") {
    return;
  }
  if (!isValidChangeWindow(value.scheduledStart, value.scheduledEnd)) {
    ctx.addIssue({
      code: "custom",
      path: ["scheduledEnd"],
      message: "helpdesk.change.window.required"
    });
  }
  if (value.risk === "HIGH" && plainTextFromHtml(value.rollbackPlan ?? "").length < 1) {
    ctx.addIssue({
      code: "custom",
      path: ["rollbackPlan"],
      message: "Bei hohem Risiko ist ein Rollback-Plan Pflicht."
    });
  }
});
var createTicketFormChangeRefine = (value, ctx) => {
  if (value.type !== "CHANGE") {
    return;
  }
  const startRaw = value.scheduledStart?.trim() ?? "";
  const endRaw = value.scheduledEnd?.trim() ?? "";
  const startIso = startRaw ? fromChangeDatetimeLocalValue(startRaw) : null;
  const endIso = endRaw ? fromChangeDatetimeLocalValue(endRaw) : null;
  if (startRaw && !startIso) {
    ctx.addIssue({
      code: "custom",
      path: ["scheduledStart"],
      message: "helpdesk.change.start.invalid"
    });
  }
  if (endRaw && !endIso) {
    ctx.addIssue({
      code: "custom",
      path: ["scheduledEnd"],
      message: "helpdesk.change.end.invalid"
    });
  }
  if (!isValidChangeWindow(startIso, endIso)) {
    ctx.addIssue({
      code: "custom",
      path: ["scheduledEnd"],
      message: "helpdesk.change.window.required"
    });
  }
  if (value.changeRisk === "HIGH" && plainTextFromHtml(value.rollbackPlan ?? "").length < 1) {
    ctx.addIssue({
      code: "custom",
      path: ["rollbackPlan"],
      message: "Bei hohem Risiko ist ein Rollback-Plan Pflicht."
    });
  }
};
function buildCreateTicketFormSchema(options) {
  const categoryRequired = options?.categoryRequired ?? true;
  return z.object({
    type: ticketTypeSchema,
    subject: z.string().trim().min(3, "helpdesk.ticket.subject.min").max(200, "Der Betreff darf h\xF6chstens 200 Zeichen lang sein."),
    description: z.string().max(2e4, "Die Beschreibung ist zu lang.").refine((value) => plainTextFromHtml(value).length >= 1, {
      message: "helpdesk.ticket.describe.required"
    }),
    categoryId: z.string(),
    priority: ticketPrioritySchema,
    estimatedValue: z.string().optional().refine(
      (value) => {
        if (!value || value.trim() === "") {
          return true;
        }
        return /^\d+([.,]\d{1,2})?$/.test(value.trim());
      },
      { message: "helpdesk.ticket.amount" }
    ),
    impact: impactLevelSchema,
    urgency: urgencyLevelSchema,
    severity: incidentSeveritySchema,
    changeRisk: changeRiskSchema,
    rollbackPlan: z.string().max(2e4).optional(),
    scheduledStart: z.string().optional(),
    scheduledEnd: z.string().optional(),
    affectedServices: z.string().max(5e3).optional()
  }).superRefine((value, ctx) => {
    const categoryId = value.categoryId.trim();
    if (categoryRequired) {
      if (!z.string().uuid().safeParse(categoryId).success) {
        ctx.addIssue({
          code: "custom",
          path: ["categoryId"],
          message: "helpdesk.ticket.category.choose"
        });
      }
    } else if (categoryId !== "" && !z.string().uuid().safeParse(categoryId).success) {
      ctx.addIssue({
        code: "custom",
        path: ["categoryId"],
        message: "helpdesk.ticket.category.choose"
      });
    }
    createTicketFormChangeRefine(value, ctx);
  });
}
var createTicketFormSchema = buildCreateTicketFormSchema({ categoryRequired: true });
var updateTicketRequestSchema = z.object({
  subject: z.string().trim().min(3).max(200).optional(),
  description: z.string().trim().min(1).max(2e4).optional(),
  priority: ticketPrioritySchema.optional(),
  categoryId: optionalNullableUuid3,
  estimatedValueCents: z.number().int().nonnegative().max(1e8).nullable().optional()
});
var updateIncidentRequestSchema = z.object({
  severity: incidentSeveritySchema.optional(),
  impact: impactLevelSchema.optional(),
  urgency: urgencyLevelSchema.optional()
}).refine(
  (value) => value.severity !== void 0 || value.impact !== void 0 || value.urgency !== void 0,
  { message: "helpdesk.incident.field.min" }
);
var linkMajorIncidentChildRequestSchema = z.object({
  ticketId: z.string().uuid().optional(),
  ticketNumber: z.number().int().positive().optional()
}).refine((value) => Boolean(value.ticketId) || Boolean(value.ticketNumber), {
  message: "helpdesk.incident.child-ref"
});
var broadcastIncidentRequestSchema = z.object({
  body: z.string().max(2e4).refine((value) => plainTextFromHtml(value).length >= 1, {
    message: "helpdesk.ticket.notice.required"
  })
});
var updateProblemRequestSchema = z.object({
  lifecycle: problemLifecycleSchema.optional(),
  rca: problemRcaSchema.optional(),
  workaround: z.string().max(2e4).nullable().optional(),
  resolution: z.string().max(2e4).nullable().optional(),
  knownError: z.boolean().optional(),
  suggestKbDraft: z.boolean().optional()
}).refine(
  (value) => value.lifecycle !== void 0 || value.rca !== void 0 || value.workaround !== void 0 || value.resolution !== void 0 || value.knownError !== void 0 || value.suggestKbDraft !== void 0,
  { message: "helpdesk.problem.field.min" }
);
var linkProblemIncidentRequestSchema = linkMajorIncidentChildRequestSchema;
var nullableIsoDatetime = z.preprocess(
  (value) => value === "" ? null : value,
  z.iso.datetime().nullable().optional()
);
var updateChangeRequestSchema = z.object({
  risk: changeRiskSchema.optional(),
  rollbackPlan: z.string().max(2e4).nullable().optional(),
  scheduledStart: nullableIsoDatetime,
  scheduledEnd: nullableIsoDatetime,
  affectedServices: affectedServicesInputSchema,
  implementationNotes: z.string().max(2e4).nullable().optional()
}).refine(
  (value) => value.risk !== void 0 || value.rollbackPlan !== void 0 || value.scheduledStart !== void 0 || value.scheduledEnd !== void 0 || value.affectedServices !== void 0 || value.implementationNotes !== void 0,
  { message: "helpdesk.change.field.min" }
);
var changeCalendarQuerySchema = z.object({
  year: z.coerce.number().int().min(2e3).max(2100),
  month: z.coerce.number().int().min(1).max(12)
});
var changeCalendarItemSchema = z.object({
  id: z.string().uuid(),
  number: z.number().int().positive(),
  subject: z.string(),
  status: ticketStatusSchema,
  risk: changeRiskSchema,
  scheduledStart: z.iso.datetime(),
  scheduledEnd: z.iso.datetime(),
  assignee: ticketPersonSchema.nullable(),
  affectedServices: z.array(z.string()),
  assets: z.array(ticketChangeAssetSchema),
  approved: z.boolean()
});
var changeCalendarResponseSchema = z.object({
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  timezone: z.string(),
  rangeStart: z.iso.datetime(),
  rangeEnd: z.iso.datetime(),
  items: z.array(changeCalendarItemSchema)
});
var assignTicketRequestSchema = z.object({
  assigneeId: optionalNullableUuid3,
  queueId: optionalNullableUuid3,
  reason: z.string().trim().max(500).optional()
}).refine((value) => value.assigneeId !== void 0 || value.queueId !== void 0, {
  message: "helpdesk.ticket.assign.required"
});
var changeTicketStatusRequestSchema = z.object({
  status: ticketStatusSchema,
  comment: z.string().trim().max(2e3).optional()
});
var ticketCategoryNodeSchema = z.lazy(
  () => z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    parentId: z.string().uuid().nullable(),
    isActive: z.boolean(),
    portalVisible: z.boolean().default(true),
    description: z.string().nullable().default(null),
    keywords: z.array(z.string()).default([]),
    children: z.array(ticketCategoryNodeSchema)
  })
);
var ticketLookupsSchema = z.object({
  categories: z.array(ticketCategoryNodeSchema),
  queues: z.array(
    ticketQueueRefSchema.extend({
      isActive: z.boolean(),
      teamId: z.string().uuid().nullable()
    })
  ),
  agents: z.array(ticketPersonSchema),
  statuses: z.array(z.object({ code: ticketStatusSchema, label: z.string() })),
  priorities: z.array(
    z.object({ code: ticketPrioritySchema, label: z.string(), weight: z.number().int() })
  ),
  typeDefinitions: z.array(ticketTypeDefinitionSchema).default([]),
  catalogSettings: ticketCatalogSettingsSchema.default(DEFAULT_TICKET_CATALOG_SETTINGS)
});
var createTicketCommentRequestSchema = z.object({
  body: z.string().trim().min(1).max(4e3),
  isInternal: z.boolean().default(false)
});
var ticketCommentFormSchema = z.object({
  body: z.string().trim().min(1, "helpdesk.ticket.comment.required").max(4e3, "Der Kommentar darf h\xF6chstens 4000 Zeichen lang sein."),
  isInternal: z.boolean()
});
var ticketCommentSchema = z.object({
  id: z.string().uuid(),
  body: z.string(),
  isInternal: z.boolean(),
  author: ticketPersonSchema,
  createdAt: z.iso.datetime()
});
var ticketCommentsResponseSchema = z.object({
  items: z.array(ticketCommentSchema)
});
var ticketAttachmentSchema = z.object({
  id: z.string().uuid(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  scanStatus: attachmentScanStatusSchema,
  uploadedBy: ticketPersonSchema,
  createdAt: z.iso.datetime()
});
var ticketAttachmentsResponseSchema = z.object({
  items: z.array(ticketAttachmentSchema)
});
var ticketHistoryItemSchema = z.object({
  id: z.string().uuid(),
  action: z.string(),
  summary: z.string(),
  actor: ticketPersonSchema.nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  metadata: z.unknown().nullable(),
  createdAt: z.iso.datetime()
});
var ticketHistoryResponseSchema = z.object({
  items: z.array(ticketHistoryItemSchema)
});
var UNASSIGNED_QUEUE_COLUMN = "unassigned";
var queueBoardTicketSchema = ticketListItemSchema;
var queueBoardColumnSchema = z.object({
  id: z.string(),
  queue: ticketQueueRefSchema.extend({
    isActive: z.boolean(),
    sortOrder: z.number().int(),
    teamId: z.string().uuid().nullable(),
    defaultAssigneeId: z.string().uuid().nullable()
  }).nullable(),
  openCount: z.number().int().nonnegative(),
  tickets: z.array(queueBoardTicketSchema),
  /** Soft warnings for intake notifications (no team / empty team). */
  healthHints: z.array(z.string()).default([])
});
var queueBoardSchema = z.object({
  columns: z.array(queueBoardColumnSchema)
});
var reorderQueuesRequestSchema = z.object({
  queueIds: z.array(z.string().uuid()).min(1)
});
var queueSlugSchema = z.string().trim().min(2).max(80).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug: nur Kleinbuchstaben, Ziffern und Bindestriche.");
var emptyToNullQueue = (value) => value === "" || value === void 0 ? null : value;
var optionalNullableQueueUuid = z.preprocess(
  emptyToNullQueue,
  z.union([z.string().uuid(), z.null()]).optional()
);
var managedQueueSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  teamId: z.string().uuid().nullable(),
  defaultAssigneeId: z.string().uuid().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var managedQueueListItemSchema = managedQueueSchema.extend({
  ticketCount: z.number().int().nonnegative(),
  openCount: z.number().int().nonnegative(),
  /** True when inactive and no tickets still reference the queue. */
  canDelete: z.boolean()
});
var managedQueueListSchema = z.object({
  items: z.array(managedQueueListItemSchema)
});
var createQueueRequestSchema = z.object({
  name: z.string().trim().min(2).max(80),
  slug: queueSlugSchema,
  teamId: optionalNullableQueueUuid,
  defaultAssigneeId: optionalNullableQueueUuid
});
var updateQueueRequestSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  slug: queueSlugSchema.optional(),
  teamId: optionalNullableQueueUuid,
  defaultAssigneeId: optionalNullableQueueUuid,
  isActive: z.boolean().optional()
}).refine(
  (value) => value.name !== void 0 || value.slug !== void 0 || value.teamId !== void 0 || value.defaultAssigneeId !== void 0 || value.isActive !== void 0,
  { message: "Mindestens ein Feld muss gesetzt sein." }
);
function asRecord(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value;
  }
  return {};
}
function summarizeTicketHistoryItem(item) {
  const metadata = asRecord(item.metadata);
  const after = asRecord(item.after);
  const before = asRecord(item.before);
  if (item.action === TicketAuditAction.CREATED) {
    return "Ticket wurde angelegt.";
  }
  if (item.action === TicketAuditAction.STATUS_CHANGED) {
    const from = ticketStatusSchema.safeParse(metadata.from);
    const to = ticketStatusSchema.safeParse(metadata.to);
    const parts = [];
    if (from.success && to.success) {
      parts.push(`${TICKET_STATUS_LABELS[from.data]} \u2192 ${TICKET_STATUS_LABELS[to.data]}`);
    }
    if (typeof metadata.comment === "string" && metadata.comment.trim()) {
      parts.push(metadata.comment.trim());
    }
    return parts.join(" \xB7 ") || TICKET_AUDIT_ACTION_LABELS.TICKET_STATUS_CHANGED;
  }
  if (item.action === TicketAuditAction.ASSIGNED) {
    const assigneeName = typeof metadata.assigneeName === "string" ? metadata.assigneeName : after.assigneeId ? "Agent zugewiesen" : "kein Agent";
    const queueName = typeof metadata.queueName === "string" ? metadata.queueName : after.queueId ? "Queue zugewiesen" : "keine Queue";
    const reason = typeof metadata.reason === "string" ? metadata.reason.trim() : "";
    const line = `${assigneeName} \xB7 ${queueName}`;
    return reason ? `${line} (${reason})` : line;
  }
  if (item.action === TicketAuditAction.COMMENTED) {
    if (metadata.isInternal === true) {
      return typeof metadata.body === "string" && metadata.body.trim() ? `Interne Notiz: ${metadata.body.trim()}` : "Interne Notiz hinzugef\xFCgt.";
    }
    return typeof metadata.body === "string" ? metadata.body : "Kommentar hinzugef\xFCgt.";
  }
  if (item.action === TicketAuditAction.ATTACHMENT_ADDED) {
    return typeof metadata.fileName === "string" ? `Datei \u201E${metadata.fileName}\u201C angeh\xE4ngt.` : "Datei angeh\xE4ngt.";
  }
  if (item.action === TicketAuditAction.UPDATED) {
    const changes = [];
    if (before.subject !== after.subject && typeof after.subject === "string") {
      changes.push(`Betreff: ${after.subject}`);
    }
    if (before.priority !== after.priority) {
      const parsed = ticketPrioritySchema.safeParse(after.priority);
      if (parsed.success) {
        changes.push(`Priorit\xE4t: ${TICKET_PRIORITY_LABELS[parsed.data]}`);
      }
    }
    if (before.categoryId !== after.categoryId) {
      changes.push("Kategorie ge\xE4ndert");
    }
    return changes.join(" \xB7 ") || "Ticket ge\xE4ndert.";
  }
  if (item.action === TicketAuditAction.SLA_ESCALATED) {
    return typeof metadata.summary === "string" && metadata.summary.trim() ? metadata.summary.trim() : "SLA-Eskalation ausgel\xF6st.";
  }
  if (item.action === TicketAuditAction.APPROVAL_STARTED) {
    const workflowName = typeof metadata.workflowName === "string" ? metadata.workflowName.trim() : "";
    return workflowName ? `Freigabe gestartet: ${workflowName}` : "Freigabe gestartet.";
  }
  if (item.action === TicketAuditAction.APPROVAL_DECIDED) {
    const decision = typeof metadata.decision === "string" ? metadata.decision : "";
    const stepName = typeof metadata.stepName === "string" ? metadata.stepName.trim() : "";
    const verb = decision === "REJECTED" ? "abgelehnt" : "freigegeben";
    const comment = typeof metadata.comment === "string" ? metadata.comment.trim() : "";
    const line = stepName ? `${stepName}: ${verb}` : `Freigabe ${verb}`;
    return comment ? `${line} (${comment})` : line;
  }
  if (item.action === TicketAuditAction.AI_CLASSIFIED) {
    return typeof metadata.summary === "string" && metadata.summary.trim() ? metadata.summary.trim() : TICKET_AUDIT_ACTION_LABELS.TICKET_AI_CLASSIFIED;
  }
  if (item.action === TicketAuditAction.AI_ASSIST) {
    return typeof metadata.summary === "string" && metadata.summary.trim() ? metadata.summary.trim() : TICKET_AUDIT_ACTION_LABELS.TICKET_AI_ASSIST;
  }
  if (item.action === TicketAuditAction.INCIDENT_UPDATED) {
    return typeof metadata.summary === "string" && metadata.summary.trim() ? metadata.summary.trim() : TICKET_AUDIT_ACTION_LABELS.TICKET_INCIDENT_UPDATED;
  }
  if (item.action === TicketAuditAction.INCIDENT_LINKED) {
    const number = metadata.ticketNumber;
    return typeof number === "number" ? `Ticket #${number} mit dem Major Incident verkn\xFCpft.` : TICKET_AUDIT_ACTION_LABELS.TICKET_INCIDENT_LINKED;
  }
  if (item.action === TicketAuditAction.INCIDENT_UNLINKED) {
    const number = metadata.ticketNumber;
    return typeof number === "number" ? `Ticket #${number} aus dem Major Incident gel\xF6st.` : TICKET_AUDIT_ACTION_LABELS.TICKET_INCIDENT_UNLINKED;
  }
  if (item.action === TicketAuditAction.INCIDENT_BROADCAST) {
    return typeof metadata.body === "string" && metadata.body.trim() ? `Mitteilung: ${metadata.body.trim()}` : TICKET_AUDIT_ACTION_LABELS.TICKET_INCIDENT_BROADCAST;
  }
  if (item.action === TicketAuditAction.PROBLEM_UPDATED) {
    return typeof metadata.summary === "string" && metadata.summary.trim() ? metadata.summary.trim() : TICKET_AUDIT_ACTION_LABELS.TICKET_PROBLEM_UPDATED;
  }
  if (item.action === TicketAuditAction.PROBLEM_LINKED) {
    const number = metadata.ticketNumber;
    return typeof number === "number" ? `St\xF6rung #${number} mit dem Problem verkn\xFCpft.` : TICKET_AUDIT_ACTION_LABELS.TICKET_PROBLEM_LINKED;
  }
  if (item.action === TicketAuditAction.PROBLEM_UNLINKED) {
    const number = metadata.ticketNumber;
    return typeof number === "number" ? `St\xF6rung #${number} vom Problem gel\xF6st.` : TICKET_AUDIT_ACTION_LABELS.TICKET_PROBLEM_UNLINKED;
  }
  if (item.action === TicketAuditAction.PROBLEM_KB_DRAFT) {
    return typeof metadata.proposedTitle === "string" && metadata.proposedTitle.trim() ? `KB-Entwurf vorgeschlagen: ${metadata.proposedTitle.trim()}` : TICKET_AUDIT_ACTION_LABELS.TICKET_PROBLEM_KB_DRAFT;
  }
  if (item.action === TicketAuditAction.CHANGE_UPDATED) {
    return typeof metadata.summary === "string" && metadata.summary.trim() ? metadata.summary.trim() : TICKET_AUDIT_ACTION_LABELS.TICKET_CHANGE_UPDATED;
  }
  if (item.action === TicketAuditAction.AUTOMATION_APPLIED) {
    const ruleName = typeof metadata.ruleName === "string" ? metadata.ruleName.trim() : "";
    const summary = typeof metadata.summary === "string" ? metadata.summary.trim() : "";
    if (ruleName && summary) {
      return `Regel \u201E${ruleName}\u201C: ${summary}`;
    }
    return ruleName ? `Regel \u201E${ruleName}\u201C ausgef\xFChrt.` : TICKET_AUDIT_ACTION_LABELS.TICKET_AUTOMATION_APPLIED;
  }
  if (item.action === TicketAuditAction.REMOTE_SESSION_REQUESTED || item.action === TicketAuditAction.REMOTE_SESSION_ACTIVATED || item.action === TicketAuditAction.REMOTE_SESSION_DENIED || item.action === TicketAuditAction.REMOTE_SESSION_LAUNCH_REQUESTED || item.action === TicketAuditAction.REMOTE_SESSION_CREDENTIALS_ACCESSED || item.action === TicketAuditAction.REMOTE_SESSION_CREDENTIALS_REJECTED || item.action === TicketAuditAction.REMOTE_SESSION_ENDED || item.action === TicketAuditAction.REMOTE_SESSION_EXPIRED) {
    const label = TICKET_AUDIT_ACTION_LABELS[item.action];
    return typeof metadata.summary === "string" && metadata.summary.trim() ? `${label}: ${metadata.summary.trim()}` : label;
  }
  return TICKET_AUDIT_ACTION_LABELS[item.action] ?? item.action;
}
function transitionsForActor(from, permissions) {
  return allowedTicketTransitions(from).filter(
    (to) => permissions.includes(permissionForTicketStatusChange(to))
  );
}

// src/kb.ts
var KB_EMBEDDING_DIMENSIONS = 768;
var KB_HYBRID_RRF_K = 60;
var KB_SEARCH_CANDIDATE_LIMIT = 50;
var KbVisibility = {
  INTERNAL: "INTERNAL",
  PUBLIC: "PUBLIC"
};
var kbVisibilitySchema = z.enum(KbVisibility);
var KB_VISIBILITY_LABELS = {
  INTERNAL: "Intern",
  PUBLIC: "\xD6ffentlich (Self-Service)"
};
var KbArticleStatus = {
  DRAFT: "DRAFT",
  IN_REVIEW: "IN_REVIEW",
  PUBLISHED: "PUBLISHED",
  ARCHIVED: "ARCHIVED"
};
var kbArticleStatusSchema = z.enum(KbArticleStatus);
var KB_ARTICLE_STATUS_LABELS = {
  DRAFT: "Entwurf",
  IN_REVIEW: "In Pr\xFCfung",
  PUBLISHED: "Ver\xF6ffentlicht",
  ARCHIVED: "Archiviert"
};
var KbSearchMode = {
  HYBRID: "hybrid",
  LEXICAL: "lexical",
  SEMANTIC: "semantic"
};
var kbSearchModeSchema = z.enum(KbSearchMode);
var emptyToUndefined2 = (value) => value === "" || value === "all" ? void 0 : value;
var optionalNullableUuid4 = z.union([z.string().uuid(), z.null()]).optional();
var kbTagSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string()
});
var kbCategorySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  parentId: z.string().uuid().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean()
});
var kbTicketLinkSchema = z.object({
  id: z.string().uuid(),
  number: z.number().int().positive(),
  subject: z.string(),
  status: z.string()
});
var kbArticleListItemSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  summary: z.string().nullable(),
  status: kbArticleStatusSchema,
  visibility: kbVisibilitySchema,
  category: kbCategorySchema.nullable(),
  tags: z.array(kbTagSchema),
  author: ticketPersonSchema,
  helpfulCount: z.number().int().nonnegative(),
  publishedAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
  currentVersion: z.number().int().positive(),
  hasEmbedding: z.boolean()
});
var kbArticleSchema = kbArticleListItemSchema.extend({
  body: z.string(),
  ticketCategoryId: z.string().uuid().nullable(),
  tickets: z.array(kbTicketLinkSchema),
  createdAt: z.iso.datetime()
});
var kbArticleVersionSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int().positive(),
  title: z.string(),
  body: z.string(),
  summary: z.string().nullable(),
  isCurrent: z.boolean(),
  author: ticketPersonSchema,
  createdAt: z.iso.datetime(),
  hasEmbedding: z.boolean()
});
var kbArticleVersionListSchema = z.object({
  items: z.array(kbArticleVersionSchema)
});
var kbLookupsSchema = z.object({
  categories: z.array(kbCategorySchema),
  tags: z.array(kbTagSchema),
  embedding: z.object({
    available: z.boolean(),
    model: z.string(),
    dimensions: z.number().int().positive()
  })
});
var kbArticleListQuerySchema = paginationQuerySchema.extend({
  q: z.preprocess(emptyToUndefined2, z.string().trim().max(500).optional()),
  status: z.preprocess(emptyToUndefined2, kbArticleStatusSchema.optional()),
  visibility: z.preprocess(emptyToUndefined2, kbVisibilitySchema.optional()),
  categoryId: z.preprocess(emptyToUndefined2, z.string().uuid().optional()),
  tag: z.preprocess(emptyToUndefined2, z.string().trim().max(80).optional()),
  ticketId: z.preprocess(emptyToUndefined2, z.string().uuid().optional()),
  sortBy: z.enum(["updatedAt", "title", "publishedAt", "helpfulCount"]).default("updatedAt")
});
var kbArticleListResponseSchema = paginatedSchema(kbArticleListItemSchema);
var createKbCategoryRequestSchema = z.object({
  name: z.string().trim().min(2).max(80),
  parentId: optionalNullableUuid4,
  sortOrder: z.number().int().min(0).max(1e4).optional()
});
var updateKbCategoryRequestSchema = createKbCategoryRequestSchema.partial().extend({
  isActive: z.boolean().optional()
});
var tagNamesSchema = z.array(z.string().trim().min(1).max(40)).max(20).optional();
var createKbArticleRequestSchema = z.object({
  title: z.string().trim().min(3, "kb.title.min").max(200),
  body: z.string().max(1e5).refine((value) => plainTextFromHtml(value).length >= 1, {
    message: "helpdesk.kb.body.required"
  }),
  summary: z.string().trim().max(500).optional(),
  categoryId: optionalNullableUuid4,
  visibility: kbVisibilitySchema.default("INTERNAL"),
  status: kbArticleStatusSchema.optional(),
  tags: tagNamesSchema,
  ticketIds: z.array(z.string().uuid()).max(20).optional(),
  ticketNumbers: z.array(z.number().int().positive()).max(20).optional()
});
var updateKbArticleRequestSchema = z.object({
  title: z.string().trim().min(3).max(200).optional(),
  body: z.string().max(1e5).refine((value) => plainTextFromHtml(value).length >= 1, {
    message: "helpdesk.kb.body.required"
  }).optional(),
  summary: z.string().trim().max(500).nullable().optional(),
  categoryId: optionalNullableUuid4,
  visibility: kbVisibilitySchema.optional(),
  status: kbArticleStatusSchema.optional(),
  tags: tagNamesSchema,
  changeNote: z.string().trim().max(500).optional()
});
var createKbArticleFormSchema = z.object({
  title: z.string().trim().min(3, "kb.title.min").max(200, "Der Titel darf h\xF6chstens 200 Zeichen lang sein."),
  body: z.string().max(1e5, "Der Inhalt ist zu lang.").refine((value) => plainTextFromHtml(value).length >= 1, {
    message: "helpdesk.kb.body.required"
  }),
  summary: z.string().max(500).optional(),
  categoryId: z.string().optional(),
  visibility: kbVisibilitySchema,
  tags: z.string().max(400).optional(),
  ticketNumbers: z.string().max(200).optional()
});
var kbSearchQuerySchema = z.object({
  q: z.string().trim().min(1).max(1e3),
  limit: z.coerce.number().int().min(1).max(25).default(8),
  categoryId: z.preprocess(emptyToUndefined2, z.string().uuid().optional()),
  visibility: z.preprocess(emptyToUndefined2, kbVisibilitySchema.optional())
});
var kbSearchHitSchema = z.object({
  article: kbArticleListItemSchema,
  score: z.number(),
  lexicalScore: z.number().nullable(),
  semanticScore: z.number().nullable(),
  sources: z.array(z.enum(["lexical", "semantic"])),
  snippet: z.string(),
  /** Matching passage when chunk search found a section; omit for legacy article-only hits. */
  passageText: z.string().optional()
});
var kbSearchResponseSchema = z.object({
  q: z.string(),
  mode: kbSearchModeSchema,
  embeddingAvailable: z.boolean(),
  items: z.array(kbSearchHitSchema)
});
var TICKET_CREATE_SUGGEST_MIN_CHARS = 8;
var TICKET_CREATE_SUGGEST_DEBOUNCE_MS = 400;
var TICKET_CREATE_SUGGEST_LIMIT = 5;
function ticketCreateSuggestQuery(subject, descriptionHtml) {
  const text = [subject.trim(), plainTextFromHtml(descriptionHtml)].filter((part) => part.length > 0).join(" ").replace(/\s+/g, " ").trim();
  if (text.length < TICKET_CREATE_SUGGEST_MIN_CHARS) {
    return null;
  }
  return text.slice(0, 1e3);
}
var kbSuggestQuerySchema = kbSearchQuerySchema.extend({
  limit: z.coerce.number().int().min(1).max(10).default(TICKET_CREATE_SUGGEST_LIMIT)
});
var kbSimilarTicketHitSchema = z.object({
  id: z.string().uuid(),
  number: z.number().int().positive(),
  subject: z.string(),
  status: ticketStatusSchema,
  score: z.number(),
  snippet: z.string()
});
var kbSuggestResponseSchema = z.object({
  q: z.string(),
  mode: kbSearchModeSchema,
  embeddingAvailable: z.boolean(),
  articles: z.array(kbSearchHitSchema),
  tickets: z.array(kbSimilarTicketHitSchema)
});
var kbHelpfulResponseSchema = z.object({
  ok: z.literal(true),
  helpfulCount: z.number().int().nonnegative(),
  slug: z.string(),
  title: z.string()
});
var linkKbTicketRequestSchema = z.object({
  ticketId: z.string().uuid().optional(),
  ticketNumber: z.number().int().positive().optional()
}).refine((value) => value.ticketId != null || value.ticketNumber != null, {
  message: "helpdesk.kb.ticket-ref"
});
function reciprocalRankFusion(lexical, semantic, k = KB_HYBRID_RRF_K) {
  const byId = /* @__PURE__ */ new Map();
  const add = (list, source, pickScore) => {
    list.forEach((item, index) => {
      const current = byId.get(item.id) ?? {
        id: item.id,
        score: 0,
        lexicalScore: null,
        semanticScore: null,
        sources: []
      };
      current.score += 1 / (k + index + 1);
      const value = pickScore(item);
      if (source === "lexical" && value != null) {
        current.lexicalScore = value;
      }
      if (source === "semantic" && value != null) {
        current.semanticScore = value;
      }
      if (!current.sources.includes(source)) {
        current.sources.push(source);
      }
      byId.set(item.id, current);
    });
  };
  add(lexical, "lexical", (item) => item.lexicalScore);
  add(semantic, "semantic", (item) => item.semanticScore);
  return [...byId.values()].sort((a, b) => b.score - a.score);
}
function slugifyKbTitle(title) {
  const slug = title.replace(/[Ää]/g, "ae").replace(/[Öö]/g, "oe").replace(/[Üü]/g, "ue").replace(/ß/g, "ss").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);
  return slug || "artikel";
}
function parseTagInput(value) {
  if (!value) {
    return [];
  }
  const seen = /* @__PURE__ */ new Set();
  const tags = [];
  for (const part of value.split(/[,;]+/)) {
    const name = part.trim();
    if (!name) {
      continue;
    }
    const key = name.toLocaleLowerCase("de-DE");
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    tags.push(name.slice(0, 40));
  }
  return tags.slice(0, 20);
}
function parseTicketNumberInput(value) {
  if (!value) {
    return [];
  }
  const numbers = [];
  for (const part of value.split(/[,;\s]+/)) {
    const raw = part.replace(/^#/, "").trim();
    if (!raw) {
      continue;
    }
    const parsed = Number(raw);
    if (Number.isInteger(parsed) && parsed > 0 && !numbers.includes(parsed)) {
      numbers.push(parsed);
    }
  }
  return numbers.slice(0, 20);
}
function kbSnippet(html, query, maxLength = 220) {
  const text = plainTextFromHtml(html).replace(/\s+/g, " ").trim();
  if (!text) {
    return "";
  }
  const needle = query.trim().toLocaleLowerCase("de-DE");
  const haystack = text.toLocaleLowerCase("de-DE");
  const index = needle ? haystack.indexOf(needle) : 0;
  const start = index > 40 ? index - 40 : 0;
  const slice = text.slice(start, start + maxLength);
  const prefix = start > 0 ? "\u2026" : "";
  const suffix = start + maxLength < text.length ? "\u2026" : "";
  return `${prefix}${slice}${suffix}`;
}
function embeddingToSqlLiteral(values) {
  if (values.length === 0 || values.some((value) => !Number.isFinite(value))) {
    throw new Error("Invalid embedding vector");
  }
  return `[${values.join(",")}]`;
}
function kbEmbeddingText(input) {
  const body = plainTextFromHtml(input.body);
  const parts = [input.title.trim(), input.summary?.trim() ?? "", body].filter(Boolean);
  return parts.join("\n\n").slice(0, 8e3);
}

// src/ai.ts
var AI_CHAT_MAX_MESSAGES = 12;
var AI_CHAT_MAX_CONTENT = 2e3;
var AI_CHAT_RETRIEVE_LIMIT = 4;
var PORTAL_CHAT_DRAFT_STORAGE_KEY = "gb-portal-chat-draft";
var aiChatRoleSchema = z.enum(["user", "assistant"]);
var aiChatMessageSchema = z.object({
  role: aiChatRoleSchema,
  content: z.string().trim().min(1).max(AI_CHAT_MAX_CONTENT)
});
var aiChatRequestSchema = z.object({
  messages: z.array(aiChatMessageSchema).min(1).max(AI_CHAT_MAX_MESSAGES)
});
var aiChatSourceSchema = z.object({
  slug: z.string(),
  title: z.string(),
  summary: z.string().nullable()
});
var aiChatTicketDraftSchema = z.object({
  subject: z.string().min(3).max(200),
  description: z.string().min(1).max(2e4)
});
var aiChatResponseSchema = z.object({
  answer: z.string().min(1),
  sources: z.array(aiChatSourceSchema),
  offerTicket: z.boolean(),
  ticketDraft: aiChatTicketDraftSchema.nullable(),
  chatAvailable: z.boolean(),
  retrievalMode: kbSearchModeSchema
});
var aiStatusSchema = z.object({
  hostConfigured: z.boolean(),
  embedding: z.object({
    available: z.boolean(),
    model: z.string(),
    dimensions: z.number().int().positive()
  }),
  chat: z.object({
    available: z.boolean(),
    model: z.string()
  })
});
var modelJsonSchema = z.object({
  canAnswer: z.boolean(),
  answer: z.string(),
  sourceSlugs: z.array(z.string()).optional()
});
function lastUserQuestion(messages) {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const item = messages[index];
    if (item?.role === "user") {
      return item.content;
    }
  }
  return "";
}
function parseAiChatModelJson(raw) {
  const trimmed = raw.trim();
  const fenced = /```(?:json)?\s*([\s\S]*?)```/i.exec(trimmed);
  const candidate = fenced?.[1]?.trim() ?? trimmed;
  const start = candidate.indexOf("{");
  const end = candidate.lastIndexOf("}");
  if (start < 0 || end <= start) {
    return null;
  }
  try {
    const parsed = modelJsonSchema.safeParse(JSON.parse(candidate.slice(start, end + 1)));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
function escapeHtml(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function buildChatTicketDraft(messages) {
  const question = lastUserQuestion(messages) || "Anfrage aus der IT-Hilfe";
  const subject = question.replace(/\s+/g, " ").trim().slice(0, 80);
  const lines = messages.slice(-8).map((item) => {
    const who = item.role === "user" ? "Ich" : "IT-Hilfe";
    return `<p><strong>${escapeHtml(who)}:</strong> ${escapeHtml(item.content)}</p>`;
  });
  const description = `<p>Gespr\xE4ch in der IT-Hilfe:</p>${lines.join("")}`;
  return {
    subject: subject.length >= 3 ? subject : "Anfrage aus der IT-Hilfe",
    description: description.slice(0, 15e3)
  };
}
var AI_CHAT_CONTEXT_DEFAULT_CHARS = 900;
function chatContextPlain(html, maxLength = AI_CHAT_CONTEXT_DEFAULT_CHARS) {
  return plainTextFromHtml(html).replace(/\s+/g, " ").trim().slice(0, maxLength);
}
var AI_EMBEDDING_DIMENSIONS = 768;
var AI_NUM_CTX_MIN = 512;
var AI_NUM_CTX_MAX = 32768;
var AI_NUM_PREDICT_MIN = 64;
var AI_NUM_PREDICT_MAX = 4096;
var AI_MAX_PASSAGES_MIN = 1;
var AI_MAX_PASSAGES_MAX = 12;
var AI_PASSAGE_CHARS_MIN = 200;
var AI_PASSAGE_CHARS_MAX = 4e3;
var AI_KEEP_ALIVE_MIN = 0;
var AI_KEEP_ALIVE_MAX = 3600;
var aiHardwareProfileIdSchema = z.enum(["low", "mid", "high"]);
var AI_HARDWARE_PROFILES = [
  {
    id: "low",
    label: "Wenig Speicher",
    description: "Etwa 4 GB Arbeitsspeicher. Kurzes Fenster, drei Stellen, kein zweiter Versuch.",
    numCtx: 2048,
    numPredict: 512,
    maxPassages: 3,
    passageChars: 500,
    allowRefetch: false,
    keepAliveSeconds: 30
  },
  {
    id: "mid",
    label: "Arbeitsrechner",
    description: "Etwa 16 GB, zum Beispiel qwen2.5:7b. L\xE4ngere Antworten und ein zweiter Versuch.",
    numCtx: 8192,
    numPredict: 1024,
    maxPassages: 6,
    passageChars: 900,
    allowRefetch: true,
    keepAliveSeconds: 300
  },
  {
    id: "high",
    label: "Server",
    description: "32 GB Arbeitsspeicher oder eine GPU. Das gr\xF6\xDFte Fenster und die meisten Stellen.",
    numCtx: 16384,
    numPredict: 1536,
    maxPassages: 8,
    passageChars: 1200,
    allowRefetch: true,
    keepAliveSeconds: 300
  }
];
function matchingHardwareProfile(values) {
  return AI_HARDWARE_PROFILES.find(
    (profile) => profile.numCtx === values.numCtx && profile.numPredict === values.numPredict && profile.maxPassages === values.maxPassages && profile.passageChars === values.passageChars && profile.allowRefetch === values.allowRefetch && profile.keepAliveSeconds === values.keepAliveSeconds
  );
}
var aiProviderSchema = z.enum(["ollama", "openai", "grok"]);
function aiProviderUsesApiKey(provider) {
  return provider === "openai" || provider === "grok";
}
var aiConnectionSchema = z.object({
  enabled: z.boolean(),
  intranetEnabled: z.boolean(),
  provider: aiProviderSchema,
  baseUrl: z.string(),
  chatModel: z.string(),
  assistModel: z.string(),
  embeddingModel: z.string(),
  embeddingDimensions: z.number().int(),
  chatTimeoutMs: z.number().int(),
  embedTimeoutMs: z.number().int(),
  numCtx: z.number().int(),
  numPredict: z.number().int(),
  maxPassages: z.number().int(),
  passageChars: z.number().int(),
  allowRefetch: z.boolean(),
  keepAliveSeconds: z.number().int(),
  apiKeyConfigured: z.boolean(),
  updatedAt: z.iso.datetime()
});
var updateAiConnectionRequestSchema = z.strictObject({
  enabled: z.boolean(),
  intranetEnabled: z.boolean(),
  provider: aiProviderSchema,
  baseUrl: z.string().trim().max(500),
  chatModel: z.string().trim().min(1).max(200),
  assistModel: z.string().trim().max(200),
  embeddingModel: z.string().trim().min(1).max(200),
  embeddingDimensions: z.number().int(),
  chatTimeoutMs: z.number().int().min(5e3).max(18e4),
  embedTimeoutMs: z.number().int().min(1e3).max(12e4),
  numCtx: z.number().int().min(AI_NUM_CTX_MIN).max(AI_NUM_CTX_MAX),
  numPredict: z.number().int().min(AI_NUM_PREDICT_MIN).max(AI_NUM_PREDICT_MAX),
  maxPassages: z.number().int().min(AI_MAX_PASSAGES_MIN).max(AI_MAX_PASSAGES_MAX),
  passageChars: z.number().int().min(AI_PASSAGE_CHARS_MIN).max(AI_PASSAGE_CHARS_MAX),
  allowRefetch: z.boolean(),
  keepAliveSeconds: z.number().int().min(AI_KEEP_ALIVE_MIN).max(AI_KEEP_ALIVE_MAX),
  /** Omitted or blank keeps the stored key. Never returned by GET. */
  apiKey: z.string().max(2e3).optional(),
  /** Removes the stored key. A non-blank apiKey in the same request wins. */
  clearApiKey: z.boolean().optional()
});
var aiConnectionProbeSchema = z.object({
  reachable: z.boolean(),
  chatModelPresent: z.boolean(),
  embeddingModelPresent: z.boolean(),
  /** Model is listed, but embed returned no vector of the configured length. */
  embeddingRejected: z.boolean(),
  /** Grok language-model ids and aliases from the last probe. Empty for Ollama and OpenAI. */
  languageModels: z.array(z.string())
});
var NotificationProductCode = {
  PLATFORM: "platform",
  ...ProductCode
};
var notificationProductCodeSchema = z.enum(NotificationProductCode);
var NOTIFICATION_PRODUCT_LABELS = {
  platform: "Mitarbeiterportal",
  helpdesk: PRODUCT_META.helpdesk.labelDe,
  leave: PRODUCT_META.leave.labelDe,
  fleet: PRODUCT_META.fleet.labelDe,
  maintenance: PRODUCT_META.maintenance.labelDe,
  onboarding: PRODUCT_META.onboarding.labelDe,
  tools: PRODUCT_META.tools.labelDe,
  intranet: PRODUCT_META.intranet.labelDe
};
var NotificationEventKey = {
  CREATED: "ticket.created",
  STATUS_CHANGED: "ticket.status_changed",
  COMMENTED: "ticket.commented",
  ASSIGNED: "ticket.assigned",
  UPDATED: "ticket.updated",
  SLA_WARNING: "ticket.sla_warning",
  APPROVAL_REQUESTED: "ticket.approval_requested",
  APPROVAL_DECIDED: "ticket.approval_decided",
  INCIDENT_BROADCAST: "ticket.incident_broadcast",
  AUTOMATION: "ticket.automation",
  REMOTE_SESSION: "ticket.remote_session",
  CSAT: "ticket.csat",
  RESOLVED_REMINDER: "ticket.resolved_reminder",
  INTRANET_NEWS_PUBLISHED: "intranet.news_published",
  INTRANET_ACK_REQUIRED: "intranet.ack_required",
  INTRANET_ACK_REMINDER: "intranet.ack_reminder",
  INTRANET_CONTENT_LIKED: "intranet.content_liked",
  INTRANET_COMMENT_CREATED: "intranet.comment_created",
  INTRANET_COMMENT_REPLIED: "intranet.comment_replied",
  INTRANET_COMMENT_LIKED: "intranet.comment_liked",
  INTRANET_MENTIONED: "intranet.mentioned",
  INTRANET_REVIEW_DUE: "intranet.review_due",
  INTRANET_MELDUNG: "intranet.meldung",
  INTRANET_CHAT_MESSAGE: "intranet.chat_message",
  INTRANET_SITE_DIARY_SUBMITTED: "intranet.site_diary_submitted",
  LEAVE_REQUEST_SUBMITTED: "leave.request_submitted",
  LEAVE_REQUEST_APPROVED: "leave.request_approved",
  LEAVE_REQUEST_REJECTED: "leave.request_rejected",
  LEAVE_REQUEST_CANCELLED: "leave.request_cancelled",
  LEAVE_REMINDER_UPCOMING: "leave.reminder_upcoming",
  LEAVE_SICK_REPORTED: "leave.sick_reported",
  FLEET_ASSIGNMENT_STARTED: "fleet.assignment_started",
  FLEET_DAMAGE_REPORTED: "fleet.damage_reported",
  FLEET_DAMAGE_CLOSED: "fleet.damage_closed",
  FLEET_REMINDER_UPCOMING: "fleet.reminder_upcoming",
  FLEET_REMINDER_OVERDUE: "fleet.reminder_overdue",
  FLEET_TELEMATICS_STALE: "fleet.telematics_stale",
  FLEET_FUEL_EXCEPTION: "fleet.fuel_exception",
  FLEET_GUEST_INVOICE: "fleet.guest_invoice",
  MAINTENANCE_WORK_ORDER_ASSIGNED: "maintenance.work_order_assigned",
  MAINTENANCE_WORK_ORDER_REASSIGNED: "maintenance.work_order_reassigned",
  MAINTENANCE_WORK_ORDER_DUE_SOON: "maintenance.work_order_due_soon",
  MAINTENANCE_WORK_ORDER_OVERDUE: "maintenance.work_order_overdue",
  MAINTENANCE_PROTOCOL_COMPLETED: "maintenance.protocol_completed",
  MAINTENANCE_FINALIZATION_FAILED: "maintenance.finalization_failed",
  ONBOARDING_TASK_ASSIGNED: "onboarding.task_assigned",
  ONBOARDING_TASK_DUE_SOON: "onboarding.task_due_soon",
  ONBOARDING_TASK_OVERDUE: "onboarding.task_overdue",
  ONBOARDING_CASE_STARTED: "onboarding.case_started",
  ONBOARDING_SUBJECT_ADMITTED: "onboarding.subject_admitted",
  ONBOARDING_CASE_COMPLETED: "onboarding.case_completed",
  ASSIGNMENTS_RELEASED: "platform.assignments_released"
};
var notificationEventKeySchema = z.enum(NotificationEventKey);
var NOTIFICATION_EVENT_KEYS = [
  NotificationEventKey.CREATED,
  NotificationEventKey.STATUS_CHANGED,
  NotificationEventKey.COMMENTED,
  NotificationEventKey.ASSIGNED,
  NotificationEventKey.UPDATED,
  NotificationEventKey.SLA_WARNING,
  NotificationEventKey.APPROVAL_REQUESTED,
  NotificationEventKey.APPROVAL_DECIDED,
  NotificationEventKey.INCIDENT_BROADCAST,
  NotificationEventKey.AUTOMATION,
  NotificationEventKey.REMOTE_SESSION,
  NotificationEventKey.CSAT,
  NotificationEventKey.RESOLVED_REMINDER,
  NotificationEventKey.INTRANET_NEWS_PUBLISHED,
  NotificationEventKey.INTRANET_ACK_REQUIRED,
  NotificationEventKey.INTRANET_ACK_REMINDER,
  NotificationEventKey.INTRANET_CONTENT_LIKED,
  NotificationEventKey.INTRANET_COMMENT_CREATED,
  NotificationEventKey.INTRANET_COMMENT_REPLIED,
  NotificationEventKey.INTRANET_COMMENT_LIKED,
  NotificationEventKey.INTRANET_MENTIONED,
  NotificationEventKey.INTRANET_REVIEW_DUE,
  NotificationEventKey.INTRANET_MELDUNG,
  NotificationEventKey.INTRANET_CHAT_MESSAGE,
  NotificationEventKey.INTRANET_SITE_DIARY_SUBMITTED,
  NotificationEventKey.LEAVE_REQUEST_SUBMITTED,
  NotificationEventKey.LEAVE_REQUEST_APPROVED,
  NotificationEventKey.LEAVE_REQUEST_REJECTED,
  NotificationEventKey.LEAVE_REQUEST_CANCELLED,
  NotificationEventKey.LEAVE_REMINDER_UPCOMING,
  NotificationEventKey.LEAVE_SICK_REPORTED,
  NotificationEventKey.FLEET_ASSIGNMENT_STARTED,
  NotificationEventKey.FLEET_DAMAGE_REPORTED,
  NotificationEventKey.FLEET_DAMAGE_CLOSED,
  NotificationEventKey.FLEET_REMINDER_UPCOMING,
  NotificationEventKey.FLEET_REMINDER_OVERDUE,
  NotificationEventKey.FLEET_TELEMATICS_STALE,
  NotificationEventKey.FLEET_FUEL_EXCEPTION,
  NotificationEventKey.FLEET_GUEST_INVOICE,
  NotificationEventKey.MAINTENANCE_WORK_ORDER_ASSIGNED,
  NotificationEventKey.MAINTENANCE_WORK_ORDER_REASSIGNED,
  NotificationEventKey.MAINTENANCE_WORK_ORDER_DUE_SOON,
  NotificationEventKey.MAINTENANCE_WORK_ORDER_OVERDUE,
  NotificationEventKey.MAINTENANCE_PROTOCOL_COMPLETED,
  NotificationEventKey.MAINTENANCE_FINALIZATION_FAILED,
  NotificationEventKey.ONBOARDING_TASK_ASSIGNED,
  NotificationEventKey.ONBOARDING_TASK_DUE_SOON,
  NotificationEventKey.ONBOARDING_TASK_OVERDUE,
  NotificationEventKey.ONBOARDING_CASE_STARTED,
  NotificationEventKey.ONBOARDING_SUBJECT_ADMITTED,
  NotificationEventKey.ONBOARDING_CASE_COMPLETED,
  NotificationEventKey.ASSIGNMENTS_RELEASED
];
var NOTIFICATION_EVENT_EMAIL_DEFAULT = {
  [NotificationEventKey.LEAVE_REMINDER_UPCOMING]: false,
  [NotificationEventKey.LEAVE_SICK_REPORTED]: false,
  [NotificationEventKey.FLEET_DAMAGE_CLOSED]: false,
  [NotificationEventKey.INTRANET_CONTENT_LIKED]: false,
  [NotificationEventKey.INTRANET_COMMENT_CREATED]: false,
  [NotificationEventKey.INTRANET_COMMENT_REPLIED]: false,
  [NotificationEventKey.INTRANET_COMMENT_LIKED]: false,
  [NotificationEventKey.INTRANET_MENTIONED]: false,
  [NotificationEventKey.INTRANET_REVIEW_DUE]: false,
  [NotificationEventKey.INTRANET_CHAT_MESSAGE]: false,
  [NotificationEventKey.INTRANET_SITE_DIARY_SUBMITTED]: true
};
var NOTIFICATION_EVENT_PRODUCT = {
  "ticket.created": NotificationProductCode.HELPDESK,
  "ticket.status_changed": NotificationProductCode.HELPDESK,
  "ticket.commented": NotificationProductCode.HELPDESK,
  "ticket.assigned": NotificationProductCode.HELPDESK,
  "ticket.updated": NotificationProductCode.HELPDESK,
  "ticket.sla_warning": NotificationProductCode.HELPDESK,
  "ticket.approval_requested": NotificationProductCode.HELPDESK,
  "ticket.approval_decided": NotificationProductCode.HELPDESK,
  "ticket.incident_broadcast": NotificationProductCode.HELPDESK,
  "ticket.automation": NotificationProductCode.HELPDESK,
  "ticket.remote_session": NotificationProductCode.HELPDESK,
  "ticket.csat": NotificationProductCode.HELPDESK,
  "ticket.resolved_reminder": NotificationProductCode.HELPDESK,
  "intranet.news_published": NotificationProductCode.INTRANET,
  "intranet.ack_required": NotificationProductCode.INTRANET,
  "intranet.ack_reminder": NotificationProductCode.INTRANET,
  "intranet.content_liked": NotificationProductCode.INTRANET,
  "intranet.comment_created": NotificationProductCode.INTRANET,
  "intranet.comment_replied": NotificationProductCode.INTRANET,
  "intranet.comment_liked": NotificationProductCode.INTRANET,
  "intranet.mentioned": NotificationProductCode.INTRANET,
  "intranet.review_due": NotificationProductCode.INTRANET,
  "intranet.meldung": NotificationProductCode.INTRANET,
  "intranet.chat_message": NotificationProductCode.INTRANET,
  "intranet.site_diary_submitted": NotificationProductCode.INTRANET,
  "leave.request_submitted": NotificationProductCode.LEAVE,
  "leave.request_approved": NotificationProductCode.LEAVE,
  "leave.request_rejected": NotificationProductCode.LEAVE,
  "leave.request_cancelled": NotificationProductCode.LEAVE,
  "leave.reminder_upcoming": NotificationProductCode.LEAVE,
  "leave.sick_reported": NotificationProductCode.LEAVE,
  "fleet.assignment_started": NotificationProductCode.FLEET,
  "fleet.damage_reported": NotificationProductCode.FLEET,
  "fleet.damage_closed": NotificationProductCode.FLEET,
  "fleet.reminder_upcoming": NotificationProductCode.FLEET,
  "fleet.reminder_overdue": NotificationProductCode.FLEET,
  "fleet.telematics_stale": NotificationProductCode.FLEET,
  "fleet.fuel_exception": NotificationProductCode.FLEET,
  "fleet.guest_invoice": NotificationProductCode.FLEET,
  "maintenance.work_order_assigned": NotificationProductCode.MAINTENANCE,
  "maintenance.work_order_reassigned": NotificationProductCode.MAINTENANCE,
  "maintenance.work_order_due_soon": NotificationProductCode.MAINTENANCE,
  "maintenance.work_order_overdue": NotificationProductCode.MAINTENANCE,
  "maintenance.protocol_completed": NotificationProductCode.MAINTENANCE,
  "maintenance.finalization_failed": NotificationProductCode.MAINTENANCE,
  "onboarding.task_assigned": NotificationProductCode.ONBOARDING,
  "onboarding.task_due_soon": NotificationProductCode.ONBOARDING,
  "onboarding.task_overdue": NotificationProductCode.ONBOARDING,
  "onboarding.case_started": NotificationProductCode.ONBOARDING,
  "onboarding.subject_admitted": NotificationProductCode.ONBOARDING,
  "onboarding.case_completed": NotificationProductCode.ONBOARDING,
  "platform.assignments_released": NotificationProductCode.PLATFORM
};
var NOTIFICATION_EVENT_LABELS = {
  "ticket.created": "Neues Ticket",
  "ticket.status_changed": "Statuswechsel",
  "ticket.commented": "Neue Antwort",
  "ticket.assigned": "Zuweisung",
  "ticket.updated": "Ticket ge\xE4ndert",
  "ticket.sla_warning": "SLA-Warnung",
  "ticket.approval_requested": "Freigabe angefordert",
  "ticket.approval_decided": "Freigabe entschieden",
  "ticket.incident_broadcast": "Major-Incident-Mitteilung",
  "ticket.automation": "Automatisierungsregel",
  "ticket.remote_session": "Remote-Support",
  "ticket.csat": "Zufriedenheitsumfrage",
  "ticket.resolved_reminder": "Erinnerung vor automatischer Schlie\xDFung",
  "intranet.news_published": "Neue Unternehmensnachricht",
  "intranet.ack_required": "Lesebest\xE4tigung erforderlich",
  "intranet.ack_reminder": "Erinnerung Lesebest\xE4tigung",
  "intranet.content_liked": "platform.notify.label.content-liked",
  "intranet.comment_created": "platform.notify.label.comment-created",
  "intranet.comment_replied": "platform.notify.label.comment-replied",
  "intranet.comment_liked": "platform.notify.label.comment-liked",
  "intranet.mentioned": "Erw\xE4hnung",
  "intranet.review_due": "Pr\xFCfung f\xE4llig",
  "intranet.meldung": "Meldung",
  "intranet.chat_message": "Neue Chat-Nachricht",
  "intranet.site_diary_submitted": "Bautagesbericht",
  "leave.request_submitted": "Urlaubsantrag eingereicht",
  "leave.request_approved": "Urlaub genehmigt",
  "leave.request_rejected": "Urlaub abgelehnt",
  "leave.request_cancelled": "Urlaubsantrag zur\xFCckgezogen",
  "leave.reminder_upcoming": "Erinnerung vor Urlaubsbeginn",
  "leave.sick_reported": "Krankmeldung erfasst",
  "fleet.assignment_started": "Fahrzeug zugeordnet",
  "fleet.damage_reported": "Schaden oder Mangel gemeldet",
  "fleet.damage_closed": "Schadenmeldung geschlossen",
  "fleet.reminder_upcoming": "Fuhrpark-Frist bald f\xE4llig",
  "fleet.reminder_overdue": "Fuhrpark-Frist \xFCberf\xE4llig",
  "fleet.telematics_stale": "Fuhrpark: kein Telematik-Signal",
  "fleet.fuel_exception": "Fuhrpark: Tankkarten-Ausnahme",
  "fleet.guest_invoice": "Fuhrpark: Gast-Rechnung",
  "maintenance.work_order_assigned": "Wartungsauftrag zugewiesen",
  "maintenance.work_order_reassigned": "Wartungsauftrag neu zugewiesen",
  "maintenance.work_order_due_soon": "Wartungsauftrag bald f\xE4llig",
  "maintenance.work_order_overdue": "Wartungsauftrag \xFCberf\xE4llig",
  "maintenance.protocol_completed": "Wartungsprotokoll abgeschlossen",
  "maintenance.finalization_failed": "Wartungsprotokoll: PDF-Erzeugung fehlgeschlagen",
  "onboarding.task_assigned": "Aufgabe zugewiesen",
  "onboarding.task_due_soon": "Aufgabe bald f\xE4llig",
  "onboarding.task_overdue": "Aufgabe \xFCberf\xE4llig",
  "onboarding.case_started": "Vorgang gestartet",
  "onboarding.subject_admitted": "Person aufgenommen",
  "onboarding.case_completed": "Vorgang abgeschlossen",
  "platform.assignments_released": "Zuordnungen beendet"
};
var NOTIFICATION_EVENT_DESCRIPTIONS = {
  "ticket.created": "platform.notify.desc.ticket.created",
  "ticket.status_changed": "platform.notify.desc.ticket.status",
  "ticket.commented": "Wenn jemand \xF6ffentlich auf ein Ticket antwortet (bei Anfragen ohne Agent auch die Warteschlange).",
  "ticket.assigned": "platform.notify.desc.ticket.assigned",
  "ticket.updated": "platform.notify.desc.ticket.updated",
  "ticket.sla_warning": "Wenn eine SLA-Frist bald abl\xE4uft oder \xFCberschritten ist.",
  "ticket.approval_requested": "platform.notify.desc.ticket.approval",
  "ticket.approval_decided": "Wenn eine Freigabe erteilt oder abgelehnt wurde.",
  "ticket.incident_broadcast": "platform.notify.desc.ticket.incident",
  "ticket.automation": "platform.notify.desc.ticket.automation",
  "ticket.remote_session": "platform.notify.desc.ticket.remote",
  "ticket.csat": "platform.notify.desc.ticket.csat",
  "ticket.resolved_reminder": "platform.notify.desc.ticket.resolved",
  "intranet.news_published": "platform.notify.desc.intranet.news",
  "intranet.ack_required": "platform.notify.desc.intranet.ack",
  "intranet.ack_reminder": "platform.notify.desc.intranet.ack-reminder",
  "intranet.content_liked": "platform.notify.desc.intranet.liked",
  "intranet.comment_created": "platform.notify.desc.intranet.comment",
  "intranet.comment_replied": "platform.notify.desc.intranet.reply",
  "intranet.comment_liked": "platform.notify.desc.intranet.comment-liked",
  "intranet.mentioned": "platform.notify.desc.intranet.mention",
  "intranet.review_due": "Wenn eine Anleitung, ein Download oder ein Formular gepr\xFCft werden soll (Standard: nur Glocke).",
  "intranet.meldung": "Wenn jemand eine Meldung zu einer ver\xF6ffentlichten Seite schickt (Standard: Glocke und E-Mail, solange die eigene Einstellung das zul\xE4sst).",
  "intranet.chat_message": "Neue Nachricht im Intranet-Chat (Glocke und Push, keine E-Mail).",
  "intranet.site_diary_submitted": "platform.notify.desc.intranet.diary",
  "leave.request_submitted": "Als F\xFChrungskraft: wenn ein Mitarbeitender einen Urlaubsantrag einreicht.",
  "leave.request_approved": "platform.notify.desc.leave.approved",
  "leave.request_rejected": "platform.notify.desc.leave.rejected",
  "leave.request_cancelled": "Als F\xFChrungskraft oder Vertretung: wenn ein eingereichter Antrag zur\xFCckgezogen wurde.",
  "leave.reminder_upcoming": "Ein paar Tage vor Beginn eines genehmigten Urlaubs (Standard: nur Glocke, E-Mail aus).",
  "leave.sick_reported": "Als F\xFChrungskraft: wenn eine Krankmeldung erfasst wurde (Standard: nur Glocke).",
  "fleet.assignment_started": "platform.notify.desc.fleet.assigned",
  "fleet.damage_reported": "Als Fuhrpark-Admin: wenn jemand einen Schaden oder Mangel meldet (Standard: Glocke und E-Mail).",
  "fleet.damage_closed": "platform.notify.desc.fleet.closed",
  "fleet.reminder_upcoming": "Wenn eine Fuhrpark-Frist bald f\xE4llig wird (Standard: Glocke und E-Mail).",
  "fleet.reminder_overdue": "Wenn eine Fuhrpark-Frist \xFCberf\xE4llig ist (Standard: Glocke und E-Mail).",
  "fleet.telematics_stale": "Als Fuhrpark-Admin: wenn ein Fahrzeug mit Telematik l\xE4nger kein Standortsignal sendet (Standard: Glocke und E-Mail).",
  "fleet.fuel_exception": "Als Fuhrpark-Admin: wenn ein Tankkarten-Umsatz nicht zugeordnet werden konnte (Standard: Glocke und E-Mail).",
  "fleet.guest_invoice": "Rechnung an einen Gast nach einer Ladung. Die Vorlage liegt bei den anderen E-Mail-Vorlagen.",
  "maintenance.work_order_assigned": "platform.notify.desc.maintenance.assigned",
  "maintenance.work_order_reassigned": "platform.notify.desc.maintenance.reassigned",
  "maintenance.work_order_due_soon": "Wenn ein offener Wartungsauftrag in den n\xE4chsten sieben Tagen f\xE4llig wird.",
  "maintenance.work_order_overdue": "Wenn ein offener Wartungsauftrag \xFCberf\xE4llig ist.",
  "maintenance.protocol_completed": "Wenn ein Wartungsprotokoll abgeschlossen wurde und das PDF bereitsteht (Standard: Glocke und E-Mail).",
  "maintenance.finalization_failed": "Wenn die PDF-Erzeugung eines Wartungsprotokolls fehlgeschlagen ist (Standard: Glocke und E-Mail).",
  "onboarding.task_assigned": "platform.notify.desc.onboarding.assigned",
  "onboarding.task_due_soon": "platform.notify.desc.onboarding.due",
  "onboarding.task_overdue": "platform.notify.desc.onboarding.overdue",
  "onboarding.case_started": "Wenn ein Eintritts- oder Austrittsvorgang gestartet wurde (Standard: Glocke und E-Mail).",
  "onboarding.subject_admitted": "Wenn eine Person aus der Vorbereitung als Benutzer aufgenommen wurde (Standard: Glocke und E-Mail).",
  "onboarding.case_completed": "Wenn ein Eintritts- oder Austrittsvorgang abgeschlossen wurde (Standard: Glocke und E-Mail).",
  "platform.assignments_released": "Wenn offene Ticket- oder Fahrzeugzuordnungen einer deaktivierten Person beendet wurden (Standard: Glocke und E-Mail)."
};
function isAssignedEmailRequired(roles) {
  if (roles.includes(RoleCode.IT_ADMIN)) {
    return false;
  }
  return roles.includes(RoleCode.IT_AGENT) || roles.includes(RoleCode.IT_LEAD);
}
function isIntranetChatMessageEvent(eventKey) {
  return eventKey === NotificationEventKey.INTRANET_CHAT_MESSAGE;
}
var NOTIFICATION_PREFERENCE_EVENT_KEYS = NOTIFICATION_EVENT_KEYS;
var notificationChannelSchema = z.enum(["IN_APP", "EMAIL"]);
var notificationSchema = z.object({
  id: z.string().uuid(),
  eventKey: z.string(),
  title: z.string(),
  body: z.string(),
  productCode: notificationProductCodeSchema,
  /** Internal path to open. Null on legacy rows; clients fall back to `ticketNumber`. */
  href: z.string().nullable(),
  ticketId: z.string().uuid().nullable(),
  ticketNumber: z.number().int().positive().nullable(),
  readAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime()
});
var NOTIFICATION_INBOX_PAGE_SIZE = 20;
var notificationListResponseSchema = paginatedSchema(notificationSchema).extend({
  unreadCount: z.number().int().nonnegative()
});
var notificationListQuerySchema = paginationQuerySchema.pick({ page: true, pageSize: true }).extend({
  unread: z.enum(["true", "false"]).optional(),
  productCode: notificationProductCodeSchema.optional(),
  /** Bell dropdown: first page, up to this many rows (legacy). */
  limit: z.coerce.number().int().min(1).max(100).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).optional()
});
var notificationDeleteAllResponseSchema = z.object({
  ok: z.literal(true),
  deleted: z.number().int().nonnegative()
});
var notificationPreferenceSchema = z.object({
  productCode: notificationProductCodeSchema.optional(),
  eventKey: notificationEventKeySchema,
  inApp: z.boolean(),
  email: z.boolean(),
  /** System push (Web Push). When unset on read, defaults to the stored value or inApp. */
  push: z.boolean(),
  /** True when the API forces e-mail on for this event (agent/lead + ticket.assigned). */
  emailLocked: z.boolean().optional(),
  /** True when in-app and push stay on and e-mail stays off (intranet chat). */
  channelsLocked: z.boolean().optional()
});
var notificationPreferencesResponseSchema = z.object({
  items: z.array(notificationPreferenceSchema),
  /** False when org mail kill-switch disables e-mail for this user's role world. */
  orgEmailEnabled: z.boolean().optional(),
  /** True only when a tenant standard exists and this person has no personal row. */
  followsTenantStandard: z.boolean(),
  /** True when any personal NotificationPreference row exists. */
  hasPersonalPreferences: z.boolean()
});
var updateNotificationPreferencesRequestSchema = z.object({
  items: z.array(notificationPreferenceSchema).refine(
    (items) => NOTIFICATION_PREFERENCE_EVENT_KEYS.every(
      (key) => items.some((item) => item.eventKey === key)
    ) && items.length === NOTIFICATION_PREFERENCE_EVENT_KEYS.length,
    { message: "platform.notify.prefs.incomplete" }
  )
});
var notificationStandardResponseSchema = z.object({
  saved: z.boolean(),
  items: z.array(notificationPreferenceSchema)
});
var updateNotificationStandardRequestSchema = updateNotificationPreferencesRequestSchema;
var notificationActionResponseSchema = z.object({
  ok: z.literal(true)
});
var markNotificationsForPathRequestSchema = z.object({
  path: z.string().trim().min(1).max(512)
});

// src/modules.ts
var AppModuleCode = {
  INCIDENTS: "incidents",
  PROBLEMS: "problems",
  CHANGES: "changes",
  QUEUES: "queues",
  ASSETS: "assets",
  KNOWLEDGE: "knowledge",
  AI: "ai",
  SLA: "sla",
  APPROVALS: "approvals",
  AUTOMATION: "automation",
  CSAT: "csat",
  AUDIT: "audit",
  INTEGRATIONS: "integrations",
  REMOTE_SUPPORT: "remote_support",
  STATUS: "status"
};
var appModuleCodeSchema = z.enum(AppModuleCode);
var APP_MODULE_CODES = Object.values(AppModuleCode);
var APP_MODULE_LABELS = {
  incidents: "St\xF6rungen",
  problems: "Probleme",
  changes: "Changes",
  queues: "Warteschlangen",
  assets: "Assets",
  knowledge: "Wissen",
  ai: "KI",
  sla: "SLA",
  approvals: "Freigaben",
  automation: "Automatisierung",
  csat: "Auswertung",
  audit: "Audit-Log",
  integrations: "Integrationen",
  remote_support: "Remote-Support",
  status: "Systemstatus"
};
var APP_MODULE_DESCRIPTIONS = {
  incidents: "Ticket-Typ St\xF6rung mit Impact/Urgency, Major-Incident-B\xFCndelung und Statusmeldungen an Betroffene.",
  problems: "Ticket-Typ Problem mit RCA, Workaround und Verkn\xFCpfung zu St\xF6rungen. KB-Entwurf nur wenn Wissen aktiv ist.",
  changes: "Ticket-Typ \xC4nderung, Change-Kalender und CAB. Neue Freigaben starten nur, wenn Freigaben aktiv sind.",
  queues: "Kanban-Board, Queue-Filter und Zuweisung in der Oberfl\xE4che. Die stille Standard-Queue-Zuordnung bleibt erhalten.",
  assets: "CMDB f\xFCr Ger\xE4te und Lizenzen sowie die Verkn\xFCpfung von Assets mit Tickets.",
  knowledge: "Wissensdatenbank, Portal-Hilfe, FAQ und Vorschl\xE4ge beim Ticket anlegen. Schaltet KI mit aus.",
  ai: "Portal-Chat, Ticket-Klassifizierung, KI-Ticket-Hilfe und KB-Assistent. Setzt Wissen voraus.",
  sla: "SLA-Policies, Countdown am Ticket und Eskalationen. Laufende Uhren laufen zu Ende; neue werden nicht gesetzt.",
  approvals: "Freigabe-Workflows und Ticket-Panel. Bereits gestartete Freigaben k\xF6nnen abgeschlossen werden.",
  automation: "Wenn-dann-Regeln. Gespeicherte Regeln bleiben, werden aber nicht mehr ausgef\xFChrt.",
  csat: "Zufriedenheitsumfrage nach dem Schlie\xDFen und die Auswertung. Bereits versendete Links bleiben g\xFCltig.",
  audit: "Die Audit-Log-Oberfl\xE4che. Protokolliert wird weiterhin, auch wenn das Modul aus ist.",
  integrations: "E-Mail-Eingang, LDAP/AD und Webhooks. Geheimnisse in der Umgebung bleiben unangetastet.",
  remote_support: "Connector-Kopplung und Fernwartung am Ticket. Offene Sitzungen d\xFCrfen normal enden.",
  status: "Die Seite Systemstatus in der Navigation. Health-Checks f\xFCr Docker bleiben \xF6ffentlich."
};
var AppModuleGroup = {
  ITSM: "itsm",
  ORGANISATION: "organisation",
  KNOWLEDGE: "knowledge",
  CONTROL: "control",
  OPERATIONS: "operations"
};
var appModuleGroupSchema = z.enum(AppModuleGroup);
var APP_MODULE_GROUP_LABELS = {
  itsm: "ITSM",
  organisation: "Organisation",
  knowledge: "Wissen & KI",
  control: "Steuerung",
  operations: "Betrieb"
};
var APP_MODULE_GROUP = {
  incidents: AppModuleGroup.ITSM,
  problems: AppModuleGroup.ITSM,
  changes: AppModuleGroup.ITSM,
  queues: AppModuleGroup.ORGANISATION,
  assets: AppModuleGroup.ORGANISATION,
  knowledge: AppModuleGroup.KNOWLEDGE,
  ai: AppModuleGroup.KNOWLEDGE,
  sla: AppModuleGroup.CONTROL,
  approvals: AppModuleGroup.CONTROL,
  automation: AppModuleGroup.CONTROL,
  csat: AppModuleGroup.CONTROL,
  audit: AppModuleGroup.OPERATIONS,
  integrations: AppModuleGroup.OPERATIONS,
  remote_support: AppModuleGroup.OPERATIONS,
  status: AppModuleGroup.OPERATIONS
};
var APP_MODULE_HIDES = {
  incidents: ["Typ St\xF6rung", "Major-Incident", "Ticket-Panel"],
  problems: ["Typ Problem", "RCA-Panel", "KB-Entwurf"],
  changes: ["Change-Kalender", "Typ \xC4nderung", "Ticket-Panel"],
  queues: ["Kanban", "Queue-Filter", "Queue-Zuweisung", "Queue-Verwaltung"],
  assets: ["CMDB", "Ticket-Panel"],
  knowledge: ["Wissen", "Portal-Hilfe", "Vorschl\xE4ge"],
  ai: ["Portal-Chat", "KI-Hilfe", "Klassifizierung"],
  sla: ["SLA-Seite", "Countdown", "Eskalationen"],
  approvals: ["Freigaben", "Ticket-Panel", "Workflows"],
  automation: ["Automatisierung"],
  csat: ["Auswertung", "Umfragen"],
  audit: ["Audit-Log"],
  integrations: ["Integrationen"],
  remote_support: ["Connector", "Ticket-Panel"],
  status: ["Systemstatus"]
};
var APP_MODULE_DEPENDENTS = {
  knowledge: [AppModuleCode.AI]
};
var APP_MODULE_DEFAULTS = {
  incidents: true,
  problems: true,
  changes: true,
  queues: true,
  assets: true,
  knowledge: true,
  ai: true,
  sla: true,
  approvals: true,
  automation: true,
  csat: true,
  audit: true,
  integrations: true,
  remote_support: true,
  status: true
};
var enabledModulesSchema = z.array(appModuleCodeSchema);
var appModuleCatalogItemSchema = z.object({
  code: appModuleCodeSchema,
  label: z.string(),
  description: z.string(),
  group: appModuleGroupSchema,
  enabled: z.boolean(),
  locked: z.boolean(),
  dependents: z.array(appModuleCodeSchema),
  hides: z.array(z.string())
});
var appModuleCatalogSchema = z.object({
  items: z.array(appModuleCatalogItemSchema),
  updatedAt: z.iso.datetime().nullable(),
  enabledModules: enabledModulesSchema
});
var patchModulesRequestSchema = z.object({
  code: appModuleCodeSchema,
  enabled: z.boolean()
});
var patchModulesResponseSchema = appModuleCatalogSchema.extend({
  changed: z.array(appModuleCodeSchema)
});
function dependentsOf(code) {
  return [...APP_MODULE_DEPENDENTS[code] ?? []];
}
function modulesTurnedOffWith(code) {
  return [code, ...dependentsOf(code)];
}
function isModuleEnabledInMap(modules, code) {
  return modules?.[code] !== false;
}
function enabledModulesFromMap(modules) {
  return APP_MODULE_CODES.filter((code) => isModuleEnabledInMap(modules, code));
}
var TICKET_TYPE_MODULE = {
  [TicketType.INCIDENT]: AppModuleCode.INCIDENTS,
  [TicketType.PROBLEM]: AppModuleCode.PROBLEMS,
  [TicketType.CHANGE]: AppModuleCode.CHANGES
};
function moduleForTicketType(type) {
  return TICKET_TYPE_MODULE[type] ?? null;
}
function isTicketTypeEnabled(type, enabledModules) {
  const code = moduleForTicketType(type);
  return !code || Boolean(enabledModules?.includes(code));
}
var NOTIFICATION_EVENT_MODULE = {
  [NotificationEventKey.SLA_WARNING]: AppModuleCode.SLA,
  [NotificationEventKey.APPROVAL_REQUESTED]: AppModuleCode.APPROVALS,
  [NotificationEventKey.APPROVAL_DECIDED]: AppModuleCode.APPROVALS,
  [NotificationEventKey.INCIDENT_BROADCAST]: AppModuleCode.INCIDENTS,
  [NotificationEventKey.AUTOMATION]: AppModuleCode.AUTOMATION,
  [NotificationEventKey.REMOTE_SESSION]: AppModuleCode.REMOTE_SUPPORT,
  [NotificationEventKey.CSAT]: AppModuleCode.CSAT
};
function isNotificationEventVisible(eventKey, enabledModules) {
  const code = NOTIFICATION_EVENT_MODULE[eventKey];
  return !code || Boolean(enabledModules?.includes(code));
}
var AUTOMATION_ACTION_MODULE = {
  assign_queue: AppModuleCode.QUEUES,
  set_sla_policy: AppModuleCode.SLA
};
var AUTOMATION_CONDITION_MODULE = {
  queueId: AppModuleCode.QUEUES
};
function isAutomationActionEnabled(type, enabledModules) {
  const code = AUTOMATION_ACTION_MODULE[type];
  return !code || Boolean(enabledModules?.includes(code));
}
function isAutomationConditionEnabled(field, enabledModules) {
  const code = AUTOMATION_CONDITION_MODULE[field];
  return !code || Boolean(enabledModules?.includes(code));
}
function catalogItemFor(code, enabled) {
  return {
    code,
    label: APP_MODULE_LABELS[code],
    description: APP_MODULE_DESCRIPTIONS[code],
    group: APP_MODULE_GROUP[code],
    enabled,
    locked: false,
    dependents: dependentsOf(code),
    hides: [...APP_MODULE_HIDES[code]]
  };
}
function buildAppModuleCatalog(modules, updatedAt = null) {
  return {
    items: APP_MODULE_CODES.map(
      (code) => catalogItemFor(code, isModuleEnabledInMap(modules, code))
    ),
    updatedAt,
    enabledModules: enabledModulesFromMap(modules)
  };
}
var addressFormSchema = z.enum(["sie", "du"]);
var personNamePartsSchema = z.enum(["given", "given_family"]);
var personNameOrderSchema = z.enum(["given_family", "family_given"]);
var voiceSchema = z.object({
  addressForm: addressFormSchema,
  personNameParts: personNamePartsSchema,
  personNameOrder: personNameOrderSchema
});
var DEFAULT_VOICE = {
  addressForm: "sie",
  personNameParts: "given_family",
  personNameOrder: "given_family"
};
var CANONICAL_PERSON_VOICE = DEFAULT_VOICE;
var updateVoiceRequestSchema = voiceSchema.partial().refine((body) => Object.keys(body).length > 0, { message: "voice.value.invalid" });

// src/auth.ts
var passwordSchema = z.string().min(12, "Mindestens 12 Zeichen").max(128).regex(/[a-z]/, "Mindestens ein Kleinbuchstabe").regex(/[A-Z]/, "Mindestens ein Gro\xDFbuchstabe").regex(/[0-9]/, "Mindestens eine Ziffer");
var totpCodeSchema = z.string().regex(/^\d{6}$/, "Der Code muss aus 6 Ziffern bestehen");
var loginRequestSchema = z.object({
  email: z.string().trim().email(),
  password: z.string().min(1).max(128),
  totpCode: totpCodeSchema.optional(),
  /** Persistent refresh cookie (stay signed in). Defaults to on. */
  rememberMe: z.boolean().optional().default(true)
});
var authUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  isActive: z.boolean(),
  totpEnabled: z.boolean(),
  roles: z.array(roleCodeSchema),
  permissions: z.array(permissionCodeSchema),
  /** Optional product modules that are currently on. Core areas are implied. */
  enabledModules: enabledModulesSchema,
  /**
   * Platform products the user may open (live ∩ enabled ∩ permission).
   * Planned products are omitted — see PRODUCT_META in products.ts.
   */
  products: sessionProductsSchema,
  profilePhotoUrl: z.string().nullable(),
  addressForm: addressFormSchema.optional(),
  personNameParts: personNamePartsSchema.optional(),
  personNameOrder: personNameOrderSchema.optional()
});
var loginOkResponseSchema = z.object({
  status: z.literal("ok"),
  user: authUserSchema
});
var totpRequiredResponseSchema = z.object({
  status: z.literal("totp_required"),
  message: z.string()
});
var totpSetupRequiredResponseSchema = z.object({
  status: z.literal("totp_setup_required"),
  message: z.string(),
  setupToken: z.string(),
  otpauthUrl: z.string(),
  secret: z.string()
});
var loginResponseSchema = z.discriminatedUnion("status", [
  loginOkResponseSchema,
  totpRequiredResponseSchema,
  totpSetupRequiredResponseSchema
]);
var totpEnableRequestSchema = z.object({
  totpCode: totpCodeSchema,
  setupToken: z.string().min(1),
  rememberMe: z.boolean().optional().default(true)
});
var forgotPasswordRequestSchema = z.object({
  email: z.string().trim().email()
});
var resetPasswordRequestSchema = z.object({
  token: z.string().min(1),
  password: passwordSchema
});
var messageResponseSchema = z.object({
  message: z.string()
});
var ssoStatusResponseSchema = z.object({
  configured: z.boolean()
});
var roleAssignmentCodeSchema = z.string().trim().min(2).max(41).regex(/^[A-Z][A-Z0-9_]{1,40}$/, "Ung\xFCltiger Code f\xFCr die Berechtigungsgruppe.");
var roleAssignmentSchema = z.object({
  code: roleAssignmentCodeSchema,
  name: z.string(),
  isSystem: z.boolean()
});
var managedRoleSchema = roleAssignmentSchema.extend({
  id: z.string().uuid(),
  description: z.string().nullable(),
  assignToNewUsers: z.boolean(),
  sortOrder: z.number().int(),
  permissions: z.array(permissionCodeSchema),
  memberCount: z.number().int().nonnegative()
});
var roleMemberSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string().email(),
  isActive: z.boolean()
});
var roleMembersSchema = z.object({
  id: z.string().uuid(),
  code: roleAssignmentCodeSchema,
  name: z.string(),
  isSystem: z.boolean(),
  count: z.number().int().nonnegative(),
  items: z.array(roleMemberSchema).max(50)
});
var managedRoleListSchema = z.object({
  items: z.array(managedRoleSchema)
});
var createRoleRequestSchema = z.object({
  name: z.string().trim().min(2).max(80),
  description: z.union([z.string().trim().max(500), z.null()]).optional(),
  assignToNewUsers: z.boolean().default(false),
  permissions: z.array(permissionCodeSchema).default([])
});
var updateRoleRequestSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  description: z.union([z.string().trim().max(500), z.null()]).optional(),
  assignToNewUsers: z.boolean().optional(),
  permissions: z.array(permissionCodeSchema).optional()
}).refine(
  (value) => value.name !== void 0 || value.description !== void 0 || value.assignToNewUsers !== void 0 || value.permissions !== void 0,
  { message: "Mindestens ein Feld muss gesetzt sein." }
);
var DISPLAY_FONTS = [
  "system",
  "inter",
  "source-sans-3",
  "ibm-plex-sans",
  "noto-sans"
];
var displayFontSchema = z.enum(DISPLAY_FONTS);
var DISPLAY_FONT_SIZES = ["15.5", "16", "16.5", "17", "17.5"];
var displayFontSizeSchema = z.enum(DISPLAY_FONT_SIZES);
var typographySchema = z.strictObject({
  displayFont: displayFontSchema,
  displayFontSize: displayFontSizeSchema
});
var DEFAULT_TYPOGRAPHY = {
  displayFont: "system",
  displayFontSize: "16"
};
var DISPLAY_FONT_LABELS = {
  system: "System",
  inter: "Inter",
  "source-sans-3": "Source Sans 3",
  "ibm-plex-sans": "IBM Plex Sans",
  "noto-sans": "Noto Sans"
};
var updateTypographyRequestSchema = typographySchema;
function typographyFromUnknown(font, size) {
  const parsed = typographySchema.safeParse({ displayFont: font, displayFontSize: size });
  return parsed.success ? parsed.data : { ...DEFAULT_TYPOGRAPHY };
}

// src/user.ts
var orgUnitSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  isActive: z.boolean()
});
var teamSummarySchema = orgUnitSchema.extend({
  departmentId: z.string().uuid().nullable()
});
var orgSlugSchema = z.string().trim().min(2).max(80).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug: nur Kleinbuchstaben, Ziffern und Bindestriche.");
function suggestOrgSlug(name) {
  const slug = name.replace(/[Ää]/g, "ae").replace(/[Öö]/g, "oe").replace(/[Üü]/g, "ue").replace(/ß/g, "ss").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);
  return slug.length >= 2 ? slug : "org";
}
var emptyToUndefined3 = (value) => value === "" || value === "all" ? void 0 : value;
var emptyToNullOrg = (value) => value === "" || value === void 0 ? null : value;
var optionalNullableOrgUuid = z.preprocess(
  emptyToNullOrg,
  z.union([z.string().uuid(), z.null()]).optional()
);
var queryBoolean = (defaultValue) => z.preprocess((value) => {
  if (value === "" || value === void 0 || value === null) {
    return defaultValue;
  }
  if (value === true || value === "true" || value === "1") {
    return true;
  }
  if (value === false || value === "false" || value === "0") {
    return false;
  }
  return value;
}, z.boolean().default(defaultValue));
var orgListQuerySchema = z.object({
  q: z.preprocess(emptyToUndefined3, z.string().trim().max(200).optional()),
  /** Admin lists include inactive by default; set false to hide deactivated rows. */
  includeInactive: queryBoolean(true),
  departmentId: z.preprocess(emptyToUndefined3, z.string().uuid().optional())
});
var managedDepartmentSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  isActive: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  userCount: z.number().int().nonnegative(),
  teamCount: z.number().int().nonnegative(),
  assetCount: z.number().int().nonnegative(),
  /** Hard-delete only when inactive and unused (no users/teams/assets). */
  canDelete: z.boolean()
});
var managedDepartmentListSchema = z.object({
  items: z.array(managedDepartmentSchema)
});
var createDepartmentRequestSchema = z.object({
  name: z.string().trim().min(2).max(80),
  slug: orgSlugSchema.optional()
});
var updateDepartmentRequestSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  slug: orgSlugSchema.optional(),
  isActive: z.boolean().optional()
}).refine(
  (value) => value.name !== void 0 || value.slug !== void 0 || value.isActive !== void 0,
  { message: "Mindestens ein Feld muss gesetzt sein." }
);
var createSiteRequestSchema = z.object({
  name: z.string().trim().min(2).max(80),
  slug: orgSlugSchema.optional()
});
var updateSiteRequestSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  slug: orgSlugSchema.optional(),
  isActive: z.boolean().optional()
}).refine(
  (value) => value.name !== void 0 || value.slug !== void 0 || value.isActive !== void 0,
  { message: "Mindestens ein Feld muss gesetzt sein." }
);
var managedSiteSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  isActive: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  userCount: z.number().int().nonnegative(),
  /** Hard-delete only when inactive and no users are assigned. */
  canDelete: z.boolean()
});
var managedSiteListSchema = z.object({
  items: z.array(managedSiteSchema)
});
var teamLeadSummarySchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string().email()
});
var managedTeamSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  departmentId: z.string().uuid().nullable(),
  department: orgUnitSchema.nullable(),
  leadId: z.string().uuid().nullable(),
  lead: teamLeadSummarySchema.nullable(),
  isActive: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  userCount: z.number().int().nonnegative(),
  queueCount: z.number().int().nonnegative(),
  /** Hard-delete only when inactive and unused (no users/queues). */
  canDelete: z.boolean()
});
var managedTeamListSchema = z.object({
  items: z.array(managedTeamSchema)
});
var createTeamRequestSchema = z.object({
  name: z.string().trim().min(2).max(80),
  slug: orgSlugSchema.optional(),
  departmentId: optionalNullableOrgUuid,
  leadId: optionalNullableOrgUuid
});
var updateTeamRequestSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  slug: orgSlugSchema.optional(),
  departmentId: optionalNullableOrgUuid,
  leadId: optionalNullableOrgUuid,
  isActive: z.boolean().optional()
}).refine(
  (value) => value.name !== void 0 || value.slug !== void 0 || value.departmentId !== void 0 || value.leadId !== void 0 || value.isActive !== void 0,
  { message: "Mindestens ein Feld muss gesetzt sein." }
);
var teamMemberSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string().email(),
  isActive: z.boolean(),
  /** True when this user is `Team.leadId` for the current team. */
  isLead: z.boolean(),
  profilePhotoUrl: z.string().nullable().optional()
});
var teamMembersResponseSchema = z.object({
  teamId: z.string().uuid(),
  teamName: z.string(),
  items: z.array(teamMemberSchema)
});
var addTeamMemberRequestSchema = z.object({
  userId: z.string().uuid()
});
var roleOptionSchema = z.object({
  id: z.string().uuid(),
  code: roleAssignmentCodeSchema,
  name: z.string(),
  isSystem: z.boolean()
});
var roleOptionListSchema = z.object({
  items: z.array(roleOptionSchema)
});
var userListItemSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  jobTitle: z.string().nullable(),
  isActive: z.boolean(),
  lastLoginAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  roles: z.array(roleCodeSchema),
  roleAssignments: z.array(roleAssignmentSchema),
  department: orgUnitSchema.nullable(),
  team: teamSummarySchema.nullable(),
  /** Teams this user leads (`Team.leadId`). */
  ledTeams: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  /** Microsoft-365 SSO only: no GB-Ticket password (`passwordHash` null and `entraObjectId` set). */
  ssoOnly: z.boolean(),
  /** Row waits for Entra sync to attach the directory object. No local password yet. */
  directoryLinkPending: z.boolean().optional(),
  /** Servable profile photo, or null when the person has none. */
  profilePhotoUrl: z.string().nullable().optional()
});
var userManagerSummarySchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string().email(),
  profilePhotoUrl: z.string().nullable().optional()
});
var userDetailSchema = userListItemSchema.extend({
  locale: z.string(),
  timezone: z.string(),
  phone: z.string().nullable(),
  personnelNumber: z.string().nullable(),
  totpEnabled: z.boolean(),
  updatedAt: z.iso.datetime(),
  managerId: z.string().uuid().nullable(),
  manager: userManagerSummarySchema.nullable(),
  managerLocked: z.boolean()
});
var userListStatusSchema = z.enum(["all", "active", "inactive"]);
var userListSortBySchema = z.enum([
  "lastName",
  "email",
  "displayName",
  "createdAt",
  "isActive"
]);
var userListQuerySchema = paginationQuerySchema.extend({
  q: z.preprocess(emptyToUndefined3, z.string().trim().max(200).optional()),
  role: z.preprocess(emptyToUndefined3, roleAssignmentCodeSchema.optional()),
  departmentId: z.preprocess(emptyToUndefined3, z.string().uuid().optional()),
  teamId: z.preprocess(emptyToUndefined3, z.string().uuid().optional()),
  status: userListStatusSchema.default("all"),
  sortBy: userListSortBySchema.default("lastName"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var userListResponseSchema = paginatedSchema(userListItemSchema);
var userLookupsQuerySchema = z.object({
  includeInactive: queryBoolean(false)
});
var userLookupsSchema = z.object({
  departments: z.array(orgUnitSchema),
  teams: z.array(teamSummarySchema),
  roles: z.array(roleOptionSchema)
});
var staffUserSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string()
});
var staffUserListSchema = z.object({
  items: z.array(staffUserSchema)
});
var optionalNullableUuid5 = z.union([z.string().uuid(), z.null()]).optional();
var personnelNumberSchema = z.string().trim().regex(
  /^[A-Za-z0-9]{1,20}$/,
  "Die Personalnummer darf nur Buchstaben und Ziffern enthalten (h\xF6chstens 20)."
);
var updateUserRequestSchema = z.object({
  firstName: z.string().trim().min(1).max(80).optional(),
  lastName: z.string().trim().min(1).max(80).optional(),
  displayName: z.string().trim().min(1).max(160).optional(),
  phone: z.union([z.string().trim().max(40), z.null()]).optional(),
  personnelNumber: z.union([personnelNumberSchema, z.null()]).optional(),
  departmentId: optionalNullableUuid5,
  teamId: optionalNullableUuid5,
  managerId: optionalNullableUuid5,
  managerLocked: z.boolean().optional(),
  isActive: z.boolean().optional()
});
var createLocalUserRequestSchema = z.object({
  email: z.string().trim().email("Bitte eine g\xFCltige E-Mail angeben.").max(200),
  firstName: z.string().trim().min(1, "Bitte den Vornamen angeben.").max(80),
  lastName: z.string().trim().min(1, "Bitte den Nachnamen angeben.").max(80),
  password: passwordSchema,
  departmentId: z.string().uuid().nullable().optional(),
  role: roleAssignmentCodeSchema.default("END_USER")
});
var setLocalPasswordRequestSchema = z.object({
  password: passwordSchema
});
var COLOR_MODES = ["system", "light", "dark"];
var colorModeSchema = z.enum(COLOR_MODES);
var DESIGN_VARIANTS = ["brand", "neutral"];
var designVariantSchema = z.enum(DESIGN_VARIANTS);
var updateOwnProfileRequestSchema = z.strictObject({
  phone: z.union([z.string().trim().max(40), z.null()]).optional(),
  jobTitle: z.union([z.string().trim().max(120), z.null()]).optional(),
  locale: z.literal("de-DE").optional(),
  timezone: z.string().trim().min(1).max(80).optional(),
  colorMode: z.union([colorModeSchema, z.null()]).optional(),
  designVariant: z.union([designVariantSchema, z.null()]).optional(),
  displayFont: z.union([displayFontSchema, z.null()]).optional(),
  displayFontSize: z.union([displayFontSizeSchema, z.null()]).optional(),
  siteId: z.union([z.string().uuid(), z.null()]).optional(),
  aboutMe: z.union([z.string().trim().max(500), z.null()]).optional()
}).superRefine((value, ctx) => {
  const fontMissing = value.displayFont === void 0;
  const sizeMissing = value.displayFontSize === void 0;
  if (fontMissing !== sizeMissing || value.displayFont == null !== (value.displayFontSize == null)) {
    ctx.addIssue({
      code: "custom",
      path: fontMissing ? ["displayFont"] : ["displayFontSize"],
      message: "Bitte Schrift und Gr\xF6\xDFe gemeinsam setzen oder beide leeren."
    });
  }
});
var ownProfileOptionSchema = z.object({
  id: z.string().uuid(),
  name: z.string()
});
var ownProfileOptionsSchema = z.object({
  departments: z.array(ownProfileOptionSchema),
  sites: z.array(ownProfileOptionSchema)
});
var calendarMonth = z.number().int().min(1).max(12);
var calendarDay = z.number().int().min(1).max(31);
function daysInMonth(month) {
  return [0, 31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month] ?? 31;
}
var updateOwnBirthdayRequestSchema = z.strictObject({
  birthdayMonth: z.union([calendarMonth, z.null()]).optional(),
  birthdayDay: z.union([calendarDay, z.null()]).optional(),
  showBirthday: z.boolean().optional(),
  hiredOn: z.union([
    z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Bitte ein Datum im Format JJJJ-MM-TT."),
    z.null()
  ]).optional()
}).superRefine((value, ctx) => {
  const month = value.birthdayMonth;
  const day = value.birthdayDay;
  if (month === void 0 !== (day === void 0)) {
    ctx.addIssue({
      code: "custom",
      path: month === void 0 ? ["birthdayMonth"] : ["birthdayDay"],
      message: "Bitte Monat und Tag gemeinsam setzen oder beide leeren."
    });
    return;
  }
  if (month == null !== (day == null)) {
    ctx.addIssue({
      code: "custom",
      path: month == null ? ["birthdayMonth"] : ["birthdayDay"],
      message: "Bitte Monat und Tag gemeinsam setzen oder beide leeren."
    });
    return;
  }
  if (month == null && day == null) {
    if (value.showBirthday === true) {
      ctx.addIssue({
        code: "custom",
        path: ["showBirthday"],
        message: "Ohne Geburtsdatum kann der Geburtstag nicht angezeigt werden."
      });
    }
    return;
  }
  if (month != null && day != null && day > daysInMonth(month)) {
    ctx.addIssue({
      code: "custom",
      path: ["birthdayDay"],
      message: "Dieser Tag existiert in dem Monat nicht."
    });
  }
});
var ownProfileSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  jobTitle: z.string().nullable(),
  /** Null when the person has not chosen a department. */
  departmentId: z.string().uuid().nullable(),
  departmentName: z.string().nullable(),
  /** True when nothing is chosen. False only when the chosen department is inactive. */
  departmentActive: z.boolean(),
  siteId: z.string().uuid().nullable(),
  siteName: z.string().nullable(),
  /** True when nothing is chosen. False only when the chosen site is inactive. */
  siteActive: z.boolean(),
  aboutMe: z.string().nullable(),
  entraObjectId: z.string().nullable(),
  lastLoginAt: z.iso.datetime().nullable(),
  phone: z.string().nullable(),
  birthdayMonth: z.number().int().nullable(),
  birthdayDay: z.number().int().nullable(),
  showBirthday: z.boolean(),
  /** Hire date as `YYYY-MM-DD`, or null. */
  hiredOn: z.string().nullable(),
  locale: z.string(),
  timezone: z.string(),
  /** Null while the person follows the tenant appearance. */
  colorMode: colorModeSchema.nullable(),
  designVariant: designVariantSchema.nullable(),
  /** Null while the person follows the tenant font. */
  displayFont: displayFontSchema.nullable(),
  displayFontSize: displayFontSizeSchema.nullable(),
  /** Local break-glass password is set (`passwordHash` present). */
  hasLocalPassword: z.boolean(),
  totpEnabled: z.boolean(),
  roles: z.array(roleCodeSchema),
  roleAssignments: z.array(roleAssignmentSchema),
  /** JPEG preview when scan is CLEAN/SKIPPED; null while pending or without photo. */
  profilePhotoUrl: z.string().nullable()
});
var assignUserRolesRequestSchema = z.object({
  roles: z.array(roleAssignmentCodeSchema).min(1, "Mindestens eine Berechtigungsgruppe ist erforderlich")
});
var assignUserRolesBulkRequestSchema = z.object({
  userIds: z.array(z.string().uuid()).min(1).max(100),
  roles: z.array(roleAssignmentCodeSchema).min(1).max(30),
  mode: z.enum(["add", "remove"])
}).refine((value) => new Set(value.userIds).size === value.userIds.length, {
  message: "Jede Person darf nur einmal vorkommen.",
  path: ["userIds"]
}).refine((value) => new Set(value.roles).size === value.roles.length, {
  message: "Jede Berechtigungsgruppe darf nur einmal vorkommen.",
  path: ["roles"]
});
var assignUserRolesBulkResultSchema = z.object({
  updated: z.number().int().nonnegative()
});

// src/asset.ts
var AssetStatus = {
  IN_STOCK: "IN_STOCK",
  IN_SERVICE: "IN_SERVICE",
  DEFECTIVE: "DEFECTIVE",
  DECOMMISSIONED: "DECOMMISSIONED"
};
var assetStatusSchema = z.enum(AssetStatus);
var ASSET_STATUS_LABELS = {
  IN_STOCK: "Auf Lager",
  IN_SERVICE: "In Betrieb",
  DEFECTIVE: "Defekt",
  DECOMMISSIONED: "Ausgemustert"
};
var assetDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var emptyToUndefined4 = (value) => value === "" || value === "all" ? void 0 : value;
var emptyToNull2 = (value) => value === "" ? null : value;
var optionalNullableUuid6 = z.union([z.string().uuid(), z.null()]).optional();
var optionalNullableDate = z.preprocess(
  emptyToNull2,
  z.union([assetDateSchema, z.null()]).optional()
);
var optionalNullableText = (max) => z.preprocess(emptyToNull2, z.union([z.string().trim().max(max), z.null()]).optional());
var assetTypeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string()
});
var assetPersonSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string().email(),
  profilePhotoUrl: z.string().nullable().optional()
});
var assetTicketRefSchema = z.object({
  id: z.string().uuid(),
  number: z.number().int().positive(),
  subject: z.string(),
  status: ticketStatusSchema,
  priority: ticketPrioritySchema,
  createdAt: z.iso.datetime()
});
var assetListItemSchema = z.object({
  id: z.string().uuid(),
  inventoryTag: z.string().nullable(),
  name: z.string(),
  serialNumber: z.string().nullable(),
  assetType: assetTypeSchema,
  status: assetStatusSchema,
  location: z.string().nullable(),
  department: orgUnitSchema.nullable(),
  assignedUser: assetPersonSchema.nullable(),
  purchaseDate: assetDateSchema.nullable(),
  warrantyUntil: assetDateSchema.nullable(),
  maintenanceUntil: assetDateSchema.nullable(),
  warrantyExpired: z.boolean(),
  maintenanceExpired: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var assetDetailSchema = assetListItemSchema.extend({
  notes: z.string().nullable(),
  tickets: z.array(assetTicketRefSchema)
});
var assetListSortBySchema = z.enum([
  "name",
  "inventoryTag",
  "status",
  "purchaseDate",
  "warrantyUntil",
  "updatedAt"
]);
var assetListQuerySchema = paginationQuerySchema.extend({
  q: z.preprocess(emptyToUndefined4, z.string().trim().max(200).optional()),
  status: z.preprocess(emptyToUndefined4, assetStatusSchema.optional()),
  assetTypeId: z.preprocess(emptyToUndefined4, z.string().uuid().optional()),
  departmentId: z.preprocess(emptyToUndefined4, z.string().uuid().optional()),
  assignedUserId: z.preprocess(emptyToUndefined4, z.string().uuid().optional()),
  ticketId: z.preprocess(emptyToUndefined4, z.string().uuid().optional()),
  sortBy: assetListSortBySchema.default("name"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var assetListResponseSchema = paginatedSchema(assetListItemSchema);
var assetLookupsSchema = z.object({
  types: z.array(assetTypeSchema),
  departments: z.array(orgUnitSchema),
  users: z.array(assetPersonSchema),
  statuses: z.array(
    z.object({
      code: assetStatusSchema,
      label: z.string()
    })
  )
});
var createAssetRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  assetTypeId: z.string().uuid(),
  status: assetStatusSchema.default(AssetStatus.IN_SERVICE),
  inventoryTag: optionalNullableText(40),
  serialNumber: optionalNullableText(80),
  location: optionalNullableText(200),
  departmentId: optionalNullableUuid6,
  assignedUserId: optionalNullableUuid6,
  purchaseDate: optionalNullableDate,
  warrantyUntil: optionalNullableDate,
  maintenanceUntil: optionalNullableDate,
  notes: optionalNullableText(5e3)
});
var updateAssetRequestSchema = createAssetRequestSchema.partial();
var createAssetFormSchema = z.object({
  name: z.string().trim().min(1, "helpdesk.asset.name.required").max(200, "Die Bezeichnung darf h\xF6chstens 200 Zeichen lang sein."),
  assetTypeId: z.string().uuid("helpdesk.asset.type.required"),
  status: assetStatusSchema,
  inventoryTag: z.string().trim().max(40).optional(),
  serialNumber: z.string().trim().max(80).optional(),
  location: z.string().trim().max(200).optional(),
  departmentId: z.string().optional(),
  assignedUserId: z.string().optional(),
  purchaseDate: z.string().optional(),
  warrantyUntil: z.string().optional(),
  maintenanceUntil: z.string().optional(),
  notes: z.string().max(5e3, "Die Notiz ist zu lang.").optional()
});
var linkAssetTicketRequestSchema = z.object({
  ticketId: z.string().uuid().optional(),
  ticketNumber: z.coerce.number().int().positive().optional()
}).refine((value) => value.ticketId !== void 0 || value.ticketNumber !== void 0, {
  message: "helpdesk.asset.ticket.required"
});
var assetOkSchema = z.object({ ok: z.literal(true) });
function toAssetRequest(values) {
  const none = (value) => value == null || value === "" || value === "none";
  return createAssetRequestSchema.parse({
    name: values.name,
    assetTypeId: values.assetTypeId,
    status: values.status,
    inventoryTag: blankToNull(values.inventoryTag),
    serialNumber: blankToNull(values.serialNumber),
    location: blankToNull(values.location),
    departmentId: none(values.departmentId) ? null : values.departmentId,
    assignedUserId: none(values.assignedUserId) ? null : values.assignedUserId,
    purchaseDate: blankToNull(values.purchaseDate),
    warrantyUntil: blankToNull(values.warrantyUntil),
    maintenanceUntil: blankToNull(values.maintenanceUntil),
    notes: blankToNull(values.notes)
  });
}
function blankToNull(value) {
  if (value == null || value === "") {
    return null;
  }
  return value;
}
var SystemAuditAction = {
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILED: "LOGIN_FAILED",
  LOGOUT: "LOGOUT",
  TOTP_SETUP_STARTED: "TOTP_SETUP_STARTED",
  TOTP_ENABLED: "TOTP_ENABLED",
  TOTP_FAILED: "TOTP_FAILED",
  PASSWORD_RESET_REQUEST: "PASSWORD_RESET_REQUEST",
  PASSWORD_RESET: "PASSWORD_RESET",
  REFRESH_REUSE_DETECTED: "REFRESH_REUSE_DETECTED",
  USER_UPDATED: "USER_UPDATED",
  USER_DEACTIVATED: "USER_DEACTIVATED",
  USER_REACTIVATED: "USER_REACTIVATED",
  USER_ROLES_CHANGED: "USER_ROLES_CHANGED",
  ROLE_CREATED: "ROLE_CREATED",
  ROLE_UPDATED: "ROLE_UPDATED",
  ROLE_DELETED: "ROLE_DELETED",
  DEPARTMENT_CREATED: "DEPARTMENT_CREATED",
  DEPARTMENT_UPDATED: "DEPARTMENT_UPDATED",
  DEPARTMENT_DEACTIVATED: "DEPARTMENT_DEACTIVATED",
  DEPARTMENT_REACTIVATED: "DEPARTMENT_REACTIVATED",
  TEAM_CREATED: "TEAM_CREATED",
  TEAM_UPDATED: "TEAM_UPDATED",
  TEAM_DEACTIVATED: "TEAM_DEACTIVATED",
  TEAM_REACTIVATED: "TEAM_REACTIVATED",
  SITE_CREATED: "SITE_CREATED",
  SITE_UPDATED: "SITE_UPDATED",
  SITE_DEACTIVATED: "SITE_DEACTIVATED",
  SITE_REACTIVATED: "SITE_REACTIVATED",
  SITE_DELETED: "SITE_DELETED",
  KB_ARTICLE_CREATED: "KB_ARTICLE_CREATED",
  KB_ARTICLE_UPDATED: "KB_ARTICLE_UPDATED",
  KB_ARTICLE_PUBLISHED: "KB_ARTICLE_PUBLISHED",
  KB_ARTICLE_ARCHIVED: "KB_ARTICLE_ARCHIVED",
  ASSET_CREATED: "ASSET_CREATED",
  ASSET_UPDATED: "ASSET_UPDATED",
  ASSET_DELETED: "ASSET_DELETED",
  ASSET_TICKET_LINKED: "ASSET_TICKET_LINKED",
  ASSET_TICKET_UNLINKED: "ASSET_TICKET_UNLINKED",
  CONNECTOR_ENROLLMENT_CODE_ISSUED: "CONNECTOR_ENROLLMENT_CODE_ISSUED",
  CONNECTOR_REGISTERED: "CONNECTOR_REGISTERED",
  CONNECTOR_REPAIRED: "CONNECTOR_REPAIRED",
  CONNECTOR_REVOKED: "CONNECTOR_REVOKED",
  AUTOMATION_RULE_CREATED: "AUTOMATION_RULE_CREATED",
  AUTOMATION_RULE_UPDATED: "AUTOMATION_RULE_UPDATED",
  AUTOMATION_RULE_TOGGLED: "AUTOMATION_RULE_TOGGLED",
  AUTOMATION_RULE_DELETED: "AUTOMATION_RULE_DELETED",
  SLA_CALENDAR_UPDATED: "SLA_CALENDAR_UPDATED",
  SLA_HOLIDAY_ADDED: "SLA_HOLIDAY_ADDED",
  SLA_HOLIDAY_REMOVED: "SLA_HOLIDAY_REMOVED",
  SLA_POLICY_CREATED: "SLA_POLICY_CREATED",
  SLA_POLICY_UPDATED: "SLA_POLICY_UPDATED",
  SLA_ESCALATION_CREATED: "SLA_ESCALATION_CREATED",
  SLA_ESCALATION_UPDATED: "SLA_ESCALATION_UPDATED",
  SLA_ESCALATION_DELETED: "SLA_ESCALATION_DELETED",
  APPROVAL_WORKFLOW_CREATED: "APPROVAL_WORKFLOW_CREATED",
  APPROVAL_WORKFLOW_UPDATED: "APPROVAL_WORKFLOW_UPDATED",
  APPROVAL_WORKFLOW_DELETED: "APPROVAL_WORKFLOW_DELETED",
  APPROVAL_STEP_CREATED: "APPROVAL_STEP_CREATED",
  APPROVAL_STEP_UPDATED: "APPROVAL_STEP_UPDATED",
  APPROVAL_STEP_DELETED: "APPROVAL_STEP_DELETED",
  KB_CATEGORY_CREATED: "KB_CATEGORY_CREATED",
  KB_CATEGORY_UPDATED: "KB_CATEGORY_UPDATED",
  KB_TICKET_LINKED: "KB_TICKET_LINKED",
  KB_TICKET_UNLINKED: "KB_TICKET_UNLINKED",
  KB_AI_PROPOSAL_APPLIED: "KB_AI_PROPOSAL_APPLIED",
  KB_AI_PROPOSAL_REJECTED: "KB_AI_PROPOSAL_REJECTED",
  INTEGRATION_UPDATED: "INTEGRATION_UPDATED",
  WEBHOOK_CREATED: "WEBHOOK_CREATED",
  WEBHOOK_UPDATED: "WEBHOOK_UPDATED",
  WEBHOOK_DELETED: "WEBHOOK_DELETED",
  MODULES_UPDATED: "MODULES_UPDATED",
  PRODUCTS_UPDATED: "PRODUCTS_UPDATED",
  MODULE_INSTALLED: "MODULE_INSTALLED",
  MODULE_REMOVED: "MODULE_REMOVED",
  MODULE_UPGRADED: "MODULE_UPGRADED",
  MODULE_SETUP_COMPLETED: "MODULE_SETUP_COMPLETED",
  AI_CONNECTION_UPDATED: "AI_CONNECTION_UPDATED",
  MAIL_CONNECTION_UPDATED: "MAIL_CONNECTION_UPDATED",
  MAIL_CONNECTION_PROBED: "MAIL_CONNECTION_PROBED",
  INTRANET_AI_DRAFT: "INTRANET_AI_DRAFT",
  INTRANET_AI_ASK: "INTRANET_AI_ASK",
  INTRANET_MODULE_INSTANCE_CREATED: "intranet.module_instance_created",
  INTRANET_MODULE_INSTANCE_UPDATED: "intranet.module_instance_updated",
  INTRANET_MODULE_INSTANCE_DELETED: "intranet.module_instance_deleted",
  INTRANET_SITE_DIARY_SUBMITTED: "intranet.site_diary_submitted",
  INTRANET_SITE_DIARY_RESENT: "intranet.site_diary_resent",
  DIRECTORY_SYNC_REQUESTED: "DIRECTORY_SYNC_REQUESTED",
  TICKET_CATEGORY_CREATED: "TICKET_CATEGORY_CREATED",
  TICKET_CATEGORY_UPDATED: "TICKET_CATEGORY_UPDATED",
  TICKET_CATEGORY_DELETED: "TICKET_CATEGORY_DELETED",
  TICKET_TYPE_DEFINITION_CREATED: "TICKET_TYPE_DEFINITION_CREATED",
  TICKET_TYPE_DEFINITION_UPDATED: "TICKET_TYPE_DEFINITION_UPDATED",
  TICKET_TYPE_DEFINITION_DELETED: "TICKET_TYPE_DEFINITION_DELETED",
  TICKET_CATALOG_SETTINGS_UPDATED: "TICKET_CATALOG_SETTINGS_UPDATED",
  MAIL_POLICY_UPDATED: "MAIL_POLICY_UPDATED",
  MAIL_TEMPLATE_UPDATED: "MAIL_TEMPLATE_UPDATED",
  MAIL_TEMPLATE_RESET: "MAIL_TEMPLATE_RESET",
  LEAVE_OVERTIME_BOOKED: "LEAVE_OVERTIME_BOOKED",
  LEAVE_BALANCE_ADJUSTED: "LEAVE_BALANCE_ADJUSTED",
  LEAVE_AREAS_UPDATED: "LEAVE_AREAS_UPDATED",
  FLEET_LICENSE_CHECKED: "fleet.license_checked",
  FLEET_ASSIGNMENT_OVERRIDE: "fleet.assignment_override",
  FLEET_POOL_BOOKING_RELEASED: "fleet.pool_booking_released",
  FLEET_POOL_BOOKING_DELETED: "fleet.pool_booking_deleted",
  FLEET_POOL_BOOKING_UPDATED: "fleet.pool_booking_updated",
  FLEET_POOL_BOOKING_ENDED_EARLY: "fleet.pool_booking_ended_early",
  FLEET_ODOMETER_CORRECTED: "fleet.odometer_corrected",
  FLEET_VEHICLE_RELEASED: "fleet.vehicle_released",
  FLEET_VEHICLE_RETIRED: "fleet.vehicle_retired",
  FLEET_VEHICLE_DELETED: "fleet.vehicle_deleted",
  FLEET_DOCUMENT_DELETED: "fleet.document_deleted",
  FLEET_POLICY_UPDATED: "fleet.policy_updated",
  FLEET_TELEMATICS_TOKEN_ROTATED: "fleet.telematics_token_rotated",
  FLEET_CHARGE_STATION_CREATED: "fleet.charge_station_created",
  FLEET_CHARGE_STATION_PASSWORD_ROTATED: "fleet.charge_station_password_rotated",
  FLEET_CHARGE_TAG_LINKED: "fleet.charge_tag_linked",
  FLEET_LOAD_GROUP_CREATED: "fleet.load_group_created",
  FLEET_LOAD_GROUP_LIMIT_CHANGED: "fleet.load_group_limit_changed",
  FLEET_LOAD_GROUP_CONNECTOR_CHANGED: "fleet.load_group_connector_changed",
  FLEET_TARIFF_PRICE_CHANGED: "fleet.tariff_price_changed",
  FLEET_TARIFF_GUEST_SETTING_CHANGED: "fleet.tariff_guest_setting_changed",
  FLEET_TARIFF_QUIET_CHANGED: "fleet.tariff_quiet_changed",
  FLEET_TARIFF_CHIP_CREATED: "fleet.tariff_chip_created",
  FLEET_TARIFF_GUEST_RELEASED: "fleet.tariff_guest_released",
  FLEET_CHARGE_INVOICE_PAID: "fleet.charge_invoice_paid",
  FLEET_OCMF_KEY_CHANGED: "fleet.ocmf_key_changed",
  FLEET_OCMF_ENERGY_REPLACED: "fleet.ocmf_energy_replaced",
  FLEET_CHARGE_REMOTE_STARTED: "fleet.charge_remote_started",
  FLEET_CHARGE_REMOTE_STOPPED: "fleet.charge_remote_stopped",
  FLEET_CHARGE_UNLOCKED: "fleet.charge_unlocked",
  FLEET_CHARGE_RESET_SOFT: "fleet.charge_reset_soft",
  FLEET_CHARGE_RESET_HARD: "fleet.charge_reset_hard",
  FLEET_CHARGE_FIRMWARE_REQUESTED: "fleet.charge_firmware_requested",
  FLEET_LOAD_GROUP_DELETED: "fleet.load_group_deleted",
  FLEET_CONNECTOR_LIMIT_CHANGED: "fleet.connector_limit_changed",
  FLEET_TRIP_CLASSIFIED: "fleet.trip_classified",
  FLEET_FUEL_EXCEPTION_RESOLVED: "fleet.fuel_exception_resolved",
  FLEET_FUEL_IMPORTED: "fleet.fuel_imported",
  FLEET_AREAS_UPDATED: "fleet.areas_updated",
  MAINTENANCE_TEMPLATE_PUBLISHED: "MAINTENANCE_TEMPLATE_PUBLISHED",
  MAINTENANCE_TEMPLATE_DEACTIVATED: "MAINTENANCE_TEMPLATE_DEACTIVATED",
  MAINTENANCE_TEMPLATE_VERSION_REOPENED: "MAINTENANCE_TEMPLATE_VERSION_REOPENED",
  MAINTENANCE_TEMPLATE_VERSION_RETIRED: "MAINTENANCE_TEMPLATE_VERSION_RETIRED",
  MAINTENANCE_TEMPLATE_CLEANUP_DELETED: "MAINTENANCE_TEMPLATE_CLEANUP_DELETED",
  MAINTENANCE_TEMPLATE_CLEANUP_ARCHIVED: "MAINTENANCE_TEMPLATE_CLEANUP_ARCHIVED",
  MAINTENANCE_CUSTOMER_DEACTIVATED: "MAINTENANCE_CUSTOMER_DEACTIVATED",
  MAINTENANCE_PROJECT_DEACTIVATED: "MAINTENANCE_PROJECT_DEACTIVATED",
  MAINTENANCE_INSTALLATION_DEACTIVATED: "MAINTENANCE_INSTALLATION_DEACTIVATED",
  MAINTENANCE_WORK_ORDER_CREATED: "MAINTENANCE_WORK_ORDER_CREATED",
  MAINTENANCE_WORK_ORDER_ASSIGNED: "MAINTENANCE_WORK_ORDER_ASSIGNED",
  MAINTENANCE_WORK_ORDER_REASSIGNED_IN_PROGRESS: "MAINTENANCE_WORK_ORDER_REASSIGNED_IN_PROGRESS",
  MAINTENANCE_WORK_ORDER_CANCELLED: "MAINTENANCE_WORK_ORDER_CANCELLED",
  MAINTENANCE_WORK_ORDER_ARCHIVED: "MAINTENANCE_WORK_ORDER_ARCHIVED",
  MAINTENANCE_WORK_ORDER_RESTORED: "MAINTENANCE_WORK_ORDER_RESTORED",
  MAINTENANCE_WORK_ORDER_TRASHED: "MAINTENANCE_WORK_ORDER_TRASHED",
  MAINTENANCE_WORK_ORDER_RESTORED_FROM_TRASH: "MAINTENANCE_WORK_ORDER_RESTORED_FROM_TRASH",
  MAINTENANCE_WORK_ORDER_PURGED: "MAINTENANCE_WORK_ORDER_PURGED",
  MAINTENANCE_PROTOCOL_FINALIZED: "MAINTENANCE_PROTOCOL_FINALIZED",
  ONBOARDING_TEMPLATE_PUBLISHED: "ONBOARDING_TEMPLATE_PUBLISHED",
  ONBOARDING_TEMPLATE_DELETED: "ONBOARDING_TEMPLATE_DELETED",
  ONBOARDING_CASE_STARTED: "ONBOARDING_CASE_STARTED",
  ONBOARDING_CASE_COMPLETED: "ONBOARDING_CASE_COMPLETED",
  ONBOARDING_CASE_CANCELLED: "ONBOARDING_CASE_CANCELLED",
  ONBOARDING_CASE_REOPENED: "ONBOARDING_CASE_REOPENED",
  ONBOARDING_CASE_DATES_CHANGED: "ONBOARDING_CASE_DATES_CHANGED",
  ONBOARDING_TASK_SKIPPED: "ONBOARDING_TASK_SKIPPED",
  ONBOARDING_TASK_CANCELLED: "ONBOARDING_TASK_CANCELLED",
  ONBOARDING_TASK_REOPENED: "ONBOARDING_TASK_REOPENED",
  ONBOARDING_TASK_REASSIGNED: "ONBOARDING_TASK_REASSIGNED",
  ONBOARDING_DOCUMENT_ADDED: "ONBOARDING_DOCUMENT_ADDED",
  ONBOARDING_SYSTEM_ACTION: "ONBOARDING_SYSTEM_ACTION",
  MASTER_DATA_CHANGED: "MASTER_DATA_CHANGED",
  ARTICLE_CHANGED: "ARTICLE_CHANGED"
};
var AuditAction = {
  ...SystemAuditAction,
  ...TicketAuditAction
};
var SYSTEM_AUDIT_ACTION_LABELS = {
  LOGIN_SUCCESS: "Anmeldung",
  LOGIN_FAILED: "Anmeldung fehlgeschlagen",
  LOGOUT: "Abmeldung",
  TOTP_SETUP_STARTED: "2FA-Einrichtung gestartet",
  TOTP_ENABLED: "2FA aktiviert",
  TOTP_FAILED: "2FA-Code ung\xFCltig",
  PASSWORD_RESET_REQUEST: "Passwort-Reset angefordert",
  PASSWORD_RESET: "Passwort zur\xFCckgesetzt",
  REFRESH_REUSE_DETECTED: "Refresh-Token wiederverwendet",
  USER_UPDATED: "Benutzer ge\xE4ndert",
  USER_DEACTIVATED: "Benutzer deaktiviert",
  USER_REACTIVATED: "Benutzer reaktiviert",
  USER_ROLES_CHANGED: "Rollen ge\xE4ndert",
  ROLE_CREATED: "Berechtigungsgruppe erstellt",
  ROLE_UPDATED: "Berechtigungsgruppe ge\xE4ndert",
  ROLE_DELETED: "Berechtigungsgruppe gel\xF6scht",
  DEPARTMENT_CREATED: "Abteilung erstellt",
  DEPARTMENT_UPDATED: "Abteilung ge\xE4ndert",
  DEPARTMENT_DEACTIVATED: "Abteilung deaktiviert",
  DEPARTMENT_REACTIVATED: "Abteilung reaktiviert",
  TEAM_CREATED: "Team erstellt",
  TEAM_UPDATED: "Team ge\xE4ndert",
  TEAM_DEACTIVATED: "Team deaktiviert",
  TEAM_REACTIVATED: "Team reaktiviert",
  SITE_CREATED: "Standort erstellt",
  SITE_UPDATED: "Standort ge\xE4ndert",
  SITE_DEACTIVATED: "Standort deaktiviert",
  SITE_REACTIVATED: "Standort reaktiviert",
  SITE_DELETED: "Standort gel\xF6scht",
  KB_ARTICLE_CREATED: "KB-Artikel erstellt",
  KB_ARTICLE_UPDATED: "KB-Artikel ge\xE4ndert",
  KB_ARTICLE_PUBLISHED: "KB-Artikel ver\xF6ffentlicht",
  KB_ARTICLE_ARCHIVED: "KB-Artikel archiviert",
  ASSET_CREATED: "Asset erstellt",
  ASSET_UPDATED: "Asset ge\xE4ndert",
  ASSET_DELETED: "Asset gel\xF6scht",
  ASSET_TICKET_LINKED: "Asset mit Ticket verkn\xFCpft",
  ASSET_TICKET_UNLINKED: "Asset-Ticket-Verkn\xFCpfung gel\xF6st",
  CONNECTOR_ENROLLMENT_CODE_ISSUED: "Connector-Kopplungscode erzeugt",
  CONNECTOR_REGISTERED: "Connector gekoppelt",
  CONNECTOR_REPAIRED: "Connector neu gekoppelt",
  CONNECTOR_REVOKED: "Connector widerrufen",
  AUTOMATION_RULE_CREATED: "Automatisierungsregel erstellt",
  AUTOMATION_RULE_UPDATED: "Automatisierungsregel ge\xE4ndert",
  AUTOMATION_RULE_TOGGLED: "Automatisierungsregel umgeschaltet",
  AUTOMATION_RULE_DELETED: "Automatisierungsregel gel\xF6scht",
  SLA_CALENDAR_UPDATED: "SLA-Kalender ge\xE4ndert",
  SLA_HOLIDAY_ADDED: "SLA-Feiertag hinzugef\xFCgt",
  SLA_HOLIDAY_REMOVED: "SLA-Feiertag entfernt",
  SLA_POLICY_CREATED: "SLA-Policy erstellt",
  SLA_POLICY_UPDATED: "SLA-Policy ge\xE4ndert",
  SLA_ESCALATION_CREATED: "SLA-Eskalation erstellt",
  SLA_ESCALATION_UPDATED: "SLA-Eskalation ge\xE4ndert",
  SLA_ESCALATION_DELETED: "SLA-Eskalation gel\xF6scht",
  APPROVAL_WORKFLOW_CREATED: "Freigabe-Workflow erstellt",
  APPROVAL_WORKFLOW_UPDATED: "Freigabe-Workflow ge\xE4ndert",
  APPROVAL_WORKFLOW_DELETED: "Freigabe-Workflow gel\xF6scht",
  APPROVAL_STEP_CREATED: "Freigabestufe erstellt",
  APPROVAL_STEP_UPDATED: "Freigabestufe ge\xE4ndert",
  APPROVAL_STEP_DELETED: "Freigabestufe gel\xF6scht",
  KB_CATEGORY_CREATED: "KB-Kategorie erstellt",
  KB_CATEGORY_UPDATED: "KB-Kategorie ge\xE4ndert",
  KB_TICKET_LINKED: "KB-Artikel mit Ticket verkn\xFCpft",
  KB_TICKET_UNLINKED: "KB-Ticketverkn\xFCpfung gel\xF6st",
  KB_AI_PROPOSAL_APPLIED: "KB-KI-Vorschlag \xFCbernommen",
  KB_AI_PROPOSAL_REJECTED: "KB-KI-Vorschlag verworfen",
  INTEGRATION_UPDATED: "Integration ge\xE4ndert",
  WEBHOOK_CREATED: "Webhook angelegt",
  WEBHOOK_UPDATED: "Webhook ge\xE4ndert",
  WEBHOOK_DELETED: "Webhook gel\xF6scht",
  MODULES_UPDATED: "Module ge\xE4ndert",
  PRODUCTS_UPDATED: "Produkte ge\xE4ndert",
  MODULE_INSTALLED: "Modul installiert",
  MODULE_REMOVED: "Modul entfernt",
  MODULE_UPGRADED: "Modul aktualisiert",
  MODULE_SETUP_COMPLETED: "Modulauswahl abgeschlossen",
  AI_CONNECTION_UPDATED: "KI-Anschluss ge\xE4ndert",
  MAIL_CONNECTION_UPDATED: "E-Mail-Verbindung ge\xE4ndert",
  MAIL_CONNECTION_PROBED: "E-Mail-Verbindung gepr\xFCft",
  INTRANET_AI_DRAFT: "Intranet-Entwurf angefragt",
  INTRANET_AI_ASK: "Intranet-Frage gestellt",
  "intranet.module_instance_created": "Schnellzugriff-Modul angelegt",
  "intranet.module_instance_updated": "Schnellzugriff-Modul ge\xE4ndert",
  "intranet.module_instance_deleted": "Schnellzugriff-Modul gel\xF6scht",
  "intranet.site_diary_submitted": "Bautagesbericht abgeschickt",
  "intranet.site_diary_resent": "Bautagesbericht erneut gesendet",
  DIRECTORY_SYNC_REQUESTED: "Verzeichnis-Sync angesto\xDFen",
  TICKET_CATEGORY_CREATED: "Ticket-Kategorie erstellt",
  TICKET_CATEGORY_UPDATED: "Ticket-Kategorie ge\xE4ndert",
  TICKET_CATEGORY_DELETED: "Ticket-Kategorie gel\xF6scht",
  TICKET_TYPE_DEFINITION_CREATED: "Ticketart erstellt",
  TICKET_TYPE_DEFINITION_UPDATED: "Ticketart ge\xE4ndert",
  TICKET_TYPE_DEFINITION_DELETED: "Ticketart gel\xF6scht",
  TICKET_CATALOG_SETTINGS_UPDATED: "Ticket-Katalog ge\xE4ndert",
  MAIL_POLICY_UPDATED: "E-Mail-Politik ge\xE4ndert",
  MAIL_TEMPLATE_UPDATED: "E-Mail-Vorlage ge\xE4ndert",
  MAIL_TEMPLATE_RESET: "E-Mail-Vorlage zur\xFCckgesetzt",
  LEAVE_OVERTIME_BOOKED: "\xDCberstunden gebucht",
  LEAVE_BALANCE_ADJUSTED: "Urlaubssaldo korrigiert",
  LEAVE_AREAS_UPDATED: "Urlaubsbereiche ge\xE4ndert",
  "fleet.license_checked": "F\xFChrerscheinkontrolle erfasst",
  "fleet.assignment_override": "Zuordnung mit Compliance-Ausnahme",
  "fleet.pool_booking_released": "Pool-Buchung abgebucht",
  "fleet.pool_booking_deleted": "Pool-Buchung gel\xF6scht",
  "fleet.pool_booking_updated": "Pool-Buchung ge\xE4ndert",
  "fleet.pool_booking_ended_early": "Pool-Buchung vorzeitig beendet",
  "fleet.odometer_corrected": "Kilometerstand korrigiert",
  "fleet.vehicle_released": "Fahrzeug freigegeben",
  "fleet.vehicle_retired": "Fahrzeug stillgelegt",
  "fleet.vehicle_deleted": "Fahrzeug gel\xF6scht",
  "fleet.document_deleted": "Fuhrpark-Dokument gel\xF6scht",
  "fleet.policy_updated": "Fuhrpark-Richtlinie ge\xE4ndert",
  "fleet.telematics_token_rotated": "Telematik-Token erzeugt oder erneuert",
  "fleet.charge_station_created": "Ladestation angelegt",
  "fleet.charge_station_password_rotated": "Ladestations-Passwort erneuert",
  "fleet.charge_tag_linked": "Ladechip zugeordnet",
  "fleet.load_group_created": "Ladegruppe angelegt",
  "fleet.load_group_limit_changed": "Ladegruppen-Grenze ge\xE4ndert",
  "fleet.load_group_connector_changed": "Ladegruppen-Anschluss ge\xE4ndert",
  "fleet.tariff_price_changed": "Ladepreis ge\xE4ndert",
  "fleet.tariff_guest_setting_changed": "Gast-Freigabe umgestellt",
  "fleet.tariff_quiet_changed": "Rechnungs-Ruhezeit ge\xE4ndert",
  "fleet.tariff_chip_created": "Ladechip eingetragen",
  "fleet.tariff_guest_released": "Gast-Chip freigegeben",
  "fleet.charge_invoice_paid": "Gast-Rechnung als bezahlt markiert",
  "fleet.ocmf_key_changed": "Wallbox-Schl\xFCssel gesetzt oder geleert",
  "fleet.ocmf_energy_replaced": "Signierte Wattstunden \xFCbernommen",
  "fleet.charge_remote_started": "Fernstart",
  "fleet.charge_remote_stopped": "Fernstopp",
  "fleet.charge_unlocked": "Anschluss entriegelt",
  "fleet.charge_reset_soft": "Sanfter Neustart",
  "fleet.charge_reset_hard": "Harter Neustart",
  "fleet.charge_firmware_requested": "Firmware-Installation angefordert",
  "fleet.load_group_deleted": "Ladegruppe gel\xF6scht",
  "fleet.connector_limit_changed": "Anschluss-Ampere ge\xE4ndert",
  "fleet.trip_classified": "Fahrt klassifiziert",
  "fleet.fuel_exception_resolved": "Tankkarten-Ausnahme aufgel\xF6st",
  "fleet.fuel_imported": "Tankkartenimport ausgef\xFChrt",
  "fleet.areas_updated": "Fuhrpark-Bereiche ge\xE4ndert",
  MAINTENANCE_TEMPLATE_PUBLISHED: "Wartungsvorlage ver\xF6ffentlicht",
  MAINTENANCE_TEMPLATE_DEACTIVATED: "Wartungsvorlage deaktiviert",
  MAINTENANCE_TEMPLATE_VERSION_REOPENED: "Wartungsvorlage wieder als Entwurf",
  MAINTENANCE_TEMPLATE_VERSION_RETIRED: "Wartungsvorlage zur\xFCckgezogen",
  MAINTENANCE_TEMPLATE_CLEANUP_DELETED: "Wartungsvorlage automatisch gel\xF6scht",
  MAINTENANCE_TEMPLATE_CLEANUP_ARCHIVED: "Wartungsvorlage automatisch archiviert",
  MAINTENANCE_CUSTOMER_DEACTIVATED: "Wartungskunde deaktiviert",
  MAINTENANCE_PROJECT_DEACTIVATED: "Wartungsprojekt deaktiviert",
  MAINTENANCE_INSTALLATION_DEACTIVATED: "Wartungsanlage deaktiviert",
  MAINTENANCE_WORK_ORDER_CREATED: "Wartungsauftrag angelegt",
  MAINTENANCE_WORK_ORDER_ASSIGNED: "Wartungsauftrag zugewiesen",
  MAINTENANCE_WORK_ORDER_REASSIGNED_IN_PROGRESS: "Wartungsauftrag in Bearbeitung neu zugewiesen",
  MAINTENANCE_WORK_ORDER_CANCELLED: "Wartungsauftrag storniert",
  MAINTENANCE_WORK_ORDER_ARCHIVED: "Wartungsauftrag archiviert",
  MAINTENANCE_WORK_ORDER_RESTORED: "Wartungsauftrag wiederhergestellt",
  MAINTENANCE_WORK_ORDER_TRASHED: "Wartungsauftrag in den Papierkorb gelegt",
  MAINTENANCE_WORK_ORDER_RESTORED_FROM_TRASH: "Wartungsauftrag aus dem Papierkorb geholt",
  MAINTENANCE_WORK_ORDER_PURGED: "Wartungsauftrag endg\xFCltig gel\xF6scht",
  MAINTENANCE_PROTOCOL_FINALIZED: "Wartungsprotokoll finalisiert",
  ONBOARDING_TEMPLATE_PUBLISHED: "Eintrittsvorlage ver\xF6ffentlicht",
  ONBOARDING_TEMPLATE_DELETED: "Eintrittsvorlage gel\xF6scht",
  ONBOARDING_CASE_STARTED: "Eintrittsvorgang gestartet",
  ONBOARDING_CASE_COMPLETED: "Eintrittsvorgang abgeschlossen",
  ONBOARDING_CASE_CANCELLED: "Eintrittsvorgang abgebrochen",
  ONBOARDING_CASE_REOPENED: "Eintrittsvorgang wieder ge\xF6ffnet",
  ONBOARDING_CASE_DATES_CHANGED: "Eintrittsvorgang: Termine ge\xE4ndert",
  ONBOARDING_TASK_SKIPPED: "Eintrittsaufgabe \xFCbersprungen",
  ONBOARDING_TASK_CANCELLED: "Eintrittsaufgabe storniert",
  ONBOARDING_TASK_REOPENED: "Eintrittsaufgabe wieder ge\xF6ffnet",
  ONBOARDING_TASK_REASSIGNED: "Eintrittsaufgabe neu zugewiesen",
  ONBOARDING_DOCUMENT_ADDED: "Eintrittsdokument hinzugef\xFCgt",
  ONBOARDING_SYSTEM_ACTION: "Eintritt: Systemaktion ausgef\xFChrt",
  MASTER_DATA_CHANGED: "Stammdaten ge\xE4ndert",
  ARTICLE_CHANGED: "Artikel ge\xE4ndert"
};
var AUDIT_ACTION_LABELS = {
  ...SYSTEM_AUDIT_ACTION_LABELS,
  ...TICKET_AUDIT_ACTION_LABELS
};
var AUDIT_ACTIONS = Object.values(AuditAction);
var AuditEntityType = {
  USER: "User",
  ROLE: "Role",
  DEPARTMENT: "Department",
  TEAM: "Team",
  SITE: "Site",
  TICKET: "Ticket",
  KNOWLEDGE_BASE_ARTICLE: "KnowledgeBaseArticle",
  KNOWLEDGE_BASE_CATEGORY: "KnowledgeBaseCategory",
  KNOWLEDGE_BASE_AI_PROPOSAL: "KnowledgeBaseAiProposal",
  ASSET: "Asset",
  CONNECTOR_DEVICE: "ConnectorDevice",
  AUTOMATION_RULE: "AutomationRule",
  BUSINESS_CALENDAR: "BusinessCalendar",
  SLA_POLICY: "SlaPolicy",
  SLA_ESCALATION: "SlaEscalation",
  APPROVAL_WORKFLOW: "ApprovalWorkflow",
  INTEGRATION_CONFIG: "IntegrationConfig",
  WEBHOOK_ENDPOINT: "WebhookEndpoint",
  SYSTEM_MODULE_SETTING: "SystemModuleSetting",
  SYSTEM_PRODUCT_SETTING: "SystemProductSetting",
  MODULE_INSTALLATION: "ModuleInstallation",
  AI_CONNECTION: "AiConnection",
  MAIL_CONNECTION: "MailConnection",
  INTRANET_AI: "IntranetAi",
  TICKET_CATEGORY: "TicketCategory",
  TICKET_TYPE_DEFINITION: "TicketTypeDefinition",
  TICKET_CATALOG_SETTINGS: "TicketCatalogSettings",
  MAIL_POLICY: "MailPolicy",
  MAIL_TEMPLATE: "MailTemplate",
  INTRANET_MODULE_INSTANCE: "IntranetModuleInstance",
  INTRANET_MODULE_REPORT: "IntranetModuleReport",
  LEAVE_OVERTIME_ENTRY: "LeaveOvertimeEntry",
  LEAVE_BALANCE_ADJUSTMENT: "LeaveBalanceAdjustment",
  LEAVE_POLICY: "LeavePolicy",
  FLEET_LICENSE_CHECK: "FleetLicenseCheck",
  FLEET_ASSIGNMENT: "FleetAssignment",
  FLEET_ODOMETER_READING: "FleetOdometerReading",
  FLEET_VEHICLE: "FleetVehicle",
  FLEET_DOCUMENT: "FleetDocument",
  FLEET_TELEMATICS_CONNECTION: "FleetTelematicsConnection",
  FLEET_CHARGE_STATION: "FleetChargeStation",
  FLEET_CHARGE_TAG: "FleetChargeTag",
  FLEET_LOAD_GROUP: "FleetLoadGroup",
  FLEET_CHARGE_TARIFF: "FleetChargeTariff",
  FLEET_CHARGE_TARIFF_CHIP: "FleetChargeTariffChip",
  FLEET_CHARGE_INVOICE: "FleetChargeInvoice",
  FLEET_CHARGE_SESSION: "FleetChargeSession",
  FLEET_TRIP: "FleetTrip",
  FLEET_FUEL_TRANSACTION: "FleetFuelTransaction",
  FLEET_POLICY: "FleetPolicy",
  MAINTENANCE_TEMPLATE: "MaintenanceTemplate",
  MAINTENANCE_CUSTOMER: "MaintenanceCustomer",
  MAINTENANCE_PROJECT: "MaintenanceProject",
  MAINTENANCE_INSTALLATION: "MaintenanceInstallation",
  MAINTENANCE_WORK_ORDER: "MaintenanceWorkOrder",
  MAINTENANCE_PROTOCOL: "MaintenanceProtocol",
  ONBOARDING_TEMPLATE: "OnboardingTemplate",
  ONBOARDING_CASE: "OnboardingCase",
  ONBOARDING_TASK: "OnboardingTask",
  BUSINESS_PARTNER: "BusinessPartner",
  PAYMENT_TERM: "PaymentTerm",
  PAYMENT_METHOD: "PaymentMethod",
  CURRENCY: "Currency",
  UNIT_OF_MEASURE: "UnitOfMeasure",
  COST_CENTER: "CostCenter",
  ARTICLE: "Article",
  ARTICLE_GROUP: "ArticleGroup",
  ARTICLE_PURCHASE_PRICE: "ArticlePurchasePrice"
};
var AUDIT_ENTITY_TYPE_LABELS = {
  User: "Benutzer",
  Role: "Berechtigungsgruppe",
  Department: "Abteilung",
  Team: "Team",
  Site: "Standort",
  Ticket: "Ticket",
  KnowledgeBaseArticle: "Wissensartikel",
  KnowledgeBaseCategory: "KB-Kategorie",
  KnowledgeBaseAiProposal: "KB-KI-Vorschlag",
  Asset: "Asset",
  ConnectorDevice: "Connector-Ger\xE4t",
  AutomationRule: "Automatisierungsregel",
  BusinessCalendar: "Gesch\xE4ftskalender",
  SlaPolicy: "SLA-Policy",
  SlaEscalation: "SLA-Eskalation",
  ApprovalWorkflow: "Freigabe-Workflow",
  IntegrationConfig: "Integration",
  WebhookEndpoint: "Webhook",
  SystemModuleSetting: "Systemmodule",
  SystemProductSetting: "Produkte",
  ModuleInstallation: "Modulinstallation",
  AiConnection: "KI-Anschluss",
  MailConnection: "E-Mail-Verbindung",
  IntranetAi: "Intranet-KI",
  TicketCategory: "Ticket-Kategorie",
  TicketTypeDefinition: "Ticketart",
  TicketCatalogSettings: "Ticket-Katalog",
  MailPolicy: "E-Mail-Politik",
  MailTemplate: "E-Mail-Vorlage",
  IntranetModuleInstance: "Schnellzugriff-Modul",
  IntranetModuleReport: "Bautagesbericht",
  LeaveOvertimeEntry: "\xDCberstundenbuchung",
  LeaveBalanceAdjustment: "Urlaubskorrektur",
  LeavePolicy: "Urlaubsrichtlinie",
  FleetLicenseCheck: "F\xFChrerscheinkontrolle",
  FleetAssignment: "Fahrzeugzuordnung",
  FleetOdometerReading: "Kilometerstand",
  FleetVehicle: "Fahrzeug",
  FleetDocument: "Fuhrpark-Dokument",
  FleetTelematicsConnection: "Telematik-Verbindung",
  FleetChargeStation: "Ladestation",
  FleetChargeTag: "Ladechip",
  FleetLoadGroup: "Ladegruppe",
  FleetChargeTariff: "Ladetarif",
  FleetChargeTariffChip: "Gast- oder Mitarbeiterchip",
  FleetChargeInvoice: "Gast-Rechnung",
  FleetChargeSession: "Ladesitzung",
  FleetTrip: "Fahrt",
  FleetFuelTransaction: "Tankkarten-Umsatz",
  FleetPolicy: "Fuhrpark-Richtlinie",
  MaintenanceTemplate: "Wartungsvorlage",
  MaintenanceCustomer: "Wartungskunde",
  MaintenanceProject: "Wartungsprojekt",
  MaintenanceInstallation: "Wartungsanlage",
  MaintenanceWorkOrder: "Wartungsauftrag",
  MaintenanceProtocol: "Wartungsprotokoll",
  OnboardingTemplate: "Eintrittsvorlage",
  OnboardingCase: "Eintrittsvorgang",
  OnboardingTask: "Eintrittsaufgabe",
  BusinessPartner: "Gesch\xE4ftspartner",
  PaymentTerm: "Zahlungsbedingung",
  PaymentMethod: "Zahlungsart",
  Currency: "W\xE4hrung",
  UnitOfMeasure: "Mengeneinheit",
  CostCenter: "Kostenstelle",
  Article: "Artikel",
  ArticleGroup: "Artikelgruppe",
  ArticlePurchasePrice: "Artikelpreis"
};
function auditActionLabel(action) {
  return AUDIT_ACTION_LABELS[action] ?? action;
}
function auditEntityTypeLabel(entityType) {
  return AUDIT_ENTITY_TYPE_LABELS[entityType] ?? entityType;
}
var emptyToUndefined5 = (value) => value === "" || value === "all" ? void 0 : value;
var auditListQuerySchema = paginationQuerySchema.extend({
  q: z.preprocess(emptyToUndefined5, z.string().trim().max(200).optional()),
  actorId: z.preprocess(emptyToUndefined5, z.string().uuid().optional()),
  action: z.preprocess(emptyToUndefined5, z.string().trim().min(1).max(80).optional()),
  entityType: z.preprocess(emptyToUndefined5, z.string().trim().min(1).max(80).optional()),
  entityId: z.preprocess(emptyToUndefined5, z.string().trim().min(1).max(80).optional()),
  from: z.preprocess(emptyToUndefined5, z.iso.datetime().optional()),
  to: z.preprocess(emptyToUndefined5, z.iso.datetime().optional())
});
var auditLogActorSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string().email()
});
var auditLogItemSchema = z.object({
  id: z.string().uuid(),
  entityType: z.string(),
  entityId: z.string(),
  action: z.string(),
  actionLabel: z.string(),
  actor: auditLogActorSchema.nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  metadata: z.unknown().nullable(),
  createdAt: z.iso.datetime()
});
var auditListResponseSchema = paginatedSchema(auditLogItemSchema);
var auditLookupsSchema = z.object({
  actions: z.array(z.object({ code: z.string(), label: z.string() })),
  entityTypes: z.array(z.object({ code: z.string(), label: z.string() })),
  actors: z.array(auditLogActorSchema)
});
var AutomationTrigger = {
  /** A ticket was created (after routing, SLA and approval start). */
  TICKET_CREATED: "TICKET_CREATED",
  /** Ticket status changed. `previousStatus` / `status` conditions apply to from / to. */
  TICKET_STATUS_CHANGED: "TICKET_STATUS_CHANGED",
  /** Fields, category, priority or assignment changed by a user. */
  TICKET_UPDATED: "TICKET_UPDATED",
  /** Evaluated periodically for every open ticket (e.g. "24 h without activity"). */
  SCHEDULED: "SCHEDULED"
};
var automationTriggerSchema = z.enum(AutomationTrigger);
var AUTOMATION_TRIGGER_LABELS = {
  TICKET_CREATED: "Ticket wird erstellt",
  TICKET_STATUS_CHANGED: "Status \xE4ndert sich",
  TICKET_UPDATED: "Ticket wird ge\xE4ndert",
  SCHEDULED: "Zeitgesteuert (alle 5 Minuten gepr\xFCft)"
};
var AUTOMATION_TRIGGER_DESCRIPTIONS = {
  TICKET_CREATED: "L\xE4uft einmal, direkt nach dem Anlegen eines Tickets.",
  TICKET_STATUS_CHANGED: "L\xE4uft bei jedem Statuswechsel. Mit \u201EVorheriger Status\u201C und \u201EStatus\u201C l\xE4sst sich der \xDCbergang eingrenzen.",
  TICKET_UPDATED: "L\xE4uft, wenn Betreff, Priorit\xE4t, Kategorie oder Zuweisung ge\xE4ndert werden.",
  SCHEDULED: "Wird regelm\xE4\xDFig f\xFCr alle offenen Tickets gepr\xFCft. Pro Ticket wird die Regel erst wieder ausgef\xFChrt, wenn es neue Aktivit\xE4t gab."
};
var uuid = z.string().uuid();
var uuidList = z.array(uuid).min(1).max(50);
var tagValue = z.string().trim().min(1).max(40).regex(/^[\p{L}\p{N}_\-. ]+$/u, "Nur Buchstaben, Ziffern, Leerzeichen, \u201E-\u201C, \u201E_\u201C und \u201E.\u201C");
var automationTagSchema = tagValue;
var enumCondition = (field, schema) => z.discriminatedUnion("operator", [
  z.object({ field: z.literal(field), operator: z.literal("eq"), value: schema }),
  z.object({ field: z.literal(field), operator: z.literal("neq"), value: schema }),
  z.object({
    field: z.literal(field),
    operator: z.literal("in"),
    value: z.array(schema).min(1).max(20)
  }),
  z.object({
    field: z.literal(field),
    operator: z.literal("not_in"),
    value: z.array(schema).min(1).max(20)
  })
]);
var referenceCondition = (field) => z.discriminatedUnion("operator", [
  z.object({ field: z.literal(field), operator: z.literal("eq"), value: uuid }),
  z.object({ field: z.literal(field), operator: z.literal("neq"), value: uuid }),
  z.object({ field: z.literal(field), operator: z.literal("in"), value: uuidList }),
  z.object({ field: z.literal(field), operator: z.literal("not_in"), value: uuidList }),
  z.object({ field: z.literal(field), operator: z.literal("is_empty") }),
  z.object({ field: z.literal(field), operator: z.literal("is_not_empty") })
]);
var minutes = z.number().int().min(1).max(525600);
var automationConditionSchema = z.union([
  enumCondition("type", ticketTypeSchema),
  /** Current status (for TICKET_STATUS_CHANGED: the new status). */
  enumCondition("status", ticketStatusSchema),
  /** Only meaningful for TICKET_STATUS_CHANGED; never matches otherwise. */
  enumCondition("previousStatus", ticketStatusSchema),
  enumCondition("source", ticketSourceSchema),
  z.discriminatedUnion("operator", [
    z.object({
      field: z.literal("priority"),
      operator: z.literal("eq"),
      value: ticketPrioritySchema
    }),
    z.object({
      field: z.literal("priority"),
      operator: z.literal("neq"),
      value: ticketPrioritySchema
    }),
    z.object({
      field: z.literal("priority"),
      operator: z.literal("in"),
      value: z.array(ticketPrioritySchema).min(1).max(4)
    }),
    z.object({
      field: z.literal("priority"),
      operator: z.literal("not_in"),
      value: z.array(ticketPrioritySchema).min(1).max(4)
    }),
    /** Priority rank (LOW < MEDIUM < HIGH < CRITICAL) at least / at most. */
    z.object({
      field: z.literal("priority"),
      operator: z.literal("gte"),
      value: ticketPrioritySchema
    }),
    z.object({
      field: z.literal("priority"),
      operator: z.literal("lte"),
      value: ticketPrioritySchema
    })
  ]),
  /** Category match includes sub-categories (the ticket category or any ancestor). */
  referenceCondition("categoryId"),
  referenceCondition("queueId"),
  referenceCondition("assigneeId"),
  z.discriminatedUnion("operator", [
    z.object({ field: z.literal("tags"), operator: z.literal("contains"), value: tagValue }),
    z.object({ field: z.literal("tags"), operator: z.literal("not_contains"), value: tagValue })
  ]),
  /** Literal, case-insensitive substring match — no regular expressions by design. */
  z.discriminatedUnion("operator", [
    z.object({
      field: z.literal("subject"),
      operator: z.literal("contains"),
      value: z.string().trim().min(1).max(200)
    }),
    z.object({
      field: z.literal("subject"),
      operator: z.literal("not_contains"),
      value: z.string().trim().min(1).max(200)
    })
  ]),
  /** Minutes since the last activity (comment, status, assignment, edit). */
  z.discriminatedUnion("operator", [
    z.object({ field: z.literal("inactivityMinutes"), operator: z.literal("gte"), value: minutes }),
    z.object({ field: z.literal("inactivityMinutes"), operator: z.literal("lte"), value: minutes })
  ]),
  /** Minutes since the ticket was created. */
  z.discriminatedUnion("operator", [
    z.object({ field: z.literal("ageMinutes"), operator: z.literal("gte"), value: minutes }),
    z.object({ field: z.literal("ageMinutes"), operator: z.literal("lte"), value: minutes })
  ])
]);
var AUTOMATION_CONDITION_FIELDS = [
  "type",
  "status",
  "previousStatus",
  "priority",
  "categoryId",
  "queueId",
  "assigneeId",
  "source",
  "tags",
  "subject",
  "inactivityMinutes",
  "ageMinutes"
];
var AUTOMATION_CONDITION_FIELD_LABELS = {
  type: "Tickettyp",
  status: "Status",
  previousStatus: "Vorheriger Status",
  priority: "Priorit\xE4t",
  categoryId: "Kategorie (inkl. Unterkategorien)",
  queueId: "Warteschlange",
  assigneeId: "Zugewiesener Agent",
  source: "Eingangskanal",
  tags: "Tag",
  subject: "Betreff",
  inactivityMinutes: "Keine Aktivit\xE4t seit (Minuten)",
  ageMinutes: "Ticket-Alter (Minuten)"
};
var AUTOMATION_OPERATOR_LABELS = {
  eq: "ist",
  neq: "ist nicht",
  in: "ist eine von",
  not_in: "ist keine von",
  is_empty: "ist leer",
  is_not_empty: "ist gesetzt",
  contains: "enth\xE4lt",
  not_contains: "enth\xE4lt nicht",
  gte: "mindestens",
  lte: "h\xF6chstens"
};
var AUTOMATION_FIELD_OPERATORS = {
  type: ["eq", "neq", "in", "not_in"],
  status: ["eq", "neq", "in", "not_in"],
  previousStatus: ["eq", "neq", "in", "not_in"],
  priority: ["eq", "neq", "in", "not_in", "gte", "lte"],
  categoryId: ["eq", "neq", "in", "not_in", "is_empty", "is_not_empty"],
  queueId: ["eq", "neq", "in", "not_in", "is_empty", "is_not_empty"],
  assigneeId: ["eq", "neq", "in", "not_in", "is_empty", "is_not_empty"],
  source: ["eq", "neq", "in", "not_in"],
  tags: ["contains", "not_contains"],
  subject: ["contains", "not_contains"],
  inactivityMinutes: ["gte", "lte"],
  ageMinutes: ["gte", "lte"]
};
var AUTOMATION_TRIGGER_ONLY_FIELDS = {
  previousStatus: AutomationTrigger.TICKET_STATUS_CHANGED,
  inactivityMinutes: AutomationTrigger.SCHEDULED
};
var AutomationNotifyRecipient = {
  ASSIGNEE: "ASSIGNEE",
  REQUESTER: "REQUESTER",
  QUEUE_TEAM: "QUEUE_TEAM",
  ROLE: "ROLE",
  USERS: "USERS"
};
var automationNotifyRecipientSchema = z.enum(AutomationNotifyRecipient);
var AUTOMATION_NOTIFY_RECIPIENT_LABELS = {
  ASSIGNEE: "Zugewiesener Agent",
  REQUESTER: "Anforderer",
  QUEUE_TEAM: "Team der Warteschlange",
  ROLE: "Alle Nutzer einer Rolle",
  USERS: "Bestimmte Nutzer"
};
var AUTOMATION_TEMPLATE_PLACEHOLDERS = [
  "ticket.number",
  "ticket.subject",
  "ticket.status",
  "ticket.priority",
  "ticket.type",
  "ticket.requester",
  "ticket.assignee",
  "ticket.queue",
  "ticket.category",
  "rule.name"
];
var template = (max) => z.string().trim().min(1).max(max);
var automationActionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("assign_user"), userId: uuid }),
  z.object({
    type: z.literal("assign_queue"),
    queueId: uuid,
    /** Keep the current agent (default: replace with the queue's default agent, if any). */
    keepAssignee: z.boolean().optional()
  }),
  z.object({ type: z.literal("set_priority"), priority: ticketPrioritySchema }),
  z.object({ type: z.literal("add_tag"), tag: tagValue }),
  z.object({ type: z.literal("remove_tag"), tag: tagValue }),
  z.object({ type: z.literal("set_sla_policy"), slaPolicyId: uuid }),
  z.object({
    type: z.literal("notify"),
    recipient: automationNotifyRecipientSchema,
    roleId: uuid.optional(),
    userIds: uuidList.optional(),
    title: template(140),
    body: template(1e3)
  })
]);
var AUTOMATION_ACTION_TYPES = [
  "assign_user",
  "assign_queue",
  "set_priority",
  "add_tag",
  "remove_tag",
  "set_sla_policy",
  "notify"
];
var AUTOMATION_ACTION_LABELS = {
  assign_user: "Agent zuweisen",
  assign_queue: "In Warteschlange verschieben",
  set_priority: "Priorit\xE4t setzen",
  add_tag: "Tag setzen",
  remove_tag: "Tag entfernen",
  set_sla_policy: "SLA-Policy setzen",
  notify: "Benachrichtigung senden"
};
var AUTOMATION_MAX_CONDITIONS = 12;
var AUTOMATION_MAX_ACTIONS = 8;
var automationConditionsSchema = z.array(automationConditionSchema).max(AUTOMATION_MAX_CONDITIONS);
var automationActionsSchema = z.array(automationActionSchema).min(1).max(AUTOMATION_MAX_ACTIONS);
function refineRule(rule, ctx) {
  rule.conditions.forEach((condition, index) => {
    const onlyFor = AUTOMATION_TRIGGER_ONLY_FIELDS[condition.field];
    if (onlyFor && onlyFor !== rule.trigger) {
      ctx.addIssue({
        code: "custom",
        path: ["conditions", index, "field"],
        message: `\u201E${AUTOMATION_CONDITION_FIELD_LABELS[condition.field]}\u201C ist nur beim Ausl\xF6ser \u201E${AUTOMATION_TRIGGER_LABELS[onlyFor]}\u201C m\xF6glich.`
      });
    }
  });
  if (rule.trigger === AutomationTrigger.SCHEDULED) {
    const timeBased = rule.conditions.some(
      (condition) => condition.field === "inactivityMinutes" || condition.field === "ageMinutes"
    );
    if (!timeBased) {
      ctx.addIssue({
        code: "custom",
        path: ["conditions"],
        message: "Zeitgesteuerte Regeln brauchen eine Bedingung zu \u201EKeine Aktivit\xE4t seit\u201C oder \u201ETicket-Alter\u201C."
      });
    }
  }
  rule.actions.forEach((action, index) => {
    if (action.type !== "notify") {
      return;
    }
    if (action.recipient === AutomationNotifyRecipient.ROLE && !action.roleId) {
      ctx.addIssue({
        code: "custom",
        path: ["actions", index, "roleId"],
        message: "Bitte eine Rolle w\xE4hlen."
      });
    }
    if (action.recipient === AutomationNotifyRecipient.USERS && !action.userIds?.length) {
      ctx.addIssue({
        code: "custom",
        path: ["actions", index, "userIds"],
        message: "Bitte mindestens einen Nutzer w\xE4hlen."
      });
    }
  });
}
var upsertAutomationRuleRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable().optional(),
  isActive: z.boolean().optional(),
  trigger: automationTriggerSchema,
  conditions: automationConditionsSchema,
  actions: automationActionsSchema,
  /** Rules run in ascending order; `stopProcessing` skips lower-priority rules for that event. */
  sortOrder: z.number().int().min(0).max(1e4).optional(),
  stopProcessing: z.boolean().optional()
}).superRefine(refineRule);
var automationRuleSchema = z.object({
  id: uuid,
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  trigger: automationTriggerSchema,
  conditions: automationConditionsSchema,
  actions: automationActionsSchema,
  sortOrder: z.number().int(),
  stopProcessing: z.boolean(),
  runCount: z.number().int().nonnegative(),
  lastRunAt: z.iso.datetime().nullable(),
  createdBy: z.object({ id: uuid, displayName: z.string() }).nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var automationRuleListResponseSchema = z.object({
  items: z.array(automationRuleSchema)
});
var automationRuleRunSchema = z.object({
  id: uuid,
  ruleId: uuid,
  ticketId: uuid,
  ticketNumber: z.number().int().positive().nullable(),
  trigger: automationTriggerSchema,
  succeeded: z.boolean(),
  summary: z.string(),
  error: z.string().nullable(),
  firedAt: z.iso.datetime()
});
var automationRuleRunListResponseSchema = z.object({
  items: z.array(automationRuleRunSchema)
});
var automationLookupsSchema = z.object({
  categories: z.array(z.object({ id: uuid, name: z.string(), parentId: uuid.nullable() })),
  queues: z.array(z.object({ id: uuid, name: z.string() })),
  agents: z.array(z.object({ id: uuid, displayName: z.string(), email: z.string() })),
  roles: z.array(z.object({ id: uuid, code: z.string(), name: z.string() })),
  slaPolicies: z.array(z.object({ id: uuid, name: z.string() })),
  tags: z.array(z.string())
});
var automationDryRunRequestSchema = z.object({
  ticketRef: z.string().trim().min(1).max(40),
  /** Only used for TICKET_STATUS_CHANGED rules to simulate the previous status. */
  previousStatus: ticketStatusSchema.optional()
});
var automationDryRunResponseSchema = z.object({
  matched: z.boolean(),
  ticketNumber: z.number().int().positive(),
  conditions: z.array(
    z.object({
      index: z.number().int().nonnegative(),
      label: z.string(),
      matched: z.boolean(),
      actual: z.string()
    })
  ),
  plannedActions: z.array(z.string())
});
var CSAT_MIN_RATING = 1;
var CSAT_MAX_RATING = 5;
var CSAT_REMINDER_DAYS = 3;
var CSAT_RATING_LABELS = {
  1: "Sehr unzufrieden",
  2: "Unzufrieden",
  3: "Neutral",
  4: "Zufrieden",
  5: "Sehr zufrieden"
};
var csatRatingSchema = z.number().int().min(CSAT_MIN_RATING).max(CSAT_MAX_RATING);
var submitCsatRequestSchema = z.object({
  rating: csatRatingSchema,
  comment: z.string().trim().max(2e3).optional()
});
var csatSurveySchema = z.object({
  id: z.string().uuid(),
  ticketId: z.string().uuid(),
  ticketNumber: z.number().int().positive(),
  ticketSubject: z.string(),
  rating: csatRatingSchema.nullable(),
  comment: z.string().nullable(),
  sentAt: z.iso.datetime(),
  reminderSentAt: z.iso.datetime().nullable(),
  respondedAt: z.iso.datetime().nullable(),
  canRespond: z.boolean()
});
var csatPendingListSchema = z.object({
  items: z.array(csatSurveySchema)
});
var csatTicketSurveySchema = z.object({
  survey: csatSurveySchema.nullable()
});
var csatActionPreviewSchema = z.object({
  tokenValid: z.boolean(),
  alreadyResponded: z.boolean(),
  cancelled: z.boolean(),
  ticketNumber: z.number().int().positive().nullable(),
  ticketSubject: z.string().nullable(),
  recipientDisplayName: z.string().nullable(),
  rating: csatRatingSchema.nullable()
});
var csatSubmitResponseSchema = z.object({
  ok: z.literal(true),
  rating: csatRatingSchema,
  ticketNumber: z.number().int().positive()
});
var emptyToUndefined6 = (value) => value === "" || value === "all" ? void 0 : value;
var csatAnalyticsQuerySchema = paginationQuerySchema.pick({ page: true, pageSize: true }).extend({
  from: z.preprocess(emptyToUndefined6, z.iso.datetime().optional()),
  to: z.preprocess(emptyToUndefined6, z.iso.datetime().optional()),
  departmentId: z.preprocess(emptyToUndefined6, z.string().uuid().optional()),
  teamId: z.preprocess(emptyToUndefined6, z.string().uuid().optional())
});
var namedCountSchema = z.object({
  id: z.string().nullable(),
  name: z.string(),
  count: z.number().int().nonnegative(),
  averageRating: z.number().nullable()
});
var csatAnalyticsSchema = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  summary: z.object({
    sent: z.number().int().nonnegative(),
    responded: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    responseRate: z.number().min(0).max(1),
    averageRating: z.number().nullable(),
    remindersSent: z.number().int().nonnegative()
  }),
  byRating: z.array(z.object({ rating: csatRatingSchema, count: z.number().int().nonnegative() })),
  byAgent: z.array(namedCountSchema),
  byCategory: z.array(namedCountSchema),
  byDay: z.array(
    z.object({
      date: z.string(),
      responded: z.number().int().nonnegative(),
      averageRating: z.number().nullable()
    })
  ),
  recentComments: z.array(
    z.object({
      id: z.string().uuid(),
      ticketNumber: z.number().int().positive(),
      ticketSubject: z.string(),
      rating: csatRatingSchema,
      comment: z.string(),
      agentName: z.string().nullable(),
      categoryName: z.string().nullable(),
      respondedAt: z.iso.datetime()
    })
  )
});
var csatAnalyticsLookupsSchema = z.object({
  departments: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  teams: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      departmentId: z.string().uuid().nullable()
    })
  )
});
var DASHBOARD_TIMEZONE = "Europe/Berlin";
var DASHBOARD_SERIES_DAYS = 14;
var DASHBOARD_ASSET_EXPIRING_SOON_DAYS = 90;
var DASHBOARD_ASSET_EXPIRING_WARN_DAYS = 30;
var DASHBOARD_ASSET_EXPIRING_LIMIT = 5;
var DASHBOARD_SOFTWARE_LICENSE_SLUG = "software";
var DASHBOARD_SERVER_ASSET_SLUG = "server";
var DASHBOARD_SERVER_ASSETS_LIMIT = 8;
var DASHBOARD_TOP_PROBLEMS_LIMIT = 8;
var DASHBOARD_TOP_PROBLEMS_SORT = "linkedIncidentCount desc, priority desc, updatedAt desc";
var dashboardDailyPointSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  created: z.number().int().nonnegative(),
  closed: z.number().int().nonnegative()
});
var dashboardHelpdeskKpisSchema = z.object({
  openTotal: z.number().int().nonnegative(),
  createdToday: z.number().int().nonnegative(),
  resolvedOrClosedToday: z.number().int().nonnegative(),
  unassignedOpen: z.number().int().nonnegative(),
  /** Null when SLA module is off. */
  slaAtRisk: z.number().int().nonnegative().nullable(),
  /** Null when Incidents module is off. */
  majorIncidentsOpen: z.number().int().nonnegative().nullable()
});
var dashboardOpenByPrioritySchema = z.object({
  priority: ticketPrioritySchema,
  count: z.number().int().nonnegative()
});
var dashboardTopQueueSchema = z.object({
  queueId: z.string().uuid(),
  name: z.string(),
  count: z.number().int().nonnegative()
});
var dashboardHelpdeskSummarySchema = z.object({
  kpis: dashboardHelpdeskKpisSchema,
  seriesDaily: z.array(dashboardDailyPointSchema),
  openByPriority: z.array(dashboardOpenByPrioritySchema),
  topQueues: z.array(dashboardTopQueueSchema)
});
var dashboardSecurityLinkSchema = z.object({
  id: z.string(),
  label: z.string(),
  url: z.string().url()
});
var dashboardAssetTypeCountSchema = z.object({
  typeId: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  count: z.number().int().nonnegative()
});
var dashboardAssetStatusCountSchema = z.object({
  status: assetStatusSchema,
  label: z.string(),
  count: z.number().int().nonnegative()
});
var DashboardAssetExpiringKind = {
  WARRANTY: "warranty",
  MAINTENANCE: "maintenance"
};
var dashboardAssetExpiringKindSchema = z.enum(DashboardAssetExpiringKind);
var dashboardAssetExpiringSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  inventoryTag: z.string().nullable(),
  kind: dashboardAssetExpiringKindSchema,
  until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** True when the date is within the short warn window (30 days). */
  urgent: z.boolean()
});
var dashboardAssetsSummarySchema = z.object({
  countsByType: z.array(dashboardAssetTypeCountSchema),
  countsByStatus: z.array(dashboardAssetStatusCountSchema),
  licenceCount: z.number().int().nonnegative(),
  expiringSoon: z.array(dashboardAssetExpiringSchema)
});
var dashboardTopProblemSchema = z.object({
  ticketId: z.string().uuid(),
  number: z.number().int().positive(),
  subject: z.string(),
  status: ticketStatusSchema,
  priority: ticketPrioritySchema,
  lifecycle: problemLifecycleSchema.nullable(),
  linkedIncidentCount: z.number().int().nonnegative(),
  updatedAt: z.iso.datetime()
});
var dashboardTopProblemsSummarySchema = z.object({
  /** Berlin calendar month of the ranking window (1–12). */
  month: z.number().int().min(1).max(12),
  year: z.number().int(),
  /** Machine-readable sort rule for this list. */
  sort: z.literal(DASHBOARD_TOP_PROBLEMS_SORT),
  items: z.array(dashboardTopProblemSchema)
});
var dashboardServerAssetSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: assetStatusSchema,
  statusLabel: z.string(),
  location: z.string().nullable(),
  inventoryTag: z.string().nullable()
});
var dashboardSummarySchema = z.object({
  generatedAt: z.iso.datetime(),
  timezone: z.literal(DASHBOARD_TIMEZONE),
  helpdesk: dashboardHelpdeskSummarySchema.nullable(),
  securityLinks: z.array(dashboardSecurityLinkSchema).optional(),
  /** Null without assets:read or when Assets module is off. */
  assets: dashboardAssetsSummarySchema.nullable(),
  /**
   * Null without tickets:read:all or when Problems module is off.
   * Window: PROBLEM tickets with `createdAt` in the current Berlin calendar month.
   */
  topProblemsThisMonth: dashboardTopProblemsSummarySchema.nullable(),
  /** Null without assets:read or when Assets module is off. Type slug `server`. */
  serverAssets: z.array(dashboardServerAssetSchema).nullable()
});
var FleetSolarState = {
  EXPORT: "export",
  IMPORT: "import",
  UNAVAILABLE: "unavailable"
};
var fleetSolarStateSchema = z.enum(FleetSolarState);
var FleetSolarForecastState = {
  OK: "ok",
  UNAVAILABLE: "unavailable"
};
var fleetSolarForecastStateSchema = z.enum(FleetSolarForecastState);
var fleetSolarAdviceSchema = z.enum(["Jetzt laden", "Jetzt nicht laden"]);
var fleetSolarGridLabelSchema = z.enum(["Einspeisung", "Bezug"]);
var FleetSolarBatteryFlow = {
  CHARGE: "charge",
  DISCHARGE: "discharge",
  IDLE: "idle"
};
var fleetSolarBatteryFlowSchema = z.enum(FleetSolarBatteryFlow);
var fleetSolarSchema = z.object({
  state: fleetSolarStateSchema,
  advice: fleetSolarAdviceSchema.nullable(),
  pvKw: z.number().nullable(),
  gridKw: z.number().nullable(),
  gridLabel: fleetSolarGridLabelSchema.nullable(),
  loadKw: z.number().nullable(),
  batterySoc: z.number().int().min(0).max(100).nullable(),
  batteryKw: z.number().nullable(),
  batteryFlow: fleetSolarBatteryFlowSchema.nullable(),
  observedAt: z.iso.datetime().nullable(),
  stale: z.boolean(),
  sunSentence: z.string().nullable(),
  surplusSentence: z.string().nullable(),
  forecastState: fleetSolarForecastStateSchema,
  sourceLabel: z.string().nullable()
});
var SOLARWEB_TOKEN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function solarWebPublicToken(input) {
  let url;
  try {
    url = new URL(input.trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:" || url.hostname !== "www.solarweb.com") return null;
  const path = url.pathname.replace(/\/$/, "");
  if (path !== "/PublicDisplay/PvSystem" && path !== "/ActualData/GetCompareDataForPublicDisplay") {
    return null;
  }
  const token = url.searchParams.get("token") ?? url.searchParams.get("PublicDisplayToken");
  if (!token || !SOLARWEB_TOKEN.test(token)) return null;
  return token;
}
function solarWebCompareUrl(input) {
  const token = solarWebPublicToken(input);
  if (!token) return null;
  return `https://www.solarweb.com/ActualData/GetCompareDataForPublicDisplay?PublicDisplayToken=${encodeURIComponent(token)}`;
}
function formatFleetKw(value) {
  const tenths = Math.round(value * 10) / 10;
  const negative = tenths < 0;
  const abs = Math.abs(tenths);
  const body = abs >= 10 ? String(Math.round(abs)) : abs.toFixed(1).replace(".", ",");
  return negative ? `-${body}` : body;
}

// src/fleet.ts
var isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Bitte ein Datum im Format JJJJ-MM-TT.");
var FleetVehicleCategory = {
  CAR: "car",
  VAN: "van",
  TRUCK: "truck",
  TRAILER: "trailer",
  OTHER: "other"
};
var fleetVehicleCategorySchema = z.enum(FleetVehicleCategory);
var FleetFuelType = {
  PETROL: "petrol",
  DIESEL: "diesel",
  ELECTRIC: "electric",
  HYBRID: "hybrid",
  OTHER: "other"
};
var fleetFuelTypeSchema = z.enum(FleetFuelType);
var FleetOwnership = {
  OWNED: "owned",
  LEASED: "leased"
};
var fleetOwnershipSchema = z.enum(FleetOwnership);
var FleetUsageMode = {
  ASSIGNED: "assigned",
  POOL: "pool"
};
var fleetUsageModeSchema = z.enum(FleetUsageMode);
var FleetOperationalStatus = {
  IN_SERVICE: "in_service",
  OUT_OF_SERVICE: "out_of_service",
  IN_WORKSHOP: "in_workshop",
  RETIRED: "retired"
};
var fleetOperationalStatusSchema = z.enum(FleetOperationalStatus);
var FleetInspectionKind = {
  HU: "hu",
  UVV_VEHICLE: "uvv_vehicle"
};
var fleetInspectionKindSchema = z.enum(FleetInspectionKind);
var FleetInspectionResult = {
  PASSED: "passed",
  FAILED: "failed"
};
var fleetInspectionResultSchema = z.enum(FleetInspectionResult);
var FLEET_LICENSE_CLASSES = ["B", "BE", "C1", "C", "CE", "D1", "D", "DE"];
var fleetLicenseClassSchema = z.enum(FLEET_LICENSE_CLASSES);
var optionalDate = isoDate.nullable().optional();
var optionalMonths = z.number().int().min(1).max(120).nullable().optional();
var fleetPolicySchema = z.object({
  missing: z.boolean(),
  id: z.string().uuid().nullable(),
  licenseCheckIntervalMonths: z.number().int(),
  instructionIntervalMonths: z.number().int(),
  reminderDaysLegal: z.number().int(),
  reminderDaysContract: z.number().int(),
  mileageStaleDays: z.number().int(),
  maxDailyKmWarning: z.number().int(),
  allowLicensePhoto: z.boolean(),
  licensePhotoRequired: z.boolean(),
  licensePhotoRetentionMonths: z.number().int(),
  telematicsEnabled: z.boolean(),
  telematicsStaleHours: z.number().int(),
  tripRetentionMonths: z.number().int(),
  solarWebUrl: z.string().nullable(),
  weatherLatitude: z.number().nullable(),
  weatherLongitude: z.number().nullable(),
  updatedAt: z.iso.datetime().nullable()
});
var updateFleetPolicyRequestSchema = z.object({
  licenseCheckIntervalMonths: z.number().int().min(1).max(36),
  instructionIntervalMonths: z.number().int().min(1).max(36),
  reminderDaysLegal: z.number().int().min(0).max(365),
  reminderDaysContract: z.number().int().min(0).max(365),
  mileageStaleDays: z.number().int().min(1).max(365),
  maxDailyKmWarning: z.number().int().min(1).max(1e4),
  allowLicensePhoto: z.boolean(),
  licensePhotoRequired: z.boolean(),
  licensePhotoRetentionMonths: z.number().int().min(1).max(120),
  telematicsEnabled: z.boolean(),
  telematicsStaleHours: z.number().int().min(1).max(720),
  tripRetentionMonths: z.number().int().min(1).max(120),
  solarWebUrl: z.string().trim().max(500).nullable().optional().refine((value) => value == null || value === "" || solarWebPublicToken(value) != null, {
    message: "Bitte den \xF6ffentlichen Solar.web-Link oder den Compare-Link einf\xFCgen."
  }),
  weatherLatitude: z.number().min(-90).max(90).nullable().optional(),
  weatherLongitude: z.number().min(-180).max(180).nullable().optional()
}).superRefine((value, ctx) => {
  if (value.weatherLatitude != null !== (value.weatherLongitude != null)) {
    ctx.addIssue({
      code: "custom",
      message: "Breitengrad und L\xE4ngengrad bitte zusammen angeben.",
      path: ["weatherLatitude"]
    });
  }
});
var fleetDeadlineSchema = z.object({
  dueOn: isoDate.nullable(),
  missingRegistration: z.boolean()
});
var fleetVehicleSchema = z.object({
  id: z.string().uuid(),
  licensePlate: z.string(),
  vin: z.string().nullable(),
  make: z.string(),
  model: z.string(),
  category: fleetVehicleCategorySchema,
  fuelType: fleetFuelTypeSchema,
  ownership: fleetOwnershipSchema,
  firstRegisteredOn: isoDate.nullable(),
  requiredLicenseClass: fleetLicenseClassSchema,
  usageMode: fleetUsageModeSchema,
  operationalStatus: fleetOperationalStatusSchema,
  departmentId: z.string().uuid().nullable(),
  departmentName: z.string().nullable(),
  leasingEndsOn: isoDate.nullable(),
  insuranceEndsOn: isoDate.nullable(),
  huFirstMonths: z.number().int().nullable(),
  huNextMonths: z.number().int().nullable(),
  uvvIntervalMonths: z.number().int(),
  serviceIntervalKm: z.number().int().nullable(),
  serviceIntervalMonths: z.number().int().nullable(),
  lastServiceKm: z.number().int().nullable(),
  lastServiceOn: isoDate.nullable(),
  privateUseAllowed: z.boolean(),
  locationNote: z.string().nullable(),
  telematicsRef: z.string().nullable(),
  tankCapacityLiters: z.number().int().nullable(),
  retiredAt: z.iso.datetime().nullable(),
  hu: fleetDeadlineSchema,
  uvv: fleetDeadlineSchema
});
var fleetVehicleListItemSchema = fleetVehicleSchema.extend({
  holderName: z.string().nullable(),
  currentKm: z.number().int().nullable()
});
var fleetVehicleDetailSchema = fleetVehicleListItemSchema;
var fleetVehicleListSchema = z.object({
  items: z.array(fleetVehicleListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1)
});
var fleetVehicleListQuerySchema = z.object({
  includeRetired: z.enum(["true", "false"]).optional(),
  /** When true, only vehicles assigned/booked to the actor today (even for manage). */
  mine: z.enum(["true", "false"]).optional(),
  q: z.string().trim().max(80).optional(),
  operationalStatus: fleetOperationalStatusSchema.optional(),
  usageMode: fleetUsageModeSchema.optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).optional()
});
var fleetVehicleWriteSchema = z.object({
  licensePlate: z.string().trim().min(1).max(20),
  vin: z.string().trim().max(17).nullable().optional(),
  make: z.string().trim().min(1).max(80),
  model: z.string().trim().min(1).max(80),
  category: fleetVehicleCategorySchema,
  fuelType: fleetFuelTypeSchema,
  ownership: fleetOwnershipSchema,
  firstRegisteredOn: optionalDate,
  requiredLicenseClass: fleetLicenseClassSchema.optional(),
  usageMode: fleetUsageModeSchema,
  departmentId: z.string().uuid().nullable().optional(),
  leasingEndsOn: optionalDate,
  insuranceEndsOn: optionalDate,
  huFirstMonths: optionalMonths,
  huNextMonths: optionalMonths,
  uvvIntervalMonths: z.number().int().min(1).max(120).optional(),
  serviceIntervalKm: z.number().int().min(1).max(2e6).nullable().optional(),
  serviceIntervalMonths: optionalMonths,
  lastServiceKm: z.number().int().min(0).max(2e6).nullable().optional(),
  lastServiceOn: optionalDate,
  privateUseAllowed: z.boolean().optional(),
  locationNote: z.string().trim().max(500).nullable().optional(),
  tankCapacityLiters: z.number().int().min(1).max(2e3).nullable().optional()
});
var updateFleetVehicleRequestSchema = fleetVehicleWriteSchema.partial().extend({
  operationalStatus: z.enum([
    FleetOperationalStatus.IN_SERVICE,
    FleetOperationalStatus.OUT_OF_SERVICE,
    FleetOperationalStatus.IN_WORKSHOP
  ]).optional(),
  telematicsRef: z.string().trim().max(120).nullable().optional()
});
var fleetInspectionSchema = z.object({
  id: z.string().uuid(),
  vehicleId: z.string().uuid(),
  kind: fleetInspectionKindSchema,
  performedOn: isoDate,
  result: fleetInspectionResultSchema,
  nextDueOn: isoDate,
  note: z.string().nullable(),
  recordedById: z.string().uuid(),
  createdAt: z.iso.datetime()
});
var fleetInspectionListSchema = z.object({
  items: z.array(fleetInspectionSchema)
});
var createFleetInspectionRequestSchema = z.object({
  kind: fleetInspectionKindSchema,
  performedOn: isoDate,
  result: fleetInspectionResultSchema,
  nextDueOn: isoDate.optional(),
  note: z.string().trim().max(1e3).optional()
});
var fleetDepartmentOptionSchema = z.object({
  id: z.string().uuid(),
  name: z.string()
});
var fleetDepartmentListSchema = z.object({
  items: z.array(fleetDepartmentOptionSchema)
});
var FleetLicenseCheckResult = {
  VALID: "valid",
  INVALID: "invalid",
  RESTRICTED: "restricted"
};
var fleetLicenseCheckResultSchema = z.enum(FleetLicenseCheckResult);
var fleetDriverProfileSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  displayName: z.string(),
  email: z.string(),
  profilePhotoUrl: z.string().nullable().optional(),
  licenseClasses: z.array(fleetLicenseClassSchema),
  cardValidUntil: isoDate.nullable(),
  entitlementValidUntil: isoDate.nullable(),
  updatedAt: z.iso.datetime()
});
var updateFleetDriverProfileRequestSchema = z.object({
  licenseClasses: z.array(fleetLicenseClassSchema).min(1),
  cardValidUntil: optionalDate,
  entitlementValidUntil: optionalDate
});
var fleetLicenseCheckSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  checkedAt: isoDate,
  checkedById: z.string().uuid(),
  checkedByName: z.string().nullable().optional(),
  result: fleetLicenseCheckResultSchema,
  classesConfirmed: z.array(fleetLicenseClassSchema),
  cardValidUntil: isoDate.nullable(),
  nextCheckDue: isoDate,
  note: z.string().nullable(),
  createdAt: z.iso.datetime()
});
var createFleetLicenseCheckRequestSchema = z.object({
  checkedAt: isoDate,
  result: fleetLicenseCheckResultSchema,
  classesConfirmed: z.array(fleetLicenseClassSchema).min(1),
  cardValidUntil: optionalDate,
  note: z.string().trim().max(1e3).optional()
});
var fleetDriverDetailSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  email: z.string(),
  profilePhotoUrl: z.string().nullable().optional(),
  profile: fleetDriverProfileSchema.nullable(),
  latestCheck: fleetLicenseCheckSchema.nullable(),
  checks: z.array(fleetLicenseCheckSchema),
  technician: z.boolean().optional(),
  latestInstruction: z.object({
    sessionId: z.string().uuid(),
    instructedOn: isoDate,
    title: z.string()
  }).nullable()
});
var fleetDriverVehicleSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("current"),
    plates: z.array(z.string().min(1)).min(1)
  }),
  z.object({ status: z.literal("past") }),
  z.object({ status: z.literal("none") })
]);
var fleetDriverListItemSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  email: z.string(),
  profilePhotoUrl: z.string().nullable().optional(),
  licenseClasses: z.array(fleetLicenseClassSchema),
  lastCheckedAt: isoDate.nullable(),
  nextCheckDue: isoDate.nullable(),
  lastInstructedOn: isoDate.nullable(),
  nextInstructionDue: isoDate.nullable(),
  technician: z.boolean().optional(),
  vehicle: fleetDriverVehicleSchema
});
var fleetDriverListSchema = z.object({
  items: z.array(fleetDriverListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1)
});
var fleetPersonSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string(),
  profilePhotoUrl: z.string().nullable().optional(),
  technician: z.boolean().optional()
});
var fleetPersonListSchema = z.object({
  items: z.array(fleetPersonSchema)
});
var createFleetInstructionRequestSchema = z.object({
  instructedOn: isoDate,
  title: z.string().trim().min(1).max(200),
  note: z.string().trim().max(1e3).optional(),
  userIds: z.array(z.string().uuid()).min(1)
});
var fleetInstructionSessionSchema = z.object({
  id: z.string().uuid(),
  instructedOn: isoDate,
  title: z.string(),
  note: z.string().nullable(),
  createdById: z.string().uuid(),
  createdAt: z.iso.datetime(),
  attendeeIds: z.array(z.string().uuid())
});
var fleetMeComplianceSchema = z.object({
  lastLicenseCheck: fleetLicenseCheckSchema.nullable(),
  nextLicenseCheckDue: isoDate.nullable(),
  lastInstruction: z.object({
    sessionId: z.string().uuid(),
    instructedOn: isoDate,
    title: z.string()
  }).nullable(),
  nextInstructionDue: isoDate.nullable()
});
var FleetAssignmentKind = {
  PERMANENT: "permanent",
  POOL: "pool"
};
var fleetAssignmentKindSchema = z.enum(FleetAssignmentKind);
var fleetAssignmentSchema = z.object({
  id: z.string().uuid(),
  vehicleId: z.string().uuid(),
  licensePlate: z.string().optional(),
  userId: z.string().uuid(),
  displayName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  profilePhotoUrl: z.string().nullable().optional(),
  kind: fleetAssignmentKindSchema,
  validFrom: isoDate,
  validTo: isoDate.nullable(),
  startsAt: z.iso.datetime().nullable().optional(),
  endsAt: z.iso.datetime().nullable().optional(),
  cancelledAt: z.iso.datetime().nullable().optional(),
  complianceOverrideReason: z.string().nullable(),
  createdById: z.string().uuid(),
  createdAt: z.iso.datetime(),
  warning: z.enum(["licenseRestricted"]).nullable().optional()
});
var fleetAssignmentListSchema = z.object({
  items: z.array(fleetAssignmentSchema)
});
var createFleetAssignmentRequestSchema = z.object({
  vehicleId: z.string().uuid(),
  userId: z.string().uuid(),
  kind: fleetAssignmentKindSchema,
  validFrom: isoDate,
  validTo: isoDate.nullable().optional(),
  complianceOverrideReason: z.string().trim().min(10).max(1e3).optional()
});
var timeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Bitte eine Uhrzeit im Format HH:MM.");
var createFleetPoolBookingRequestSchema = z.object({
  vehicleId: z.string().uuid(),
  userId: z.string().uuid().optional(),
  validFrom: isoDate,
  validTo: isoDate,
  /** Europe/Berlin. Both times or neither. Neither books the whole days. */
  fromTime: timeOfDay.optional(),
  toTime: timeOfDay.optional(),
  /** Ignored for pool:book; only assignments:manage may override via POST /assignments. */
  complianceOverrideReason: z.string().trim().min(10).max(1e3).optional()
});
var updateFleetPoolBookingRequestSchema = z.object({
  validFrom: isoDate,
  validTo: isoDate,
  fromTime: timeOfDay,
  toTime: timeOfDay
});
var closeFleetAssignmentRequestSchema = z.object({
  validTo: isoDate
});
var FleetOdometerSource = {
  DRIVER: "driver",
  WORKSHOP: "workshop",
  CORRECTION: "correction",
  TELEMATICS: "telematics",
  FUEL_CARD: "fuel_card"
};
var fleetOdometerSourceSchema = z.enum(FleetOdometerSource);
var fleetOdometerReadingSchema = z.object({
  id: z.string().uuid(),
  vehicleId: z.string().uuid(),
  readingKm: z.number().int(),
  recordedAt: z.iso.datetime(),
  recordedById: z.string().uuid().nullable(),
  recordedByName: z.string().nullable().optional(),
  source: fleetOdometerSourceSchema,
  externalKey: z.string().nullable().optional(),
  note: z.string().nullable(),
  warning: z.string().nullable()
});
var fleetOdometerListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).optional()
});
var fleetOdometerListSchema = z.object({
  currentKm: z.number().int().nullable(),
  items: z.array(fleetOdometerReadingSchema),
  total: z.number().int().nonnegative().optional(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).optional()
});
var createFleetOdometerRequestSchema = z.object({
  readingKm: z.number().int().min(0).max(1e7),
  note: z.string().trim().max(1e3).optional()
});
var createFleetOdometerCorrectionRequestSchema = z.object({
  readingKm: z.number().int().min(0).max(1e7),
  note: z.string().trim().min(1).max(1e3)
});
var fleetServiceDueSchema = z.object({
  byKm: z.object({
    status: z.enum(["ok", "due", "missing_baseline", "no_interval"]),
    message: z.string().nullable()
  }),
  byDate: z.object({
    status: z.enum(["ok", "due", "missing_baseline", "no_interval"]),
    message: z.string().nullable()
  })
});
var FLEET_FILE_MAX_BYTES = 10 * 1024 * 1024;
var FLEET_DAMAGE_PHOTO_MAX = 4;
var FLEET_DAMAGE_PHOTO_MAX_EDGE = 2e3;
var FLEET_DAMAGE_PHOTO_ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp"];
var FleetDamageKind = {
  DAMAGE: "damage",
  DEFECT: "defect"
};
var fleetDamageKindSchema = z.enum(FleetDamageKind);
var FleetDamageSeverity = {
  DRIVABLE: "drivable",
  NOT_DRIVABLE: "not_drivable"
};
var fleetDamageSeveritySchema = z.enum(FleetDamageSeverity);
var FleetDamageStatus = {
  REPORTED: "reported",
  TRIAGED: "triaged",
  IN_WORKSHOP: "in_workshop",
  DONE: "done",
  REJECTED: "rejected"
};
var fleetDamageStatusSchema = z.enum(FleetDamageStatus);
var FleetDamageEventType = {
  REPORTED: "reported",
  TRIAGED: "triaged",
  IN_WORKSHOP: "in_workshop",
  DONE: "done",
  REJECTED: "rejected",
  VEHICLE_HELD: "vehicle_held",
  VEHICLE_RELEASED: "vehicle_released"
};
var fleetDamageEventTypeSchema = z.enum(FleetDamageEventType);
var fleetDamageEventSchema = z.object({
  id: z.string().uuid(),
  reportId: z.string().uuid(),
  type: fleetDamageEventTypeSchema,
  actorId: z.string().uuid(),
  actorName: z.string().nullable().optional(),
  note: z.string().nullable(),
  createdAt: z.iso.datetime()
});
var FleetDocumentKind = {
  REGISTRATION: "registration",
  LEASING: "leasing",
  INSURANCE: "insurance",
  INSPECTION_REPORT: "inspection_report",
  INVOICE: "invoice",
  DAMAGE_PHOTO: "damage_photo",
  LICENSE_EVIDENCE: "license_evidence",
  OTHER: "other"
};
var fleetDocumentKindSchema = z.enum(FleetDocumentKind);
var fleetDocumentSchema = z.object({
  id: z.string().uuid(),
  vehicleId: z.string().uuid().nullable(),
  userId: z.string().uuid().nullable(),
  damageReportId: z.string().uuid().nullable(),
  licenseCheckId: z.string().uuid().nullable(),
  kind: fleetDocumentKindSchema,
  fileName: z.string(),
  mimeType: z.string(),
  byteSize: z.number().int(),
  scanStatus: z.enum(["PENDING", "CLEAN", "BLOCKED", "SKIPPED"]),
  createdAt: z.iso.datetime()
});
var fleetDocumentListSchema = z.object({
  items: z.array(fleetDocumentSchema)
});
var FLEET_DOCUMENT_ALLOWED_MIME = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];
var FleetWorkshopKind = {
  SERVICE: "service",
  REPAIR: "repair",
  INSPECTION: "inspection",
  OTHER: "other"
};
var fleetWorkshopKindSchema = z.enum(FleetWorkshopKind);
var fleetWorkshopVisitSchema = z.object({
  id: z.string().uuid(),
  vehicleId: z.string().uuid(),
  damageReportId: z.string().uuid().nullable(),
  kind: fleetWorkshopKindSchema,
  workshopName: z.string(),
  performedOn: isoDate,
  odometerKm: z.number().int().nullable(),
  summary: z.string(),
  costCents: z.number().int().nullable(),
  createdById: z.string().uuid(),
  createdAt: z.iso.datetime()
});
var fleetWorkshopVisitListSchema = z.object({
  items: z.array(fleetWorkshopVisitSchema)
});
var createFleetWorkshopVisitRequestSchema = z.object({
  vehicleId: z.string().uuid(),
  damageReportId: z.string().uuid().nullable().optional(),
  kind: fleetWorkshopKindSchema,
  workshopName: z.string().trim().min(1).max(200),
  performedOn: isoDate,
  odometerKm: z.number().int().min(0).max(1e7).nullable().optional(),
  summary: z.string().trim().min(1).max(4e3),
  costCents: z.number().int().min(0).max(1e8).nullable().optional()
});
function formatCentAsEuro(cents2) {
  const euros = cents2 / 100;
  return `${euros.toFixed(2).replace(".", ",")} \u20AC`;
}
function expireLicenseEvidence(docs, retentionMonths, todayIso) {
  const [todayY, todayM, todayD] = todayIso.split("-").map(Number);
  const todayUtc = Date.UTC(todayY, todayM - 1, todayD);
  const expired = [];
  for (const doc of docs) {
    const created = new Date(doc.createdAt);
    const expiry = new Date(
      Date.UTC(
        created.getUTCFullYear(),
        created.getUTCMonth() + retentionMonths,
        created.getUTCDate()
      )
    );
    if (expiry.getTime() < todayUtc) {
      expired.push(doc.id);
    }
  }
  return expired;
}
var fleetDamageReportSchema = z.object({
  id: z.string().uuid(),
  vehicleId: z.string().uuid(),
  licensePlate: z.string().optional(),
  reportedById: z.string().uuid(),
  reportedByName: z.string().nullable().optional(),
  kind: fleetDamageKindSchema,
  severity: fleetDamageSeveritySchema,
  status: fleetDamageStatusSchema,
  description: z.string(),
  locationNote: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  photos: z.array(fleetDocumentSchema).optional(),
  events: z.array(fleetDamageEventSchema).optional()
});
var fleetDamageListSchema = z.object({
  items: z.array(fleetDamageReportSchema),
  total: z.number().int().nonnegative().optional(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).optional()
});
var fleetDamageListQuerySchema = z.object({
  scope: z.enum(["mine", "all"]).optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).optional()
});
var createFleetDamageRequestSchema = z.object({
  vehicleId: z.string().uuid(),
  kind: fleetDamageKindSchema,
  severity: fleetDamageSeveritySchema,
  description: z.string().trim().min(1).max(4e3),
  locationNote: z.string().trim().max(500).optional().nullable()
});
var updateFleetDamageStatusRequestSchema = z.object({
  status: z.enum(["triaged", "in_workshop", "done", "rejected"]),
  note: z.string().trim().max(1e3).optional()
});
var fleetDamageEventListSchema = z.object({
  items: z.array(fleetDamageEventSchema)
});
var FleetTaskKind = {
  HU: "hu",
  UVV_VEHICLE: "uvv_vehicle",
  SERVICE: "service",
  LEASING: "leasing",
  INSURANCE: "insurance",
  LICENSE: "license",
  INSTRUCTION: "instruction",
  MILEAGE_STALE: "mileage_stale",
  TELEMATICS_STALE: "telematics_stale",
  FUEL_EXCEPTION: "fuel_exception",
  DAMAGE_OPEN: "damage_open",
  VEHICLE_HELD: "vehicle_held",
  MANUAL: "manual"
};
var fleetTaskKindSchema = z.enum(FleetTaskKind);
var fleetTaskStatusSchema = z.enum(["open", "cancelled"]);
var fleetTaskSchema = z.object({
  id: z.string(),
  kind: fleetTaskKindSchema,
  title: z.string(),
  dueOn: isoDate.nullable(),
  overdue: z.boolean(),
  subjectHref: z.string(),
  subjectLabel: z.string(),
  status: fleetTaskStatusSchema.optional()
});
var createFleetManualTaskRequestSchema = z.object({
  title: z.string().trim().min(1).max(200),
  dueOn: isoDate,
  assigneeId: z.string().uuid()
});
var fleetManualTaskResultSchema = z.object({
  id: z.string().uuid()
});
var fleetPageQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).optional()
});
var fleetDriverListQuerySchema = fleetPageQuerySchema.extend({
  q: z.string().optional()
});
var fleetTaskGroupSchema = z.enum(["overdue", "upcoming", "damages", "mileage", "held"]);
var fleetTaskListQuerySchema = fleetPageQuerySchema.extend({
  group: fleetTaskGroupSchema.optional(),
  status: fleetTaskStatusSchema.optional()
});
var fleetVehicleDeleteBlockedDetailsSchema = z.object({
  licensePlate: z.string(),
  make: z.string(),
  model: z.string(),
  reasons: z.array(z.string()),
  canRetire: z.boolean()
});
var fleetTaskListSchema = z.object({
  group: fleetTaskGroupSchema.nullable().optional(),
  items: z.array(fleetTaskSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1)
});
var fleetSummaryTodayRowSchema = z.object({
  vehicleId: z.string().uuid(),
  licensePlate: z.string(),
  holderName: z.string().nullable(),
  operationalStatus: fleetOperationalStatusSchema,
  currentKm: z.number().int().nullable(),
  huDueOn: isoDate.nullable(),
  huMissingRegistration: z.boolean()
});
var fleetSummarySchema = z.object({
  inService: z.number().int().nonnegative(),
  outOfService: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
  damageOpen: z.number().int().nonnegative(),
  telematicsStale: z.number().int().nonnegative(),
  fuelExceptions: z.number().int().nonnegative(),
  today: z.array(fleetSummaryTodayRowSchema)
});
var fleetMeAssignmentsQuerySchema = z.object({
  from: isoDate.optional(),
  to: isoDate.optional()
});
var FleetAreaCode = {
  VEHICLES: "vehicles",
  POOL: "pool",
  MILEAGE: "mileage",
  DAMAGE: "damage",
  COMPLIANCE: "compliance",
  INSTRUCTION: "instruction",
  DEADLINES: "deadlines",
  WORKSHOP: "workshop",
  TELEMATICS: "telematics",
  TRIPS: "trips",
  FUEL: "fuel"
};
var fleetAreaCodeSchema = z.enum(FleetAreaCode);
var FLEET_AREA_CODES = Object.values(FleetAreaCode);
var FLEET_AREA_SWITCHABLE = [
  FleetAreaCode.POOL,
  FleetAreaCode.MILEAGE,
  FleetAreaCode.DAMAGE,
  FleetAreaCode.COMPLIANCE,
  FleetAreaCode.INSTRUCTION,
  FleetAreaCode.DEADLINES,
  FleetAreaCode.WORKSHOP,
  FleetAreaCode.TELEMATICS,
  FleetAreaCode.TRIPS,
  FleetAreaCode.FUEL
];
var FLEET_AREA_LABELS = {
  vehicles: "Fahrzeuge",
  pool: "Pool buchen",
  mileage: "Kilometer",
  damage: "Schaden",
  compliance: "F\xFChrerscheinkontrolle",
  instruction: "UVV-Unterweisung",
  deadlines: "Fristen",
  workshop: "Werkstatt",
  telematics: "Telematik",
  trips: "Fahrten",
  fuel: "Tankkarten"
};
var FLEET_AREA_DESCRIPTIONS = {
  pool: "Poolfahrzeuge buchen. Vorhandene Buchungen bleiben.",
  mileage: "Neue Kilometerst\xE4nde. Vorhandene St\xE4nde bleiben lesbar.",
  damage: "Neue Schadenmeldungen. Vorhandene Meldungen bleiben.",
  compliance: "F\xFChrerscheinkontrolle, Aufgabe und Erinnerung dazu.",
  instruction: "UVV-Unterweisung der fahrenden Person. Die Unterweisung kann auch au\xDFerhalb liegen, zum Beispiel im T\xDCV-Portal.",
  deadlines: "HU, UVV-Pr\xFCfung am Fahrzeug, Service, Leasing und Versicherung.",
  workshop: "Werkstattbesuche und der Werkstattanteil der Kosten.",
  telematics: "Karte, neue Signale und die Aufgabe bei fehlendem Signal.",
  trips: "Fahrten lesen und einordnen. Unabh\xE4ngig von der Telematik.",
  fuel: "Tankkarten, Ums\xE4tze und der Kraftstoffanteil der Kosten."
};
var FleetAreaGroup = {
  DAILY: "daily",
  DUTIES: "duties",
  OPERATIONS: "operations"
};
var fleetAreaGroupSchema = z.enum(FleetAreaGroup);
var FLEET_AREA_GROUP_LABELS = {
  daily: "Alltag",
  duties: "Pflichten",
  operations: "Betrieb"
};
var FLEET_AREA_GROUP = {
  pool: FleetAreaGroup.DAILY,
  mileage: FleetAreaGroup.DAILY,
  damage: FleetAreaGroup.DAILY,
  compliance: FleetAreaGroup.DUTIES,
  instruction: FleetAreaGroup.DUTIES,
  deadlines: FleetAreaGroup.DUTIES,
  workshop: FleetAreaGroup.DUTIES,
  telematics: FleetAreaGroup.OPERATIONS,
  trips: FleetAreaGroup.OPERATIONS,
  fuel: FleetAreaGroup.OPERATIONS
};
var FLEET_AREA_HIDES = {
  pool: ["Buchen"],
  mileage: ["Kilometer"],
  damage: ["Melden", "Meldungsreiter"],
  compliance: ["F\xFChrerscheinkontrolle auf der Fahrerseite", "Aufgabe und Erinnerung dazu"],
  instruction: ["UVV-Unterweisung", "Erinnerung und die Sperre bei der Zuordnung"],
  deadlines: [
    "Aufgaben der Fahrzeugfristen",
    "HU",
    "UVV-Pr\xFCfung am Fahrzeug",
    "Service",
    "Leasing",
    "Versicherung"
  ],
  workshop: ["Werkstattreiter", "Werkstattanteil der Kosten"],
  telematics: ["Karte", "Ingest", "Signal-Aufgabe"],
  trips: ["Fahrten"],
  fuel: ["Tankkarten", "Tank-Ausnahmen", "Kraftstoffanteil der Kosten"]
};
var FLEET_AREA_DEPENDENTS = {};
var FLEET_AREA_DEFAULTS = {
  pool: true,
  mileage: true,
  damage: true,
  compliance: true,
  instruction: true,
  deadlines: true,
  workshop: true,
  telematics: false,
  trips: true,
  fuel: true
};
function isFleetAreaLocked(code) {
  return code === FleetAreaCode.VEHICLES;
}
function fleetAreaDependents(code) {
  return [...FLEET_AREA_DEPENDENTS[code] ?? []];
}
function isFleetAreaEnabled(areas, code, telematicsEnabled) {
  if (isFleetAreaLocked(code)) {
    return true;
  }
  const stored = areas?.[code];
  if (typeof stored === "boolean") {
    return stored;
  }
  if (code === FleetAreaCode.TELEMATICS) {
    return telematicsEnabled === true;
  }
  return true;
}
function parseFleetAreas(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  const record = value;
  const parsed = {};
  for (const code of FLEET_AREA_SWITCHABLE) {
    if (typeof record[code] === "boolean") {
      parsed[code] = record[code];
    }
  }
  return parsed;
}
function resolveFleetAreaMap(areas, telematicsEnabled) {
  return Object.fromEntries(
    FLEET_AREA_SWITCHABLE.map((code) => [code, isFleetAreaEnabled(areas, code, telematicsEnabled)])
  );
}
var fleetAreaCatalogItemSchema = z.object({
  code: z.enum(FLEET_AREA_SWITCHABLE),
  label: z.string(),
  description: z.string(),
  group: fleetAreaGroupSchema,
  enabled: z.boolean(),
  dependents: z.array(z.enum(FLEET_AREA_SWITCHABLE)),
  hides: z.array(z.string())
});
var fleetAreaCatalogSchema = z.object({
  items: z.array(fleetAreaCatalogItemSchema),
  updatedAt: z.iso.datetime().nullable()
});
var patchFleetAreasRequestSchema = z.object({
  code: fleetAreaCodeSchema,
  enabled: z.boolean()
});
var patchFleetAreasResponseSchema = fleetAreaCatalogSchema.extend({
  changed: z.array(z.enum(FLEET_AREA_SWITCHABLE))
});
function buildFleetAreaCatalog(areas, updatedAt = null, telematicsEnabled) {
  return {
    items: FLEET_AREA_SWITCHABLE.map((code) => ({
      code,
      label: FLEET_AREA_LABELS[code],
      description: FLEET_AREA_DESCRIPTIONS[code],
      group: FLEET_AREA_GROUP[code],
      enabled: isFleetAreaEnabled(areas, code, telematicsEnabled),
      dependents: fleetAreaDependents(code),
      hides: [...FLEET_AREA_HIDES[code]]
    })),
    updatedAt
  };
}

// src/fleet-due.ts
var FleetDueCategory = {
  CAR: "car",
  VAN: "van",
  TRUCK: "truck",
  TRAILER: "trailer",
  OTHER: "other"
};
var HU_DEFAULTS = {
  car: { first: 36, next: 24 },
  van: { first: 36, next: 24 },
  truck: { first: 12, next: 12 },
  trailer: { first: 12, next: 12 },
  other: { first: 24, next: 24 }
};
function normalizePlate(input) {
  return input.trim().toUpperCase().replace(/\s+/g, " ");
}
function normalizeVin(input) {
  if (input == null || input.trim() === "") {
    return { ok: true, vin: null };
  }
  const vin = input.trim().toUpperCase();
  if (vin.length !== 17 || /[IOQ]/.test(vin)) {
    return { ok: false };
  }
  return { ok: true, vin };
}
function operationalStatusAfterInspection(result) {
  return result === "failed" ? "out_of_service" : null;
}
function addCalendarMonths(isoDate5, months) {
  const [yearText, monthText, dayText] = isoDate5.split("-");
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  const day = Number(dayText);
  const target = monthIndex + months;
  const nextYear = year + Math.floor(target / 12);
  const nextMonth = (target % 12 + 12) % 12;
  const lastDay = new Date(Date.UTC(nextYear, nextMonth + 1, 0)).getUTCDate();
  const nextDay = Math.min(day, lastDay);
  const month = String(nextMonth + 1).padStart(2, "0");
  const dayOut = String(nextDay).padStart(2, "0");
  return `${nextYear}-${month}-${dayOut}`;
}
function latestInspection(inspections, kind) {
  return inspections.find((row) => row.kind === kind) ?? null;
}
function huDueOn(input) {
  const latest = latestInspection(input.inspections, "hu");
  if (latest) {
    return { dueOn: latest.nextDueOn, missingRegistration: false };
  }
  if (!input.firstRegisteredOn) {
    return { dueOn: null, missingRegistration: true };
  }
  const months = input.huFirstMonths ?? HU_DEFAULTS[input.category].first;
  return {
    dueOn: addCalendarMonths(input.firstRegisteredOn, months),
    missingRegistration: false
  };
}
function uvvDueOn(input) {
  const latest = latestInspection(input.inspections, "uvv_vehicle");
  if (latest) {
    return { dueOn: latest.nextDueOn, missingRegistration: false };
  }
  if (!input.firstRegisteredOn) {
    return { dueOn: null, missingRegistration: true };
  }
  return {
    dueOn: addCalendarMonths(input.firstRegisteredOn, input.uvvIntervalMonths),
    missingRegistration: false
  };
}
function suggestNextDueOn(input) {
  if (input.kind === "hu") {
    const months = input.huNextMonths ?? HU_DEFAULTS[input.category].next;
    return addCalendarMonths(input.performedOn, months);
  }
  return addCalendarMonths(input.performedOn, input.uvvIntervalMonths);
}
function nextLicenseCheckDue(input) {
  const computed = addCalendarMonths(input.checkedAt, input.licenseCheckIntervalMonths);
  if (input.cardValidUntil && input.cardValidUntil < computed) {
    return input.cardValidUntil;
  }
  return computed;
}
function nextInstructionDue(instructedOn, instructionIntervalMonths) {
  return addCalendarMonths(instructedOn, instructionIntervalMonths);
}
var ENTITLEMENT_LICENSE_CLASSES = ["C1", "C", "CE", "D1", "D", "DE"];
function assessDriverCompliance(input) {
  if (!input.latestCheck) {
    return {
      ok: false,
      code: "LICENSE_CHECK_MISSING",
      message: "Es fehlt eine g\xFCltige F\xFChrerscheinkontrolle."
    };
  }
  if (input.latestCheck.result === "invalid") {
    return {
      ok: false,
      code: "LICENSE_CHECK_INVALID",
      message: "Die letzte F\xFChrerscheinkontrolle ist ung\xFCltig."
    };
  }
  if (input.latestCheck.nextCheckDue < input.today) {
    return {
      ok: false,
      code: "LICENSE_CHECK_DUE",
      message: "Die F\xFChrerscheinkontrolle ist abgelaufen."
    };
  }
  if (input.latestCheck.cardValidUntil && input.latestCheck.cardValidUntil < input.today) {
    return {
      ok: false,
      code: "LICENSE_CARD_EXPIRED",
      message: "Der F\xFChrerschein ist abgelaufen."
    };
  }
  if (!input.profile) {
    return {
      ok: false,
      code: "DRIVER_PROFILE_MISSING",
      message: "Es fehlt ein Fahrerprofil mit F\xFChrerscheinklassen."
    };
  }
  const required = input.requiredLicenseClass;
  if (!input.latestCheck.classesConfirmed.includes(required) || !input.profile.licenseClasses.includes(required)) {
    return {
      ok: false,
      code: "LICENSE_CLASS_MISSING",
      message: `Die erforderliche F\xFChrerscheinklasse ${required} fehlt.`
    };
  }
  if (ENTITLEMENT_LICENSE_CLASSES.includes(required)) {
    if (!input.profile.entitlementValidUntil || input.profile.entitlementValidUntil < input.today) {
      return {
        ok: false,
        code: "LICENSE_ENTITLEMENT_EXPIRED",
        message: `Die Berechtigung f\xFCr Klasse ${required} fehlt oder ist abgelaufen.`
      };
    }
  }
  if (input.instructionRequired !== false) {
    if (!input.latestInstructionOn) {
      return {
        ok: false,
        code: "INSTRUCTION_MISSING",
        message: "Es fehlt eine UVV-Unterweisung."
      };
    }
    const instructionDue = nextInstructionDue(
      input.latestInstructionOn,
      input.instructionIntervalMonths
    );
    if (instructionDue < input.today) {
      return {
        ok: false,
        code: "INSTRUCTION_DUE",
        message: "Die UVV-Unterweisung ist abgelaufen."
      };
    }
  }
  return {
    ok: true,
    licenseRestricted: input.latestCheck.result === "restricted"
  };
}
function assignmentRangesOverlap(existing, next) {
  const existingEnd = existing.validTo ?? "9999-12-31";
  return existing.validFrom <= next.validTo && existingEnd >= next.validFrom;
}
function dayBefore(isoDate5) {
  const [yearText, monthText, dayText] = isoDate5.split("-");
  const date = new Date(Date.UTC(Number(yearText), Number(monthText) - 1, Number(dayText)));
  date.setUTCDate(date.getUTCDate() - 1);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function currentOdometerKm(readings) {
  if (readings.length === 0) {
    return null;
  }
  const sorted = [...readings].sort((a, b) => {
    if (a.recordedAt === b.recordedAt) {
      return 0;
    }
    return a.recordedAt < b.recordedAt ? 1 : -1;
  });
  return sorted[0]?.readingKm ?? null;
}
function evaluateDriverOdometer(input) {
  if (input.currentKm != null && input.readingKm < input.currentKm) {
    return {
      ok: false,
      code: "ODOMETER_DECREASE",
      message: `Der Stand ${input.readingKm} km liegt unter dem bisherigen Stand ${input.currentKm} km.`
    };
  }
  if (input.currentKm != null && input.readingKm - input.currentKm > input.maxDailyKmWarning) {
    return {
      ok: true,
      warning: `Kilometersprung \xFCber dem Richtwert (${input.maxDailyKmWarning} km).`
    };
  }
  return { ok: true, warning: null };
}
function serviceDueStatus(input) {
  let byKm = { status: "no_interval", message: null };
  if (input.serviceIntervalKm != null) {
    if (input.lastServiceKm == null) {
      byKm = { status: "missing_baseline", message: "Letzter Service-Kilometer fehlt" };
    } else if (input.currentKm != null && input.currentKm >= input.lastServiceKm + input.serviceIntervalKm) {
      byKm = { status: "due", message: "Service nach Kilometer f\xE4llig" };
    } else {
      byKm = { status: "ok", message: null };
    }
  }
  let byDate = { status: "no_interval", message: null };
  if (input.serviceIntervalMonths != null) {
    if (!input.lastServiceOn) {
      byDate = { status: "missing_baseline", message: "Letztes Service-Datum fehlt" };
    } else {
      const dueOn = addCalendarMonths(input.lastServiceOn, input.serviceIntervalMonths);
      if (dueOn <= input.today) {
        byDate = { status: "due", message: "Service nach Datum f\xE4llig" };
      } else {
        byDate = { status: "ok", message: null };
      }
    }
  }
  return { byKm, byDate };
}
function evaluateVehicleRelease(input) {
  if (input.openHoldingDamageCount > 0) {
    return {
      ok: false,
      code: "VEHICLE_HOLDING_DAMAGE",
      message: "fleet.release.holding-damage"
    };
  }
  if (input.lastHuFailed) {
    return {
      ok: false,
      code: "VEHICLE_HU_FAILED",
      message: "Die letzte HU ist nicht bestanden. Eine Freigabe ist nicht m\xF6glich."
    };
  }
  if (input.lastUvvFailed) {
    return {
      ok: false,
      code: "VEHICLE_UVV_FAILED",
      message: "Die letzte UVV-Pr\xFCfung ist nicht bestanden. Eine Freigabe ist nicht m\xF6glich."
    };
  }
  return { ok: true };
}
var LAT_MIN_E7 = -9e8;
var LAT_MAX_E7 = 9e8;
var LON_MIN_E7 = -18e8;
var LON_MAX_E7 = 18e8;
var fleetTelematicsEventKindSchema = z.enum(["position", "trip", "odometer"]);
var fleetTelematicsWaypointSchema = z.object({
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  placeLabel: z.string().max(200).optional(),
  occurredAt: z.string().optional()
});
var fleetTelematicsRawEventSchema = z.object({
  externalVehicleRef: z.string().trim().min(1),
  occurredAt: z.string().trim().min(1),
  kind: fleetTelematicsEventKindSchema,
  externalId: z.string().trim().min(1).optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  placeLabel: z.string().trim().max(200).optional(),
  odometerKm: z.number().int().optional(),
  endedAt: z.string().trim().optional(),
  endPlace: z.string().trim().max(200).optional(),
  startPlace: z.string().trim().max(200).optional(),
  distanceKm: z.number().int().optional(),
  points: z.array(fleetTelematicsWaypointSchema).optional()
});
var fleetTelematicsNormalizedEventSchema = fleetTelematicsRawEventSchema.omit({ points: true }).extend({
  startPlace: z.string().max(200).optional()
});
function toCoordinateE7(degrees, axis) {
  const e7 = Math.round(degrees * 1e7);
  const min = axis === "latitude" ? LAT_MIN_E7 : LON_MIN_E7;
  const max = axis === "latitude" ? LAT_MAX_E7 : LON_MAX_E7;
  if (e7 < min || e7 > max) {
    return null;
  }
  return e7;
}
function fromCoordinateE7(e7) {
  return e7 / 1e7;
}
function sanitizeCoords(latitude, longitude) {
  if (latitude == null || longitude == null) {
    return {};
  }
  const lat = toCoordinateE7(latitude, "latitude");
  const lon = toCoordinateE7(longitude, "longitude");
  if (lat == null || lon == null) {
    return {};
  }
  return { latitude: fromCoordinateE7(lat), longitude: fromCoordinateE7(lon) };
}
function normalizeTelematicsBatch(events) {
  return events.map((event) => {
    const { points, ...rest } = event;
    const coords = sanitizeCoords(rest.latitude, rest.longitude);
    const base = {
      externalVehicleRef: rest.externalVehicleRef,
      occurredAt: rest.occurredAt,
      kind: rest.kind,
      externalId: rest.externalId,
      placeLabel: rest.placeLabel,
      odometerKm: rest.odometerKm,
      endedAt: rest.endedAt,
      endPlace: rest.endPlace,
      startPlace: rest.startPlace,
      distanceKm: rest.distanceKm,
      ...coords
    };
    if (event.kind !== "trip" || !points || points.length === 0) {
      return base;
    }
    const first = points[0];
    const last = points[points.length - 1];
    return {
      ...base,
      startPlace: base.startPlace ?? first?.placeLabel,
      endPlace: base.endPlace ?? last?.placeLabel
    };
  });
}
var FLEET_TELEMATICS_INGEST_MAX_EVENTS = 500;
var fleetTelematicsIngestRequestSchema = z.object({
  events: z.array(fleetTelematicsRawEventSchema).min(1).max(FLEET_TELEMATICS_INGEST_MAX_EVENTS)
});
var fleetTelematicsIngestResultSchema = z.object({
  accepted: z.number().int().nonnegative(),
  duplicated: z.number().int().nonnegative(),
  issues: z.number().int().nonnegative()
});
var fleetTelematicsConnectionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean(),
  lastIngestAt: z.string().nullable(),
  lastError: z.string().nullable(),
  createdAt: z.string()
});
var fleetTelematicsConnectionListSchema = z.object({
  items: z.array(fleetTelematicsConnectionSchema)
});
var createFleetTelematicsConnectionRequestSchema = z.object({
  name: z.string().trim().min(1).max(200)
});
var createFleetTelematicsConnectionResponseSchema = fleetTelematicsConnectionSchema.extend(
  {
    token: z.string().min(32)
  }
);
var rotateFleetTelematicsTokenResponseSchema = z.object({
  token: z.string().min(32)
});
var fleetTripPurposeSchema = z.enum(["unset", "business", "private"]);
var setFleetTripPurposeRequestSchema = z.object({
  purpose: z.enum(["business", "private"])
});
var fleetTelematicsTripSchema = z.object({
  id: z.string().uuid(),
  vehicleId: z.string().uuid(),
  connectionId: z.string().uuid().nullable(),
  externalId: z.string().nullable(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  startPlace: z.string().nullable(),
  endPlace: z.string().nullable(),
  distanceKm: z.number().int().nullable(),
  odometerStartKm: z.number().int().nullable(),
  odometerEndKm: z.number().int().nullable(),
  purpose: fleetTripPurposeSchema,
  classifiedById: z.string().uuid().nullable()
});
var fleetTelematicsTripListSchema = z.object({
  items: z.array(fleetTelematicsTripSchema)
});
var fleetTelematicsTripListQuerySchema = z.object({
  vehicleId: z.string().uuid().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  purpose: fleetTripPurposeSchema.optional()
});
var fleetTelematicsPositionSchema = z.object({
  vehicleId: z.string().uuid(),
  latitude: z.number(),
  longitude: z.number(),
  placeLabel: z.string().nullable(),
  observedAt: z.string()
});
var fleetTelematicsPositionListSchema = z.object({
  items: z.array(fleetTelematicsPositionSchema)
});
var fleetTelematicsIssueSchema = z.object({
  id: z.string().uuid(),
  connectionId: z.string().uuid().nullable(),
  externalRef: z.string(),
  reason: z.string(),
  occurredAt: z.string(),
  createdAt: z.string()
});
var fleetTelematicsIssueListSchema = z.object({
  items: z.array(fleetTelematicsIssueSchema)
});
function headerIndex(headers, name) {
  const key = name.toLowerCase();
  return headers.findIndex((cell) => cell.trim().toLowerCase() === key);
}
function parseTelematicsDateTime(raw) {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const date2 = new Date(trimmed);
    return Number.isNaN(date2.getTime()) ? null : date2.toISOString();
  }
  const match = /^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2})$/.exec(trimmed);
  if (!match) {
    return null;
  }
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute));
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
function parseTelematicsCsv(csv) {
  const lines = csv.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length === 0) {
    return { events: [], rowErrors: [{ line: 1, message: "Die Datei ist leer." }] };
  }
  const delimiter = lines[0].includes(";") ? ";" : ",";
  const headers = lines[0].split(delimiter).map((cell) => cell.trim());
  const events = [];
  const rowErrors = [];
  for (let i = 1; i < lines.length; i++) {
    const lineNo = i + 1;
    const cells = lines[i].split(delimiter).map((cell) => cell.trim());
    const get = (name) => {
      const idx = headerIndex(headers, name);
      return idx >= 0 ? cells[idx] ?? "" : "";
    };
    const externalId = get("externalId");
    const vehicleRef = get("vehicleRef");
    const startedAtRaw = get("startedAt");
    if (!externalId) {
      rowErrors.push({ line: lineNo, message: "externalId fehlt." });
      continue;
    }
    if (!vehicleRef) {
      rowErrors.push({ line: lineNo, message: "vehicleRef fehlt." });
      continue;
    }
    const startedAt = parseTelematicsDateTime(startedAtRaw);
    if (!startedAt) {
      rowErrors.push({ line: lineNo, message: "startedAt fehlt oder ist ung\xFCltig." });
      continue;
    }
    const endedAtRaw = get("endedAt");
    const endedAt = endedAtRaw ? parseTelematicsDateTime(endedAtRaw) : void 0;
    if (endedAtRaw && !endedAt) {
      rowErrors.push({ line: lineNo, message: "endedAt ist ung\xFCltig." });
      continue;
    }
    const distanceRaw = get("distanceKm");
    let distanceKm;
    if (distanceRaw) {
      const value = Number(distanceRaw.replace(",", "."));
      if (!Number.isFinite(value)) {
        rowErrors.push({ line: lineNo, message: "distanceKm ist ung\xFCltig." });
        continue;
      }
      distanceKm = Math.round(value);
    }
    const odometerRaw = get("odometerKm");
    let odometerKm;
    if (odometerRaw) {
      const value = Number(odometerRaw.replace(",", "."));
      if (!Number.isFinite(value)) {
        rowErrors.push({ line: lineNo, message: "odometerKm ist ung\xFCltig." });
        continue;
      }
      odometerKm = Math.round(value);
    }
    events.push({
      externalVehicleRef: vehicleRef,
      occurredAt: startedAt,
      kind: "trip",
      externalId,
      startPlace: get("startPlace") || void 0,
      endPlace: get("endPlace") || void 0,
      endedAt: endedAt ?? void 0,
      distanceKm,
      odometerKm
    });
  }
  return { events, rowErrors };
}

// src/fleet-fuel.ts
var fleetFuelIssuerSchema = z.enum(["dkv", "uta", "shell", "aral", "other"]);
var fleetFuelCardStatusSchema = z.enum(["active", "blocked", "lost", "returned"]);
var fleetFuelProductSchema = z.enum(["diesel", "petrol", "adblue", "charge", "other"]);
var fleetFuelUnitSchema = z.enum(["litre", "kwh"]);
var fleetFuelMatchStatusSchema = z.enum(["matched", "exception"]);
function fuelProductFitsVehicle(fuelType, product) {
  if (product === "other") {
    return fuelType === "other";
  }
  if (fuelType === "other") {
    return true;
  }
  switch (fuelType) {
    case "diesel":
      return product === "diesel" || product === "adblue";
    case "petrol":
      return product === "petrol";
    case "electric":
      return product === "charge";
    case "hybrid":
      return product === "petrol" || product === "charge";
    default:
      return false;
  }
}
var PRODUCT_ALIASES = {
  diesel: "diesel",
  petrol: "petrol",
  adblue: "adblue",
  charge: "charge",
  other: "other",
  benzin: "petrol",
  strom: "charge",
  sonstiges: "other"
};
function headerIndex2(headers, name) {
  const key = name.toLowerCase();
  return headers.findIndex((cell) => cell.trim().toLowerCase() === key);
}
function parseProduct(raw) {
  const key = raw.trim().toLowerCase();
  return PRODUCT_ALIASES[key] ?? null;
}
function parseUnit(raw) {
  const key = raw.trim().toLowerCase();
  if (key === "l" || key === "litre" || key === "liter") {
    return "litre";
  }
  if (key === "kwh") {
    return "kwh";
  }
  return null;
}
function parseFuelAmountToCents(raw) {
  const normalized = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized) {
    return null;
  }
  const value = Number(normalized);
  if (!Number.isFinite(value) || value < 0) {
    return null;
  }
  return Math.round(value * 100);
}
function panToLast4(raw) {
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 13) {
    return null;
  }
  const last4 = digits.slice(-4);
  return /^\d{4}$/.test(last4) ? last4 : null;
}
function parseQuantityMilli(raw) {
  const normalized = raw.trim().replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value) || value < 0) {
    return null;
  }
  return Math.round(value * 1e3);
}
function parseFuelCsv(csv) {
  const lines = csv.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length === 0) {
    return { rows: [], rowErrors: [{ line: 1, message: "Die Datei ist leer." }] };
  }
  const delimiter = lines[0].includes(";") ? ";" : ",";
  const headers = lines[0].split(delimiter).map((cell) => cell.trim());
  const rows = [];
  const rowErrors = [];
  for (let i = 1; i < lines.length; i++) {
    const lineNo = i + 1;
    const cells = lines[i].split(delimiter).map((cell) => cell.trim());
    const get = (name) => {
      const idx = headerIndex2(headers, name);
      return idx >= 0 ? cells[idx] ?? "" : "";
    };
    const externalId = get("externalId");
    if (!externalId) {
      rowErrors.push({ line: lineNo, message: "externalId fehlt." });
      continue;
    }
    const amountRaw = get("amount");
    const amountCents = parseFuelAmountToCents(amountRaw);
    if (amountCents == null) {
      rowErrors.push({ line: lineNo, message: "Betrag fehlt oder ist ung\xFCltig." });
      continue;
    }
    const product = parseProduct(get("product"));
    if (!product) {
      rowErrors.push({ line: lineNo, message: "Produkt fehlt oder ist ung\xFCltig." });
      continue;
    }
    const quantityMilli = parseQuantityMilli(get("quantity"));
    if (quantityMilli == null) {
      rowErrors.push({ line: lineNo, message: "Menge fehlt oder ist ung\xFCltig." });
      continue;
    }
    const unit = parseUnit(get("unit"));
    if (!unit) {
      rowErrors.push({ line: lineNo, message: "Einheit fehlt oder ist ung\xFCltig." });
      continue;
    }
    const occurredAtRaw = get("occurredAt");
    if (!occurredAtRaw) {
      rowErrors.push({ line: lineNo, message: "occurredAt fehlt." });
      continue;
    }
    const occurredAt = parseTelematicsDateTime(occurredAtRaw);
    if (!occurredAt) {
      rowErrors.push({
        line: lineNo,
        message: "occurredAt ist ung\xFCltig (ISO oder TT.MM.JJJJ hh:mm)."
      });
      continue;
    }
    const cardRef = get("cardRef");
    const panRaw = get("pan");
    const last4FromPan = panRaw ? panToLast4(panRaw) : null;
    const last4 = last4FromPan ?? void 0;
    if (!cardRef && !last4) {
      rowErrors.push({ line: lineNo, message: "cardRef oder pan fehlt." });
      continue;
    }
    const odometerRaw = get("odometerKm");
    const odometerKm = odometerRaw ? Number.parseInt(odometerRaw, 10) : void 0;
    const licensePlate = get("licensePlate") || void 0;
    const stationName = get("station") || void 0;
    rows.push({
      externalId,
      cardRef: cardRef || void 0,
      last4,
      occurredAt,
      amountCents,
      product,
      quantityMilli,
      unit,
      stationName,
      odometerKm: Number.isFinite(odometerKm) ? odometerKm : void 0,
      licensePlate
    });
  }
  return { rows, rowErrors };
}
function coversDay(from, to, day) {
  if (day < from) {
    return false;
  }
  if (to != null && day > to) {
    return false;
  }
  return true;
}
function findCard(input) {
  return input.cards.find((card) => {
    if (card.issuer !== input.issuer) {
      return false;
    }
    if (input.row.cardRef && card.externalRef === input.row.cardRef) {
      return true;
    }
    if (input.row.last4 && card.last4 === input.row.last4) {
      return true;
    }
    return false;
  }) ?? null;
}
function resolveVehicleId(input, card) {
  const assignment = input.cardAssignments.find(
    (row) => row.cardId === card.id && coversDay(row.validFrom, row.validTo, input.occurredOn)
  );
  if (!assignment) {
    return null;
  }
  if (assignment.vehicleId) {
    return { vehicleId: assignment.vehicleId };
  }
  if (!assignment.userId) {
    return null;
  }
  const userVehicles = input.vehicleAssignments.filter((row) => row.userId === assignment.userId);
  if (userVehicles.length === 0) {
    return { unclear: true };
  }
  if (userVehicles.length === 1) {
    return { vehicleId: userVehicles[0].vehicleId };
  }
  const plate = input.row.licensePlate?.trim();
  if (plate) {
    const normalized = plate.replace(/\s+/g, " ").toUpperCase();
    const hit = userVehicles.find(
      (row) => row.licensePlate.replace(/\s+/g, " ").toUpperCase() === normalized
    );
    if (hit) {
      return { vehicleId: hit.vehicleId };
    }
  }
  return { unclear: true };
}
function matchFuelTransaction(input) {
  const card = findCard(input);
  if (!card) {
    return { status: "exception", exceptionCode: "unknown_card", vehicleId: null };
  }
  if (card.status !== "active") {
    return { status: "exception", exceptionCode: "card_inactive", vehicleId: null };
  }
  const hasAssignment = input.cardAssignments.some(
    (row) => row.cardId === card.id && coversDay(row.validFrom, row.validTo, input.occurredOn)
  );
  if (!hasAssignment) {
    return { status: "exception", exceptionCode: "unassigned", vehicleId: null };
  }
  const vehicleResolved = resolveVehicleId(input, card);
  if (vehicleResolved === null) {
    return { status: "exception", exceptionCode: "unassigned", vehicleId: null };
  }
  if ("unclear" in vehicleResolved) {
    return { status: "exception", exceptionCode: "vehicle_unclear", vehicleId: null };
  }
  const vehicle = input.vehicles.get(vehicleResolved.vehicleId);
  if (!vehicle) {
    return { status: "exception", exceptionCode: "vehicle_unclear", vehicleId: null };
  }
  if (!fuelProductFitsVehicle(vehicle.fuelType, input.row.product)) {
    return { status: "exception", exceptionCode: "fuel_mismatch", vehicleId: vehicle.id };
  }
  if (input.row.unit === "litre" && vehicle.tankCapacityLiters != null && input.row.quantityMilli > vehicle.tankCapacityLiters * 1e3 * 1.05) {
    return { status: "exception", exceptionCode: "tank_overflow", vehicleId: vehicle.id };
  }
  if (input.row.odometerKm != null) {
    const currentKm = input.currentKmByVehicle.get(vehicle.id) ?? null;
    const evaluated = evaluateDriverOdometer({
      currentKm,
      readingKm: input.row.odometerKm,
      maxDailyKmWarning: input.maxDailyKmWarning
    });
    if (!evaluated.ok) {
      return { status: "exception", exceptionCode: "odometer_decrease", vehicleId: vehicle.id };
    }
    return { status: "matched", vehicleId: vehicle.id, odometerAccepted: true };
  }
  return { status: "matched", vehicleId: vehicle.id, odometerAccepted: false };
}
var FLEET_FUEL_EXCEPTION_LABELS = {
  unknown_card: "Unbekannte Karte",
  card_inactive: "Karte nicht aktiv",
  unassigned: "Karte nicht zugeordnet",
  vehicle_unclear: "Fahrzeug unklar",
  fuel_mismatch: "Kraftstoff passt nicht",
  tank_overflow: "Menge \xFCber Tank",
  odometer_decrease: "Kilometerstand sinkt"
};
var last4Schema = z.string().regex(/^\d{4}$/, "Genau vier Ziffern.");
var fleetFuelCardSchema = z.object({
  id: z.string().uuid(),
  issuer: fleetFuelIssuerSchema,
  externalRef: z.string(),
  last4: last4Schema,
  status: fleetFuelCardStatusSchema,
  label: z.string().nullable(),
  createdAt: z.string()
});
var fleetFuelCardListSchema = z.object({
  items: z.array(fleetFuelCardSchema)
});
var createFleetFuelCardRequestSchema = z.object({
  issuer: fleetFuelIssuerSchema,
  externalRef: z.string().trim().min(1).max(120),
  last4: last4Schema,
  label: z.string().trim().max(200).nullable().optional()
});
var updateFleetFuelCardRequestSchema = z.object({
  status: fleetFuelCardStatusSchema.optional(),
  label: z.string().trim().max(200).nullable().optional()
});
var isoDate2 = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var createFleetFuelCardAssignmentRequestSchema = z.object({
  vehicleId: z.string().uuid().nullable().optional(),
  userId: z.string().uuid().nullable().optional(),
  validFrom: isoDate2,
  validTo: isoDate2.nullable().optional()
}).refine((body) => body.vehicleId != null || body.userId != null, {
  message: "Fahrzeug oder Person ist erforderlich."
});
var fleetFuelCardAssignmentSchema = z.object({
  id: z.string().uuid(),
  cardId: z.string().uuid(),
  vehicleId: z.string().uuid().nullable(),
  userId: z.string().uuid().nullable(),
  validFrom: isoDate2,
  validTo: isoDate2.nullable(),
  createdAt: z.string()
});
var fleetFuelImportResultSchema = z.object({
  created: z.number().int().nonnegative(),
  duplicated: z.number().int().nonnegative(),
  exceptions: z.number().int().nonnegative(),
  rowErrors: z.array(
    z.object({
      line: z.number().int().positive(),
      message: z.string()
    })
  )
});
var fleetFuelTransactionSchema = z.object({
  id: z.string().uuid(),
  issuer: fleetFuelIssuerSchema,
  externalId: z.string(),
  cardId: z.string().uuid().nullable(),
  vehicleId: z.string().uuid().nullable(),
  userId: z.string().uuid().nullable(),
  occurredAt: z.string(),
  stationName: z.string().nullable(),
  product: fleetFuelProductSchema,
  quantityMilli: z.number().int(),
  unit: fleetFuelUnitSchema,
  amountCents: z.number().int(),
  odometerKm: z.number().int().nullable(),
  status: fleetFuelMatchStatusSchema,
  exceptionCode: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  resolveNote: z.string().nullable(),
  createdAt: z.string()
});
var fleetFuelTransactionListSchema = z.object({
  items: z.array(fleetFuelTransactionSchema)
});
var fleetFuelTransactionListQuerySchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/).optional(),
  status: fleetFuelMatchStatusSchema.optional(),
  vehicleId: z.string().uuid().optional()
});
var resolveFleetFuelTransactionRequestSchema = z.object({
  note: z.string().trim().min(10).max(2e3)
});
var fleetCostMonthQuerySchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/)
});
var fleetCostMonthRowSchema = z.object({
  vehicleId: z.string().uuid(),
  licensePlate: z.string(),
  workshopCents: z.number().int().nonnegative(),
  fuelCents: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative()
});
var fleetCostMonthListSchema = z.object({
  month: z.string(),
  items: z.array(fleetCostMonthRowSchema)
});
var fleetVehicleCostQuerySchema = z.object({
  from: isoDate2,
  to: isoDate2
});
var fleetVehicleCostLineSchema = z.object({
  kind: z.enum(["workshop", "fuel"]),
  id: z.string().uuid(),
  occurredOn: z.string(),
  label: z.string(),
  amountCents: z.number().int().nonnegative()
});
var fleetVehicleCostDetailSchema = z.object({
  vehicleId: z.string().uuid(),
  from: isoDate2,
  to: isoDate2,
  workshopCents: z.number().int().nonnegative(),
  fuelCents: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
  items: z.array(fleetVehicleCostLineSchema)
});
var FleetChargeInterface = {
  OCPP: "ocpp",
  MODBUS: "modbus",
  REST: "rest"
};
var fleetChargeInterfaceSchema = z.enum(FleetChargeInterface);
var FleetChargeConnectorState = {
  AVAILABLE: "available",
  OCCUPIED: "occupied",
  CHARGING: "charging",
  FAULTED: "faulted",
  UNAVAILABLE: "unavailable",
  OFFLINE: "offline",
  UNSUPPORTED: "unsupported"
};
var fleetChargeConnectorStateSchema = z.enum(FleetChargeConnectorState);
var FLEET_CHARGE_CONNECTOR_LABELS = {
  available: "Frei",
  occupied: "Verbunden",
  charging: "L\xE4dt",
  faulted: "St\xF6rung",
  unavailable: "Gesperrt",
  offline: "Keine Verbindung",
  unsupported: "Noch nicht verbunden"
};
var fleetChargeIdentityPattern = /^[A-Za-z0-9_-]{1,48}$/;
var identitySchema = z.string().regex(
  fleetChargeIdentityPattern,
  "Die Kennung darf nur Buchstaben, Ziffern, Bindestrich und Unterstrich enthalten."
);
var passwordSchema2 = z.string().min(8, "Das Passwort braucht mindestens 8 Zeichen.").max(64, "Das Passwort darf h\xF6chstens 64 Zeichen haben.");
var pollSchema = z.number().int().min(5).max(300);
var httpUrlSchema = z.string().trim().refine((value) => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}, "Bitte eine g\xFCltige REST-Adresse angeben.");
var fleetChargeLineInputSchema = z.object({
  state: fleetChargeConnectorStateSchema,
  powerW: z.number().int().nullable(),
  energyWh: z.number().int().nullable(),
  socPercent: z.number().int().min(0).max(100).nullable()
});
function fleetChargeIdentityFromSerial(serial) {
  const identity = serial.trim().replaceAll(".", "_");
  return fleetChargeIdentityPattern.test(identity) ? identity : "";
}
function buildFleetChargeLine(input) {
  if (input.state === "occupied") {
    return "Verbunden, l\xE4dt nicht";
  }
  if (input.state === "charging") {
    const power = input.powerW == null ? null : formatFleetKw(input.powerW / 1e3);
    const energy = input.energyWh == null ? null : formatFleetKw(input.energyWh / 1e3);
    if (power != null && energy != null) {
      return `L\xE4dt, ${power} kW, ${energy} kWh`;
    }
    if (energy != null) {
      return `L\xE4dt, ${energy} kWh`;
    }
    return "L\xE4dt";
  }
  if (input.state === "faulted") {
    return "St\xF6rung";
  }
  if (input.state === "offline") {
    return "Wallbox nicht erreichbar";
  }
  if (input.state === "unavailable") {
    return "Gesperrt";
  }
  if (input.state === "unsupported") {
    return "Noch nicht verbunden";
  }
  return "Nicht an der Wallbox";
}
var fleetChargeConnectorSchema = z.object({
  id: z.string().uuid(),
  stationId: z.string().uuid(),
  stationName: z.string(),
  connectorNumber: z.number().int(),
  state: fleetChargeConnectorStateSchema,
  label: z.string(),
  powerW: z.number().int().nullable(),
  energyWh: z.number().int().nullable(),
  socPercent: z.number().int().min(0).max(100).nullable(),
  idTag: z.string().nullable(),
  vehicleId: z.string().uuid().nullable(),
  licensePlate: z.string().nullable(),
  driverName: z.string().nullable(),
  observedAt: z.string().datetime().nullable(),
  lastError: z.string().nullable()
});
var fleetChargeConnectorListSchema = z.object({
  items: z.array(fleetChargeConnectorSchema)
});
var fleetChargeStationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  interface: fleetChargeInterfaceSchema,
  isActive: z.boolean(),
  connectorCount: z.union([z.literal(1), z.literal(2)]),
  ocppIdentity: z.string().nullable(),
  passwordSet: z.boolean(),
  websocketUrl: z.string().nullable(),
  modbusHost: z.string().nullable(),
  modbusPort: z.number().int().nullable(),
  modbusUnitId: z.number().int().nullable(),
  restBaseUrl: z.string().nullable(),
  restUsername: z.string().nullable(),
  secretSet: z.boolean(),
  pollIntervalSeconds: z.number().int(),
  lastConnectedAt: z.string().datetime().nullable(),
  lastError: z.string().nullable()
});
var fleetChargeVehicleOptionSchema = z.object({
  id: z.string().uuid(),
  licensePlate: z.string(),
  fuelType: z.enum(["electric", "hybrid"])
});
var fleetChargeTagSchema = z.object({
  idTag: z.string(),
  vehicleId: z.string().uuid(),
  licensePlate: z.string()
});
var fleetChargeStationListSchema = z.object({
  items: z.array(fleetChargeStationSchema),
  vehicles: z.array(fleetChargeVehicleOptionSchema),
  tags: z.array(fleetChargeTagSchema)
});
var fleetChargeStationCreatedSchema = fleetChargeStationSchema.extend({
  password: z.string().nullable()
});
var fleetChargePasswordSchema = z.object({
  password: z.string()
});
var createFleetChargeStationRequestSchema = z.object({
  name: z.string().trim().min(1, "Bitte einen Namen angeben.").max(80),
  interface: fleetChargeInterfaceSchema,
  connectorCount: z.union([z.literal(1), z.literal(2)]).default(2),
  ocppIdentity: identitySchema.optional(),
  password: passwordSchema2.optional(),
  modbusHost: z.string().trim().min(1).max(253).optional(),
  modbusPort: z.number().int().min(1).max(65535).optional(),
  modbusUnitId: z.number().int().min(0).max(255).optional(),
  restBaseUrl: httpUrlSchema.optional(),
  restUsername: z.string().trim().max(200).optional(),
  secret: z.string().min(1).max(500).optional(),
  pollIntervalSeconds: pollSchema.optional()
}).superRefine((body, ctx) => {
  if (body.interface === "ocpp") {
    if (!body.ocppIdentity) {
      ctx.addIssue({
        code: "custom",
        path: ["ocppIdentity"],
        message: "Bitte eine Kennung angeben."
      });
    }
  }
  if (body.interface === "modbus") {
    if (!body.modbusHost) {
      ctx.addIssue({
        code: "custom",
        path: ["modbusHost"],
        message: "Bitte eine IP-Adresse oder einen Host angeben."
      });
    }
    if (body.modbusUnitId == null) {
      ctx.addIssue({
        code: "custom",
        path: ["modbusUnitId"],
        message: "Bitte eine Modbus-ID angeben."
      });
    }
  }
  if (body.interface === "rest" && !body.restBaseUrl) {
    ctx.addIssue({
      code: "custom",
      path: ["restBaseUrl"],
      message: "Bitte eine REST-Adresse angeben."
    });
  }
  if (body.interface === "rest" && !body.secret) {
    ctx.addIssue({ code: "custom", path: ["secret"], message: "Bitte ein Geheimnis angeben." });
  }
});
var updateFleetChargeStationRequestSchema = z.object({
  name: z.string().trim().min(1, "Bitte einen Namen angeben.").max(80),
  isActive: z.boolean(),
  modbusHost: z.string().trim().min(1).max(253).optional(),
  modbusPort: z.number().int().min(1).max(65535).optional(),
  modbusUnitId: z.number().int().min(0).max(255).optional(),
  restBaseUrl: httpUrlSchema.optional(),
  restUsername: z.string().trim().max(200).optional(),
  secret: z.string().min(1).max(500).optional(),
  pollIntervalSeconds: pollSchema.optional()
});
var rotateFleetChargePasswordRequestSchema = z.object({
  password: passwordSchema2
});
var fleetChargeTagLinkedSchema = z.object({
  vehicleId: z.string().uuid()
});
var putFleetChargeTagRequestSchema = z.object({
  idTag: z.string().trim().min(1, "Bitte die Chip-Nummer angeben.").max(20, "Die Chip-Nummer darf h\xF6chstens 20 Zeichen haben."),
  vehicleId: z.string().uuid()
});
var fleetChargeVehicleSchema = z.object({
  vehicleId: z.string().uuid(),
  socPercent: z.number().int().min(0).max(100).nullable(),
  state: fleetChargeConnectorStateSchema,
  line: z.string(),
  observedAt: z.string().datetime().nullable()
});
function fleetChargeAmounts(energyWh, netCentsPerKwh) {
  const wattHours = Math.max(0, Math.trunc(energyWh));
  const price = Math.max(0, Math.trunc(netCentsPerKwh));
  const netCents = roundHalfUp(wattHours * price, 1e3);
  const vatCents = roundHalfUp(netCents * 19, 100);
  return { netCents, vatCents, grossCents: netCents + vatCents };
}
function roundHalfUp(numerator, denominator) {
  if (numerator <= 0) {
    return 0;
  }
  return Math.floor((numerator + denominator / 2) / denominator);
}
function fleetAuthorizeStatus(input) {
  if (input.onVehicle) {
    return "Accepted";
  }
  if (input.tariffClass === "employee") {
    return input.employeePrice == null ? "Invalid" : "Accepted";
  }
  if (input.tariffClass === "guest") {
    if (input.guestPrice == null) {
      return "Invalid";
    }
    if (input.guestRequiresRelease && !input.released) {
      return "Blocked";
    }
    return "Accepted";
  }
  return "Invalid";
}
var cents = z.number().int().min(0).max(1e5).nullable();
var fleetTariffSettingsSchema = z.object({
  guestNetCentsPerKwh: cents,
  employeeNetCentsPerKwh: cents,
  guestRequiresRelease: z.boolean(),
  quietMinutes: z.number().int().min(0).max(10080),
  creditorName: z.string().nullable(),
  creditorId: z.string().nullable(),
  creditorIban: z.string().nullable()
});
var saveFleetTariffSettingsSchema = fleetTariffSettingsSchema;
var fleetTariffChipSchema = z.object({
  id: z.string().uuid(),
  idTag: z.string(),
  class: z.enum(["guest", "employee"]),
  released: z.boolean(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  street: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  iban: z.string().nullable(),
  mandateDate: z.string().nullable()
});
var saveFleetTariffChipSchema = z.object({
  idTag: z.string().trim().min(1, "Bitte einen Chip angeben.").max(40),
  class: z.enum(["guest", "employee"]),
  name: z.string().trim().max(120).optional().default(""),
  email: z.string().trim().max(200).optional().default(""),
  street: z.string().trim().max(120).optional().default(""),
  postalCode: z.string().trim().max(12).optional().default(""),
  city: z.string().trim().max(80).optional().default(""),
  iban: z.string().trim().max(42).optional().default(""),
  mandateDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional()
});
var updateFleetTariffChipSchema = saveFleetTariffChipSchema.omit({
  idTag: true,
  class: true
});
var fleetChargeInvoiceSchema = z.object({
  id: z.string().uuid(),
  number: z.number().int(),
  grossCents: z.number().int(),
  netCents: z.number().int(),
  vatCents: z.number().int(),
  paid: z.boolean(),
  sent: z.boolean()
});
var fleetSepaFileSchema = z.object({
  filename: z.string(),
  xml: z.string()
});

// src/fleet-energy.ts
var fleetEnergyGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  limitAmps: z.number().int(),
  connectorIds: z.array(z.string().uuid())
});
var fleetEnergyChoiceSchema = z.object({
  id: z.string().uuid(),
  stationName: z.string(),
  connectorNumber: z.number().int(),
  groupId: z.string().uuid().nullable(),
  limitAmps: z.number().int().nullable()
});
var fleetEnergyCardSchema = fleetChargeConnectorSchema.extend({
  allocatedAmps: z.number().int().nullable(),
  profileLabel: z.string().nullable(),
  sessionId: z.string().uuid().nullable(),
  departureAt: z.string().datetime().nullable(),
  canCommand: z.boolean(),
  interface: fleetChargeInterfaceSchema,
  firmwareVersion: z.string().nullable(),
  firmwareStatus: z.string().nullable(),
  limitAmps: z.number().int().nullable(),
  tariffClass: z.enum(["fleet", "guest", "employee"]).nullable(),
  netCentsPerKwh: z.number().int().nullable(),
  netCents: z.number().int().nullable(),
  vatCents: z.number().int().nullable(),
  grossCents: z.number().int().nullable(),
  ocmfTexts: z.array(z.string()),
  ocmfVerified: z.array(z.boolean()),
  ocmfPublicKey: z.string().nullable()
});
var fleetEnergyVehicleSchema = z.object({
  id: z.string().uuid(),
  licensePlate: z.string(),
  chips: z.array(z.string())
});
var fleetEnergyStationSchema = z.object({
  stationId: z.string().uuid(),
  stationName: z.string(),
  canCommand: z.boolean(),
  firmwareVersion: z.string().nullable(),
  firmwareStatus: z.string().nullable(),
  cards: z.array(fleetEnergyCardSchema)
});
function fleetEnergyStations(cards) {
  const order = [];
  const groups = /* @__PURE__ */ new Map();
  for (const card of cards) {
    const existing = groups.get(card.stationId);
    if (!existing) {
      order.push(card.stationId);
      groups.set(card.stationId, {
        stationId: card.stationId,
        stationName: card.stationName,
        canCommand: card.canCommand,
        firmwareVersion: card.firmwareVersion,
        firmwareStatus: card.firmwareStatus,
        cards: [card]
      });
      continue;
    }
    existing.cards.push(card);
  }
  return order.flatMap((id) => {
    const group = groups.get(id);
    return group ? [group] : [];
  });
}
var fleetEnergyOverviewSchema = z.object({
  groups: z.array(fleetEnergyGroupSchema),
  choices: z.array(fleetEnergyChoiceSchema),
  cards: z.array(fleetEnergyCardSchema),
  stations: z.array(fleetEnergyStationSchema).optional(),
  vehicles: z.array(fleetEnergyVehicleSchema),
  tariff: fleetTariffSettingsSchema,
  chips: z.array(fleetTariffChipSchema),
  invoices: z.array(fleetChargeInvoiceSchema)
});
var fleetEnergyCommandResultSchema = z.enum([
  "accepted",
  "rejected",
  "no_response",
  "unreachable"
]);
var fleetEnergyCommandResponseSchema = z.object({
  result: fleetEnergyCommandResultSchema
});
function fleetEnergyCommandLabel(result) {
  switch (result) {
    case "accepted":
      return "Befehl angenommen";
    case "rejected":
      return "Befehl abgelehnt";
    case "no_response":
      return "Keine Antwort der Wallbox";
    case "unreachable":
      return "Wallbox nicht erreichbar";
  }
}
var fleetEnergyStartRequestSchema = z.object({
  vehicleId: z.string().uuid(),
  idTag: z.string().trim().min(1, "Bitte einen Chip angeben.")
});
var fleetEnergyResetRequestSchema = z.object({
  type: z.enum(["soft", "hard"])
});
var FIRMWARE_ADDRESS = "Die Adresse muss mit https:// beginnen.";
var fleetEnergyFirmwareRequestSchema = z.object({
  location: z.preprocess(
    (value) => typeof value === "string" ? value.trim() : value,
    z.string().refine((value) => value.startsWith("https://") && value.length <= 4096, FIRMWARE_ADDRESS)
  )
});
var FLEET_FIRMWARE_STATUS_LABELS = {
  Downloaded: "Heruntergeladen",
  DownloadFailed: "Download fehlgeschlagen",
  Downloading: "Wird heruntergeladen",
  Idle: "Bereit",
  InstallationFailed: "Installation fehlgeschlagen",
  Installing: "Wird installiert",
  Installed: "Installiert"
};
function fleetFirmwareStatusLabel(status) {
  return FLEET_FIRMWARE_STATUS_LABELS[status] ?? status;
}
var CONNECTOR_AMPS = "Die Amperezahl liegt zwischen 6 und 2000 A.";
var setFleetConnectorLimitRequestSchema = z.object({
  limitAmps: z.custom(
    (value) => value === null || typeof value === "number" && Number.isInteger(value) && value >= 6 && value <= 2e3,
    CONNECTOR_AMPS
  )
});
var saveFleetEnergyGroupRequestSchema = z.object({
  name: z.string().trim().min(1, "Bitte einen Namen angeben.").max(80),
  limitAmps: z.number().int().min(6, "Die Obergrenze ist mindestens 6 A.").max(2e3),
  connectorIds: z.array(z.string().uuid())
});
var fleetEnergyGroupCreatedSchema = z.object({
  id: z.string().uuid()
});
var setFleetEnergyDepartureRequestSchema = z.object({
  departureAt: z.string().datetime().nullable()
});

// src/fleet-task-group.ts
var FleetTaskGroup = {
  OVERDUE: "overdue",
  UPCOMING: "upcoming",
  DAMAGES: "damages",
  MILEAGE: "mileage",
  HELD: "held"
};
var DEADLINE_KINDS = /* @__PURE__ */ new Set([
  "hu",
  "uvv_vehicle",
  "service",
  "leasing",
  "insurance",
  "license",
  "instruction",
  "manual"
]);
function fleetTaskMatchesGroup(task, group) {
  switch (group) {
    case FleetTaskGroup.OVERDUE:
      return DEADLINE_KINDS.has(task.kind) && task.overdue;
    case FleetTaskGroup.UPCOMING:
      return DEADLINE_KINDS.has(task.kind) && !task.overdue;
    case FleetTaskGroup.DAMAGES:
      return task.kind === "damage_open";
    case FleetTaskGroup.MILEAGE:
      return task.kind === "mileage_stale";
    case FleetTaskGroup.HELD:
      return task.kind === "vehicle_held";
    default:
      return false;
  }
}
var WebhookEvent = {
  TICKET_CREATED: "ticket.created",
  TICKET_UPDATED: "ticket.updated",
  TICKET_STATUS_CHANGED: "ticket.status_changed",
  TICKET_COMMENTED: "ticket.commented"
};
var WEBHOOK_EVENTS = Object.values(WebhookEvent);
var webhookEventSchema = z.enum(WebhookEvent);
var WEBHOOK_EVENT_LABELS = {
  "ticket.created": "Ticket erstellt",
  "ticket.updated": "Ticket ge\xE4ndert",
  "ticket.status_changed": "Status ge\xE4ndert",
  "ticket.commented": "Kommentar"
};
var WebhookDirection = {
  INBOUND: "INBOUND",
  OUTBOUND: "OUTBOUND"
};
var webhookDirectionSchema = z.enum(WebhookDirection);
var WEBHOOK_DIRECTION_LABELS = {
  INBOUND: "Eingehend (ERP \u2192 Mitarbeiterportal)",
  OUTBOUND: "Ausgehend (Mitarbeiterportal \u2192 ERP)"
};
var MailProvider = {
  MICROSOFT_GRAPH_MAIL: "MICROSOFT_GRAPH_MAIL",
  IMAP: "IMAP"
};
var mailProviderSchema = z.enum(MailProvider);
var MAIL_PROVIDER_LABELS = {
  MICROSOFT_GRAPH_MAIL: "Microsoft Graph (M365)",
  IMAP: "IMAP"
};
var WEBHOOK_FIELD_TARGETS = [
  "subject",
  "description",
  "priority",
  "type",
  "status",
  "requesterEmail",
  "requesterName",
  "commentBody",
  "ticketNumber",
  "externalId"
];
var webhookFieldTargetSchema = z.enum(WEBHOOK_FIELD_TARGETS);
var WEBHOOK_FIELD_TARGET_LABELS = {
  subject: "Betreff",
  description: "Beschreibung",
  priority: "Priorit\xE4t",
  type: "Ticket-Typ",
  status: "Status",
  requesterEmail: "Anfragende E-Mail",
  requesterName: "Anfragender Name",
  commentBody: "Kommentar",
  ticketNumber: "Ticketnummer",
  externalId: "Externe ID"
};
var webhookFieldTransformSchema = z.enum(["trim", "upper", "lower", "map"]);
var webhookFieldMappingSchema = z.object({
  /** Dot path into the JSON payload, with or without a leading `$.` */
  source: z.string().trim().min(1).max(200),
  /** Output key (ERP field) or inbound ticket field name. */
  target: z.string().trim().min(1).max(64),
  transform: webhookFieldTransformSchema.optional(),
  map: z.record(z.string(), z.string()).optional()
});
var updateMailIntegrationRequestSchema = z.object({
  isActive: z.boolean(),
  provider: mailProviderSchema.optional()
});
var updateLdapIntegrationRequestSchema = z.object({
  isActive: z.boolean(),
  deactivateMissing: z.boolean().optional()
});
var upsertWebhookEndpointRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  isActive: z.boolean().default(true),
  direction: webhookDirectionSchema,
  url: z.url().optional().nullable(),
  events: z.array(webhookEventSchema).max(8).default([]),
  fieldMappings: z.array(webhookFieldMappingSchema).max(40).default([])
});
var mailIntegrationStatusSchema = z.object({
  id: z.string().uuid().nullable(),
  name: z.string(),
  isActive: z.boolean(),
  provider: mailProviderSchema,
  credentialsSet: z.boolean(),
  lastReceivedAt: z.iso.datetime().nullable(),
  lastPollAt: z.iso.datetime().nullable(),
  lastError: z.string().nullable(),
  processedCount: z.number().int().nonnegative()
});
var ldapIntegrationStatusSchema = z.object({
  id: z.string().uuid().nullable(),
  name: z.string(),
  isActive: z.boolean(),
  envConfigured: z.boolean(),
  deactivateMissing: z.boolean(),
  lastSyncAt: z.iso.datetime().nullable(),
  lastError: z.string().nullable(),
  lastSyncCreated: z.number().int().nonnegative(),
  lastSyncUpdated: z.number().int().nonnegative(),
  lastSyncDeactivated: z.number().int().nonnegative()
});
var entraIntegrationStatusSchema = z.object({
  id: z.string().uuid().nullable(),
  name: z.string(),
  envConfigured: z.boolean(),
  lastSyncAt: z.iso.datetime().nullable(),
  lastError: z.string().nullable(),
  lastSyncCreated: z.number().int().nonnegative(),
  lastSyncUpdated: z.number().int().nonnegative(),
  lastSyncDeactivated: z.number().int().nonnegative()
});
var DirectorySource = {
  ENTRA: "entra",
  LDAP: "ldap"
};
var directorySourceSchema = z.enum(DirectorySource);
var directoryStatusSchema = z.object({
  entra: entraIntegrationStatusSchema,
  ldap: ldapIntegrationStatusSchema
});
var directorySyncRequestSchema = z.strictObject({
  source: directorySourceSchema
});
var directorySyncQueuedSchema = z.object({
  source: directorySourceSchema,
  queued: z.literal(true)
});
var webhookEndpointSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean(),
  direction: webhookDirectionSchema,
  url: z.string().nullable(),
  events: z.array(webhookEventSchema),
  fieldMappings: z.array(webhookFieldMappingSchema),
  hasSecret: z.boolean(),
  hasInboundToken: z.boolean(),
  inboundPath: z.string().nullable(),
  lastStatus: z.number().int().nullable(),
  lastError: z.string().nullable(),
  lastFiredAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var webhookEndpointSecretsSchema = z.object({
  inboundToken: z.string().optional(),
  inboundUrl: z.string().optional(),
  signingSecret: z.string().optional()
});
var webhookEndpointCreatedSchema = z.object({
  endpoint: webhookEndpointSchema,
  secrets: webhookEndpointSecretsSchema
});
var integrationsOverviewSchema = z.object({
  mail: mailIntegrationStatusSchema,
  ldap: ldapIntegrationStatusSchema,
  entra: entraIntegrationStatusSchema,
  webhooks: z.array(webhookEndpointSchema)
});
var mailPollResultSchema = z.object({
  processed: z.number().int().nonnegative(),
  created: z.number().int().nonnegative(),
  commented: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative()
});
var ldapSyncResultSchema = z.object({
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  deactivated: z.number().int().nonnegative()
});
var entraSyncResultSchema = ldapSyncResultSchema;
function getByPath(source, path) {
  const trimmed = path.trim();
  const normalized = trimmed.startsWith("$.") ? trimmed.slice(2) : trimmed.startsWith("$") ? trimmed.slice(1) : trimmed;
  if (!normalized) {
    return source;
  }
  const parts = normalized.split(".").filter(Boolean);
  let current = source;
  for (const part of parts) {
    if (current == null || typeof current !== "object" || Array.isArray(current)) {
      if (Array.isArray(current) && /^\d+$/.test(part)) {
        current = current[Number(part)];
        continue;
      }
      return void 0;
    }
    current = current[part];
  }
  return current;
}
function asString(value) {
  if (value == null) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}
function applyTransform(value, mapping) {
  if (!mapping.transform) {
    return value;
  }
  const text = asString(value);
  if (mapping.transform === "trim") {
    return text.trim();
  }
  if (mapping.transform === "upper") {
    return text.toUpperCase();
  }
  if (mapping.transform === "lower") {
    return text.toLowerCase();
  }
  const key = text.trim();
  return mapping.map?.[key] ?? mapping.map?.[key.toLowerCase()] ?? value;
}
function applyFieldMappings(source, mappings) {
  const out = {};
  for (const mapping of mappings) {
    const raw = getByPath(source, mapping.source);
    if (raw === void 0) {
      continue;
    }
    out[mapping.target] = applyTransform(raw, mapping);
  }
  return out;
}
function ticketNumberFromSubject(subject) {
  const match = /(?:\bticket\s*)?#(\d{1,8})\b/i.exec(subject);
  if (!match) {
    return null;
  }
  const number = Number(match[1]);
  return Number.isFinite(number) && number > 0 ? number : null;
}
function mappedString(mapped, key) {
  const value = mapped[key];
  if (typeof value !== "string") {
    return void 0;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : void 0;
}
function mappedTicketType(mapped) {
  const value = mappedString(mapped, "type");
  if (!value) {
    return void 0;
  }
  const parsed = ticketTypeSchema.safeParse(value.toUpperCase());
  return parsed.success ? parsed.data : void 0;
}
function mappedTicketPriority(mapped) {
  const value = mappedString(mapped, "priority");
  if (!value) {
    return void 0;
  }
  const parsed = ticketPrioritySchema.safeParse(value.toUpperCase());
  return parsed.success ? parsed.data : void 0;
}
function mappedTicketStatus(mapped) {
  const value = mappedString(mapped, "status");
  if (!value) {
    return void 0;
  }
  const parsed = ticketStatusSchema.safeParse(value.toUpperCase());
  return parsed.success ? parsed.data : void 0;
}
var INTRANET_HIGHLIGHT_TIMEZONE = "Europe/Berlin";
var intranetHighlightSpanSchema = z.enum(["today", "d3", "d7", "d14", "custom"]);
var pinIntranetContentRequestSchema = z.strictObject({
  span: intranetHighlightSpanSchema,
  until: z.iso.datetime().optional()
}).superRefine((value, ctx) => {
  if (value.span === "custom" && value.until === void 0) {
    ctx.addIssue({
      code: "custom",
      path: ["until"],
      message: "Bitte einen Zeitpunkt w\xE4hlen."
    });
  }
});
var SPAN_DAYS = {
  today: 0,
  d3: 3,
  d7: 7,
  d14: 14
};
function intranetHighlightUntil(span, now, customUntil) {
  if (span === "custom") {
    if (!customUntil) {
      throw new Error("custom until missing");
    }
    return customUntil;
  }
  const clock = berlinClock(now);
  if (span === "today") {
    return berlinLocalToUtc(clock.year, clock.month, clock.day, 23, 59, 59);
  }
  const day = addCalendarDays(clock, SPAN_DAYS[span]);
  return berlinLocalToUtc(day.year, day.month, day.day, clock.hour, clock.minute, clock.second);
}
function berlinClock(instant) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: INTRANET_HIGHLIGHT_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
  }).formatToParts(instant);
  const value = (type) => Number(parts.find((part) => part.type === type)?.value);
  return {
    year: value("year"),
    month: value("month"),
    day: value("day"),
    hour: value("hour"),
    minute: value("minute"),
    second: value("second")
  };
}
function daysInMonth2(year, month) {
  return [0, 31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month] ?? 31;
}
function isLeapYear(year) {
  return year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
}
function addCalendarDays(from, days) {
  let { year, month, day } = from;
  day += days;
  while (day > daysInMonth2(year, month)) {
    day -= daysInMonth2(year, month);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return { year, month, day };
}
function berlinLocalToUtc(year, month, day, hour, minute, second) {
  const desired = Date.UTC(year, month - 1, day, hour, minute, second);
  const start = desired - 4 * 36e5;
  const end = desired + 4 * 36e5;
  const matches = [];
  for (let time = start; time <= end; time += 6e4) {
    const clock = berlinClock(new Date(time));
    if (sameClock(clock, { year, month, day, hour, minute, second })) {
      matches.push(time);
    }
  }
  if (matches.length > 0) {
    return new Date(Math.min(...matches));
  }
  const wanted = hour * 3600 + minute * 60 + second;
  for (let time = start; time <= end + 36e5; time += 6e4) {
    const clock = berlinClock(new Date(time));
    if (clock.year !== year || clock.month !== month || clock.day !== day) {
      continue;
    }
    if (clock.hour * 3600 + clock.minute * 60 + clock.second >= wanted) {
      return new Date(time);
    }
  }
  throw new Error("Berlin local time not found");
}
function sameClock(left, right) {
  return left.year === right.year && left.month === right.month && left.day === right.day && left.hour === right.hour && left.minute === right.minute && left.second === right.second;
}

// src/intranet-calendar.ts
var DATE_SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/;
function pad2(value) {
  return String(value).padStart(2, "0");
}
function formatDate(parts) {
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}
function daysInMonth3(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
function shiftParts(parts, days) {
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate()
  };
}
function weekdayMonday0(parts) {
  const sunday0 = new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
  return sunday0 === 0 ? 6 : sunday0 - 1;
}
function isoWeekNumber(parts) {
  const thursday = shiftParts(parts, 3 - weekdayMonday0(parts));
  const jan4 = { year: thursday.year, month: 1, day: 4 };
  const week1Monday = shiftParts(jan4, -weekdayMonday0(jan4));
  const monday = shiftParts(parts, -weekdayMonday0(parts));
  const diff = Math.round(
    (Date.UTC(monday.year, monday.month - 1, monday.day) - Date.UTC(week1Monday.year, week1Monday.month - 1, week1Monday.day)) / 864e5
  );
  return diff / 7 + 1;
}
function parseBerlinDate(value) {
  const match = DATE_SHAPE.exec(value);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth3(year, month)) {
    return null;
  }
  return { year, month, day };
}
function berlinCalendarDate(instant) {
  const parts = zonedDateTimeParts(instant, INTRANET_HIGHLIGHT_TIMEZONE);
  if (parts.hour === 24) {
    return formatDate(shiftParts(parts, 1));
  }
  return formatDate(parts);
}
function berlinDayBounds(date) {
  const parts = parseBerlinDate(date);
  if (!parts) {
    return null;
  }
  return zonedDayRange(INTRANET_HIGHLIGHT_TIMEZONE, parts.year, parts.month, parts.day);
}
function berlinInclusiveDayCount(from, to) {
  const start = parseBerlinDate(from);
  const end = parseBerlinDate(to);
  if (!start || !end) {
    return null;
  }
  const diff = Math.round(
    (Date.UTC(end.year, end.month - 1, end.day) - Date.UTC(start.year, start.month - 1, start.day)) / 864e5
  );
  return diff + 1;
}
function berlinDateTime(date, hour, minute) {
  const parts = parseBerlinDate(date);
  if (!parts || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }
  return fromZonedTime(
    INTRANET_HIGHLIGHT_TIMEZONE,
    parts.year,
    parts.month,
    parts.day,
    hour,
    minute,
    0
  );
}
function addBerlinDays(date, days) {
  const parts = parseBerlinDate(date);
  if (!parts || !Number.isInteger(days)) {
    return null;
  }
  return formatDate(shiftParts(parts, days));
}
function berlinWeek(date) {
  const parts = parseBerlinDate(date);
  if (!parts) {
    return null;
  }
  const start = shiftParts(parts, -weekdayMonday0(parts));
  return {
    start: formatDate(start),
    end: formatDate(shiftParts(start, 6)),
    isoWeek: isoWeekNumber(parts)
  };
}
function berlinMonth(date) {
  const parts = parseBerlinDate(date);
  if (!parts) {
    return null;
  }
  return {
    start: formatDate({ year: parts.year, month: parts.month, day: 1 }),
    end: formatDate({
      year: parts.year,
      month: parts.month,
      day: daysInMonth3(parts.year, parts.month)
    })
  };
}

// src/intranet.ts
var IntranetContentKind = {
  NEWS: "news",
  GUIDE: "guide",
  DOWNLOAD: "download",
  FORM: "form"
};
var intranetContentKindSchema = z.enum(IntranetContentKind);
var INTRANET_CONTENT_KIND_LABELS = {
  news: "Nachricht",
  guide: "Anleitung",
  download: "Download",
  form: "Formular"
};
var INTRANET_LIBRARY_KINDS = [
  IntranetContentKind.GUIDE,
  IntranetContentKind.DOWNLOAD,
  IntranetContentKind.FORM
];
function isIntranetLibraryKind(kind) {
  return kind !== IntranetContentKind.NEWS;
}
var IntranetSection = {
  NEWS: "news",
  LIBRARY: "library"
};
var intranetSectionSchema = z.enum(IntranetSection);
var INTRANET_PAGE_SIZE = 20;
var INTRANET_FILE_MAX_BYTES = 10 * 1024 * 1024;
var INTRANET_FILE_ALLOWED_MIME = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "text/plain",
  "text/csv",
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "video/mp4",
  "video/webm",
  "application/zip"
];
var INTRANET_FILE_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.txt,.csv,.png,.jpg,.jpeg,.webp,.gif,.mp4,.webm,.zip";
var intranetFileRefSchema = z.object({
  id: z.string().uuid(),
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  version: z.number().int().min(1)
});
var intranetFileSchema = intranetFileRefSchema.extend({
  isCurrent: z.boolean(),
  scanStatus: attachmentScanStatusSchema,
  uploadedBy: z.object({ id: z.string().uuid(), displayName: z.string() }),
  uploadedAt: z.iso.datetime(),
  /** False when this channel has switched that file type's download off. Absent means allowed. */
  downloadable: z.boolean().optional()
});
var intranetFileListResponseSchema = z.object({
  items: z.array(intranetFileSchema)
});
var intranetContentRevisionSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  teaser: z.string().nullable(),
  body: z.string().nullable(),
  fileId: z.string().uuid().nullable(),
  createdAt: z.iso.datetime()
});
var intranetContentRevisionListResponseSchema = z.object({
  items: z.array(intranetContentRevisionSchema)
});
function intranetFileDownloadPath(fileId) {
  return `/intranet/files/${fileId}/download`;
}
var intranetFilePreviewSchema = z.object({
  text: z.string()
});
function intranetFilePreviewPath(fileId) {
  return `/intranet/files/${fileId}/preview`;
}
var INTRANET_IMAGE_MAX_BYTES = 2097152;
var INTRANET_IMAGE_MIN_BYTES = 512e3;
var INTRANET_IMAGE_ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp"];
var INTRANET_IMAGE_ACCEPT = ".jpg,.jpeg,.png,.webp";
var INTRANET_VIDEO_MAX_BYTES = 12 * 1024 * 1024 - 1;
var INTRANET_IMAGE_MAX_MB = Math.floor(INTRANET_VIDEO_MAX_BYTES / (1024 * 1024));
function intranetImageMaxMb(bytes) {
  return Math.max(1, Math.round(bytes / (1024 * 1024)));
}
function intranetImageMaxBytesFromMb(mb) {
  return mb * 1024 * 1024;
}
var INTRANET_VIDEO_ALLOWED_MIME = ["video/mp4", "video/webm"];
var INTRANET_VIDEO_ACCEPT = ".mp4,.webm";
var INTRANET_IMAGE_ALT_MAX = 200;
var intranetImageUploadMetaSchema = z.strictObject({
  alt: z.string().trim().min(3, "Bitte einen Alternativtext mit mindestens 3 Zeichen eingeben.").max(INTRANET_IMAGE_ALT_MAX)
});
var intranetCoverUploadMetaSchema = z.strictObject({
  alt: z.string().trim().max(INTRANET_IMAGE_ALT_MAX).optional().default("")
});
var intranetFeedbackCountsSchema = z.object({
  helpful: z.number().int().nonnegative(),
  notHelpful: z.number().int().nonnegative()
});
var INTRANET_FEEDBACK_NOTE_MAX = 280;
var submitIntranetFeedbackRequestSchema = z.strictObject({
  helpful: z.boolean(),
  note: z.string().trim().max(INTRANET_FEEDBACK_NOTE_MAX).nullable().optional()
});
var intranetOwnFeedbackSchema = z.object({
  helpful: z.boolean(),
  note: z.string().nullable()
});
var intranetFeedbackNoteSchema = z.object({
  id: z.string().uuid(),
  helpful: z.boolean(),
  note: z.string(),
  author: z.object({ id: z.string().uuid(), displayName: z.string() }),
  updatedAt: z.iso.datetime()
});
var intranetFeedbackNotesResponseSchema = z.object({
  counts: intranetFeedbackCountsSchema,
  items: z.array(intranetFeedbackNoteSchema)
});
var INTRANET_NOTICE_TEXT_MAX = 2e3;
var submitIntranetNoticeRequestSchema = z.strictObject({
  text: z.string().trim().min(1, "Bitte einen Text eingeben.").max(INTRANET_NOTICE_TEXT_MAX)
});
var intranetNoticePersonSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string()
});
var intranetNoticeSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  createdAt: z.iso.datetime(),
  author: intranetNoticePersonSchema,
  recipients: z.array(intranetNoticePersonSchema),
  responsible: intranetNoticePersonSchema.nullable()
});
var intranetNoticeListSchema = z.object({
  responsible: intranetNoticePersonSchema.nullable(),
  items: z.array(intranetNoticeSchema.omit({ responsible: true }))
});
var lucideIconName = z.string().trim().regex(/^[A-Za-z][A-Za-z0-9]*$/, "Bitte einen Lucide-Icon-Namen eingeben.").max(40);
var categoryDescription = z.string().trim().max(2e3);
var INTRANET_CATEGORY_PUBLICATION = [
  "existing_authors",
  "channel_admins",
  "members",
  "members_approval"
];
var intranetCategoryPublicationSchema = z.enum(INTRANET_CATEGORY_PUBLICATION);
var intranetCategorySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  sortOrder: z.number().int(),
  iconName: z.string().nullable(),
  canPost: z.boolean(),
  /** Present only for callers allowed to manage channel authors and readers. */
  posterRoleIds: z.array(z.string().uuid()).optional(),
  /** Present only for callers allowed to manage channel authors and readers. */
  readerRoleIds: z.array(z.string().uuid()).optional(),
  /** Direct persons, including inactive ones, for the Mitglieder tab. */
  directMembers: z.array(
    z.object({
      id: z.string().uuid(),
      displayName: z.string()
    })
  ).optional(),
  /** Present for callers who may save Allgemein. */
  description: categoryDescription.nullable().optional(),
  /** Present for callers who may save Allgemein. Null after the role was cleared. */
  managerRoleId: z.string().uuid().nullable().optional(),
  canManage: z.boolean().optional(),
  /** Servable channel image. Omitted while the scan has not released it. */
  imageUrl: z.string().nullable().optional(),
  bannerUrl: z.string().nullable().optional(),
  imageScanStatus: attachmentScanStatusSchema.nullable().optional(),
  bannerScanStatus: attachmentScanStatusSchema.nullable().optional(),
  managerRoleOptions: z.array(roleOptionSchema).optional(),
  /** False until the Sichtbarkeit tab opens the channel. Existing channels stay closed. */
  open: z.boolean().optional(),
  /** False on an open channel the person has not joined and cannot otherwise read. */
  canRead: z.boolean().optional(),
  /** Own join. Absent when the channel is closed or the person can already read it. */
  canJoin: z.boolean().optional(),
  /** Own direct membership. Leaving does not drop a role. */
  canLeave: z.boolean().optional(),
  /** Absent on older payloads. On when the channel has not switched comments off. */
  commentsEnabled: z.boolean().optional(),
  reactionsEnabled: z.boolean().optional(),
  pdfDownloadEnabled: z.boolean().optional(),
  mediaDownloadEnabled: z.boolean().optional(),
  newsfeedEnabled: z.boolean().optional(),
  memberListEnabled: z.boolean().optional(),
  /** News only. Absent on older payloads. New channels use channel admins. */
  publication: intranetCategoryPublicationSchema.optional()
});
var intranetCategoryListResponseSchema = z.object({
  items: z.array(intranetCategorySchema)
});
var createIntranetCategoryRequestSchema = z.strictObject({
  name: z.string().trim().min(2, "Bitte einen Namen mit mindestens 2 Zeichen eingeben.").max(80),
  sortOrder: z.number().int().min(0).max(1e4).optional(),
  iconName: z.union([lucideIconName, z.literal(""), z.null()]).optional(),
  description: categoryDescription.optional(),
  /** Required at create. Later updates may clear it. */
  managerRoleId: z.string().uuid()
});
var updateIntranetCategoryRequestSchema = createIntranetCategoryRequestSchema.omit({ managerRoleId: true, description: true }).partial().extend({
  description: categoryDescription.nullable().optional(),
  managerRoleId: z.string().uuid().nullable().optional(),
  posterRoleIds: z.array(z.string().uuid()).max(200).optional(),
  readerRoleIds: z.array(z.string().uuid()).max(200).optional(),
  /** Replaces direct persons. Does not change a platform role assignment. */
  memberUserIds: z.array(z.string().uuid()).max(500).optional(),
  /** Sichtbarkeit tab. Omitted fields stay as they are. */
  open: z.boolean().optional(),
  commentsEnabled: z.boolean().optional(),
  reactionsEnabled: z.boolean().optional(),
  pdfDownloadEnabled: z.boolean().optional(),
  mediaDownloadEnabled: z.boolean().optional(),
  newsfeedEnabled: z.boolean().optional(),
  memberListEnabled: z.boolean().optional(),
  /** Einstellungen tab. Omitted fields stay as they are. */
  publication: intranetCategoryPublicationSchema.optional()
});
var INTRANET_CATEGORY_ALL_USERS_MESSAGE = "Alle Intranet-Nutzer sehen diesen Channel.";
var intranetCategoryMemberPersonSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string()
});
var intranetCategoryMembersOverviewSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("hidden")
  }),
  z.object({
    kind: z.literal("all-intranet-users"),
    message: z.literal(INTRANET_CATEGORY_ALL_USERS_MESSAGE)
  }),
  z.object({
    kind: z.literal("lists"),
    persons: z.array(intranetCategoryMemberPersonSchema),
    readerRoles: z.array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        activeUserCount: z.number().int().nonnegative()
      })
    )
  })
]);
var INTRANET_CHANNEL_ICONS = [
  "Bookmark",
  "Building2",
  "Newspaper",
  "Users",
  "Wrench"
];
var intranetChannelIconSchema = z.enum(INTRANET_CHANNEL_ICONS);
var knowledgeCategoryIcon = z.union([intranetChannelIconSchema, z.literal(""), z.null()]);
var intranetKnowledgeCategoryParentSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string()
});
var intranetKnowledgeCategorySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  sortOrder: z.number().int(),
  iconName: z.string().nullable(),
  /** Set once the banner scan has released the image. */
  bannerUrl: z.string().nullable().optional(),
  /** Assigned pages, including drafts. Present on the editorial list. */
  pageCount: z.number().int().nonnegative().optional(),
  /** Empty on a top category means every intranet reader. A subcategory can only narrow that set. */
  readerRoleIds: z.array(z.string().uuid()).optional(),
  /** 1 is a top category. 2 is a subcategory. Older payloads omit it. */
  level: z.union([z.literal(1), z.literal(2)]).optional(),
  /** Null on a top category. Set on a subcategory so the path can open the parent. */
  parent: intranetKnowledgeCategoryParentSchema.nullable().optional()
});
var intranetKnowledgeCategoryListResponseSchema = z.object({
  items: z.array(intranetKnowledgeCategorySchema)
});
var knowledgeCategoryName = z.string().trim().min(2, "Bitte einen Namen mit mindestens 2 Zeichen eingeben.").max(80);
var createIntranetKnowledgeCategoryRequestSchema = z.strictObject({
  name: knowledgeCategoryName,
  sortOrder: z.number().int().min(0).max(1e4).optional(),
  iconName: knowledgeCategoryIcon.optional(),
  /** Set to hang this category under one top category. Omitted, it stays a top category. */
  parentId: z.string().uuid().optional(),
  /** Omitted, a subcategory keeps the parent’s reader roles. A sent list can only narrow them. */
  readerRoleIds: z.array(z.string().uuid()).optional()
});
var updateIntranetKnowledgeCategoryRequestSchema = z.strictObject({
  name: knowledgeCategoryName.optional(),
  sortOrder: z.number().int().min(0).max(1e4).optional(),
  iconName: knowledgeCategoryIcon.optional(),
  /** Moves a subcategory under another top category. */
  parentId: z.string().uuid().optional(),
  /** A subcategory can only narrow the current parent’s reader roles. */
  readerRoleIds: z.array(z.string().uuid()).optional()
});
var intranetKnowledgeCategoryPageEntrySchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  slug: z.string(),
  kind: intranetContentKindSchema,
  teaser: z.string().nullable()
});
var intranetKnowledgeCategoryPageSchema = z.object({
  category: intranetKnowledgeCategorySchema.pick({
    id: true,
    name: true,
    slug: true,
    sortOrder: true,
    iconName: true,
    bannerUrl: true,
    level: true,
    parent: true
  }),
  /** Second level, in the stored order. Empty on a subcategory. */
  subcategories: z.array(
    intranetKnowledgeCategoryParentSchema.extend({
      sortOrder: z.number().int()
    })
  ),
  pages: z.array(intranetKnowledgeCategoryPageEntrySchema)
});
var INTRANET_KNOWLEDGE_RAIL_LIMIT = 8;
var INTRANET_KNOWLEDGE_FREQUENT_DAYS = 90;
var intranetKnowledgeHomeTileSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  iconName: z.string().nullable()
});
var intranetKnowledgeHomeSchema = z.object({
  tiles: z.array(intranetKnowledgeHomeTileSchema),
  quickAccess: z.array(intranetKnowledgeCategoryPageEntrySchema),
  frequentlyUsed: z.array(intranetKnowledgeCategoryPageEntrySchema),
  recentlyUpdated: z.array(intranetKnowledgeCategoryPageEntrySchema),
  favorites: z.array(intranetKnowledgeCategoryPageEntrySchema),
  /** Published pages with no leading category, in `librarySortOrder`. */
  uncategorized: z.array(intranetKnowledgeCategoryPageEntrySchema).optional()
});
var replaceIntranetKnowledgeQuickAccessRequestSchema = z.strictObject({
  contentIds: z.array(z.string().uuid())
});
var intranetKnowledgeQuickAccessEntrySchema = z.object({
  contentId: z.string().uuid(),
  title: z.string(),
  slug: z.string(),
  kind: intranetContentKindSchema,
  published: z.boolean()
});
var intranetKnowledgeQuickAccessListSchema = z.object({
  items: z.array(intranetKnowledgeQuickAccessEntrySchema)
});
var removeIntranetKnowledgeCategoryRequestSchema = z.strictObject({
  /** Another category receives the assignments. Null clears them. The pages stay. */
  targetCategoryId: z.string().uuid().nullable()
});
var assignIntranetLeadingCategoryRequestSchema = z.strictObject({
  leadingCategoryId: z.string().uuid().nullable(),
  /** Omitted, the existing find-spots stay. The leading category is never stored again here. */
  furtherCategoryIds: z.array(z.string().uuid()).optional()
});
var intranetLeadingCategoryAssignmentSchema = z.object({
  slug: z.string(),
  leadingCategory: intranetKnowledgeCategorySchema.pick({ id: true, name: true, slug: true }).nullable(),
  /** Find-spots. Absent on older payloads. Never includes the leading category. */
  furtherCategories: z.array(intranetKnowledgeCategorySchema.pick({ id: true, name: true, slug: true })).optional()
});
var knowledgeManufacturerName = z.string().trim().min(1, "Bitte einen Herstellernamen eingeben.").max(80);
var intranetKnowledgeManufacturerSchema = z.object({
  id: z.string().uuid(),
  name: z.string()
});
var intranetKnowledgeManufacturerListResponseSchema = z.object({
  items: z.array(intranetKnowledgeManufacturerSchema)
});
var createIntranetKnowledgeManufacturerRequestSchema = z.strictObject({
  name: knowledgeManufacturerName
});
var updateIntranetKnowledgeManufacturerRequestSchema = z.strictObject({
  name: knowledgeManufacturerName
});
var removeIntranetKnowledgeManufacturerRequestSchema = z.strictObject({
  /** Another manufacturer, or null when the pages should keep none. */
  manufacturerId: z.string().uuid().nullable()
});
var intranetKnowledgeManufacturerAssignmentSchema = z.object({
  slug: z.string(),
  manufacturer: intranetKnowledgeManufacturerSchema.nullable()
});
var assignIntranetKnowledgeManufacturerRequestSchema = z.strictObject({
  manufacturerId: z.string().uuid().nullable()
});
var intranetKnowledgeManufacturerRemovalSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  pages: z.array(intranetKnowledgeManufacturerAssignmentSchema)
});
var intranetKnowledgeAdoptionCategorySchema = z.object({
  name: z.string(),
  slug: z.string(),
  sortOrder: z.number().int(),
  iconName: z.string().nullable(),
  readerRoleIds: z.array(z.string().uuid())
});
var intranetKnowledgeAdoptionSchema = z.object({
  categories: z.array(intranetKnowledgeAdoptionCategorySchema),
  pages: z.array(
    z.object({
      slug: z.string(),
      kind: intranetContentKindSchema,
      leadingCategorySlug: z.string()
    })
  ),
  /** News stay on their channel and receive no leading category. */
  news: z.array(
    z.object({
      slug: z.string(),
      categorySlug: z.string(),
      leadingCategorySlug: z.string().nullable()
    })
  )
});
var intranetDepartmentOptionSchema = z.object({
  id: z.string().uuid(),
  name: z.string()
});
var intranetDepartmentOptionsResponseSchema = z.object({
  items: z.array(intranetDepartmentOptionSchema)
});
var intranetContentSiteSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean()
});
var intranetKnowledgeSitesResponseSchema = z.object({
  sites: z.array(intranetContentSiteSchema.pick({ id: true, name: true })),
  /** The person’s site when it is still active. Null when they have none. */
  preselectedSiteId: z.string().uuid().nullable()
});
var intranetKnowledgeHomeQuerySchema = z.object({
  siteId: z.string().uuid().optional()
});
var intranetKnowledgeCategoryFilterItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  parent: z.object({ id: z.string().uuid(), name: z.string() }).nullable()
});
var intranetKnowledgeCategoryFilterResponseSchema = z.object({
  items: z.array(intranetKnowledgeCategoryFilterItemSchema)
});
var intranetKnowledgeLibraryKindSchema = z.enum(INTRANET_LIBRARY_KINDS);
var intranetKnowledgeCategoryPageQuerySchema = z.object({
  siteId: z.string().uuid().optional(),
  kind: intranetKnowledgeLibraryKindSchema.optional(),
  manufacturerId: z.string().uuid().optional()
});
var intranetContentStateSchema = z.enum(["draft", "published", "archived"]);
var intranetPersonSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  /** Servable profile photo, or null when the person has none. */
  profilePhotoUrl: z.string().nullable().optional()
});
var intranetContentSummarySchema = z.object({
  id: z.string().uuid(),
  kind: intranetContentKindSchema,
  title: z.string(),
  slug: z.string(),
  teaser: z.string().nullable(),
  /** True when the body has more text than the teaser (news cards show „… Mehr anzeigen“). */
  teaserHasMore: z.boolean().default(false),
  category: intranetCategorySchema.pick({ id: true, name: true, slug: true }).nullable(),
  /** Leading Wissensdatenbank category. Absent on older payloads. News stay null. */
  leadingCategory: intranetKnowledgeCategorySchema.pick({ id: true, name: true, slug: true }).nullable().optional(),
  /** Set on a Wissensdatenbank page. Older payloads omit it. */
  librarySortOrder: z.number().int().optional(),
  /** Find-spots. They do not repeat the leading category and do not add readers. */
  furtherCategories: z.array(intranetKnowledgeCategorySchema.pick({ id: true, name: true, slug: true })).optional(),
  targetAll: z.boolean(),
  targetDepts: z.array(z.string().uuid()),
  isDraft: z.boolean(),
  /** Draft, published, or archived. The server always sends it. Older payloads omit it. */
  state: intranetContentStateSchema.optional(),
  publishedAt: z.iso.datetime().nullable(),
  /** Set while a draft waits for the publish job. */
  publishAt: z.iso.datetime().nullable().optional(),
  /** Set while a published entry is due to return to draft. */
  expiresAt: z.iso.datetime().nullable().optional(),
  /** Whether publishing news notifies the audience. Acknowledgements stay separate. */
  notifyAudience: z.boolean().optional(),
  /** Library kinds only. Null on news. */
  reviewDueAt: z.iso.datetime().nullable().optional(),
  /** Prüfdatum. Calendar day in Europe/Berlin. Only „Geprüft“ sets it. */
  reviewedOn: z.iso.date().nullable().optional(),
  reviewerId: z.string().uuid().nullable().optional(),
  /** Name of the responsible person. Stays when that person is inactive. */
  reviewer: z.object({
    id: z.string().uuid(),
    displayName: z.string()
  }).nullable().optional(),
  requiresAcknowledgement: z.boolean(),
  ackDeadline: z.iso.datetime().nullable(),
  createdBy: intranetPersonSchema,
  /** When the row was created. The editorial calendar uses it for news without a termin. */
  createdAt: z.iso.datetime().optional(),
  updatedAt: z.iso.datetime(),
  /** Unique readers per Berlin calendar day. Editorial list shows this as „Gesehen“. */
  viewCount: z.number().int().nonnegative(),
  /** Current file version (library entries); null when nothing is uploaded yet. */
  currentFile: intranetFileRefSchema.nullable(),
  feedback: intranetFeedbackCountsSchema,
  /** True when the page has no selected site. Absent on older payloads. */
  siteWide: z.boolean().optional(),
  /** Selected sites, including a stored inactive site. Empty when the page is site-wide. */
  sites: z.array(intranetContentSiteSchema).optional()
});
var INTRANET_REACTION_KINDS = [
  "like",
  "clap",
  "heart",
  "idea",
  "wow",
  "sad",
  "love",
  "laugh",
  "party",
  "fire",
  "eyes",
  "thanks"
];
var intranetReactionKindSchema = z.enum(INTRANET_REACTION_KINDS);
var intranetReactionCountSchema = z.object({
  reaction: intranetReactionKindSchema,
  count: z.number().int().positive()
});
var INTRANET_REACTION_QUICK_KINDS = [
  "like",
  "clap",
  "heart",
  "idea",
  "wow",
  "sad"
];
var INTRANET_REACTION_EMOJI = {
  like: "\u{1F44D}",
  clap: "\u{1F44F}",
  heart: "\u2764\uFE0F",
  idea: "\u{1F4A1}",
  wow: "\u{1F62E}",
  sad: "\u{1F622}",
  love: "\u{1F60D}",
  laugh: "\u{1F602}",
  party: "\u{1F389}",
  fire: "\u{1F525}",
  eyes: "\u{1F440}",
  thanks: "\u{1F64F}"
};
var INTRANET_REACTION_LABEL = {
  like: "Gef\xE4llt mir",
  clap: "Applaus",
  heart: "Herz",
  idea: "Idee",
  wow: "Wow",
  sad: "Traurig",
  love: "Liebe",
  laugh: "Lachen",
  party: "Feiern",
  fire: "Feuer",
  eyes: "Hinsehen",
  thanks: "Danke"
};
var intranetCommentMentionSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string()
});
var intranetCommentAttachmentKindSchema = z.enum(["image", "file", "gif"]);
var intranetCommentAttachmentSchema = z.object({
  id: z.string().uuid(),
  kind: intranetCommentAttachmentKindSchema,
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  scanStatus: attachmentScanStatusSchema,
  /** False when this channel has switched that file type's download off. Absent means allowed. */
  downloadable: z.boolean().optional()
});
function intranetCommentAttachmentPath(attachmentId) {
  return `/intranet/comments/attachments/${encodeURIComponent(attachmentId)}`;
}
var intranetCommentFields = {
  id: z.string().uuid(),
  body: z.string(),
  author: intranetPersonSchema,
  createdAt: z.iso.datetime(),
  likeCount: z.number().int().nonnegative(),
  likedByMe: z.boolean(),
  /** Null when this person has not reacted. Absent on older payloads. */
  myReaction: intranetReactionKindSchema.nullable().optional(),
  /** Kinds with a count of zero are omitted. Absent on older payloads. */
  reactions: z.array(intranetReactionCountSchema).optional(),
  mentions: z.array(intranetCommentMentionSchema),
  attachment: intranetCommentAttachmentSchema.nullable().optional()
};
var intranetCommentReplySchema = z.object(intranetCommentFields);
var intranetCommentSchema = z.object({
  ...intranetCommentFields,
  replies: z.array(intranetCommentReplySchema)
});
var intranetFeedItemSchema = intranetContentSummarySchema.extend({
  /** API path (prefix with the API base URL); null without a cover or while it is being scanned. */
  coverUrl: z.string().nullable(),
  coverAlt: z.string().nullable(),
  /** Null when the entry has no cover. Pending and blocked images stay off the public card. */
  coverScanStatus: attachmentScanStatusSchema.nullable(),
  /** Which kind sits in the one cover slot. The card plays `video` where `coverUrl` is set. */
  coverMedia: z.enum(["image", "video"]).nullable().optional(),
  pinned: z.boolean(),
  highlightUntil: z.iso.datetime().nullable().optional(),
  highlightSpan: intranetHighlightSpanSchema.nullable().optional(),
  /** Set only on the publish that could not apply a stored highlight. */
  highlightNotice: z.enum(["full", "past"]).optional(),
  canEdit: z.boolean().optional(),
  likeCount: z.number().int().nonnegative(),
  commentCount: z.number().int().nonnegative(),
  likedByMe: z.boolean(),
  bookmarkedByMe: z.boolean(),
  /** Null when this person has not reacted. Absent on older payloads. */
  myReaction: intranetReactionKindSchema.nullable().optional(),
  /** Kinds with a count of zero are omitted. Absent on older payloads. */
  reactions: z.array(intranetReactionCountSchema).optional(),
  /** False when the channel has comments switched off. Absent means on. */
  commentsEnabled: z.boolean().optional(),
  /** False when the channel has reactions switched off. Absent means on. */
  reactionsEnabled: z.boolean().optional()
});
var INTRANET_MAX_PINNED = 6;
var intranetContentDetailSchema = intranetFeedItemSchema.extend({
  body: z.string().nullable(),
  commentsLocked: z.boolean(),
  comments: z.array(intranetCommentSchema),
  myFeedback: intranetOwnFeedbackSchema.nullable(),
  /** Own confirmation time when `requiresAcknowledgement`; null if not yet confirmed. */
  acknowledgedAt: z.iso.datetime().nullable(),
  /** Who receives the Prüferinnerung. Empty when the page is not a Wissensdatenbank page. */
  reviewRecipients: z.array(
    z.object({
      id: z.string().uuid(),
      displayName: z.string()
    })
  ).optional()
});
var INTRANET_POLL_OPTION_MIN = 2;
var INTRANET_POLL_OPTION_MAX = 6;
var intranetPollOptionSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  voteCount: z.number().int().nonnegative()
});
var intranetPollSchema = z.object({
  id: z.string().uuid(),
  question: z.string(),
  options: z.array(intranetPollOptionSchema),
  totalVotes: z.number().int().nonnegative(),
  /** The option this person chose; they may change it until they pick another answer. */
  myOptionId: z.string().uuid().nullable(),
  /** Options stay editable only while `totalVotes` is 0. */
  locked: z.boolean()
});
var intranetPollResponseSchema = z.object({
  poll: intranetPollSchema.nullable()
});
var upsertIntranetPollRequestSchema = z.strictObject({
  question: z.string().trim().min(3, "Bitte eine Frage mit mindestens 3 Zeichen eingeben.").max(200),
  options: z.array(z.string().trim().min(1, "Bitte einen Antworttext eingeben.").max(80)).min(INTRANET_POLL_OPTION_MIN, "Eine Umfrage hat zwei bis sechs Antworten.").max(INTRANET_POLL_OPTION_MAX, "Eine Umfrage hat zwei bis sechs Antworten.")
});
var voteIntranetPollRequestSchema = z.strictObject({
  optionId: z.string().uuid()
});
var intranetContentListQuerySchema = z.object({
  kind: intranetContentKindSchema.optional(),
  /** `news` = kind news only; `library` = guides, downloads, forms. Combined with `kind` if both set. */
  section: intranetSectionSchema.optional(),
  categoryId: z.string().uuid().optional(),
  /** `true` = editorial view incl. drafts and every audience (needs `intranet.content:write`). */
  drafts: z.enum(["true", "false"]).optional(),
  /** Editorial only. Omitted = drafts, published pages and the archive together. */
  status: z.enum(["draft", "published", "archived"]).optional(),
  /** `true` = own bookmarks only (published, own audience). */
  saved: z.enum(["true", "false"]).optional(),
  /** `true` = pinned entries only; `false` = everything except pinned (feed stream). */
  pinned: z.enum(["true", "false"]).optional(),
  /** Editorial: library entries whose review deadline has passed. */
  reviewDue: z.enum(["true", "false"]).optional(),
  /** Editorial calendar, both or neither. `YYYY-MM-DD` in Europe/Berlin. */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** Active site. Omitted means the filter is cleared. */
  siteId: z.string().uuid().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(INTRANET_PAGE_SIZE)
});
var intranetContentListResponseSchema = z.object({
  items: z.array(intranetFeedItemSchema),
  /** News drafts without `publishAt`. Present only when `from` and `to` were set. */
  unscheduled: z.array(intranetFeedItemSchema).optional(),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1)
});
function isIntranetHtmlBody(body) {
  return /^\s*<[a-z]/i.test(body);
}
var INTRANET_TEASER_MAX = 500;
var INTRANET_TEASER_LINES = 4;
function excerptFromIntranetBody(body) {
  const withBreaks = (body ?? "").replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, " ").replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, " ").replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li|h[1-6]|tr)>/gi, "\n").replace(/<(p|div|li|h[1-6]|tr)(?:\s[^>]*)?>/gi, "\n").replace(/<[^>]+>/g, "").replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&").replace(/&lt;/gi, "<").replace(/&gt;/gi, ">").replace(/&quot;/gi, '"').replace(/&#39;/g, "'").replace(/\r\n?/g, "\n").replace(/[^\S\n]+/g, " ").replace(/ *\n */g, "\n").replace(/\n{2,}/g, "\n").trim();
  if (!withBreaks) {
    return { teaser: null, hasMore: false };
  }
  const lines = withBreaks.split("\n").map((line) => line.trim()).filter((line) => line.length > 0);
  let teaser = lines.slice(0, INTRANET_TEASER_LINES).join("\n");
  let hasMore = lines.length > INTRANET_TEASER_LINES;
  if (teaser.length > INTRANET_TEASER_MAX) {
    const head = teaser.slice(0, INTRANET_TEASER_MAX);
    const lastBreak = Math.max(head.lastIndexOf("\n"), head.lastIndexOf(" "));
    teaser = (lastBreak > 0 ? head.slice(0, lastBreak) : head).trimEnd();
    hasMore = true;
  }
  return { teaser, hasMore };
}
var intranetContentFields = {
  kind: intranetContentKindSchema,
  title: z.string().trim().min(3, "Bitte einen Titel mit mindestens 3 Zeichen eingeben.").max(200),
  teaser: z.string().trim().max(500).nullable(),
  body: z.string().max(5e4).nullable(),
  categoryId: z.string().uuid().nullable(),
  leadingCategoryId: z.string().uuid().nullable(),
  /** Place of a Wissensdatenbank page that has no leading category. */
  librarySortOrder: z.number().int().min(0).max(1e4).optional(),
  targetAll: z.boolean(),
  targetDepts: z.array(z.string().uuid()).max(100),
  requiresAcknowledgement: z.boolean(),
  ackDeadline: z.iso.datetime().nullable(),
  commentsLocked: z.boolean(),
  publishAt: z.iso.datetime().nullable(),
  expiresAt: z.iso.datetime().nullable(),
  notifyAudience: z.boolean(),
  reviewDueAt: z.iso.datetime().nullable(),
  reviewerId: z.string().uuid().nullable(),
  highlightSpan: intranetHighlightSpanSchema.nullable(),
  highlightUntil: z.iso.datetime().nullable(),
  /** Empty means site-wide. Omitted on update leaves the stored sites. News reject a non-empty list. */
  siteIds: z.array(z.string().uuid()).max(100).optional()
};
function intranetContentIssues(content) {
  const issues = [];
  if (!content.targetAll && content.targetDepts.length === 0) {
    issues.push({
      path: "targetDepts",
      message: "Bitte mindestens eine Abteilung w\xE4hlen oder \u201EAlle Mitarbeiter\u201C aktivieren."
    });
  }
  if (content.requiresAcknowledgement && !content.ackDeadline) {
    issues.push({
      path: "ackDeadline",
      message: "Bitte eine Frist f\xFCr die Lesebest\xE4tigung setzen."
    });
  }
  if (content.highlightSpan === "custom" && !content.highlightUntil) {
    issues.push({
      path: "highlightUntil",
      message: "Bitte einen Zeitpunkt w\xE4hlen."
    });
  }
  return issues;
}
var createIntranetContentRequestSchema = z.strictObject({
  ...intranetContentFields,
  kind: intranetContentFields.kind.default(IntranetContentKind.NEWS),
  teaser: intranetContentFields.teaser.optional().default(null),
  body: intranetContentFields.body.optional().default(null),
  categoryId: intranetContentFields.categoryId.optional().default(null),
  leadingCategoryId: intranetContentFields.leadingCategoryId.optional().default(null),
  targetAll: intranetContentFields.targetAll.default(true),
  targetDepts: intranetContentFields.targetDepts.default([]),
  requiresAcknowledgement: intranetContentFields.requiresAcknowledgement.default(false),
  ackDeadline: intranetContentFields.ackDeadline.optional().default(null),
  commentsLocked: intranetContentFields.commentsLocked.default(false),
  publishAt: intranetContentFields.publishAt.optional().default(null),
  expiresAt: intranetContentFields.expiresAt.optional().default(null),
  notifyAudience: intranetContentFields.notifyAudience.default(true),
  reviewDueAt: intranetContentFields.reviewDueAt.optional().default(null),
  reviewerId: intranetContentFields.reviewerId.optional().default(null),
  highlightSpan: intranetContentFields.highlightSpan.optional().default(null),
  highlightUntil: intranetContentFields.highlightUntil.optional().default(null)
}).superRefine((value, ctx) => {
  for (const issue of intranetContentIssues(value)) {
    ctx.addIssue({ code: "custom", path: [issue.path], message: issue.message });
  }
});
var updateIntranetContentRequestSchema = z.strictObject(intranetContentFields).partial();
var publishIntranetContentRequestSchema = z.preprocess(
  (value) => value ?? {},
  z.strictObject({
    notifyAudience: z.boolean().optional()
  })
);
var markIntranetContentReviewedRequestSchema = z.strictObject({
  reviewDueAt: z.iso.datetime()
});
var INTRANET_COMMENT_MAX = 2e3;
var intranetCommentWriteFields = {
  body: z.string().trim().max(
    INTRANET_COMMENT_MAX,
    `Der Kommentar darf h\xF6chstens ${INTRANET_COMMENT_MAX} Zeichen haben.`
  ).default(""),
  /** Top-level comment to reply to. A reply to a reply is rejected. */
  parentId: z.string().uuid().optional(),
  /** Colleague ids chosen from the @ list. Plain @-text is not parsed here. */
  mentionUserIds: z.array(z.string().uuid()).max(20).optional()
};
var createIntranetCommentRequestSchema = z.strictObject(intranetCommentWriteFields).superRefine((value, ctx) => {
  if (value.body.length === 0) {
    ctx.addIssue({
      code: "custom",
      path: ["body"],
      message: "Bitte einen Kommentar eingeben."
    });
  }
});
var createIntranetCommentWithFileRequestSchema = z.strictObject(
  intranetCommentWriteFields
);
var IntranetActivityKind = {
  LIKE: "like",
  COMMENT: "comment",
  REPLY: "reply",
  MENTION: "mention"
};
var intranetActivityKindSchema = z.enum(IntranetActivityKind);
var intranetActivityEventSchema = z.object({
  id: z.string(),
  kind: intranetActivityKindSchema,
  actorName: z.string(),
  href: z.string(),
  createdAt: z.iso.datetime()
});
var intranetActivitySchema = z.object({
  likesReceived: z.number().int().nonnegative(),
  commentsWritten: z.number().int().nonnegative(),
  commentsReceived: z.number().int().nonnegative(),
  mentions: z.number().int().nonnegative(),
  events: z.array(intranetActivityEventSchema).max(10)
});
var lockIntranetCommentsRequestSchema = z.strictObject({
  locked: z.boolean()
});
var intranetReactionStateSchema = z.object({
  liked: z.boolean(),
  likeCount: z.number().int().nonnegative(),
  /** Absent on the older like toggle. Null when the person has no reaction. */
  myReaction: intranetReactionKindSchema.nullable().optional(),
  /** Absent on the older like toggle. Kinds with a count of zero are omitted. */
  counts: z.array(intranetReactionCountSchema).optional()
});
var setIntranetReactionRequestSchema = z.strictObject({
  reaction: intranetReactionKindSchema
});
var intranetReactionListItemSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  reaction: intranetReactionKindSchema,
  departmentName: z.string().nullable(),
  profilePhotoUrl: z.string().nullable()
});
var intranetReactionListSchema = z.object({
  items: z.array(intranetReactionListItemSchema)
});
var intranetBookmarkStateSchema = z.object({
  bookmarked: z.boolean()
});
var intranetViewStateSchema = z.object({
  viewCount: z.number().int().nonnegative(),
  counted: z.boolean()
});
function intranetCoverPath(slug, version) {
  return `/intranet/contents/${encodeURIComponent(slug)}/cover?v=${encodeURIComponent(String(version))}`;
}
function intranetKnowledgeCategoryBannerPath(id, version) {
  return `/intranet/knowledge-categories/${id}/banner?v=${encodeURIComponent(String(version))}`;
}
function intranetCategoryImagePath(id, kind, version) {
  return `/intranet/categories/${id}/${kind}?v=${encodeURIComponent(String(version))}`;
}
var INTRANET_APPEARANCE_TITLE_MAX = 60;
var intranetImageMaxBytesSchema = z.number().int("Bitte eine ganze Zahl eingeben.").min(INTRANET_IMAGE_MIN_BYTES, "Bitte mindestens 512 KB als Grenze eingeben.").max(INTRANET_VIDEO_MAX_BYTES, "Die Grenze darf h\xF6chstens knapp unter 12 MB liegen.");
var intranetContentImageMaxBytesSchema = z.number().int("Bitte eine ganze Zahl eingeben.").min(INTRANET_IMAGE_MIN_BYTES, "Bitte mindestens 512 KB als Grenze eingeben.").max(INTRANET_FILE_MAX_BYTES, "Die Grenze f\xFCr Beitragsbilder darf h\xF6chstens 10 MB betragen.");
var INTRANET_COMMENT_GIF_MAX_BYTES = INTRANET_FILE_MAX_BYTES;
var intranetCommentGifMaxBytesSchema = z.number().int("Bitte eine ganze Zahl eingeben.").min(INTRANET_IMAGE_MIN_BYTES, "Bitte mindestens 512 KB als Grenze eingeben.").max(INTRANET_FILE_MAX_BYTES, "Die Grenze f\xFCr Kommentar-GIFs darf h\xF6chstens 10 MB betragen.");
var INTRANET_KNOWLEDGE_FILE_MAX_BYTES = 250 * 1024 * 1024;
var intranetKnowledgeFileMaxBytesSchema = z.number().int("Bitte eine ganze Zahl eingeben.").min(INTRANET_IMAGE_MIN_BYTES, "Bitte mindestens 512 KB als Grenze eingeben.").max(
  INTRANET_KNOWLEDGE_FILE_MAX_BYTES,
  "Die Grenze f\xFCr Dateien in der Wissensdatenbank darf h\xF6chstens 250 MB betragen."
);
var intranetAppearanceSchema = z.object({
  title: z.string(),
  /** API path (prefix with the API base URL); null = navy fallback. */
  backgroundUrl: z.string().nullable(),
  backgroundAlt: z.string().nullable(),
  /** Null when no background file is stored. Pending stays navy on the public hero. */
  backgroundScanStatus: attachmentScanStatusSchema.nullable(),
  /** Cap for cover, hero background, module logo and site-diary photos (JPEG/PNG/WebP). */
  imageMaxBytes: intranetImageMaxBytesSchema,
  /** Cap for JPEG/PNG/WebP on content file uploads and chat images. */
  contentImageMaxBytes: intranetContentImageMaxBytesSchema,
  /** Cap for GIF on news comments. */
  commentGifMaxBytes: intranetCommentGifMaxBytesSchema,
  /** Cap for documents on a Wissensdatenbank page. */
  knowledgeFileMaxBytes: intranetKnowledgeFileMaxBytesSchema,
  updatedAt: z.iso.datetime()
});
var updateIntranetAppearanceRequestSchema = z.strictObject({
  title: z.string().trim().min(2, "Bitte einen Titel mit mindestens 2 Zeichen eingeben.").max(INTRANET_APPEARANCE_TITLE_MAX).optional(),
  backgroundAlt: z.string().trim().min(3).max(INTRANET_IMAGE_ALT_MAX).optional(),
  imageMaxBytes: intranetImageMaxBytesSchema.optional(),
  contentImageMaxBytes: intranetContentImageMaxBytesSchema.optional(),
  commentGifMaxBytes: intranetCommentGifMaxBytesSchema.optional(),
  knowledgeFileMaxBytes: intranetKnowledgeFileMaxBytesSchema.optional()
});
function intranetAppearanceBackgroundPath(version) {
  return `/intranet/appearance/background?v=${encodeURIComponent(String(version))}`;
}
var IntranetWidgetKind = {
  PENDING_ACK: "pending-ack",
  BIRTHDAYS: "birthdays",
  MODULES: "modules"
};
var intranetWidgetKindSchema = z.enum(IntranetWidgetKind);
var intranetWidgetSchema = z.object({
  id: z.string().uuid(),
  kind: intranetWidgetKindSchema,
  enabled: z.boolean(),
  sortOrder: z.number().int()
});
var intranetWidgetListResponseSchema = z.object({
  items: z.array(intranetWidgetSchema)
});
var updateIntranetWidgetRequestSchema = z.strictObject({
  enabled: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(1e4).optional()
}).refine((value) => value.enabled !== void 0 || value.sortOrder !== void 0, {
  message: "Bitte mindestens ein Feld \xE4ndern."
});
var intranetAcknowledgementSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  acknowledgedAt: z.iso.datetime()
});
var intranetAcknowledgementPendingItemSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string()
});
var intranetAcknowledgementReportSchema = z.object({
  contentId: z.string().uuid(),
  title: z.string(),
  slug: z.string(),
  ackDeadline: z.iso.datetime().nullable(),
  audienceSize: z.number().int().nonnegative(),
  acknowledgedCount: z.number().int().nonnegative(),
  /** 0–100, rounded. 0 when nobody is in the audience. */
  completionRate: z.number().int().min(0).max(100),
  items: z.array(intranetAcknowledgementSchema),
  pending: z.array(intranetAcknowledgementPendingItemSchema),
  /** Confirmations from people who are no longer in the audience. They do not count toward the rate. */
  leavers: z.array(intranetAcknowledgementSchema)
});
var intranetPendingAcknowledgementsResponseSchema = z.object({
  items: z.array(intranetContentSummarySchema)
});
var intranetOwnAcknowledgementSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  slug: z.string(),
  kind: intranetContentKindSchema,
  acknowledgedAt: z.iso.datetime()
});
var intranetOwnAcknowledgementsResponseSchema = z.object({
  items: z.array(intranetOwnAcknowledgementSchema)
});
function intranetNewsPath(slug) {
  return `/intranet/nachrichten/${encodeURIComponent(slug)}`;
}
function intranetAcknowledgementsPath(slug) {
  return `/intranet/redaktion/${encodeURIComponent(slug)}/bestaetigungen`;
}
function intranetLibraryPath(slug) {
  return `/intranet/wissensdatenbank/${encodeURIComponent(slug)}`;
}
function intranetKnowledgeCategoryPath(slug) {
  return `/intranet/wissensdatenbank/kategorie/${encodeURIComponent(slug)}`;
}
function intranetContentPath(content) {
  return isIntranetLibraryKind(content.kind) ? intranetLibraryPath(content.slug) : intranetNewsPath(content.slug);
}
function intranetColleaguePath() {
  return "/intranet/kollegen";
}
var INTRANET_SEARCH_MIN_QUERY = 2;
var INTRANET_SEARCH_MAX_RESULTS = 50;
var INTRANET_OCCASION_WINDOW_DAYS = 7;
var IntranetSearchHitType = {
  CONTENT: "content",
  COLLEAGUE: "colleague"
};
var intranetSearchHitTypeSchema = z.enum(IntranetSearchHitType);
var intranetSearchQuerySchema = z.object({
  q: z.string().max(200).optional().default(""),
  kind: intranetContentKindSchema.optional(),
  categoryId: z.string().uuid().optional()
});
var intranetSearchHitSchema = z.object({
  type: intranetSearchHitTypeSchema,
  id: z.string().uuid(),
  title: z.string(),
  kind: intranetContentKindSchema.optional(),
  slug: z.string().optional(),
  href: z.string()
});
var intranetSearchResponseSchema = z.object({
  items: z.array(intranetSearchHitSchema)
});
var intranetKnowledgeSearchQuerySchema = z.object({
  q: z.string().max(200).optional().default(""),
  /** Top category or subcategory. Omitted, the search stays unscoped. */
  categoryId: z.string().uuid().optional(),
  /** Active site. Omitted means the filter is cleared. */
  siteId: z.string().uuid().optional(),
  kind: intranetKnowledgeLibraryKindSchema.optional(),
  manufacturerId: z.string().uuid().optional()
});
var intranetKnowledgeSearchHitSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  teaser: z.string().nullable(),
  kind: z.enum([IntranetContentKind.GUIDE, IntranetContentKind.DOWNLOAD, IntranetContentKind.FORM]),
  slug: z.string(),
  href: z.string(),
  leadingCategory: intranetKnowledgeCategorySchema.pick({ id: true, name: true, slug: true }).nullable()
});
var intranetKnowledgeSearchResponseSchema = z.object({
  items: z.array(intranetKnowledgeSearchHitSchema)
});
var intranetColleagueQuerySchema = z.object({
  q: z.string().max(200).optional(),
  departmentId: z.string().uuid().optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).optional()
});
var intranetColleagueSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  jobTitle: z.string().nullable(),
  departmentName: z.string().nullable(),
  siteName: z.string().nullable(),
  phone: z.string().nullable(),
  profilePhotoUrl: z.string().nullable(),
  /** True when the signed-in person and this colleague both have Entra — open Teams, not the portal chat. */
  reachesViaTeams: z.boolean(),
  /** True when this person has an Entra object id (Teams-reachable). Raw oid stays server-side. */
  hasMicrosoftAccount: z.boolean()
});
var intranetColleagueListResponseSchema = z.object({
  items: z.array(intranetColleagueSchema),
  departments: z.array(intranetDepartmentOptionSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1)
});
var INTRANET_ABSENCE_NOTE_MAX = 280;
var INTRANET_ABSENCE_NOTE_END_BEFORE_START = "Das Ende liegt vor dem Start.";
var INTRANET_ABSENCE_NOTE_TOO_LONG = `Die Abwesenheitsnotiz darf h\xF6chstens ${INTRANET_ABSENCE_NOTE_MAX} Zeichen haben.`;
var INTRANET_ABSENCE_NOTE_TIME_REQUIRED = "Bitte Start- und Endzeit angeben.";
var intranetAbsenceDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var intranetAbsenceTimeSchema = z.string().regex(/^\d{2}:\d{2}$/);
var intranetAbsenceNoteProfileSchema = z.object({
  label: z.string(),
  body: z.string().nullable()
});
var intranetAbsenceNoteViewSchema = intranetAbsenceNoteProfileSchema.extend({
  userId: z.string().uuid(),
  displayName: z.string()
});
var intranetAbsenceNoteOwnSchema = z.object({
  startDate: intranetAbsenceDateSchema,
  endDate: intranetAbsenceDateSchema,
  hasTime: z.boolean(),
  startTime: intranetAbsenceTimeSchema.nullable(),
  endTime: intranetAbsenceTimeSchema.nullable(),
  body: z.string().nullable()
});
var intranetAbsenceNoteResponseSchema = z.object({
  note: intranetAbsenceNoteOwnSchema.nullable()
});
var saveIntranetAbsenceNoteRequestSchema = z.object({
  startDate: intranetAbsenceDateSchema,
  endDate: intranetAbsenceDateSchema,
  hasTime: z.boolean(),
  startTime: intranetAbsenceTimeSchema.nullable().optional(),
  endTime: intranetAbsenceTimeSchema.nullable().optional(),
  body: z.string().max(2e3).nullable().optional()
});
var intranetColleagueProfileSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  jobTitle: z.string().nullable(),
  departmentName: z.string().nullable(),
  siteName: z.string().nullable(),
  aboutMe: z.string().nullable(),
  email: z.string().min(1),
  phone: z.string().nullable(),
  managerName: z.string().nullable(),
  birthdayMonth: z.number().int().min(1).max(12).nullable(),
  birthdayDay: z.number().int().min(1).max(31).nullable(),
  profilePhotoUrl: z.string().nullable(),
  /** True when the signed-in person and this colleague both have Entra — open Teams, not the portal chat. */
  reachesViaTeams: z.boolean(),
  /** True when this person has an Entra object id (Teams-reachable). Raw oid stays server-side. */
  hasMicrosoftAccount: z.boolean(),
  /** Current absence note, or null. Missing on older payloads means none. */
  absenceNote: intranetAbsenceNoteProfileSchema.nullable().optional().default(null)
});
var INTRANET_CHAT_MESSAGE_MAX = 2e3;
var INTRANET_CHAT_FORWARD_MAX = 10;
var INTRANET_CHAT_LATITUDE_E7_MIN = -9e8;
var INTRANET_CHAT_LATITUDE_E7_MAX = 9e8;
var INTRANET_CHAT_LONGITUDE_E7_MIN = -18e8;
var INTRANET_CHAT_LONGITUDE_E7_MAX = 18e8;
function intranetChatLocationE7(degrees) {
  return Math.round(degrees * 1e7);
}
function intranetChatDegrees(e7) {
  return e7 / 1e7;
}
function intranetChatDegreeText(e7) {
  return (e7 / 1e7).toFixed(6).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
}
function intranetChatMapUrl(latitudeE7, longitudeE7) {
  const latitude = intranetChatDegreeText(latitudeE7);
  const longitude = intranetChatDegreeText(longitudeE7);
  return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
}
function intranetChatLocationPair(latitudeE7, longitudeE7) {
  if (latitudeE7 === void 0 && longitudeE7 === void 0) {
    return null;
  }
  if (latitudeE7 === void 0 || longitudeE7 === void 0 || !Number.isInteger(latitudeE7) || !Number.isInteger(longitudeE7) || latitudeE7 < INTRANET_CHAT_LATITUDE_E7_MIN || latitudeE7 > INTRANET_CHAT_LATITUDE_E7_MAX || longitudeE7 < INTRANET_CHAT_LONGITUDE_E7_MIN || longitudeE7 > INTRANET_CHAT_LONGITUDE_E7_MAX) {
    return "invalid";
  }
  return { latitudeE7, longitudeE7 };
}
var INTRANET_CHAT_PREVIEW_MAX = 80;
var INTRANET_CHAT_PAGE_SIZE = 50;
var INTRANET_GROUP_CHAT_MIN_MEMBERS = 3;
var INTRANET_GROUP_CHAT_NAME_MAX = 80;
var INTRANET_GROUP_CHAT_ALL_TEAMS_MESSAGE = "Alle ausgew\xE4hlten Teilnehmer haben eine Teams-Lizenz. Bitte nutze f\xFCr diesen Gruppenchat Microsoft Teams.";
var intranetConversationKindSchema = z.enum(["DIRECT", "GROUP"]);
function validateIntranetGroupChatLicenses(memberHasMicrosoftAccount) {
  if (memberHasMicrosoftAccount.length < INTRANET_GROUP_CHAT_MIN_MEMBERS) {
    return { ok: false, code: "TOO_FEW" };
  }
  if (memberHasMicrosoftAccount.every(Boolean)) {
    return { ok: false, code: "ALL_TEAMS" };
  }
  return { ok: true };
}
var openIntranetChatRequestSchema = z.strictObject({
  userId: z.string().uuid()
});
var openIntranetChatResponseSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("teams"),
    teamsUrl: z.string().url()
  }),
  z.object({
    mode: z.literal("portal"),
    conversationId: z.string().uuid()
  })
]);
var createIntranetGroupChatRequestSchema = z.strictObject({
  name: z.string().trim().min(1, "Bitte einen Gruppennamen eingeben.").max(
    INTRANET_GROUP_CHAT_NAME_MAX,
    `Der Gruppenname darf h\xF6chstens ${INTRANET_GROUP_CHAT_NAME_MAX} Zeichen haben.`
  ),
  memberIds: z.array(z.string().uuid()).min(
    INTRANET_GROUP_CHAT_MIN_MEMBERS - 1,
    `Bitte mindestens ${INTRANET_GROUP_CHAT_MIN_MEMBERS - 1} weitere Personen ausw\xE4hlen.`
  ).max(49)
});
var createIntranetGroupChatResponseSchema = z.object({
  conversationId: z.string().uuid()
});
var locationOutside = "Der Standort liegt au\xDFerhalb der Karte.";
var intranetChatLatitudeE7Schema = z.number().int().min(INTRANET_CHAT_LATITUDE_E7_MIN, locationOutside).max(INTRANET_CHAT_LATITUDE_E7_MAX, locationOutside);
var intranetChatLongitudeE7Schema = z.number().int().min(INTRANET_CHAT_LONGITUDE_E7_MIN, locationOutside).max(INTRANET_CHAT_LONGITUDE_E7_MAX, locationOutside);
var createIntranetChatMessageRequestSchema = z.strictObject({
  body: z.string().trim().max(
    INTRANET_CHAT_MESSAGE_MAX,
    `Die Nachricht darf h\xF6chstens ${INTRANET_CHAT_MESSAGE_MAX} Zeichen haben.`
  ).default(""),
  replyToMessageId: z.string().uuid().optional(),
  latitudeE7: intranetChatLatitudeE7Schema.optional(),
  longitudeE7: intranetChatLongitudeE7Schema.optional()
}).superRefine((value, ctx) => {
  const pair = intranetChatLocationPair(value.latitudeE7, value.longitudeE7);
  if (pair === "invalid") {
    ctx.addIssue({
      code: "custom",
      message: "Der Standort liegt au\xDFerhalb der Karte.",
      path: ["latitudeE7"]
    });
  }
});
var intranetChatAttachmentKindSchema = z.enum(["image", "video", "file", "gif", "voice"]);
var intranetChatAttachmentSchema = z.object({
  id: z.string().uuid(),
  kind: intranetChatAttachmentKindSchema,
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  scanStatus: attachmentScanStatusSchema
});
function intranetChatAttachmentPath(attachmentId) {
  return `/intranet/chat/attachments/${encodeURIComponent(attachmentId)}`;
}
var intranetChatMessagesQuerySchema = z.object({
  /** Oldest message id of the current page — load the 50 messages before it. */
  cursor: z.string().uuid().optional()
});
var intranetChatPersonSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  /** Servable profile photo, or null when the person has none. */
  profilePhotoUrl: z.string().nullable().optional()
});
var intranetChatReplySchema = z.object({
  id: z.string().uuid(),
  authorName: z.string(),
  excerpt: z.string()
});
var intranetChatForwardedFromSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string()
});
var intranetChatMessageSchema = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  author: intranetChatPersonSchema,
  body: z.string(),
  attachment: intranetChatAttachmentSchema.nullable(),
  replyTo: intranetChatReplySchema.nullable(),
  forwardedFrom: intranetChatForwardedFromSchema.nullable(),
  latitudeE7: z.number().int().nullable(),
  longitudeE7: z.number().int().nullable(),
  createdAt: z.iso.datetime()
});
var intranetChatMessagesResponseSchema = z.object({
  items: z.array(intranetChatMessageSchema),
  /** Pass as `cursor` to load older messages. Null when there is no older page. */
  nextCursor: z.string().uuid().nullable()
});
var intranetChatConversationSchema = z.object({
  id: z.string().uuid(),
  kind: intranetConversationKindSchema,
  /** Peer display name for DIRECT, group name for GROUP. */
  title: z.string(),
  /** The other person in a DIRECT chat; null for GROUP. */
  other: intranetChatPersonSchema.nullable(),
  /** Member count for GROUP (including the viewer); null for DIRECT. */
  memberCount: z.number().int().positive().nullable(),
  /** First members of a GROUP chat, for stacked portraits. Empty on DIRECT. */
  memberPreview: z.array(intranetChatPersonSchema).max(4).optional(),
  lastMessagePreview: z.string().nullable(),
  lastMessageAt: z.iso.datetime().nullable(),
  unreadCount: z.number().int().nonnegative(),
  /** Personal pin. Absent on older payloads means not pinned. */
  pinned: z.boolean().optional().default(false),
  /** Personal archive. Absent on older payloads means not archived. */
  archived: z.boolean().optional().default(false),
  /** Personal mute. Absent on older payloads means not muted. */
  muted: z.boolean().optional().default(false),
  /** Personal mark-unread. Absent on older payloads means not marked. */
  markedUnread: z.boolean().optional().default(false),
  /** Current notes of the other people. Absent on older payloads means none. */
  absenceNotes: z.array(intranetAbsenceNoteViewSchema).optional().default([]),
  updatedAt: z.iso.datetime()
});
var intranetChatConversationListResponseSchema = z.object({
  items: z.array(intranetChatConversationSchema)
});
var INTRANET_CHAT_SEARCH_MAX = 20;
var INTRANET_CHAT_SEARCH_QUERY_MAX = 200;
var INTRANET_CHAT_PREFERENCE_EMPTY_MESSAGE = "Bitte einen Zustand angeben.";
var INTRANET_CHAT_SEARCH_TOO_SHORT_MESSAGE = "Bitte mindestens 2 Zeichen eingeben.";
var INTRANET_CHAT_SEARCH_TOO_LONG_MESSAGE = "Die Suche darf h\xF6chstens 200 Zeichen haben.";
var intranetChatPreferenceRequestSchema = z.strictObject({
  pinned: z.boolean().optional(),
  archived: z.boolean().optional(),
  muted: z.boolean().optional(),
  markedUnread: z.boolean().optional()
}).refine(
  (value) => value.pinned !== void 0 || value.archived !== void 0 || value.muted !== void 0 || value.markedUnread !== void 0,
  { message: INTRANET_CHAT_PREFERENCE_EMPTY_MESSAGE }
);
var intranetChatPreferenceResponseSchema = z.object({
  pinned: z.boolean(),
  archived: z.boolean(),
  muted: z.boolean(),
  markedUnread: z.boolean()
});
var intranetChatSearchQuerySchema = z.object({
  q: z.string().trim().min(2, INTRANET_CHAT_SEARCH_TOO_SHORT_MESSAGE).max(INTRANET_CHAT_SEARCH_QUERY_MAX, INTRANET_CHAT_SEARCH_TOO_LONG_MESSAGE)
});
var intranetChatSearchHitSchema = z.object({
  conversationId: z.string().uuid(),
  conversationTitle: z.string(),
  messageId: z.string().uuid(),
  excerpt: z.string(),
  createdAt: z.iso.datetime()
});
var intranetChatSearchResponseSchema = z.object({
  items: z.array(intranetChatSearchHitSchema).max(INTRANET_CHAT_SEARCH_MAX)
});
var forwardIntranetChatRequestSchema = z.strictObject({
  messageId: z.string().uuid(),
  targetConversationIds: z.array(z.string().uuid()),
  comment: z.string().max(
    INTRANET_CHAT_MESSAGE_MAX,
    `Die Nachricht darf h\xF6chstens ${INTRANET_CHAT_MESSAGE_MAX} Zeichen haben.`
  ).optional()
});
var forwardIntranetChatTargetSchema = z.object({
  conversationId: z.string().uuid(),
  messageId: z.string().uuid(),
  commentMessageId: z.string().uuid().nullable()
});
var forwardIntranetChatResponseSchema = z.object({
  targets: z.array(forwardIntranetChatTargetSchema)
});
var intranetChatPhotoSchema = z.object({
  messageId: z.string().uuid(),
  attachment: intranetChatAttachmentSchema,
  createdAt: z.iso.datetime()
});
var intranetChatPhotosResponseSchema = z.object({
  items: z.array(intranetChatPhotoSchema),
  nextCursor: z.string().uuid().nullable()
});
var httpUrl = z.string().trim().url("Bitte eine g\xFCltige URL eingeben.").refine((value) => /^https?:\/\//i.test(value), "Bitte eine http- oder https-Adresse eingeben.");
var intranetContactSchema = z.object({
  id: z.string().uuid(),
  functionTitle: z.string(),
  user: z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    jobTitle: z.string().nullable(),
    phone: z.string().nullable(),
    /** Servable profile photo, or null when the person has none. */
    profilePhotoUrl: z.string().nullable().optional()
  }).nullable(),
  department: intranetDepartmentOptionSchema.nullable(),
  externalUrl: z.string().nullable(),
  sortOrder: z.number().int()
});
var intranetContactListResponseSchema = z.object({
  items: z.array(intranetContactSchema)
});
var createIntranetContactRequestSchema = z.strictObject({
  functionTitle: z.string().trim().min(2, "Bitte eine Funktion mit mindestens 2 Zeichen eingeben.").max(120),
  userId: z.string().uuid().nullable().optional().default(null),
  departmentId: z.string().uuid().nullable().optional().default(null),
  externalUrl: z.union([httpUrl, z.literal(""), z.null()]).optional().default(null),
  sortOrder: z.number().int().min(0).max(1e4).optional().default(0)
});
var updateIntranetContactRequestSchema = z.strictObject({
  functionTitle: createIntranetContactRequestSchema.shape.functionTitle.optional(),
  userId: z.string().uuid().nullable().optional(),
  departmentId: z.string().uuid().nullable().optional(),
  externalUrl: z.union([httpUrl, z.literal(""), z.null()]).optional(),
  sortOrder: z.number().int().min(0).max(1e4).optional()
});
var INTRANET_SHORTCUT_ICONS = [
  "Newspaper",
  "BookOpen",
  "Search",
  "Users",
  "Phone",
  "ExternalLink",
  "Calendar",
  "Clock",
  "FileText",
  "Link",
  "Building2",
  "Cake",
  "ClipboardList",
  "HardHat"
];
var intranetShortcutIconSchema = z.enum(INTRANET_SHORTCUT_ICONS);
function isIntranetShortcutHref(value) {
  const href = value.trim();
  if (/^[a-z][a-z0-9+.-]*:/i.test(href) && !/^https?:/i.test(href)) {
    return false;
  }
  if (href.startsWith("/") && !href.startsWith("//") && !href.includes("\\")) {
    return true;
  }
  try {
    const url = new URL(href);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}
var intranetShortcutHrefSchema = z.string().trim().min(1, "Bitte einen Pfad oder eine Adresse eingeben.").max(500).refine(isIntranetShortcutHref, "Bitte einen internen Pfad oder eine http(s)-Adresse eingeben.");
var IntranetLinkTone = {
  NAVY: "navy",
  RED: "red",
  GREEN: "green",
  AMBER: "amber"
};
var intranetLinkToneSchema = z.enum(IntranetLinkTone);
var intranetLinkSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  url: z.string(),
  iconName: z.string().nullable(),
  tone: intranetLinkToneSchema,
  enabled: z.boolean(),
  sortOrder: z.number().int(),
  audienceRoleIds: z.array(z.string().uuid())
});
var intranetLinkListResponseSchema = z.object({
  items: z.array(intranetLinkSchema)
});
var intranetLinkAudienceRoleIdsSchema = z.array(z.string().uuid()).max(200);
function rejectDuplicateLinkAudience(audienceRoleIds, ctx) {
  if (audienceRoleIds !== void 0 && new Set(audienceRoleIds).size !== audienceRoleIds.length) {
    ctx.addIssue({
      code: "custom",
      path: ["audienceRoleIds"],
      message: "Jede Berechtigungsgruppe darf nur einmal vorkommen."
    });
  }
}
var createIntranetLinkRequestObject = z.strictObject({
  label: z.string().trim().min(2, "Bitte eine Bezeichnung mit mindestens 2 Zeichen eingeben.").max(80),
  url: intranetShortcutHrefSchema,
  iconName: z.union([lucideIconName, z.literal(""), z.null()]).optional().default(null),
  tone: intranetLinkToneSchema.optional().default(IntranetLinkTone.NAVY),
  enabled: z.boolean().optional().default(true),
  sortOrder: z.number().int().min(0).max(1e4).optional().default(0),
  audienceRoleIds: intranetLinkAudienceRoleIdsSchema.optional().default([])
});
var createIntranetLinkRequestSchema = createIntranetLinkRequestObject.superRefine(
  (value, ctx) => rejectDuplicateLinkAudience(value.audienceRoleIds, ctx)
);
var updateIntranetLinkRequestSchema = z.strictObject({
  label: createIntranetLinkRequestObject.shape.label.optional(),
  url: intranetShortcutHrefSchema.optional(),
  iconName: z.union([lucideIconName, z.literal(""), z.null()]).optional(),
  tone: intranetLinkToneSchema.optional(),
  enabled: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(1e4).optional(),
  audienceRoleIds: intranetLinkAudienceRoleIdsSchema.optional()
}).superRefine((value, ctx) => rejectDuplicateLinkAudience(value.audienceRoleIds, ctx));
var IntranetOccasionKind = {
  BIRTHDAY: "birthday",
  ANNIVERSARY: "anniversary"
};
var intranetOccasionKindSchema = z.enum(IntranetOccasionKind);
var intranetOccasionSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  departmentName: z.string().nullable(),
  kind: intranetOccasionKindSchema,
  month: z.number().int().min(1).max(12),
  day: z.number().int().min(1).max(31),
  daysUntil: z.number().int().min(0).max(INTRANET_OCCASION_WINDOW_DAYS),
  years: z.number().int().min(1).optional()
});
var intranetBirthdayListResponseSchema = z.object({
  items: z.array(intranetOccasionSchema)
});
var IntranetModuleKind = {
  SITE_DIARY: "site_diary"
};
var intranetModuleKindSchema = z.enum(IntranetModuleKind);
var INTRANET_MODULE_KIND_LABELS = {
  site_diary: "Bautagesbericht"
};
var INTRANET_MODULE_KINDS = new Set(Object.values(IntranetModuleKind));
function isIntranetModuleKind(kind) {
  return INTRANET_MODULE_KINDS.has(kind);
}
var SiteDiaryLogoMode = {
  PORTAL: "portal",
  CUSTOM: "custom"
};
var siteDiaryLogoModeSchema = z.enum(SiteDiaryLogoMode);
var SiteDiaryDelivery = {
  NOTIFICATION: "notification",
  EMAIL: "email",
  BOTH: "both"
};
var siteDiaryDeliverySchema = z.enum(SiteDiaryDelivery);
var SITE_DIARY_DELIVERY_LABELS = {
  notification: "Benachrichtigung",
  email: "E-Mail",
  both: "Beides"
};
var SITE_DIARY_SAVED_PREFIX = "Der Bericht ist gespeichert. ";
var SITE_DIARY_MAIL_OFF_MESSAGE = `${SITE_DIARY_SAVED_PREFIX}E-Mail-Versand ist aus.`;
var SITE_DIARY_MAIL_PREF_OFF_MESSAGE = `${SITE_DIARY_SAVED_PREFIX}Die Empf\xE4nger haben E-Mail ausgeschaltet.`;
var SITE_DIARY_MAIL_FAILED_MESSAGE = `${SITE_DIARY_SAVED_PREFIX}Die E-Mail konnte nicht gesendet werden.`;
var SITE_DIARY_NO_RECIPIENT_MESSAGE = `${SITE_DIARY_SAVED_PREFIX}Es ist kein Empf\xE4nger eingetragen.`;
var SITE_DIARY_NOTIFY_PREF_OFF_MESSAGE = `${SITE_DIARY_SAVED_PREFIX}Die Empf\xE4nger haben die Benachrichtigung ausgeschaltet.`;
var SITE_DIARY_NOTIFY_FAILED_MESSAGE = `${SITE_DIARY_SAVED_PREFIX}Die Benachrichtigung konnte nicht gesendet werden.`;
var SiteDiaryJobTitle = {
  AZUBI: "azubi",
  HELFER: "helfer",
  JUNGGESSELLE: "junggeselle",
  MONTEUR: "monteur",
  OBERMONTEUR: "obermonteur",
  MEISTER: "meister"
};
var siteDiaryJobTitleSchema = z.enum(SiteDiaryJobTitle);
var SITE_DIARY_JOB_TITLE_LABELS = {
  azubi: "Azubi",
  helfer: "Helfer",
  junggeselle: "Junggeselle",
  monteur: "Monteur",
  obermonteur: "Obermonteur",
  meister: "Meister"
};
var SITE_DIARY_JOB_TITLE_HINT = "Bitte die korrekten Stellenbezeichnungen angeben: Azubi, Helfer, Junggeselle, Monteur, Obermonteur, Meister.";
var SITE_DIARY_COLLEAGUES_HINT = "z. B. Gewerk mit Kostenstelle, darunter Name mit Stellenbezeichnung, Uhrzeit und Stunden.";
var COLLEAGUES_REQUIRED_MESSAGE = "Bitte die beteiligten Kollegen und Arbeitszeiten angeben.";
var COLLEAGUES_MAX_MESSAGE = "Die Kollegenangaben d\xFCrfen h\xF6chstens 8.000 Zeichen lang sein.";
var COLLEAGUES_MAX_LENGTH = 8e3;
var SiteDiaryActivity = {
  MONTAGE: "montage",
  WARTUNG: "wartung",
  STOERUNG: "stoerung",
  PLANUNG: "planung",
  REPARATUR: "reparatur",
  SONSTIGES: "sonstiges"
};
var siteDiaryActivitySchema = z.enum(SiteDiaryActivity);
var SITE_DIARY_ACTIVITY_LABELS = {
  montage: "Montage",
  wartung: "Wartung",
  stoerung: "St\xF6rung",
  planung: "Planung",
  reparatur: "Reparatur",
  sonstiges: "Sonstiges"
};
var SiteDiaryWeather = {
  SONNE: "sonne",
  BEDECKT: "bedeckt",
  REGEN: "regen",
  NEBEL: "nebel",
  SCHNEE: "schnee",
  FROST: "frost",
  WINDIG: "windig",
  STURM: "sturm"
};
var siteDiaryWeatherSchema = z.enum(SiteDiaryWeather);
var SITE_DIARY_WEATHER_LABELS = {
  sonne: "Sonne",
  bedeckt: "Bedeckt",
  regen: "Regen",
  nebel: "Nebel",
  schnee: "Schnee",
  frost: "Frost",
  windig: "Windig",
  sturm: "Sturm"
};
var SITE_DIARY_INCIDENT_HINT = "z. B. Lieferung von Gro\xDFkomponenten, Stromausfall";
var SITE_DIARY_PHOTO_HINT = "Mehrere Fotos werden im PDF zu einem Bild zusammengef\xFCgt.";
var SITE_DIARY_TEMPERATURE_HINT = "z. B. 6\xB0C bis 21\xB0C";
var SITE_DIARY_MAX_PHOTOS = 12;
var SITE_DIARY_DEFAULT_LABEL = "Bautagesbericht";
var TEMPERATURE_REQUIRED_MESSAGE = "Bitte die Temperatur angeben.";
var TEMPERATURE_MAX_MESSAGE = "Die Temperaturangabe darf h\xF6chstens 80 Zeichen lang sein.";
var TEMPERATURE_MAX_LENGTH = 80;
var RECIPIENT_MESSAGE = "Bitte mindestens eine Empf\xE4ngergruppe oder eine Person w\xE4hlen.";
function temperatureTextFromLegacy(value) {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${value}\xB0C`;
  }
  if (value == null) {
    return "";
  }
  return "";
}
function temperatureReportTextSchema() {
  return z.preprocess(
    (value) => temperatureTextFromLegacy(value),
    z.string().trim().min(1, TEMPERATURE_REQUIRED_MESSAGE).max(TEMPERATURE_MAX_LENGTH, TEMPERATURE_MAX_MESSAGE)
  );
}
function temperatureDraftTextSchema() {
  return z.preprocess(
    (value) => temperatureTextFromLegacy(value),
    z.string().max(TEMPERATURE_MAX_LENGTH, TEMPERATURE_MAX_MESSAGE).default("")
  );
}
function hasDuplicate(values) {
  return new Set(values).size !== values.length;
}
function singleChoiceFromLegacyArray(schema, requiredMessage) {
  return z.preprocess(
    (value) => {
      const raw = Array.isArray(value) ? value[0] : value;
      return raw === "" || raw == null ? void 0 : raw;
    },
    schema.optional().refine((entry) => entry != null, {
      error: requiredMessage
    }).transform((entry) => entry)
  );
}
function optionalChoiceFromLegacyArray(schema) {
  return z.preprocess(
    (value) => {
      if (Array.isArray(value)) {
        return value[0] ?? "";
      }
      if (value == null) {
        return "";
      }
      return value;
    },
    schema.or(z.literal("")).default("")
  );
}
var logoStorageKeySchema = z.union([z.string().trim().min(1), z.literal(""), z.null()]).transform((value) => value === "" ? null : value);
var siteDiarySettingsSchema = z.strictObject({
  numberPrefix: z.string().trim().regex(/^[A-Z]{2,6}$/, "Das Prefix besteht aus 2 bis 6 Gro\xDFbuchstaben.").default("BT"),
  logoMode: siteDiaryLogoModeSchema.default(SiteDiaryLogoMode.PORTAL),
  logoStorageKey: logoStorageKeySchema.optional().default(null),
  delivery: siteDiaryDeliverySchema.default(SiteDiaryDelivery.EMAIL),
  audienceRoleIds: z.array(z.string().uuid()).max(200).default([]),
  recipientRoleIds: z.array(z.string().uuid()).max(200).default([]),
  recipientUserIds: z.array(z.string().uuid()).max(200).default([])
}).superRefine((value, ctx) => {
  if (value.logoMode === SiteDiaryLogoMode.PORTAL && value.logoStorageKey !== null) {
    ctx.addIssue({
      code: "custom",
      path: ["logoStorageKey"],
      message: "Beim Portal-Logo bleibt die eigene Logo-Datei leer."
    });
  }
  if (value.logoMode === SiteDiaryLogoMode.CUSTOM && value.logoStorageKey === null) {
    ctx.addIssue({
      code: "custom",
      path: ["logoStorageKey"],
      message: "Bitte ein Logo f\xFCr diese Instanz w\xE4hlen."
    });
  }
  if (hasDuplicate(value.audienceRoleIds)) {
    ctx.addIssue({
      code: "custom",
      path: ["audienceRoleIds"],
      message: "Jede Berechtigungsgruppe darf nur einmal vorkommen."
    });
  }
  if (hasDuplicate(value.recipientRoleIds)) {
    ctx.addIssue({
      code: "custom",
      path: ["recipientRoleIds"],
      message: "Jede Berechtigungsgruppe darf nur einmal vorkommen."
    });
  }
  if (hasDuplicate(value.recipientUserIds)) {
    ctx.addIssue({
      code: "custom",
      path: ["recipientUserIds"],
      message: "Jede Person darf nur einmal vorkommen."
    });
  }
});
var siteDiarySettingsViewSchema = z.strictObject({
  numberPrefix: z.string().trim().regex(/^[A-Z]{2,6}$/, "Das Prefix besteht aus 2 bis 6 Gro\xDFbuchstaben.").default("BT"),
  logoMode: siteDiaryLogoModeSchema.default(SiteDiaryLogoMode.PORTAL),
  logoStorageKey: logoStorageKeySchema.optional().default(null),
  delivery: siteDiaryDeliverySchema.default(SiteDiaryDelivery.EMAIL),
  audienceRoleIds: z.array(z.string().uuid()).max(200).default([]),
  recipientRoleIds: z.array(z.string().uuid()).max(200).default([]),
  recipientUserIds: z.array(z.string().uuid()).max(200).default([])
});
var siteDiaryStoredSettingsSchema = z.strictObject({
  numberPrefix: z.string().trim().regex(/^[A-Z]{2,6}$/, "Das Prefix besteht aus 2 bis 6 Gro\xDFbuchstaben."),
  logoMode: siteDiaryLogoModeSchema,
  logoStorageKey: logoStorageKeySchema,
  delivery: siteDiaryDeliverySchema.default(SiteDiaryDelivery.EMAIL)
});
function toSiteDiaryStoredSettings(settings) {
  return {
    numberPrefix: settings.numberPrefix,
    logoMode: settings.logoMode,
    logoStorageKey: settings.logoMode === SiteDiaryLogoMode.CUSTOM ? settings.logoStorageKey : null,
    delivery: settings.delivery
  };
}
var siteDiaryDeliveryResultSchema = z.object({
  delivery: siteDiaryDeliverySchema,
  mailSent: z.boolean(),
  mailError: z.string().nullable(),
  notified: z.boolean(),
  notifyError: z.string().nullable(),
  recipientCount: z.number().int().nonnegative()
});
function siteDiaryDeliveryMessage(input) {
  const wantsNotify = input.delivery !== SiteDiaryDelivery.EMAIL;
  const wantsMail = input.delivery !== SiteDiaryDelivery.NOTIFICATION;
  const notifySentence = wantsNotify ? input.notifyError : null;
  const mailSentence = wantsMail ? input.mailError : null;
  if (!input.notified && !input.mailSent) {
    return [notifySentence, mailSentence].filter((sentence) => Boolean(sentence)).join(" ");
  }
  const base = input.mode === "resend" ? "Erneut gesendet." : input.runningNumber ? `Bautagesbericht ${input.runningNumber} abgeschickt.` : "Bautagesbericht abgeschickt.";
  const extras = [];
  if (wantsNotify && !input.notified && notifySentence) {
    extras.push(siteDiaryDeliveryClause(notifySentence));
  }
  if (wantsMail && !input.mailSent && mailSentence) {
    extras.push(siteDiaryDeliveryClause(mailSentence));
  }
  return [base, ...extras].join(" ");
}
function siteDiaryDeliveryClause(sentence) {
  return sentence.startsWith(SITE_DIARY_SAVED_PREFIX) ? sentence.slice(SITE_DIARY_SAVED_PREFIX.length) : sentence;
}
function colleaguesTextFromLegacy(value) {
  if (typeof value === "string") {
    return value;
  }
  if (value == null) {
    return "";
  }
  if (!Array.isArray(value)) {
    return "";
  }
  return value.map((entry) => {
    if (typeof entry === "string") {
      return entry;
    }
    if (!entry || typeof entry !== "object") {
      return "";
    }
    const row = entry;
    const freeName = typeof row.freeName === "string" ? row.freeName.trim() : "";
    const name = freeName || (row.userId ? "Portal-Person" : "");
    const jobTitle = typeof row.jobTitle === "string" && row.jobTitle in SITE_DIARY_JOB_TITLE_LABELS ? SITE_DIARY_JOB_TITLE_LABELS[row.jobTitle] : "";
    const hours = typeof row.hours === "number" && Number.isFinite(row.hours) ? `${row.hours} h` : "";
    return [name, jobTitle, hours].filter(Boolean).join(" \xB7 ");
  }).filter((line) => line.length > 0).join("\n");
}
function colleaguesReportTextSchema() {
  return z.preprocess(
    (value) => colleaguesTextFromLegacy(value),
    z.string().trim().min(1, COLLEAGUES_REQUIRED_MESSAGE).max(COLLEAGUES_MAX_LENGTH, COLLEAGUES_MAX_MESSAGE)
  );
}
function colleaguesDraftTextSchema() {
  return z.preprocess(
    (value) => colleaguesTextFromLegacy(value),
    z.string().max(COLLEAGUES_MAX_LENGTH, COLLEAGUES_MAX_MESSAGE).default("")
  );
}
var RUNNING_NUMBER_REQUIRED_MESSAGE = "Bitte eine laufende Nummer angeben.";
var RUNNING_NUMBER_MAX_MESSAGE = "Die laufende Nummer darf h\xF6chstens 80 Zeichen lang sein.";
var RUNNING_NUMBER_MAX_LENGTH = 80;
var siteDiaryReportSchema = z.strictObject({
  /** Free text entered by the installer; also stored on the report column. */
  runningNumber: z.string().trim().min(1, RUNNING_NUMBER_REQUIRED_MESSAGE).max(RUNNING_NUMBER_MAX_LENGTH, RUNNING_NUMBER_MAX_MESSAGE),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Bitte ein Datum angeben.").refine((value) => {
    const [year, month, day] = value.split("-").map(Number);
    if (year === void 0 || month === void 0 || day === void 0) {
      return false;
    }
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  }, "Bitte ein Datum angeben."),
  project: z.string().trim().min(2, "Bitte ein Bauprojekt mit mindestens 2 Zeichen angeben.").max(200, "Das Bauprojekt darf h\xF6chstens 200 Zeichen lang sein."),
  address: z.string().trim().min(1, "Bitte die Adresse der Baustelle angeben.").max(500, "Die Adresse darf h\xF6chstens 500 Zeichen lang sein."),
  /** Free text; legacy structured rows become plain lines. */
  colleagues: colleaguesReportTextSchema(),
  /** Single choice; legacy array payloads keep the first entry. */
  activities: singleChoiceFromLegacyArray(
    siteDiaryActivitySchema,
    "Bitte eine T\xE4tigkeit w\xE4hlen."
  ),
  activityOther: z.string().default(""),
  notes: z.string().trim().max(4e3, "Notizen d\xFCrfen h\xF6chstens 4.000 Zeichen lang sein.").default(""),
  sguNotes: z.string().trim().max(4e3, "SGU-Besonderheiten d\xFCrfen h\xF6chstens 4.000 Zeichen lang sein.").default(""),
  incidents: z.string().trim().max(4e3, "Besondere Vorkommnisse d\xFCrfen h\xF6chstens 4.000 Zeichen lang sein.").default(""),
  extraEffort: z.boolean(),
  extraEffortReason: z.string().default(""),
  /** Single choice; legacy array payloads keep the first entry. */
  weather: singleChoiceFromLegacyArray(siteDiaryWeatherSchema, "Bitte eine Wetterlage w\xE4hlen."),
  temperature: temperatureReportTextSchema()
}).superRefine((value, ctx) => {
  if (value.activities === SiteDiaryActivity.SONSTIGES) {
    const other = value.activityOther.trim();
    if (other.length < 2) {
      ctx.addIssue({
        code: "custom",
        path: ["activityOther"],
        message: "Bitte Sonstiges mit mindestens 2 Zeichen beschreiben."
      });
    } else if (other.length > 200) {
      ctx.addIssue({
        code: "custom",
        path: ["activityOther"],
        message: "Sonstiges darf h\xF6chstens 200 Zeichen lang sein."
      });
    }
  }
  if (value.extraEffort) {
    const reason = value.extraEffortReason.trim();
    if (reason.length < 1) {
      ctx.addIssue({
        code: "custom",
        path: ["extraEffortReason"],
        message: "Bitte die Begr\xFCndung f\xFCr den Montagemehraufwand angeben."
      });
    } else if (reason.length > 2e3) {
      ctx.addIssue({
        code: "custom",
        path: ["extraEffortReason"],
        message: "Die Begr\xFCndung darf h\xF6chstens 2.000 Zeichen lang sein."
      });
    }
  }
}).transform((value) => ({
  ...value,
  activityOther: value.activities === SiteDiaryActivity.SONSTIGES ? value.activityOther.trim() : "",
  extraEffortReason: value.extraEffort ? value.extraEffortReason.trim() : ""
}));
var siteDiaryDraftSchema = z.strictObject({
  runningNumber: z.string().trim().max(RUNNING_NUMBER_MAX_LENGTH, RUNNING_NUMBER_MAX_MESSAGE).default(""),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Bitte ein Datum angeben.").or(z.literal("")).default(""),
  project: z.string().trim().max(200, "Das Bauprojekt darf h\xF6chstens 200 Zeichen lang sein.").default(""),
  address: z.string().trim().max(500, "Die Adresse darf h\xF6chstens 500 Zeichen lang sein.").default(""),
  colleagues: colleaguesDraftTextSchema(),
  activities: optionalChoiceFromLegacyArray(siteDiaryActivitySchema),
  activityOther: z.string().max(200).default(""),
  notes: z.string().trim().max(4e3, "Notizen d\xFCrfen h\xF6chstens 4.000 Zeichen lang sein.").default(""),
  sguNotes: z.string().trim().max(4e3, "SGU-Besonderheiten d\xFCrfen h\xF6chstens 4.000 Zeichen lang sein.").default(""),
  incidents: z.string().trim().max(4e3, "Besondere Vorkommnisse d\xFCrfen h\xF6chstens 4.000 Zeichen lang sein.").default(""),
  extraEffort: z.boolean().default(false),
  extraEffortReason: z.string().max(2e3).default(""),
  weather: optionalChoiceFromLegacyArray(siteDiaryWeatherSchema),
  temperature: temperatureDraftTextSchema()
});
var IntranetModuleReportStatus = {
  DRAFT: "draft",
  SUBMITTED: "submitted"
};
var intranetModuleReportStatusSchema = z.enum(IntranetModuleReportStatus);
var putIntranetModuleDraftRequestSchema = siteDiaryDraftSchema;
var submitIntranetModuleReportRequestSchema = siteDiaryReportSchema;
var intranetModuleReportPhotoSchema = z.object({
  id: z.string().uuid(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  scanStatus: attachmentScanStatusSchema,
  sortOrder: z.number().int().nonnegative(),
  /** Same-origin path to stream the image. */
  url: z.string()
});
var reorderIntranetModulePhotosRequestSchema = z.strictObject({
  photoIds: z.array(z.string().uuid()).min(1).max(SITE_DIARY_MAX_PHOTOS)
});
var intranetModuleReportSchema = z.object({
  id: z.string().uuid(),
  instanceId: z.string().uuid(),
  authorId: z.string().uuid(),
  authorDisplayName: z.string().optional(),
  status: intranetModuleReportStatusSchema,
  payload: z.unknown(),
  runningNumber: z.string().nullable(),
  year: z.number().int().nullable(),
  sequence: z.number().int().nullable(),
  pdfStorageKey: z.string().nullable(),
  photos: z.array(intranetModuleReportPhotoSchema),
  submittedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var intranetModuleReportListResponseSchema = z.object({
  items: z.array(intranetModuleReportSchema)
});
var submitIntranetModuleReportResponseSchema = intranetModuleReportSchema.extend(
  siteDiaryDeliveryResultSchema.shape
);
function formatSiteDiaryRunningNumber(prefix, year, sequence) {
  return `${prefix}-${year}-${String(sequence).padStart(4, "0")}`;
}
var intranetModuleInstanceWriteSchema = z.strictObject({
  kind: z.literal(IntranetModuleKind.SITE_DIARY),
  label: z.string().trim().min(2, "Bitte eine Bezeichnung mit mindestens 2 Zeichen angeben.").max(40, "Die Bezeichnung darf h\xF6chstens 40 Zeichen lang sein."),
  iconName: intranetShortcutIconSchema.default("ClipboardList"),
  tone: intranetLinkToneSchema.default(IntranetLinkTone.NAVY),
  sortOrder: z.number().int().min(0).max(1e4).default(0),
  enabled: z.boolean(),
  settings: siteDiarySettingsSchema
}).superRefine((value, ctx) => {
  if (!value.enabled) {
    return;
  }
  if (value.settings.audienceRoleIds.length === 0) {
    ctx.addIssue({
      code: "custom",
      path: ["settings", "audienceRoleIds"],
      message: "Bitte mindestens eine Sichtgruppe w\xE4hlen."
    });
  }
  if (value.settings.recipientRoleIds.length === 0 && value.settings.recipientUserIds.length === 0) {
    ctx.addIssue({
      code: "custom",
      path: ["settings", "recipientRoleIds"],
      message: RECIPIENT_MESSAGE
    });
  }
});
var createIntranetModuleInstanceRequestSchema = intranetModuleInstanceWriteSchema;
var updateIntranetModuleInstanceRequestSchema = z.strictObject({
  label: intranetModuleInstanceWriteSchema.shape.label.optional(),
  iconName: intranetShortcutIconSchema.optional(),
  tone: intranetLinkToneSchema.optional(),
  sortOrder: z.number().int().min(0).max(1e4).optional(),
  enabled: z.boolean().optional(),
  settings: siteDiarySettingsSchema.optional()
});
var intranetModuleKindOptionSchema = z.object({
  kind: intranetModuleKindSchema,
  labelDe: z.string()
});
var intranetModuleKindListResponseSchema = z.object({
  items: z.array(intranetModuleKindOptionSchema)
});
var intranetModuleInstanceSchema = z.object({
  id: z.string().uuid(),
  kind: intranetModuleKindSchema,
  kindLabel: z.string(),
  label: z.string(),
  iconName: z.string(),
  tone: intranetLinkToneSchema,
  sortOrder: z.number().int(),
  enabled: z.boolean(),
  /** Full settings for managers. Audience view omits recipient ids and logo storage key. */
  settings: siteDiarySettingsViewSchema,
  logoScanStatus: attachmentScanStatusSchema.nullable(),
  /** Servable custom logo path, portal branding path, or null. */
  logoUrl: z.string().nullable(),
  recipientCount: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var intranetModuleInstanceListResponseSchema = z.object({
  items: z.array(intranetModuleInstanceSchema)
});
var IntranetQuickAccessItemType = {
  LINK: "link",
  MODULE: "module"
};
var intranetQuickAccessItemTypeSchema = z.enum(IntranetQuickAccessItemType);
var intranetQuickAccessItemSchema = z.object({
  type: intranetQuickAccessItemTypeSchema,
  id: z.string().uuid(),
  label: z.string(),
  url: z.string(),
  iconName: z.string().nullable(),
  tone: intranetLinkToneSchema,
  sortOrder: z.number().int(),
  /** Set when type is module. */
  kind: intranetModuleKindSchema.optional()
});
var intranetQuickAccessResponseSchema = z.object({
  items: z.array(intranetQuickAccessItemSchema)
});
function intranetModulePath(instanceId) {
  return `/intranet/modul/${instanceId}`;
}
function intranetModuleLogoPath(instanceId, version) {
  const base = `/api/intranet/modules/${instanceId}/logo`;
  return version ? `${base}?v=${encodeURIComponent(version)}` : base;
}
function intranetModulePhotoPath(instanceId, photoId) {
  return `/api/intranet/modules/${instanceId}/photos/${photoId}`;
}
var INTRANET_AI_INSTRUCTION_MAX = 2e3;
var INTRANET_AI_HTML_MAX = 2e4;
var INTRANET_AI_QUESTION_MAX = 2e3;
var intranetAiDraftRequestSchema = z.strictObject({
  instruction: z.string().trim().min(1).max(INTRANET_AI_INSTRUCTION_MAX),
  title: z.string().trim().max(200).optional(),
  teaser: z.string().trim().max(500).optional(),
  bodyHtml: z.string().max(INTRANET_AI_HTML_MAX).optional()
});
var intranetAiDraftResponseSchema = z.object({
  chatAvailable: z.boolean(),
  title: z.string().optional(),
  teaser: z.string().optional(),
  bodyHtml: z.string().optional()
});
var intranetAiAskRequestSchema = z.strictObject({
  question: z.string().trim().min(1).max(INTRANET_AI_QUESTION_MAX),
  /** Accepted now. Which files an agent may read is applied in a later step. */
  agentId: z.string().uuid().optional()
});
var intranetAiAskSourceSchema = z.object({
  slug: z.string(),
  title: z.string(),
  kind: intranetContentKindSchema,
  /** Reader path, with `#kommentar-…` when the answer comes from a comment. */
  href: z.string().optional()
});
var intranetAiAskResponseSchema = z.object({
  chatAvailable: z.boolean(),
  answer: z.string().min(1),
  sources: z.array(intranetAiAskSourceSchema)
});
var INTRANET_AI_ASK_HISTORY_MAX = 30;
var INTRANET_AI_ASK_PAGE_SIZE = 10;
var intranetAiAskHistoryItemSchema = z.object({
  id: z.string().uuid(),
  question: z.string(),
  answer: z.string(),
  sources: z.array(intranetAiAskSourceSchema),
  agentId: z.string().uuid().nullable(),
  agentName: z.string().nullable(),
  createdAt: z.iso.datetime()
});
var intranetAiAskScopeSchema = z.enum(["intranet", "agent"]);
var intranetAiAskHistoryQuerySchema = paginationQuerySchema.pick({ page: true, pageSize: true }).extend({
  pageSize: z.coerce.number().int().min(1).max(INTRANET_AI_ASK_HISTORY_MAX).default(INTRANET_AI_ASK_PAGE_SIZE),
  scope: intranetAiAskScopeSchema.optional()
});
var intranetAiAskDeleteAllQuerySchema = z.object({
  scope: intranetAiAskScopeSchema.optional()
});
var intranetAiAskHistorySchema = paginatedSchema(intranetAiAskHistoryItemSchema);
var intranetAiAskDeleteAllResponseSchema = z.object({
  ok: z.literal(true),
  deleted: z.number().int().nonnegative()
});
var intranetAiAssistantSchema = z.object({
  intranetAssistant: z.boolean()
});
var INTRANET_AI_AGENT_NAME_MAX = 80;
var INTRANET_AI_AGENT_DESCRIPTION_MAX = 240;
var INTRANET_AI_AGENT_FILES_MAX = 20;
var intranetAiAgentWriteSchema = z.strictObject({
  id: z.string().uuid().optional(),
  name: z.string().trim().min(1).max(INTRANET_AI_AGENT_NAME_MAX),
  description: z.string().trim().max(INTRANET_AI_AGENT_DESCRIPTION_MAX),
  instruction: z.string().trim().min(1).max(INTRANET_AI_INSTRUCTION_MAX),
  enabled: z.boolean(),
  roleIds: z.array(z.string().uuid()),
  contentIds: z.array(z.string().uuid()).max(INTRANET_AI_AGENT_FILES_MAX, "Ein Agent darf h\xF6chstens 20 Beitr\xE4ge haben.")
});
var replaceIntranetAiAgentsRequestSchema = z.strictObject({
  agents: z.array(intranetAiAgentWriteSchema)
});
var intranetAiAgentSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  instruction: z.string(),
  enabled: z.boolean(),
  roleIds: z.array(z.string().uuid()),
  contentIds: z.array(z.string().uuid()),
  updatedAt: z.string()
});
var intranetAiAgentListSchema = z.object({
  items: z.array(intranetAiAgentSchema)
});
var intranetAiAgentMineItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string()
});
var intranetAiAgentMineSchema = z.object({
  items: z.array(intranetAiAgentMineItemSchema)
});
var INTRANET_AI_AGENT_TEMPLATES = [
  {
    id: "handbuch",
    name: "Handbuch",
    description: "Fragen zu Richtlinien und Abl\xE4ufen in den angeh\xE4ngten Dateien.",
    instruction: "Beantworte die Frage mit dem Inhalt aus den Stellen (Regeln, Fristen, Schritte). Verweise nicht auf Abs\xE4tze oder Dateien. Steht es nicht in den Stellen, sage das."
  },
  {
    id: "formulare",
    name: "Formulare",
    description: "Welches Formular passt, und wo es liegt.",
    instruction: "Nenne das passende Formular und was laut den Stellen damit zu tun ist. Verweise nicht nur auf den Ablageort. Erfinde keine Fristen und keine Betr\xE4ge."
  },
  {
    id: "it-anleitung",
    name: "IT-Anleitung",
    description: "Schritte aus den Anleitungen, ohne ein Ticket anzulegen.",
    instruction: "Gib die konkreten Schritte aus den Stellen wieder. Sage nicht nur, in welchem Dokument sie stehen. Lege kein Ticket an und verweise nicht auf Systeme, die nicht in den Stellen stehen."
  },
  {
    id: "betriebsanweisung",
    name: "Betriebsanweisung",
    description: "Eine Seite mit mehreren Gefahrstoff-Dokumenten. Die Antwort gilt dem genannten Produkt.",
    instruction: "Die Seite kann mehrere Dokumente enthalten. Nutze das Dokument, das den genannten Stoff, die Nummer oder den Handelsnamen tr\xE4gt. Nenne Schutzma\xDFnahmen und Verhaltensregeln aus diesem Text als Liste. Antworte nicht mit dem Dateinamen. Steht der Stoff in keinem Dokument, sage das."
  },
  {
    id: "herstellerkatalog",
    name: "Herstellerkatalog",
    description: "Lange Kataloge: Vorgaben, Zahlen und Anwendungssituationen aus dem Abschnitt.",
    instruction: "Lies den Abschnitt, der zur Frage passt, auch wenn der Katalog lang ist. Nenne die Vorgaben mit Zahlen und die Anwendungssituationen aus dem Text. Eine Antwort, die nur die Datei oder das PDF nennt, ist keine Antwort. Fehlt der Abschnitt im Text, sage das."
  },
  {
    id: "checkliste",
    name: "Checkliste",
    description: "Schritte aus dem passenden Dokument, in der Reihenfolge des Textes.",
    instruction: "Gib die Schritte in der Reihenfolge des Dokuments wieder. \xDCberspringe keine Bedingung und keine Zahl. Nenne das Dokument nicht als Ersatz f\xFCr die Schritte. Fehlt ein Schritt im Text, sage das."
  },
  {
    id: "kennwerte",
    name: "Kennwerte",
    description: "Grenzwerte und Einheiten aus dem Text, ohne auf die Datei zu verweisen.",
    instruction: "Antworte mit den Kennwerten aus dem Text: Zahl, Einheit und wof\xFCr sie gilt. Runde nicht. Ersetze einen Wert nicht durch einen Verweis auf die Datei. Steht der Wert nicht im Text, sage das."
  },
  {
    id: "leer",
    name: "Neuer Agent",
    description: "Eigene Anweisung ohne Vorlage.",
    instruction: ""
  }
];
var GermanStateCode = {
  BW: "BW",
  BY: "BY",
  BE: "BE",
  BB: "BB",
  HB: "HB",
  HH: "HH",
  HE: "HE",
  MV: "MV",
  NI: "NI",
  NW: "NW",
  RP: "RP",
  SL: "SL",
  SN: "SN",
  ST: "ST",
  SH: "SH",
  TH: "TH"
};
var germanStateCodeSchema = z.enum(GermanStateCode);
var GERMAN_STATE_CODES = Object.values(GermanStateCode);
var LeaveHolidayScope = {
  STATE: "state",
  COMPANY: "company"
};
var leaveHolidayScopeSchema = z.enum(LeaveHolidayScope);
var dayWeight = z.number().refine((value) => value === 0 || value === 0.5 || value === 1, {
  message: "Ein Wochentag ist 0, 0,5 oder 1."
});
var isoDate3 = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Bitte ein Datum im Format JJJJ-MM-TT.");
var leaveWorkingTimeModeSchema = z.enum(["off", "warn", "enforce"]);
var leavePolicySchema = z.object({
  id: z.string().uuid(),
  annualVacationDaysFullTime: z.number(),
  referenceWorkDaysPerWeek: z.number(),
  vacationYearStartMonth: z.number().int(),
  carryOverExpireMonth: z.number().int(),
  carryOverExpireDay: z.number().int(),
  requireSubstitute: z.boolean(),
  enforceCapacity: z.boolean(),
  hoursPerFullDay: z.number(),
  reminderDaysBefore: z.number().int(),
  maxCarryOverDays: z.number().nullable(),
  defaultStateCode: germanStateCodeSchema,
  allowPastRequests: z.boolean(),
  approvalSteps: z.number().int().min(1).max(2),
  maxOvertimeHours: z.number().nullable(),
  enforceOvertimeLimit: z.boolean(),
  datevConsultantNumber: z.string().nullable(),
  datevClientNumber: z.string().nullable(),
  workingTimeMode: leaveWorkingTimeModeSchema,
  updatedAt: z.iso.datetime()
});
var updateLeavePolicyRequestSchema = z.object({
  annualVacationDaysFullTime: z.number().min(0).max(60),
  referenceWorkDaysPerWeek: z.number().min(1).max(7),
  vacationYearStartMonth: z.number().int().min(1).max(12),
  carryOverExpireMonth: z.number().int().min(1).max(12),
  carryOverExpireDay: z.number().int().min(1).max(31),
  requireSubstitute: z.boolean(),
  enforceCapacity: z.boolean(),
  hoursPerFullDay: z.number().min(0.5).max(24),
  reminderDaysBefore: z.number().int().min(0).max(60),
  maxCarryOverDays: z.number().min(0).max(60).nullable(),
  defaultStateCode: germanStateCodeSchema,
  allowPastRequests: z.boolean(),
  approvalSteps: z.number().int().min(1).max(2),
  maxOvertimeHours: z.number().min(0).max(9999).nullable(),
  enforceOvertimeLimit: z.boolean(),
  datevConsultantNumber: z.union([
    z.string().trim().regex(/^\d{1,7}$/),
    z.null()
  ]),
  datevClientNumber: z.union([
    z.string().trim().regex(/^\d{1,5}$/),
    z.null()
  ]),
  workingTimeMode: leaveWorkingTimeModeSchema
});
var upsertLeaveWorkPatternRequestSchema = z.object({
  effectiveFrom: isoDate3,
  stateCode: germanStateCodeSchema,
  mon: dayWeight,
  tue: dayWeight,
  wed: dayWeight,
  thu: dayWeight,
  fri: dayWeight,
  sat: dayWeight,
  sun: dayWeight,
  hoursPerFullDay: z.number().min(0.5).max(24).nullable().optional()
});
var leaveWorkPatternSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  effectiveFrom: isoDate3,
  stateCode: germanStateCodeSchema,
  mon: z.number(),
  tue: z.number(),
  wed: z.number(),
  thu: z.number(),
  fri: z.number(),
  sat: z.number(),
  sun: z.number(),
  hoursPerFullDay: z.number().nullable()
});
var createCompanyHolidayRequestSchema = z.object({
  date: isoDate3,
  nameDe: z.string().trim().min(1).max(120)
});
var leaveHolidaySchema = z.object({
  id: z.string().uuid(),
  date: isoDate3,
  nameDe: z.string(),
  scope: leaveHolidayScopeSchema,
  stateCode: germanStateCodeSchema.nullable(),
  year: z.number().int()
});
var generateLeaveHolidaysQuerySchema = z.object({
  year: z.coerce.number().int().min(2e3).max(2100)
});
var generateLeaveHolidaysResultSchema = z.object({
  year: z.number().int(),
  created: z.number().int(),
  total: z.number().int()
});
var leaveAdminSetupSchema = z.object({
  year: z.number().int(),
  nextYear: z.number().int(),
  policyReady: z.boolean(),
  holidaysReady: z.boolean(),
  typesReady: z.boolean(),
  missingPersonnelCount: z.number().int().nonnegative().nullable()
});
var LeaveDayPartCode = {
  FULL: "full",
  MORNING: "morning",
  AFTERNOON: "afternoon"
};
var leaveDayPartSchema = z.enum(LeaveDayPartCode);
var LeaveRequestStatusCode = {
  DRAFT: "draft",
  SUBMITTED: "submitted",
  APPROVED: "approved",
  REJECTED: "rejected",
  CANCELLED: "cancelled"
};
var leaveRequestStatusSchema = z.enum(LeaveRequestStatusCode);
var leaveBalanceQuerySchema = z.object({
  year: z.coerce.number().int().min(2e3).max(2100).optional()
});
var leaveBalanceProjectionQuerySchema = z.object({
  startsOn: isoDate3,
  endsOn: isoDate3,
  startPart: leaveDayPartSchema,
  endPart: leaveDayPartSchema
});
var leaveBalanceProjectionSchema = z.object({
  entitlement: z.number(),
  carryOver: z.number(),
  taken: z.number(),
  pending: z.number(),
  rest: z.number(),
  restAfterPending: z.number()
});
var weekWeightSchema = z.object({
  mon: z.number(),
  tue: z.number(),
  wed: z.number(),
  thu: z.number(),
  fri: z.number(),
  sat: z.number(),
  sun: z.number(),
  stateCode: germanStateCodeSchema
});
var LeavePatternSource = {
  STORED: "stored",
  DEFAULT: "default"
};
var leavePatternSourceSchema = z.enum(LeavePatternSource);
var leaveBalanceSchema = z.object({
  year: z.number().int(),
  entitlement: z.number(),
  carryOver: z.number(),
  taken: z.number(),
  pending: z.number(),
  rest: z.number(),
  restAfterPending: z.number(),
  overtimeHours: z.number(),
  carryOverExpiresOn: isoDate3,
  pattern: weekWeightSchema.nullable(),
  patternSource: leavePatternSourceSchema,
  holidays: z.array(isoDate3)
});
var LeaveAbsenceKindCode = {
  VACATION: "vacation",
  SICK: "sick",
  TRAINING: "training",
  SPECIAL: "special",
  UNPAID: "unpaid",
  COMP_TIME: "comp_time",
  HOME_OFFICE: "home_office",
  BUSINESS_TRIP: "business_trip"
};
var leaveAbsenceKindSchema = z.enum(LeaveAbsenceKindCode);
var LEAVE_ABSENCE_KIND_LABELS = {
  vacation: "Erholungsurlaub",
  sick: "Krankheit",
  training: "Fortbildung",
  special: "Sonderurlaub",
  unpaid: "Unbezahlte Freistellung",
  comp_time: "Freizeitausgleich",
  home_office: "Homeoffice",
  business_trip: "Dienstreise"
};
var LEAVE_ABSENCE_KIND_HINTS = {
  vacation: "Verbraucht Erholungsurlaub.",
  sick: "Wird erfasst. Kolleginnen sehen nur \u201EAbwesend\u201C.",
  training: "Z\xE4hlt nicht als Erholungsurlaub.",
  special: "Sonderurlaub. Zieht keinen Erholungsurlaub ab.",
  unpaid: "Unbezahlt. Z\xE4hlt nicht als Erholungsurlaub.",
  comp_time: "Baut das \xDCberstundenkonto ab.",
  home_office: "Z\xE4hlt nicht f\xFCr die Mindestbesetzung und nicht als Urlaub.",
  business_trip: "Der Arbeitsplatz ist leer. Z\xE4hlt nicht als Urlaub."
};
var LEAVE_VACATION_BALANCE_KINDS = [LeaveAbsenceKindCode.VACATION];
var LeaveAbsenceColor = {
  SKY: "sky",
  AMBER: "amber",
  ROSE: "rose",
  EMERALD: "emerald",
  VIOLET: "violet",
  ORANGE: "orange",
  SLATE: "slate",
  TEAL: "teal",
  LIME: "lime",
  FUCHSIA: "fuchsia",
  CYAN: "cyan",
  STONE: "stone"
};
var leaveAbsenceColorSchema = z.enum(LeaveAbsenceColor);
var leaveAbsenceMarkSchema = z.string().trim().regex(/^[A-Z0-9]{1,2}$/, "Das Kurzzeichen ist ein oder zwei Zeichen A\u2013Z oder 0\u20139.");
var LEAVE_SYSTEM_ABSENCE_TYPES = [
  {
    id: "00000000-0000-4000-8000-00000000a511",
    code: "vacation",
    kind: LeaveAbsenceKindCode.VACATION,
    mark: "U",
    countsTowardCapacity: true
  },
  {
    id: "00000000-0000-4000-8000-00000000a512",
    code: "sick",
    kind: LeaveAbsenceKindCode.SICK,
    mark: "K",
    countsTowardCapacity: true
  },
  {
    id: "00000000-0000-4000-8000-00000000a513",
    code: "training",
    kind: LeaveAbsenceKindCode.TRAINING,
    mark: "F",
    countsTowardCapacity: true
  },
  {
    id: "00000000-0000-4000-8000-00000000a514",
    code: "unpaid",
    kind: LeaveAbsenceKindCode.UNPAID,
    mark: "N",
    countsTowardCapacity: true
  },
  {
    id: "00000000-0000-4000-8000-00000000a515",
    code: "comp_time",
    kind: LeaveAbsenceKindCode.COMP_TIME,
    mark: "A",
    countsTowardCapacity: true
  },
  {
    id: "00000000-0000-4000-8000-00000000a516",
    code: "home_office",
    kind: LeaveAbsenceKindCode.HOME_OFFICE,
    mark: "H",
    countsTowardCapacity: false
  },
  {
    id: "00000000-0000-4000-8000-00000000a517",
    code: "business_trip",
    kind: LeaveAbsenceKindCode.BUSINESS_TRIP,
    mark: "D",
    countsTowardCapacity: true
  },
  {
    id: "00000000-0000-4000-8000-00000000a501",
    code: "wedding",
    kind: LeaveAbsenceKindCode.SPECIAL,
    mark: "HZ",
    countsTowardCapacity: true
  },
  {
    id: "00000000-0000-4000-8000-00000000a502",
    code: "birth",
    kind: LeaveAbsenceKindCode.SPECIAL,
    mark: "GB",
    countsTowardCapacity: true
  },
  {
    id: "00000000-0000-4000-8000-00000000a503",
    code: "bereavement",
    kind: LeaveAbsenceKindCode.SPECIAL,
    mark: "TR",
    countsTowardCapacity: true
  },
  {
    id: "00000000-0000-4000-8000-00000000a504",
    code: "move",
    kind: LeaveAbsenceKindCode.SPECIAL,
    mark: "UM",
    countsTowardCapacity: true
  }
];
var optionalLeaveDays = z.number().min(0).max(60).nullable();
var leaveAbsenceTypeSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  labelDe: z.string(),
  kind: leaveAbsenceKindSchema,
  color: leaveAbsenceColorSchema,
  mark: leaveAbsenceMarkSchema,
  defaultDays: optionalLeaveDays,
  maxDaysPerRequest: optionalLeaveDays,
  requiresApproval: z.boolean(),
  countsTowardCapacity: z.boolean(),
  visibleToColleagues: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  system: z.boolean()
});
var leaveAbsenceTypeListSchema = z.object({
  items: z.array(leaveAbsenceTypeSchema)
});
var absenceTypeCodeSchema = z.string().trim().regex(
  /^[a-z][a-z0-9_]{0,31}$/,
  "Der Code beginnt mit einem Buchstaben und nutzt a\u2013z, 0\u20139 oder _."
);
var createLeaveAbsenceTypeRequestSchema = z.object({
  code: absenceTypeCodeSchema,
  labelDe: z.string().trim().min(1).max(120),
  kind: leaveAbsenceKindSchema,
  color: leaveAbsenceColorSchema,
  mark: leaveAbsenceMarkSchema,
  defaultDays: optionalLeaveDays,
  maxDaysPerRequest: optionalLeaveDays,
  requiresApproval: z.boolean(),
  countsTowardCapacity: z.boolean(),
  visibleToColleagues: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int().min(0).max(999)
});
var updateLeaveAbsenceTypeRequestSchema = createLeaveAbsenceTypeRequestSchema.omit({
  code: true,
  kind: true
});
var leaveHolidayListQuerySchema = z.object({
  year: z.coerce.number().int().min(2e3).max(2100)
});
var leaveHolidayListSchema = z.object({
  items: z.array(leaveHolidaySchema)
});
var upsertLeaveDepartmentCapacityRequestSchema = z.object({
  minPresent: z.number().int().min(0).max(999)
});
var leaveDepartmentCapacityItemSchema = z.object({
  departmentId: z.string().uuid(),
  departmentName: z.string(),
  minPresent: z.number().int().nullable()
});
var leaveDepartmentCapacityListSchema = z.object({
  items: z.array(leaveDepartmentCapacityItemSchema)
});
var LeaveExportKindCode = {
  REQUESTS: "requests",
  BALANCES: "balances",
  OVERTIME: "overtime"
};
var leaveExportKindSchema = z.enum(LeaveExportKindCode);
var LeaveExportFormatCode = {
  CSV: "csv",
  XLSX: "xlsx",
  DATEV: "datev",
  LOGA: "loga"
};
var leaveExportFormatSchema = z.enum(LeaveExportFormatCode);
var optionalExportUuid = z.preprocess(
  (value) => value === "" || value == null ? void 0 : value,
  z.string().uuid().optional()
);
var optionalExportDate = z.preprocess(
  (value) => value === "" || value == null ? void 0 : value,
  isoDate3.optional()
);
var leaveExportFilterObject = z.object({
  year: z.coerce.number().int().min(2e3).max(2100),
  from: optionalExportDate,
  to: optionalExportDate,
  departmentId: optionalExportUuid,
  userId: optionalExportUuid,
  kind: leaveExportKindSchema
});
function refineExportRange(value, ctx) {
  if (value.from && value.to && value.to < value.from) {
    ctx.addIssue({
      code: "custom",
      path: ["to"],
      message: "Das Enddatum liegt vor dem Startdatum."
    });
  }
}
var leaveExportFilterSchema = leaveExportFilterObject.superRefine(refineExportRange);
var leaveExportQuerySchema = leaveExportFilterObject.extend({ format: leaveExportFormatSchema }).superRefine(refineExportRange);
var leavePayrollSkippedSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string()
});
var leavePayrollExportSchema = z.object({
  filename: z.string(),
  mimeType: z.string(),
  contentBase64: z.string(),
  skipped: z.array(leavePayrollSkippedSchema)
});
var leaveExportCountSchema = z.object({
  total: z.number().int().nonnegative()
});
var leaveExportOptionsSchema = z.object({
  departments: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  users: z.array(
    z.object({
      id: z.string().uuid(),
      displayName: z.string(),
      departmentId: z.string().uuid().nullable()
    })
  )
});
var leaveRequestWriteSchema = z.object({
  absenceTypeId: z.string().uuid(),
  startsOn: isoDate3,
  endsOn: isoDate3,
  startPart: leaveDayPartSchema.default("full"),
  endPart: leaveDayPartSchema.default("full"),
  comment: z.string().trim().max(500).nullable().optional(),
  substituteUserId: z.string().uuid().nullable().optional()
});
var createLeaveOvertimeRequestSchema = z.object({
  userId: z.string().uuid().optional(),
  hours: z.number().min(0.25).max(999),
  occurredOn: isoDate3,
  note: z.string().trim().max(300).nullable().optional()
});
var leavePunchKindSchema = z.enum(["in", "out", "break_start", "break_end"]);
var createLeaveTimePunchSchema = z.object({
  kind: leavePunchKindSchema
});
var leaveTimePunchSchema = z.object({
  id: z.string().uuid(),
  at: z.iso.datetime(),
  kind: leavePunchKindSchema
});
var leaveWorkingTimeWarningSchema = z.object({
  code: z.enum(["DAILY_MAX", "BREAK_30", "BREAK_45", "REST_11"]),
  message: z.string()
});
var leaveWorkingTimeStatusSchema = z.object({
  mode: leaveWorkingTimeModeSchema,
  date: isoDate3,
  punches: z.array(leaveTimePunchSchema),
  state: z.enum(["not_started", "working", "break", "finished"]),
  actualHours: z.number(),
  targetHours: z.number(),
  suggestionHours: z.number().nullable(),
  confirmed: z.boolean(),
  warnings: z.array(leaveWorkingTimeWarningSchema)
});
var leaveOvertimeStatusSchema = z.enum(["submitted", "approved", "rejected"]);
var leaveOvertimeLimitWarningSchema = z.object({
  approvedHours: z.number(),
  projectedHours: z.number(),
  maxOvertimeHours: z.number()
});
var leaveOvertimeEntrySchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  hours: z.number(),
  occurredOn: isoDate3,
  note: z.string().nullable(),
  requestId: z.string().uuid().nullable(),
  status: leaveOvertimeStatusSchema,
  createdAt: z.iso.datetime(),
  overtimeLimit: leaveOvertimeLimitWarningSchema.nullable().optional()
});
var createLeaveBalanceAdjustmentRequestSchema = z.object({
  userId: z.string().uuid(),
  year: z.number().int().min(2e3).max(2100),
  days: z.number().min(-60).max(60),
  reason: z.string().trim().min(1).max(300)
});
var leaveBalanceAdjustmentSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  year: z.number().int(),
  days: z.number(),
  reason: z.string(),
  createdAt: z.iso.datetime()
});
var leaveRequestSchema = z.object({
  id: z.string().uuid(),
  kind: leaveAbsenceKindSchema,
  status: leaveRequestStatusSchema,
  startsOn: isoDate3,
  endsOn: isoDate3,
  startPart: leaveDayPartSchema,
  endPart: leaveDayPartSchema,
  comment: z.string().nullable(),
  days: z.number(),
  absenceType: z.object({
    id: z.string().uuid(),
    code: z.string(),
    labelDe: z.string(),
    color: leaveAbsenceColorSchema
  }),
  substitute: z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    profilePhotoUrl: z.string().nullable().optional()
  }).nullable(),
  requester: z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    profilePhotoUrl: z.string().nullable().optional()
  }),
  approvalStep: z.number().int().min(1).max(2),
  /** Step 2 has no skip-level manager, so LEAVE_ADMIN sees the request. */
  missingSkipManager: z.boolean().optional(),
  createdAt: z.iso.datetime()
});
var setPersonnelNumberSchema = z.object({
  personnelNumber: z.preprocess(
    (value) => typeof value === "string" && value.trim() === "" ? null : value,
    z.union([personnelNumberSchema, z.null()])
  )
});
var leavePersonSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string().email().optional(),
  personnelNumber: z.string().nullable().optional(),
  profilePhotoUrl: z.string().nullable().optional()
});
var leavePersonListSchema = z.object({
  items: z.array(leavePersonSchema)
});
var leaveColleagueSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  departmentName: z.string(),
  siteName: z.string().nullable(),
  phone: z.string().nullable(),
  profilePhotoUrl: z.string().nullable().optional()
});
var leaveColleagueListSchema = z.object({
  items: z.array(leaveColleagueSchema)
});
var leaveColleagueCalendarPersonSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  departmentName: z.string().nullable(),
  profilePhotoUrl: z.string().nullable().optional()
});
var LEAVE_REQUESTS_PATH = "/leave/antraege";
function leaveRequestPath(requestId) {
  return `${LEAVE_REQUESTS_PATH}/${requestId}`;
}
var leaveCapacityWarningSchema = z.object({
  date: isoDate3,
  present: z.number().int(),
  minPresent: z.number().int()
});
var leaveRequestWarningsSchema = z.object({
  substitutionOtherDepartment: z.boolean().optional(),
  capacityUnderMin: z.array(leaveCapacityWarningSchema).optional()
});
var LeaveCalendarKindCode = {
  VACATION: "vacation",
  SICK: "sick",
  TRAINING: "training",
  SPECIAL: "special",
  UNPAID: "unpaid",
  COMP_TIME: "comp_time",
  HOME_OFFICE: "home_office",
  BUSINESS_TRIP: "business_trip",
  AWAY: "away"
};
var leaveCalendarKindSchema = z.enum(LeaveCalendarKindCode);
var LEAVE_CALENDAR_AWAY_FIELDS = {
  labelDe: "Abwesend",
  color: LeaveAbsenceColor.SLATE,
  mark: null,
  absenceTypeId: null,
  comment: null
};
var leaveCalendarQuerySchema = z.object({
  from: isoDate3,
  to: isoDate3,
  departmentId: z.string().uuid().optional()
});
var leaveCalendarDepartmentSchema = z.object({
  id: z.string().uuid(),
  name: z.string()
});
var leaveCalendarDepartmentListSchema = z.object({
  items: z.array(leaveCalendarDepartmentSchema)
});
var leaveCalendarEntrySchema = z.object({
  requestId: z.string().uuid(),
  userId: z.string().uuid(),
  displayName: z.string(),
  kind: leaveCalendarKindSchema,
  /** Null when the type is masked as away. */
  absenceTypeId: z.string().uuid().nullable(),
  labelDe: z.string(),
  mark: leaveAbsenceMarkSchema.nullable(),
  color: leaveAbsenceColorSchema,
  status: leaveRequestStatusSchema,
  part: leaveDayPartSchema,
  substituteName: z.string().nullable(),
  comment: z.string().nullable()
});
var leaveCalendarHolidaySchema = z.object({
  date: isoDate3,
  nameDe: z.string()
});
var leaveCalendarDaySchema = z.object({
  date: isoDate3,
  holidays: z.array(leaveCalendarHolidaySchema),
  entries: z.array(leaveCalendarEntrySchema)
});
var leaveCalendarYearSchoolHolidaySchema = z.object({
  nameDe: z.string(),
  startsOn: isoDate3,
  endsOn: isoDate3
});
var leaveCalendarResponseSchema = z.object({
  departmentId: z.string().uuid(),
  departmentName: z.string(),
  from: isoDate3,
  to: isoDate3,
  stateCode: germanStateCodeSchema.nullable(),
  schoolHolidays: z.array(leaveCalendarYearSchoolHolidaySchema),
  days: z.array(leaveCalendarDaySchema)
});
var leaveColleagueCalendarResponseSchema = z.object({
  from: isoDate3,
  to: isoDate3,
  stateCode: germanStateCodeSchema.nullable(),
  schoolHolidays: z.array(leaveCalendarYearSchoolHolidaySchema),
  people: z.array(leaveColleagueCalendarPersonSchema),
  days: z.array(leaveCalendarDaySchema)
});
var leaveCalendarYearQuerySchema = z.object({
  year: z.coerce.number().int().min(2e3).max(2100),
  userId: z.string().uuid().optional()
});
var leaveCalendarYearAbsenceSchema = z.object({
  date: isoDate3,
  requestId: z.string().uuid(),
  /** Null when the type is masked as away. */
  absenceTypeId: z.string().uuid().nullable(),
  labelDe: z.string(),
  mark: leaveAbsenceMarkSchema.nullable(),
  color: leaveAbsenceColorSchema,
  kind: leaveCalendarKindSchema,
  status: leaveRequestStatusSchema,
  part: leaveDayPartSchema
});
var leaveCalendarYearTotalUnitSchema = z.enum(["days", "hours"]);
var leaveCalendarYearTotalSchema = z.object({
  absenceTypeId: z.string().uuid().nullable(),
  labelDe: z.string(),
  mark: leaveAbsenceMarkSchema.nullable(),
  color: leaveAbsenceColorSchema,
  status: leaveRequestStatusSchema,
  amount: z.number(),
  unit: leaveCalendarYearTotalUnitSchema
});
var leaveCalendarYearResponseSchema = z.object({
  year: z.number().int(),
  userId: z.string().uuid(),
  displayName: z.string(),
  stateCode: germanStateCodeSchema.nullable(),
  holidays: z.array(leaveCalendarHolidaySchema),
  schoolHolidays: z.array(leaveCalendarYearSchoolHolidaySchema),
  absences: z.array(leaveCalendarYearAbsenceSchema),
  totals: z.array(leaveCalendarYearTotalSchema)
});
var leaveRequestListQuerySchema = z.object({
  scope: z.enum(["mine", "inbox", "all"]).default("mine"),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).optional()
});
var leaveRequestDecideSchema = z.object({
  decision: z.enum(["approved", "rejected"]),
  decisionNote: z.string().trim().max(500).nullable().optional()
}).superRefine((value, ctx) => {
  if (value.decision === "rejected" && !value.decisionNote?.trim()) {
    ctx.addIssue({
      code: "custom",
      message: "leave.reject.reason.required",
      path: ["decisionNote"]
    });
  }
});
var leaveApprovalDelegateWriteSchema = z.object({
  delegateUserId: z.string().uuid().nullable(),
  validFrom: isoDate3,
  validTo: isoDate3
}).superRefine((value, ctx) => {
  if (value.validTo < value.validFrom) {
    ctx.addIssue({
      code: "custom",
      message: "Das Enddatum liegt vor dem Startdatum.",
      path: ["validTo"]
    });
  }
  if (value.delegateUserId?.length === 0) {
    ctx.addIssue({
      code: "custom",
      message: "leave.substitute.required",
      path: ["delegateUserId"]
    });
  }
});
var leaveApprovalDelegateSchema = z.object({
  delegateUserId: z.string().uuid().nullable(),
  validFrom: isoDate3.nullable(),
  validTo: isoDate3.nullable(),
  delegate: leavePersonSchema.nullable()
});
var leaveRequestEventSchema = z.object({
  id: z.string().uuid(),
  type: z.enum([
    "created",
    "submitted",
    "approved",
    "rejected",
    "cancelled",
    "updated",
    "substitute_set",
    "vacation_restored"
  ]),
  at: z.iso.datetime(),
  actor: leavePersonSchema,
  payload: z.record(z.string(), z.unknown())
});
var leaveRequestEventListSchema = z.object({
  items: z.array(leaveRequestEventSchema)
});
var leaveRequestPersonSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  departmentId: z.string().uuid().nullable().optional(),
  profilePhotoUrl: z.string().nullable().optional()
});
var leaveRequestDetailSchema = leaveRequestSchema.extend({
  requester: leaveRequestPersonSchema,
  decidedBy: leavePersonSchema.nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decisionNote: z.string().nullable(),
  warnings: leaveRequestWarningsSchema.optional()
});
var leaveRequestResponseSchema = leaveRequestSchema.extend({
  warnings: leaveRequestWarningsSchema.optional()
});
var leaveRequestListSchema = z.object({
  items: z.array(leaveRequestResponseSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1)
});
var leaveInboxRequestItemSchema = leaveRequestResponseSchema.extend({
  entryKind: z.literal("request")
});
var leaveInboxOvertimeItemSchema = z.object({
  entryKind: z.literal("overtime"),
  id: z.string().uuid(),
  user: z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    profilePhotoUrl: z.string().nullable().optional()
  }),
  hours: z.number(),
  occurredOn: isoDate3,
  note: z.string().nullable(),
  overtimeLimit: leaveOvertimeLimitWarningSchema.nullable()
});
var leaveInboxItemSchema = z.discriminatedUnion("entryKind", [
  leaveInboxRequestItemSchema,
  leaveInboxOvertimeItemSchema
]);
var leaveInboxListSchema = z.object({
  items: z.array(leaveInboxItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1)
});
var leaveCompanyClosureWriteSchema = z.object({
  nameDe: z.string().trim().min(1).max(120),
  startsOn: isoDate3,
  endsOn: isoDate3,
  departmentIds: z.array(z.string().uuid()).min(1)
}).superRefine((value, ctx) => {
  if (value.endsOn < value.startsOn) {
    ctx.addIssue({
      code: "custom",
      message: "Das Enddatum liegt vor dem Startdatum.",
      path: ["endsOn"]
    });
  }
});
var leaveCompanyClosurePersonSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  days: z.number(),
  rest: z.number(),
  restAfter: z.number(),
  included: z.boolean(),
  skipReason: z.enum(["balance", "overlap", "no_days", "capacity"]).nullable()
});
var leaveCompanyClosurePreviewSchema = z.object({
  nameDe: z.string(),
  startsOn: isoDate3,
  endsOn: isoDate3,
  people: z.array(leaveCompanyClosurePersonSchema)
});
var leaveCompanyClosureResultSchema = leaveCompanyClosurePreviewSchema.extend({
  id: z.string().uuid(),
  created: z.number().int().nonnegative()
});
var leaveCompanyClosureCancelSchema = z.object({
  cancelled: z.number().int().nonnegative()
});
var LeaveAreaCode = {
  REQUESTS: "requests",
  CALENDAR: "calendar",
  SICK: "sick",
  OVERTIME: "overtime",
  DELEGATES: "delegates",
  TIME: "time",
  CLOSURE: "closure",
  CAPACITY: "capacity",
  SCHOOL_HOLIDAYS: "school_holidays",
  EXPORT: "export"
};
var leaveAreaCodeSchema = z.enum(LeaveAreaCode);
var LEAVE_AREA_CODES = Object.values(LeaveAreaCode);
var LEAVE_AREA_SWITCHABLE = [
  LeaveAreaCode.CALENDAR,
  LeaveAreaCode.SICK,
  LeaveAreaCode.OVERTIME,
  LeaveAreaCode.DELEGATES,
  LeaveAreaCode.TIME,
  LeaveAreaCode.CLOSURE,
  LeaveAreaCode.CAPACITY,
  LeaveAreaCode.SCHOOL_HOLIDAYS,
  LeaveAreaCode.EXPORT
];
var LEAVE_AREA_LABELS = {
  requests: "Antr\xE4ge",
  calendar: "Kalender",
  sick: "Krankmeldung",
  overtime: "\xDCberstunden",
  delegates: "Vertretung",
  time: "Zeiterfassung",
  closure: "Betriebsurlaub",
  capacity: "Mindestbesetzung",
  school_holidays: "Schulferien",
  export: "Export"
};
var LEAVE_AREA_DESCRIPTIONS = {
  calendar: "Abteilungsmonat und der Jahreskalender.",
  sick: "Neue Krankmeldungen. Vorhandene Antr\xE4ge bleiben.",
  overtime: "Aufbau und Freizeitausgleich. Schaltet die Zeiterfassung mit aus.",
  delegates: "Genehmigungsvertretung einer F\xFChrungskraft.",
  time: "Kommen, Gehen und Pause. Die gespeicherte Stempelregel bleibt.",
  closure: "Betriebsurlaub anlegen. Bereits angelegte Urlaubsantr\xE4ge bleiben.",
  capacity: "Mindestbesetzung und Warnung. Gespeicherte Werte bleiben.",
  school_holidays: "Ferienfl\xE4che im Jahreskalender. Kein Einfluss auf den Saldo.",
  export: "CSV, Excel und DATEV. Gespeicherte DATEV-Nummern bleiben."
};
var LeaveAreaGroup = {
  DAILY: "daily",
  OPERATIONS: "operations",
  CLOSING: "closing"
};
var leaveAreaGroupSchema = z.enum(LeaveAreaGroup);
var LEAVE_AREA_GROUP_LABELS = {
  daily: "Alltag",
  operations: "Zeit und Betrieb",
  closing: "Abschluss"
};
var LEAVE_AREA_GROUP = {
  calendar: LeaveAreaGroup.DAILY,
  sick: LeaveAreaGroup.DAILY,
  overtime: LeaveAreaGroup.DAILY,
  delegates: LeaveAreaGroup.DAILY,
  time: LeaveAreaGroup.OPERATIONS,
  closure: LeaveAreaGroup.OPERATIONS,
  capacity: LeaveAreaGroup.OPERATIONS,
  school_holidays: LeaveAreaGroup.OPERATIONS,
  export: LeaveAreaGroup.CLOSING
};
var LEAVE_AREA_HIDES = {
  calendar: ["Kalender", "Heute in der Abteilung"],
  sick: ["Krankmeldung im Assistenten"],
  overtime: ["\xDCberstunden", "Freizeitausgleich"],
  delegates: ["Vertretung"],
  time: ["Stempelkarte"],
  closure: ["Betriebsurlaub"],
  capacity: ["Besetzung", "Besetzungswarnung"],
  school_holidays: ["Schulferien im Kalender"],
  export: ["Export"]
};
var LEAVE_AREA_DEPENDENTS = {
  overtime: [LeaveAreaCode.TIME]
};
var LEAVE_AREA_DEFAULTS = {
  calendar: true,
  sick: true,
  overtime: true,
  delegates: true,
  time: true,
  closure: true,
  capacity: true,
  school_holidays: true,
  export: true
};
function isLeaveAreaLocked(code) {
  return code === LeaveAreaCode.REQUESTS;
}
function leaveAreaDependents(code) {
  return [...LEAVE_AREA_DEPENDENTS[code] ?? []];
}
function leaveAreasTurnedOffWith(code) {
  return [code, ...leaveAreaDependents(code)];
}
function isLeaveAreaEnabled(areas, code) {
  if (isLeaveAreaLocked(code)) {
    return true;
  }
  if (code === LeaveAreaCode.TIME && areas?.overtime === false) {
    return false;
  }
  return areas?.[code] !== false;
}
function parseLeaveAreas(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  const record = value;
  const parsed = {};
  for (const code of LEAVE_AREA_SWITCHABLE) {
    if (typeof record[code] === "boolean") {
      parsed[code] = record[code];
    }
  }
  return parsed;
}
function resolveLeaveAreaMap(areas) {
  return Object.fromEntries(
    LEAVE_AREA_SWITCHABLE.map((code) => [code, isLeaveAreaEnabled(areas, code)])
  );
}
var leaveAreaCatalogItemSchema = z.object({
  code: z.enum(LEAVE_AREA_SWITCHABLE),
  label: z.string(),
  description: z.string(),
  group: leaveAreaGroupSchema,
  enabled: z.boolean(),
  dependents: z.array(z.enum(LEAVE_AREA_SWITCHABLE)),
  hides: z.array(z.string())
});
var leaveAreaCatalogSchema = z.object({
  items: z.array(leaveAreaCatalogItemSchema),
  updatedAt: z.iso.datetime().nullable()
});
var patchLeaveAreasRequestSchema = z.object({
  code: leaveAreaCodeSchema,
  enabled: z.boolean()
});
var patchLeaveAreasResponseSchema = leaveAreaCatalogSchema.extend({
  changed: z.array(z.enum(LEAVE_AREA_SWITCHABLE))
});
function buildLeaveAreaCatalog(areas, updatedAt = null) {
  return {
    items: LEAVE_AREA_SWITCHABLE.map((code) => ({
      code,
      label: LEAVE_AREA_LABELS[code],
      description: LEAVE_AREA_DESCRIPTIONS[code],
      group: LEAVE_AREA_GROUP[code],
      enabled: isLeaveAreaEnabled(areas, code),
      dependents: leaveAreaDependents(code),
      hides: [...LEAVE_AREA_HIDES[code]]
    })),
    updatedAt
  };
}
var MAINTENANCE_CHECKLIST_SCHEMA_VERSION = 1;
var maintenanceChecklistFieldTypeSchema = z.enum([
  "info",
  "boolean",
  "choice",
  "short_text",
  "long_text",
  "integer",
  "decimal",
  "measurement",
  "date",
  "time",
  "attachment"
]);
var fieldKeySchema = z.string().trim().regex(/^[a-z][a-z0-9_]{1,58}$/, "fieldKey: Kleinbuchstaben, Ziffern und Unterstrich.");
var maintenancePdfPlacementSchema = z.object({
  page: z.number().int().min(1),
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  width: z.number().min(0.01).max(1),
  height: z.number().min(0.01).max(1)
}).refine((p) => p.x + p.width <= 1.000001 && p.y + p.height <= 1.000001, {
  message: "Das Feld liegt au\xDFerhalb der Seite."
});
var fieldBaseSchema = z.object({
  fieldKey: fieldKeySchema,
  label: z.string().trim().min(1).max(200),
  helpText: z.union([z.string().trim().max(500), z.null()]).optional().transform((v) => v === void 0 ? null : v),
  required: z.boolean().default(false),
  sortOrder: z.number().int().min(0),
  placement: maintenancePdfPlacementSchema.optional()
});
var choiceOptionSchema = z.object({
  value: z.string().trim().min(1).max(80),
  label: z.string().trim().min(1).max(200)
});
var maintenanceChecklistInfoFieldSchema = fieldBaseSchema.extend({
  type: z.literal("info"),
  content: z.string().trim().min(1).max(4e3)
});
var maintenanceChecklistBooleanFieldSchema = fieldBaseSchema.extend({
  type: z.literal("boolean")
});
var maintenanceChecklistChoiceFieldSchema = fieldBaseSchema.extend({
  type: z.literal("choice"),
  options: z.array(choiceOptionSchema).min(1).max(50),
  allowMultiple: z.boolean().default(false)
});
var maintenanceChecklistShortTextFieldSchema = fieldBaseSchema.extend({
  type: z.literal("short_text"),
  maxLength: z.number().int().min(1).max(500).default(200)
});
var maintenanceChecklistLongTextFieldSchema = fieldBaseSchema.extend({
  type: z.literal("long_text"),
  maxLength: z.number().int().min(1).max(8e3).default(4e3)
});
var maintenanceChecklistIntegerFieldSchema = fieldBaseSchema.extend({
  type: z.literal("integer"),
  min: z.number().int().optional(),
  max: z.number().int().optional()
});
var maintenanceChecklistDecimalFieldSchema = fieldBaseSchema.extend({
  type: z.literal("decimal"),
  min: z.number().optional(),
  max: z.number().optional(),
  decimalPlaces: z.number().int().min(0).max(6).default(2)
});
var maintenanceChecklistMeasurementFieldSchema = fieldBaseSchema.extend({
  type: z.literal("measurement"),
  unit: z.string().trim().min(1).max(20),
  min: z.number().optional(),
  max: z.number().optional()
});
var maintenanceChecklistDateFieldSchema = fieldBaseSchema.extend({
  type: z.literal("date")
});
var maintenanceChecklistTimeFieldSchema = fieldBaseSchema.extend({
  type: z.literal("time")
});
var maintenanceChecklistAttachmentFieldSchema = fieldBaseSchema.extend({
  type: z.literal("attachment"),
  acceptImagesOnly: z.boolean().default(true)
});
var maintenanceChecklistFieldSchema = z.discriminatedUnion("type", [
  maintenanceChecklistInfoFieldSchema,
  maintenanceChecklistBooleanFieldSchema,
  maintenanceChecklistChoiceFieldSchema,
  maintenanceChecklistShortTextFieldSchema,
  maintenanceChecklistLongTextFieldSchema,
  maintenanceChecklistIntegerFieldSchema,
  maintenanceChecklistDecimalFieldSchema,
  maintenanceChecklistMeasurementFieldSchema,
  maintenanceChecklistDateFieldSchema,
  maintenanceChecklistTimeFieldSchema,
  maintenanceChecklistAttachmentFieldSchema
]);
var maintenanceChecklistSectionSchema = z.object({
  id: z.string().trim().min(1).max(80),
  title: z.string().trim().min(1).max(200),
  sortOrder: z.number().int().min(0),
  fields: z.array(maintenanceChecklistFieldSchema).default([])
});
var maintenanceChecklistSignatureSettingsSchema = z.object({
  requireCustomerSignature: z.boolean().default(false)
});
var maintenanceChecklistDefinitionSchema = z.object({
  schemaVersion: z.literal(MAINTENANCE_CHECKLIST_SCHEMA_VERSION),
  sections: z.array(maintenanceChecklistSectionSchema).default([]),
  signatureSettings: maintenanceChecklistSignatureSettingsSchema.optional()
});
function maintenanceDefinitionRequiresCustomerSignature(definition) {
  const parsed = maintenanceChecklistDefinitionSchema.safeParse(definition);
  return parsed.success ? parsed.data.signatureSettings?.requireCustomerSignature ?? false : false;
}
var emptyMaintenanceChecklistDefinition = () => maintenanceChecklistDefinitionSchema.parse({
  schemaVersion: MAINTENANCE_CHECKLIST_SCHEMA_VERSION,
  sections: []
});
var INPUT_FIELD_TYPES = /* @__PURE__ */ new Set([
  "boolean",
  "choice",
  "short_text",
  "long_text",
  "integer",
  "decimal",
  "measurement",
  "date",
  "time",
  "attachment"
]);
function isInputField(field) {
  return INPUT_FIELD_TYPES.has(field.type);
}
function validateMaintenanceChecklistDefinitionForPublish(raw, context) {
  const parsed = maintenanceChecklistDefinitionSchema.safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      issues: parsed.error.issues.map((issue) => ({
        path: issue.path.join("."),
        message: issue.message
      }))
    };
  }
  const definition = parsed.data;
  const issues = [];
  const sectionIds = /* @__PURE__ */ new Set();
  const fieldKeys = /* @__PURE__ */ new Set();
  let inputFieldCount = 0;
  for (const section of definition.sections) {
    if (sectionIds.has(section.id)) {
      issues.push({ path: `sections.${section.id}`, message: "Abschnitts-ID ist doppelt." });
    }
    sectionIds.add(section.id);
    const fieldOrders = /* @__PURE__ */ new Set();
    for (const field of section.fields) {
      if (fieldKeys.has(field.fieldKey)) {
        issues.push({
          path: `field.${field.fieldKey}`,
          message: "fieldKey ist nicht eindeutig."
        });
      }
      fieldKeys.add(field.fieldKey);
      if (fieldOrders.has(field.sortOrder)) {
        issues.push({
          path: `field.${field.fieldKey}`,
          message: "sortOrder ist innerhalb des Abschnitts doppelt."
        });
      }
      fieldOrders.add(field.sortOrder);
      if (isInputField(field)) {
        inputFieldCount += 1;
      }
      if (context.kind === "structured" && field.placement) {
        issues.push({
          path: `field.${field.fieldKey}`,
          message: "Strukturierte Vorlagen d\xFCrfen keine PDF-Platzierung haben."
        });
      }
      if ((context.kind === "pdf_overlay" || context.kind === "pdf_acroform") && isInputField(field) && !field.placement) {
        issues.push({
          path: `field.${field.fieldKey}`,
          message: "PDF-Felder ben\xF6tigen eine Platzierung."
        });
      }
      if (field.placement && context.pageCount != null && field.placement.page > context.pageCount) {
        issues.push({
          path: `field.${field.fieldKey}`,
          message: "Die Seitennummer liegt au\xDFerhalb des PDFs."
        });
      }
      if (field.type === "choice") {
        const values = /* @__PURE__ */ new Set();
        for (const opt of field.options) {
          if (values.has(opt.value)) {
            issues.push({
              path: `field.${field.fieldKey}`,
              message: "Auswahlwerte m\xFCssen eindeutig sein."
            });
          }
          values.add(opt.value);
        }
      }
      if (field.type === "integer" || field.type === "decimal" || field.type === "measurement") {
        const min = field.min;
        const max = field.max;
        if (min !== void 0 && max !== void 0 && min > max) {
          issues.push({
            path: `field.${field.fieldKey}`,
            message: "Min darf nicht gr\xF6\xDFer als Max sein."
          });
        }
      }
    }
  }
  if (context.kind !== "pdf_ink" && inputFieldCount === 0) {
    issues.push({
      path: "sections",
      message: "Mindestens ein Eingabefeld ist erforderlich."
    });
  }
  if (issues.length > 0) {
    return { ok: false, issues };
  }
  return { ok: true, definition };
}
function validateMaintenanceChecklistFieldValue(field, value) {
  if (field.type === "info") {
    return { ok: true };
  }
  const issues = [];
  const empty = value === null || value === void 0 || value === "" || Array.isArray(value) && value.length === 0;
  if (field.required && empty) {
    issues.push({ path: field.fieldKey, message: "Pflichtfeld." });
    return { ok: false, issues };
  }
  if (empty) {
    return { ok: true };
  }
  switch (field.type) {
    case "boolean":
      if (typeof value !== "boolean") {
        issues.push({ path: field.fieldKey, message: "Erwartet Ja/Nein." });
      }
      break;
    case "choice":
      if (field.allowMultiple) {
        if (!Array.isArray(value) || !value.every((v) => typeof v === "string")) {
          issues.push({ path: field.fieldKey, message: "Ung\xFCltige Mehrfachauswahl." });
        }
      } else if (typeof value !== "string") {
        issues.push({ path: field.fieldKey, message: "Ung\xFCltige Auswahl." });
      }
      break;
    case "short_text":
    case "long_text":
      if (typeof value !== "string") {
        issues.push({ path: field.fieldKey, message: "Erwartet Text." });
      } else if (value.length > field.maxLength) {
        issues.push({
          path: field.fieldKey,
          message: `Text ist zu lang (h\xF6chstens ${field.maxLength} Zeichen).`
        });
      }
      break;
    case "integer":
      if (typeof value !== "number" || !Number.isInteger(value)) {
        issues.push({ path: field.fieldKey, message: "Erwartet Ganzzahl." });
      } else {
        if (field.min !== void 0 && value < field.min) {
          issues.push({
            path: field.fieldKey,
            message: `Wert liegt unter dem Minimum (${field.min}).`
          });
        }
        if (field.max !== void 0 && value > field.max) {
          issues.push({
            path: field.fieldKey,
            message: `Wert liegt \xFCber dem Maximum (${field.max}).`
          });
        }
      }
      break;
    case "decimal":
    case "measurement":
      if (typeof value !== "number" || Number.isNaN(value)) {
        issues.push({ path: field.fieldKey, message: "Erwartet Zahl." });
      } else {
        if (field.min !== void 0 && value < field.min) {
          issues.push({
            path: field.fieldKey,
            message: `Wert liegt unter dem Minimum (${field.min}).`
          });
        }
        if (field.max !== void 0 && value > field.max) {
          issues.push({
            path: field.fieldKey,
            message: `Wert liegt \xFCber dem Maximum (${field.max}).`
          });
        }
      }
      break;
    case "date":
      if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        issues.push({ path: field.fieldKey, message: "Erwartet Datum (YYYY-MM-DD)." });
      }
      break;
    case "time":
      if (typeof value !== "string" || !/^\d{2}:\d{2}$/.test(value)) {
        issues.push({ path: field.fieldKey, message: "Erwartet Uhrzeit (HH:MM)." });
      }
      break;
    case "attachment":
      if (typeof value !== "string" || value.length < 1) {
        issues.push({ path: field.fieldKey, message: "Erwartet Anhang-Referenz." });
      }
      break;
  }
  if (issues.length > 0) {
    return { ok: false, issues };
  }
  return { ok: true };
}
function maintenancePdfPlacementToPixels(placement, pageWidthPx, pageHeightPx) {
  return {
    left: placement.x * pageWidthPx,
    top: placement.y * pageHeightPx,
    width: placement.width * pageWidthPx,
    height: placement.height * pageHeightPx
  };
}
function maintenancePdfPixelsToPlacement(rect, page, pageWidthPx, pageHeightPx) {
  if (pageWidthPx <= 0 || pageHeightPx <= 0) {
    throw new Error("INVALID_PAGE_SIZE");
  }
  const raw = {
    page,
    x: rect.left / pageWidthPx,
    y: rect.top / pageHeightPx,
    width: rect.width / pageWidthPx,
    height: rect.height / pageHeightPx
  };
  return maintenancePdfPlacementSchema.parse(raw);
}

// src/maintenance.ts
var emptyToUndefined7 = (value) => value === "" || value === "all" ? void 0 : value;
var emptyToNull3 = (value) => value === "" ? null : value;
var maintenanceDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var maintenanceAddressSchema = z.object({
  line1: z.string().trim().min(1).max(200),
  line2: z.preprocess(emptyToNull3, z.union([z.string().trim().max(200), z.null()]).optional()),
  postalCode: z.string().trim().min(1).max(20),
  city: z.string().trim().min(1).max(100),
  country: z.string().trim().length(2).default("DE")
});
var maintenanceCustomerSchema = z.object({
  id: z.string().uuid(),
  customerNumber: z.string().nullable(),
  name: z.string(),
  address: maintenanceAddressSchema,
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var maintenanceCustomerSummarySchema = z.object({
  id: z.string().uuid(),
  customerNumber: z.string().nullable(),
  name: z.string(),
  isActive: z.boolean()
});
var maintenanceProjectSchema = z.object({
  id: z.string().uuid(),
  customerId: z.string().uuid(),
  customer: maintenanceCustomerSummarySchema,
  projectNumber: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  siteAddress: maintenanceAddressSchema,
  isActive: z.boolean(),
  installationCount: z.number().int().nonnegative().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var maintenanceInstallationSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  project: z.object({
    id: z.string().uuid(),
    name: z.string(),
    customer: maintenanceCustomerSummarySchema
  }),
  installationNumber: z.string(),
  name: z.string(),
  installationType: z.string(),
  manufacturer: z.string().nullable(),
  model: z.string().nullable(),
  serialNumber: z.string().nullable(),
  location: z.string(),
  commissionedOn: maintenanceDateSchema.nullable(),
  isActive: z.boolean(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var maintenanceInstallationHistoryItemSchema = z.object({
  id: z.string().uuid(),
  orderNumber: z.string(),
  status: z.enum(["planned", "assigned", "in_progress", "finalizing", "completed", "cancelled"]),
  protocolId: z.string().uuid(),
  templateName: z.string(),
  templateVersionNumber: z.number().int().positive(),
  assignedTo: z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    profilePhotoUrl: z.string().nullable().optional()
  }),
  scheduledFor: maintenanceDateSchema.nullable(),
  performedOn: maintenanceDateSchema.nullable(),
  completedAt: z.iso.datetime().nullable(),
  hasFinalPdf: z.boolean()
});
var maintenanceInstallationHistorySchema = z.object({
  items: z.array(maintenanceInstallationHistoryItemSchema)
});
var optionalNullableText2 = (max) => z.preprocess(emptyToNull3, z.union([z.string().trim().max(max), z.null()]).optional());
var createMaintenanceCustomerRequestSchema = z.object({
  customerNumber: optionalNullableText2(40),
  name: z.string().trim().min(1).max(200),
  address: maintenanceAddressSchema,
  contactName: optionalNullableText2(120),
  contactEmail: optionalNullableText2(200),
  contactPhone: optionalNullableText2(40),
  isActive: z.boolean().default(true)
});
var updateMaintenanceCustomerRequestSchema = createMaintenanceCustomerRequestSchema.partial().refine((value) => Object.keys(value).length > 0, {
  message: "Mindestens ein Feld ist erforderlich."
});
var createMaintenanceProjectRequestSchema = z.object({
  customerId: z.string().uuid(),
  projectNumber: optionalNullableText2(40),
  name: z.string().trim().min(1).max(200),
  description: optionalNullableText2(4e3),
  siteAddress: maintenanceAddressSchema,
  isActive: z.boolean().default(true)
});
var updateMaintenanceProjectRequestSchema = createMaintenanceProjectRequestSchema.omit({ customerId: true }).partial().extend({ customerId: z.string().uuid().optional() }).refine((value) => Object.keys(value).length > 0, {
  message: "Mindestens ein Feld ist erforderlich."
});
var maintenanceMetadataSchema = z.record(z.string(), z.unknown()).refine((value) => JSON.stringify(value).length <= 8e3, {
  message: "Metadaten sind zu gro\xDF."
});
var createMaintenanceInstallationRequestSchema = z.object({
  projectId: z.string().uuid(),
  installationNumber: z.string().trim().min(1).max(60),
  name: z.string().trim().min(1).max(200),
  installationType: z.string().trim().min(1).max(120),
  manufacturer: optionalNullableText2(120),
  model: optionalNullableText2(120),
  serialNumber: optionalNullableText2(120),
  location: z.string().trim().min(1).max(300),
  commissionedOn: z.preprocess(emptyToNull3, z.union([maintenanceDateSchema, z.null()]).optional()),
  isActive: z.boolean().default(true),
  metadata: z.preprocess(emptyToNull3, maintenanceMetadataSchema.nullable().optional())
});
var updateMaintenanceInstallationRequestSchema = createMaintenanceInstallationRequestSchema.omit({ projectId: true }).partial().extend({ projectId: z.string().uuid().optional() }).refine((value) => Object.keys(value).length > 0, {
  message: "Mindestens ein Feld ist erforderlich."
});
var maintenanceCustomerListQuerySchema = paginationQuerySchema.extend({
  q: z.preprocess(emptyToUndefined7, z.string().trim().max(200).optional()),
  isActive: z.preprocess(
    (value) => value === "true" ? true : value === "false" ? false : void 0,
    z.boolean().optional()
  ),
  sortBy: z.enum(["name", "customerNumber", "updatedAt"]).default("name"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var maintenanceProjectListQuerySchema = paginationQuerySchema.extend({
  q: z.preprocess(emptyToUndefined7, z.string().trim().max(200).optional()),
  customerId: z.preprocess(emptyToUndefined7, z.string().uuid().optional()),
  isActive: z.preprocess(
    (value) => value === "true" ? true : value === "false" ? false : void 0,
    z.boolean().optional()
  ),
  sortBy: z.enum(["name", "projectNumber", "updatedAt"]).default("name"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var maintenanceInstallationListQuerySchema = paginationQuerySchema.extend({
  q: z.preprocess(emptyToUndefined7, z.string().trim().max(200).optional()),
  projectId: z.preprocess(emptyToUndefined7, z.string().uuid().optional()),
  customerId: z.preprocess(emptyToUndefined7, z.string().uuid().optional()),
  isActive: z.preprocess(
    (value) => value === "true" ? true : value === "false" ? false : void 0,
    z.boolean().optional()
  ),
  sortBy: z.enum(["name", "installationNumber", "updatedAt"]).default("name"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var maintenanceCustomerListResponseSchema = paginatedSchema(maintenanceCustomerSchema);
var maintenanceProjectListResponseSchema = paginatedSchema(maintenanceProjectSchema);
var maintenanceInstallationListResponseSchema = paginatedSchema(
  maintenanceInstallationSchema
);
var MAINTENANCE_TEMPLATE_PDF_MIME = "application/pdf";
var MAINTENANCE_TEMPLATE_PDF_MAX_BYTES = 25 * 1024 * 1024;
var MAINTENANCE_TEMPLATE_PDF_MAX_PAGES = 100;
var maintenanceTemplateKindSchema = z.enum([
  "structured",
  "pdf_overlay",
  "pdf_ink",
  "pdf_acroform"
]);
var maintenanceTemplateVersionStatusSchema = z.enum(["draft", "published", "retired"]);
var maintenanceTemplateDefinitionSchema = maintenanceChecklistDefinitionSchema;
var maintenanceTemplateVersionSchema = z.object({
  id: z.string().uuid(),
  templateId: z.string().uuid(),
  versionNumber: z.number().int().positive(),
  status: maintenanceTemplateVersionStatusSchema,
  definition: maintenanceTemplateDefinitionSchema,
  sourcePdfFileName: z.string().nullable(),
  sourcePdfMimeType: z.string().nullable(),
  sourcePdfSizeBytes: z.number().int().nonnegative().nullable(),
  sourcePdfSha256: z.string().nullable(),
  sourcePdfScanStatus: z.enum(["PENDING", "CLEAN", "BLOCKED", "SKIPPED"]).nullable(),
  pageCount: z.number().int().positive().nullable(),
  hasSourcePdf: z.boolean(),
  /** True when a work order or protocol still points at this version. */
  inUse: z.boolean(),
  createdById: z.string().uuid(),
  createdAt: z.iso.datetime(),
  publishedById: z.string().uuid().nullable(),
  publishedAt: z.iso.datetime().nullable()
});
var maintenanceAcroformUnsupportedWidgetSchema = z.object({
  name: z.string().min(1).max(500),
  reason: z.string().min(1).max(500)
});
var maintenanceTemplateSourcePdfUploadResponseSchema = maintenanceTemplateVersionSchema.extend({
  unsupportedWidgets: z.array(maintenanceAcroformUnsupportedWidgetSchema)
});
var maintenanceTemplateScopeSchema = z.object({
  id: z.string().uuid(),
  templateId: z.string().uuid(),
  projectId: z.string().uuid().nullable(),
  installationId: z.string().uuid().nullable(),
  createdAt: z.iso.datetime(),
  installation: z.object({
    id: z.string().uuid(),
    installationNumber: z.string(),
    name: z.string(),
    location: z.string()
  }).nullable().optional(),
  project: z.object({
    id: z.string().uuid(),
    name: z.string()
  }).nullable().optional()
});
var maintenanceTemplateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  kind: maintenanceTemplateKindSchema,
  isActive: z.boolean(),
  currentPublishedVersionId: z.string().uuid().nullable(),
  createdById: z.string().uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  /** Open work orders that still reference a version of this template. */
  usageCount: z.number().int().nonnegative()
});
function maintenanceTemplateDeleteBlock(usageCount) {
  if (usageCount <= 0) {
    return null;
  }
  if (usageCount === 1) {
    return "Die Vorlage wird noch von einem offenen Auftrag verwendet und kann nicht gel\xF6scht werden.";
  }
  return `Die Vorlage wird noch von ${usageCount} offenen Auftr\xE4gen verwendet und kann nicht gel\xF6scht werden.`;
}
function maintenanceTemplateRetentionMonths(raw) {
  if (raw === void 0 || raw === null) {
    return 0;
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed === "") {
      return 0;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0) {
      return 0;
    }
    return parsed;
  }
  if (!Number.isInteger(raw) || raw < 0) {
    return 0;
  }
  return raw;
}
function maintenanceTemplateCleanupCutoff(now, retentionMonths) {
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - retentionMonths);
  return cutoff;
}
function maintenanceTemplateCleanupDecision(input) {
  if (input.usageCount > 0) {
    return null;
  }
  if (input.updatedAt.getTime() > input.cutoff.getTime()) {
    return null;
  }
  if (input.versionStatuses.length === 0) {
    return null;
  }
  if (input.versionStatuses.every((status) => status === "draft")) {
    return "delete";
  }
  if (input.versionStatuses.some((status) => status === "published" || status === "retired")) {
    return "archive";
  }
  return null;
}
var maintenanceTemplateDetailSchema = maintenanceTemplateSchema.extend({
  versions: z.array(maintenanceTemplateVersionSchema),
  scopes: z.array(maintenanceTemplateScopeSchema)
});
var createMaintenanceTemplateRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: optionalNullableText2(4e3),
  kind: maintenanceTemplateKindSchema
});
var updateMaintenanceTemplateRequestSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: optionalNullableText2(4e3),
  isActive: z.boolean().optional()
}).refine((value) => Object.keys(value).length > 0, {
  message: "Mindestens ein Feld ist erforderlich."
});
var updateMaintenanceTemplateVersionRequestSchema = z.object({
  definition: maintenanceTemplateDefinitionSchema.optional()
}).refine((value) => Object.keys(value).length > 0, {
  message: "Mindestens ein Feld ist erforderlich."
});
var createMaintenanceTemplateScopeRequestSchema = z.object({
  projectId: z.preprocess(emptyToUndefined7, z.string().uuid().optional()),
  installationId: z.preprocess(emptyToUndefined7, z.string().uuid().optional())
}).refine((value) => Boolean(value.projectId) !== Boolean(value.installationId), {
  message: "Genau ein Ziel (Projekt oder Anlage) ist erforderlich."
});
var maintenanceTemplateListQuerySchema = paginationQuerySchema.extend({
  q: z.preprocess(emptyToUndefined7, z.string().trim().max(200).optional()),
  isActive: z.preprocess(
    (value) => value === "true" ? true : value === "false" ? false : void 0,
    z.boolean().optional()
  ),
  kind: z.preprocess(emptyToUndefined7, maintenanceTemplateKindSchema.optional()),
  sortBy: z.enum(["name", "updatedAt"]).default("name"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var maintenanceTemplateListResponseSchema = paginatedSchema(maintenanceTemplateSchema);
var masterDataDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var businessPartnerRoleSchema = z.enum(["supplier", "customer"]);
var businessPartnerAddressPurposeSchema = z.enum([
  "registered",
  "billing",
  "shipping",
  "other"
]);
var paymentTermBaselineSchema = z.enum(["document_date", "month_end"]);
var currencyRoundingModeSchema = z.enum(["half_up"]);
var unitDimensionSchema = z.enum(["count", "length", "mass", "volume", "time"]);
var paymentMethodCodeSchema = z.enum(["bank_transfer", "direct_debit"]);
var PARTNER_NUMBER_PATTERN = /^GP-\d{4}-\d{6}$/;
function formatPartnerNumber(year, sequence) {
  return `GP-${year}-${String(sequence).padStart(6, "0")}`;
}
function isPartnerNumber(value) {
  return PARTNER_NUMBER_PATTERN.test(value);
}
function isoFromUtc(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function utcDate(iso) {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}
function addCalendarDays2(iso, days) {
  const date = utcDate(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return isoFromUtc(date);
}
function endOfMonth(iso) {
  const [year, month] = iso.split("-").map(Number);
  return isoFromUtc(new Date(Date.UTC(year, month, 0)));
}
function paymentDueOn(documentOn, baseline, netDays) {
  const start = baseline === "month_end" ? endOfMonth(documentOn) : documentOn;
  return addCalendarDays2(start, netDays);
}
function discountMinorUnits(amountMinor, discountBps) {
  if (!Number.isInteger(amountMinor) || amountMinor <= 0) {
    throw new Error("Skonto gilt nur f\xFCr einen positiven Ganzzahlbetrag.");
  }
  if (!Number.isInteger(discountBps) || discountBps < 1 || discountBps > 1e4) {
    throw new Error("Skonto liegt zwischen 1 und 10.000 Basispunkten.");
  }
  const discountMinor = Math.floor((amountMinor * discountBps + 5e3) / 1e4);
  return { discountMinor, payableMinor: amountMinor - discountMinor };
}
function roundHalfUpMinor(amountMinor, step) {
  if (!Number.isInteger(amountMinor) || !Number.isInteger(step) || step <= 0) {
    throw new Error("Die Rundung braucht einen Ganzzahlbetrag und eine positive Schrittweite.");
  }
  const sign = amountMinor < 0 ? -1 : 1;
  const abs = Math.abs(amountMinor);
  const quotient = Math.floor((abs + Math.floor(step / 2)) / step);
  return sign * quotient * step;
}
function reduceUnitFactor(numerator, denominator) {
  if (!Number.isInteger(numerator) || !Number.isInteger(denominator) || numerator <= 0 || denominator <= 0) {
    return null;
  }
  const divisor = greatestCommonDivisor(numerator, denominator);
  return { numerator: numerator / divisor, denominator: denominator / divisor };
}
function greatestCommonDivisor(left, right) {
  let a = left;
  let b = right;
  while (b !== 0) {
    const next = a % b;
    a = b;
    b = next;
  }
  return a;
}
function masterDataIntervalsOverlap(left, right) {
  const leftEnd = left.validTo ?? "9999-12-31";
  const rightEnd = right.validTo ?? "9999-12-31";
  return left.validFrom <= rightEnd && right.validFrom <= leftEnd;
}
function isValidIban(raw) {
  const compact = raw.replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(compact) || compact.length > 34) {
    return false;
  }
  const rearranged = `${compact.slice(4)}${compact.slice(0, 4)}`;
  let remainder = 0;
  for (const char of rearranged) {
    const digits = char >= "A" && char <= "Z" ? String(char.charCodeAt(0) - 55) : char;
    for (const digit of digits) {
      remainder = (remainder * 10 + Number(digit)) % 97;
    }
  }
  return remainder === 1;
}
function isValidVatId(country, vatId) {
  const countryCode = country.trim().toUpperCase();
  const value = vatId.replace(/\s+/g, "").toUpperCase();
  if (!value) {
    return false;
  }
  if (countryCode === "DE") {
    return /^DE\d{9}$/.test(value);
  }
  return true;
}
var snapshotAddressSchema = z.object({
  id: z.string().uuid(),
  addressKey: z.string().uuid(),
  purpose: businessPartnerAddressPurposeSchema,
  isPrimary: z.boolean(),
  line1: z.string(),
  line2: z.string().nullable(),
  postalCode: z.string(),
  city: z.string(),
  country: z.string(),
  validFrom: masterDataDateSchema,
  validTo: masterDataDateSchema.nullable()
});
var snapshotBankSchema = z.object({
  id: z.string().uuid(),
  bankKey: z.string().uuid(),
  accountHolder: z.string(),
  ibanLast4: z.string(),
  configured: z.literal(true),
  bic: z.string().nullable(),
  bankName: z.string().nullable(),
  currencyCode: z.string(),
  isPrimary: z.boolean(),
  validFrom: masterDataDateSchema,
  validTo: masterDataDateSchema.nullable()
});
var snapshotTaxSchema = z.object({
  id: z.string().uuid(),
  taxKey: z.string().uuid(),
  country: z.string(),
  vatIdLast4: z.string().nullable(),
  configured: z.literal(true),
  validFrom: masterDataDateSchema,
  validTo: masterDataDateSchema.nullable()
});
var masterDataSnapshotInputSchema = z.object({
  asOf: masterDataDateSchema,
  partner: z.object({
    id: z.string().uuid(),
    partnerNumber: z.string(),
    isActive: z.boolean()
  }),
  name: z.object({
    id: z.string().uuid(),
    legalName: z.string(),
    tradeName: z.string().nullable(),
    validFrom: masterDataDateSchema,
    validTo: masterDataDateSchema.nullable()
  }),
  addresses: z.array(snapshotAddressSchema),
  contacts: z.array(
    z.object({
      id: z.string().uuid(),
      contactKey: z.string().uuid(),
      name: z.string(),
      email: z.string().nullable(),
      phone: z.string().nullable(),
      functionTitle: z.string().nullable(),
      isPrimary: z.boolean(),
      validFrom: masterDataDateSchema,
      validTo: masterDataDateSchema.nullable()
    })
  ),
  bankAccounts: z.array(snapshotBankSchema),
  taxProfiles: z.array(snapshotTaxSchema)
});
function buildMasterDataSnapshot(input) {
  return structuredClone(masterDataSnapshotInputSchema.parse(input));
}
var optionalDate2 = masterDataDateSchema.nullable().optional();
var masterDataDuplicateCandidateSchema = z.object({
  id: z.string().uuid(),
  partnerNumber: z.string(),
  legalName: z.string()
});
var masterDataDuplicateDetailsSchema = z.object({
  candidates: z.array(masterDataDuplicateCandidateSchema)
});
var masterDataCsvRequestSchema = z.object({
  csv: z.string().trim().min(1).max(1e6)
});
var masterDataImportErrorSchema = z.object({
  line: z.number().int(),
  message: z.string()
});
var masterDataImportPreviewSchema = z.object({
  errors: z.array(masterDataImportErrorSchema),
  rows: z.array(
    z.object({
      line: z.number().int(),
      externalKey: z.string(),
      legalName: z.string(),
      status: z.enum(["new", "existing"]),
      partnerNumber: z.string().nullable()
    })
  )
});
var masterDataImportResultSchema = z.object({
  items: z.array(
    z.object({
      externalKey: z.string(),
      id: z.string().uuid(),
      partnerNumber: z.string(),
      created: z.boolean()
    })
  )
});
var masterDataExportSchema = z.object({
  filename: z.string(),
  mimeType: z.string(),
  csv: z.string()
});
var createBusinessPartnerRequestSchema = z.object({
  partnerNumber: z.string().regex(PARTNER_NUMBER_PATTERN).optional(),
  legalName: z.string().trim().min(1).max(200),
  tradeName: z.string().trim().min(1).max(200).nullable().optional(),
  validFrom: masterDataDateSchema,
  acknowledgeDuplicate: z.boolean().optional()
});
var patchBusinessPartnerRequestSchema = z.object({
  isActive: z.boolean().optional(),
  legalName: z.string().trim().min(1).max(200).optional(),
  tradeName: z.string().trim().min(1).max(200).nullable().optional(),
  effectiveFrom: masterDataDateSchema.optional(),
  acknowledgeDuplicate: z.boolean().optional()
});
var createBusinessPartnerRoleRequestSchema = z.object({
  role: businessPartnerRoleSchema,
  validFrom: masterDataDateSchema,
  validTo: optionalDate2
});
var patchBusinessPartnerRoleRequestSchema = z.object({
  isActive: z.boolean().optional(),
  validFrom: masterDataDateSchema.optional(),
  validTo: optionalDate2,
  effectiveFrom: masterDataDateSchema.optional()
});
var addressBody = {
  purpose: businessPartnerAddressPurposeSchema,
  isPrimary: z.boolean().default(false),
  line1: z.string().trim().min(1).max(200),
  line2: z.string().trim().max(200).nullable().optional(),
  postalCode: z.string().trim().min(1).max(20),
  city: z.string().trim().min(1).max(120),
  country: z.string().trim().length(2),
  validFrom: masterDataDateSchema,
  validTo: optionalDate2
};
var createBusinessPartnerAddressRequestSchema = z.object(addressBody);
var patchBusinessPartnerAddressRequestSchema = z.object({
  ...addressBody,
  validFrom: masterDataDateSchema.optional(),
  effectiveFrom: masterDataDateSchema.optional()
});
var createBusinessPartnerContactRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  email: z.string().trim().email().nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  functionTitle: z.string().trim().max(120).nullable().optional(),
  isPrimary: z.boolean().default(false),
  validFrom: masterDataDateSchema,
  validTo: optionalDate2
});
var patchBusinessPartnerContactRequestSchema = createBusinessPartnerContactRequestSchema.partial().extend({
  effectiveFrom: masterDataDateSchema.optional()
});
var createBusinessPartnerBankAccountRequestSchema = z.object({
  accountHolder: z.string().trim().min(1).max(200),
  iban: z.string().trim().min(15).max(42),
  bic: z.string().trim().max(11).nullable().optional(),
  bankName: z.string().trim().max(200).nullable().optional(),
  currencyCode: z.string().trim().length(3),
  isPrimary: z.boolean().default(false),
  validFrom: masterDataDateSchema,
  validTo: optionalDate2
});
var patchBusinessPartnerBankAccountRequestSchema = createBusinessPartnerBankAccountRequestSchema.partial().extend({
  effectiveFrom: masterDataDateSchema.optional()
});
var createBusinessPartnerTaxProfileRequestSchema = z.object({
  country: z.string().trim().length(2),
  vatId: z.string().trim().min(1).max(32),
  taxId: z.string().trim().min(1).max(64).nullable().optional(),
  validFrom: masterDataDateSchema,
  validTo: optionalDate2
});
var patchBusinessPartnerTaxProfileRequestSchema = createBusinessPartnerTaxProfileRequestSchema.partial().extend({
  effectiveFrom: masterDataDateSchema.optional()
});
var createBusinessPartnerSettlementRequestSchema = z.object({
  paymentTermId: z.string().uuid(),
  paymentMethodId: z.string().uuid(),
  validFrom: masterDataDateSchema,
  validTo: optionalDate2
});
var patchBusinessPartnerSettlementRequestSchema = createBusinessPartnerSettlementRequestSchema.partial().extend({
  effectiveFrom: masterDataDateSchema.optional()
});
var masterDataListQuerySchema = z.object({
  includeInactive: z.enum(["true", "false"]).optional().transform((value) => value === "true")
});
var masterDataAsOfQuerySchema = z.object({
  asOf: masterDataDateSchema.optional()
});
var paymentTermDiscountSchema = z.object({
  days: z.number().int().nonnegative(),
  discountBps: z.number().int().min(1).max(1e4)
});
var createPaymentTermRequestSchema = z.object({
  code: z.string().trim().min(1).max(40),
  baseline: paymentTermBaselineSchema,
  netDays: z.number().int().nonnegative(),
  validFrom: masterDataDateSchema,
  discounts: z.array(paymentTermDiscountSchema).default([])
});
var patchPaymentTermRequestSchema = z.object({
  isActive: z.boolean().optional(),
  baseline: paymentTermBaselineSchema.optional(),
  netDays: z.number().int().nonnegative().optional(),
  discounts: z.array(paymentTermDiscountSchema).optional(),
  effectiveFrom: masterDataDateSchema.optional()
});
var createPaymentMethodRequestSchema = z.object({
  code: paymentMethodCodeSchema,
  name: z.string().trim().min(1).max(120)
});
var patchPaymentMethodRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  isActive: z.boolean().optional()
});
var createCurrencyRequestSchema = z.object({
  code: z.string().trim().length(3),
  minorUnits: z.number().int().min(0).max(4),
  validFrom: masterDataDateSchema,
  roundingMode: currencyRoundingModeSchema.default("half_up")
});
var patchCurrencyRequestSchema = z.object({
  isActive: z.boolean().optional(),
  roundingMode: currencyRoundingModeSchema.optional(),
  effectiveFrom: masterDataDateSchema.optional()
});
var createUnitRequestSchema = z.object({
  code: z.string().trim().min(1).max(16),
  name: z.string().trim().min(1).max(120),
  dimension: unitDimensionSchema
});
var patchUnitRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  isActive: z.boolean().optional()
});
var createUnitConversionRequestSchema = z.object({
  fromUnitId: z.string().uuid(),
  toUnitId: z.string().uuid(),
  numerator: z.number().int().positive(),
  denominator: z.number().int().positive(),
  validFrom: masterDataDateSchema,
  validTo: optionalDate2
});
var patchUnitConversionRequestSchema = createUnitConversionRequestSchema.partial().extend({ effectiveFrom: masterDataDateSchema.optional() });
var createCostCenterRequestSchema = z.object({
  code: z.string().trim().min(1).max(40),
  name: z.string().trim().min(1).max(200),
  validFrom: masterDataDateSchema
});
var patchCostCenterRequestSchema = z.object({
  isActive: z.boolean().optional(),
  name: z.string().trim().min(1).max(200).optional(),
  effectiveFrom: masterDataDateSchema.optional()
});
var responseDate = z.string();
var businessPartnerListItemSchema = z.object({
  id: z.string().uuid(),
  partnerNumber: z.string(),
  isActive: z.boolean(),
  legalName: z.string().nullable(),
  inactiveLabel: z.string().nullable()
});
var businessPartnerListSchema = z.object({
  items: z.array(businessPartnerListItemSchema)
});
var bankAccountResponseSchema = z.object({
  id: z.string().uuid(),
  bankKey: z.string().uuid(),
  accountHolder: z.string(),
  ibanLast4: z.string(),
  configured: z.literal(true),
  iban: z.string().optional(),
  bic: z.string().nullable(),
  bankName: z.string().nullable(),
  currencyCode: z.string(),
  isPrimary: z.boolean(),
  validFrom: responseDate.nullable(),
  validTo: responseDate.nullable()
});
var businessPartnerDetailSchema = z.object({
  id: z.string().uuid(),
  partnerNumber: z.string(),
  isActive: z.boolean(),
  inactiveLabel: z.string().nullable(),
  names: z.array(
    z.object({
      id: z.string().uuid(),
      legalName: z.string(),
      tradeName: z.string().nullable(),
      validFrom: responseDate.nullable(),
      validTo: responseDate.nullable()
    })
  ),
  roles: z.array(
    z.object({
      id: z.string().uuid(),
      role: businessPartnerRoleSchema,
      isActive: z.boolean(),
      validFrom: responseDate.nullable(),
      validTo: responseDate.nullable()
    })
  ),
  addresses: z.array(
    z.object({
      id: z.string().uuid(),
      addressKey: z.string().uuid(),
      purpose: businessPartnerAddressPurposeSchema,
      isPrimary: z.boolean(),
      line1: z.string(),
      line2: z.string().nullable(),
      postalCode: z.string(),
      city: z.string(),
      country: z.string(),
      validFrom: responseDate.nullable(),
      validTo: responseDate.nullable()
    })
  ),
  contacts: z.array(
    z.object({
      id: z.string().uuid(),
      contactKey: z.string().uuid(),
      name: z.string(),
      email: z.string().nullable(),
      phone: z.string().nullable(),
      functionTitle: z.string().nullable(),
      isPrimary: z.boolean(),
      validFrom: responseDate.nullable(),
      validTo: responseDate.nullable()
    })
  ),
  bankAccounts: z.array(bankAccountResponseSchema),
  taxProfiles: z.array(
    z.object({
      id: z.string().uuid(),
      taxKey: z.string().uuid(),
      country: z.string(),
      vatIdLast4: z.string().nullable(),
      configured: z.literal(true),
      vatId: z.string().optional(),
      taxId: z.string().optional(),
      validFrom: responseDate.nullable(),
      validTo: responseDate.nullable()
    })
  ),
  settlements: z.array(
    z.object({
      id: z.string().uuid(),
      paymentTermId: z.string().uuid(),
      paymentMethodId: z.string().uuid(),
      paymentTermCode: z.string(),
      paymentMethodName: z.string(),
      validFrom: responseDate.nullable(),
      validTo: responseDate.nullable(),
      paymentTermVersion: z.object({
        id: z.string().uuid(),
        baseline: paymentTermBaselineSchema,
        netDays: z.number().int(),
        validFrom: responseDate.nullable(),
        validTo: responseDate.nullable(),
        discounts: z.array(z.object({ days: z.number().int(), discountBps: z.number().int() }))
      }).nullable().optional()
    })
  )
});
var paymentTermListSchema = z.object({
  items: z.array(
    z.object({
      id: z.string().uuid(),
      code: z.string(),
      isActive: z.boolean(),
      versions: z.array(
        z.object({
          id: z.string().uuid(),
          baseline: paymentTermBaselineSchema,
          netDays: z.number().int(),
          validFrom: responseDate,
          validTo: responseDate.nullable(),
          discounts: z.array(z.object({ days: z.number().int(), discountBps: z.number().int() }))
        })
      )
    })
  )
});
var paymentMethodListSchema = z.object({
  items: z.array(
    z.object({
      id: z.string().uuid(),
      code: paymentMethodCodeSchema,
      name: z.string(),
      isActive: z.boolean()
    })
  )
});
var currencyListSchema = z.object({
  items: z.array(
    z.object({
      id: z.string().uuid(),
      code: z.string(),
      minorUnits: z.number().int(),
      isActive: z.boolean(),
      roundingVersions: z.array(
        z.object({
          roundingMode: currencyRoundingModeSchema,
          validFrom: responseDate,
          validTo: responseDate.nullable()
        })
      )
    })
  )
});
var unitListSchema = z.object({
  items: z.array(
    z.object({
      id: z.string().uuid(),
      code: z.string(),
      name: z.string(),
      dimension: unitDimensionSchema,
      isActive: z.boolean()
    })
  )
});
var unitConversionListSchema = z.object({
  items: z.array(
    z.object({
      id: z.string().uuid(),
      fromUnitId: z.string().uuid(),
      toUnitId: z.string().uuid(),
      numerator: z.number().int(),
      denominator: z.number().int(),
      validFrom: responseDate,
      validTo: responseDate.nullable()
    })
  )
});
var costCenterListSchema = z.object({
  items: z.array(
    z.object({
      id: z.string().uuid(),
      code: z.string(),
      isActive: z.boolean(),
      versions: z.array(
        z.object({
          name: z.string(),
          validFrom: responseDate,
          validTo: responseDate.nullable()
        })
      )
    })
  )
});
var articleTypeSchema = z.enum(["material", "service"]);
var articleTaxClassSchema = z.enum(["standard", "reduced", "zero", "exempt"]);
var ARTICLE_NUMBER_PATTERN = /^AR-\d{4}-\d{6}$/;
function formatArticleNumber(year, sequence) {
  return `AR-${year}-${String(sequence).padStart(6, "0")}`;
}
function isArticleNumber(value) {
  return ARTICLE_NUMBER_PATTERN.test(value);
}
function formatArticlePriceMinor(priceMinor, currencyCode, minorUnits) {
  const digits = Math.min(4, Math.max(0, minorUnits));
  const sign = priceMinor < 0 ? "-" : "";
  const text = String(Math.abs(priceMinor)).padStart(digits + 1, "0");
  const whole = digits === 0 ? text : text.slice(0, -digits);
  const fraction = digits === 0 ? "" : text.slice(-digits);
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const amount = fraction.length > 0 ? `${grouped},${fraction}` : grouped;
  return `${sign}${amount} ${currencyCode}`;
}
function articleRequiresUnitConversion(baseUnitId, purchaseUnitId) {
  return baseUnitId !== purchaseUnitId;
}
function assertArticleStockManaged(type, stockManaged) {
  if (type === "service" && stockManaged) {
    throw new Error("Eine Dienstleistung kann nicht lagergef\xFChrt sein.");
  }
}
var optionalDate3 = masterDataDateSchema.nullable().optional();
var emptyToUndefined8 = (value) => value === "" || value === "all" ? void 0 : value;
var queryFlag = z.enum(["true", "false"]).optional().transform((value) => value == null ? void 0 : value === "true");
function stockManagedIssue(type, stockManaged) {
  if (type === "service" && stockManaged === true) {
    return "Eine Dienstleistung kann nicht lagergef\xFChrt sein.";
  }
  return null;
}
var articleSnapshotPriceSchema = z.object({
  priceMinor: z.number().int().positive(),
  currencyCode: z.string().trim().length(3),
  unitId: z.string().uuid(),
  validFrom: masterDataDateSchema,
  validTo: masterDataDateSchema.nullable()
});
var articleSnapshotInputSchema = z.object({
  asOf: masterDataDateSchema,
  article: z.object({
    id: z.string().uuid(),
    articleNumber: z.string(),
    type: articleTypeSchema,
    isActive: z.boolean()
  }),
  name: z.object({
    id: z.string().uuid(),
    name: z.string(),
    validFrom: masterDataDateSchema,
    validTo: masterDataDateSchema.nullable()
  }),
  baseUnitId: z.string().uuid(),
  purchaseUnitId: z.string().uuid(),
  price: articleSnapshotPriceSchema.nullable()
});
function buildArticleSnapshot(input) {
  return structuredClone(articleSnapshotInputSchema.parse(input));
}
var optionalEan = z.string().trim().min(8).max(14).regex(/^\d+$/, "Die EAN enth\xE4lt nur Ziffern.").nullable().optional();
var articleDocumentKindSchema = z.enum(["document", "image", "datasheet"]);
var createArticleRequestSchema = z.object({
  articleNumber: z.string().regex(ARTICLE_NUMBER_PATTERN).optional(),
  name: z.string().trim().min(1).max(200),
  validFrom: masterDataDateSchema,
  type: articleTypeSchema,
  groupId: z.string().uuid(),
  baseUnitId: z.string().uuid(),
  purchaseUnitId: z.string().uuid(),
  packUnitId: z.string().uuid().nullable().optional(),
  stockManaged: z.boolean(),
  taxClass: articleTaxClassSchema,
  costCenterId: z.string().uuid().nullable().optional(),
  ean: optionalEan
}).superRefine((value, ctx) => {
  const message = stockManagedIssue(value.type, value.stockManaged);
  if (message) {
    ctx.addIssue({ code: "custom", message, path: ["stockManaged"] });
  }
});
var patchArticleRequestSchema = z.object({
  isActive: z.boolean().optional(),
  name: z.string().trim().min(1).max(200).optional(),
  effectiveFrom: masterDataDateSchema.optional(),
  type: articleTypeSchema.optional(),
  groupId: z.string().uuid().optional(),
  baseUnitId: z.string().uuid().optional(),
  purchaseUnitId: z.string().uuid().optional(),
  packUnitId: z.string().uuid().nullable().optional(),
  stockManaged: z.boolean().optional(),
  taxClass: articleTaxClassSchema.optional(),
  costCenterId: z.string().uuid().nullable().optional(),
  ean: optionalEan
}).superRefine((value, ctx) => {
  const message = stockManagedIssue(value.type, value.stockManaged);
  if (message) {
    ctx.addIssue({ code: "custom", message, path: ["stockManaged"] });
  }
});
var createArticleGroupRequestSchema = z.object({
  code: z.string().trim().min(1).max(40),
  name: z.string().trim().min(1).max(200),
  parentId: z.string().uuid().nullable().optional()
});
var patchArticleGroupRequestSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  isActive: z.boolean().optional(),
  parentId: z.string().uuid().nullable().optional()
});
var createArticleSupplierRequestSchema = z.object({
  partnerId: z.string().uuid(),
  supplierArticleNumber: z.string().trim().min(1).max(80),
  isActive: z.boolean().optional(),
  isPreferred: z.boolean().optional()
});
var patchArticleSupplierRequestSchema = z.object({
  supplierArticleNumber: z.string().trim().min(1).max(80).optional(),
  isActive: z.boolean().optional(),
  isPreferred: z.boolean().optional()
});
var createArticlePurchasePriceRequestSchema = z.object({
  articleSupplierId: z.string().uuid(),
  currencyId: z.string().uuid(),
  unitId: z.string().uuid(),
  priceMinor: z.number().int().positive(),
  minQuantity: z.number().int().positive().default(1),
  discountBps: z.number().int().min(1).max(1e4).nullable().optional(),
  validFrom: masterDataDateSchema,
  validTo: optionalDate3
});
var createArticleUnitFactorRequestSchema = z.object({
  fromUnitId: z.string().uuid(),
  toUnitId: z.string().uuid(),
  numerator: z.number().int().positive(),
  denominator: z.number().int().positive(),
  validFrom: masterDataDateSchema,
  validTo: optionalDate3
});
var articleListSortBySchema = z.enum(["articleNumber", "name"]);
var articleListQuerySchema = paginationQuerySchema.extend({
  q: z.preprocess(emptyToUndefined8, z.string().trim().max(200).optional()),
  includeInactive: z.enum(["true", "false"]).optional().transform((value) => value === "true"),
  type: z.preprocess(emptyToUndefined8, articleTypeSchema.optional()),
  groupId: z.preprocess(emptyToUndefined8, z.string().uuid().optional()),
  stockManaged: queryFlag,
  sortBy: articleListSortBySchema.default("articleNumber"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var responseDate2 = z.string();
var articleListItemSchema = z.object({
  id: z.string().uuid(),
  articleNumber: z.string(),
  name: z.string().nullable(),
  type: articleTypeSchema,
  groupId: z.string().uuid(),
  groupName: z.string().nullable(),
  stockManaged: z.boolean(),
  isActive: z.boolean(),
  inactiveLabel: z.string().nullable()
});
var articleListResponseSchema = paginatedSchema(articleListItemSchema);
var articleNameSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  validFrom: responseDate2.nullable(),
  validTo: responseDate2.nullable()
});
var articleSupplierSchema = z.object({
  id: z.string().uuid(),
  partnerId: z.string().uuid(),
  partnerNumber: z.string().nullable(),
  supplierArticleNumber: z.string(),
  isActive: z.boolean(),
  isPreferred: z.boolean()
});
var articlePurchasePriceSchema = z.object({
  id: z.string().uuid(),
  articleSupplierId: z.string().uuid(),
  currencyId: z.string().uuid(),
  currencyCode: z.string(),
  minorUnits: z.number().int().min(0).max(4),
  unitId: z.string().uuid(),
  priceMinor: z.number().int().positive(),
  minQuantity: z.number().int().positive(),
  discountBps: z.number().int().min(1).max(1e4).nullable(),
  validFrom: responseDate2.nullable(),
  validTo: responseDate2.nullable()
});
var articleUnitFactorSchema = z.object({
  id: z.string().uuid(),
  fromUnitId: z.string().uuid(),
  toUnitId: z.string().uuid(),
  numerator: z.number().int().positive(),
  denominator: z.number().int().positive(),
  validFrom: responseDate2.nullable(),
  validTo: responseDate2.nullable()
});
var articleDocumentSchema = z.object({
  id: z.string().uuid(),
  kind: articleDocumentKindSchema,
  fileName: z.string(),
  mimeType: z.string(),
  byteSize: z.number().int().nonnegative(),
  scanStatus: z.enum(["PENDING", "CLEAN", "BLOCKED", "SKIPPED"]),
  createdAt: z.string()
});
var articleDetailSchema = z.object({
  id: z.string().uuid(),
  articleNumber: z.string(),
  type: articleTypeSchema,
  groupId: z.string().uuid(),
  baseUnitId: z.string().uuid(),
  purchaseUnitId: z.string().uuid(),
  packUnitId: z.string().uuid().nullable(),
  stockManaged: z.boolean(),
  taxClass: articleTaxClassSchema,
  costCenterId: z.string().uuid().nullable(),
  ean: z.string().nullable(),
  isActive: z.boolean(),
  inactiveLabel: z.string().nullable(),
  names: z.array(articleNameSchema),
  suppliers: z.array(articleSupplierSchema),
  unitFactors: z.array(articleUnitFactorSchema),
  documents: z.array(articleDocumentSchema),
  prices: z.array(articlePurchasePriceSchema).optional()
});
var articlePriceListSchema = z.object({
  items: z.array(articlePurchasePriceSchema)
});
var articleGroupListItemSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  parentId: z.string().uuid().nullable()
});
var articleGroupListSchema = z.object({
  items: z.array(articleGroupListItemSchema)
});
var articleSupplierOptionSchema = z.object({
  id: z.string().uuid(),
  partnerNumber: z.string(),
  legalName: z.string().nullable()
});
var articleSupplierOptionListSchema = z.object({
  items: z.array(articleSupplierOptionSchema)
});
var ARTICLE_FILE_MAX_BYTES = 10 * 1024 * 1024;
var emptyToUndefined9 = (value) => value === "" || value === "all" ? void 0 : value;
var maintenanceWorkOrderStatusSchema = z.enum([
  "planned",
  "assigned",
  "in_progress",
  "finalizing",
  "completed",
  "cancelled"
]);
var MAINTENANCE_WORK_ORDER_OPEN_STATUSES = [
  "planned",
  "assigned",
  "in_progress",
  "finalizing"
];
var MAINTENANCE_WORK_ORDER_CLOSED_STATUSES = [
  "completed",
  "cancelled"
];
var maintenanceWorkOrderPrioritySchema = z.enum(["low", "normal", "high"]);
var maintenanceProtocolEventTypeSchema = z.enum([
  "work_order_created",
  "technician_assigned",
  "technician_reassigned",
  "work_started",
  "work_order_cancelled",
  "technician_signed",
  "customer_signed",
  "finalization_started",
  "protocol_completed",
  "finalization_failed"
]);
var maintenanceUserSummarySchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string(),
  profilePhotoUrl: z.string().nullable().optional()
});
var maintenanceProtocolSnapshotSchema = z.object({
  templateVersionId: z.string().uuid(),
  templateId: z.string().uuid(),
  templateName: z.string(),
  templateKind: maintenanceTemplateKindSchema,
  versionNumber: z.number().int().positive(),
  definition: maintenanceChecklistDefinitionSchema
});
var maintenanceCustomerSnapshotSchema = maintenanceCustomerSummarySchema.extend({
  addressLine1: z.string(),
  addressLine2: z.string().nullable(),
  postalCode: z.string(),
  city: z.string(),
  country: z.string()
});
var maintenanceProjectSnapshotSchema = z.object({
  id: z.string().uuid(),
  projectNumber: z.string().nullable(),
  name: z.string(),
  siteLine1: z.string(),
  siteLine2: z.string().nullable(),
  sitePostalCode: z.string(),
  siteCity: z.string(),
  siteCountry: z.string()
});
var maintenanceInstallationSnapshotSchema = z.object({
  id: z.string().uuid(),
  installationNumber: z.string(),
  name: z.string(),
  installationType: z.string(),
  manufacturer: z.string().nullable(),
  model: z.string().nullable(),
  serialNumber: z.string().nullable(),
  location: z.string(),
  commissionedOn: maintenanceDateSchema.nullable()
});
var maintenanceProtocolSummarySchema = z.object({
  id: z.string().uuid(),
  workOrderId: z.string().uuid(),
  templateVersionId: z.string().uuid().nullable()
});
var maintenanceProtocolDetailSchema = maintenanceProtocolSummarySchema.extend({
  templateSnapshot: maintenanceProtocolSnapshotSchema,
  generalNotes: z.string().nullable(),
  customerSnapshot: maintenanceCustomerSnapshotSchema,
  projectSnapshot: maintenanceProjectSnapshotSchema,
  installationSnapshot: maintenanceInstallationSnapshotSchema
});
var maintenanceWorkOrderSchema = z.object({
  id: z.string().uuid(),
  orderNumber: z.string(),
  installationId: z.string().uuid(),
  templateVersionId: z.string().uuid().nullable(),
  assignedToId: z.string().uuid(),
  assignedTo: maintenanceUserSummarySchema,
  scheduledFor: maintenanceDateSchema.nullable(),
  dueAt: z.iso.datetime().nullable(),
  status: maintenanceWorkOrderStatusSchema,
  priority: maintenanceWorkOrderPrioritySchema,
  instructions: z.string().nullable(),
  requireTechnicianSignature: z.boolean(),
  requireCustomerSignature: z.boolean(),
  createdById: z.string().uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  cancellationReason: z.string().nullable(),
  archivedAt: z.iso.datetime().nullable(),
  trashedAt: z.iso.datetime().nullable(),
  revision: z.number().int().nonnegative(),
  customer: maintenanceCustomerSummarySchema,
  project: z.object({ id: z.string().uuid(), name: z.string() }),
  installation: z.object({
    id: z.string().uuid(),
    name: z.string(),
    installationNumber: z.string(),
    location: z.string()
  }),
  templateName: z.string(),
  templateVersionNumber: z.number().int().positive(),
  /** Answers, attachments (including ink) or signatures exist on the protocol. */
  protocolHasContent: z.boolean(),
  protocol: maintenanceProtocolSummarySchema
});
var maintenanceWorkOrderListItemSchema = maintenanceWorkOrderSchema;
var maintenanceWorkOrderDetailSchema = maintenanceWorkOrderSchema.extend({
  protocol: maintenanceProtocolDetailSchema
});
var maintenanceWorkOrderListResponseSchema = paginatedSchema(
  maintenanceWorkOrderListItemSchema
);
var createMaintenanceWorkOrderRequestSchema = z.object({
  installationId: z.string().uuid(),
  templateVersionId: z.string().uuid(),
  assignedToId: z.string().uuid(),
  scheduledFor: z.preprocess(emptyToUndefined9, maintenanceDateSchema.optional()),
  dueAt: z.preprocess(emptyToUndefined9, z.iso.datetime().optional()),
  priority: maintenanceWorkOrderPrioritySchema.default("normal"),
  instructions: z.preprocess(
    (v) => v === "" ? null : v,
    z.union([z.string().trim().max(4e3), z.null()]).optional()
  ),
  status: z.enum(["planned", "assigned"]).default("assigned"),
  requireTechnicianSignature: z.boolean().optional(),
  requireCustomerSignature: z.boolean().optional(),
  idempotencyKey: z.string().trim().min(8).max(80).optional()
});
var updateMaintenanceWorkOrderRequestSchema = z.object({
  revision: z.number().int().nonnegative(),
  assignedToId: z.string().uuid().optional(),
  scheduledFor: z.preprocess(
    emptyToUndefined9,
    z.union([maintenanceDateSchema, z.null()]).optional()
  ),
  dueAt: z.preprocess(emptyToUndefined9, z.union([z.iso.datetime(), z.null()]).optional()),
  priority: maintenanceWorkOrderPrioritySchema.optional(),
  instructions: z.preprocess(
    (v) => v === "" ? null : v,
    z.union([z.string().trim().max(4e3), z.null()]).optional()
  ),
  status: z.enum(["planned", "assigned"]).optional(),
  requireTechnicianSignature: z.boolean().optional(),
  requireCustomerSignature: z.boolean().optional(),
  confirmInProgressReassignment: z.boolean().optional()
}).refine((value) => Object.keys(value).filter((key) => key !== "revision").length > 0, {
  message: "Mindestens ein Feld ist erforderlich."
});
var cancelMaintenanceWorkOrderRequestSchema = z.object({
  revision: z.number().int().nonnegative(),
  reason: z.string().trim().min(3).max(500)
});
var maintenanceWorkOrderListQuerySchema = paginationQuerySchema.extend({
  status: z.preprocess(emptyToUndefined9, maintenanceWorkOrderStatusSchema.optional()),
  assignedToId: z.preprocess(emptyToUndefined9, z.string().uuid().optional()),
  customerId: z.preprocess(emptyToUndefined9, z.string().uuid().optional()),
  projectId: z.preprocess(emptyToUndefined9, z.string().uuid().optional()),
  installationId: z.preprocess(emptyToUndefined9, z.string().uuid().optional()),
  templateName: z.preprocess(emptyToUndefined9, z.string().trim().max(200).optional()),
  completedFrom: z.preprocess(emptyToUndefined9, maintenanceDateSchema.optional()),
  completedTo: z.preprocess(emptyToUndefined9, maintenanceDateSchema.optional()),
  scheduledFrom: z.preprocess(emptyToUndefined9, maintenanceDateSchema.optional()),
  scheduledTo: z.preprocess(emptyToUndefined9, maintenanceDateSchema.optional()),
  sortBy: z.enum([
    "scheduledFor",
    "dueAt",
    "createdAt",
    "completedAt",
    "orderNumber",
    "status",
    "customerName",
    "installationName",
    "assignedToName"
  ]).default("scheduledFor"),
  sortOrder: z.enum(["asc", "desc"]).default("asc"),
  includeArchived: z.preprocess((value) => {
    if (value === true || value === "true") {
      return true;
    }
    if (value === false || value === "false" || value === void 0 || value === "") {
      return false;
    }
    return value;
  }, z.boolean().default(false))
});
var MAINTENANCE_WORK_ORDER_TRANSITIONS = {
  planned: ["assigned", "cancelled"],
  assigned: ["in_progress", "cancelled", "planned"],
  in_progress: ["finalizing", "cancelled"],
  finalizing: ["completed"],
  completed: [],
  cancelled: []
};
var MAINTENANCE_WORK_ORDER_DELETE_IN_PROGRESS = "Dieser Auftrag wird bereits bearbeitet und kann nicht gel\xF6scht werden.";
var MAINTENANCE_WORK_ORDER_DELETE_COMPLETED = "Abgeschlossene Wartungsprotokolle werden nicht gel\xF6scht.";
var MAINTENANCE_WORK_ORDER_DELETE_CANCELLED = "Stornierte Auftr\xE4ge bleiben bestehen und werden nicht gel\xF6scht.";
var MAINTENANCE_WORK_ORDER_DELETE_HAS_ENTRIES = "Der Auftrag enth\xE4lt schon Eintr\xE4ge und kann nicht gel\xF6scht werden. Er kann storniert werden, solange er noch nicht gestartet ist.";
function maintenanceWorkOrderDeleteBlock(status, protocolHasContent) {
  if (status === "in_progress" || status === "finalizing") {
    return MAINTENANCE_WORK_ORDER_DELETE_IN_PROGRESS;
  }
  if (status === "completed") {
    return MAINTENANCE_WORK_ORDER_DELETE_COMPLETED;
  }
  if (status === "cancelled") {
    return MAINTENANCE_WORK_ORDER_DELETE_CANCELLED;
  }
  if (protocolHasContent) {
    return MAINTENANCE_WORK_ORDER_DELETE_HAS_ENTRIES;
  }
  return null;
}
function canTransitionMaintenanceWorkOrderStatus(from, to) {
  return MAINTENANCE_WORK_ORDER_TRANSITIONS[from].includes(to);
}
function mayStartMaintenanceWorkOrder(status) {
  return canTransitionMaintenanceWorkOrderStatus(status, "in_progress");
}
function mayEditMaintenanceWorkOrder(status) {
  return canTransitionMaintenanceWorkOrderStatus(status, "finalizing");
}
function mayFinalizeMaintenanceWorkOrder(status) {
  return mayEditMaintenanceWorkOrder(status) || canTransitionMaintenanceWorkOrderStatus(status, "completed");
}
var MAINTENANCE_PROTOCOL_ATTACHMENT_MAX_BYTES = 12 * 1024 * 1024;
var MAINTENANCE_PROTOCOL_ATTACHMENT_ALLOWED_MIME = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf"
];
var maintenanceProtocolAnswerSchema = z.object({
  fieldKey: z.string(),
  value: z.unknown(),
  comment: z.string().nullable().optional(),
  updatedAt: z.iso.datetime()
});
var maintenanceProtocolAttachmentSchema = z.object({
  id: z.string().uuid(),
  protocolId: z.string().uuid(),
  fieldKey: z.string().nullable(),
  fileName: z.string(),
  mimeType: z.string(),
  byteSize: z.number().int().nonnegative(),
  sha256: z.string(),
  scanStatus: z.enum(["PENDING", "CLEAN", "BLOCKED", "SKIPPED"]),
  uploadedById: z.string().uuid(),
  createdAt: z.iso.datetime()
});
var upsertMaintenanceProtocolAnswerRequestSchema = z.object({
  revision: z.number().int().nonnegative(),
  value: z.unknown(),
  comment: z.union([z.string().trim().max(500), z.null()]).optional().transform((v) => v === void 0 ? null : v)
});
var upsertMaintenanceProtocolAnswerResponseSchema = z.object({
  revision: z.number().int().nonnegative(),
  answer: maintenanceProtocolAnswerSchema
});
var MAINTENANCE_INK_MIME = "application/json";
var MAINTENANCE_INK_SCHEMA_VERSION = 1;
var MAINTENANCE_INK_COLORS = ["#1d4ed8", "#111827", "#b91c1c"];
var MAINTENANCE_INK_DEFAULT_COLOR = MAINTENANCE_INK_COLORS[0];
var MAINTENANCE_INK_WIDTHS = [2e-3, 4e-3, 8e-3];
var MAINTENANCE_INK_DEFAULT_WIDTH = MAINTENANCE_INK_WIDTHS[1];
var MAINTENANCE_INK_LEGACY_PDF_COLOR = { r: 0.11, g: 0.25, b: 0.72 };
var MAINTENANCE_INK_LEGACY_PDF_THICKNESS = 1.5;
var MAINTENANCE_INK_LEGACY_PDF_DOT = 1.2;
var maintenanceInkColorSchema = z.enum(MAINTENANCE_INK_COLORS);
var maintenanceInkWidthSchema = z.union([
  z.literal(2e-3),
  z.literal(4e-3),
  z.literal(8e-3)
]);
var maintenanceInkPointSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1)
});
var maintenanceInkStrokeSchema = z.object({
  points: z.array(maintenanceInkPointSchema).min(1).max(4e3),
  color: maintenanceInkColorSchema.optional(),
  width: maintenanceInkWidthSchema.optional()
});
function maintenanceInkPreviewStyle(stroke) {
  return {
    color: stroke.color ?? MAINTENANCE_INK_DEFAULT_COLOR,
    width: stroke.width ?? MAINTENANCE_INK_DEFAULT_WIDTH
  };
}
function maintenanceInkHexRgb(color) {
  const value = Number.parseInt(color.slice(1), 16);
  return {
    r: (value >> 16 & 255) / 255,
    g: (value >> 8 & 255) / 255,
    b: (value & 255) / 255
  };
}
function maintenanceInkPdfPaint(stroke, pageWidth) {
  if (stroke.color == null && stroke.width == null) {
    return {
      ...MAINTENANCE_INK_LEGACY_PDF_COLOR,
      thickness: MAINTENANCE_INK_LEGACY_PDF_THICKNESS,
      dotSize: MAINTENANCE_INK_LEGACY_PDF_DOT
    };
  }
  const rgb = maintenanceInkHexRgb(stroke.color ?? MAINTENANCE_INK_DEFAULT_COLOR);
  const thickness = (stroke.width ?? MAINTENANCE_INK_DEFAULT_WIDTH) * pageWidth;
  return { ...rgb, thickness, dotSize: thickness / 2 };
}
var WIN_ANSI_EXTRA = /* @__PURE__ */ new Set([
  338,
  339,
  352,
  353,
  376,
  381,
  382,
  402,
  710,
  732,
  8211,
  8212,
  8216,
  8217,
  8218,
  8220,
  8221,
  8222,
  8224,
  8225,
  8226,
  8230,
  8240,
  8249,
  8250,
  8364,
  8482
]);
var MAINTENANCE_INK_TEXT_MAX = 40;
var MAINTENANCE_INK_TEXT_FONT_RATIO = 0.02;
function maintenanceInkIsWinAnsiChar(char) {
  const code = char.codePointAt(0) ?? 0;
  if (code >= 32 && code <= 126) {
    return true;
  }
  if (code >= 160 && code <= 255) {
    return true;
  }
  return WIN_ANSI_EXTRA.has(code);
}
function maintenanceInkSanitizeText(value) {
  return [...value.replace(/\s+/g, " ")].filter((char) => maintenanceInkIsWinAnsiChar(char)).join("").trim().slice(0, 200);
}
var maintenanceInkTextSchema = z.object({
  id: z.string().uuid(),
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  text: z.string().transform((value) => maintenanceInkSanitizeText(value)).pipe(z.string().min(1).max(200)),
  color: maintenanceInkColorSchema.optional()
});
var maintenanceInkLayerSchema = z.object({
  schemaVersion: z.literal(MAINTENANCE_INK_SCHEMA_VERSION),
  page: z.number().int().min(1).max(100),
  strokes: z.array(maintenanceInkStrokeSchema).max(500),
  texts: z.array(maintenanceInkTextSchema).max(MAINTENANCE_INK_TEXT_MAX).optional()
});
function maintenanceInkFieldKey(page) {
  return `ink_${page}`;
}
var upsertMaintenanceInkLayerRequestSchema = z.object({
  revision: z.number().int().nonnegative(),
  strokes: z.array(maintenanceInkStrokeSchema).max(500),
  texts: z.array(maintenanceInkTextSchema).max(MAINTENANCE_INK_TEXT_MAX).optional()
});
var upsertMaintenanceInkLayerResponseSchema = z.object({
  revision: z.number().int().nonnegative(),
  layer: maintenanceInkLayerSchema
});
var maintenanceInkLayerListSchema = z.object({
  layers: z.array(maintenanceInkLayerSchema)
});
var maintenanceProtocolSignatureKindSchema = z.enum(["technician", "customer"]);
var maintenanceProtocolSignatureSchema = z.object({
  id: z.string().uuid(),
  protocolId: z.string().uuid(),
  kind: maintenanceProtocolSignatureKindSchema,
  userId: z.string().uuid().nullable(),
  signerName: z.string(),
  signerRole: z.string().nullable(),
  signedAt: z.iso.datetime()
});
var maintenanceProtocolEditorSchema = z.object({
  id: z.string().uuid(),
  workOrderId: z.string().uuid(),
  workOrderStatus: z.enum([
    "planned",
    "assigned",
    "in_progress",
    "finalizing",
    "completed",
    "cancelled"
  ]),
  revision: z.number().int().nonnegative(),
  generalNotes: z.string().nullable(),
  templateSnapshot: maintenanceProtocolSnapshotSchema,
  requireTechnicianSignature: z.boolean(),
  requireCustomerSignature: z.boolean(),
  finalizationError: z.string().nullable(),
  hasFinalPdf: z.boolean(),
  answers: z.array(maintenanceProtocolAnswerSchema),
  attachments: z.array(maintenanceProtocolAttachmentSchema),
  signatures: z.array(maintenanceProtocolSignatureSchema)
});
var maintenanceProtocolArchiveDetailSchema = z.object({
  id: z.string().uuid(),
  workOrderId: z.string().uuid(),
  orderNumber: z.string(),
  workOrderStatus: maintenanceWorkOrderStatusSchema,
  scheduledFor: z.string().nullable(),
  completedAt: z.iso.datetime().nullable(),
  templateSnapshot: maintenanceProtocolSnapshotSchema,
  customerSnapshot: maintenanceCustomerSnapshotSchema,
  projectSnapshot: maintenanceProjectSnapshotSchema,
  installationSnapshot: maintenanceInstallationSnapshotSchema,
  performedOn: z.string().nullable(),
  generalNotes: z.string().nullable(),
  hasFinalPdf: z.boolean(),
  finalPdfSha256: z.string().nullable(),
  finalPdfSizeBytes: z.number().int().nonnegative().nullable(),
  finalizedAt: z.iso.datetime().nullable(),
  answers: z.array(maintenanceProtocolAnswerSchema),
  attachments: z.array(maintenanceProtocolAttachmentSchema),
  signatures: z.array(maintenanceProtocolSignatureSchema),
  events: z.array(
    z.object({
      id: z.string().uuid(),
      type: maintenanceProtocolEventTypeSchema,
      at: z.iso.datetime(),
      actor: maintenanceUserSummarySchema,
      payload: z.unknown()
    })
  )
});
var finalizeMaintenanceWorkOrderRequestSchema = z.object({
  revision: z.number().int().nonnegative(),
  consentConfirmed: z.boolean().refine((value) => value === true, "maintenance.protocol.finalize.confirm")
});
var MAINTENANCE_PROTOCOL_SIGNATURE_FIELD_KEYS = {
  technician: "_signature_technician",
  customer: "_signature_customer"
};
function maintenanceProtocolRequiresCustomerSignature(snapshot) {
  return snapshot.definition.signatureSettings?.requireCustomerSignature ?? false;
}
var maintenanceProtocolValidationResultSchema = z.object({
  ok: z.boolean(),
  issues: z.array(
    z.object({
      fieldKey: z.string(),
      message: z.string()
    })
  )
});
function findMaintenanceChecklistField(snapshot, fieldKey) {
  for (const section of snapshot.definition.sections) {
    const field = section.fields.find((f) => f.fieldKey === fieldKey);
    if (field) {
      return maintenanceChecklistFieldSchema.parse(field);
    }
  }
  return null;
}
var INPUT_TYPES = /* @__PURE__ */ new Set([
  "boolean",
  "choice",
  "short_text",
  "long_text",
  "integer",
  "decimal",
  "measurement",
  "date",
  "time",
  "attachment"
]);
function isInputField2(field) {
  return INPUT_TYPES.has(field.type);
}
function attachmentSatisfiesField(attachment) {
  if (!attachment) {
    return false;
  }
  return attachment.scanStatus === "CLEAN" || attachment.scanStatus === "SKIPPED";
}
function validateMaintenanceProtocolCompleteness(snapshot, answers, attachments) {
  const answerMap = new Map(answers.map((a) => [a.fieldKey, a.value]));
  const attachmentByField = /* @__PURE__ */ new Map();
  for (const att of attachments) {
    if (att.fieldKey) {
      attachmentByField.set(att.fieldKey, att);
    }
  }
  const issues = [];
  for (const section of snapshot.definition.sections) {
    for (const field of section.fields) {
      if (!isInputField2(field)) {
        continue;
      }
      const value = answerMap.get(field.fieldKey);
      if (field.type === "attachment") {
        const att = attachmentByField.get(field.fieldKey);
        if (field.required && !attachmentSatisfiesField(att)) {
          issues.push({
            fieldKey: field.fieldKey,
            message: protocolFieldIssueMessage(
              field.label,
              att?.scanStatus === "PENDING" ? "Der Anhang wird noch gepr\xFCft." : "Der Pflichtanhang fehlt oder ist nicht freigegeben."
            )
          });
          continue;
        }
        if (value !== void 0 && value !== null && value !== "") {
          const check2 = validateMaintenanceChecklistFieldValue(field, value);
          if (!check2.ok) {
            issues.push({
              fieldKey: field.fieldKey,
              message: protocolFieldIssueMessage(
                field.label,
                check2.issues[0]?.message ?? "Ung\xFCltiger Anhang."
              )
            });
          }
        }
        continue;
      }
      const check = validateMaintenanceChecklistFieldValue(field, value);
      if (!check.ok) {
        issues.push({
          fieldKey: field.fieldKey,
          message: protocolFieldIssueMessage(
            field.label,
            check.issues[0]?.message === "Pflichtfeld." ? "Pflichtfeld, bitte ausf\xFCllen." : check.issues[0]?.message ?? "Ung\xFCltiger Wert."
          )
        });
      }
    }
  }
  return { ok: issues.length === 0, issues };
}
function validateMaintenanceProtocolForFinalize(snapshot, answers, attachments, signatures, requirements = {
  requireTechnicianSignature: true,
  requireCustomerSignature: maintenanceProtocolRequiresCustomerSignature(snapshot)
}) {
  const base = validateMaintenanceProtocolCompleteness(snapshot, answers, attachments);
  const issues = [...base.issues];
  const kinds = new Set(signatures.map((s) => s.kind));
  if (requirements.requireTechnicianSignature && !kinds.has("technician")) {
    issues.push({
      fieldKey: MAINTENANCE_PROTOCOL_SIGNATURE_FIELD_KEYS.technician,
      message: "Die Unterschrift des Monteurs fehlt."
    });
  }
  if (requirements.requireCustomerSignature && !kinds.has("customer")) {
    issues.push({
      fieldKey: MAINTENANCE_PROTOCOL_SIGNATURE_FIELD_KEYS.customer,
      message: "maintenance.protocol.customer-signature.missing"
    });
  }
  return { ok: issues.length === 0, issues };
}
function protocolFieldIssueMessage(label, detail) {
  const name = label.trim();
  return name ? `\u201E${name}\u201C: ${detail}` : detail;
}
function maintenanceProtocolIssuesMessage(issues) {
  const unique = [...new Set(issues.map((issue) => issue.message.trim()).filter(Boolean))];
  if (unique.length === 0) {
    return "Das Protokoll kann noch nicht abgeschlossen werden.";
  }
  return unique.join(" ");
}
var updateMaintenanceProtocolNotesRequestSchema = z.object({
  generalNotes: z.preprocess(
    (value) => value === "" ? null : value,
    z.union([z.string().trim().max(4e3), z.null()])
  )
});
var updateMaintenanceProtocolNotesResponseSchema = z.object({
  revision: z.number().int().nonnegative(),
  generalNotes: z.string().nullable()
});
function validateMaintenanceProtocolAnswerUpsert(snapshot, fieldKey, value) {
  const field = findMaintenanceChecklistField(snapshot, fieldKey);
  if (!field) {
    return {
      ok: false,
      issues: [{ path: fieldKey, message: "Unbekanntes Feld." }]
    };
  }
  if (field.type === "info") {
    return {
      ok: false,
      issues: [{ path: fieldKey, message: "Informationsfelder sind nicht beschreibbar." }]
    };
  }
  const check = validateMaintenanceChecklistFieldValue(field, value);
  if (!check.ok) {
    return {
      ok: false,
      issues: check.issues.map((issue) => ({
        path: issue.path,
        message: protocolFieldIssueMessage(
          field.label,
          issue.message === "Pflichtfeld." ? "Pflichtfeld, bitte ausf\xFCllen." : issue.message
        )
      }))
    };
  }
  return { ok: true, field };
}

// src/person-display-name.ts
function cleanPersonNamePart(value) {
  return (value ?? "").replace(/\s+/g, " ").trim().replace(/,+$/g, "").trim();
}
var ORG_HINT = /\b(gmbh|mbh|ag|kg|ug|e\.?\s*v\.?|inc|ltd|llc|&\s*co|co\.|company|gesellschaft)\b/i;
function looksLikeOrg(value) {
  return ORG_HINT.test(value);
}
function normalizeCommaPersonName(name) {
  const cleaned = cleanPersonNamePart(name);
  if (!cleaned) {
    return "";
  }
  const parts = cleaned.split(",").map((part) => cleanPersonNamePart(part)).filter((part) => part.length > 0);
  if (parts.length >= 3) {
    const last = parts[0];
    const first = parts[1];
    if (last && first) {
      return `${first} ${last}`;
    }
  }
  if (parts.length === 2) {
    const left = parts[0];
    const right = parts[1];
    if (!left || !right) {
      return cleaned;
    }
    if (looksLikeOrg(right)) {
      return left;
    }
    if (looksLikeOrg(left)) {
      return right;
    }
    return `${right} ${left}`;
  }
  return cleaned;
}
var SURNAME_PARTICLES = /* @__PURE__ */ new Set([
  "von",
  "van",
  "de",
  "zu",
  "da",
  "del",
  "der",
  "den",
  "ten",
  "ter",
  "und",
  "af",
  "auf"
]);
function sameName(left, right) {
  return left.localeCompare(right, "de", { sensitivity: "accent" }) === 0;
}
function surnameKey(name) {
  return name.toLowerCase().split(/[\s-]+/).filter((token) => token.length > 0 && !SURNAME_PARTICLES.has(token)).join("");
}
function emailNameHint(email) {
  const local = (email ?? "").split("@")[0] ?? "";
  const match = /^([a-zäöü])\.([a-zäöüß-]+)$/i.exec(local);
  if (!match?.[1] || !match[2]) {
    return null;
  }
  return { initial: match[1].toLowerCase(), surnameKey: match[2].toLowerCase().replace(/-/g, "") };
}
function surnameMatches(family, hintKey) {
  const key = surnameKey(family);
  return key.length > 0 && (key === hintKey || key.endsWith(hintKey) || hintKey.endsWith(key));
}
function nameAgrees(given, family, hint) {
  if (!given || !family) {
    return false;
  }
  if (!hint) {
    return true;
  }
  return given.toLowerCase().startsWith(hint.initial) && surnameMatches(family, hint.surnameKey);
}
function commaDirectoryName(displayName) {
  const cleaned = cleanPersonNamePart(displayName);
  if (!cleaned.includes(",")) {
    return null;
  }
  const parts = cleaned.split(",").map((part) => cleanPersonNamePart(part)).filter((part) => part.length > 0);
  if (parts.length >= 3) {
    const family = parts[0];
    const given = parts[1];
    if (family && given && !looksLikeOrg(family) && !looksLikeOrg(given)) {
      return { given, family };
    }
  }
  if (parts.length === 2) {
    const family = parts[0];
    const given = parts[1];
    if (family && given && !looksLikeOrg(family) && !looksLikeOrg(given)) {
      return { given, family };
    }
  }
  return null;
}
function named(given, family) {
  const firstName = given || family;
  const lastName = given && family ? family : "-";
  const displayName = given && family ? `${given} ${family}` : firstName;
  return { firstName, lastName, displayName };
}
function resolveDirectoryPerson(input) {
  const storedGiven = cleanPersonNamePart(input.firstName);
  const storedFamilyRaw = cleanPersonNamePart(input.lastName);
  const storedFamily = storedFamilyRaw === "-" ? "" : storedFamilyRaw;
  const hint = emailNameHint(input.email);
  const fromComma = commaDirectoryName(input.displayName);
  if (fromComma && nameAgrees(fromComma.given, fromComma.family, hint)) {
    if (sameName(storedGiven, fromComma.given) && sameName(storedFamily, fromComma.family)) {
      return named(storedGiven, storedFamily);
    }
    return named(fromComma.given, fromComma.family);
  }
  if (storedGiven && storedFamily && hint) {
    if (nameAgrees(storedGiven, storedFamily, hint)) {
      return named(storedGiven, storedFamily);
    }
    if (nameAgrees(storedFamily, storedGiven, hint)) {
      return named(storedFamily, storedGiven);
    }
  }
  if (storedGiven || storedFamily) {
    return named(storedGiven, storedFamily);
  }
  if (fromComma) {
    return named(fromComma.given, fromComma.family);
  }
  const normalized = normalizeCommaPersonName(input.displayName);
  if (!normalized) {
    return named("", "");
  }
  const split = splitNormalizedName(normalized);
  return named(split.given, split.family);
}
function splitNormalizedName(name) {
  const space = name.indexOf(" ");
  if (space === -1) {
    return { given: name, family: "" };
  }
  return { given: name.slice(0, space), family: name.slice(space + 1).trim() };
}
function partsFromInput(input) {
  const resolved = resolveDirectoryPerson(input);
  const given = resolved.firstName === "-" ? "" : resolved.firstName;
  const family = resolved.lastName === "-" ? "" : resolved.lastName;
  return { given, family };
}
function formatPersonName(input, voice) {
  const { given, family } = partsFromInput(input);
  if (voice.personNameParts === "given") {
    return given || family;
  }
  if (given && family) {
    return voice.personNameOrder === "family_given" ? `${family}, ${given}` : `${given} ${family}`;
  }
  return given || family;
}
function personDisplayName(input) {
  return formatPersonName(input, CANONICAL_PERSON_VOICE);
}
function personInitials(input, voice) {
  const { given, family } = partsFromInput(input);
  const familyLetter = voice.personNameParts === "given" ? "" : family.trim().charAt(0);
  const letters = `${given.trim().charAt(0)}${familyLetter}`.toLocaleUpperCase("de-DE");
  return letters || "?";
}

// src/voice-catalog/helpdesk.ts
var HELPDESK_VOICE_COPY = {
  "helpdesk.home.desk": {
    sie: "Ihre Anlaufstelle f\xFCr IT-Hilfe.",
    du: "Deine Anlaufstelle f\xFCr IT-Hilfe."
  },
  "helpdesk.home.sign-in": {
    sie: "Melden Sie sich an, um Hilfe zu finden oder eine Anfrage zu stellen.",
    du: "Melde dich an, um Hilfe zu finden oder eine Anfrage zu stellen."
  },
  "helpdesk.form.check": {
    sie: "Bitte pr\xFCfen Sie die Eingaben.",
    du: "Bitte pr\xFCfe die Eingaben."
  },
  "helpdesk.permission.required": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr diese Aktion.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr diese Aktion."
  },
  "helpdesk.permission.action.denied": {
    sie: "Sie haben keine Berechtigung f\xFCr diese Aktion.",
    du: "Du hast keine Berechtigung f\xFCr diese Aktion."
  },
  "helpdesk.editor.placeholder": {
    sie: "Beschreiben Sie das Anliegen \u2026",
    du: "Beschreibe das Anliegen \u2026"
  },
  "helpdesk.portal.home.lead": {
    sie: "Viele Anliegen sind in wenigen Minuten selbst gel\xF6st. Schauen Sie zuerst in der Hilfe nach. Wenn Sie uns brauchen, schreiben Sie uns eine Anfrage \u2014 wir melden uns.",
    du: "Viele Anliegen sind in wenigen Minuten selbst gel\xF6st. Schau zuerst in der Hilfe nach. Wenn du uns brauchst, schreib uns eine Anfrage \u2014 wir melden uns."
  },
  "helpdesk.portal.home.while-writing": {
    sie: "Wir schlagen passende L\xF6sungen vor, w\xE4hrend Sie schreiben.",
    du: "Wir schlagen passende L\xF6sungen vor, w\xE4hrend du schreibst."
  },
  "helpdesk.portal.home.pending-csat": {
    sie: "Einige Ihrer Anfragen sind geschlossen. Eine Sterne-Bewertung reicht \u2014 der Kommentar ist optional.",
    du: "Einige deiner Anfragen sind geschlossen. Eine Sterne-Bewertung reicht \u2014 der Kommentar ist optional."
  },
  "helpdesk.portal.home.rate": {
    sie: "Bitte bewerten Sie die Bearbeitung.",
    du: "Bitte bewerte die Bearbeitung."
  },
  "helpdesk.portal.home.recent": {
    sie: "Ihre letzten Anfragen",
    du: "Deine letzten Anfragen"
  },
  "helpdesk.portal.home.open-one": {
    sie: "Tippen Sie eine Anfrage an, um den Verlauf und Antworten der IT zu sehen.",
    du: "Tippe eine Anfrage an, um den Verlauf und Antworten der IT zu sehen."
  },
  "helpdesk.portal.home.none-faq": {
    sie: "Sie haben noch keine Anfrage gestellt. Oft hilft schon ein Blick in die FAQ oder die Anleitungen.",
    du: "Du hast noch keine Anfrage gestellt. Oft hilft schon ein Blick in die FAQ oder die Anleitungen."
  },
  "helpdesk.portal.home.none": {
    sie: "Sie haben noch keine Anfrage gestellt.",
    du: "Du hast noch keine Anfrage gestellt."
  },
  "helpdesk.portal.faq.lead": {
    sie: "Kurze Antworten, ohne Fachbegriffe. Tippen Sie eine Frage an, um die Erkl\xE4rung zu \xF6ffnen.",
    du: "Kurze Antworten, ohne Fachbegriffe. Tippe eine Frage an, um die Erkl\xE4rung zu \xF6ffnen."
  },
  "helpdesk.portal.faq.missing": {
    sie: "Ihre Frage war nicht dabei?",
    du: "Deine Frage war nicht dabei?"
  },
  "helpdesk.portal.faq.search-or-ask": {
    sie: "Durchsuchen Sie die Anleitungen oder schreiben Sie uns \u2014 wir helfen gerne.",
    du: "Durchsuche die Anleitungen oder schreib uns \u2014 wir helfen gerne."
  },
  "helpdesk.portal.kb.lead": {
    sie: "Suchen Sie nach einem Stichwort oder w\xE4hlen Sie ein Thema. Die Texte sind f\xFCr den Alltag geschrieben \u2014 ohne IT-Fachchinesisch.",
    du: "Suche nach einem Stichwort oder w\xE4hle ein Thema. Die Texte sind f\xFCr den Alltag geschrieben \u2014 ohne IT-Fachchinesisch."
  },
  "helpdesk.portal.kb.search": {
    sie: "Wonach suchen Sie?",
    du: "Wonach suchst du?"
  },
  "helpdesk.portal.kb.no-hit": {
    sie: "Nichts Passendes gefunden. Stellen Sie uns gerne eine Anfrage.",
    du: "Nichts Passendes gefunden. Stelle uns gerne eine Anfrage."
  },
  "helpdesk.portal.article.unsolved": {
    sie: "Hat das Ihr Anliegen nicht gel\xF6st?",
    du: "Hat das dein Anliegen nicht gel\xF6st?"
  },
  "helpdesk.portal.article.ask": {
    sie: "Kein Problem \u2014 schreiben Sie uns. Wir sehen, worum es geht, und helfen weiter.",
    du: "Kein Problem \u2014 schreib uns. Wir sehen, worum es geht, und helfen weiter."
  },
  "helpdesk.portal.tickets.only-yours": {
    sie: "Hier sehen Sie nur Ihre eigenen Anfragen. Sie k\xF6nnen antworten, Dateien anh\xE4ngen oder eine erledigte Anfrage wieder \xF6ffnen.",
    du: "Hier siehst du nur deine eigenen Anfragen. Du kannst antworten, Dateien anh\xE4ngen oder eine erledigte Anfrage wieder \xF6ffnen."
  },
  "helpdesk.portal.tickets.loading": {
    sie: "Ihre Anfragen werden geladen \u2026",
    du: "Deine Anfragen werden geladen \u2026"
  },
  "helpdesk.portal.tickets.empty": {
    sie: "Noch keine Anfragen. Schauen Sie zuerst in der Hilfe nach \u2014 oft ist die L\xF6sung schon da.",
    du: "Noch keine Anfragen. Schau zuerst in der Hilfe nach \u2014 oft ist die L\xF6sung schon da."
  },
  "helpdesk.portal.chat.greeting": {
    sie: "Hallo! Ich schaue in unseren Anleitungen nach. Fragen Sie zum Beispiel nach VPN, Passwort oder Drucker. Wenn ich nicht weiterwei\xDF, helfe ich Ihnen beim Schreiben einer Anfrage an die IT.",
    du: "Hallo! Ich schaue in unseren Anleitungen nach. Frage zum Beispiel nach VPN, Passwort oder Drucker. Wenn ich nicht weiterwei\xDF, helfe ich dir beim Schreiben einer Anfrage an die IT."
  },
  "helpdesk.portal.chat.offline": {
    sie: "Die IT-Hilfe ist gerade nicht erreichbar. Sie k\xF6nnen uns trotzdem eine Anfrage schreiben.",
    du: "Die IT-Hilfe ist gerade nicht erreichbar. Du kannst uns trotzdem eine Anfrage schreiben."
  },
  "helpdesk.portal.chat.question": {
    sie: "Ihre Frage",
    du: "Deine Frage"
  },
  "helpdesk.chat.no-hit": {
    sie: "In den Anleitungen habe ich dazu nichts Passendes gefunden. Stellen Sie uns gerne eine Anfrage \u2014 wir helfen pers\xF6nlich weiter.",
    du: "In den Anleitungen habe ich dazu nichts Passendes gefunden. Stelle uns gerne eine Anfrage \u2014 wir helfen pers\xF6nlich weiter."
  },
  "helpdesk.chat.sources": {
    sie: "Diese Anleitungen passen zu Ihrer Frage: {{titles}}. Wenn das nicht reicht, stellen Sie uns gerne eine Anfrage.",
    du: "Diese Anleitungen passen zu deiner Frage: {{titles}}. Wenn das nicht reicht, stelle uns gerne eine Anfrage."
  },
  "helpdesk.ticket.create.forbidden": {
    sie: "Sie d\xFCrfen keine Anfrage anlegen.",
    du: "Du darfst keine Anfrage anlegen."
  },
  "helpdesk.ticket.create.portal-lead": {
    sie: "Beschreiben Sie, wobei wir helfen k\xF6nnen. Rechts erscheinen passende Tipps \u2014 oft ist die L\xF6sung schon da.",
    du: "Beschreibe, wobei wir helfen k\xF6nnen. Rechts erscheinen passende Tipps \u2014 oft ist die L\xF6sung schon da."
  },
  "helpdesk.ticket.create.change-lead": {
    sie: "Risiko, Wartungsfenster und Rollback-Plan legen Sie hier fest. Nach dem Anlegen erscheint die \xC4nderung im Kalender.",
    du: "Risiko, Wartungsfenster und Rollback-Plan legst du hier fest. Nach dem Anlegen erscheint die \xC4nderung im Kalender."
  },
  "helpdesk.ticket.create.agent-lead": {
    sie: "Beschreiben Sie Ihr Anliegen. Rechts erscheinen passende L\xF6sungen aus der Wissensdatenbank.",
    du: "Beschreibe dein Anliegen. Rechts erscheinen passende L\xF6sungen aus der Wissensdatenbank."
  },
  "helpdesk.ticket.create.change-assets": {
    sie: "Assets verkn\xFCpfen Sie nach dem Anlegen auf der Ticketkarte. Der Change startet den CAB-Workflow (IT-Teamleitung, danach IT-Leitung).",
    du: "Assets verkn\xFCpfst du nach dem Anlegen auf der Ticketkarte. Der Change startet den CAB-Workflow (IT-Teamleitung, danach IT-Leitung)."
  },
  "helpdesk.ticket.create.remote-step": {
    sie: "3. Dort \u201ERemote-Support starten\u201C. Nach Ihrer Zustimmung am Rechner klickt der Agent \u201EMit RustDesk verbinden\u201C \u2014 ohne ID oder Passwort in diesem Portal.",
    du: "3. Dort \u201ERemote-Support starten\u201C. Nach deiner Zustimmung am Rechner klickt der Agent \u201EMit RustDesk verbinden\u201C \u2014 ohne ID oder Passwort in diesem Portal."
  },
  "helpdesk.ticket.created.portal": {
    sie: "Ihre Anfrage #{{number}} ist bei der IT eingegangen.",
    du: "Deine Anfrage #{{number}} ist bei der IT eingegangen."
  },
  "helpdesk.ticket.suggest.title": {
    sie: "Meinten Sie \u2026?",
    du: "Meintest du \u2026?"
  },
  "helpdesk.ticket.suggest.skip": {
    sie: "Diese L\xF6sung k\xF6nnte helfen \u2014 dann m\xFCssen Sie kein Ticket anlegen.",
    du: "Diese L\xF6sung k\xF6nnte helfen \u2014 dann musst du kein Ticket anlegen."
  },
  "helpdesk.ticket.search.prompt": {
    sie: "Wonach suchen Sie?",
    du: "Wonach suchst du?"
  },
  "helpdesk.ticket.messages.yours": {
    sie: "Ihre Nachrichten und Antworten der IT.",
    du: "Deine Nachrichten und Antworten der IT."
  },
  "helpdesk.ticket.from-you": {
    sie: "Von Ihnen: ",
    du: "Von dir: "
  },
  "helpdesk.ticket.resolved.check": {
    sie: "Bitte pr\xFCfen Sie die L\xF6sung. Ohne R\xFCckmeldung schlie\xDFen wir die Anfrage automatisch.",
    du: "Bitte pr\xFCfe die L\xF6sung. Ohne R\xFCckmeldung schlie\xDFen wir die Anfrage automatisch."
  },
  "helpdesk.ticket.reopen.hint": {
    sie: "Wenn das Thema wieder auftritt, k\xF6nnen Sie die Anfrage innerhalb der Frist erneut \xF6ffnen.",
    du: "Wenn das Thema wieder auftritt, kannst du die Anfrage innerhalb der Frist erneut \xF6ffnen."
  },
  "helpdesk.ticket.reopen.follow-up": {
    sie: "Die Frist zur Wiederer\xF6ffnung ist abgelaufen. Legen Sie ein neues Ticket zu diesem Vorgang an.",
    du: "Die Frist zur Wiederer\xF6ffnung ist abgelaufen. Lege ein neues Ticket zu diesem Vorgang an."
  },
  "helpdesk.ticket.comments.empty-portal": {
    sie: "Noch keine Nachrichten. Schreiben Sie uns gerne, wenn Sie etwas erg\xE4nzen m\xF6chten.",
    du: "Noch keine Nachrichten. Schreib uns gerne, wenn du etwas erg\xE4nzen m\xF6chtest."
  },
  "helpdesk.ticket.number.enter": {
    sie: "Bitte geben Sie eine Ticketnummer an.",
    du: "Bitte gib eine Ticketnummer an."
  },
  "helpdesk.ticket.describe.required": {
    sie: "Bitte beschreiben Sie das Anliegen.",
    du: "Bitte beschreibe das Anliegen."
  },
  "helpdesk.ticket.subject.min": {
    sie: "Bitte geben Sie einen Betreff mit mindestens 3 Zeichen ein.",
    du: "Bitte gib einen Betreff mit mindestens 3 Zeichen ein."
  },
  "helpdesk.ticket.amount": {
    sie: "Bitte geben Sie einen Betrag in Euro ein, z. B. 500 oder 500,00.",
    du: "Bitte gib einen Betrag in Euro ein, z. B. 500 oder 500,00."
  },
  "helpdesk.ticket.category.choose": {
    sie: "Bitte w\xE4hlen Sie eine Kategorie.",
    du: "Bitte w\xE4hle eine Kategorie."
  },
  "helpdesk.ticket.category.leaf": {
    sie: "Bitte w\xE4hlen Sie eine Unterkategorie, keine \xFCbergeordnete Gruppe.",
    du: "Bitte w\xE4hle eine Unterkategorie, keine \xFCbergeordnete Gruppe."
  },
  "helpdesk.ticket.notice.required": {
    sie: "Bitte geben Sie eine Mitteilung ein.",
    du: "Bitte gib eine Mitteilung ein."
  },
  "helpdesk.ticket.comment.required": {
    sie: "Bitte geben Sie einen Kommentar ein.",
    du: "Bitte gib einen Kommentar ein."
  },
  "helpdesk.ticket.assign.required": {
    sie: "Bitte weisen Sie einen Agenten oder eine Warteschlange zu.",
    du: "Bitte weise einen Agenten oder eine Warteschlange zu."
  },
  "helpdesk.ticket.requester.self": {
    sie: "Sie d\xFCrfen Tickets nur f\xFCr sich selbst erstellen.",
    du: "Du darfst Tickets nur f\xFCr dich selbst erstellen."
  },
  "helpdesk.ticket.reopen.expired": {
    sie: "Die Frist zur Wiederer\xF6ffnung ist abgelaufen. Bitte legen Sie ein neues, verkn\xFCpftes Ticket an.",
    du: "Die Frist zur Wiederer\xF6ffnung ist abgelaufen. Bitte lege ein neues, verkn\xFCpftes Ticket an."
  },
  "helpdesk.ticket.reopen-existing": {
    sie: "Bitte \xF6ffnen Sie das bestehende Ticket erneut. Ein Folgeticket ist erst nach Ablauf der Wiederer\xF6ffnungsfrist m\xF6glich.",
    du: "Bitte \xF6ffne das bestehende Ticket erneut. Ein Folgeticket ist erst nach Ablauf der Wiederer\xF6ffnungsfrist m\xF6glich."
  },
  "helpdesk.ticket.stale": {
    sie: "Das Ticket wurde zwischenzeitlich ge\xE4ndert. Bitte laden Sie die Ansicht neu.",
    du: "Das Ticket wurde zwischenzeitlich ge\xE4ndert. Bitte lade die Ansicht neu."
  },
  "helpdesk.ticket.assigned": {
    sie: "{{actor}} hat Ihnen das Ticket \u201E{{subject}}\u201C zugewiesen.",
    du: "{{actor}} hat dir das Ticket \u201E{{subject}}\u201C zugewiesen."
  },
  "helpdesk.ticket.inbound-assigned": {
    sie: "Eingehende Nachricht \u201E{{subject}}\u201C wurde Ihnen zugewiesen.",
    du: "Eingehende Nachricht \u201E{{subject}}\u201C wurde dir zugewiesen."
  },
  "helpdesk.ticket.resolved.reminder": {
    sie: "Ihre Anfrage \u201E{{subject}}\u201C ist als gel\xF6st markiert. Ohne R\xFCckmeldung schlie\xDFen wir sie am {{date}}. Antworten oder \xF6ffnen Sie das Ticket erneut, wenn das Thema noch offen ist.",
    du: "Deine Anfrage \u201E{{subject}}\u201C ist als gel\xF6st markiert. Ohne R\xFCckmeldung schlie\xDFen wir sie am {{date}}. Antworte oder \xF6ffne das Ticket erneut, wenn das Thema noch offen ist."
  },
  "helpdesk.incident.major-bundle": {
    sie: "Nach dem Speichern als Major k\xF6nnen Sie Tickets b\xFCndeln und eine Mitteilung an alle Betroffenen senden.",
    du: "Nach dem Speichern als Major kannst du Tickets b\xFCndeln und eine Mitteilung an alle Betroffenen senden."
  },
  "helpdesk.incident.resolve-children": {
    sie: "Bitte l\xF6sen Sie zuerst alle verkn\xFCpften Tickets, bevor Sie den Schweregrad auf Minor setzen.",
    du: "Bitte l\xF6se zuerst alle verkn\xFCpften Tickets, bevor du den Schweregrad auf Minor setzt."
  },
  "helpdesk.incident.major-first": {
    sie: "Bitte setzen Sie den Schweregrad zuerst auf Major.",
    du: "Bitte setze den Schweregrad zuerst auf Major."
  },
  "helpdesk.incident.field.min": {
    sie: "Bitte geben Sie mindestens ein Incident-Feld an.",
    du: "Bitte gib mindestens ein Incident-Feld an."
  },
  "helpdesk.incident.child-ref": {
    sie: "Bitte geben Sie eine Ticket-ID oder -nummer an.",
    du: "Bitte gib eine Ticket-ID oder -nummer an."
  },
  "helpdesk.problem.workaround": {
    sie: "Bitte hinterlegen Sie einen Workaround, bevor Sie den Status setzen.",
    du: "Bitte hinterlege einen Workaround, bevor du den Status setzt."
  },
  "helpdesk.problem.rca": {
    sie: "Bitte f\xFCllen Sie die Zusammenfassung der Ursachenanalyse aus.",
    du: "Bitte f\xFClle die Zusammenfassung der Ursachenanalyse aus."
  },
  "helpdesk.problem.resolution": {
    sie: "Bitte dokumentieren Sie die endg\xFCltige L\xF6sung getrennt vom Workaround.",
    du: "Bitte dokumentiere die endg\xFCltige L\xF6sung getrennt vom Workaround."
  },
  "helpdesk.problem.field.min": {
    sie: "Bitte geben Sie mindestens ein Problem-Feld an.",
    du: "Bitte gib mindestens ein Problem-Feld an."
  },
  "helpdesk.change.window.required": {
    sie: "Bitte geben Sie Start und Ende des Zeitfensters an. Das Ende darf nicht vor dem Start liegen.",
    du: "Bitte gib Start und Ende des Zeitfensters an. Das Ende darf nicht vor dem Start liegen."
  },
  "helpdesk.change.start.invalid": {
    sie: "Bitte geben Sie einen g\xFCltigen Startzeitpunkt an.",
    du: "Bitte gib einen g\xFCltigen Startzeitpunkt an."
  },
  "helpdesk.change.end.invalid": {
    sie: "Bitte geben Sie einen g\xFCltigen Endzeitpunkt an.",
    du: "Bitte gib einen g\xFCltigen Endzeitpunkt an."
  },
  "helpdesk.change.field.min": {
    sie: "Bitte geben Sie mindestens ein Change-Feld an.",
    du: "Bitte gib mindestens ein Change-Feld an."
  },
  "helpdesk.change.notes.after-approval": {
    sie: "Nach der Freigabe k\xF6nnen Sie hier die Umsetzung dokumentieren.",
    du: "Nach der Freigabe kannst du hier die Umsetzung dokumentieren."
  },
  "helpdesk.change.assets.use-card": {
    sie: " \u2014 nutzen Sie die Asset-Karte darunter.",
    du: " \u2014 nutze die Asset-Karte darunter."
  },
  "helpdesk.change.calendar.empty-month": {
    sie: "Es gibt keine Changes mit Zeitfenster f\xFCr {{month}}. Nutzen Sie den Tab \u201EOhne Fenster\u201C, um ungeplante Changes zu finden.",
    du: "Es gibt keine Changes mit Zeitfenster f\xFCr {{month}}. Nutze den Tab \u201EOhne Fenster\u201C, um ungeplante Changes zu finden."
  },
  "helpdesk.change.calendar.set-window": {
    sie: "Changes ohne Start/Ende erscheinen nicht im Kalender. Setzen Sie das Zeitfenster auf der Ticketkarte.",
    du: "Changes ohne Start/Ende erscheinen nicht im Kalender. Setze das Zeitfenster auf der Ticketkarte."
  },
  "helpdesk.approval.workflows.lead": {
    sie: "Legen Sie fest, welche Ticketart oder Kategorie eine mehrstufige Freigabe ausl\xF6st. Bis zur Entscheidung bleibt das Ticket pausiert; Genehmiger erhalten eine E-Mail mit Freigeben/Ablehnen.",
    du: "Lege fest, welche Ticketart oder Kategorie eine mehrstufige Freigabe ausl\xF6st. Bis zur Entscheidung bleibt das Ticket pausiert; Genehmiger erhalten eine E-Mail mit Freigeben/Ablehnen."
  },
  "helpdesk.approval.step.min": {
    sie: "Bitte legen Sie mindestens eine Stufe an.",
    du: "Bitte lege mindestens eine Stufe an."
  },
  "helpdesk.approval.step.workflow-min": {
    sie: "Bitte legen Sie mindestens eine Freigabestufe an.",
    du: "Bitte lege mindestens eine Freigabestufe an."
  },
  "helpdesk.approval.steps.locked": {
    sie: "Die Stufen k\xF6nnen nicht ersetzt werden, solange bereits Freigaben mit diesem Workflow existieren. Bearbeiten Sie einzelne Stufen.",
    du: "Die Stufen k\xF6nnen nicht ersetzt werden, solange bereits Freigaben mit diesem Workflow existieren. Bearbeite einzelne Stufen."
  },
  "helpdesk.approval.step.stale": {
    sie: "Diese Stufe ist nicht mehr aktuell. Bitte \xF6ffnen Sie das Ticket im Portal.",
    du: "Diese Stufe ist nicht mehr aktuell. Bitte \xF6ffne das Ticket im Portal."
  },
  "helpdesk.approval.not-your-turn": {
    sie: "Sie d\xFCrfen in dieser Stufe gerade nicht entscheiden.",
    du: "Du darfst in dieser Stufe gerade nicht entscheiden."
  },
  "helpdesk.approval.decide": {
    sie: "bitte entscheiden Sie \xFCber Ticket #{{number}}.",
    du: "bitte entscheide \xFCber Ticket #{{number}}."
  },
  "helpdesk.approval.requested": {
    sie: "Bitte geben Sie das Ticket \u201E{{subject}}\u201C frei oder lehnen Sie es ab (Workflow: {{workflow}}, Stufe: {{step}}).",
    du: "Bitte gib das Ticket \u201E{{subject}}\u201C frei oder lehne es ab (Workflow: {{workflow}}, Stufe: {{step}})."
  },
  "helpdesk.approval.rule.min": {
    sie: "Bitte w\xE4hlen Sie mindestens eine Regel: Ticketart, Kategorie, Mindestpriorit\xE4t oder Schwellenwert.",
    du: "Bitte w\xE4hle mindestens eine Regel: Ticketart, Kategorie, Mindestpriorit\xE4t oder Schwellenwert."
  },
  "helpdesk.csat.legend": {
    sie: "Ihre Bewertung",
    du: "Deine Bewertung"
  },
  "helpdesk.csat.thanks": {
    sie: "Vielen Dank f\xFCr Ihre Bewertung.",
    du: "Vielen Dank f\xFCr deine Bewertung."
  },
  "helpdesk.csat.satisfied": {
    sie: "Wie zufrieden waren Sie?",
    du: "Wie zufrieden warst du?"
  },
  "helpdesk.csat.your-rating": {
    sie: "Ihre Bewertung zu Ticket #{{number}}: {{score}}.",
    du: "Deine Bewertung zu Ticket #{{number}}: {{score}}."
  },
  "helpdesk.csat.closed-ask": {
    sie: "Ihr Ticket \u201E{{subject}}\u201C ist geschlossen. Wie zufrieden waren Sie mit der Bearbeitung?",
    du: "Dein Ticket \u201E{{subject}}\u201C ist geschlossen. Wie zufrieden warst du mit der Bearbeitung?"
  },
  "helpdesk.csat.already": {
    sie: "Sie haben bereits bewertet.",
    du: "Du hast bereits bewertet."
  },
  "helpdesk.dashboard.show-zone": {
    sie: "Blenden Sie Arbeit oder IT-Lagebild wieder ein, um das Dashboard zu nutzen.",
    du: "Blende Arbeit oder IT-Lagebild wieder ein, um das Dashboard zu nutzen."
  },
  "helpdesk.dashboard.no-open-tickets": {
    sie: "Sie haben gerade keine offenen Tickets.",
    du: "Du hast gerade keine offenen Tickets."
  },
  "helpdesk.dashboard.no-servers": {
    sie: "Keine Server-Assets im Inventar. Legen Sie Ger\xE4te vom Typ \u201EServer\u201C an.",
    du: "Keine Server-Assets im Inventar. Lege Ger\xE4te vom Typ \u201EServer\u201C an."
  },
  "helpdesk.kb.edit.forbidden": {
    sie: "Sie d\xFCrfen die Wissensdatenbank nicht bearbeiten.",
    du: "Du darfst die Wissensdatenbank nicht bearbeiten."
  },
  "helpdesk.kb.body.required": {
    sie: "Bitte geben Sie einen Artikelinhalt ein.",
    du: "Bitte gib einen Artikelinhalt ein."
  },
  "helpdesk.kb.ticket-ref": {
    sie: "Bitte geben Sie eine Ticketnummer oder -ID an.",
    du: "Bitte gib eine Ticketnummer oder -ID an."
  },
  "helpdesk.kb.proposal.before-apply": {
    sie: "Nichts geht live, bevor Sie \xFCbernehmen{{extra}}.",
    du: "Nichts geht live, bevor du \xFCbernimmst{{extra}}."
  },
  "helpdesk.kb.assistant.greeting": {
    sie: "Diktieren Sie eine \xC4nderung, zum Beispiel \u201EErg\xE4nze im Artikel VPN von unterwegs einen Hinweis auf Split-Tunnel\u201C. Ich lege einen Diff vor \u2014 live wird erst, wenn Sie \xFCbernehmen.",
    du: "Diktiere eine \xC4nderung, zum Beispiel \u201EErg\xE4nze im Artikel VPN von unterwegs einen Hinweis auf Split-Tunnel\u201C. Ich lege einen Diff vor \u2014 live wird erst, wenn du \xFCbernimmst."
  },
  "helpdesk.kb.assistant.approval": {
    sie: "\xC4nderungen in nat\xFCrlicher Sprache. Jeder Vorschlag braucht Ihre Freigabe.",
    du: "\xC4nderungen in nat\xFCrlicher Sprache. Jeder Vorschlag braucht deine Freigabe."
  },
  "helpdesk.kb.assistant.pick-article": {
    sie: "Welchen Artikel soll ich \xE4ndern? W\xE4hlen Sie einen Artikel aus oder nennen Sie den Titel, zum Beispiel \u201EErg\xE4nze im Artikel VPN von unterwegs \u2026\u201C.",
    du: "Welchen Artikel soll ich \xE4ndern? W\xE4hle einen Artikel aus oder nenne den Titel, zum Beispiel \u201EErg\xE4nze im Artikel VPN von unterwegs \u2026\u201C."
  },
  "helpdesk.kb.assistant.model-down": {
    sie: "Das Sprachmodell ist gerade nicht erreichbar. Artikel \u201E{{title}}\u201C k\xF6nnen Sie unter Wissen weiterhin selbst bearbeiten.",
    du: "Das Sprachmodell ist gerade nicht erreichbar. Artikel \u201E{{title}}\u201C kannst du unter Wissen weiterhin selbst bearbeiten."
  },
  "helpdesk.kb.assistant.narrow": {
    sie: "Ich konnte daraus keinen konkreten \xC4nderungsvorschlag erzeugen. Formulieren Sie die \xC4nderung bitte enger, zum Beispiel \u201EErg\xE4nze einen Hinweis auf Split-Tunnel\u201C.",
    du: "Ich konnte daraus keinen konkreten \xC4nderungsvorschlag erzeugen. Formuliere die \xC4nderung bitte enger, zum Beispiel \u201EErg\xE4nze einen Hinweis auf Split-Tunnel\u201C."
  },
  "helpdesk.kb.assistant.proposal-ready": {
    sie: "Vorschlag f\xFCr \u201E{{title}}\u201C liegt vor. Bitte pr\xFCfen Sie das Diff und \xFCbernehmen Sie es erst nach Freigabe.",
    du: "Vorschlag f\xFCr \u201E{{title}}\u201C liegt vor. Bitte pr\xFCfe das Diff und \xFCbernimm es erst nach Freigabe."
  },
  "helpdesk.kb.optimize.lead": {
    sie: "T\xE4glich um 02:15 (BullMQ). Suchanfragen ohne gute Treffer, gel\xF6ste Tickets ohne Artikel, veraltete aber oft verkn\xFCpfte Artikel. Neue Entw\xFCrfe bleiben Entw\xFCrfe, bis Sie sie \xFCbernehmen und ver\xF6ffentlichen.",
    du: "T\xE4glich um 02:15 (BullMQ). Suchanfragen ohne gute Treffer, gel\xF6ste Tickets ohne Artikel, veraltete aber oft verkn\xFCpfte Artikel. Neue Entw\xFCrfe bleiben Entw\xFCrfe, bis du sie \xFCbernimmst und ver\xF6ffentlichst."
  },
  "helpdesk.kb.optimize.empty": {
    sie: "Keine offenen KI-Vorschl\xE4ge. Starten Sie eine Analyse oder den",
    du: "Keine offenen KI-Vorschl\xE4ge. Starte eine Analyse oder den"
  },
  "helpdesk.ai.assist.offline": {
    sie: "Die KI ist gerade nicht erreichbar. Unten sehen Sie trotzdem passende Artikel und \xE4hnliche Tickets.",
    du: "Die KI ist gerade nicht erreichbar. Unten siehst du trotzdem passende Artikel und \xE4hnliche Tickets."
  },
  "helpdesk.ai.assist.send-later": {
    sie: "Der Text landet im Kommentarfeld zum Bearbeiten. Gesendet wird erst, wenn Sie auf \u201EKommentar senden\u201C klicken.",
    du: "Der Text landet im Kommentarfeld zum Bearbeiten. Gesendet wird erst, wenn du auf \u201EKommentar senden\u201C klickst."
  },
  "helpdesk.remote.connector.about": {
    sie: "Der Mitarbeiterportal Connector ist ein kleines Windows-Programm (nicht diese Website und nicht RustDesk). Es sitzt in der Taskleiste, holt Ihre Zustimmung ein und spricht mit dem Mitarbeiterportal. RustDesk ist das separate Bildschirm-Programm.",
    du: "Der Mitarbeiterportal Connector ist ein kleines Windows-Programm (nicht diese Website und nicht RustDesk). Es sitzt in der Taskleiste, holt deine Zustimmung ein und spricht mit dem Mitarbeiterportal. RustDesk ist das separate Bildschirm-Programm."
  },
  "helpdesk.remote.account.reload": {
    sie: "Fernwartung ist f\xFCr Ihr Konto noch nicht aktiv. Bitte laden Sie die Seite neu (einmal ab- und anmelden reicht, falls der Server gerade aktualisiert wurde).",
    du: "Fernwartung ist f\xFCr dein Konto noch nicht aktiv. Bitte lade die Seite neu (einmal ab- und anmelden reicht, falls der Server gerade aktualisiert wurde)."
  },
  "helpdesk.remote.account.sign-in": {
    sie: "Fernwartung ist f\xFCr Ihr Konto noch nicht aktiv. Bitte laden Sie die Seite neu oder melden Sie sich einmal ab und wieder an.",
    du: "Fernwartung ist f\xFCr dein Konto noch nicht aktiv. Bitte lade die Seite neu oder melde dich einmal ab und wieder an."
  },
  "helpdesk.remote.request.sent": {
    sie: "Anfrage gesendet. Bitte best\xE4tigen Sie den Dialog auf Ihrem Rechner.",
    du: "Anfrage gesendet. Bitte best\xE4tige den Dialog auf deinem Rechner."
  },
  "helpdesk.remote.consent.only": {
    sie: "Fernwartung nur mit Ihrer Zustimmung am Rechner",
    du: "Fernwartung nur mit deiner Zustimmung am Rechner"
  },
  "helpdesk.remote.dialog.appears": {
    sie: "Auf Ihrem Rechner erscheint ein Dialog. Erst nach \u201EZulassen\u201C kann der zugewiesene Agent \xFCber seinen eigenen Connector \u201EMit RustDesk verbinden\u201C. Kopplungscode, Ger\xE4te-GUID und RustDesk-ID geh\xF6ren nicht in dieses Ticket.",
    du: "Auf deinem Rechner erscheint ein Dialog. Erst nach \u201EZulassen\u201C kann der zugewiesene Agent \xFCber seinen eigenen Connector \u201EMit RustDesk verbinden\u201C. Kopplungscode, Ger\xE4te-GUID und RustDesk-ID geh\xF6ren nicht in dieses Ticket."
  },
  "helpdesk.remote.confirm.dialog": {
    sie: "Bitte best\xE4tigen Sie den Dialog des Mitarbeiterportal Connectors auf Ihrem Rechner.",
    du: "Bitte best\xE4tige den Dialog des Mitarbeiterportal Connectors auf deinem Rechner."
  },
  "helpdesk.remote.pair.pc": {
    sie: "Bitte unter Einstellungen einen Kopplungscode erzeugen und den Connector auf Ihrem PC koppeln. Den Code nur in den Mitarbeiterportal Connector eingeben, nie in RustDesk.",
    du: "Bitte unter Einstellungen einen Kopplungscode erzeugen und den Connector auf deinem PC koppeln. Den Code nur in den Mitarbeiterportal Connector eingeben, nie in RustDesk."
  },
  "helpdesk.remote.revoke.forbidden": {
    sie: "Sie d\xFCrfen dieses Ger\xE4t nicht widerrufen.",
    du: "Du darfst dieses Ger\xE4t nicht widerrufen."
  },
  "helpdesk.remote.connector.missing": {
    sie: "Auf Ihrem Rechner ist kein Mitarbeiterportal Connector gekoppelt. Bitte koppeln Sie den Connector zuerst.",
    du: "Auf deinem Rechner ist kein Mitarbeiterportal Connector gekoppelt. Bitte kopple den Connector zuerst."
  },
  "helpdesk.remote.permission": {
    sie: "Sie haben keine Berechtigung f\xFCr Remote-Support.",
    du: "Du hast keine Berechtigung f\xFCr Remote-Support."
  },
  "helpdesk.remote.end.forbidden": {
    sie: "Sie d\xFCrfen diese Sitzung nicht beenden.",
    du: "Du darfst diese Sitzung nicht beenden."
  },
  "helpdesk.attachment.choose-file": {
    sie: "Bitte w\xE4hlen Sie eine Datei aus.",
    du: "Bitte w\xE4hle eine Datei aus."
  },
  "helpdesk.asset.name.required": {
    sie: "Bitte geben Sie eine Bezeichnung ein.",
    du: "Bitte gib eine Bezeichnung ein."
  },
  "helpdesk.asset.type.required": {
    sie: "Bitte w\xE4hlen Sie einen Typ.",
    du: "Bitte w\xE4hle einen Typ."
  },
  "helpdesk.asset.ticket.required": {
    sie: "Bitte geben Sie ein Ticket an.",
    du: "Bitte gib ein Ticket an."
  },
  "helpdesk.automation.assigned": {
    sie: "Die Automatisierungsregel \u201E{{rule}}\u201C hat Ihnen das Ticket \u201E{{subject}}\u201C zugewiesen.",
    du: "Die Automatisierungsregel \u201E{{rule}}\u201C hat dir das Ticket \u201E{{subject}}\u201C zugewiesen."
  },
  "helpdesk.sla.assigned": {
    sie: "Die SLA-Eskalation \u201E{{name}}\u201C hat Ihnen das Ticket \u201E{{subject}}\u201C zugewiesen.",
    du: "Die SLA-Eskalation \u201E{{name}}\u201C hat dir das Ticket \u201E{{subject}}\u201C zugewiesen."
  },
  "helpdesk.dashboard.empty-mine": {
    sie: "Sie haben gerade keine offenen Tickets.",
    du: "Du hast gerade keine offenen Tickets."
  },
  "helpdesk.remote.requested": {
    sie: "Anfrage gesendet. Bitte best\xE4tigen Sie den Dialog auf Ihrem Rechner.",
    du: "Anfrage gesendet. Bitte best\xE4tige den Dialog auf deinem Rechner."
  },
  "helpdesk.remote.consent": {
    sie: "Fernwartung nur mit Ihrer Zustimmung am Rechner",
    du: "Fernwartung nur mit deiner Zustimmung am Rechner"
  },
  "helpdesk.remote.confirm-dialog": {
    sie: "Bitte best\xE4tigen Sie den Dialog des Mitarbeiterportal Connectors auf Ihrem Rechner.",
    du: "Bitte best\xE4tige den Dialog des Mitarbeiterportal Connectors auf deinem Rechner."
  },
  "helpdesk.approvals.step-required": {
    sie: "Bitte legen Sie mindestens eine Stufe an.",
    du: "Bitte lege mindestens eine Stufe an."
  },
  "helpdesk.kb.write.denied": {
    sie: "Sie d\xFCrfen die Wissensdatenbank nicht bearbeiten.",
    du: "Du darfst die Wissensdatenbank nicht bearbeiten."
  },
  "helpdesk.kb.assistant.intro": {
    sie: "Diktieren Sie eine \xC4nderung, zum Beispiel \u201EErg\xE4nze im Artikel VPN von unterwegs einen Hinweis auf Split-Tunnel\u201C. Ich lege einen Diff vor \u2014 live wird erst, wenn Sie \xFCbernehmen.",
    du: "Diktier eine \xC4nderung, zum Beispiel \u201EErg\xE4nze im Artikel VPN von unterwegs einen Hinweis auf Split-Tunnel\u201C. Ich lege einen Diff vor \u2014 live wird erst, wenn du \xFCbernimmst."
  }
};

// src/voice-catalog/intranet.ts
var INTRANET_VOICE_COPY = {
  "intranet.ack.confirmed": {
    sie: "Sie haben das Lesen am {{at}} best\xE4tigt.",
    du: "Du hast das Lesen am {{at}} best\xE4tigt."
  },
  "intranet.ack.not-audience": {
    sie: "Sie geh\xF6ren nicht zur Zielgruppe dieser Lesebest\xE4tigung.",
    du: "Du geh\xF6rst nicht zur Zielgruppe dieser Lesebest\xE4tigung."
  },
  "intranet.ack.please-confirm": {
    sie: "Bitte best\xE4tigen Sie, dass Sie diese Nachricht gelesen haben",
    du: "Bitte best\xE4tige, dass du diese Nachricht gelesen hast"
  },
  "intranet.ack.report-denied": {
    sie: "Sie d\xFCrfen keine Lesebest\xE4tigungen auswerten.",
    du: "Du darfst keine Lesebest\xE4tigungen auswerten."
  },
  "intranet.action.denied": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr diese Aktion.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr diese Aktion."
  },
  "intranet.ai.agent-denied": {
    sie: "Sie haben keinen Zugriff auf diesen Agenten.",
    du: "Du hast keinen Zugriff auf diesen Agenten."
  },
  "intranet.ai.answer-shape": {
    sie: "Sie besteht nicht aus dem Dateinamen, nicht aus Absatz- oder Seitenangaben und nicht aus der Aufforderung, die Datei zu \xF6ffnen.",
    du: "Die Antwort besteht nicht aus dem Dateinamen, nicht aus Absatz- oder Seitenangaben und nicht aus der Aufforderung, die Datei zu \xF6ffnen."
  },
  "intranet.ai.ask-persona": {
    sie: "Sie sind die Intranet-Assistenz.",
    du: "Du bist die Intranet-Assistenz."
  },
  "intranet.ai.chat-denied": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr den Intranet-KI-Chat.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr den Intranet-KI-Chat."
  },
  "intranet.ai.draft-persona": {
    sie: "Sie sind die Redaktionsassistenz im Intranet.",
    du: "Du bist die Redaktionsassistenz im Intranet."
  },
  "intranet.ai.instruction-limit": {
    sie: "Sie erlaubt kein eigenes Wissen und keine Fakten au\xDFerhalb der Beitr\xE4ge.",
    du: "Die Anweisung erlaubt kein eigenes Wissen und keine Fakten au\xDFerhalb der Beitr\xE4ge."
  },
  "intranet.ai.no-hit": {
    sie: "Dazu steht im Intranet nichts, das Sie sehen k\xF6nnen.",
    du: "Dazu steht im Intranet nichts, das du sehen kannst."
  },
  "intranet.ai.page-description": {
    sie: "Stellen Sie Fragen an hinterlegte Beitr\xE4ge und Dateien. Jede Antwort bleibt in Ihrem Verlauf.",
    du: "Stelle Fragen an hinterlegte Beitr\xE4ge und Dateien. Jede Antwort bleibt in deinem Verlauf."
  },
  "intranet.ask.clear-all": {
    sie: "Alle Ihre gespeicherten Fragen und Antworten werden entfernt. Das l\xE4sst sich nicht r\xFCckg\xE4ngig machen.",
    du: "Alle deine gespeicherten Fragen und Antworten werden entfernt. Das l\xE4sst sich nicht r\xFCckg\xE4ngig machen."
  },
  "intranet.ask.clear-one": {
    sie: "Diese Frage und Antwort werden aus Ihrem Verlauf entfernt. Das l\xE4sst sich nicht r\xFCckg\xE4ngig machen.",
    du: "Diese Frage und Antwort werden aus deinem Verlauf entfernt. Das l\xE4sst sich nicht r\xFCckg\xE4ngig machen."
  },
  "intranet.ask.empty": {
    sie: "Sie haben noch keine Frage gestellt.",
    du: "Du hast noch keine Frage gestellt."
  },
  "intranet.ask.history-heading": {
    sie: "Ihre Fragen",
    du: "Deine Fragen"
  },
  "intranet.ask.history-hint": {
    sie: "Nur Ihre Fragen an KI-Agenten. Intranet fragen bleibt getrennt.",
    du: "Nur deine Fragen an KI-Agenten. Intranet fragen bleibt getrennt."
  },
  "intranet.ask.home-empty": {
    sie: "Noch keine Intranet-Frage. Stellen Sie Fragen unter Intranet fragen auf der Startseite.",
    du: "Noch keine Intranet-Frage. Stelle Fragen unter Intranet fragen auf der Startseite."
  },
  "intranet.ask.no-agent": {
    sie: "Ihnen ist kein KI-Agent zugeordnet.",
    du: "Dir ist kein KI-Agent zugeordnet."
  },
  "intranet.ask.question-label": {
    sie: "Ihre Frage",
    du: "Deine Frage"
  },
  "intranet.ask.question-placeholder": {
    sie: "Ihre Frage \u2026",
    du: "Deine Frage \u2026"
  },
  "intranet.category.join-already": {
    sie: "Diesen Channel sehen Sie schon.",
    du: "Diesen Channel siehst du schon."
  },
  "intranet.category.leave-not-member": {
    sie: "Sie sind kein direktes Mitglied dieses Channels.",
    du: "Du bist kein direktes Mitglied dieses Channels."
  },
  "intranet.category.manage-denied": {
    sie: "Sie d\xFCrfen keine Channels verwalten.",
    du: "Du darfst keine Channels verwalten."
  },
  "intranet.chat.first-message": {
    sie: "Schreiben Sie die erste Nachricht.",
    du: "Schreib die erste Nachricht."
  },
  "intranet.chat.group-too-few": {
    sie: "Ein Gruppenchat braucht mindestens {{min}} Personen (einschlie\xDFlich Ihnen).",
    du: "Ein Gruppenchat braucht mindestens {{min}} Personen (einschlie\xDFlich dir)."
  },
  "intranet.chat.pick-conversation": {
    sie: "W\xE4hlen Sie links ein Gespr\xE4ch aus.",
    du: "W\xE4hle links ein Gespr\xE4ch aus."
  },
  "intranet.comment.delete-own": {
    sie: "Sie k\xF6nnen nur eigene Kommentare l\xF6schen.",
    du: "Du kannst nur eigene Kommentare l\xF6schen."
  },
  "intranet.comment.wrong-parent": {
    sie: "Der Kommentar, auf den Sie antworten, geh\xF6rt nicht zu diesem Beitrag.",
    du: "Der Kommentar, auf den du antwortest, geh\xF6rt nicht zu diesem Beitrag."
  },
  "intranet.contact.manage-denied": {
    sie: "Sie d\xFCrfen keine Ansprechpartner verwalten.",
    du: "Du darfst keine Ansprechpartner verwalten."
  },
  "intranet.content.not-released": {
    sie: "Diesen Beitrag gibt es nicht oder er ist nicht f\xFCr Sie freigegeben.",
    du: "Diesen Beitrag gibt es nicht oder er ist nicht f\xFCr dich freigegeben."
  },
  "intranet.editor.channel-required": {
    sie: "Bitte w\xE4hlen Sie einen Channel, in dem Sie Beitr\xE4ge schreiben d\xFCrfen.",
    du: "Bitte w\xE4hle einen Channel, in dem du Beitr\xE4ge schreiben darfst."
  },
  "intranet.editor.draft-private": {
    sie: "Ihr Entwurf bleibt bis zur Ver\xF6ffentlichung nur f\xFCr Sie sichtbar.",
    du: "Dein Entwurf bleibt bis zur Ver\xF6ffentlichung nur f\xFCr dich sichtbar."
  },
  "intranet.editor.no-channel": {
    sie: "Sie sind aktuell keinem Channel als Autorin oder Autor zugeordnet.",
    du: "Du bist aktuell keinem Channel als Autorin oder Autor zugeordnet."
  },
  "intranet.editor.publish-denied": {
    sie: "Sie d\xFCrfen in diesem Channel keine Beitr\xE4ge ver\xF6ffentlichen.",
    du: "Du darfst in diesem Channel keine Beitr\xE4ge ver\xF6ffentlichen."
  },
  "intranet.feedback.thanks": {
    sie: "Danke f\xFCr Ihre R\xFCckmeldung.",
    du: "Danke f\xFCr deine R\xFCckmeldung."
  },
  "intranet.file.choose": {
    sie: "Bitte w\xE4hlen Sie eine Datei aus.",
    du: "Bitte w\xE4hle eine Datei aus."
  },
  "intranet.image.choose": {
    sie: "Bitte w\xE4hlen Sie ein Bild oder ein Video aus.",
    du: "Bitte w\xE4hle ein Bild oder ein Video aus."
  },
  "intranet.library.not-released": {
    sie: "Diesen Eintrag gibt es nicht oder er ist nicht f\xFCr Sie freigegeben.",
    du: "Diesen Eintrag gibt es nicht oder er ist nicht f\xFCr dich freigegeben."
  },
  "intranet.location.gps-unavailable": {
    sie: "Der Standort ist nicht verf\xFCgbar. Setzen Sie einen Punkt auf der Karte.",
    du: "Der Standort ist nicht verf\xFCgbar. Setze einen Punkt auf der Karte."
  },
  "intranet.location.pin-with-button": {
    sie: "Den Pin setzen Sie mit \u201EMeinen Standort\u201C. Die Karte ist Google Maps.",
    du: "Den Pin setzt du mit \u201EMeinen Standort\u201C. Die Karte ist Google Maps."
  },
  "intranet.location.tap-map": {
    sie: "Tippen Sie auf die Karte, um den Pin zu setzen.",
    du: "Tippe auf die Karte, um den Pin zu setzen."
  },
  "intranet.news.not-released": {
    sie: "Diese Nachricht gibt es nicht oder sie ist nicht f\xFCr Sie freigegeben.",
    du: "Diese Nachricht gibt es nicht oder sie ist nicht f\xFCr dich freigegeben."
  },
  "intranet.notify.ack-reminder": {
    sie: "Bitte best\xE4tigen Sie noch das Lesen dieser Nachricht. Die Frist l\xE4uft bald ab.",
    du: "Bitte best\xE4tige noch das Lesen dieser Nachricht. Die Frist l\xE4uft bald ab."
  },
  "intranet.notify.ack-required": {
    sie: "Bitte lesen Sie diese Nachricht und best\xE4tigen Sie das Lesen.",
    du: "Bitte lies diese Nachricht und best\xE4tige das Lesen."
  },
  "intranet.notify.chat-open": {
    sie: "\xD6ffnen Sie den Chat im Intranet.",
    du: "\xD6ffne den Chat im Intranet."
  },
  "intranet.notify.comment-liked": {
    sie: "{{actor}} gef\xE4llt Ihren Kommentar.",
    du: "{{actor}} gef\xE4llt deinen Kommentar."
  },
  "intranet.notify.comment-replied": {
    sie: "{{actor}} hat auf Ihren Kommentar geantwortet.",
    du: "{{actor}} hat auf deinen Kommentar geantwortet."
  },
  "intranet.notify.mention-comment": {
    sie: "{{actor}} hat Sie in einem Kommentar erw\xE4hnt.",
    du: "{{actor}} hat dich in einem Kommentar erw\xE4hnt."
  },
  "intranet.notify.mention-content": {
    sie: "{{actor}} hat Sie in \u201E{{title}}\u201C erw\xE4hnt.",
    du: "{{actor}} hat dich in \u201E{{title}}\u201C erw\xE4hnt."
  },
  "intranet.poll.anonymous": {
    sie: "Ihre Antwort ist anonym",
    du: "Deine Antwort ist anonym"
  },
  "intranet.poll.change-vote": {
    sie: "Tippen Sie eine andere Antwort an, um Ihre Stimme zu \xE4ndern.",
    du: "Tippe eine andere Antwort an, um deine Stimme zu \xE4ndern."
  },
  "intranet.poll.not-audience": {
    sie: "Sie geh\xF6ren nicht zur Zielgruppe dieser Umfrage.",
    du: "Du geh\xF6rst nicht zur Zielgruppe dieser Umfrage."
  },
  "intranet.poll.vote-failed": {
    sie: "Ihre Stimme konnte gerade nicht gespeichert werden. Bitte erneut versuchen.",
    du: "Deine Stimme konnte gerade nicht gespeichert werden. Bitte erneut versuchen."
  },
  "intranet.poll.your-vote": {
    sie: "{{label}} (Ihre Stimme)",
    du: "{{label}} (deine Stimme)"
  },
  "intranet.quicklink.manage-denied": {
    sie: "Sie d\xFCrfen den Schnellzugriff nicht verwalten.",
    du: "Du darfst den Schnellzugriff nicht verwalten."
  }
};

// src/voice-catalog/operations.ts
var OPERATIONS_VOICE_COPY = {
  "leave.colleagues.title": {
    sie: "Ihre Kolleg:innen",
    du: "Deine Kolleg:innen"
  },
  "leave.calendar.personal-year": {
    sie: "Ihr pers\xF6nliches Jahr",
    du: "Dein pers\xF6nliches Jahr"
  },
  "leave.calendar.department": {
    sie: "Abwesenheiten Ihrer Abteilung",
    du: "Abwesenheiten deiner Abteilung"
  },
  "leave.calendar.department-empty": {
    sie: "In Ihrer Abteilung ist niemand eingetragen.",
    du: "In deiner Abteilung ist niemand eingetragen."
  },
  "leave.calendar.no-department": {
    sie: "Ihrer Person ist keine Abteilung zugeordnet.",
    du: "Deiner Person ist keine Abteilung zugeordnet."
  },
  "leave.calendar.own-department-only": {
    sie: "Sie d\xFCrfen nur den Kalender Ihrer eigenen Abteilung einsehen.",
    du: "Du darfst nur den Kalender deiner eigenen Abteilung einsehen."
  },
  "leave.calendar.view.denied": {
    sie: "Sie d\xFCrfen diese Kalenderansicht nicht einsehen.",
    du: "Du darfst diese Kalenderansicht nicht einsehen."
  },
  "leave.calendar.year.denied": {
    sie: "Sie d\xFCrfen den Jahreskalender dieser Person nicht einsehen.",
    du: "Du darfst den Jahreskalender dieser Person nicht einsehen."
  },
  "leave.delegate.description": {
    sie: "Wer darf in Ihrer Abwesenheit Urlaubsantr\xE4ge Ihrer Berichte genehmigen?",
    du: "Wer darf in deiner Abwesenheit Urlaubsantr\xE4ge deiner Berichte genehmigen?"
  },
  "leave.balance.week-hint": {
    sie: "\u201EG\xFCltig ab\u201C muss den Jahresanfang abdecken (1. Januar), sonst erscheint die Woche nicht in der \xDCbersicht der Person. Die eigene Seite /leave zeigt nur Ihre Arbeitswoche, nicht die der hier gew\xE4hlten Person.",
    du: "\u201EG\xFCltig ab\u201C muss den Jahresanfang abdecken (1. Januar), sonst erscheint die Woche nicht in der \xDCbersicht der Person. Die eigene Seite /leave zeigt nur deine Arbeitswoche, nicht die der hier gew\xE4hlten Person."
  },
  "leave.request.kind.required": {
    sie: "Bitte w\xE4hlen Sie eine Art.",
    du: "Bitte w\xE4hle eine Art."
  },
  "leave.request.sick.notice": {
    sie: "Die Krankmeldung wird erfasst und Ihre F\xFChrungskraft informiert.",
    du: "Die Krankmeldung wird erfasst und deine F\xFChrungskraft informiert."
  },
  "leave.request.approved": {
    sie: "Ihr Urlaubsantrag ({{range}}) wurde genehmigt.",
    du: "Dein Urlaubsantrag ({{range}}) wurde genehmigt."
  },
  "leave.request.substitute": {
    sie: "Sie vertreten {{name}} im Zeitraum {{range}}.",
    du: "Du vertrittst {{name}} im Zeitraum {{range}}."
  },
  "leave.request.rejected.note": {
    sie: "Ihr Urlaubsantrag ({{range}}) wurde abgelehnt: {{note}}",
    du: "Dein Urlaubsantrag ({{range}}) wurde abgelehnt: {{note}}"
  },
  "leave.request.rejected": {
    sie: "Ihr Urlaubsantrag ({{range}}) wurde abgelehnt.",
    du: "Dein Urlaubsantrag ({{range}}) wurde abgelehnt."
  },
  "leave.reminder.upcoming": {
    sie: "Ihr genehmigter Urlaub ({{range}}) beginnt in {{days}} Tagen.",
    du: "Dein genehmigter Urlaub ({{range}}) beginnt in {{days}} Tagen."
  },
  "leave.substitute.required": {
    sie: "Bitte w\xE4hlen Sie eine Vertretung.",
    du: "Bitte w\xE4hle eine Vertretung."
  },
  "leave.reject.reason.required": {
    sie: "Bitte geben Sie einen Grund f\xFCr die Ablehnung an.",
    du: "Bitte gib einen Grund f\xFCr die Ablehnung an."
  },
  "leave.permission.denied": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr diese Aktion.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr diese Aktion."
  },
  "leave.request.decide.denied": {
    sie: "Sie d\xFCrfen diesen Antrag nicht entscheiden.",
    du: "Du darfst diesen Antrag nicht entscheiden."
  },
  "leave.request.read.denied": {
    sie: "Sie d\xFCrfen diesen Antrag nicht einsehen.",
    du: "Du darfst diesen Antrag nicht einsehen."
  },
  "leave.request.read-all.denied": {
    sie: "Sie d\xFCrfen nicht alle Antr\xE4ge einsehen.",
    du: "Du darfst nicht alle Antr\xE4ge einsehen."
  },
  "leave.overtime.decide.denied": {
    sie: "Sie d\xFCrfen diese \xDCberstunden nicht entscheiden.",
    du: "Du darfst diese \xDCberstunden nicht entscheiden."
  },
  "leave.overtime.book.denied": {
    sie: "Sie d\xFCrfen f\xFCr diese Person keine \xDCberstunden buchen.",
    du: "Du darfst f\xFCr diese Person keine \xDCberstunden buchen."
  },
  "fleet.home.assigned": {
    sie: "Ihre zugeordneten Fahrzeuge und Pflichten.",
    du: "Deine zugeordneten Fahrzeuge und Pflichten."
  },
  "fleet.home.none": {
    sie: "Ihnen ist kein Fahrzeug zugeordnet.",
    du: "Dir ist kein Fahrzeug zugeordnet."
  },
  "fleet.odometer.description": {
    sie: "Stand f\xFCr ein Ihnen heute zugeordnetes Fahrzeug melden.",
    du: "Stand f\xFCr ein dir heute zugeordnetes Fahrzeug melden."
  },
  "fleet.vehicle.none-today": {
    sie: "Ihnen ist heute kein Fahrzeug zugeordnet.",
    du: "Dir ist heute kein Fahrzeug zugeordnet."
  },
  "fleet.trips.description": {
    sie: "Ihre Telematik-Fahrten und Zweck.",
    du: "Deine Telematik-Fahrten und Zweck."
  },
  "fleet.trips.denied": {
    sie: "Sie haben keinen Zugriff auf Fahrten.",
    du: "Du hast keinen Zugriff auf Fahrten."
  },
  "fleet.pool.self-only": {
    sie: "Sie k\xF6nnen Poolfahrzeuge nur f\xFCr sich selbst buchen.",
    du: "Du kannst Poolfahrzeuge nur f\xFCr dich selbst buchen."
  },
  "fleet.assignment.started": {
    sie: "Ihnen wurde das Fahrzeug {{plate}} zugeordnet ({{period}}).",
    du: "Dir wurde das Fahrzeug {{plate}} zugeordnet ({{period}})."
  },
  "fleet.file.required": {
    sie: "Bitte w\xE4hlen Sie eine Datei aus.",
    du: "Bitte w\xE4hle eine Datei aus."
  },
  "fleet.files.min": {
    sie: "Bitte w\xE4hlen Sie mindestens eine Datei aus.",
    du: "Bitte w\xE4hle mindestens eine Datei aus."
  },
  "fleet.damage.read-all.denied": {
    sie: "Sie d\xFCrfen nicht alle Meldungen lesen.",
    du: "Du darfst nicht alle Meldungen lesen."
  },
  "fleet.damage.resolved": {
    sie: "Ihre Meldung zu {{plate}} wurde erledigt.",
    du: "Deine Meldung zu {{plate}} wurde erledigt."
  },
  "fleet.damage.rejected": {
    sie: "Ihre Meldung zu {{plate}} wurde abgelehnt.",
    du: "Deine Meldung zu {{plate}} wurde abgelehnt."
  },
  "fleet.damage.photo.add.denied": {
    sie: "Sie d\xFCrfen keine Fotos zu dieser Meldung hinzuf\xFCgen.",
    du: "Du darfst keine Fotos zu dieser Meldung hinzuf\xFCgen."
  },
  "fleet.damage.photo.upload.denied": {
    sie: "Sie d\xFCrfen kein Foto zu dieser Meldung hochladen.",
    du: "Du darfst kein Foto zu dieser Meldung hochladen."
  },
  "fleet.damage.photo.read.denied": {
    sie: "Sie d\xFCrfen dieses Foto nicht lesen.",
    du: "Du darfst dieses Foto nicht lesen."
  },
  "fleet.damage.read.denied": {
    sie: "Sie d\xFCrfen diese Meldung nicht lesen.",
    du: "Du darfst diese Meldung nicht lesen."
  },
  "fleet.damage.vehicle.denied": {
    sie: "Sie d\xFCrfen die Meldungen dieses Fahrzeugs nicht lesen.",
    du: "Du darfst die Meldungen dieses Fahrzeugs nicht lesen."
  },
  "fleet.document.read.denied": {
    sie: "Sie d\xFCrfen dieses Dokument nicht lesen.",
    du: "Du darfst dieses Dokument nicht lesen."
  },
  "fleet.document.upload.denied": {
    sie: "Sie d\xFCrfen keine Fuhrpark-Dokumente hochladen.",
    du: "Du darfst keine Fuhrpark-Dokumente hochladen."
  },
  "fleet.license.photo.denied": {
    sie: "Sie d\xFCrfen F\xFChrerschein-Fotos nicht verwalten.",
    du: "Du darfst F\xFChrerschein-Fotos nicht verwalten."
  },
  "fleet.license.self-check.denied": {
    sie: "Sie k\xF6nnen Ihre eigene F\xFChrerscheinkontrolle nicht erfassen.",
    du: "Du kannst deine eigene F\xFChrerscheinkontrolle nicht erfassen."
  },
  "fleet.odometer.read.denied": {
    sie: "Sie d\xFCrfen den Kilometerstand dieses Fahrzeugs nicht lesen.",
    du: "Du darfst den Kilometerstand dieses Fahrzeugs nicht lesen."
  },
  "fleet.odometer.report.denied": {
    sie: "Sie d\xFCrfen keinen Kilometerstand melden.",
    du: "Du darfst keinen Kilometerstand melden."
  },
  "fleet.vehicle.not-assigned-today": {
    sie: "Sie sind heute nicht diesem Fahrzeug zugeordnet.",
    du: "Du bist heute nicht diesem Fahrzeug zugeordnet."
  },
  "fleet.task.complete.denied": {
    sie: "Sie d\xFCrfen diese Aufgabe nicht erledigen.",
    du: "Du darfst diese Aufgabe nicht erledigen."
  },
  "fleet.vehicle.read.denied": {
    sie: "Sie d\xFCrfen dieses Fahrzeug nicht lesen.",
    du: "Du darfst dieses Fahrzeug nicht lesen."
  },
  "fleet.person.search-all.denied": {
    sie: "Sie d\xFCrfen nicht alle Personen suchen.",
    du: "Du darfst nicht alle Personen suchen."
  },
  "fleet.driver.search.denied": {
    sie: "Sie d\xFCrfen die Fahrersuche nicht nutzen.",
    du: "Du darfst die Fahrersuche nicht nutzen."
  },
  "fleet.release.holding-damage": {
    sie: "Das Fahrzeug hat noch einen offenen Schaden mit \u201ENicht fahrbereit\u201C. Schlie\xDFen Sie ihn zuerst.",
    du: "Das Fahrzeug hat noch einen offenen Schaden mit \u201ENicht fahrbereit\u201C. Schlie\xDF ihn zuerst."
  },
  "maintenance.signature.consent.required": {
    sie: "Bitte best\xE4tigen Sie die Einwilligung zur Unterschrift.",
    du: "Bitte best\xE4tige die Einwilligung zur Unterschrift."
  },
  "maintenance.signature.name.required": {
    sie: "Bitte geben Sie den Namen der unterschreibenden Person an.",
    du: "Bitte gib den Namen der unterschreibenden Person an."
  },
  "maintenance.signature.confirm-accuracy": {
    sie: "Einfache elektronische Unterschrift \u2013 kein Ersatz f\xFCr eine qualifizierte elektronische Signatur. Mit dem Abschluss best\xE4tigen Sie die Richtigkeit der Angaben.",
    du: "Einfache elektronische Unterschrift \u2013 kein Ersatz f\xFCr eine qualifizierte elektronische Signatur. Mit dem Abschluss best\xE4tigst du die Richtigkeit der Angaben."
  },
  "maintenance.signature.confirm": {
    sie: "Bitte best\xE4tigen Sie die Unterschrift.",
    du: "Bitte best\xE4tige die Unterschrift."
  },
  "maintenance.signature.pad.required": {
    sie: "Bitte unterschreiben Sie im Feld.",
    du: "Bitte unterschreibe im Feld."
  },
  "maintenance.work-order.archive.one": {
    sie: "Der Auftrag verschwindet aus Ihrer Liste. Sie k\xF6nnen ihn \xFCber den Filter wieder einblenden.",
    du: "Der Auftrag verschwindet aus deiner Liste. Du kannst ihn \xFCber den Filter wieder einblenden."
  },
  "maintenance.work-order.archive.many": {
    sie: "Die Auftr\xE4ge verschwinden aus Ihrer Liste. Sie k\xF6nnen sie \xFCber den Filter wieder einblenden.",
    du: "Die Auftr\xE4ge verschwinden aus deiner Liste. Du kannst sie \xFCber den Filter wieder einblenden."
  },
  "maintenance.work-order.assigned": {
    sie: "Ihnen wurde ein neuer Wartungsauftrag zugewiesen.",
    du: "Dir wurde ein neuer Wartungsauftrag zugewiesen."
  },
  "maintenance.work-order.reassigned": {
    sie: "Ihnen wurde ein Wartungsauftrag neu zugewiesen.",
    du: "Dir wurde ein Wartungsauftrag neu zugewiesen."
  },
  "maintenance.template.publish.failed": {
    sie: "Ver\xF6ffentlichen fehlgeschlagen. Pr\xFCfen Sie die Quelldatei.",
    du: "Ver\xF6ffentlichen fehlgeschlagen. Pr\xFCfe die Quelldatei."
  },
  "maintenance.template.missing.before": {
    sie: "F\xFCr diese Anlage ist keine Vorlage zugeordnet. \xD6ffnen Sie unter",
    du: "F\xFCr diese Anlage ist keine Vorlage zugeordnet. \xD6ffne unter"
  },
  "maintenance.template.missing.after": {
    sie: "die gew\xFCnschte Vorlage, ver\xF6ffentlichen Sie eine Version und ordnen Sie die Vorlage der Anlage zu.",
    du: "die gew\xFCnschte Vorlage, ver\xF6ffentliche eine Version und ordne die Vorlage der Anlage zu."
  },
  "maintenance.template.version.locked": {
    sie: "Diese Version wird bereits verwendet. Bestehende Protokolle bleiben unver\xE4ndert. Zum \xC4ndern legen Sie eine neue Version an.",
    du: "Diese Version wird bereits verwendet. Bestehende Protokolle bleiben unver\xE4ndert. Zum \xC4ndern leg eine neue Version an."
  },
  "maintenance.template.version.in-use": {
    sie: "Diese Version wird bereits verwendet. Legen Sie eine neue Version an. Bestehende Protokolle bleiben unver\xE4ndert.",
    du: "Diese Version wird bereits verwendet. Leg eine neue Version an. Bestehende Protokolle bleiben unver\xE4ndert."
  },
  "maintenance.template.pdf.required": {
    sie: "Bitte w\xE4hlen Sie eine PDF-Datei aus.",
    du: "Bitte w\xE4hle eine PDF-Datei aus."
  },
  "maintenance.file.required": {
    sie: "Bitte w\xE4hlen Sie eine Datei.",
    du: "Bitte w\xE4hle eine Datei."
  },
  "maintenance.protocol.finalize.confirm": {
    sie: "Bitte best\xE4tigen Sie den verbindlichen Abschluss.",
    du: "Bitte best\xE4tige den verbindlichen Abschluss."
  },
  "maintenance.protocol.customer-signature.missing": {
    sie: "Name und Unterschrift des Kunden fehlen. Bitte beides speichern, bevor Sie abschlie\xDFen.",
    du: "Name und Unterschrift des Kunden fehlen. Bitte beides speichern, bevor du abschlie\xDFt."
  },
  "onboarding.access.denied": {
    sie: "Sie haben keinen Zugriff auf diesen Bereich.",
    du: "Du hast keinen Zugriff auf diesen Bereich."
  },
  "onboarding.cases.empty": {
    sie: "Noch keine Vorg\xE4nge. Starten Sie eine ver\xF6ffentlichte Vorlage.",
    du: "Noch keine Vorg\xE4nge. Starte eine ver\xF6ffentlichte Vorlage."
  },
  "onboarding.template.phases.empty": {
    sie: "Noch keine Phasen. F\xFCgen Sie eine Phase hinzu und legen Sie Aufgaben an.",
    du: "Noch keine Phasen. F\xFCge eine Phase hinzu und leg Aufgaben an."
  },
  "onboarding.templates.empty": {
    sie: "Noch keine Vorlagen. Legen Sie eine Vorlage an, um Phasen und Aufgaben zu pflegen.",
    du: "Noch keine Vorlagen. Leg eine Vorlage an, um Phasen und Aufgaben zu pflegen."
  },
  "onboarding.template.import.hint": {
    sie: "Die Datei wird gepr\xFCft. Gespeichert wird erst, wenn Sie Importieren best\xE4tigen.",
    du: "Die Datei wird gepr\xFCft. Gespeichert wird erst, wenn du Importieren best\xE4tigst."
  },
  "onboarding.template.name.taken": {
    sie: "Eine Vorlage mit diesem Namen ist bereits vorhanden. W\xE4hlen Sie einen neuen Namen.",
    du: "Eine Vorlage mit diesem Namen ist bereits vorhanden. W\xE4hle einen neuen Namen."
  },
  "onboarding.task.access.denied": {
    sie: "Sie haben keinen Zugriff auf diese Aufgabe.",
    du: "Du hast keinen Zugriff auf diese Aufgabe."
  },
  "onboarding.task.complete.denied": {
    sie: "Sie d\xFCrfen diese Aufgabe nicht abschlie\xDFen.",
    du: "Du darfst diese Aufgabe nicht abschlie\xDFen."
  },
  "onboarding.task.claim.denied": {
    sie: "Sie d\xFCrfen diese Aufgabe nicht \xFCbernehmen.",
    du: "Du darfst diese Aufgabe nicht \xFCbernehmen."
  },
  "onboarding.task.assigned": {
    sie: "Ihnen wurde die Aufgabe \u201E{{task}}\u201C f\xFCr {{subject}} zugewiesen.",
    du: "Dir wurde die Aufgabe \u201E{{task}}\u201C f\xFCr {{subject}} zugewiesen."
  },
  "onboarding.case.complete.denied": {
    sie: "Sie d\xFCrfen diesen Vorgang nicht abschlie\xDFen.",
    du: "Du darfst diesen Vorgang nicht abschlie\xDFen."
  },
  "onboarding.case.access.denied": {
    sie: "Sie haben keinen Zugriff auf diesen Vorgang.",
    du: "Du hast keinen Zugriff auf diesen Vorgang."
  }
};

// src/voice-catalog/platform.ts
var PLATFORM_VOICE_COPY = {
  "platform.auth.signed-out": {
    sie: "Sie wurden abgemeldet.",
    du: "Du wurdest abgemeldet."
  },
  "platform.auth.password.changed": {
    sie: "Das Passwort wurde ge\xE4ndert. Bitte melden Sie sich neu an.",
    du: "Das Passwort wurde ge\xE4ndert. Bitte melde dich neu an."
  },
  "platform.auth.module.unavailable": {
    sie: "Dieses Modul ist f\xFCr Sie nicht verf\xFCgbar.",
    du: "Dieses Modul ist f\xFCr dich nicht verf\xFCgbar."
  },
  "platform.auth.denied.action": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr diese Aktion.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr diese Aktion."
  },
  "platform.auth.totp.code": {
    sie: "Bitte geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.",
    du: "Bitte gib den 6-stelligen Code aus deiner Authenticator-App ein."
  },
  "platform.auth.totp.setup": {
    sie: "F\xFCr die Rolle IT-Admin ist Zwei-Faktor-Authentifizierung Pflicht. Scannen Sie den QR-Code mit Ihrer Authenticator-App.",
    du: "F\xFCr die Rolle IT-Admin ist Zwei-Faktor-Authentifizierung Pflicht. Scanne den QR-Code mit deiner Authenticator-App."
  },
  "platform.auth.login.code": {
    sie: "Bitte geben Sie den 6-stelligen Code ein.",
    du: "Bitte gib den 6-stelligen Code ein."
  },
  "platform.auth.login.credentials": {
    sie: "Bitte pr\xFCfen Sie E-Mail und Passwort.",
    du: "Bitte pr\xFCfe E-Mail und Passwort."
  },
  "platform.auth.login.totp-admin": {
    sie: "F\xFCr IT-Admins ist Zwei-Faktor-Authentifizierung Pflicht. Scannen Sie den QR-Code mit Ihrer Authenticator-App.",
    du: "F\xFCr IT-Admins ist Zwei-Faktor-Authentifizierung Pflicht. Scanne den QR-Code mit deiner Authenticator-App."
  },
  "platform.auth.login.totp-confirmed": {
    sie: "Passwort best\xE4tigt. Geben Sie jetzt den 6-stelligen Code aus Ihrer Authenticator-App ein.",
    du: "Passwort best\xE4tigt. Gib jetzt den 6-stelligen Code aus deiner Authenticator-App ein."
  },
  "platform.auth.login.microsoft": {
    sie: "Anmeldung mit Ihrem Microsoft-365-Konto (Outlook).",
    du: "Anmeldung mit deinem Microsoft-365-Konto (Outlook)."
  },
  "platform.auth.login.helpdesk": {
    sie: "Melden Sie sich an, um Hilfe zu finden oder eine Anfrage an die IT zu stellen.",
    du: "Melde dich an, um Hilfe zu finden oder eine Anfrage an die IT zu stellen."
  },
  "platform.auth.microsoft.failed": {
    sie: "Die Anmeldung mit Microsoft 365 ist fehlgeschlagen. Bitte versuchen Sie es erneut.",
    du: "Die Anmeldung mit Microsoft 365 ist fehlgeschlagen. Bitte versuche es erneut."
  },
  "platform.auth.microsoft.unavailable": {
    sie: "Die Anmeldung mit Microsoft 365 ist derzeit nicht verf\xFCgbar. Bitte nutzen Sie E-Mail und Passwort.",
    du: "Die Anmeldung mit Microsoft 365 ist derzeit nicht verf\xFCgbar. Bitte nutze E-Mail und Passwort."
  },
  "platform.access.area.denied": {
    sie: "Sie haben keinen Zugriff auf diesen Bereich.",
    du: "Du hast keinen Zugriff auf diesen Bereich."
  },
  "platform.access.admin.denied": {
    sie: "F\xFCr die Verwaltung fehlt Ihnen die erforderliche Berechtigung.",
    du: "F\xFCr die Verwaltung fehlt dir die erforderliche Berechtigung."
  },
  "platform.access.profile.denied": {
    sie: "F\xFCr diesen Profilbereich fehlt Ihnen die Berechtigung.",
    du: "F\xFCr diesen Profilbereich fehlt dir die Berechtigung."
  },
  "platform.access.intranet.denied": {
    sie: "Intranet ist f\xFCr Sie nicht verf\xFCgbar.",
    du: "Intranet ist f\xFCr dich nicht verf\xFCgbar."
  },
  "platform.account.self.deactivate": {
    sie: "Sie k\xF6nnen Ihr eigenes Konto nicht deaktivieren.",
    du: "Du kannst dein eigenes Konto nicht deaktivieren."
  },
  "platform.account.self.deactivate.order": {
    sie: "Ihr eigenes Konto k\xF6nnen Sie nicht deaktivieren.",
    du: "Dein eigenes Konto kannst du nicht deaktivieren."
  },
  "platform.form.date": {
    sie: "Bitte geben Sie ein Datum im Format JJJJ-MM-TT an.",
    du: "Bitte gib ein Datum im Format JJJJ-MM-TT an."
  },
  "platform.form.legal-name": {
    sie: "Bitte geben Sie den rechtlichen Namen an.",
    du: "Bitte gib den rechtlichen Namen an."
  },
  "platform.form.account-holder": {
    sie: "Bitte geben Sie den Kontoinhaber an.",
    du: "Bitte gib den Kontoinhaber an."
  },
  "platform.form.iban": {
    sie: "Bitte geben Sie die IBAN an.",
    du: "Bitte gib die IBAN an."
  },
  "platform.form.currency": {
    sie: "Bitte w\xE4hlen Sie eine W\xE4hrung.",
    du: "Bitte w\xE4hle eine W\xE4hrung."
  },
  "platform.form.vat-id": {
    sie: "Bitte geben Sie die Umsatzsteuer-Id an.",
    du: "Bitte gib die Umsatzsteuer-Id an."
  },
  "platform.form.street": {
    sie: "Bitte geben Sie die Stra\xDFe an.",
    du: "Bitte gib die Stra\xDFe an."
  },
  "platform.form.postal-code": {
    sie: "Bitte geben Sie die Postleitzahl an.",
    du: "Bitte gib die Postleitzahl an."
  },
  "platform.form.city": {
    sie: "Bitte geben Sie den Ort an.",
    du: "Bitte gib den Ort an."
  },
  "platform.form.country": {
    sie: "Bitte geben Sie das Land mit zwei Buchstaben an.",
    du: "Bitte gib das Land mit zwei Buchstaben an."
  },
  "platform.form.name": {
    sie: "Bitte geben Sie den Namen an.",
    du: "Bitte gib den Namen an."
  },
  "platform.form.email": {
    sie: "Bitte geben Sie eine g\xFCltige E-Mail an.",
    du: "Bitte gib eine g\xFCltige E-Mail an."
  },
  "platform.form.designation": {
    sie: "Bitte geben Sie eine Bezeichnung an.",
    du: "Bitte gib eine Bezeichnung an."
  },
  "platform.form.article-group": {
    sie: "Bitte w\xE4hlen Sie eine Artikelgruppe.",
    du: "Bitte w\xE4hle eine Artikelgruppe."
  },
  "platform.form.base-unit": {
    sie: "Bitte w\xE4hlen Sie eine Basiseinheit.",
    du: "Bitte w\xE4hle eine Basiseinheit."
  },
  "platform.form.purchase-unit": {
    sie: "Bitte w\xE4hlen Sie eine Einkaufseinheit.",
    du: "Bitte w\xE4hle eine Einkaufseinheit."
  },
  "platform.form.code": {
    sie: "Bitte geben Sie einen Code an.",
    du: "Bitte gib einen Code an."
  },
  "platform.form.csv": {
    sie: "Bitte w\xE4hlen Sie eine CSV-Datei.",
    du: "Bitte w\xE4hle eine CSV-Datei."
  },
  "platform.form.file": {
    sie: "Bitte w\xE4hlen Sie eine Datei aus.",
    du: "Bitte w\xE4hle eine Datei aus."
  },
  "platform.form.image": {
    sie: "Bitte w\xE4hlen Sie ein Bild aus.",
    du: "Bitte w\xE4hle ein Bild aus."
  },
  "platform.form.partner": {
    sie: "Bitte w\xE4hlen Sie einen Gesch\xE4ftspartner.",
    du: "Bitte w\xE4hle einen Gesch\xE4ftspartner."
  },
  "platform.form.supplier-sku": {
    sie: "Bitte geben Sie die Lieferantenartikelnummer an.",
    du: "Bitte gib die Lieferantenartikelnummer an."
  },
  "platform.form.supplier": {
    sie: "Bitte w\xE4hlen Sie einen Lieferanten.",
    du: "Bitte w\xE4hle einen Lieferanten."
  },
  "platform.form.unit": {
    sie: "Bitte w\xE4hlen Sie eine Einheit.",
    du: "Bitte w\xE4hle eine Einheit."
  },
  "platform.form.price-cent": {
    sie: "Bitte geben Sie einen positiven Preis in Cent an.",
    du: "Bitte gib einen positiven Preis in Cent an."
  },
  "platform.profile.email.off": {
    sie: "E-Mails f\xFCr Ihre Rolle sind derzeit deaktiviert.",
    du: "E-Mails f\xFCr deine Rolle sind derzeit deaktiviert."
  },
  "platform.profile.email.on": {
    sie: "E-Mails f\xFCr Ihre Rolle sind aktiv.",
    du: "E-Mails f\xFCr deine Rolle sind aktiv."
  },
  "platform.profile.account": {
    sie: "Ihr Konto. Name und E-Mail pflegt die IT im Verzeichnis.",
    du: "Dein Konto. Name und E-Mail pflegt die IT im Verzeichnis."
  },
  "platform.profile.birthday": {
    sie: "Nur Monat und Tag, kein Jahr. Die Anzeige im Intranet ist abgeschaltet, bis Sie zustimmen.",
    du: "Nur Monat und Tag, kein Jahr. Die Anzeige im Intranet ist abgeschaltet, bis du zustimmst."
  },
  "platform.profile.unsaved": {
    sie: "Sie haben \xC4nderungen, die noch nicht gespeichert wurden. M\xF6chten Sie die Seite verlassen und die \xC4nderungen verwerfen?",
    du: "Du hast \xC4nderungen, die noch nicht gespeichert wurden. M\xF6chtest du die Seite verlassen und die \xC4nderungen verwerfen?"
  },
  "platform.profile.appearance.system": {
    sie: "System folgt der Einstellung Ihres Ger\xE4ts.",
    du: "System folgt der Einstellung deines Ger\xE4ts."
  },
  "platform.profile.admin.intro": {
    sie: "Zentrale Einstellungen, f\xFCr die Sie berechtigt sind.",
    du: "Zentrale Einstellungen, f\xFCr die du berechtigt bist."
  },
  "platform.profile.photo.zoom": {
    sie: "Tippen Sie das Foto an, um es zu vergr\xF6\xDFern.",
    du: "Tippe das Foto an, um es zu vergr\xF6\xDFern."
  },
  "platform.profile.notify.org-email-off": {
    sie: "Die Organisation hat E-Mails f\xFCr Ihre Rolle derzeit deaktiviert. In-App-Meldungen funktionieren weiter.",
    du: "Die Organisation hat E-Mails f\xFCr deine Rolle derzeit deaktiviert. In-App-Meldungen funktionieren weiter."
  },
  "platform.profile.notify.account-email": {
    sie: "Ziel ist Ihre Kontoadresse ({{email}}), hier nicht editierbar.",
    du: "Ziel ist deine Kontoadresse ({{email}}), hier nicht editierbar."
  },
  "platform.profile.notify.assign-locked": {
    sie: "E-Mail bei Zuweisung ist f\xFCr Ihre Rolle vorgeschrieben.",
    du: "E-Mail bei Zuweisung ist f\xFCr deine Rolle vorgeschrieben."
  },
  "platform.profile.password.local": {
    sie: "Pflicht f\xFCr lokale Konten. Setzen Sie ein Passwort oder senden Sie einen Link f\xFCr die erste Anmeldung und zum Zur\xFCcksetzen.",
    du: "Pflicht f\xFCr lokale Konten. Setze ein Passwort oder sende einen Link f\xFCr die erste Anmeldung und zum Zur\xFCcksetzen."
  },
  "platform.users.filters.reset": {
    sie: "Setzen Sie die Filter mit Zur\xFCcksetzen zur\xFCck.",
    du: "Setze die Filter mit Zur\xFCcksetzen zur\xFCck."
  },
  "platform.users.maintain": {
    sie: "pflegen Sie unter Stammdaten.",
    du: "pflegst du unter Stammdaten."
  },
  "platform.users.empty.before": {
    sie: "Richten Sie zuerst ",
    du: "Richte zuerst "
  },
  "platform.users.empty.between": {
    sie: " ein, pr\xFCfen Sie bei Bedarf die ",
    du: " ein, pr\xFCfe bei Bedarf die "
  },
  "platform.setup.org": {
    sie: "Legen Sie Ihre Organisation und das erste Administratorkonto an.",
    du: "Lege deine Organisation und das erste Administratorkonto an."
  },
  "platform.setup.modules": {
    sie: "W\xE4hlen Sie die Fachmodule f\xFCr den Start. Eine leere Auswahl bel\xE4sst nur den Kern. Sp\xE4ter \xE4ndern Sie die Auswahl unter Verwaltung \u2192 Module.",
    du: "W\xE4hle die Fachmodule f\xFCr den Start. Eine leere Auswahl bel\xE4sst nur den Kern. Sp\xE4ter \xE4nderst du die Auswahl unter Verwaltung \u2192 Module."
  },
  "platform.notifications.yours": {
    sie: "Ihre Mitteilungen aus allen Modulen.",
    du: "Deine Mitteilungen aus allen Modulen."
  },
  "platform.notifications.delete-all": {
    sie: "Ihr gesamter Mitteilungsverlauf wird entfernt, unabh\xE4ngig von den Filtern. Das l\xE4sst sich nicht r\xFCckg\xE4ngig machen.",
    du: "Dein gesamter Mitteilungsverlauf wird entfernt, unabh\xE4ngig von den Filtern. Das l\xE4sst sich nicht r\xFCckg\xE4ngig machen."
  },
  "platform.admin.voice.description": {
    sie: "Anrede und wie Namen erscheinen",
    du: "Anrede und wie Namen erscheinen"
  },
  "platform.ai.limits": {
    sie: "Fenster, Antwortl\xE4nge und wie viele Stellen eine Frage bekommt. Ein Profil tr\xE4gt die passenden Werte ein. Danach k\xF6nnen Sie jedes Feld einzeln \xE4ndern.",
    du: "Fenster, Antwortl\xE4nge und wie viele Stellen eine Frage bekommt. Ein Profil tr\xE4gt die passenden Werte ein. Danach kannst du jedes Feld einzeln \xE4ndern."
  },
  "platform.push.blocked": {
    sie: "Push wurde blockiert. Erlauben Sie Mitteilungen in den Browser-Einstellungen.",
    du: "Push wurde blockiert. Erlaube Mitteilungen in den Browser-Einstellungen."
  },
  "platform.push.blocked.browser": {
    sie: "Push ist im Browser blockiert. Entsperren Sie Mitteilungen in den Browser-Einstellungen und laden Sie die Seite neu.",
    du: "Push ist im Browser blockiert. Entsperre Mitteilungen in den Browser-Einstellungen und lade die Seite neu."
  },
  "platform.push.copied": {
    sie: "Adresse kopiert. F\xFCgen Sie sie in die Adresszeile ein.",
    du: "Adresse kopiert. F\xFCge sie in die Adresszeile ein."
  },
  "platform.push.copy-failed": {
    sie: "Die Adresse konnte nicht kopiert werden. Markieren Sie sie und kopieren Sie sie von Hand.",
    du: "Die Adresse konnte nicht kopiert werden. Markiere sie und kopiere sie von Hand."
  },
  "platform.push.copy-manual": {
    sie: "Danach diese Seite neu laden. Falls der Browser die Seite nicht selbst \xF6ffnet, kopieren Sie die Adresse in die Adresszeile.",
    du: "Danach diese Seite neu laden. Falls der Browser die Seite nicht selbst \xF6ffnet, kopiere die Adresse in die Adresszeile."
  },
  "platform.push.brave": {
    sie: "Schalten Sie \u201EGoogle-Dienste f\xFCr Push-Nachrichten verwenden\u201C ein.",
    du: "Schalte \u201EGoogle-Dienste f\xFCr Push-Nachrichten verwenden\u201C ein."
  },
  "platform.push.allow.site": {
    sie: "Erlauben Sie Mitteilungen f\xFCr diese Website.",
    du: "Erlaube Mitteilungen f\xFCr diese Website."
  },
  "platform.push.allow.firefox": {
    sie: "Erlauben Sie unter Berechtigungen die Benachrichtigungen f\xFCr diese Website.",
    du: "Erlaube unter Berechtigungen die Benachrichtigungen f\xFCr diese Website."
  },
  "platform.push.service.steps": {
    sie: "{{browser}} erreicht den Push-Dienst nicht. {{action}} \xD6ffnen Sie {{url}} und laden Sie danach diese Seite neu.",
    du: "{{browser}} erreicht den Push-Dienst nicht. {{action}} \xD6ffne {{url}} und lade danach diese Seite neu."
  },
  "platform.push.service.blocked": {
    sie: "Dieser Browser erreicht den Push-Dienst nicht. Schalten Sie den Push-Dienst in den Browser-Einstellungen ein und laden Sie die Seite neu.",
    du: "Dieser Browser erreicht den Push-Dienst nicht. Schalte den Push-Dienst in den Browser-Einstellungen ein und lade die Seite neu."
  },
  "platform.push.prompt.background": {
    sie: "System-Mitteilungen erscheinen auch, wenn der Browser im Hintergrund ist oder geschlossen wurde. Sie k\xF6nnen die Ereignisse weiter unter Profil \u2192 Mitteilungen steuern.",
    du: "System-Mitteilungen erscheinen auch, wenn der Browser im Hintergrund ist oder geschlossen wurde. Du kannst die Ereignisse weiter unter Profil \u2192 Mitteilungen steuern."
  },
  "platform.push.test": {
    sie: "Wenn Sie diese Mitteilung sehen, funktioniert Push auf diesem Ger\xE4t.",
    du: "Wenn du diese Mitteilung siehst, funktioniert Push auf diesem Ger\xE4t."
  },
  "platform.roles.it-admin.exclusion": {
    sie: "Diese Rechte bleiben der IT-Admin-Gruppe vorenthalten. Sie k\xF6nnen sie nur vergeben, wenn Sie sie selbst haben.",
    du: "Diese Rechte bleiben der IT-Admin-Gruppe vorenthalten. Du kannst sie nur vergeben, wenn du sie selbst hast."
  },
  "platform.roles.in-use": {
    sie: "Diese Berechtigungsgruppe ist {{people}} zugeordnet: {{listed}}. Entfernen Sie die Zuordnung, bevor Sie die Gruppe l\xF6schen.",
    du: "Diese Berechtigungsgruppe ist {{people}} zugeordnet: {{listed}}. Entferne die Zuordnung, bevor du die Gruppe l\xF6schst."
  },
  "platform.mail.reset.preheader": {
    sie: "Link zum Zur\xFCcksetzen Ihres Passworts",
    du: "Link zum Zur\xFCcksetzen deines Passworts"
  },
  "platform.mail.reset.body": {
    sie: "f\xFCr Ihr Konto im {{app}} wurde ein Link zum Zur\xFCcksetzen des Passworts angefordert. Der Link ist 60 Minuten g\xFCltig.",
    du: "f\xFCr dein Konto im {{app}} wurde ein Link zum Zur\xFCcksetzen des Passworts angefordert. Der Link ist 60 Minuten g\xFCltig."
  },
  "platform.mail.reset.requested": {
    sie: "f\xFCr Ihr Konto im {{app}} wurde ein Link zum Zur\xFCcksetzen des Passworts angefordert.",
    du: "f\xFCr dein Konto im {{app}} wurde ein Link zum Zur\xFCcksetzen des Passworts angefordert."
  },
  "platform.mail.reset.ignore": {
    sie: "Wenn Sie das nicht waren, k\xF6nnen Sie diese E-Mail ignorieren.",
    du: "Wenn du das nicht warst, kannst du diese E-Mail ignorieren."
  },
  "platform.mail.test.body": {
    sie: "dies ist eine Testnachricht aus der E-Mail-Verwaltung. Wenn Sie diese Nachricht lesen, ist der Versand eingerichtet.",
    du: "dies ist eine Testnachricht aus der E-Mail-Verwaltung. Wenn du diese Nachricht liest, ist der Versand eingerichtet."
  },
  "platform.mail.closed.comment": {
    sie: "Dieses Ticket ist geschlossen. \xD6ffentliche Antworten der IT erreichen Sie weiterhin per E-Mail. Nach Ablauf der Wiederer\xF6ffnungsfrist legen Sie bitte ein neues, verkn\xFCpftes Folgeticket an.",
    du: "Dieses Ticket ist geschlossen. \xD6ffentliche Antworten der IT erreichen dich weiterhin per E-Mail. Nach Ablauf der Wiederer\xF6ffnungsfrist lege bitte ein neues, verkn\xFCpftes Folgeticket an."
  },
  "platform.mail.closed.status": {
    sie: "Das Ticket ist geschlossen. Innerhalb der Wiederer\xF6ffnungsfrist k\xF6nnen Sie im Portal antworten; danach bitte ein verkn\xFCpftes Folgeticket anlegen.",
    du: "Das Ticket ist geschlossen. Innerhalb der Wiederer\xF6ffnungsfrist kannst du im Portal antworten; danach bitte ein verkn\xFCpftes Folgeticket anlegen."
  },
  "platform.mail.csat.title": {
    sie: "{{tag}} \u2013 Wie zufrieden waren Sie?",
    du: "{{tag}} \u2013 Wie zufrieden warst du?"
  },
  "platform.mail.csat.body": {
    sie: "Ihr Ticket #{{number}} \u201E{{subject}}\u201C ist geschlossen. Wie zufrieden waren Sie mit der Bearbeitung? Eine Sterne-Bewertung reicht.",
    du: "Dein Ticket #{{number}} \u201E{{subject}}\u201C ist geschlossen. Wie zufrieden warst du mit der Bearbeitung? Eine Sterne-Bewertung reicht."
  },
  "platform.mail.csat.body.short": {
    sie: "Ihr Ticket #{{number}} \u201E{{subject}}\u201C ist geschlossen. Wie zufrieden waren Sie mit der Bearbeitung?",
    du: "Dein Ticket #{{number}} \u201E{{subject}}\u201C ist geschlossen. Wie zufrieden warst du mit der Bearbeitung?"
  },
  "platform.mail.csat.reminder": {
    sie: "vor ein paar Tagen haben wir Sie um eine kurze Bewertung zu Ticket #{{number}} \u201E{{subject}}\u201C gebeten. Eine Sterne-Bewertung reicht \u2014 das Freitextfeld ist optional.",
    du: "vor ein paar Tagen haben wir dich um eine kurze Bewertung zu Ticket #{{number}} \u201E{{subject}}\u201C gebeten. Eine Sterne-Bewertung reicht \u2014 das Freitextfeld ist optional."
  },
  "platform.mail.csat.reminder.short": {
    sie: "vor ein paar Tagen haben wir Sie um eine kurze Bewertung zu Ticket #{{number}} gebeten. Falls Sie noch nicht geantwortet haben: eine Sterne-Bewertung reicht.",
    du: "vor ein paar Tagen haben wir dich um eine kurze Bewertung zu Ticket #{{number}} gebeten. Falls du noch nicht geantwortet hast: eine Sterne-Bewertung reicht."
  },
  "platform.mail.csat.open": {
    sie: "Oder \xF6ffnen Sie die Umfrage: {{url}}",
    du: "Oder \xF6ffne die Umfrage: {{url}}"
  },
  "platform.mail.intranet.ack.body": {
    sie: "bitte lesen Sie \u201E{{content.title}}\u201C und best\xE4tigen Sie das Lesen.",
    du: "bitte lies \u201E{{content.title}}\u201C und best\xE4tige das Lesen."
  },
  "platform.mail.leave.rejected.body": {
    sie: "Ihr Urlaubsantrag ({{leave.range}}) wurde abgelehnt.",
    du: "Dein Urlaubsantrag ({{leave.range}}) wurde abgelehnt."
  },
  "platform.mail.leave.soon.heading": {
    sie: "Ihr Urlaub beginnt bald",
    du: "Dein Urlaub beginnt bald"
  },
  "platform.mail.leave.soon.body": {
    sie: "Ihr genehmigter Urlaub ({{leave.range}}) beginnt in {{leave.days}} Tagen.",
    du: "Dein genehmigter Urlaub ({{leave.range}}) beginnt in {{leave.days}} Tagen."
  },
  "platform.mail.fleet.assigned.body": {
    sie: "Ihnen wurde das Fahrzeug {{vehicle.plate}} zugeordnet ({{fleet.period}}).",
    du: "Dir wurde das Fahrzeug {{vehicle.plate}} zugeordnet ({{fleet.period}})."
  },
  "platform.mail.maintenance.assigned.body": {
    sie: "Ihnen wurde der Wartungsauftrag {{order.number}} zugewiesen.",
    du: "Dir wurde der Wartungsauftrag {{order.number}} zugewiesen."
  },
  "platform.mail.maintenance.reassigned.body": {
    sie: "Ihnen wurde der Wartungsauftrag {{order.number}} neu zugewiesen.",
    du: "Dir wurde der Wartungsauftrag {{order.number}} neu zugewiesen."
  },
  "platform.mail.maintenance.pdf-failed.body": {
    sie: "die PDF-Erzeugung f\xFCr Auftrag {{order.number}} ist fehlgeschlagen. Bitte schlie\xDFen Sie den Auftrag erneut ab.",
    du: "die PDF-Erzeugung f\xFCr Auftrag {{order.number}} ist fehlgeschlagen. Bitte schlie\xDFe den Auftrag erneut ab."
  },
  "platform.mail.onboarding.assigned.body": {
    sie: "Ihnen wurde die Aufgabe \u201E{{onboarding.task}}\u201C f\xFCr {{onboarding.subject}} zugewiesen.",
    du: "Dir wurde die Aufgabe \u201E{{onboarding.task}}\u201C f\xFCr {{onboarding.subject}} zugewiesen."
  },
  "platform.mail.sample.comment": {
    sie: "Bitte pr\xFCfen Sie den Drucker erneut.",
    du: "Bitte pr\xFCfe den Drucker erneut."
  },
  "platform.mail.sample.assigned": {
    sie: "Ihnen wurde ein Ticket zugewiesen.",
    du: "Dir wurde ein Ticket zugewiesen."
  },
  "platform.mail.sample.approval": {
    sie: "Bitte entscheiden Sie \xFCber dieses Ticket.",
    du: "Bitte entscheide \xFCber dieses Ticket."
  },
  "platform.mail.sample.csat.title": {
    sie: "[Helpdesk #42] \u2013 Wie zufrieden waren Sie?",
    du: "[Helpdesk #42] \u2013 Wie zufrieden warst du?"
  },
  "platform.mail.sample.csat.body": {
    sie: "Ihr Ticket #42 \u201EDrucker klemmt\u201C ist geschlossen. Wie zufrieden waren Sie mit der Bearbeitung? Eine Sterne-Bewertung reicht.",
    du: "Dein Ticket #42 \u201EDrucker klemmt\u201C ist geschlossen. Wie zufrieden warst du mit der Bearbeitung? Eine Sterne-Bewertung reicht."
  },
  "platform.mail.sample.ack.body": {
    sie: "Bitte lesen Sie diese Nachricht und best\xE4tigen Sie das Lesen.",
    du: "Bitte lies diese Nachricht und best\xE4tige das Lesen."
  },
  "platform.mail.sample.ack.reminder": {
    sie: "Bitte best\xE4tigen Sie noch das Lesen dieser Nachricht.",
    du: "Bitte best\xE4tige noch das Lesen dieser Nachricht."
  },
  "platform.mail.sample.comment.reply": {
    sie: "Lea Leitung hat auf Ihren Kommentar geantwortet.",
    du: "Lea Leitung hat auf deinen Kommentar geantwortet."
  },
  "platform.mail.sample.comment.liked": {
    sie: "Lea Leitung gef\xE4llt Ihren Kommentar.",
    du: "Lea Leitung gef\xE4llt deinen Kommentar."
  },
  "platform.mail.sample.mention": {
    sie: "Lea Leitung hat Sie in \u201ESommerfest\u201C erw\xE4hnt.",
    du: "Lea Leitung hat dich in \u201ESommerfest\u201C erw\xE4hnt."
  },
  "platform.mail.sample.leave.approved": {
    sie: "Ihr Urlaubsantrag (02.03.2026 \u2013 06.03.2026) wurde genehmigt.",
    du: "Dein Urlaubsantrag (02.03.2026 \u2013 06.03.2026) wurde genehmigt."
  },
  "platform.mail.sample.leave.rejected": {
    sie: "Ihr Urlaubsantrag (02.03.2026 \u2013 06.03.2026) wurde abgelehnt.",
    du: "Dein Urlaubsantrag (02.03.2026 \u2013 06.03.2026) wurde abgelehnt."
  },
  "platform.mail.sample.leave.soon": {
    sie: "Ihr genehmigter Urlaub (02.03.2026 \u2013 06.03.2026) beginnt in 5 Tagen.",
    du: "Dein genehmigter Urlaub (02.03.2026 \u2013 06.03.2026) beginnt in 5 Tagen."
  },
  "platform.mail.sample.fleet.assigned": {
    sie: "Ihnen wurde das Fahrzeug WI-GB 2042 zugeordnet (01.03.2026\u201331.03.2026).",
    du: "Dir wurde das Fahrzeug WI-GB 2042 zugeordnet (01.03.2026\u201331.03.2026)."
  },
  "platform.mail.sample.fleet.closed": {
    sie: "Ihre Meldung zu WI-GB 2042 wurde erledigt.",
    du: "Deine Meldung zu WI-GB 2042 wurde erledigt."
  },
  "platform.mail.sample.maintenance.assigned": {
    sie: "Ihnen wurde ein neuer Wartungsauftrag zugewiesen.",
    du: "Dir wurde ein neuer Wartungsauftrag zugewiesen."
  },
  "platform.mail.sample.maintenance.reassigned": {
    sie: "Ihnen wurde ein Wartungsauftrag neu zugewiesen.",
    du: "Dir wurde ein Wartungsauftrag neu zugewiesen."
  },
  "platform.mail.sample.onboarding.assigned": {
    sie: "Ihnen wurde die Aufgabe \u201ELaptop ausgeben\u201C f\xFCr Anna Beispiel zugewiesen.",
    du: "Dir wurde die Aufgabe \u201ELaptop ausgeben\u201C f\xFCr Anna Beispiel zugewiesen."
  },
  "platform.notify.ticket.assigned.title": {
    sie: "Ticket #{{number}}: Ihnen zugewiesen",
    du: "Ticket #{{number}}: dir zugewiesen"
  },
  "platform.notify.ticket.assigned.body": {
    sie: "Ihnen wurde das Ticket \u201E{{subject}}\u201C zugewiesen.",
    du: "Dir wurde das Ticket \u201E{{subject}}\u201C zugewiesen."
  },
  "platform.notify.prefs.incomplete": {
    sie: "Bitte setzen Sie alle Benachrichtigungsarten.",
    du: "Bitte setze alle Benachrichtigungsarten."
  },
  "platform.notify.label.content-liked": {
    sie: "Like auf Ihren Beitrag",
    du: "Like auf deinen Beitrag"
  },
  "platform.notify.label.comment-created": {
    sie: "Kommentar zu Ihrem Beitrag",
    du: "Kommentar zu deinem Beitrag"
  },
  "platform.notify.label.comment-replied": {
    sie: "Antwort auf Ihren Kommentar",
    du: "Antwort auf deinen Kommentar"
  },
  "platform.notify.label.comment-liked": {
    sie: "Like auf Ihren Kommentar",
    du: "Like auf deinen Kommentar"
  },
  "platform.notify.desc.ticket.created": {
    sie: "IT: Wenn ein neues Ticket in Ihrer Warteschlange oder Ihrem Team eingeht (Teamleitung und IT-Admin werden zus\xE4tzlich informiert). Pers\xF6nliche Zuweisung kommt separat als \u201EZuweisung\u201C.",
    du: "IT: Wenn ein neues Ticket in deiner Warteschlange oder deinem Team eingeht (Teamleitung und IT-Admin werden zus\xE4tzlich informiert). Pers\xF6nliche Zuweisung kommt separat als \u201EZuweisung\u201C."
  },
  "platform.notify.desc.ticket.status": {
    sie: "Wenn sich der Status eines Ihrer Tickets \xE4ndert.",
    du: "Wenn sich der Status eines deiner Tickets \xE4ndert."
  },
  "platform.notify.desc.ticket.assigned": {
    sie: "IT: Wenn Ihnen ein Ticket pers\xF6nlich zugewiesen wird. F\xFCr Agenten und Teamleitung ist die E-Mail Pflicht; IT-Admins k\xF6nnen sie abschalten.",
    du: "IT: Wenn dir ein Ticket pers\xF6nlich zugewiesen wird. F\xFCr Agenten und Teamleitung ist die E-Mail Pflicht; IT-Admins k\xF6nnen sie abschalten."
  },
  "platform.notify.desc.ticket.updated": {
    sie: "Wenn Betreff, Priorit\xE4t oder Kategorie eines Ihrer Tickets ge\xE4ndert wird (geb\xFCndelt, nicht bei jedem Tastendruck).",
    du: "Wenn Betreff, Priorit\xE4t oder Kategorie eines deiner Tickets ge\xE4ndert wird (geb\xFCndelt, nicht bei jedem Tastendruck)."
  },
  "platform.notify.desc.ticket.approval": {
    sie: "Wenn Sie ein Ticket freigeben oder ablehnen sollen.",
    du: "Wenn du ein Ticket freigeben oder ablehnen sollst."
  },
  "platform.notify.desc.ticket.incident": {
    sie: "Wenn die IT eine Statusmeldung zu einem Major Incident sendet, der Sie betrifft.",
    du: "Wenn die IT eine Statusmeldung zu einem Major Incident sendet, der dich betrifft."
  },
  "platform.notify.desc.ticket.automation": {
    sie: "Wenn eine Automatisierungsregel eine Benachrichtigung an Sie sendet.",
    du: "Wenn eine Automatisierungsregel eine Benachrichtigung an dich sendet."
  },
  "platform.notify.desc.ticket.remote": {
    sie: "Wenn eine Remote-Support-Sitzung zu Ihrem Ticket angefragt, freigegeben oder beendet wird.",
    du: "Wenn eine Remote-Support-Sitzung zu deinem Ticket angefragt, freigegeben oder beendet wird."
  },
  "platform.notify.desc.ticket.csat": {
    sie: "Wenn wir Sie nach dem Schlie\xDFen eines Tickets um eine kurze Bewertung bitten.",
    du: "Wenn wir dich nach dem Schlie\xDFen eines Tickets um eine kurze Bewertung bitten."
  },
  "platform.notify.desc.ticket.resolved": {
    sie: "Wenn ein gel\xF6stes Ticket bald automatisch geschlossen wird, falls Sie sich nicht zur\xFCckmelden.",
    du: "Wenn ein gel\xF6stes Ticket bald automatisch geschlossen wird, falls du dich nicht zur\xFCckmeldest."
  },
  "platform.notify.desc.intranet.news": {
    sie: "Wenn im Intranet eine neue Nachricht f\xFCr Sie ver\xF6ffentlicht wird.",
    du: "Wenn im Intranet eine neue Nachricht f\xFCr dich ver\xF6ffentlicht wird."
  },
  "platform.notify.desc.intranet.ack": {
    sie: "Wenn Sie eine wichtige Nachricht lesen und best\xE4tigen sollen.",
    du: "Wenn du eine wichtige Nachricht lesen und best\xE4tigen sollst."
  },
  "platform.notify.desc.intranet.ack-reminder": {
    sie: "Wenn die Frist f\xFCr eine Lesebest\xE4tigung bald abl\xE4uft und Sie noch nicht best\xE4tigt haben.",
    du: "Wenn die Frist f\xFCr eine Lesebest\xE4tigung bald abl\xE4uft und du noch nicht best\xE4tigt hast."
  },
  "platform.notify.desc.intranet.liked": {
    sie: "Wenn jemand Ihren Intranet-Beitrag mit \u201EGef\xE4llt mir\u201C markiert (Standard: nur Glocke).",
    du: "Wenn jemand deinen Intranet-Beitrag mit \u201EGef\xE4llt mir\u201C markiert (Standard: nur Glocke)."
  },
  "platform.notify.desc.intranet.comment": {
    sie: "Wenn jemand Ihren Intranet-Beitrag kommentiert (Standard: nur Glocke).",
    du: "Wenn jemand deinen Intranet-Beitrag kommentiert (Standard: nur Glocke)."
  },
  "platform.notify.desc.intranet.reply": {
    sie: "Wenn jemand auf Ihren Kommentar antwortet (Standard: nur Glocke).",
    du: "Wenn jemand auf deinen Kommentar antwortet (Standard: nur Glocke)."
  },
  "platform.notify.desc.intranet.comment-liked": {
    sie: "Wenn jemand Ihren Kommentar mit \u201EGef\xE4llt mir\u201C markiert (Standard: nur Glocke).",
    du: "Wenn jemand deinen Kommentar mit \u201EGef\xE4llt mir\u201C markiert (Standard: nur Glocke)."
  },
  "platform.notify.desc.intranet.mention": {
    sie: "Wenn Sie in einem Kommentar mit @ erw\xE4hnt werden (Standard: nur Glocke).",
    du: "Wenn du in einem Kommentar mit @ erw\xE4hnt wirst (Standard: nur Glocke)."
  },
  "platform.notify.desc.intranet.diary": {
    sie: "Wenn ein Bautagesbericht an Sie geht. Glocke, Push und die E-Mail mit PDF lassen sich hier abschalten. Die Redaktion legt an der Modul-Instanz fest, welche Kan\xE4le versucht werden.",
    du: "Wenn ein Bautagesbericht an dich geht. Glocke, Push und die E-Mail mit PDF lassen sich hier abschalten. Die Redaktion legt an der Modul-Instanz fest, welche Kan\xE4le versucht werden."
  },
  "platform.notify.desc.leave.approved": {
    sie: "Wenn Ihr Urlaubsantrag genehmigt wurde.",
    du: "Wenn dein Urlaubsantrag genehmigt wurde."
  },
  "platform.notify.desc.leave.rejected": {
    sie: "Wenn Ihr Urlaubsantrag abgelehnt wurde.",
    du: "Wenn dein Urlaubsantrag abgelehnt wurde."
  },
  "platform.notify.desc.fleet.assigned": {
    sie: "Wenn Ihnen ein Firmenwagen zugeordnet oder ein Poolfahrzeug gebucht wurde (Standard: Glocke und E-Mail).",
    du: "Wenn dir ein Firmenwagen zugeordnet oder ein Poolfahrzeug gebucht wurde (Standard: Glocke und E-Mail)."
  },
  "platform.notify.desc.fleet.closed": {
    sie: "Wenn Ihre Schadenmeldung erledigt oder abgelehnt wurde (Standard: nur Glocke).",
    du: "Wenn deine Schadenmeldung erledigt oder abgelehnt wurde (Standard: nur Glocke)."
  },
  "platform.notify.desc.maintenance.assigned": {
    sie: "Wenn Ihnen ein Wartungsauftrag zugewiesen wurde (Standard: Glocke und E-Mail).",
    du: "Wenn dir ein Wartungsauftrag zugewiesen wurde (Standard: Glocke und E-Mail)."
  },
  "platform.notify.desc.maintenance.reassigned": {
    sie: "Wenn Ihnen ein Wartungsauftrag neu zugewiesen wurde (Standard: Glocke und E-Mail).",
    du: "Wenn dir ein Wartungsauftrag neu zugewiesen wurde (Standard: Glocke und E-Mail)."
  },
  "platform.notify.desc.onboarding.assigned": {
    sie: "Wenn Ihnen eine Aufgabe in Vorbereitung, Eintritt oder Austritt zugewiesen wurde (Standard: Glocke und E-Mail).",
    du: "Wenn dir eine Aufgabe in Vorbereitung, Eintritt oder Austritt zugewiesen wurde (Standard: Glocke und E-Mail)."
  },
  "platform.notify.desc.onboarding.due": {
    sie: "Wenn eine Ihrer Aufgaben in Vorbereitung, Eintritt oder Austritt bald f\xE4llig wird (Standard: Glocke und E-Mail).",
    du: "Wenn eine deiner Aufgaben in Vorbereitung, Eintritt oder Austritt bald f\xE4llig wird (Standard: Glocke und E-Mail)."
  },
  "platform.notify.desc.onboarding.overdue": {
    sie: "Wenn eine Ihrer Aufgaben in Vorbereitung, Eintritt oder Austritt \xFCberf\xE4llig ist (Standard: Glocke und E-Mail).",
    du: "Wenn eine deiner Aufgaben in Vorbereitung, Eintritt oder Austritt \xFCberf\xE4llig ist (Standard: Glocke und E-Mail)."
  },
  "platform.seed.intranet.welcome.body": {
    sie: "Das Intranet ist die Startseite des Mitarbeiterportals. Hier finden Sie aktuelle Nachrichten und die Anwendungen der Firma.",
    du: "Das Intranet ist die Startseite des Mitarbeiterportals. Hier findest du aktuelle Nachrichten und die Anwendungen der Firma."
  },
  "platform.seed.kb.vpn.summary": {
    sie: "So verbinden Sie Notebooks au\xDFerhalb des B\xFCros mit dem Firmennetz.",
    du: "So verbindest du Notebooks au\xDFerhalb des B\xFCros mit dem Firmennetz."
  },
  "platform.seed.kb.password.body": {
    sie: "<p>\xD6ffnen Sie <strong>https://aka.ms/sspr</strong> und folgen Sie den Schritten. Die IT setzt Kennw\xF6rter nicht telefonisch zur\xFCck.</p><p>Nach dem Reset bitte am PC und am Handy neu anmelden.</p>",
    du: "<p>\xD6ffne <strong>https://aka.ms/sspr</strong> und folge den Schritten. Die IT setzt Kennw\xF6rter nicht telefonisch zur\xFCck.</p><p>Nach dem Reset bitte am PC und am Handy neu anmelden.</p>"
  },
  "platform.seed.kb.faq-hours.body": {
    sie: "<p>Wir lesen neue Anfragen fortlaufend w\xE4hrend der Arbeitszeit. Einfache Themen (Passwort, Drucker, VPN) sind oft schnell erledigt. Bei Bestellungen oder Freigaben kann es etwas l\xE4nger dauern, weil Kolleginnen und Kollegen zustimmen m\xFCssen.</p><p>Wenn nichts mehr geht \u2014 zum Beispiel niemand kann sich anmelden \u2014 schreiben Sie bitte \u201Edringend\u201C in den Betreff oder rufen Sie uns intern an.</p>",
    du: "<p>Wir lesen neue Anfragen fortlaufend w\xE4hrend der Arbeitszeit. Einfache Themen (Passwort, Drucker, VPN) sind oft schnell erledigt. Bei Bestellungen oder Freigaben kann es etwas l\xE4nger dauern, weil Kolleginnen und Kollegen zustimmen m\xFCssen.</p><p>Wenn nichts mehr geht \u2014 zum Beispiel niemand kann sich anmelden \u2014 schreib bitte \u201Edringend\u201C in den Betreff oder ruf uns intern an.</p>"
  },
  "platform.seed.kb.faq-ticket.summary": {
    sie: "\xDCber \u201ENeue Anfrage\u201C beschreiben Sie Ihr Anliegen. Oft erscheint schon beim Tippen eine passende Anleitung.",
    du: "\xDCber \u201ENeue Anfrage\u201C beschreibst du dein Anliegen. Oft erscheint schon beim Tippen eine passende Anleitung."
  },
  "platform.seed.kb.faq-ticket.body": {
    sie: "<p>\xD6ffnen Sie <strong>Meine Anfragen</strong> und w\xE4hlen Sie <strong>Neue Anfrage</strong>. Ein kurzer Betreff und ein paar S\xE4tze reichen. Bilder oder Fehlermeldungen als Anhang helfen uns sehr.</p><p>W\xE4hrend Sie schreiben, schlagen wir passende Hilfeseiten vor. Wenn eine L\xF6sung passt, m\xFCssen Sie uns nicht extra schreiben.</p>",
    du: "<p>\xD6ffne <strong>Meine Anfragen</strong> und w\xE4hle <strong>Neue Anfrage</strong>. Ein kurzer Betreff und ein paar S\xE4tze reichen. Bilder oder Fehlermeldungen als Anhang helfen uns sehr.</p><p>W\xE4hrend du schreibst, schlagen wir passende Hilfeseiten vor. Wenn eine L\xF6sung passt, musst du uns nicht extra schreiben.</p>"
  },
  "platform.seed.kb.faq-password.summary": {
    sie: "Setzen Sie das Kennwort selbst \xFCber die Microsoft-Seite zur\xFCck. Die IT fragt am Telefon nie nach Ihrem Passwort.",
    du: "Setze das Kennwort selbst \xFCber die Microsoft-Seite zur\xFCck. Die IT fragt am Telefon nie nach deinem Passwort."
  },
  "platform.seed.kb.faq-password.body": {
    sie: "<p>\xD6ffnen Sie <strong>https://aka.ms/sspr</strong> und folgen Sie den Schritten. Danach bitte am PC und am Handy neu anmelden.</p><p>Die IT setzt Kennw\xF6rter nicht telefonisch zur\xFCck \u2014 das sch\xFCtzt Ihr Konto. Eine ausf\xFChrliche Anleitung finden Sie unter \u201EPasswort zur\xFCcksetzen\u201C.</p>",
    du: "<p>\xD6ffne <strong>https://aka.ms/sspr</strong> und folge den Schritten. Danach bitte am PC und am Handy neu anmelden.</p><p>Die IT setzt Kennw\xF6rter nicht telefonisch zur\xFCck \u2014 das sch\xFCtzt dein Konto. Eine ausf\xFChrliche Anleitung findest du unter \u201EPasswort zur\xFCcksetzen\u201C.</p>"
  },
  "platform.seed.kb.faq-vpn.summary": {
    sie: "Verbinden Sie das Notebook \xFCber das Firmen-VPN. Ohne VPN erreichen Sie die internen Laufwerke und Drucker nicht.",
    du: "Verbinde das Notebook \xFCber das Firmen-VPN. Ohne VPN erreichst du die internen Laufwerke und Drucker nicht."
  },
  "platform.seed.kb.faq-vpn.body": {
    sie: "<p>Starten Sie den VPN-Client, w\xE4hlen Sie das Profil \u201EGebr\xFCder Becker\u201C und melden Sie sich mit Ihrem \xFCblichen Konto an. Eine Schritt-f\xFCr-Schritt-Anleitung steht unter \u201EVPN von unterwegs einrichten\u201C.</p><p>Hakt die Verbindung, pr\xFCfen Sie zuerst das WLAN. Bleibt es rot, schreiben Sie uns eine Anfrage.</p>",
    du: "<p>Starte den VPN-Client, w\xE4hle das Profil \u201EGebr\xFCder Becker\u201C und melde dich mit deinem \xFCblichen Konto an. Eine Schritt-f\xFCr-Schritt-Anleitung steht unter \u201EVPN von unterwegs einrichten\u201C.</p><p>Hakt die Verbindung, pr\xFCfe zuerst das WLAN. Bleibt es rot, schreib uns eine Anfrage.</p>"
  },
  "platform.seed.kb.faq-privacy.summary": {
    sie: "Nur Sie und die IT. Kolleginnen und Kollegen aus anderen Bereichen k\xF6nnen Ihre Anfragen nicht lesen.",
    du: "Nur du und die IT. Kolleginnen und Kollegen aus anderen Bereichen k\xF6nnen deine Anfragen nicht lesen."
  },
  "platform.seed.kb.faq-privacy.body": {
    sie: "<p>Ihre Anfragen sind privat. Die IT sieht sie, um zu helfen. Interne Notizen der IT bleiben f\xFCr Sie unsichtbar.</p><p>Wenn eine Freigabe n\xF6tig ist (zum Beispiel bei kostenpflichtigen Bestellungen), werden die zust\xE4ndigen Personen informiert \u2014 nicht die ganze Firma.</p>",
    du: "<p>\nDeine Anfragen sind privat. Die IT sieht sie, um zu helfen. Interne Notizen der IT bleiben f\xFCr dich unsichtbar.</p><p>Wenn eine Freigabe n\xF6tig ist (zum Beispiel bei kostenpflichtigen Bestellungen), werden die zust\xE4ndigen Personen informiert \u2014 nicht die ganze Firma.</p>"
  },
  "platform.seed.kb.faq-hardware.summary": {
    sie: "Stellen Sie eine Anfrage mit dem gew\xFCnschten Artikel. Ab einem bestimmten Betrag muss jemand aus Ihrer Abteilung zustimmen.",
    du: "Stelle eine Anfrage mit dem gew\xFCnschten Artikel. Ab einem bestimmten Betrag muss jemand aus deiner Abteilung zustimmen."
  },
  "platform.seed.kb.faq-hardware.body": {
    sie: "<p>Schreiben Sie uns, welches Ger\xE4t oder welche Software Sie brauchen und wof\xFCr. Wenn Kosten entstehen, tragen Sie gern einen ungef\xE4hren Betrag ein.</p><p>Manche Bestellungen brauchen eine Freigabe. Sie erhalten dann eine E-Mail und sehen den Stand in Ihrer Anfrage.</p>",
    du: "<p>Schreib uns, welches Ger\xE4t oder welche Software du brauchst und wof\xFCr. Wenn Kosten entstehen, trag gern einen ungef\xE4hren Betrag ein.</p><p>Manche Bestellungen brauchen eine Freigabe. Du erh\xE4ltst dann eine E-Mail und siehst den Stand in deiner Anfrage.</p>"
  }
};

// src/voice-copy.ts
var VOICE_COPY = {
  "voice.copy.missing": {
    sie: "Dieser Text ist nicht hinterlegt.",
    du: "Dieser Text ist nicht hinterlegt."
  },
  "kb.title.min": {
    sie: "Bitte geben Sie einen Titel mit mindestens 3 Zeichen ein.",
    du: "Bitte gib einen Titel mit mindestens 3 Zeichen ein."
  },
  "intranet.ack.heading": {
    sie: "Bitte best\xE4tigen Sie das Lesen",
    du: "Bitte best\xE4tige das Lesen"
  },
  "portal.home.welcome": {
    sie: "Willkommen, {{actor}}. W\xE4hlen Sie eine Anwendung.",
    du: "Willkommen, {{actor}}. W\xE4hle eine Anwendung."
  },
  "voice.preview.sample": {
    sie: "Bitte pr\xFCfen Sie diese Vorschau.",
    du: "Bitte pr\xFCfe diese Vorschau."
  },
  "voice.value.invalid": {
    sie: "W\xE4hlen Sie eine g\xFCltige Anrede oder Namensanzeige.",
    du: "W\xE4hle eine g\xFCltige Anrede oder Namensanzeige."
  },
  "auth.sign-in.required": {
    sie: "Bitte melden Sie sich an.",
    du: "Bitte melde dich an."
  },
  "auth.voice.denied": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr die Anrede.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr die Anrede."
  },
  "auth.typography.denied": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr die Darstellung.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr die Darstellung."
  },
  "typography.value.invalid": {
    sie: "W\xE4hlen Sie eine g\xFCltige Schrift und Schriftgr\xF6\xDFe.",
    du: "W\xE4hle eine g\xFCltige Schrift und Schriftgr\xF6\xDFe."
  },
  "auth.denied.generic": {
    sie: "Sie haben nicht die erforderliche Berechtigung.",
    du: "Du hast nicht die erforderliche Berechtigung."
  },
  "auth.denied.audit": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr das Audit-Log.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr das Audit-Log."
  },
  "auth.denied.csat": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr die CSAT-Auswertung.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr die CSAT-Auswertung."
  },
  "auth.denied.master-data": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr die Stammdaten.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr die Stammdaten."
  },
  "auth.denied.organisation": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr die Organisationsverwaltung.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr die Organisationsverwaltung."
  },
  "auth.denied.roles": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr Berechtigungsgruppen.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr Berechtigungsgruppen."
  },
  "auth.denied.modules": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr die Module.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr die Module."
  },
  "auth.denied.tenants": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr die Mandantenverwaltung.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr die Mandantenverwaltung."
  },
  "auth.denied.ai": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr den KI-Anschluss.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr den KI-Anschluss."
  },
  "auth.denied.mail": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr die E-Mail-Verbindung.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr die E-Mail-Verbindung."
  },
  "auth.denied.directory": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr das Verzeichnis.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr das Verzeichnis."
  },
  "auth.denied.tickets": {
    sie: "Sie haben nicht die erforderliche Berechtigung, Tickets zu sehen.",
    du: "Du hast nicht die erforderliche Berechtigung, Tickets zu sehen."
  },
  "auth.denied.queues": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr die Warteschlangen-\xDCbersicht.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr die Warteschlangen-\xDCbersicht."
  },
  "auth.denied.changes": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr den Change-Kalender.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr den Change-Kalender."
  },
  "auth.denied.sla": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr die SLA-Verwaltung.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr die SLA-Verwaltung."
  },
  "auth.denied.approvals": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr die Freigabe-Workflows.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr die Freigabe-Workflows."
  },
  "auth.denied.automation": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr Automatisierungsregeln.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr Automatisierungsregeln."
  },
  "auth.denied.integrations": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr Integrationen.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr Integrationen."
  },
  "auth.denied.kb": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr die Wissensdatenbank.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr die Wissensdatenbank."
  },
  "auth.denied.assets": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr die Asset-Verwaltung.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr die Asset-Verwaltung."
  },
  "auth.denied.settings": {
    sie: "Sie haben nicht die erforderliche Berechtigung f\xFCr die Einstellungen.",
    du: "Du hast nicht die erforderliche Berechtigung f\xFCr die Einstellungen."
  },
  "voice.mail.pronoun": {
    sie: "Die Fassung der Vorlage passt nicht zum Feld.",
    du: "Die Fassung der Vorlage passt nicht zum Feld."
  },
  "ai.address.instruction": {
    sie: "Sprechen Sie die lesende Person formell an.",
    du: "Sprich die lesende Person informell an."
  },
  ...HELPDESK_VOICE_COPY,
  ...INTRANET_VOICE_COPY,
  ...OPERATIONS_VOICE_COPY,
  ...PLATFORM_VOICE_COPY
};
var PLACEHOLDER = /\{\{\s*([A-Za-z0-9_.]+)\s*\}\}/g;
function placeholderValue(value, voice) {
  if (typeof value === "string") {
    return value;
  }
  return formatPersonName(value, voice);
}
function renderVoiceCopy(id, voice, params = {}) {
  const entry = Object.prototype.hasOwnProperty.call(VOICE_COPY, id) ? VOICE_COPY[id] : VOICE_COPY["voice.copy.missing"];
  const template2 = voice.addressForm === "du" ? entry.du : entry.sie;
  return template2.replace(PLACEHOLDER, (_match, token) => {
    const value = params[token];
    if (value == null) {
      return "";
    }
    return placeholderValue(value, voice);
  });
}
function presentVoiceText(text, voice, params = {}) {
  if (Object.prototype.hasOwnProperty.call(VOICE_COPY, text)) {
    return renderVoiceCopy(text, voice, params);
  }
  return text;
}
function isVoiceCopyId(value) {
  return Object.prototype.hasOwnProperty.call(VOICE_COPY, value);
}
var LEGACY_COPY_PATTERNS = [];
function matchLegacyCopy(eventKey, title, body) {
  const hit = LEGACY_COPY_PATTERNS.find(
    (pattern) => pattern.eventKey === eventKey && pattern.title === title && pattern.body === body
  );
  if (!hit) {
    return null;
  }
  return {
    copyKey: hit.copyKey,
    copyParams: hit.titleKey ? { titleKey: hit.titleKey } : {}
  };
}
function copyParamsFromJson(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  const params = {};
  for (const [key, item] of Object.entries(value)) {
    if (typeof item === "string") {
      params[key] = item;
    } else if (item && typeof item === "object" && "displayName" in item && typeof item.displayName === "string") {
      const person = item;
      params[key] = {
        firstName: typeof person.firstName === "string" ? person.firstName : "",
        lastName: typeof person.lastName === "string" ? person.lastName : "",
        displayName: person.displayName
      };
    }
  }
  return params;
}
function renderNotificationText(input, voice) {
  const stored = input.copyKey ? { copyKey: input.copyKey, copyParams: copyParamsFromJson(input.copyParams) } : matchLegacyCopy(input.eventKey, input.title, input.body);
  if (!stored) {
    return { title: input.title, body: input.body };
  }
  const titleKey = stored.copyParams.titleKey;
  return {
    title: typeof titleKey === "string" ? renderVoiceCopy(titleKey, voice, stored.copyParams) : input.title,
    body: renderVoiceCopy(stored.copyKey, voice, stored.copyParams)
  };
}

// src/maintenance-protocol-readiness.ts
var SCAN_MESSAGE = {
  PENDING: "Ein Anhang wird noch gepr\xFCft.",
  BLOCKED: "Ein Anhang ist nicht freigegeben."
};
var SCAN_CODE = {
  PENDING: "MAINTENANCE_ATTACHMENT_SCAN_PENDING",
  BLOCKED: "MAINTENANCE_ATTACHMENT_SCAN_BLOCKED"
};
function maintenanceProtocolFinalizeReadiness(source, requirements, voice) {
  const blocked = source.attachments.find(
    (attachment) => attachment.scanStatus === "PENDING" || attachment.scanStatus === "BLOCKED"
  );
  if (blocked && (blocked.scanStatus === "PENDING" || blocked.scanStatus === "BLOCKED")) {
    const message = SCAN_MESSAGE[blocked.scanStatus];
    return {
      ready: false,
      message,
      issues: [{ fieldKey: blocked.fieldKey ?? blocked.id, message }],
      scan: { code: SCAN_CODE[blocked.scanStatus] }
    };
  }
  const snapshot = maintenanceProtocolSnapshotSchema.parse(source.templateSnapshot);
  const validation = validateMaintenanceProtocolForFinalize(
    snapshot,
    source.answers.map((answer) => ({ fieldKey: answer.fieldKey, value: answer.value })),
    source.attachments.map((attachment) => ({
      id: attachment.id,
      fieldKey: attachment.fieldKey,
      scanStatus: attachment.scanStatus
    })),
    source.signatures.map((signature) => ({ kind: signature.kind })),
    requirements
  );
  if (validation.ok) {
    return { ready: true };
  }
  const issues = validation.issues.map((issue) => ({
    fieldKey: issue.fieldKey,
    message: presentVoiceText(issue.message, voice)
  }));
  return {
    ready: false,
    message: maintenanceProtocolIssuesMessage(issues),
    issues,
    scan: null
  };
}
var OnboardingCaseKind = {
  ONBOARDING: "onboarding",
  OFFBOARDING: "offboarding"
};
var onboardingCaseKindSchema = z.enum(OnboardingCaseKind);
var OnboardingCaseStatus = {
  DRAFT: "draft",
  ACTIVE: "active",
  COMPLETED: "completed",
  CANCELLED: "cancelled"
};
var onboardingCaseStatusSchema = z.enum(OnboardingCaseStatus);
var ONBOARDING_CASE_TRANSITIONS = {
  draft: ["active", "cancelled"],
  active: ["completed", "cancelled"],
  completed: ["active"],
  cancelled: ["active"]
};
function canTransitionOnboardingCase(from, to) {
  return ONBOARDING_CASE_TRANSITIONS[from].includes(to);
}
var onboardingTemplateRemoveBlockedDetailsSchema = z.object({
  name: z.string(),
  kind: onboardingCaseKindSchema,
  caseCount: z.number().int().nonnegative(),
  isActive: z.boolean()
});
var ONBOARDING_TEMPLATE_SCHEMA_VERSION = 1;
var onboardingTemplateVersionStatusSchema = z.enum(["draft", "published", "retired"]);
var onboardingTaskKindSchema = z.enum([
  "confirm",
  "info",
  "data",
  "document_provide",
  "document_upload",
  "review",
  "asset_issue",
  "asset_return",
  "vehicle_assign",
  "vehicle_return",
  "external_confirm"
]);
var onboardingAssigneeKindSchema = z.enum([
  "user",
  "role",
  "department",
  "process_owner",
  "subject",
  "subject_manager",
  "team"
]);
var onboardingDueAnchorSchema = z.enum(["entry", "exit", "last_working_day", "started"]);
var onboardingDataFieldTypeSchema = z.enum([
  "short_text",
  "long_text",
  "date",
  "boolean",
  "choice"
]);
var SECRET_KEY_PATTERN = /password|secret|token/i;
var FLOW_POSITION_MESSAGE = "Die Stelle liegt au\xDFerhalb des zul\xE4ssigen Bereichs.";
var flowCoordinateSchema = z.number().int().min(-1e4, FLOW_POSITION_MESSAGE).max(1e4, FLOW_POSITION_MESSAGE).optional();
function isForbiddenOnboardingSecretKey(key) {
  return SECRET_KEY_PATTERN.test(key);
}
var taskKeySchema = z.string().trim().regex(/^[a-z][a-z0-9_]{1,40}$/, "key: Kleinbuchstaben, Ziffern und Unterstrich (2\u201341 Zeichen).").refine((key) => !isForbiddenOnboardingSecretKey(key), {
  message: "Schl\xFCssel darf kein Passwort, Geheimnis oder Token bezeichnen."
});
var dataFieldKeySchema = z.string().trim().regex(/^[a-z][a-z0-9_]{1,40}$/, "Feldschl\xFCssel ung\xFCltig.").refine((key) => !isForbiddenOnboardingSecretKey(key), {
  message: "Feldschl\xFCssel darf kein Passwort, Geheimnis oder Token bezeichnen."
});
var choiceOptionSchema2 = z.object({
  value: z.string().trim().min(1).max(80),
  label: z.string().trim().min(1).max(200)
});
var dataFieldBaseSchema = z.object({
  key: dataFieldKeySchema,
  label: z.string().trim().min(1).max(200),
  required: z.boolean().default(false)
});
var onboardingDataFieldSchema = z.discriminatedUnion("type", [
  dataFieldBaseSchema.extend({ type: z.literal("short_text") }),
  dataFieldBaseSchema.extend({ type: z.literal("long_text") }),
  dataFieldBaseSchema.extend({ type: z.literal("date") }),
  dataFieldBaseSchema.extend({ type: z.literal("boolean") }),
  dataFieldBaseSchema.extend({
    type: z.literal("choice"),
    options: z.array(choiceOptionSchema2).min(1).max(50)
  })
]);
var onboardingTemplateTaskBaseSchema = z.object({
  key: taskKeySchema,
  title: z.string().trim().min(1).max(200),
  instructions: z.union([z.string().trim().max(4e3), z.null()]).optional().transform((value) => value === void 0 ? null : value),
  required: z.boolean().default(true),
  kind: onboardingTaskKindSchema,
  assignee: onboardingAssigneeKindSchema,
  assigneeUserId: z.string().uuid().nullable().optional(),
  assigneeRoleId: z.string().uuid().nullable().optional(),
  assigneeDepartmentId: z.string().uuid().nullable().optional(),
  assigneeTeamId: z.string().uuid().nullable().optional(),
  offsetDays: z.number().int().min(-365).max(365).default(0),
  anchor: onboardingDueAnchorSchema,
  dependsOn: z.array(taskKeySchema).default([]),
  visibleToSubject: z.boolean().default(false),
  dataFields: z.array(onboardingDataFieldSchema).default([]),
  x: flowCoordinateSchema,
  y: flowCoordinateSchema
});
var onboardingTemplateTaskSchema = onboardingTemplateTaskBaseSchema.superRefine(
  (task, ctx) => {
    const allowsDataFields = task.kind === "data" || task.kind === "review";
    if (!allowsDataFields && task.dataFields.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dataFields"],
        message: "dataFields sind nur bei data und review erlaubt."
      });
    }
    if (task.assignee === "user" && !task.assigneeUserId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assigneeUserId"],
        message: "Benutzer-Zust\xE4ndigkeit ben\xF6tigt assigneeUserId."
      });
    }
    if (task.assignee === "role" && !task.assigneeRoleId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assigneeRoleId"],
        message: "Rollen-Zust\xE4ndigkeit ben\xF6tigt assigneeRoleId."
      });
    }
    if (task.assignee === "department" && !task.assigneeDepartmentId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assigneeDepartmentId"],
        message: "Abteilungs-Zust\xE4ndigkeit ben\xF6tigt assigneeDepartmentId."
      });
    }
    if (task.assignee === "team" && !task.assigneeTeamId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assigneeTeamId"],
        message: "Team-Zust\xE4ndigkeit ben\xF6tigt assigneeTeamId."
      });
    }
    const fieldKeys = /* @__PURE__ */ new Set();
    for (const field of task.dataFields) {
      if (fieldKeys.has(field.key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["dataFields"],
          message: `Feldschl\xFCssel \u201E${field.key}\u201C ist doppelt.`
        });
      }
      fieldKeys.add(field.key);
    }
  }
);
var onboardingPhaseStageSchema = z.enum(["preboarding", "onboarding"]);
var onboardingTemplatePhaseSchema = z.object({
  id: z.string().trim().min(1).max(80),
  title: z.string().trim().min(1).max(200),
  sortOrder: z.number().int().min(0),
  /** Missing on older definitions. Read as the onboarding stage. */
  stage: onboardingPhaseStageSchema.default("onboarding"),
  tasks: z.array(onboardingTemplateTaskSchema).default([]),
  x: flowCoordinateSchema,
  y: flowCoordinateSchema
});
var onboardingTemplateDefinitionSchema = z.object({
  schemaVersion: z.literal(ONBOARDING_TEMPLATE_SCHEMA_VERSION),
  dueBasis: z.literal("calendar_days"),
  phases: z.array(onboardingTemplatePhaseSchema).default([])
}).superRefine((definition, ctx) => {
  const phaseIds = /* @__PURE__ */ new Set();
  const taskKeys = /* @__PURE__ */ new Set();
  for (const phase of definition.phases) {
    if (phaseIds.has(phase.id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["phases"],
        message: `Phasen-ID \u201E${phase.id}\u201C ist doppelt.`
      });
    }
    phaseIds.add(phase.id);
    for (const task of phase.tasks) {
      if (taskKeys.has(task.key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["phases"],
          message: `Aufgaben-Schl\xFCssel \u201E${task.key}\u201C ist in der Version nicht eindeutig.`
        });
      }
      taskKeys.add(task.key);
    }
  }
});
var emptyOnboardingTemplateDefinition = () => onboardingTemplateDefinitionSchema.parse({
  schemaVersion: ONBOARDING_TEMPLATE_SCHEMA_VERSION,
  dueBasis: "calendar_days",
  phases: []
});
function flattenOnboardingTemplateTasks(definition) {
  const phases = [...definition.phases].sort((a, b) => a.sortOrder - b.sortOrder);
  return phases.flatMap(
    (phase) => phase.tasks.map((task) => ({ key: task.key, dependsOn: task.dependsOn }))
  );
}
function checkOnboardingTemplateDependencies(tasks) {
  const keys = new Set(tasks.map((task) => task.key));
  if (keys.size !== tasks.length) {
    return { ok: false, message: "Aufgaben-Schl\xFCssel sind nicht eindeutig.", keys: [] };
  }
  const seenEdges = /* @__PURE__ */ new Set();
  for (const task of tasks) {
    for (const dep of task.dependsOn) {
      const edge = `${task.key}\0${dep}`;
      if (seenEdges.has(edge)) {
        return {
          ok: false,
          message: `Aufgabe \u201E${task.key}\u201C nennt die Abh\xE4ngigkeit \u201E${dep}\u201C doppelt.`,
          keys: [task.key]
        };
      }
      seenEdges.add(edge);
      if (dep === task.key) {
        return {
          ok: false,
          message: `Aufgabe \u201E${task.key}\u201C h\xE4ngt von sich selbst ab.`,
          keys: [task.key]
        };
      }
      if (!keys.has(dep)) {
        return {
          ok: false,
          message: `Unbekannte Abh\xE4ngigkeit \u201E${dep}\u201C bei Aufgabe \u201E${task.key}\u201C.`,
          keys: [task.key, dep]
        };
      }
    }
  }
  const indegree = /* @__PURE__ */ new Map();
  const adjacency = /* @__PURE__ */ new Map();
  for (const key of keys) {
    indegree.set(key, 0);
    adjacency.set(key, []);
  }
  for (const task of tasks) {
    for (const dep of task.dependsOn) {
      adjacency.get(dep).push(task.key);
      indegree.set(task.key, (indegree.get(task.key) ?? 0) + 1);
    }
  }
  const queue = [];
  for (const [key, degree] of indegree) {
    if (degree === 0) {
      queue.push(key);
    }
  }
  let visited = 0;
  while (queue.length > 0) {
    const current = queue.shift();
    visited += 1;
    for (const next of adjacency.get(current) ?? []) {
      const nextDegree = (indegree.get(next) ?? 0) - 1;
      indegree.set(next, nextDegree);
      if (nextDegree === 0) {
        queue.push(next);
      }
    }
  }
  if (visited !== keys.size) {
    const cycleKeys = [...indegree.entries()].filter(([, degree]) => degree > 0).map(([key]) => key).sort();
    return {
      ok: false,
      message: "Die Aufgabenabh\xE4ngigkeiten enthalten einen Zyklus.",
      keys: cycleKeys
    };
  }
  return { ok: true };
}
function validateOnboardingTemplateDefinitionForPublish(raw, options) {
  const parsed = onboardingTemplateDefinitionSchema.safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      issues: parsed.error.issues.map((issue) => ({
        path: issue.path.join("."),
        message: issue.message
      }))
    };
  }
  const definition = parsed.data;
  const stageIssues = phaseStageIssues(definition, options?.kind);
  if (stageIssues.length > 0) {
    return { ok: false, issues: stageIssues };
  }
  const anchorIssues = dueAnchorIssues(definition, options?.kind);
  if (anchorIssues.length > 0) {
    return { ok: false, issues: anchorIssues };
  }
  const tasks = flattenOnboardingTemplateTasks(definition);
  if (tasks.length === 0) {
    return {
      ok: false,
      issues: [{ path: "phases", message: "Mindestens eine Aufgabe ist erforderlich." }]
    };
  }
  const graph = checkOnboardingTemplateDependencies(tasks);
  if (!graph.ok) {
    return {
      ok: false,
      cycle: true,
      issues: [{ path: "dependsOn", message: graph.message }]
    };
  }
  return { ok: true, definition };
}
function phaseStageIssues(definition, kind) {
  const issues = [];
  for (const phase of definition.phases) {
    if (phase.stage === "preboarding" && kind === "offboarding") {
      issues.push({
        path: "phases",
        message: `Phase \u201E${phase.title}\u201C: Eine Austrittsvorlage hat keine Vorbereitung.`
      });
    }
    if (phase.stage !== "preboarding") {
      continue;
    }
    for (const task of phase.tasks) {
      if (task.assignee === "subject") {
        issues.push({
          path: "phases",
          message: `Aufgabe \u201E${task.key}\u201C: In der Vorbereitung gibt es noch kein Konto f\xFCr die betroffene Person.`
        });
      }
      if (task.kind === "asset_issue" || task.kind === "asset_return" || task.kind === "vehicle_assign" || task.kind === "vehicle_return") {
        issues.push({
          path: "phases",
          message: `Aufgabe \u201E${task.key}\u201C: Eine Systemaktion ist in der Vorbereitung noch nicht m\xF6glich.`
        });
      }
    }
  }
  return issues;
}
function dueAnchorIssues(definition, kind) {
  if (!kind) {
    return [];
  }
  const issues = [];
  for (const phase of definition.phases) {
    for (const task of phase.tasks) {
      if (kind === "onboarding" && task.anchor === "exit") {
        issues.push({
          path: "anchor",
          message: `Aufgabe \u201E${task.key}\u201C: Ein Eintritt hat kein Austrittsdatum.`
        });
      }
      if (kind === "offboarding" && task.anchor === "entry") {
        issues.push({
          path: "anchor",
          message: `Aufgabe \u201E${task.key}\u201C: Ein Austritt hat kein Eintrittsdatum.`
        });
      }
    }
  }
  return issues;
}
var emptyToNull4 = (value) => value === "" ? null : value;
var emptyToUndefined10 = (value) => value === "" || value === "all" ? void 0 : value;
var optionalNullableText3 = (max) => z.preprocess(emptyToNull4, z.union([z.string().trim().max(max), z.null()]).optional());
var onboardingTemplateVersionSchema = z.object({
  id: z.string().uuid(),
  templateId: z.string().uuid(),
  versionNumber: z.number().int().positive(),
  status: onboardingTemplateVersionStatusSchema,
  definition: onboardingTemplateDefinitionSchema,
  publishedById: z.string().uuid().nullable(),
  publishedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var onboardingTemplateSchema = z.object({
  id: z.string().uuid(),
  kind: onboardingCaseKindSchema,
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  currentPublishedVersionId: z.string().uuid().nullable(),
  draftVersionId: z.string().uuid().nullable(),
  createdById: z.string().uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var onboardingTemplateDetailSchema = onboardingTemplateSchema.extend({
  versions: z.array(onboardingTemplateVersionSchema)
});
var onboardingDraftPromptSchema = z.string().trim().min(1, "Die Beschreibung fehlt.").max(8e3, "Die Beschreibung darf h\xF6chstens 8000 Zeichen haben.");
var createOnboardingTemplateFromDescriptionRequestSchema = z.object({
  kind: onboardingCaseKindSchema,
  name: z.string().trim().min(1).max(200),
  prompt: onboardingDraftPromptSchema
});
var replaceOnboardingTemplateDraftFromDescriptionRequestSchema = z.object({
  prompt: onboardingDraftPromptSchema
});
var createOnboardingTemplateRequestSchema = z.object({
  kind: onboardingCaseKindSchema,
  name: z.string().trim().min(1).max(200),
  description: optionalNullableText3(4e3),
  definition: onboardingTemplateDefinitionSchema.optional()
});
var updateOnboardingTemplateRequestSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: optionalNullableText3(4e3),
  isActive: z.boolean().optional(),
  definition: onboardingTemplateDefinitionSchema.optional()
}).refine((value) => Object.keys(value).length > 0, {
  message: "Mindestens ein Feld ist erforderlich."
});
var onboardingTemplateListQuerySchema = paginationQuerySchema.extend({
  q: z.preprocess(emptyToUndefined10, z.string().trim().max(200).optional()),
  kind: z.preprocess(emptyToUndefined10, onboardingCaseKindSchema.optional()),
  isActive: z.preprocess(
    (value) => value === "true" ? true : value === "false" ? false : void 0,
    z.boolean().optional()
  ),
  sortBy: z.enum(["name", "updatedAt"]).default("name"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var onboardingTemplateListResponseSchema = paginatedSchema(onboardingTemplateSchema);
var ONBOARDING_TEMPLATE_EXCHANGE_FORMAT = "gb-ticket.onboarding-template";
var ONBOARDING_TEMPLATE_EXCHANGE_VERSION = 1;
var ONBOARDING_TEMPLATE_EXCHANGE_MAX_BYTES = 102400;
var ONBOARDING_TEMPLATE_EXCHANGE_MAX_PHASES = 40;
var ONBOARDING_TEMPLATE_EXCHANGE_MAX_TASKS = 120;
var exchangeKeySchema = z.string().trim().regex(/^[a-z][a-z0-9_]{1,40}$/, "key: Kleinbuchstaben, Ziffern und Unterstrich (2\u201341 Zeichen).").refine((key) => !isForbiddenOnboardingSecretKey(key), {
  message: "Schl\xFCssel darf kein Passwort, Geheimnis oder Token bezeichnen."
});
var exchangeFieldKeySchema = z.string().trim().regex(/^[a-z][a-z0-9_]{1,40}$/, "Feldschl\xFCssel ung\xFCltig.").refine((key) => !isForbiddenOnboardingSecretKey(key), {
  message: "Feldschl\xFCssel darf kein Passwort, Geheimnis oder Token bezeichnen."
});
var exchangeFieldBase = {
  key: exchangeFieldKeySchema,
  label: z.string().trim().min(1).max(200),
  required: z.boolean().default(false)
};
var exchangeChoiceOptionSchema = z.strictObject({
  value: z.string().trim().min(1).max(80),
  label: z.string().trim().min(1).max(200)
});
var onboardingTemplateExchangeDataFieldSchema = z.discriminatedUnion("type", [
  z.strictObject({ ...exchangeFieldBase, type: z.literal("short_text") }),
  z.strictObject({ ...exchangeFieldBase, type: z.literal("long_text") }),
  z.strictObject({ ...exchangeFieldBase, type: z.literal("date") }),
  z.strictObject({ ...exchangeFieldBase, type: z.literal("boolean") }),
  z.strictObject({
    ...exchangeFieldBase,
    type: z.literal("choice"),
    options: z.array(exchangeChoiceOptionSchema).min(1).max(50)
  })
]);
var exchangeUserRefSchema = z.strictObject({
  type: z.literal("user"),
  email: z.string().trim().email("Bitte eine g\xFCltige E-Mail angeben.").max(200)
});
var exchangeRoleRefSchema = z.strictObject({
  type: z.literal("role"),
  code: roleAssignmentCodeSchema
});
var exchangeDepartmentRefSchema = z.strictObject({
  type: z.literal("department"),
  slug: orgSlugSchema
});
var exchangeTeamRefSchema = z.strictObject({
  type: z.literal("team"),
  slug: orgSlugSchema
});
var onboardingTemplateExchangeAssigneeRefSchema = z.discriminatedUnion("type", [
  exchangeUserRefSchema,
  exchangeRoleRefSchema,
  exchangeDepartmentRefSchema,
  exchangeTeamRefSchema
]);
var exchangeTaskSchema = z.strictObject({
  key: exchangeKeySchema,
  title: z.string().trim().min(1).max(200),
  instructions: z.union([z.string().trim().max(4e3), z.null()]).optional().transform((value) => value === void 0 || value === "" ? null : value),
  required: z.boolean().default(true),
  kind: onboardingTaskKindSchema,
  assignee: onboardingAssigneeKindSchema,
  assigneeRef: z.union([onboardingTemplateExchangeAssigneeRefSchema, z.null()]),
  offsetDays: z.number().int().min(-365).max(365).default(0),
  anchor: onboardingDueAnchorSchema,
  dependsOn: z.array(exchangeKeySchema).default([]),
  visibleToSubject: z.boolean().default(false),
  dataFields: z.array(onboardingTemplateExchangeDataFieldSchema).default([]),
  x: z.number().int().min(-1e4, "Die Stelle liegt au\xDFerhalb des zul\xE4ssigen Bereichs.").max(1e4, "Die Stelle liegt au\xDFerhalb des zul\xE4ssigen Bereichs.").optional(),
  y: z.number().int().min(-1e4, "Die Stelle liegt au\xDFerhalb des zul\xE4ssigen Bereichs.").max(1e4, "Die Stelle liegt au\xDFerhalb des zul\xE4ssigen Bereichs.").optional()
}).superRefine((task, ctx) => {
  const allowsDataFields = task.kind === "data" || task.kind === "review";
  if (!allowsDataFields && task.dataFields.length > 0) {
    ctx.addIssue({
      code: "custom",
      path: ["dataFields"],
      message: "dataFields sind nur bei data und review erlaubt."
    });
  }
  const expected = task.assignee === "user" || task.assignee === "role" || task.assignee === "department" || task.assignee === "team" ? task.assignee : null;
  if (expected === null) {
    if (task.assigneeRef !== null) {
      ctx.addIssue({
        code: "custom",
        path: ["assigneeRef"],
        message: "Diese Zust\xE4ndigkeit hat keinen Empf\xE4ngerverweis."
      });
    }
  } else if (task.assigneeRef?.type !== expected) {
    ctx.addIssue({
      code: "custom",
      path: ["assigneeRef"],
      message: assigneeRefMessage(expected)
    });
  }
  const fieldKeys = /* @__PURE__ */ new Set();
  for (const field of task.dataFields) {
    if (fieldKeys.has(field.key)) {
      ctx.addIssue({
        code: "custom",
        path: ["dataFields"],
        message: `Feldschl\xFCssel \u201E${field.key}\u201C ist doppelt.`
      });
    }
    fieldKeys.add(field.key);
  }
});
function assigneeRefMessage(assignee) {
  switch (assignee) {
    case "user":
      return "Benutzer-Zust\xE4ndigkeit ben\xF6tigt einen E-Mail-Verweis.";
    case "role":
      return "Gruppen-Zust\xE4ndigkeit ben\xF6tigt einen Gruppen-Code.";
    case "department":
      return "Abteilungs-Zust\xE4ndigkeit ben\xF6tigt einen Abteilungs-Slug.";
    case "team":
      return "Team-Zust\xE4ndigkeit ben\xF6tigt einen Team-Slug.";
  }
}
var exchangePhaseSchema = z.strictObject({
  id: z.string().trim().min(1).max(80),
  title: z.string().trim().min(1).max(200),
  sortOrder: z.number().int().min(0),
  stage: z.enum(["preboarding", "onboarding"]).default("onboarding"),
  tasks: z.array(exchangeTaskSchema).default([]),
  x: z.number().int().min(-1e4, "Die Stelle liegt au\xDFerhalb des zul\xE4ssigen Bereichs.").max(1e4, "Die Stelle liegt au\xDFerhalb des zul\xE4ssigen Bereichs.").optional(),
  y: z.number().int().min(-1e4, "Die Stelle liegt au\xDFerhalb des zul\xE4ssigen Bereichs.").max(1e4, "Die Stelle liegt au\xDFerhalb des zul\xE4ssigen Bereichs.").optional()
});
var exchangeDefinitionSchema = z.strictObject({
  schemaVersion: z.literal(ONBOARDING_TEMPLATE_SCHEMA_VERSION),
  dueBasis: z.literal("calendar_days"),
  phases: z.array(exchangePhaseSchema).max(ONBOARDING_TEMPLATE_EXCHANGE_MAX_PHASES, "H\xF6chstens 40 Phasen.").default([])
}).superRefine((definition, ctx) => {
  const phaseIds = /* @__PURE__ */ new Set();
  const taskKeys = /* @__PURE__ */ new Set();
  let taskCount = 0;
  for (const phase of definition.phases) {
    if (phaseIds.has(phase.id)) {
      ctx.addIssue({
        code: "custom",
        path: ["phases"],
        message: `Phasen-ID \u201E${phase.id}\u201C ist doppelt.`
      });
    }
    phaseIds.add(phase.id);
    for (const task of phase.tasks) {
      taskCount += 1;
      if (taskKeys.has(task.key)) {
        ctx.addIssue({
          code: "custom",
          path: ["phases"],
          message: `Aufgaben-Schl\xFCssel \u201E${task.key}\u201C ist in der Version nicht eindeutig.`
        });
      }
      taskKeys.add(task.key);
    }
  }
  if (taskCount > ONBOARDING_TEMPLATE_EXCHANGE_MAX_TASKS) {
    ctx.addIssue({
      code: "custom",
      path: ["phases"],
      message: "H\xF6chstens 120 Aufgaben."
    });
  }
});
var onboardingTemplateExchangeSchema = z.strictObject({
  format: z.literal(ONBOARDING_TEMPLATE_EXCHANGE_FORMAT),
  formatVersion: z.literal(ONBOARDING_TEMPLATE_EXCHANGE_VERSION),
  kind: onboardingCaseKindSchema,
  name: z.string().trim().min(1).max(200),
  description: z.union([z.string().trim().max(4e3), z.null()]).optional().transform((value) => value === void 0 || value === "" ? null : value),
  definition: exchangeDefinitionSchema
});
var emptyToUndefined11 = (value) => value === "" ? void 0 : value;
var onboardingTemplateImportAssignmentSchema = z.strictObject({
  taskKey: z.string().trim().min(1).max(41),
  assigneeUserId: z.string().uuid().nullable().optional(),
  assigneeRoleId: z.string().uuid().nullable().optional(),
  assigneeDepartmentId: z.string().uuid().nullable().optional(),
  assigneeTeamId: z.string().uuid().nullable().optional()
});
var previewOnboardingTemplateImportRequestSchema = z.strictObject({
  document: z.unknown()
});
var commitOnboardingTemplateImportRequestSchema = z.strictObject({
  document: z.unknown(),
  name: z.string().trim().min(1).max(200),
  createIdempotencyKey: z.preprocess(emptyToUndefined11, z.string().trim().min(1).max(80).optional()),
  assignments: z.array(onboardingTemplateImportAssignmentSchema).default([])
});
var onboardingTemplateImportReferenceStatusSchema = z.enum([
  "none",
  "suggested",
  "missing",
  "inactive"
]);
var onboardingTemplateImportPreviewReferenceSchema = z.object({
  status: onboardingTemplateImportReferenceStatusSchema,
  suggestedId: z.string().uuid().nullable(),
  label: z.string().nullable()
});
var onboardingTemplateImportPreviewTaskSchema = z.object({
  key: z.string(),
  title: z.string(),
  instructions: z.string().nullable(),
  required: z.boolean(),
  kind: onboardingTaskKindSchema,
  assignee: onboardingAssigneeKindSchema,
  assigneeRef: z.union([onboardingTemplateExchangeAssigneeRefSchema, z.null()]),
  offsetDays: z.number().int(),
  anchor: onboardingDueAnchorSchema,
  dependsOn: z.array(z.string()),
  visibleToSubject: z.boolean(),
  dataFields: z.array(onboardingTemplateExchangeDataFieldSchema),
  reference: onboardingTemplateImportPreviewReferenceSchema
});
var onboardingTemplateImportPreviewPhaseSchema = z.object({
  id: z.string(),
  title: z.string(),
  sortOrder: z.number().int(),
  tasks: z.array(onboardingTemplateImportPreviewTaskSchema)
});
var onboardingTemplateImportPreviewSchema = z.object({
  kind: onboardingCaseKindSchema,
  name: z.string(),
  description: z.string().nullable(),
  suggestedName: z.string(),
  nameConflict: z.boolean(),
  phases: z.array(onboardingTemplateImportPreviewPhaseSchema)
});
var NOT_A_TEMPLATE = "Die Datei ist keine Onboarding-Vorlage.";
var SCHEMA_VERSION_FIELD = "definition.schemaVersion";
var SCHEMA_VERSION_MESSAGE = "Das Feld definition.schemaVersion wird nicht unterst\xFCtzt. Unterst\xFCtzt wird nur Version 1.";
function parseOnboardingTemplateExchange(raw, options) {
  const byteLength = options?.byteLength ?? (typeof raw === "string" ? utf8ByteLength(raw) : void 0);
  if (byteLength !== void 0 && byteLength > ONBOARDING_TEMPLATE_EXCHANGE_MAX_BYTES) {
    return failure("ONBOARDING_TEMPLATE_EXCHANGE_TOO_LARGE", "Die Datei ist zu gro\xDF.");
  }
  let value = raw;
  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw);
    } catch {
      return failure("ONBOARDING_TEMPLATE_EXCHANGE_INVALID", NOT_A_TEMPLATE);
    }
  }
  if (!isRecord(value)) {
    return failure("ONBOARDING_TEMPLATE_EXCHANGE_INVALID", NOT_A_TEMPLATE);
  }
  if (value.format !== ONBOARDING_TEMPLATE_EXCHANGE_FORMAT) {
    return failure("ONBOARDING_TEMPLATE_EXCHANGE_INVALID", NOT_A_TEMPLATE);
  }
  if (typeof value.formatVersion === "number" && Number.isFinite(value.formatVersion) && value.formatVersion > ONBOARDING_TEMPLATE_EXCHANGE_VERSION) {
    const message = `Diese Datei verwendet Formatversion ${value.formatVersion}. Unterst\xFCtzt wird nur Version 1.`;
    return failure("ONBOARDING_TEMPLATE_EXCHANGE_VERSION", message);
  }
  if (value.formatVersion !== ONBOARDING_TEMPLATE_EXCHANGE_VERSION) {
    return failure("ONBOARDING_TEMPLATE_EXCHANGE_INVALID", NOT_A_TEMPLATE);
  }
  if (!isRecord(value.definition) || value.definition.schemaVersion !== ONBOARDING_TEMPLATE_SCHEMA_VERSION) {
    return failure("ONBOARDING_TEMPLATE_EXCHANGE_INVALID", SCHEMA_VERSION_MESSAGE, [
      { path: SCHEMA_VERSION_FIELD, message: SCHEMA_VERSION_MESSAGE }
    ]);
  }
  const parsed = onboardingTemplateExchangeSchema.safeParse(value);
  if (!parsed.success) {
    const issues = issuesFromZod(parsed.error);
    return failure(
      "ONBOARDING_TEMPLATE_EXCHANGE_INVALID",
      issues[0]?.message ?? NOT_A_TEMPLATE,
      issues
    );
  }
  const graph = checkOnboardingTemplateDependencies(
    [...parsed.data.definition.phases].sort((left, right) => left.sortOrder - right.sortOrder).flatMap(
      (phase) => phase.tasks.map((task) => ({ key: task.key, dependsOn: task.dependsOn }))
    )
  );
  if (!graph.ok) {
    return failure("ONBOARDING_TEMPLATE_CYCLE", graph.message, [
      { path: "dependsOn", message: graph.message }
    ]);
  }
  return { ok: true, document: parsed.data };
}
function failure(code, message, issues = [{ path: "", message }]) {
  return { ok: false, code, message, issues };
}
function issuesFromZod(error) {
  return error.issues.flatMap((issue) => {
    const base = issue.path.map(String).join(".");
    if (issue.code === "unrecognized_keys") {
      return issue.keys.map((key) => {
        const path = base.length > 0 ? `${base}.${key}` : key;
        return {
          path,
          message: `Die Datei enth\xE4lt das unbekannte Feld \u201E${path}\u201C und wird nicht importiert.`
        };
      });
    }
    return [{ path: base, message: issue.message }];
  });
}
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function utf8ByteLength(value) {
  if (value.length > ONBOARDING_TEMPLATE_EXCHANGE_MAX_BYTES) {
    return value.length;
  }
  return new TextEncoder().encode(value).length;
}
var emptyToUndefined12 = (value) => value === "" || value === "all" ? void 0 : value;
var emptyToNull5 = (value) => value === "" ? null : value;
var onboardingDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var onboardingCancelCodeSchema = z.enum(["no_show"]);
var onboardingTaskStatusSchema = z.enum([
  "open",
  "completed",
  "skipped",
  "not_applicable",
  "cancelled"
]);
var revisionSchema = z.number().int().nonnegative();
var optionalEmail = z.preprocess(emptyToUndefined12, z.string().trim().email().optional());
var createOnboardingCaseRequestSchema = z.object({
  kind: onboardingCaseKindSchema,
  templateId: z.string().uuid(),
  subjectUserId: z.preprocess(emptyToUndefined12, z.string().uuid().optional()),
  subjectFirstName: z.preprocess(emptyToUndefined12, z.string().trim().min(1).max(80).optional()),
  subjectLastName: z.preprocess(emptyToUndefined12, z.string().trim().min(1).max(80).optional()),
  subjectEmail: optionalEmail,
  ownerUserId: z.string().uuid(),
  entryOn: z.preprocess(emptyToNull5, onboardingDateSchema.nullable().optional()),
  exitOn: z.preprocess(emptyToNull5, onboardingDateSchema.nullable().optional()),
  lastWorkingDay: z.preprocess(emptyToNull5, onboardingDateSchema.nullable().optional()),
  createIdempotencyKey: z.preprocess(
    emptyToUndefined12,
    z.string().trim().min(1).max(80).optional()
  )
}).superRefine((body, ctx) => {
  if (body.kind === OnboardingCaseKind.ONBOARDING && body.exitOn) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["exitOn"],
      message: "Eintrittsvorg\xE4nge haben kein Austrittsdatum."
    });
  }
  if (body.kind === OnboardingCaseKind.OFFBOARDING && body.entryOn) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["entryOn"],
      message: "Austrittsvorg\xE4nge haben kein Eintrittsdatum."
    });
  }
  if (body.kind === OnboardingCaseKind.OFFBOARDING && !body.subjectUserId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["subjectUserId"],
      message: "Ein Austritt braucht eine vorhandene Person."
    });
  }
  if (body.kind === OnboardingCaseKind.ONBOARDING && !body.subjectUserId && (!body.subjectFirstName || !body.subjectLastName || !body.subjectEmail)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["subjectEmail"],
      message: "Die k\xFCnftige Person braucht Vorname, Nachname und E-Mail."
    });
  }
});
var onboardingCaseRevisionBodySchema = z.object({
  revision: revisionSchema
});
var reopenOnboardingTaskRequestSchema = onboardingCaseRevisionBodySchema;
var ONBOARDING_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
var ONBOARDING_DOCUMENT_ALLOWED_MIME = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "text/plain",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];
var onboardingDocumentScanStatusSchema = z.enum([
  "PENDING",
  "CLEAN",
  "BLOCKED",
  "SKIPPED"
]);
var onboardingDocumentSchema = z.object({
  id: z.string().uuid(),
  caseId: z.string().uuid(),
  taskId: z.string().uuid().nullable(),
  fileName: z.string(),
  mimeType: z.string(),
  byteSize: z.number().int().nonnegative(),
  scanStatus: onboardingDocumentScanStatusSchema,
  visibleToSubject: z.boolean(),
  createdAt: z.iso.datetime()
});
var onboardingDocumentListSchema = z.object({
  items: z.array(onboardingDocumentSchema)
});
var onboardingAccountKindSchema = z.enum(["entra", "local"]);
var admitOnboardingCaseRequestSchema = onboardingCaseRevisionBodySchema.extend({
  /** New accounts only. An existing e-mail keeps its current sign-in. */
  accountKind: onboardingAccountKindSchema.default("entra")
});
var startOnboardingCaseRequestSchema = onboardingCaseRevisionBodySchema.extend({
  entryOn: z.preprocess(emptyToNull5, onboardingDateSchema.nullable().optional()),
  exitOn: z.preprocess(emptyToNull5, onboardingDateSchema.nullable().optional()),
  lastWorkingDay: z.preprocess(emptyToNull5, onboardingDateSchema.nullable().optional())
});
var completeOnboardingCaseRequestSchema = onboardingCaseRevisionBodySchema;
var cancelOnboardingCaseRequestSchema = onboardingCaseRevisionBodySchema.extend({
  reason: z.string().trim().min(1).max(2e3),
  cancelCode: z.preprocess(emptyToNull5, onboardingCancelCodeSchema.nullable().optional())
});
var reopenOnboardingCaseRequestSchema = onboardingCaseRevisionBodySchema.extend({
  reason: z.string().trim().min(10).max(500)
});
var patchOnboardingCaseDatesRequestSchema = onboardingCaseRevisionBodySchema.extend({
  entryOn: z.preprocess(emptyToNull5, onboardingDateSchema.nullable().optional()),
  exitOn: z.preprocess(emptyToNull5, onboardingDateSchema.nullable().optional()),
  lastWorkingDay: z.preprocess(emptyToNull5, onboardingDateSchema.nullable().optional())
}).refine(
  (body) => body.entryOn !== void 0 || body.exitOn !== void 0 || body.lastWorkingDay !== void 0,
  { message: "Mindestens ein Datum ist erforderlich." }
);
var onboardingCaseListQuerySchema = paginationQuerySchema.extend({
  kind: z.preprocess(emptyToUndefined12, onboardingCaseKindSchema.optional()),
  status: z.preprocess(emptyToUndefined12, onboardingCaseStatusSchema.optional()),
  subjectUserId: z.preprocess(emptyToUndefined12, z.string().uuid().optional()),
  departmentId: z.preprocess(emptyToUndefined12, z.string().uuid().optional()),
  siteId: z.preprocess(emptyToUndefined12, z.string().uuid().optional()),
  q: z.preprocess(emptyToUndefined12, z.string().trim().max(200).optional()),
  sortBy: z.enum(["number", "startedAt", "updatedAt"]).default("updatedAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var onboardingCaseTaskSchema = z.object({
  id: z.string().uuid(),
  caseId: z.string().uuid(),
  key: z.string(),
  title: z.string(),
  instructions: z.string().nullable(),
  required: z.boolean(),
  kind: onboardingTaskKindSchema,
  phaseId: z.string(),
  phaseTitle: z.string(),
  sortOrder: z.number().int(),
  visibleToSubject: z.boolean(),
  status: onboardingTaskStatusSchema,
  dueOn: onboardingDateSchema.nullable(),
  assigneeKind: onboardingAssigneeKindSchema,
  assigneeUserId: z.string().uuid().nullable(),
  assigneeRoleId: z.string().uuid().nullable(),
  assigneeDepartmentId: z.string().uuid().nullable(),
  assigneeTeamId: z.string().uuid().nullable(),
  claimedById: z.string().uuid().nullable(),
  claimedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  completedById: z.string().uuid().nullable(),
  skipReason: z.string().nullable(),
  waivedById: z.string().uuid().nullable(),
  result: z.record(z.string(), z.unknown()).nullable(),
  systemActionStatus: z.enum(["none", "pending", "succeeded", "failed"]),
  /** Open dependency edge. Same predicate as the task list, including a hidden predecessor. */
  blocked: z.boolean().optional(),
  /** Whether this actor may finish the task. Same predicate as the task list. */
  canComplete: z.boolean().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var onboardingCaseEventSchema = z.object({
  id: z.string().uuid(),
  caseId: z.string().uuid(),
  type: z.string(),
  actorId: z.string().uuid().nullable(),
  at: z.iso.datetime(),
  payload: z.record(z.string(), z.unknown())
});
var onboardingCaseSchema = z.object({
  id: z.string().uuid(),
  number: z.string(),
  kind: onboardingCaseKindSchema,
  status: onboardingCaseStatusSchema,
  subjectUserId: z.string().uuid().nullable(),
  subjectFirstName: z.string().nullable(),
  subjectLastName: z.string().nullable(),
  subjectEmail: z.string().nullable(),
  ownerUserId: z.string().uuid(),
  departmentId: z.string().uuid().nullable(),
  siteId: z.string().uuid().nullable(),
  entryOn: onboardingDateSchema.nullable(),
  exitOn: onboardingDateSchema.nullable(),
  lastWorkingDay: onboardingDateSchema.nullable(),
  templateVersionId: z.string().uuid().nullable(),
  templateSnapshot: onboardingTemplateDefinitionSchema.nullable(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  cancelReason: z.string().nullable(),
  cancelCode: onboardingCancelCodeSchema.nullable(),
  revision: z.number().int().nonnegative(),
  createIdempotencyKey: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  subjectDisplayName: z.string().optional(),
  ownerDisplayName: z.string().optional()
});
var onboardingCaseDetailSchema = onboardingCaseSchema.extend({
  tasks: z.array(onboardingCaseTaskSchema),
  events: z.array(onboardingCaseEventSchema),
  /** Snapshot taken at start, otherwise the version the case was created from. */
  flow: onboardingTemplateDefinitionSchema.nullable(),
  templateId: z.string().uuid().nullable()
});
var onboardingCaseListResponseSchema = paginatedSchema(onboardingCaseSchema);
function addOnboardingCalendarDays(isoDate5, offsetDays) {
  const date = /* @__PURE__ */ new Date(`${isoDate5}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + offsetDays);
  return date.toISOString().slice(0, 10);
}
var emptyToUndefined13 = (value) => value === "" || value === "all" ? void 0 : value;
var emptyToNull6 = (value) => value === "" ? null : value;
var revisionBodySchema = z.object({
  revision: z.number().int().nonnegative()
});
var reasonSchema = z.string().trim().min(10).max(500);
var onboardingTaskListQuerySchema = paginationQuerySchema.extend({
  status: z.preprocess(emptyToUndefined13, onboardingTaskStatusSchema.optional()),
  sortBy: z.enum(["dueOn", "updatedAt"]).default("dueOn"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var onboardingTaskListItemSchema = z.object({
  id: z.string().uuid(),
  caseId: z.string().uuid(),
  key: z.string(),
  title: z.string(),
  instructions: z.string().nullable(),
  status: onboardingTaskStatusSchema,
  dueOn: onboardingDateSchema.nullable(),
  required: z.boolean(),
  kind: onboardingTaskKindSchema,
  assigneeKind: onboardingAssigneeKindSchema,
  assigneeUserId: z.string().uuid().nullable(),
  assigneeTeamId: z.string().uuid().nullable(),
  claimedById: z.string().uuid().nullable(),
  caseRevision: z.number().int().nonnegative(),
  subjectDisplayName: z.string(),
  departmentName: z.string().nullable(),
  siteName: z.string().nullable(),
  blocked: z.boolean(),
  /** False when the task is only visible, not assigned to this person. */
  canClaim: z.boolean().optional(),
  canComplete: z.boolean().optional(),
  systemActionStatus: z.enum(["none", "pending", "succeeded", "failed"]),
  dataFields: z.array(onboardingDataFieldSchema),
  result: z.record(z.string(), z.unknown()).nullable()
});
var onboardingTaskListResponseSchema = paginatedSchema(onboardingTaskListItemSchema);
var claimOnboardingTaskRequestSchema = revisionBodySchema;
var unclaimOnboardingTaskRequestSchema = revisionBodySchema;
var completeOnboardingTaskRequestSchema = revisionBodySchema.extend({
  result: z.record(z.string(), z.unknown()).optional()
});
var skipOnboardingTaskRequestSchema = revisionBodySchema.extend({
  reason: reasonSchema
});
var cancelOnboardingTaskRequestSchema = revisionBodySchema.extend({
  reason: reasonSchema
});
var cancelOnboardingPhaseTasksRequestSchema = revisionBodySchema.extend({
  reason: reasonSchema
});
var notApplicableOnboardingTaskRequestSchema = revisionBodySchema.extend({
  reason: reasonSchema
});
var runOnboardingSystemActionRequestSchema = revisionBodySchema.extend({
  assetId: z.string().uuid().optional(),
  vehicleId: z.string().uuid().optional(),
  validTo: z.preprocess(emptyToNull6, onboardingDateSchema.nullable().optional())
});
var onboardingSystemActionTargetSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  usageMode: z.enum(["assigned", "pool"]).optional()
});
var onboardingSystemActionTargetsSchema = z.object({
  assets: z.array(onboardingSystemActionTargetSchema),
  vehicles: z.array(onboardingSystemActionTargetSchema)
});
var reassignOnboardingTaskRequestSchema = revisionBodySchema.extend({
  assigneeKind: onboardingAssigneeKindSchema,
  assigneeUserId: z.preprocess(emptyToNull6, z.string().uuid().nullable().optional()),
  assigneeRoleId: z.preprocess(emptyToNull6, z.string().uuid().nullable().optional()),
  assigneeDepartmentId: z.preprocess(emptyToNull6, z.string().uuid().nullable().optional()),
  assigneeTeamId: z.preprocess(emptyToNull6, z.string().uuid().nullable().optional())
}).superRefine((body, ctx) => {
  if (body.assigneeKind === "user" && !body.assigneeUserId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["assigneeUserId"],
      message: "Benutzer-Zust\xE4ndigkeit ben\xF6tigt assigneeUserId."
    });
  }
  if (body.assigneeKind === "role" && !body.assigneeRoleId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["assigneeRoleId"],
      message: "Rollen-Zust\xE4ndigkeit ben\xF6tigt assigneeRoleId."
    });
  }
  if (body.assigneeKind === "department" && !body.assigneeDepartmentId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["assigneeDepartmentId"],
      message: "Abteilungs-Zust\xE4ndigkeit ben\xF6tigt assigneeDepartmentId."
    });
  }
  if (body.assigneeKind === "team" && !body.assigneeTeamId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["assigneeTeamId"],
      message: "Team-Zust\xE4ndigkeit ben\xF6tigt assigneeTeamId."
    });
  }
});
var onboardingTaskMutationResponseSchema = z.object({
  task: onboardingCaseTaskSchema,
  caseRevision: z.number().int().nonnegative()
});
var onboardingAssigneeOptionsQuerySchema = paginationQuerySchema.pick({ page: true, pageSize: true }).extend({
  q: z.preprocess(emptyToUndefined13, z.string().trim().max(200).optional()),
  includeInactive: userLookupsQuerySchema.shape.includeInactive
});
var onboardingAssigneeUserSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string(),
  isActive: z.boolean(),
  profilePhotoUrl: z.string().nullable().optional()
});
var onboardingAssigneeOptionsResponseSchema = userLookupsSchema.extend({
  users: paginatedSchema(onboardingAssigneeUserSchema)
});

// src/leave-balance.ts
var FULL_DAY = 2;
var LeaveCalculationError = class extends Error {
  constructor(code) {
    super(code);
    this.code = code;
    this.name = "LeaveCalculationError";
  }
  code;
};
var WEEKDAY_KEY = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
function defaultLeaveWorkPattern(stateCode, effectiveFrom = "2000-01-01") {
  return {
    effectiveFrom,
    stateCode,
    mon: 1,
    tue: 1,
    wed: 1,
    thu: 1,
    fri: 1,
    sat: 0,
    sun: 0
  };
}
function capCarryOverDays(carryOverDays, maxCarryOverDays) {
  if (maxCarryOverDays == null) {
    return carryOverDays;
  }
  return Math.min(carryOverDays, maxCarryOverDays);
}
function daysToHalf(days) {
  return Math.round(days * FULL_DAY);
}
function halfToDays(half) {
  return half / FULL_DAY;
}
function addIsoDays(iso, days) {
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
function eachDate(startsOn, endsOn) {
  const dates = [];
  for (let cursor = startsOn; cursor <= endsOn; cursor = addIsoDays(cursor, 1)) {
    dates.push(cursor);
    if (dates.length > 366) {
      break;
    }
  }
  return dates;
}
function calendarSpanDays(startsOn, endsOn) {
  return eachDate(startsOn, endsOn).length;
}
function weekdayKey(iso) {
  const [year, month, day] = iso.split("-").map(Number);
  const index = new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1)).getUTCDay();
  return WEEKDAY_KEY[index] ?? "mon";
}
function patternOn(patterns, iso) {
  let match = null;
  for (const pattern of patterns) {
    if (pattern.effectiveFrom <= iso && (!match || pattern.effectiveFrom > match.effectiveFrom)) {
      match = pattern;
    }
  }
  return match;
}
function weightHalf(pattern, iso) {
  return daysToHalf(pattern[weekdayKey(iso)]);
}
function requestedHalf(iso, startsOn, endsOn, startPart, endPart) {
  const part = iso === startsOn ? startPart : iso === endsOn ? endPart : "full";
  return part === "full" ? FULL_DAY : 1;
}
function isHoliday(iso, state, holidays) {
  if (holidays.company.includes(iso)) {
    return true;
  }
  return holidays.byState[state]?.includes(iso) ?? false;
}
function consumeVacation(request, patterns, holidays) {
  if (request.endsOn < request.startsOn) {
    throw new LeaveCalculationError("ENDS_BEFORE_START");
  }
  const dates = eachDate(request.startsOn, request.endsOn);
  if (dates.length > 60) {
    throw new LeaveCalculationError("SPAN_TOO_LONG");
  }
  const consumed = [];
  for (const date of dates) {
    const pattern = patternOn(patterns, date);
    if (!pattern) {
      throw new LeaveCalculationError("NO_PATTERN");
    }
    const asked = requestedHalf(
      date,
      request.startsOn,
      request.endsOn,
      request.startPart,
      request.endPart
    );
    const weight = weightHalf(pattern, date);
    const holiday = isHoliday(date, pattern.stateCode, holidays);
    const free = weight === 0 || holiday;
    if (asked === 1 && free) {
      throw new LeaveCalculationError("HALF_ON_FREE_DAY");
    }
    const half = free ? 0 : Math.min(asked, weight);
    if (half > 0) {
      consumed.push({ date, half });
    }
  }
  if (request.maxDaysPerRequest != null && consumedDaysTotal(consumed) > request.maxDaysPerRequest) {
    throw new LeaveCalculationError("MAX_DAYS");
  }
  return consumed;
}
function consumedDaysTotal(days) {
  return halfToDays(days.reduce((sum, day) => sum + day.half, 0));
}
function entitlementDate(year, hiredOn, patterns = []) {
  const start = `${year}-01-01`;
  const on = hiredOn && hiredOn > start && hiredOn.startsWith(`${year}-`) ? hiredOn : start;
  if (patternOn(patterns, on)) {
    return on;
  }
  const firstInYear = [...patterns].filter((pattern) => pattern.effectiveFrom <= `${year}-12-31`).sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom))[0];
  if (firstInYear && firstInYear.effectiveFrom > on) {
    return firstInYear.effectiveFrom;
  }
  return on;
}
function monthsInYear(year, hiredOn) {
  if (!hiredOn || hiredOn <= `${year}-01-01`) {
    return hiredOn && hiredOn > `${year}-12-31` ? 0 : 12;
  }
  if (hiredOn > `${year}-12-31`) {
    return 0;
  }
  const month = Number(hiredOn.slice(5, 7));
  return 12 - (month - 1);
}
function entitlementHalfUnits(input) {
  const on = entitlementDate(input.year, input.hiredOn, input.patterns);
  const pattern = patternOn(input.patterns, on);
  if (!pattern || input.referenceWorkDaysPerWeek <= 0) {
    throw new LeaveCalculationError("NO_PATTERN");
  }
  const workHalf = daysToHalf(pattern.mon) + daysToHalf(pattern.tue) + daysToHalf(pattern.wed) + daysToHalf(pattern.thu) + daysToHalf(pattern.fri) + daysToHalf(pattern.sat) + daysToHalf(pattern.sun);
  const referenceHalf = daysToHalf(input.referenceWorkDaysPerWeek);
  const fullYearHalf = Math.round(
    daysToHalf(input.annualVacationDaysFullTime) * workHalf / referenceHalf
  );
  const months = monthsInYear(input.year, on);
  return Math.round(fullYearHalf * months / 12);
}
function expireOn(year, month, day) {
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}
function allocate(days, entitlementHalf, openingCarry, expiry, asOf) {
  const sorted = [...days].sort((a, b) => a.date.localeCompare(b.date));
  let carryLeft = openingCarry;
  let entitlementLeft = entitlementHalf;
  let carryUsed = 0;
  let takenHalf = 0;
  for (const day of sorted) {
    takenHalf += day.half;
    let left = day.half;
    if (day.date <= expiry && carryLeft > 0) {
      const used = Math.min(carryLeft, left);
      carryLeft -= used;
      carryUsed += used;
      left -= used;
    }
    entitlementLeft -= left;
  }
  if (asOf > expiry) {
    carryLeft = 0;
  }
  return { entitlementLeft, carryLeft, carryUsed, takenHalf };
}
function restHalfFrom(allocation) {
  if (allocation.entitlementLeft < 0) {
    return allocation.entitlementLeft;
  }
  return allocation.entitlementLeft + allocation.carryLeft;
}
function mergeConsumed(slices, year, patterns, holidays) {
  const map = /* @__PURE__ */ new Map();
  for (const slice of slices) {
    for (const day of consumeVacation(slice, patterns, holidays)) {
      if (!day.date.startsWith(`${year}-`)) {
        continue;
      }
      map.set(day.date, (map.get(day.date) ?? 0) + day.half);
    }
  }
  return map;
}
function vacationDaysRestoredBySick(vacations, sickAbsences, patterns, holidays, year) {
  const vacMap = mergeConsumed(
    vacations.filter((slice) => slice.status === "approved"),
    year,
    patterns,
    holidays
  );
  const sickMap = mergeConsumed(sickAbsences, year, patterns, holidays);
  const restored = [];
  for (const [date, sickHalf] of sickMap) {
    const vacHalf = vacMap.get(date) ?? 0;
    if (vacHalf <= 0) {
      continue;
    }
    const half = Math.min(vacHalf, sickHalf);
    restored.push({ date, half });
  }
  return restored;
}
function netApprovedVacationDays(input) {
  const vacMap = mergeConsumed(
    input.vacations.filter((slice) => slice.status === "approved"),
    input.year,
    input.patterns,
    input.holidays
  );
  const sickMap = mergeConsumed(
    input.sickAbsences ?? [],
    input.year,
    input.patterns,
    input.holidays
  );
  for (const [date, sickHalf] of sickMap) {
    const vacHalf = vacMap.get(date) ?? 0;
    if (vacHalf > 0) {
      vacMap.set(date, vacHalf - Math.min(vacHalf, sickHalf));
    }
  }
  return [...vacMap.entries()].filter(([, half]) => half > 0).map(([date, half]) => ({ date, half }));
}
function computeLeaveBalance(input) {
  const entitlementHalf = entitlementHalfUnits(input);
  const openingCarry = daysToHalf(capCarryOverDays(input.carryOverDays, input.maxCarryOverDays));
  const adjustment = daysToHalf(input.adjustmentDays);
  const expiry = expireOn(input.year, input.carryOverExpireMonth, input.carryOverExpireDay);
  const approved = netApprovedVacationDays(input);
  const pending = [];
  for (const vacation of input.vacations) {
    if (vacation.status !== "submitted") {
      continue;
    }
    pending.push(
      ...consumeVacation(vacation, input.patterns, input.holidays).filter(
        (day) => day.date.startsWith(`${input.year}-`)
      )
    );
  }
  const taken = allocate(approved, entitlementHalf + adjustment, openingCarry, expiry, input.asOf);
  const withPending = allocate(
    [...approved, ...pending],
    entitlementHalf + adjustment,
    openingCarry,
    expiry,
    input.asOf
  );
  const pendingHalf = pending.reduce((sum, day) => sum + day.half, 0);
  return {
    entitlement: halfToDays(entitlementHalf),
    carryOver: halfToDays(taken.carryLeft),
    taken: halfToDays(taken.takenHalf),
    pending: halfToDays(pendingHalf),
    rest: halfToDays(restHalfFrom(taken)),
    restAfterPending: halfToDays(restHalfFrom(withPending))
  };
}
function vacationsOverlap(left, right) {
  const dates = new Set(left.map((day) => day.date));
  return right.some((day) => dates.has(day.date));
}

// src/leave-capacity.ts
function userWorksOnDate(member, date, holidays) {
  const pattern = patternOn(member.patterns, date);
  if (!pattern) {
    return false;
  }
  try {
    return consumeVacation(
      { startsOn: date, endsOn: date, startPart: "full", endPart: "full" },
      member.patterns,
      holidays
    ).some((day) => day.date === date);
  } catch {
    return false;
  }
}
function absentUsersOnDate(absences, date, members, holidays) {
  const absent = /* @__PURE__ */ new Set();
  const memberPatterns = new Map(members.map((member) => [member.userId, member.patterns]));
  for (const absence of absences) {
    if (absence.countsTowardCapacity === false) {
      continue;
    }
    const patterns = memberPatterns.get(absence.userId);
    if (!patterns) {
      continue;
    }
    try {
      const days = consumeVacation(absence, patterns, holidays);
      if (days.some((day) => day.date === date)) {
        absent.add(absence.userId);
      }
    } catch {
    }
  }
  return absent;
}
function presentOnDate(members, absences, date, holidays) {
  const working = members.filter((member) => userWorksOnDate(member, date, holidays));
  const absent = absentUsersOnDate(absences, date, members, holidays);
  return working.filter((member) => !absent.has(member.userId)).length;
}
function capacityWarnings(members, approvedAbsences, simulatedAbsence, dates, minPresent, holidays) {
  const absences = simulatedAbsence ? [...approvedAbsences, simulatedAbsence] : approvedAbsences;
  const warnings = [];
  for (const date of dates) {
    const present = presentOnDate(members, absences, date, holidays);
    if (present < minPresent) {
      warnings.push({ date, present, minPresent });
    }
  }
  return warnings;
}
var KB_OPTIMIZE_LOOKBACK_DAYS = 30;
var KB_STALE_ARTICLE_DAYS = 90;
var KB_WEAK_HIT_MAX_SCORE = 0.012;
var KB_OPTIMIZE_MAX_PROPOSALS = 5;
var KB_ASSISTANT_MAX_CONTENT = 4e3;
var KbAiProposalKind = {
  ARTICLE_EDIT: "ARTICLE_EDIT",
  ARTICLE_DRAFT: "ARTICLE_DRAFT"
};
var kbAiProposalKindSchema = z.enum(KbAiProposalKind);
var KB_AI_PROPOSAL_KIND_LABELS = {
  ARTICLE_EDIT: "Artikel\xE4nderung",
  ARTICLE_DRAFT: "Neuer Entwurf"
};
var KbAiProposalStatus = {
  PENDING: "PENDING",
  APPLIED: "APPLIED",
  REJECTED: "REJECTED"
};
var kbAiProposalStatusSchema = z.enum(KbAiProposalStatus);
var KB_AI_PROPOSAL_STATUS_LABELS = {
  PENDING: "Offen",
  APPLIED: "\xDCbernommen",
  REJECTED: "Verworfen"
};
var KbAiProposalSource = {
  ASSISTANT: "ASSISTANT",
  SEARCH_GAP: "SEARCH_GAP",
  TICKET_GAP: "TICKET_GAP",
  STALE_ARTICLE: "STALE_ARTICLE",
  PROBLEM: "PROBLEM"
};
var kbAiProposalSourceSchema = z.enum(KbAiProposalSource);
var KB_AI_PROPOSAL_SOURCE_LABELS = {
  ASSISTANT: "Assistent",
  SEARCH_GAP: "Suche ohne Treffer",
  TICKET_GAP: "Tickets ohne Artikel",
  STALE_ARTICLE: "Veralteter Artikel",
  PROBLEM: "Gel\xF6stes Problem"
};
var kbDiffLineSchema = z.object({
  type: z.enum(["equal", "add", "remove"]),
  text: z.string()
});
var kbArticleDiffSchema = z.object({
  title: z.array(kbDiffLineSchema),
  summary: z.array(kbDiffLineSchema),
  body: z.array(kbDiffLineSchema)
});
var kbProposalArticleRefSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  status: z.string().optional()
});
var kbAiProposalSchema = z.object({
  id: z.string().uuid(),
  kind: kbAiProposalKindSchema,
  status: kbAiProposalStatusSchema,
  source: kbAiProposalSourceSchema,
  proposedTitle: z.string(),
  proposedBody: z.string(),
  proposedSummary: z.string().nullable(),
  currentTitle: z.string().nullable(),
  currentBody: z.string().nullable(),
  currentSummary: z.string().nullable(),
  instruction: z.string(),
  changeSummary: z.string().nullable(),
  sourceQuery: z.string().nullable(),
  sourceTicketIds: z.array(z.string()),
  article: kbProposalArticleRefSchema.nullable(),
  appliedArticle: kbProposalArticleRefSchema.nullable(),
  createdBy: ticketPersonSchema.nullable(),
  reviewedBy: ticketPersonSchema.nullable(),
  reviewedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  diff: kbArticleDiffSchema
});
var kbAiProposalListSchema = z.object({
  items: z.array(kbAiProposalSchema)
});
var kbAssistantChatRequestSchema = z.object({
  messages: z.array(aiChatMessageSchema).min(1).max(12),
  articleSlug: z.string().trim().min(1).max(200).optional()
});
var kbAssistantChatResponseSchema = z.object({
  answer: z.string().min(1),
  proposal: kbAiProposalSchema.nullable(),
  chatAvailable: z.boolean()
});
var kbOptimizationRunSchema = z.object({
  id: z.string().uuid(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  searchGapCount: z.number().int().nonnegative(),
  ticketGapCount: z.number().int().nonnegative(),
  staleArticleCount: z.number().int().nonnegative(),
  proposalsCreated: z.number().int().nonnegative(),
  chatAvailable: z.boolean(),
  error: z.string().nullable()
});
var kbSearchGapSchema = z.object({
  query: z.string(),
  hits: z.number().int().nonnegative(),
  lastSeen: z.iso.datetime(),
  maxScore: z.number().nullable(),
  minResultCount: z.number().int().nonnegative()
});
var kbTicketGapSchema = z.object({
  id: z.string().uuid(),
  number: z.number().int().positive(),
  subject: z.string(),
  resolvedAt: z.iso.datetime().nullable(),
  categoryName: z.string().nullable()
});
var kbStaleArticleSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  updatedAt: z.iso.datetime(),
  recentTicketCount: z.number().int().nonnegative()
});
var kbOptimizationDashboardSchema = z.object({
  lastRun: kbOptimizationRunSchema.nullable(),
  chatAvailable: z.boolean(),
  searchGaps: z.array(kbSearchGapSchema),
  ticketsWithoutArticle: z.array(kbTicketGapSchema),
  staleArticles: z.array(kbStaleArticleSchema),
  proposals: z.array(kbAiProposalSchema)
});
var kbOptimizationRunQueuedSchema = z.object({
  queued: z.literal(true)
});
var assistantModelJsonSchema = z.object({
  canEdit: z.boolean(),
  answer: z.string(),
  articleSlug: z.string().optional(),
  proposedTitle: z.string().optional(),
  proposedSummary: z.string().nullable().optional(),
  proposedBody: z.string().optional(),
  changeSummary: z.string().optional()
});
var draftModelJsonSchema = z.object({
  title: z.string(),
  summary: z.string().optional(),
  body: z.string(),
  rationale: z.string().optional()
});
var kbDraftFromTicketRequestSchema = z.object({
  ticketNumber: z.number().int().positive()
});
function extractJsonObject2(raw) {
  const trimmed = raw.trim();
  const fenced = /```(?:json)?\s*([\s\S]*?)```/i.exec(trimmed);
  const candidate = fenced?.[1]?.trim() ?? trimmed;
  const start = candidate.indexOf("{");
  const end = candidate.lastIndexOf("}");
  if (start < 0 || end <= start) {
    return null;
  }
  try {
    return JSON.parse(candidate.slice(start, end + 1));
  } catch {
    return null;
  }
}
function parseKbAssistantModelJson(raw) {
  const parsed = assistantModelJsonSchema.safeParse(extractJsonObject2(raw));
  return parsed.success ? parsed.data : null;
}
function parseKbDraftModelJson(raw) {
  const parsed = draftModelJsonSchema.safeParse(extractJsonObject2(raw));
  return parsed.success ? parsed.data : null;
}
function htmlFromAiMarkdown(markdown) {
  const parts = [];
  let list = null;
  let paragraph = [];
  const flushParagraph = () => {
    if (paragraph.length === 0) {
      return;
    }
    parts.push(`<p>${inlineMarkdown(paragraph.join(" "))}</p>`);
    paragraph = [];
  };
  const flushList = () => {
    if (!list) {
      return;
    }
    parts.push(`<ul>${list.map((item) => `<li>${inlineMarkdown(item)}</li>`).join("")}</ul>`);
    list = null;
  };
  for (const rawLine of markdown.replace(/\r\n/g, "\n").split("\n")) {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }
    const heading = /^(#{2,3})\s+(.+)$/.exec(line);
    if (heading?.[1] && heading[2]) {
      flushParagraph();
      flushList();
      const tag = heading[1].length === 2 ? "h2" : "h3";
      parts.push(`<${tag}>${inlineMarkdown(heading[2])}</${tag}>`);
      continue;
    }
    const item = /^[-*]\s+(.+)$/.exec(line);
    if (item?.[1]) {
      flushParagraph();
      list ??= [];
      list.push(item[1]);
      continue;
    }
    flushList();
    paragraph.push(line);
  }
  flushParagraph();
  flushList();
  return parts.join("");
}
function inlineMarkdown(text) {
  return escapeHtml2(text).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/`([^`]+)`/g, "<code>$1</code>");
}
function htmlToDiffText(html) {
  return html.replace(/<\/(p|div|li|h[1-6]|tr|blockquote)>/gi, "\n").replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, " ").replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&").replace(/&lt;/gi, "<").replace(/&gt;/gi, ">").replace(/&quot;/gi, '"').replace(/&#39;/g, "'").split("\n").map((line) => line.replace(/\s+/g, " ").trim()).filter((line) => line.length > 0).join("\n");
}
function lineDiff(before, after) {
  const a = splitDiffLines(before);
  const b = splitDiffLines(after);
  const n = a.length;
  const m = b.length;
  const dp = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i2 = n - 1; i2 >= 0; i2 -= 1) {
    for (let j2 = m - 1; j2 >= 0; j2 -= 1) {
      dp[i2][j2] = a[i2] === b[j2] ? (dp[i2 + 1]?.[j2 + 1] ?? 0) + 1 : Math.max(dp[i2 + 1]?.[j2] ?? 0, dp[i2]?.[j2 + 1] ?? 0);
    }
  }
  const out = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ type: "equal", text: a[i] ?? "" });
      i += 1;
      j += 1;
    } else if ((dp[i + 1]?.[j] ?? 0) >= (dp[i]?.[j + 1] ?? 0)) {
      out.push({ type: "remove", text: a[i] ?? "" });
      i += 1;
    } else {
      out.push({ type: "add", text: b[j] ?? "" });
      j += 1;
    }
  }
  while (i < n) {
    out.push({ type: "remove", text: a[i] ?? "" });
    i += 1;
  }
  while (j < m) {
    out.push({ type: "add", text: b[j] ?? "" });
    j += 1;
  }
  return out;
}
function splitDiffLines(value) {
  const lines = value.replace(/\r\n/g, "\n").split("\n");
  return lines.length === 1 && lines[0] === "" ? [] : lines;
}
function buildKbArticleDiff(input) {
  return {
    title: lineDiff(input.currentTitle ?? "", input.proposedTitle),
    summary: lineDiff(input.currentSummary ?? "", input.proposedSummary ?? ""),
    body: lineDiff(htmlToDiffText(input.currentBody ?? ""), htmlToDiffText(input.proposedBody))
  };
}
function lastAssistantInstruction(messages) {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const item = messages[index];
    if (item?.role === "user") {
      return item.content;
    }
  }
  return "";
}
function templateDraftBody(title, tickets) {
  const items = tickets.slice(0, 8).map(
    (ticket) => `<li><strong>#${ticket.number} ${escapeHtml2(ticket.subject)}</strong> \u2014 ${escapeHtml2(ticket.excerpt)}</li>`
  ).join("");
  return `<p>Automatischer Entwurf zum Thema \u201E${escapeHtml2(title)}\u201C. Bitte fachlich pr\xFCfen, bevor der Artikel ver\xF6ffentlicht wird.</p><p>Ausgangspunkt waren k\xFCrzlich gel\xF6ste Tickets:</p><ul>${items}</ul>`;
}
function escapeHtml2(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function excerptPlain(html, maxLength = 280) {
  return plainTextFromHtml(html).slice(0, maxLength);
}
var MAIL_CONNECTION_ID = "default";
var mailOutboundModeSchema = z.enum(["off", "smtp", "microsoft_graph"]);
var mailInboundModeSchema = z.enum(["off", "microsoft_graph", "imap"]);
var isoDate4 = z.iso.datetime().nullable();
var mailConnectionSchema = z.strictObject({
  outboundMode: mailOutboundModeSchema,
  fromName: z.string(),
  fromAddress: z.string(),
  replyTo: z.string(),
  organizationName: z.string(),
  logoUrl: z.string(),
  smtpHost: z.string(),
  smtpPort: z.number().int(),
  smtpSecure: z.boolean(),
  smtpUser: z.string(),
  smtpPasswordSet: z.boolean(),
  graphTenantId: z.string(),
  graphClientId: z.string(),
  graphClientSecretSet: z.boolean(),
  graphMailbox: z.string(),
  inboundMode: mailInboundModeSchema,
  imapHost: z.string(),
  imapPort: z.number().int(),
  imapSecure: z.boolean(),
  imapUser: z.string(),
  imapPasswordSet: z.boolean(),
  imapMailbox: z.string(),
  inboundWatermark: isoDate4,
  lastOutboundAt: isoDate4,
  lastOutboundError: z.string().nullable(),
  lastInboundAt: isoDate4,
  lastInboundError: z.string().nullable(),
  inboundProcessedCount: z.number().int().nonnegative(),
  updatedAt: z.iso.datetime()
});
var updateMailConnectionRequestSchema = z.strictObject({
  outboundMode: mailOutboundModeSchema,
  fromName: z.string().trim().max(200),
  fromAddress: z.string().trim().max(320),
  replyTo: z.string().trim().max(320),
  organizationName: z.string().trim().max(200),
  logoUrl: z.string().trim().max(2e3),
  smtpHost: z.string().trim().max(255),
  smtpPort: z.number().int().min(1).max(65535),
  smtpSecure: z.boolean(),
  smtpUser: z.string().trim().max(320),
  smtpPassword: z.string().max(500).optional(),
  clearSmtpPassword: z.boolean().optional(),
  graphTenantId: z.string().trim().max(200),
  graphClientId: z.string().trim().max(200),
  graphClientSecret: z.string().max(500).optional(),
  clearGraphClientSecret: z.boolean().optional(),
  graphMailbox: z.string().trim().max(320),
  inboundMode: mailInboundModeSchema,
  imapHost: z.string().trim().max(255),
  imapPort: z.number().int().min(1).max(65535),
  imapSecure: z.boolean(),
  imapUser: z.string().trim().max(320),
  imapPassword: z.string().max(500).optional(),
  clearImapPassword: z.boolean().optional(),
  imapMailbox: z.string().trim().max(200)
});
var MailConnectionRulesError = class extends Error {
  code;
  constructor(code, message) {
    super(message);
    this.name = "MailConnectionRulesError";
    this.code = code;
  }
};
function mailSecretIntent(incoming, clear) {
  if ((incoming?.trim() ?? "").length > 0) {
    return "set";
  }
  if (clear) {
    return "clear";
  }
  return "keep";
}
function mailPasswordWillBeSet(intent, currentlySet) {
  if (intent === "set") {
    return true;
  }
  if (intent === "clear") {
    return false;
  }
  return currentlySet;
}
function formatMailFrom(fromName, fromAddress) {
  const name = fromName.trim();
  const address = fromAddress.trim();
  if (!address) {
    return name;
  }
  if (!name) {
    return address;
  }
  const escaped = name.replaceAll("\\", "\\\\").replaceAll('"', '\\"');
  return `"${escaped}" <${address}>`;
}
var mailConnectionProbeSchema = z.strictObject({
  ok: z.boolean(),
  outboundMode: mailOutboundModeSchema,
  detail: z.string()
});
var mailConnectionTestResponseSchema = z.strictObject({
  ok: z.literal(true),
  to: z.string().email(),
  subject: z.string()
});
function isEmail(value) {
  return z.string().email().safeParse(value).success;
}
function isPublicIpAddress(address) {
  const host = address.toLowerCase().split("%")[0] ?? "";
  if (host.includes(":")) {
    if (host === "::" || host === "::1" || host.startsWith("fe80:") || host.startsWith("fc") || host.startsWith("fd")) {
      return false;
    }
    const mapped = /^::ffff:(\d+\.\d+\.\d+\.\d+)$/.exec(host);
    if (mapped?.[1]) {
      return !isPrivateIpv4(mapped[1]);
    }
    const hex = /^::ffff:([0-9a-f]{1,4}):([0-9a-f]{1,4})$/.exec(host);
    if (hex?.[1] && hex[2]) {
      const high = Number.parseInt(hex[1], 16);
      const low = Number.parseInt(hex[2], 16);
      const dotted = `${high >> 8 & 255}.${high & 255}.${low >> 8 & 255}.${low & 255}`;
      return !isPrivateIpv4(dotted);
    }
    return true;
  }
  return !isPrivateIpv4(host);
}
function isLinkLocalAddress(address) {
  const host = address.toLowerCase().replace(/^\[|\]$/g, "").split("%")[0] ?? "";
  if (host === "0.0.0.0" || host === "::") {
    return true;
  }
  if (host.startsWith("fe80:")) {
    return true;
  }
  const mapped = /^::ffff:(\d+\.\d+\.\d+\.\d+)$/.exec(host);
  const ipv4 = mapped?.[1] ?? host;
  const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(ipv4);
  if (!match) {
    return false;
  }
  const a = Number(match[1]);
  const b = Number(match[2]);
  if ([a, b, Number(match[3]), Number(match[4])].some((part) => part > 255)) {
    return true;
  }
  if (a === 0) {
    return true;
  }
  return a === 169 && b === 254;
}
function isMetadataHostname(host) {
  const name = host.toLowerCase().replace(/^\[|\]$/g, "").replace(/\.$/, "");
  return name === "metadata.google.internal" || name.endsWith(".metadata.google.internal") || name === "metadata.internal";
}
function isPublicHttpsUrl(value) {
  let url;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol !== "https:" || url.username || url.password) {
    return false;
  }
  const host = url.hostname.replace(/^\[|\]$/g, "").toLowerCase();
  if (host === "localhost" || host.endsWith(".localhost") || host.endsWith(".local")) {
    return false;
  }
  if (host.includes(":") || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return isPublicIpAddress(host);
  }
  return true;
}
function isPrivateIpv4(host) {
  const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (!match) {
    return false;
  }
  const a = Number(match[1]);
  const b = Number(match[2]);
  if ([a, b, Number(match[3]), Number(match[4])].some((part) => part > 255)) {
    return true;
  }
  if (a === 10 || a === 127 || a === 0) {
    return true;
  }
  if (a === 169 && b === 254) {
    return true;
  }
  if (a === 172 && b >= 16 && b <= 31) {
    return true;
  }
  if (a === 192 && b === 168) {
    return true;
  }
  return a === 100 && b >= 64 && b <= 127;
}
function assertNotMetadataHost(host, code) {
  const trimmed = host.trim();
  if (isMetadataHostname(trimmed) || isLinkLocalAddress(trimmed)) {
    throw new MailConnectionRulesError(
      code,
      "Dieser Host zeigt auf eine Metadaten- oder Link-Local-Adresse."
    );
  }
}
function assertPort(port, code) {
  if (!Number.isInteger(port) || port < 1 || port > 65535) {
    throw new MailConnectionRulesError(code, "Der Port liegt au\xDFerhalb von 1 bis 65535.");
  }
}
function assertGraph(check) {
  if (check.graphTenantId.trim().length === 0) {
    throw new MailConnectionRulesError(
      "MAIL_GRAPH_TENANT_REQUIRED",
      "Die Tenant-ID von Microsoft 365 fehlt."
    );
  }
  if (check.graphClientId.trim().length === 0) {
    throw new MailConnectionRulesError(
      "MAIL_GRAPH_CLIENT_REQUIRED",
      "Die Anwendungs-ID von Microsoft 365 fehlt."
    );
  }
  if (!check.graphClientSecretSet) {
    throw new MailConnectionRulesError(
      "MAIL_GRAPH_SECRET_REQUIRED",
      "Das Anwendungsgeheimnis von Microsoft 365 fehlt."
    );
  }
  const mailbox = check.graphMailbox.trim();
  if (!isEmail(mailbox)) {
    throw new MailConnectionRulesError(
      "MAIL_GRAPH_MAILBOX_REQUIRED",
      "Das Postfach von Microsoft 365 ist keine g\xFCltige E-Mail-Adresse."
    );
  }
  return mailbox;
}
function assertMailConnection(check) {
  const replyTo = check.replyTo.trim();
  if (replyTo.length > 0 && !isEmail(replyTo)) {
    throw new MailConnectionRulesError("MAIL_REPLY_TO_INVALID", "Die Antwortadresse ist ung\xFCltig.");
  }
  const logoUrl = check.logoUrl.trim();
  if (logoUrl.length > 0 && !isPublicHttpsUrl(logoUrl)) {
    throw new MailConnectionRulesError(
      "MAIL_LOGO_URL_INVALID",
      "Die Logo-Adresse muss eine \xF6ffentliche HTTPS-Adresse sein."
    );
  }
  let fromAddress = check.fromAddress.trim();
  if (check.outboundMode === "smtp") {
    if (!isEmail(fromAddress)) {
      throw new MailConnectionRulesError(
        "MAIL_FROM_ADDRESS_REQUIRED",
        "Die Absenderadresse fehlt oder ist ung\xFCltig."
      );
    }
    if (check.smtpHost.trim().length === 0) {
      throw new MailConnectionRulesError("MAIL_SMTP_HOST_REQUIRED", "Der SMTP-Host fehlt.");
    }
    assertNotMetadataHost(check.smtpHost, "MAIL_SMTP_HOST_REQUIRED");
    assertPort(check.smtpPort, "MAIL_SMTP_PORT_INVALID");
    const hasUser = check.smtpUser.trim().length > 0;
    if (hasUser !== check.smtpPasswordSet) {
      throw new MailConnectionRulesError(
        "MAIL_SMTP_AUTH",
        "SMTP braucht Benutzer und Kennwort zusammen, oder keins von beiden."
      );
    }
  }
  const graphActive = check.outboundMode === "microsoft_graph" || check.inboundMode === "microsoft_graph";
  if (graphActive) {
    const mailbox = assertGraph(check);
    if (check.outboundMode === "microsoft_graph") {
      fromAddress = mailbox;
    }
  }
  if (check.inboundMode === "imap") {
    if (check.imapHost.trim().length === 0) {
      throw new MailConnectionRulesError("MAIL_IMAP_HOST_REQUIRED", "Der IMAP-Host fehlt.");
    }
    assertNotMetadataHost(check.imapHost, "MAIL_IMAP_HOST_REQUIRED");
    assertPort(check.imapPort, "MAIL_IMAP_PORT_INVALID");
    if (check.imapUser.trim().length === 0) {
      throw new MailConnectionRulesError("MAIL_IMAP_USER_REQUIRED", "Der IMAP-Benutzer fehlt.");
    }
    if (!check.imapPasswordSet) {
      throw new MailConnectionRulesError("MAIL_IMAP_PASSWORD_REQUIRED", "Das IMAP-Kennwort fehlt.");
    }
    if (check.imapMailbox.trim().length === 0) {
      throw new MailConnectionRulesError("MAIL_IMAP_MAILBOX_REQUIRED", "Das IMAP-Postfach fehlt.");
    }
  }
  return {
    ...check,
    fromName: check.fromName.trim(),
    fromAddress,
    replyTo,
    organizationName: check.organizationName.trim(),
    logoUrl,
    smtpHost: check.smtpHost.trim(),
    smtpUser: check.smtpUser.trim(),
    graphTenantId: check.graphTenantId.trim(),
    graphClientId: check.graphClientId.trim(),
    graphMailbox: check.graphMailbox.trim(),
    imapHost: check.imapHost.trim(),
    imapUser: check.imapUser.trim(),
    imapMailbox: check.imapMailbox.trim()
  };
}

// src/mail-template-catalog.ts
function frame(subjectTemplate, headingTemplate, paragraphs, actionLabel) {
  return {
    subjectTemplate,
    headingTemplate,
    bodyParagraphs: ["Hallo {{recipient.name}},", ...paragraphs],
    actionLabel
  };
}
var SOCIAL = frame(
  "{{portal.name}} \u2013 {{notification.title}}",
  "{{notification.title}}",
  ["{{notification.body}}"],
  "Im Intranet \xF6ffnen"
);
var FRAMED_BODY = frame(
  "{{portal.name}} \u2013 {{notification.title}}",
  "{{notification.title}}",
  ["{{notification.body}}"],
  "Antrag \xF6ffnen"
);
var MAIL_TEMPLATE_DEFAULTS = {
  [NotificationEventKey.CSAT]: frame(
    "{{notification.title}}",
    "{{notification.title}}",
    ["{{notification.body}}"],
    "Umfrage \xF6ffnen"
  ),
  [NotificationEventKey.INTRANET_NEWS_PUBLISHED]: frame(
    "{{portal.name}} \u2013 {{content.title}}",
    "Neue Nachricht",
    ["im Intranet ist eine neue Nachricht erschienen: \u201E{{content.title}}\u201C.", "{{content.teaser}}"],
    "Nachricht \xF6ffnen"
  ),
  [NotificationEventKey.INTRANET_ACK_REQUIRED]: frame(
    "{{portal.name}} \u2013 Lesebest\xE4tigung",
    "intranet.ack.heading",
    ["platform.mail.intranet.ack.body"],
    "Nachricht \xF6ffnen"
  ),
  [NotificationEventKey.INTRANET_ACK_REMINDER]: frame(
    "{{portal.name}} \u2013 Erinnerung Lesebest\xE4tigung",
    "Lesebest\xE4tigung noch offen",
    ["f\xFCr \u201E{{content.title}}\u201C liegt noch keine Lesebest\xE4tigung vor. Die Frist l\xE4uft bald ab."],
    "Jetzt best\xE4tigen"
  ),
  [NotificationEventKey.INTRANET_REVIEW_DUE]: frame(
    "{{portal.name}} \u2013 Pr\xFCfung f\xE4llig",
    "Inhalt pr\xFCfen",
    ["\u201E{{content.title}}\u201C soll gepr\xFCft werden."],
    "Inhalt \xF6ffnen"
  ),
  [NotificationEventKey.INTRANET_MELDUNG]: frame(
    "{{portal.name}} \u2013 Meldung",
    "Meldung zu einer Seite",
    ["zu \u201E{{content.title}}\u201C liegt eine Meldung vor."],
    "Seite \xF6ffnen"
  ),
  [NotificationEventKey.INTRANET_CONTENT_LIKED]: SOCIAL,
  [NotificationEventKey.INTRANET_COMMENT_CREATED]: SOCIAL,
  [NotificationEventKey.INTRANET_COMMENT_REPLIED]: SOCIAL,
  [NotificationEventKey.INTRANET_COMMENT_LIKED]: SOCIAL,
  [NotificationEventKey.INTRANET_MENTIONED]: SOCIAL,
  [NotificationEventKey.INTRANET_SITE_DIARY_SUBMITTED]: {
    subjectTemplate: "Bautagesbericht {{report.number}} \u2013 {{report.project}}",
    headingTemplate: "Bautagesbericht",
    bodyParagraphs: [
      "{{actor.name}} hat den Bautagesbericht {{report.number}} f\xFCr {{report.project}} ({{report.date}}) abgeschickt.",
      "Baustelle: {{report.address}}",
      "Die PDF-Datei ist dieser E-Mail angeh\xE4ngt."
    ],
    actionLabel: "Bericht \xF6ffnen"
  },
  [NotificationEventKey.LEAVE_REQUEST_SUBMITTED]: frame(
    "{{portal.name}} \u2013 Urlaubsantrag",
    "Neuer Abwesenheitsantrag",
    ["{{leave.requester}} hat einen Abwesenheitsantrag eingereicht.", "Zeitraum: {{leave.range}}"],
    "Antrag \xF6ffnen"
  ),
  [NotificationEventKey.LEAVE_REQUEST_APPROVED]: FRAMED_BODY,
  [NotificationEventKey.LEAVE_REQUEST_REJECTED]: frame(
    "{{portal.name}} \u2013 Urlaub abgelehnt",
    "Urlaubsantrag abgelehnt",
    ["platform.mail.leave.rejected.body", "{{leave.note}}"],
    "Antrag \xF6ffnen"
  ),
  [NotificationEventKey.LEAVE_REQUEST_CANCELLED]: frame(
    "{{portal.name}} \u2013 Antrag zur\xFCckgezogen",
    "Abwesenheitsantrag zur\xFCckgezogen",
    ["{{leave.requester}} hat den Antrag f\xFCr den Zeitraum {{leave.range}} zur\xFCckgezogen."],
    "Antrag \xF6ffnen"
  ),
  [NotificationEventKey.LEAVE_REMINDER_UPCOMING]: frame(
    "{{portal.name}} \u2013 Urlaub beginnt bald",
    "platform.mail.leave.soon.heading",
    ["platform.mail.leave.soon.body"],
    "Antrag \xF6ffnen"
  ),
  [NotificationEventKey.FLEET_ASSIGNMENT_STARTED]: frame(
    "{{portal.name}} \u2013 Fahrzeug zugeordnet",
    "Fahrzeug {{vehicle.plate}}",
    ["platform.mail.fleet.assigned.body"],
    "Fahrzeug \xF6ffnen"
  ),
  [NotificationEventKey.FLEET_DAMAGE_REPORTED]: frame(
    "{{portal.name}} \u2013 Schadenmeldung",
    "Schaden oder Mangel an {{vehicle.plate}}",
    ["{{actor.name}} hat einen Schaden oder Mangel an {{vehicle.plate}} gemeldet."],
    "Meldung \xF6ffnen"
  ),
  [NotificationEventKey.FLEET_DAMAGE_CLOSED]: frame(
    "{{portal.name}} \u2013 {{notification.title}}",
    "{{notification.title}}",
    ["{{notification.body}}"],
    "Meldung \xF6ffnen"
  ),
  [NotificationEventKey.FLEET_REMINDER_UPCOMING]: frame(
    "{{portal.name}} \u2013 Frist bald f\xE4llig",
    "{{notification.title}}",
    ["{{notification.body}}"],
    "Im Fuhrpark \xF6ffnen"
  ),
  [NotificationEventKey.FLEET_REMINDER_OVERDUE]: frame(
    "{{portal.name}} \u2013 Frist \xFCberf\xE4llig",
    "{{notification.title}}",
    ["{{notification.body}}"],
    "Im Fuhrpark \xF6ffnen"
  ),
  [NotificationEventKey.FLEET_TELEMATICS_STALE]: frame(
    "{{portal.name}} \u2013 kein Telematik-Signal",
    "Kein Signal f\xFCr {{vehicle.plate}}",
    ["f\xFCr {{vehicle.plate}} liegt kein aktuelles Telematik-Signal vor."],
    "Fahrzeug \xF6ffnen"
  ),
  [NotificationEventKey.FLEET_FUEL_EXCEPTION]: frame(
    "{{portal.name}} \u2013 Tankkarten-Ausnahme",
    "Tankkarte pr\xFCfen",
    ["{{fleet.subject}}: {{fleet.reason}}"],
    "Umsatz \xF6ffnen"
  ),
  [NotificationEventKey.FLEET_GUEST_INVOICE]: frame(
    "{{portal.name}} \u2013 Rechnung {{invoice.number}}",
    "Rechnung {{invoice.number}}",
    [
      "Energie: {{invoice.energy}}",
      "Netto: {{invoice.net}}",
      "MwSt: {{invoice.vat}}",
      "Brutto: {{invoice.gross}}",
      "Ende der Ladung: {{invoice.endedAt}}",
      "{{invoice.ocmf}}",
      "Der Betrag wird per Lastschrift eingezogen."
    ],
    ""
  ),
  [NotificationEventKey.MAINTENANCE_WORK_ORDER_ASSIGNED]: frame(
    "{{portal.name}} \u2013 Wartungsauftrag zugewiesen",
    "Auftrag {{order.number}}",
    ["platform.mail.maintenance.assigned.body"],
    "Auftrag \xF6ffnen"
  ),
  [NotificationEventKey.MAINTENANCE_WORK_ORDER_REASSIGNED]: frame(
    "{{portal.name}} \u2013 Wartungsauftrag neu zugewiesen",
    "Auftrag {{order.number}}",
    ["platform.mail.maintenance.reassigned.body"],
    "Auftrag \xF6ffnen"
  ),
  [NotificationEventKey.MAINTENANCE_WORK_ORDER_DUE_SOON]: frame(
    "{{portal.name}} \u2013 Wartung bald f\xE4llig",
    "Auftrag {{order.number}}",
    ["der Wartungsauftrag {{order.number}} ist am {{order.dueOn}} f\xE4llig."],
    "Auftrag \xF6ffnen"
  ),
  [NotificationEventKey.MAINTENANCE_WORK_ORDER_OVERDUE]: frame(
    "{{portal.name}} \u2013 Wartung \xFCberf\xE4llig",
    "Auftrag {{order.number}}",
    ["der Wartungsauftrag {{order.number}} war am {{order.dueOn}} f\xE4llig."],
    "Auftrag \xF6ffnen"
  ),
  [NotificationEventKey.MAINTENANCE_PROTOCOL_COMPLETED]: frame(
    "{{portal.name}} \u2013 Protokoll abgeschlossen",
    "Protokoll {{order.number}}",
    [
      "das Wartungsprotokoll zum Auftrag {{order.number}} ist abgeschlossen. Das PDF steht zum Download bereit."
    ],
    "Protokoll \xF6ffnen"
  ),
  [NotificationEventKey.MAINTENANCE_FINALIZATION_FAILED]: frame(
    "{{portal.name}} \u2013 PDF fehlgeschlagen",
    "PDF-Erzeugung fehlgeschlagen",
    ["platform.mail.maintenance.pdf-failed.body"],
    "Auftrag \xF6ffnen"
  ),
  [NotificationEventKey.ONBOARDING_TASK_ASSIGNED]: frame(
    "{{portal.name}} \u2013 Aufgabe zugewiesen",
    "Aufgabe {{onboarding.task}}",
    ["platform.mail.onboarding.assigned.body", "F\xE4llig am {{onboarding.dueOn}}."],
    "Aufgabe \xF6ffnen"
  ),
  [NotificationEventKey.ONBOARDING_TASK_DUE_SOON]: frame(
    "{{portal.name}} \u2013 Aufgabe bald f\xE4llig",
    "Aufgabe {{onboarding.task}}",
    [
      "die Aufgabe \u201E{{onboarding.task}}\u201C f\xFCr {{onboarding.subject}} ist am {{onboarding.dueOn}} f\xE4llig."
    ],
    "Aufgabe \xF6ffnen"
  ),
  [NotificationEventKey.ONBOARDING_TASK_OVERDUE]: frame(
    "{{portal.name}} \u2013 Aufgabe \xFCberf\xE4llig",
    "Aufgabe {{onboarding.task}}",
    [
      "die Aufgabe \u201E{{onboarding.task}}\u201C f\xFCr {{onboarding.subject}} war am {{onboarding.dueOn}} f\xE4llig."
    ],
    "Aufgabe \xF6ffnen"
  ),
  [NotificationEventKey.ONBOARDING_CASE_STARTED]: frame(
    "{{portal.name}} \u2013 Vorgang gestartet",
    "Vorgang f\xFCr {{onboarding.subject}}",
    [
      "der Vorgang f\xFCr {{onboarding.subject}} wurde gestartet.",
      "Eintritt am {{onboarding.entryOn}}."
    ],
    "Vorgang \xF6ffnen"
  ),
  [NotificationEventKey.ONBOARDING_SUBJECT_ADMITTED]: frame(
    "{{portal.name}} \u2013 Person aufgenommen",
    "{{onboarding.subject}} ist aufgenommen",
    ["{{onboarding.subject}} ist jetzt ein Benutzer.", "Eintritt am {{onboarding.entryOn}}."],
    "Aufgaben \xF6ffnen"
  ),
  [NotificationEventKey.ONBOARDING_CASE_COMPLETED]: frame(
    "{{portal.name}} \u2013 Vorgang abgeschlossen",
    "Vorgang abgeschlossen",
    ["der Vorgang f\xFCr {{onboarding.subject}} wurde abgeschlossen."],
    "Vorgang \xF6ffnen"
  ),
  [NotificationEventKey.ASSIGNMENTS_RELEASED]: frame(
    "{{portal.name}} \u2013 Zuordnungen beendet",
    "Zuordnungen beendet",
    ["{{notification.body}}"],
    "Person \xF6ffnen"
  )
};
var MAIL_TEMPLATE_EVENT_SAMPLE = {
  [NotificationEventKey.CREATED]: {
    title: "Neues Ticket",
    body: "Ein neues Ticket ist eingegangen."
  },
  [NotificationEventKey.STATUS_CHANGED]: {
    title: "Status ge\xE4ndert",
    body: "Der Status wurde ge\xE4ndert."
  },
  [NotificationEventKey.COMMENTED]: {
    title: "Neue Antwort",
    body: "platform.mail.sample.comment"
  },
  [NotificationEventKey.ASSIGNED]: {
    title: "Zugewiesen",
    body: "platform.mail.sample.assigned"
  },
  [NotificationEventKey.UPDATED]: {
    title: "Ticket ge\xE4ndert",
    body: "Priorit\xE4t: Mittel \u2192 Hoch"
  },
  [NotificationEventKey.SLA_WARNING]: {
    title: "SLA-Warnung",
    body: "Eine Frist l\xE4uft bald ab."
  },
  [NotificationEventKey.APPROVAL_REQUESTED]: {
    title: "Freigabe angefordert",
    body: "platform.mail.sample.approval"
  },
  [NotificationEventKey.APPROVAL_DECIDED]: {
    title: "Freigabe entschieden",
    body: "Die Freigabe wurde erteilt."
  },
  [NotificationEventKey.INCIDENT_BROADCAST]: {
    title: "Major-Incident-Mitteilung",
    body: "Es gibt eine neue Statusmeldung zum Major Incident."
  },
  [NotificationEventKey.AUTOMATION]: {
    title: "Automatisierung",
    body: "Eine Regel hat eine Benachrichtigung ausgel\xF6st."
  },
  [NotificationEventKey.REMOTE_SESSION]: {
    title: "Remote-Support",
    body: "Eine Remote-Support-Sitzung wurde angefragt."
  },
  [NotificationEventKey.CSAT]: {
    title: "platform.mail.sample.csat.title",
    body: "platform.mail.sample.csat.body"
  },
  [NotificationEventKey.RESOLVED_REMINDER]: {
    title: "Erinnerung vor Schlie\xDFung",
    body: "Das Ticket wird bald automatisch geschlossen."
  },
  [NotificationEventKey.INTRANET_NEWS_PUBLISHED]: {
    title: "Neue Nachricht: Sommerfest",
    body: "Treffpunkt um 15 Uhr am Werkstor."
  },
  [NotificationEventKey.INTRANET_ACK_REQUIRED]: {
    title: "Lesebest\xE4tigung: Sommerfest",
    body: "platform.mail.sample.ack.body"
  },
  [NotificationEventKey.INTRANET_ACK_REMINDER]: {
    title: "Erinnerung: Sommerfest",
    body: "platform.mail.sample.ack.reminder"
  },
  [NotificationEventKey.INTRANET_REVIEW_DUE]: {
    title: "Pr\xFCfung f\xE4llig",
    body: "\u201ESommerfest\u201C soll gepr\xFCft werden."
  },
  [NotificationEventKey.INTRANET_MELDUNG]: {
    title: "Meldung",
    body: "Zu \u201ESommerfest\u201C liegt eine Meldung vor."
  },
  [NotificationEventKey.INTRANET_CONTENT_LIKED]: {
    title: "Gef\xE4llt mir (3)",
    body: "Lea Leitung gef\xE4llt \u201ESommerfest\u201C."
  },
  [NotificationEventKey.INTRANET_COMMENT_CREATED]: {
    title: "Kommentare (4)",
    body: "Lea Leitung hat \u201ESommerfest\u201C kommentiert."
  },
  [NotificationEventKey.INTRANET_COMMENT_REPLIED]: {
    title: "Antworten (2)",
    body: "platform.mail.sample.comment.reply"
  },
  [NotificationEventKey.INTRANET_COMMENT_LIKED]: {
    title: "Gef\xE4llt mir (2)",
    body: "platform.mail.sample.comment.liked"
  },
  [NotificationEventKey.INTRANET_MENTIONED]: {
    title: "Erw\xE4hnung",
    body: "platform.mail.sample.mention"
  },
  [NotificationEventKey.INTRANET_SITE_DIARY_SUBMITTED]: {
    title: "Bautagesbericht",
    body: "Mina Berger hat den Bautagesbericht BT-2026-0007 f\xFCr Neubau Halle 3 (14.10.2026) abgeschickt."
  },
  [NotificationEventKey.LEAVE_REQUEST_SUBMITTED]: {
    title: "Abwesenheitsantrag: Max M\xFCller",
    body: "Max M\xFCller hat einen Antrag eingereicht (02.03.2026 \u2013 06.03.2026)."
  },
  [NotificationEventKey.LEAVE_REQUEST_APPROVED]: {
    title: "Urlaub genehmigt",
    body: "platform.mail.sample.leave.approved"
  },
  [NotificationEventKey.LEAVE_REQUEST_REJECTED]: {
    title: "Urlaub abgelehnt",
    body: "platform.mail.sample.leave.rejected"
  },
  [NotificationEventKey.LEAVE_REQUEST_CANCELLED]: {
    title: "Antrag zur\xFCckgezogen: Max M\xFCller",
    body: "Max M\xFCller hat den Urlaubsantrag (02.03.2026 \u2013 06.03.2026) zur\xFCckgezogen."
  },
  [NotificationEventKey.LEAVE_REMINDER_UPCOMING]: {
    title: "Urlaub beginnt bald",
    body: "platform.mail.sample.leave.soon"
  },
  [NotificationEventKey.FLEET_ASSIGNMENT_STARTED]: {
    title: "Fahrzeug WI-GB 2042 zugeordnet",
    body: "platform.mail.sample.fleet.assigned"
  },
  [NotificationEventKey.FLEET_DAMAGE_REPORTED]: {
    title: "Schaden gemeldet: WI-GB 2042",
    body: "Lea Leitung hat einen Schaden oder Mangel an WI-GB 2042 gemeldet."
  },
  [NotificationEventKey.FLEET_DAMAGE_CLOSED]: {
    title: "Meldung geschlossen: WI-GB 2042",
    body: "platform.mail.sample.fleet.closed"
  },
  [NotificationEventKey.FLEET_REMINDER_UPCOMING]: {
    title: "Bald f\xE4llig: Hauptuntersuchung",
    body: "Die Hauptuntersuchung f\xFCr WI-GB 2042 ist betroffen."
  },
  [NotificationEventKey.FLEET_REMINDER_OVERDUE]: {
    title: "\xDCberf\xE4llig: Hauptuntersuchung",
    body: "Die Hauptuntersuchung f\xFCr WI-GB 2042 ist betroffen."
  },
  [NotificationEventKey.FLEET_TELEMATICS_STALE]: {
    title: "Kein Signal (WI-GB 2042)",
    body: "F\xFCr WI-GB 2042 liegt kein aktuelles Telematik-Signal vor."
  },
  [NotificationEventKey.FLEET_FUEL_EXCEPTION]: {
    title: "Tankkarte: Hauptuntersuchung",
    body: "Hauptuntersuchung: Karte unbekannt"
  },
  [NotificationEventKey.FLEET_GUEST_INVOICE]: {
    title: "Rechnung 12",
    body: "Der Betrag wird per Lastschrift eingezogen."
  },
  [NotificationEventKey.MAINTENANCE_WORK_ORDER_ASSIGNED]: {
    title: "Wartungsauftrag WA-1042",
    body: "platform.mail.sample.maintenance.assigned"
  },
  [NotificationEventKey.MAINTENANCE_WORK_ORDER_REASSIGNED]: {
    title: "Wartungsauftrag WA-1042",
    body: "platform.mail.sample.maintenance.reassigned"
  },
  [NotificationEventKey.MAINTENANCE_WORK_ORDER_DUE_SOON]: {
    title: "Bald f\xE4llig: WA-1042",
    body: "Der Wartungsauftrag ist am 20.03.2026 f\xE4llig."
  },
  [NotificationEventKey.MAINTENANCE_WORK_ORDER_OVERDUE]: {
    title: "\xDCberf\xE4llig: WA-1042",
    body: "Der Wartungsauftrag war am 20.03.2026 f\xE4llig."
  },
  [NotificationEventKey.MAINTENANCE_PROTOCOL_COMPLETED]: {
    title: "Protokoll WA-1042 abgeschlossen",
    body: "Das Wartungsprotokoll wurde abgeschlossen. Das PDF steht zum Download bereit."
  },
  [NotificationEventKey.MAINTENANCE_FINALIZATION_FAILED]: {
    title: "PDF-Erzeugung fehlgeschlagen (WA-1042)",
    body: "Die Erzeugung des Abschluss-PDFs ist fehlgeschlagen."
  },
  [NotificationEventKey.ONBOARDING_TASK_ASSIGNED]: {
    title: "Aufgabe: Laptop ausgeben",
    body: "platform.mail.sample.onboarding.assigned"
  },
  [NotificationEventKey.ONBOARDING_TASK_DUE_SOON]: {
    title: "Bald f\xE4llig: Laptop ausgeben",
    body: "Die Aufgabe \u201ELaptop ausgeben\u201C f\xFCr Anna Beispiel ist am 01.04.2026 f\xE4llig."
  },
  [NotificationEventKey.ONBOARDING_TASK_OVERDUE]: {
    title: "\xDCberf\xE4llig: Laptop ausgeben",
    body: "Die Aufgabe \u201ELaptop ausgeben\u201C f\xFCr Anna Beispiel war am 01.04.2026 f\xE4llig."
  },
  [NotificationEventKey.ONBOARDING_CASE_STARTED]: {
    title: "Vorgang gestartet",
    body: "Der Vorgang f\xFCr Anna Beispiel wurde gestartet."
  },
  [NotificationEventKey.ONBOARDING_SUBJECT_ADMITTED]: {
    title: "Person aufgenommen",
    body: "Anna Beispiel ist jetzt ein Benutzer."
  },
  [NotificationEventKey.ONBOARDING_CASE_COMPLETED]: {
    title: "Vorgang abgeschlossen",
    body: "Der Vorgang f\xFCr Anna Beispiel wurde abgeschlossen."
  }
};
function mailTemplateEventSample(eventKey) {
  return MAIL_TEMPLATE_EVENT_SAMPLE[eventKey] ?? {
    title: "Benachrichtigung",
    body: "Dies ist eine Beispielnachricht."
  };
}

// src/mail-workspace.ts
var MAIL_POLICY_ID = "default";
var MAIL_TEMPLATE_PLACEHOLDERS = [
  "ticket.number",
  "ticket.subject",
  "ticket.status",
  "ticket.url",
  "actor.name",
  "comment.excerpt",
  "notification.body",
  "changes.summary",
  "recipient.name",
  "portal.name",
  "notification.title",
  "action.url",
  "content.title",
  "content.teaser",
  "leave.requester",
  "leave.range",
  "leave.note",
  "leave.days",
  "vehicle.plate",
  "fleet.period",
  "fleet.subject",
  "fleet.reason",
  "order.number",
  "order.dueOn",
  "onboarding.task",
  "onboarding.dueOn",
  "onboarding.subject",
  "onboarding.entryOn",
  "report.number",
  "report.date",
  "report.project",
  "report.address",
  "instance.label",
  "invoice.number",
  "invoice.energy",
  "invoice.net",
  "invoice.vat",
  "invoice.gross",
  "invoice.endedAt",
  "invoice.ocmf"
];
var MAIL_TEMPLATE_PLACEHOLDER_HELP = {
  "ticket.number": "Ticketnummer",
  "ticket.subject": "Betreff",
  "ticket.status": "Status (deutsch)",
  "ticket.url": "Deep-Link (Portal oder Agent)",
  "actor.name": "Name der ausl\xF6senden Person",
  "comment.excerpt": "\xD6ffentlicher Kommentar / Benachrichtigungstext",
  "notification.body": "Benachrichtigungstext f\xFCr diesen Empf\xE4nger",
  "changes.summary": "Zusammenfassung bei ticket.updated",
  "recipient.name": "Vorname f\xFCr die Anrede (z. B. Hallo {{recipient.name}})",
  "portal.name": "Name des Portals",
  "notification.title": "Titel der Benachrichtigung",
  "action.url": "Link zum \xD6ffnen",
  "content.title": "Titel des Intranet-Inhalts",
  "content.teaser": "Teaser der Nachricht",
  "leave.requester": "Name der antragstellenden Person",
  "leave.range": "Zeitraum des Antrags",
  "leave.note": "Hinweis zur Ablehnung",
  "leave.days": "Tage bis zum Urlaubsbeginn",
  "vehicle.plate": "Kennzeichen",
  "fleet.period": "Zeitraum der Zuordnung",
  "fleet.subject": "Bezeichnung, zum Beispiel die Tankkarte",
  "fleet.reason": "Grund der Tankkarten-Ausnahme",
  "order.number": "Nummer des Wartungsauftrags",
  "order.dueOn": "F\xE4lligkeitsdatum",
  "onboarding.task": "Titel der Aufgabe",
  "onboarding.dueOn": "F\xE4lligkeitsdatum der Aufgabe",
  "onboarding.subject": "Anzeigename der betroffenen Person",
  "onboarding.entryOn": "Eintrittsdatum",
  "report.number": "Laufende Nummer des Bautagesberichts",
  "report.date": "Datum des Berichts",
  "report.project": "Bauprojekt",
  "report.address": "Adresse der Baustelle",
  "instance.label": "Bezeichnung der Modul-Instanz",
  "invoice.number": "Rechnungsnummer",
  "invoice.energy": "Geladene Energie",
  "invoice.net": "Nettobetrag",
  "invoice.vat": "Mehrwertsteuer",
  "invoice.gross": "Bruttobetrag",
  "invoice.endedAt": "Ende der Ladung",
  "invoice.ocmf": "Signierter Z\xE4hlerdatensatz"
};
var MAIL_TEMPLATE_PLACEHOLDER_SAMPLE = {
  "ticket.number": "42",
  "ticket.subject": "Drucker klemmt",
  "ticket.status": "In Bearbeitung",
  "ticket.url": "https://example.invalid/tickets/42",
  "actor.name": "Lea Leitung",
  "comment.excerpt": "platform.mail.sample.comment",
  "notification.body": "Im Intranet ist eine neue Nachricht erschienen.",
  "changes.summary": "Priorit\xE4t: Mittel \u2192 Hoch",
  "recipient.name": "Anna",
  "portal.name": APP_DISPLAY_NAME,
  "notification.title": "Neue Nachricht",
  "action.url": "https://example.invalid/beispiel",
  "content.title": "Sommerfest",
  "content.teaser": "Treffpunkt um 15 Uhr am Werkstor.",
  "leave.requester": "Max M\xFCller",
  "leave.range": "02.03.2026 \u2013 06.03.2026",
  "leave.note": "Betriebsurlaub",
  "leave.days": "5",
  "vehicle.plate": "WI-GB 2042",
  "fleet.period": "01.03.2026\u201331.03.2026",
  "fleet.subject": "Hauptuntersuchung",
  "fleet.reason": "Karte unbekannt",
  "order.number": "WA-1042",
  "order.dueOn": "20.03.2026",
  "onboarding.task": "Laptop ausgeben",
  "onboarding.dueOn": "01.04.2026",
  "onboarding.subject": "Anna Beispiel",
  "onboarding.entryOn": "01.04.2026",
  "report.number": "BT-2026-0007",
  "report.date": "14.10.2026",
  "report.project": "Neubau Halle 3",
  "report.address": "Musterstra\xDFe 1, 12345 Musterstadt",
  "instance.label": "Bautagesbericht Nord",
  "invoice.number": "12",
  "invoice.energy": "10,0 kWh",
  "invoice.net": "4,00 \u20AC",
  "invoice.vat": "0,76 \u20AC",
  "invoice.gross": "4,76 \u20AC",
  "invoice.endedAt": "28.10.2026, 08:00",
  "invoice.ocmf": 'OCMF|{"FV":"1.0"}|{"SA":"ECDSA-secp256r1-SHA256","SD":"aa"}'
};
var MAIL_PLACEHOLDERS_COMMON = [
  "portal.name",
  "recipient.name",
  "notification.title",
  "notification.body",
  "action.url"
];
var MAIL_PLACEHOLDERS_TICKET = [
  "ticket.number",
  "ticket.subject",
  "ticket.status",
  "ticket.url",
  "actor.name",
  "comment.excerpt",
  "changes.summary"
];
var MAIL_PLACEHOLDERS_EXTRA = {
  [NotificationEventKey.INTRANET_NEWS_PUBLISHED]: ["content.title", "content.teaser"],
  [NotificationEventKey.INTRANET_ACK_REQUIRED]: ["content.title"],
  [NotificationEventKey.INTRANET_ACK_REMINDER]: ["content.title"],
  [NotificationEventKey.INTRANET_REVIEW_DUE]: ["content.title"],
  [NotificationEventKey.INTRANET_MELDUNG]: ["content.title"],
  [NotificationEventKey.INTRANET_CONTENT_LIKED]: ["actor.name", "content.title"],
  [NotificationEventKey.INTRANET_COMMENT_CREATED]: ["actor.name", "content.title"],
  [NotificationEventKey.INTRANET_COMMENT_REPLIED]: ["actor.name", "content.title"],
  [NotificationEventKey.INTRANET_MENTIONED]: ["actor.name", "content.title"],
  [NotificationEventKey.INTRANET_COMMENT_LIKED]: ["actor.name"],
  [NotificationEventKey.LEAVE_REQUEST_SUBMITTED]: ["leave.requester", "leave.range"],
  [NotificationEventKey.LEAVE_REQUEST_CANCELLED]: ["leave.requester", "leave.range"],
  [NotificationEventKey.LEAVE_REQUEST_REJECTED]: ["leave.range", "leave.note"],
  [NotificationEventKey.LEAVE_REMINDER_UPCOMING]: ["leave.range", "leave.days"],
  [NotificationEventKey.FLEET_ASSIGNMENT_STARTED]: ["vehicle.plate", "fleet.period"],
  [NotificationEventKey.FLEET_DAMAGE_REPORTED]: ["actor.name", "vehicle.plate"],
  [NotificationEventKey.FLEET_TELEMATICS_STALE]: ["vehicle.plate"],
  [NotificationEventKey.FLEET_FUEL_EXCEPTION]: ["fleet.subject", "fleet.reason"],
  [NotificationEventKey.FLEET_GUEST_INVOICE]: [
    "recipient.name",
    "invoice.number",
    "invoice.energy",
    "invoice.net",
    "invoice.vat",
    "invoice.gross",
    "invoice.endedAt",
    "invoice.ocmf"
  ],
  [NotificationEventKey.MAINTENANCE_WORK_ORDER_ASSIGNED]: ["order.number"],
  [NotificationEventKey.MAINTENANCE_WORK_ORDER_REASSIGNED]: ["order.number"],
  [NotificationEventKey.MAINTENANCE_PROTOCOL_COMPLETED]: ["order.number"],
  [NotificationEventKey.MAINTENANCE_FINALIZATION_FAILED]: ["order.number"],
  [NotificationEventKey.MAINTENANCE_WORK_ORDER_DUE_SOON]: ["order.number", "order.dueOn"],
  [NotificationEventKey.MAINTENANCE_WORK_ORDER_OVERDUE]: ["order.number", "order.dueOn"],
  [NotificationEventKey.ONBOARDING_TASK_ASSIGNED]: [
    "onboarding.task",
    "onboarding.dueOn",
    "onboarding.subject"
  ],
  [NotificationEventKey.ONBOARDING_TASK_DUE_SOON]: [
    "onboarding.task",
    "onboarding.dueOn",
    "onboarding.subject"
  ],
  [NotificationEventKey.ONBOARDING_TASK_OVERDUE]: [
    "onboarding.task",
    "onboarding.dueOn",
    "onboarding.subject"
  ],
  [NotificationEventKey.ONBOARDING_CASE_STARTED]: ["onboarding.subject", "onboarding.entryOn"],
  [NotificationEventKey.ONBOARDING_SUBJECT_ADMITTED]: ["onboarding.subject", "onboarding.entryOn"],
  [NotificationEventKey.ONBOARDING_CASE_COMPLETED]: ["onboarding.subject"],
  [NotificationEventKey.INTRANET_SITE_DIARY_SUBMITTED]: [
    "report.number",
    "report.date",
    "report.project",
    "report.address",
    "actor.name",
    "instance.label"
  ]
};
var PRODUCT_MAIL_EVENT_ORDER = [
  NotificationEventKey.INTRANET_NEWS_PUBLISHED,
  NotificationEventKey.INTRANET_ACK_REQUIRED,
  NotificationEventKey.INTRANET_ACK_REMINDER,
  NotificationEventKey.INTRANET_REVIEW_DUE,
  NotificationEventKey.INTRANET_MELDUNG,
  NotificationEventKey.INTRANET_CONTENT_LIKED,
  NotificationEventKey.INTRANET_COMMENT_CREATED,
  NotificationEventKey.INTRANET_COMMENT_REPLIED,
  NotificationEventKey.INTRANET_COMMENT_LIKED,
  NotificationEventKey.INTRANET_MENTIONED,
  NotificationEventKey.INTRANET_SITE_DIARY_SUBMITTED,
  NotificationEventKey.LEAVE_REQUEST_SUBMITTED,
  NotificationEventKey.LEAVE_REQUEST_APPROVED,
  NotificationEventKey.LEAVE_REQUEST_REJECTED,
  NotificationEventKey.LEAVE_REQUEST_CANCELLED,
  NotificationEventKey.LEAVE_REMINDER_UPCOMING,
  NotificationEventKey.FLEET_ASSIGNMENT_STARTED,
  NotificationEventKey.FLEET_DAMAGE_REPORTED,
  NotificationEventKey.FLEET_DAMAGE_CLOSED,
  NotificationEventKey.FLEET_REMINDER_UPCOMING,
  NotificationEventKey.FLEET_REMINDER_OVERDUE,
  NotificationEventKey.FLEET_TELEMATICS_STALE,
  NotificationEventKey.FLEET_FUEL_EXCEPTION,
  NotificationEventKey.FLEET_GUEST_INVOICE,
  NotificationEventKey.MAINTENANCE_WORK_ORDER_ASSIGNED,
  NotificationEventKey.MAINTENANCE_WORK_ORDER_REASSIGNED,
  NotificationEventKey.MAINTENANCE_WORK_ORDER_DUE_SOON,
  NotificationEventKey.MAINTENANCE_WORK_ORDER_OVERDUE,
  NotificationEventKey.MAINTENANCE_PROTOCOL_COMPLETED,
  NotificationEventKey.MAINTENANCE_FINALIZATION_FAILED,
  NotificationEventKey.ONBOARDING_TASK_ASSIGNED,
  NotificationEventKey.ONBOARDING_TASK_DUE_SOON,
  NotificationEventKey.ONBOARDING_TASK_OVERDUE,
  NotificationEventKey.ONBOARDING_CASE_STARTED,
  NotificationEventKey.ONBOARDING_SUBJECT_ADMITTED,
  NotificationEventKey.ONBOARDING_CASE_COMPLETED,
  NotificationEventKey.ASSIGNMENTS_RELEASED
];
var MailTemplatePlaceholderError = class extends Error {
  token;
  constructor(token) {
    super(`Der Platzhalter {{${token}}} ist f\xFCr diese Vorlage nicht erlaubt.`);
    this.name = "MailTemplatePlaceholderError";
    this.token = token;
  }
};
var STAFF_ROLE_CODES_FOR_MAIL = [
  RoleCode.IT_AGENT,
  RoleCode.IT_LEAD,
  RoleCode.IT_ADMIN
];
function isStaffForMailPolicy(roles) {
  return roles.some((role) => STAFF_ROLE_CODES_FOR_MAIL.includes(role));
}
var mailPolicySchema = z.object({
  emailEnabledForEndUsers: z.boolean(),
  emailEnabledForAgents: z.boolean(),
  /** True when SMTP_HOST is set on the server (outbound can leave the box). */
  smtpConfigured: z.boolean(),
  updatedAt: z.iso.datetime().nullable()
});
var DEFAULT_MAIL_POLICY = {
  emailEnabledForEndUsers: true,
  emailEnabledForAgents: true
};
var updateMailPolicyRequestSchema = z.object({
  emailEnabledForEndUsers: z.boolean(),
  emailEnabledForAgents: z.boolean()
});
var mailTemplatePlaceholderViewSchema = z.object({
  token: z.string(),
  label: z.string(),
  sample: z.string()
});
var mailTemplateSchema = z.object({
  eventKey: notificationEventKeySchema,
  label: z.string(),
  productCode: notificationProductCodeSchema,
  /** True when e-mail is on before the person saves a preference. */
  emailDefault: z.boolean(),
  subjectTemplate: z.string().min(1).max(300),
  headingTemplate: z.string().min(1).max(300),
  bodyParagraphs: z.array(z.string().max(2e3)).min(1).max(12),
  actionLabel: z.string().max(120).nullable(),
  /** Du override. Null or omitted uses the shipped Du wording. */
  subjectTemplateDu: z.string().max(300).nullable().optional(),
  headingTemplateDu: z.string().max(300).nullable().optional(),
  bodyParagraphsDu: z.array(z.string().max(2e3)).max(12).nullable().optional(),
  actionLabelDu: z.string().max(120).nullable().optional(),
  /** False when serving code defaults (no DB row). */
  isCustomized: z.boolean(),
  updatedAt: z.iso.datetime().nullable(),
  placeholders: z.array(mailTemplatePlaceholderViewSchema)
});
var mailTemplatesResponseSchema = z.object({
  items: z.array(mailTemplateSchema),
  placeholders: z.array(mailTemplatePlaceholderViewSchema)
});
var updateMailTemplateRequestSchema = z.object({
  subjectTemplate: z.string().min(1).max(300),
  headingTemplate: z.string().min(1).max(300),
  bodyParagraphs: z.array(z.string().min(1).max(2e3)).min(1).max(12),
  actionLabel: z.string().max(120).nullable().optional(),
  subjectTemplateDu: z.string().max(300).nullable().optional(),
  headingTemplateDu: z.string().max(300).nullable().optional(),
  bodyParagraphsDu: z.array(z.string().min(1).max(2e3)).max(12).nullable().optional(),
  actionLabelDu: z.string().max(120).nullable().optional()
});
var mailTemplateTestRequestSchema = z.object({
  eventKey: notificationEventKeySchema
});
var mailTemplateTestResponseSchema = z.object({
  ok: z.literal(true),
  to: z.string().email(),
  subject: z.string()
});
function mailTemplatePlaceholdersList() {
  return MAIL_TEMPLATE_PLACEHOLDERS.map((token) => ({
    token: `{{${token}}}`,
    label: MAIL_TEMPLATE_PLACEHOLDER_HELP[token],
    sample: MAIL_TEMPLATE_PLACEHOLDER_SAMPLE[token]
  }));
}
function mailPlaceholdersForEvent(eventKey) {
  if (eventKey === NotificationEventKey.INTRANET_SITE_DIARY_SUBMITTED) {
    return MAIL_PLACEHOLDERS_EXTRA[eventKey] ?? [];
  }
  const extra = MAIL_PLACEHOLDERS_EXTRA[eventKey] ?? [];
  const ticket = NOTIFICATION_EVENT_PRODUCT[eventKey] === NotificationProductCode.HELPDESK ? MAIL_PLACEHOLDERS_TICKET : [];
  return [...MAIL_PLACEHOLDERS_COMMON, ...ticket, ...extra];
}
function mailTemplatePlaceholderViews(eventKey) {
  const eventSample = mailTemplateEventSample(eventKey);
  return mailPlaceholdersForEvent(eventKey).map((key) => ({
    token: `{{${key}}}`,
    label: MAIL_TEMPLATE_PLACEHOLDER_HELP[key],
    sample: key === "notification.title" ? eventSample.title : key === "notification.body" ? eventSample.body : MAIL_TEMPLATE_PLACEHOLDER_SAMPLE[key]
  }));
}
function mailEventEmailDefault(eventKey) {
  return NOTIFICATION_EVENT_EMAIL_DEFAULT[eventKey] ?? true;
}
function assertMailTemplatePlaceholders(eventKey, texts) {
  const allowed = new Set(mailPlaceholdersForEvent(eventKey));
  for (const text of texts) {
    for (const match of text.matchAll(/\{\{\s*([a-z0-9_.]+)\s*\}\}/gi)) {
      const token = match[1]?.trim() ?? "";
      if (!allowed.has(token)) {
        throw new MailTemplatePlaceholderError(token);
      }
    }
  }
}
function editableMailEventKeys() {
  const helpdesk = NOTIFICATION_EVENT_KEYS.filter(
    (eventKey) => NOTIFICATION_EVENT_PRODUCT[eventKey] === NotificationProductCode.HELPDESK
  );
  return [...helpdesk, ...PRODUCT_MAIL_EVENT_ORDER];
}
function mailEventLabel(eventKey) {
  return NOTIFICATION_EVENT_LABELS[eventKey];
}
function isOrgEmailEnabledForRoles(roles, policy) {
  if (isStaffForMailPolicy(roles)) {
    return policy.emailEnabledForAgents;
  }
  return policy.emailEnabledForEndUsers;
}

// src/notification-path.ts
function normalizeNotificationPath(raw) {
  let path = raw.trim();
  if (!path) {
    return "/";
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
    }
  }
  const cut = path.split(/[?#]/)[0] ?? path;
  if (cut.length > 1 && cut.endsWith("/")) {
    return cut.slice(0, -1);
  }
  return cut || "/";
}
function ticketNumberFromPath(pathname) {
  const path = normalizeNotificationPath(pathname);
  const match = /^(?:\/helpdesk|\/portal)?\/tickets\/(\d+)(?:\/|$)/.exec(path);
  if (!match?.[1]) {
    return null;
  }
  const n = Number(match[1]);
  return Number.isInteger(n) && n > 0 ? n : null;
}
function sameLibraryAddress(path) {
  const legacy = "/intranet/bibliothek";
  if (path === legacy || path.startsWith(`${legacy}/`)) {
    return `/intranet/wissensdatenbank${path.slice(legacy.length)}`;
  }
  return path;
}
function notificationMatchesVisitedPath(item, visitedPath) {
  const current = sameLibraryAddress(normalizeNotificationPath(visitedPath));
  const currentTicket = ticketNumberFromPath(current);
  if (item.ticketNumber != null && currentTicket === item.ticketNumber) {
    return true;
  }
  if (!item.href) {
    return false;
  }
  const hrefPath = sameLibraryAddress(normalizeNotificationPath(item.href));
  const hrefTicket = ticketNumberFromPath(hrefPath);
  if (hrefTicket != null && currentTicket === hrefTicket) {
    return true;
  }
  if (current === hrefPath) {
    return true;
  }
  if (hrefPath !== "/" && current.startsWith(`${hrefPath}/`)) {
    return true;
  }
  return false;
}
var PushTransport = {
  WEB_PUSH: "WEB_PUSH"
};
var pushTransportSchema = z.enum(PushTransport);
var PushPlatformHint = {
  DESKTOP_WINDOWS: "desktop_windows",
  DESKTOP_MAC: "desktop_mac",
  DESKTOP_LINUX: "desktop_linux",
  MOBILE_IOS: "mobile_ios",
  MOBILE_ANDROID: "mobile_android",
  OTHER: "other"
};
var pushPlatformHintSchema = z.enum(PushPlatformHint);
var pushSubscribeRequestSchema = z.strictObject({
  endpoint: z.url(),
  keys: z.strictObject({
    p256dh: z.string().min(1),
    auth: z.string().min(1)
  }),
  expirationTime: z.number().nullable().optional(),
  userAgent: z.string().max(512).optional(),
  platformHint: pushPlatformHintSchema.optional()
});
var pushUnsubscribeCurrentRequestSchema = z.strictObject({
  endpoint: z.url()
});
var pushSubscriptionSchema = z.object({
  id: z.string().uuid(),
  transport: pushTransportSchema,
  endpoint: z.string(),
  userAgent: z.string().nullable(),
  platformHint: pushPlatformHintSchema.nullable(),
  expiresAt: z.iso.datetime().nullable(),
  lastSuccessAt: z.iso.datetime().nullable(),
  lastErrorAt: z.iso.datetime().nullable(),
  lastErrorCode: z.string().nullable(),
  revokedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime()
});
var pushDeviceListResponseSchema = z.object({
  items: z.array(pushSubscriptionSchema)
});
var pushVapidPublicKeyResponseSchema = z.object({
  publicKey: z.string(),
  enabled: z.boolean()
});
var pushActionResponseSchema = z.object({
  ok: z.literal(true)
});
var RealtimeEvent = {
  /** Server -> Client: connection acknowledged. */
  CONNECTED: "connected",
  /** Server -> Client: a ticket was created or changed. */
  TICKET_UPDATED: "ticket:updated",
  /** Server -> Client: a new notification for the current user. */
  NOTIFICATION_NEW: "notification:new",
  /** Server -> Client (portal/agent UI): a remote-support session changed state. */
  REMOTE_SESSION_UPDATED: "remote_session:updated",
  /** Server -> Connector (namespace /connector): consent needed for a new session. */
  REMOTE_SESSION_REQUESTED: "remote_session:requested",
  /** Server -> Connector: session ended/expired — rotate the RustDesk password now. */
  REMOTE_SESSION_CLOSED: "remote_session:closed",
  /** Server -> Client: admin toggled optional system modules. */
  MODULES_UPDATED: "modules:updated",
  /** Server -> Client: a platform list changed; refresh that React Query key. */
  DATA_INVALIDATED: "data:invalidated"
};
var connectedPayloadSchema = z.object({
  socketId: z.string(),
  serverTime: z.iso.datetime()
});
var notificationNewPayloadSchema = z.object({
  notification: notificationSchema
});
var DataInvalidationScope = {
  NOTIFICATIONS: "notifications",
  USERS: "users",
  ORGANISATION: "organisation",
  PRODUCTS: "products",
  DIRECTORY: "directory",
  INTRANET_CHAT: "intranet_chat",
  AI: "ai",
  MAIL: "mail",
  MASTER_DATA: "master_data",
  ARTICLES: "articles"
};
var dataInvalidationScopeSchema = z.enum(DataInvalidationScope);
var dataInvalidatedPayloadSchema = z.object({
  scope: dataInvalidationScopeSchema,
  at: z.iso.datetime()
});
var modulesUpdatedPayloadSchema = z.object({
  enabledModules: enabledModulesSchema,
  changed: z.array(appModuleCodeSchema)
});
var RemoteSessionStatus = {
  REQUESTED: "REQUESTED",
  ACTIVE: "ACTIVE",
  DENIED: "DENIED",
  EXPIRED: "EXPIRED",
  ENDED: "ENDED"
};
var remoteSessionStatusSchema = z.enum(RemoteSessionStatus);
var REMOTE_SESSION_STATUS_LABELS = {
  REQUESTED: "Wartet auf Zustimmung",
  ACTIVE: "Aktiv",
  DENIED: "Abgelehnt",
  EXPIRED: "Abgelaufen",
  ENDED: "Beendet"
};
var REMOTE_SESSION_CONSENT_TIMEOUT_SECONDS = 180;
var REMOTE_SESSION_DEFAULT_VALID_MINUTES = 20;
var REMOTE_SESSION_MAX_VALID_MINUTES = 30;
var REMOTE_SESSION_REPORT_MAX_SKEW_SECONDS = 300;
var CONNECTOR_ENROLLMENT_CODE_TTL_MINUTES = 10;
var REMOTE_CONNECT_GRANT_TTL_SECONDS = 90;
var GBTICKET_REMOTE_PROTOCOL = "gbticket-remote://connect/";
var uuid2 = z.string().uuid();
var rustdeskIdSchema = z.string().trim().regex(/^[0-9]{6,12}$/, "Ung\xFCltige RustDesk-ID");
var rustdeskPasswordSchema = z.string().regex(/^[A-Za-z0-9_-]{12,64}$/, "Ung\xFCltiges Einmalpasswort");
var remoteSessionSchema = z.object({
  id: uuid2,
  ticketId: uuid2,
  ticketNumber: z.number().int().positive(),
  status: remoteSessionStatusSchema,
  requestedBy: z.object({ id: uuid2, displayName: z.string() }),
  device: z.object({ id: uuid2, hostname: z.string() }).nullable(),
  expiresAt: z.iso.datetime(),
  requestedAt: z.iso.datetime(),
  activatedAt: z.iso.datetime().nullable(),
  endedAt: z.iso.datetime().nullable(),
  retrievalCount: z.number().int().nonnegative(),
  /** True when the current user may request a connect-launch nonce. */
  canConnect: z.boolean(),
  canEnd: z.boolean()
});
var remoteSessionListResponseSchema = z.object({
  items: z.array(remoteSessionSchema)
});
var currentRemoteSessionResponseSchema = z.object({
  session: remoteSessionSchema.nullable(),
  /** The requester has at least one active connector; without it a request is pointless. */
  connectorAvailable: z.boolean()
});
var remoteConnectLaunchResponseSchema = z.object({
  protocolUrl: z.string().regex(/^gbticket-remote:\/\/connect\/[A-Za-z0-9_-]+$/, "Ung\xFCltige Connector-Start-URL"),
  expiresAt: z.iso.datetime()
});
var connectorEnrollmentCodeResponseSchema = z.object({
  code: z.string(),
  expiresAt: z.iso.datetime()
});
var connectorDeviceSchema = z.object({
  id: uuid2,
  deviceId: z.string(),
  hostname: z.string(),
  connectorVersion: z.string().nullable(),
  user: z.object({ id: uuid2, displayName: z.string(), email: z.string() }),
  isActive: z.boolean(),
  registeredAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime().nullable(),
  revokedAt: z.iso.datetime().nullable()
});
var connectorDeviceListResponseSchema = z.object({
  items: z.array(connectorDeviceSchema)
});
var CONNECTOR_TOKEN_HEADER = "authorization";
var CONNECTOR_TOKEN_PREFIX = "Connector ";
var connectorDeviceIdSchema = z.string().trim().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "Ung\xFCltige Ger\xE4te-ID");
var connectorRegisterRequestSchema = z.object({
  enrollmentCode: z.string().trim().regex(/^[A-Z2-9]{4}-[A-Z2-9]{4}$/, "Ung\xFCltiger Kopplungscode"),
  deviceId: connectorDeviceIdSchema,
  hostname: z.string().trim().min(1).max(120),
  connectorVersion: z.string().trim().max(40).optional(),
  /** SubjectPublicKeyInfo PEM of the device's ECDSA P-256 signing key. */
  publicKeyPem: z.string().trim().min(1).max(4e3).regex(
    /^-----BEGIN PUBLIC KEY-----[\s\S]+-----END PUBLIC KEY-----$/,
    "Ung\xFCltiger \xF6ffentlicher Schl\xFCssel"
  )
});
var connectorRegisterResponseSchema = z.object({
  deviceId: z.string(),
  /** Shown exactly once. The backend stores only its SHA-256 hash. */
  serviceToken: z.string(),
  user: z.object({ id: uuid2, displayName: z.string(), email: z.string() })
});
var connectorHeartbeatRequestSchema = z.object({
  hostname: z.string().trim().min(1).max(120).optional(),
  connectorVersion: z.string().trim().max(40).optional()
});
var connectorPendingSessionSchema = z.object({
  sessionId: uuid2,
  ticketNumber: z.number().int().positive(),
  ticketSubject: z.string(),
  /** Agent currently assigned (informational for the dialog); may be null. */
  agentName: z.string().nullable(),
  requestedAt: z.iso.datetime(),
  /** Consent deadline. */
  expiresAt: z.iso.datetime(),
  /** Target device; the connector must ignore events for other device ids. */
  deviceId: z.string()
});
var connectorPendingResponseSchema = z.object({
  deviceId: z.string(),
  user: z.object({ id: uuid2, displayName: z.string() }),
  items: z.array(connectorPendingSessionSchema),
  /**
   * ACTIVE sessions currently bound to this device. The connector uses this on the
   * HTTP fallback poll: if it still holds a one-time password for a session that is
   * no longer listed, it rotates RustDesk immediately (WebSocket may be blocked).
   */
  activeSessionIds: z.array(uuid2),
  serverTime: z.iso.datetime()
});
var connectorSessionClosedPayloadSchema = z.object({
  sessionId: uuid2,
  status: remoteSessionStatusSchema,
  deviceId: z.string()
});
var connectorReportCredentialsRequestSchema = z.object({
  deviceId: connectorDeviceIdSchema,
  rustdeskId: rustdeskIdSchema,
  password: rustdeskPasswordSchema,
  validForMinutes: z.number().int().min(5).max(REMOTE_SESSION_MAX_VALID_MINUTES),
  /** Unix epoch seconds on the device. */
  timestamp: z.number().int().positive(),
  nonce: z.string().regex(/^[A-Za-z0-9_-]{16,64}$/),
  signature: z.string().regex(/^[A-Za-z0-9+/=]{64,256}$/)
});
var connectorDenyRequestSchema = z.object({
  deviceId: connectorDeviceIdSchema,
  reason: z.enum(["USER_DENIED", "TIMEOUT", "RUSTDESK_UNAVAILABLE"]).optional()
});
var connectorLaunchRequestSchema = z.object({
  nonce: z.string().regex(/^[A-Za-z0-9_-]{22,64}$/),
  deviceId: connectorDeviceIdSchema,
  /** Unix epoch seconds on the device. */
  timestamp: z.number().int().positive(),
  requestNonce: z.string().regex(/^[A-Za-z0-9_-]{16,64}$/),
  signature: z.string().regex(/^[A-Za-z0-9+/=]{64,256}$/)
});
var connectorLaunchResponseSchema = z.object({
  rustdeskId: rustdeskIdSchema,
  password: rustdeskPasswordSchema,
  expiresAt: z.iso.datetime()
});
function connectorReportSigningString(input) {
  return [
    "gb-ticket-connector/v1",
    input.sessionId,
    input.deviceId.toLowerCase(),
    input.rustdeskId,
    input.passwordSha256Hex.toLowerCase(),
    String(input.validForMinutes),
    String(input.timestamp),
    input.nonce
  ].join("\n");
}
function connectorLaunchSigningString(input) {
  return [
    "gb-ticket-connector-launch/v1",
    input.nonce,
    input.deviceId.toLowerCase(),
    String(input.timestamp),
    input.requestNonce
  ].join("\n");
}
function remoteConnectProtocolUrl(nonce) {
  return `${GBTICKET_REMOTE_PROTOCOL}${nonce}`;
}
function rustdeskConnectUri(rustdeskId, password) {
  return `rustdesk://connection/new/${encodeURIComponent(rustdeskId)}?password=${encodeURIComponent(password)}`;
}
var remoteSessionUpdatedPayloadSchema = z.object({
  session: remoteSessionSchema
});

// src/ticket-lifecycle.ts
var TICKET_AUTO_CLOSE_DAYS = 3;
var TICKET_AUTO_CLOSE_REMINDER_BEFORE_DAYS = 1;
var TICKET_REOPEN_WINDOW_DAYS = 7;
var DAY_MS = 24 * 60 * 60 * 1e3;
function addDays(from, days) {
  return new Date(from.getTime() + days * DAY_MS);
}
function ticketAutoCloseAt(resolvedAt, autoCloseDays = TICKET_AUTO_CLOSE_DAYS) {
  return addDays(resolvedAt, autoCloseDays);
}
function ticketAutoCloseReminderAt(resolvedAt, autoCloseDays = TICKET_AUTO_CLOSE_DAYS, reminderBeforeDays = TICKET_AUTO_CLOSE_REMINDER_BEFORE_DAYS) {
  const closeAt = ticketAutoCloseAt(resolvedAt, autoCloseDays);
  const reminderAt = addDays(resolvedAt, Math.max(0, autoCloseDays - reminderBeforeDays));
  if (reminderAt.getTime() >= closeAt.getTime()) {
    return new Date(resolvedAt.getTime() + 60 * 60 * 1e3);
  }
  return reminderAt;
}
function ticketReopenUntil(closedAt, reopenWindowDays = TICKET_REOPEN_WINDOW_DAYS) {
  return addDays(closedAt, reopenWindowDays);
}
function isTicketReopenWindowOpen(closedAt, now = /* @__PURE__ */ new Date(), reopenWindowDays = TICKET_REOPEN_WINDOW_DAYS) {
  if (!closedAt) {
    return false;
  }
  const closed = closedAt instanceof Date ? closedAt : new Date(closedAt);
  return now.getTime() <= ticketReopenUntil(closed, reopenWindowDays).getTime();
}
function canReopenTicketStatus(status, closedAt, now = /* @__PURE__ */ new Date(), reopenWindowDays = TICKET_REOPEN_WINDOW_DAYS) {
  if (status === TicketStatus.RESOLVED) {
    return true;
  }
  if (status === TicketStatus.CLOSED) {
    return isTicketReopenWindowOpen(closedAt, now, reopenWindowDays);
  }
  return false;
}
var TICKET_AI_ASSIST_QUESTION_MAX = 2e3;
var ticketAiAssistRequestSchema = z.object({
  question: z.string().trim().max(TICKET_AI_ASSIST_QUESTION_MAX).optional()
});
var ticketAiAssistSimilarTicketSchema = z.object({
  number: z.number().int().positive(),
  subject: z.string(),
  status: ticketStatusSchema
});
var ticketAiAssistResponseSchema = z.object({
  id: z.string().uuid(),
  chatAvailable: z.boolean(),
  model: z.string(),
  summary: z.string(),
  steps: z.array(z.string()),
  publicReply: z.string().nullable(),
  internalNote: z.string().nullable(),
  sources: z.array(aiChatSourceSchema),
  similarTickets: z.array(ticketAiAssistSimilarTicketSchema),
  followUpQuestions: z.array(z.string()),
  confidence: z.number().min(0).max(1).nullable(),
  /** Agent question for this run. Null when the ticket was read without one. */
  question: z.string().nullable(),
  createdAt: z.iso.datetime()
});
var optionalText = z.preprocess((value) => value ?? void 0, z.string().optional());
function toStringList(value) {
  if (value == null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      if (typeof item === "string") {
        return splitListText(item);
      }
      if (item && typeof item === "object") {
        const record = item;
        const text = record.text ?? record.step ?? record.title;
        return typeof text === "string" && text.trim() ? [text.trim()] : [];
      }
      return [];
    });
  }
  if (typeof value === "string") {
    return splitListText(value);
  }
  return value;
}
function splitListText(value) {
  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }
  const inline = trimmed.split(/\s+(?=\d+[.)]\s+)/u);
  const pieces = inline.length > 1 ? inline : trimmed.split(/\n+/);
  const lines = pieces.map((line) => line.replace(/^\s*(?:\d+[).:-]|[-*])\s*/, "").trim()).filter(Boolean);
  return lines.length > 1 ? lines : [trimmed];
}
var stringList = z.preprocess(toStringList, z.array(z.string()));
var optionalConfidence = z.preprocess((value) => {
  if (value == null || value === "") {
    return void 0;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return void 0;
  }
  if (numeric > 1 && numeric <= 100) {
    return numeric / 100;
  }
  return numeric;
}, z.number().min(0).max(1).optional());
var assistModelJsonSchema = z.object({
  summary: optionalText,
  steps: stringList.optional(),
  publicReply: optionalText,
  internalNote: optionalText,
  sourceSlugs: stringList.optional(),
  followUpQuestions: stringList.optional(),
  confidence: optionalConfidence
});
function jsonCandidate(raw) {
  const trimmed = raw.trim();
  const fenced = /```(?:json)?\s*([\s\S]*?)```/i.exec(trimmed);
  const candidate = fenced?.[1]?.trim() ?? trimmed;
  const start = candidate.indexOf("{");
  if (start < 0) {
    return null;
  }
  const end = candidate.lastIndexOf("}");
  if (end > start) {
    return candidate.slice(start, end + 1);
  }
  return candidate.slice(start);
}
function tryParseJson(candidate) {
  try {
    return JSON.parse(candidate);
  } catch {
    try {
      return JSON.parse(candidate.replace(/,(\s*[}\]])/g, "$1"));
    } catch {
      return null;
    }
  }
}
function salvageTruncatedJson(candidate) {
  const direct = tryParseJson(candidate);
  if (direct) {
    return direct;
  }
  let text = candidate;
  let inString = false;
  let escape = false;
  let stringStart = -1;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index] ?? "";
    if (inString) {
      if (escape) {
        escape = false;
        continue;
      }
      if (char === "\\") {
        escape = true;
        continue;
      }
      if (char === '"') {
        inString = false;
        stringStart = -1;
      }
      continue;
    }
    if (char === '"') {
      inString = true;
      stringStart = index;
    }
  }
  if (inString && stringStart >= 0) {
    text = text.slice(0, stringStart);
  }
  text = text.replace(/[,:]\s*$/u, "").trimEnd();
  const stack = [];
  inString = false;
  escape = false;
  for (const char of text) {
    if (inString) {
      if (escape) {
        escape = false;
        continue;
      }
      if (char === "\\") {
        escape = true;
        continue;
      }
      if (char === '"') {
        inString = false;
      }
      continue;
    }
    if (char === '"') {
      inString = true;
      continue;
    }
    if (char === "{" || char === "[") {
      stack.push(char);
    } else if ((char === "}" || char === "]") && stack.length > 0) {
      stack.pop();
    }
  }
  if (inString) {
    return null;
  }
  while (stack.length > 0) {
    const open = stack.pop();
    text += open === "{" ? "}" : "]";
  }
  return tryParseJson(text.replace(/,\s*([}\]])/g, "$1"));
}
function extractJsonObject3(raw) {
  const candidate = jsonCandidate(raw);
  if (!candidate) {
    return null;
  }
  return salvageTruncatedJson(candidate);
}
var ASSIST_STEP_CAP = 4;
function capSteps(steps) {
  const seen = /* @__PURE__ */ new Set();
  const kept = [];
  for (const step of steps) {
    const text = step.replace(/^\s*\d+[.)]\s*/u, "").trim();
    const key = text.replace(/\s+/g, " ").toLowerCase();
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    kept.push(text);
    if (kept.length === ASSIST_STEP_CAP) {
      break;
    }
  }
  return kept;
}
function pickAliased(record) {
  const first = (...keys) => {
    for (const key of keys) {
      if (record[key] !== void 0) {
        return record[key];
      }
    }
    return void 0;
  };
  return {
    summary: first("summary", "zusammenfassung", "kurzfassung"),
    steps: first("steps", "schritte", "vorschlaege", "vorschl\xE4ge"),
    publicReply: first("publicReply", "public_reply", "antwort", "kundenantwort"),
    internalNote: first("internalNote", "internal_note", "interneNotiz", "interne_notiz"),
    sourceSlugs: first("sourceSlugs", "source_slugs", "quellen"),
    followUpQuestions: first(
      "followUpQuestions",
      "follow_up_questions",
      "rueckfragen",
      "r\xFCckfragen"
    ),
    confidence: first("confidence", "konfidenz")
  };
}
function parseTicketAiAssistModelJson(raw) {
  const extracted = extractJsonObject3(raw);
  if (!extracted || typeof extracted !== "object" || Array.isArray(extracted)) {
    return null;
  }
  const parsed = assistModelJsonSchema.safeParse(pickAliased(extracted));
  if (!parsed.success) {
    return null;
  }
  return {
    ...parsed.data,
    steps: parsed.data.steps ? capSteps(parsed.data.steps) : parsed.data.steps
  };
}
function redactAssistPublicReplyLeak(publicReply, internalBodies) {
  const reply = publicReply?.replace(/\s+/g, " ").trim() ?? "";
  if (!reply) {
    return null;
  }
  const hay = reply.toLowerCase();
  for (const body of internalBodies) {
    const needle = body.replace(/\s+/g, " ").trim().toLowerCase();
    if (needle.length < 24) {
      continue;
    }
    const fragment = needle.slice(0, Math.min(80, needle.length));
    if (hay.includes(fragment)) {
      return null;
    }
  }
  return publicReply?.trim() ?? null;
}
var TenantStatus = {
  ACTIVE: "active",
  SUSPENDED: "suspended"
};
var tenantStatusSchema = z.enum(TenantStatus);
var tenantSlugSchema = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).max(40);
function suggestTenantSlug(name) {
  const mapped = name.trim().toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40).replace(/-+$/g, "");
  return tenantSlugSchema.safeParse(mapped).success ? mapped : "firma";
}
var accentHexSchema = z.union([z.literal(""), z.string().regex(/^#[0-9A-Fa-f]{6}$/)]);
var publicBrandSchema = z.object({
  name: z.string(),
  logoUrl: z.string().nullable(),
  accentHex: accentHexSchema.nullable(),
  addressForm: addressFormSchema.optional(),
  personNameParts: personNamePartsSchema.optional(),
  personNameOrder: personNameOrderSchema.optional(),
  displayFont: displayFontSchema.default("system"),
  displayFontSize: displayFontSizeSchema.default("16"),
  colorMode: colorModeSchema.default("system"),
  designVariant: designVariantSchema.default("brand")
});
var updateTenantAppearanceRequestSchema = z.strictObject({
  colorMode: colorModeSchema,
  designVariant: designVariantSchema
});
var TENANT_SUSPENDED_MESSAGE = "Dieser Mandant ist gesperrt. Eine Anmeldung ist nicht m\xF6glich.";
var setupStatusResponseSchema = z.object({
  needsSetup: z.boolean(),
  setupCompleted: z.boolean(),
  hasTenant: z.boolean()
});
var setupCompleteResponseSchema = z.object({
  setupCompletedAt: z.string().datetime()
});
var setupCompanyRequestSchema = z.object({
  name: z.string().trim().min(2).max(120),
  slug: z.string().trim().toLowerCase().min(2).max(48).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug: klein, a-z, Ziffern, Bindestrich"),
  email: z.string().trim().email().max(320).transform((value) => value.toLowerCase()),
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  password: passwordSchema
});
var setupCompanyResponseSchema = z.object({
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
  slug: z.string(),
  name: z.string()
});
var tenantOperatorSchema = z.strictObject({
  id: z.string().uuid(),
  name: z.string(),
  slug: tenantSlugSchema,
  status: tenantStatusSchema,
  isInstallationHome: z.boolean(),
  logoStorageKey: z.string().nullable(),
  accentHex: accentHexSchema.nullable(),
  entraTenantId: z.string().nullable(),
  entraClientId: z.string().nullable(),
  entraConfigured: z.boolean(),
  setupCompletedAt: z.string().datetime().nullable(),
  addressForm: addressFormSchema,
  personNameParts: personNamePartsSchema,
  personNameOrder: personNameOrderSchema
});
var tenantOperatorListSchema = z.object({
  items: z.array(tenantOperatorSchema)
});
var optionalEntraId = z.string().trim().max(80).nullable().optional();
var createTenantRequestSchema = z.object({
  name: z.string().trim().min(2).max(120),
  slug: tenantSlugSchema,
  accentHex: accentHexSchema.optional(),
  logoStorageKey: z.string().trim().max(512).nullable().optional(),
  entraTenantId: optionalEntraId,
  entraClientId: optionalEntraId,
  /** Write-only. Encrypted at rest; never returned. */
  entraClientSecret: z.string().min(1).max(500).optional(),
  addressForm: addressFormSchema.optional(),
  personNameParts: personNamePartsSchema.optional(),
  personNameOrder: personNameOrderSchema.optional()
});
var updateTenantRequestSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  slug: tenantSlugSchema.optional(),
  status: tenantStatusSchema.optional(),
  accentHex: accentHexSchema.nullable().optional(),
  logoStorageKey: z.string().trim().max(512).nullable().optional(),
  entraTenantId: optionalEntraId,
  entraClientId: optionalEntraId,
  /** Write-only. Encrypted at rest; never returned. */
  entraClientSecret: z.string().min(1).max(500).optional(),
  addressForm: addressFormSchema.optional(),
  personNameParts: personNamePartsSchema.optional(),
  personNameOrder: personNameOrderSchema.optional()
}).refine((body) => Object.keys(body).length > 0, { message: "Mindestens ein Feld setzen." });

// src/user-profile-photo.ts
var PROFILE_PHOTO_MAX_BYTES = 2 * 1024 * 1024;
var PROFILE_PHOTO_MAX_EDGE = 512;
var PROFILE_PHOTO_ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp"];
var SERVABLE_SCAN = /* @__PURE__ */ new Set(["CLEAN", "SKIPPED"]);
function profilePhotoVersion(storageKey) {
  if (!storageKey) {
    return null;
  }
  const base = storageKey.split("/").pop() ?? "";
  const dot = base.lastIndexOf(".");
  const name = dot > 0 ? base.slice(0, dot) : base;
  return name.length > 0 ? name : null;
}
function profilePhotoUrlForUser(userId, storageKey, scanStatus) {
  if (!storageKey || !scanStatus || !SERVABLE_SCAN.has(scanStatus)) {
    return null;
  }
  const version = profilePhotoVersion(storageKey);
  if (!version) {
    return null;
  }
  return `/api/users/${userId}/profile-photo?v=${encodeURIComponent(version)}`;
}

// src/user-salutation.ts
function userSalutationName(input, voice) {
  return formatPersonName(input, voice);
}
function halloSalutation(input, voice) {
  const name = formatPersonName(input, voice);
  return name ? `Hallo, ${name}` : "Hallo";
}
function halloCommaSalutation(input, voice) {
  const name = formatPersonName(input, voice);
  return name ? `Hallo ${name},` : "Hallo,";
}
var OVERVIEW_MAX_PATHS = 80;
var overviewPathSchema = z.string().min(1).max(500).refine((value) => value.startsWith("/") || /^https?:\/\//i.test(value), "Ung\xFCltiger Pfad");
var updateOverviewLayoutSchema = z.object({
  paths: z.array(overviewPathSchema).max(OVERVIEW_MAX_PATHS).refine((paths) => new Set(paths).size === paths.length, "Pfad doppelt")
});
var overviewLayoutSchema = z.object({
  customized: z.boolean(),
  paths: z.array(z.string())
});
function canonicalOverviewPath(path) {
  const trimmed = path.trim();
  if (trimmed.startsWith("/") && !trimmed.startsWith("//") && trimmed.length > 1) {
    return trimmed.replace(/\/+$/, "");
  }
  return trimmed;
}
function overviewPathAllowed(path) {
  const trimmed = canonicalOverviewPath(path);
  if (trimmed.length === 0 || trimmed.startsWith("//") || trimmed.includes("\\")) {
    return false;
  }
  if (trimmed.startsWith("/")) {
    return true;
  }
  return /^https?:\/\//i.test(trimmed);
}

export { AI_CHAT_CONTEXT_DEFAULT_CHARS, AI_CHAT_MAX_CONTENT, AI_CHAT_MAX_MESSAGES, AI_CHAT_RETRIEVE_LIMIT, AI_EMBEDDING_DIMENSIONS, AI_HARDWARE_PROFILES, AI_KEEP_ALIVE_MAX, AI_KEEP_ALIVE_MIN, AI_MAX_PASSAGES_MAX, AI_MAX_PASSAGES_MIN, AI_NUM_CTX_MAX, AI_NUM_CTX_MIN, AI_NUM_PREDICT_MAX, AI_NUM_PREDICT_MIN, AI_PASSAGE_CHARS_MAX, AI_PASSAGE_CHARS_MIN, APPROVAL_DECISION_LABELS, APPROVAL_MODE_LABELS, APPROVAL_STATE_LABELS, APP_DISPLAY_NAME, APP_MODULE_CODES, APP_MODULE_DEFAULTS, APP_MODULE_DEPENDENTS, APP_MODULE_DESCRIPTIONS, APP_MODULE_GROUP, APP_MODULE_GROUP_LABELS, APP_MODULE_HIDES, APP_MODULE_LABELS, APP_VERSION, ARTICLE_FILE_MAX_BYTES, ARTICLE_NUMBER_PATTERN, ASSET_STATUS_LABELS, ATTACHMENT_SCAN_STATUS_LABELS, AUDIT_ACTIONS, AUDIT_ACTION_LABELS, AUDIT_ENTITY_TYPE_LABELS, AUTOMATION_ACTION_LABELS, AUTOMATION_ACTION_MODULE, AUTOMATION_ACTION_TYPES, AUTOMATION_CONDITION_FIELDS, AUTOMATION_CONDITION_FIELD_LABELS, AUTOMATION_CONDITION_MODULE, AUTOMATION_FIELD_OPERATORS, AUTOMATION_MAX_ACTIONS, AUTOMATION_MAX_CONDITIONS, AUTOMATION_NOTIFY_RECIPIENT_LABELS, AUTOMATION_OPERATOR_LABELS, AUTOMATION_TEMPLATE_PLACEHOLDERS, AUTOMATION_TRIGGER_DESCRIPTIONS, AUTOMATION_TRIGGER_LABELS, AUTOMATION_TRIGGER_ONLY_FIELDS, AppModuleCode, AppModuleGroup, ApprovalDecisionValue, ApprovalMode, ApprovalState, AssetStatus, AttachmentScanStatus, AuditAction, AuditEntityType, AutomationNotifyRecipient, AutomationTrigger, BERLIN_TIME_ZONE, CANONICAL_PERSON_VOICE, CATALOG_KEYWORD_AMBIGUOUS_MARGIN, CATALOG_KEYWORD_MAX_LENGTH, CATALOG_KEYWORD_MAX_PER_ENTRY, CATALOG_KEYWORD_MIN_LENGTH, CATALOG_KEYWORD_STOPWORDS, CHANGE_AFFECTED_SERVICES_MAX, CHANGE_AFFECTED_SERVICE_MAX_LENGTH, CHANGE_CALENDAR_TIMEZONE, CHANGE_RISK_LABELS, COLOR_MODES, CONNECTOR_DISPLAY_NAME, CONNECTOR_ENROLLMENT_CODE_TTL_MINUTES, CONNECTOR_TOKEN_HEADER, CONNECTOR_TOKEN_PREFIX, CSAT_MAX_RATING, CSAT_MIN_RATING, CSAT_RATING_LABELS, CSAT_REMINDER_DAYS, ChangeCollisionReason, ChangeCollisionSeverity, ChangeRisk, DASHBOARD_ASSET_EXPIRING_LIMIT, DASHBOARD_ASSET_EXPIRING_SOON_DAYS, DASHBOARD_ASSET_EXPIRING_WARN_DAYS, DASHBOARD_SERIES_DAYS, DASHBOARD_SERVER_ASSETS_LIMIT, DASHBOARD_SERVER_ASSET_SLUG, DASHBOARD_SOFTWARE_LICENSE_SLUG, DASHBOARD_TIMEZONE, DASHBOARD_TOP_PROBLEMS_LIMIT, DASHBOARD_TOP_PROBLEMS_SORT, DEFAULT_MAIL_POLICY, DEFAULT_TICKET_CATALOG_SETTINGS, DEFAULT_TYPOGRAPHY, DEFAULT_VOICE, DESIGN_VARIANTS, DIRECTORY_ROLE_CODES, DISPLAY_FONTS, DISPLAY_FONT_LABELS, DISPLAY_FONT_SIZES, DashboardAssetExpiringKind, DataInvalidationScope, DirectorySource, EMPTY_PROBLEM_RCA, EMPTY_TICKET_AI_STATE, ENTITLEMENT_LICENSE_CLASSES, ESCALATION_TRIGGER_LABELS, EscalationTrigger, FLEET_AREA_CODES, FLEET_AREA_DEFAULTS, FLEET_AREA_DEPENDENTS, FLEET_AREA_DESCRIPTIONS, FLEET_AREA_GROUP, FLEET_AREA_GROUP_LABELS, FLEET_AREA_HIDES, FLEET_AREA_LABELS, FLEET_AREA_SWITCHABLE, FLEET_CHARGE_CONNECTOR_LABELS, FLEET_DAMAGE_PHOTO_ALLOWED_MIME, FLEET_DAMAGE_PHOTO_MAX, FLEET_DAMAGE_PHOTO_MAX_EDGE, FLEET_DOCUMENT_ALLOWED_MIME, FLEET_EMPLOYEE_PERMISSIONS, FLEET_FILE_MAX_BYTES, FLEET_FIRMWARE_STATUS_LABELS, FLEET_FUEL_EXCEPTION_LABELS, FLEET_LICENSE_CLASSES, FLEET_TELEMATICS_INGEST_MAX_EVENTS, FleetAreaCode, FleetAreaGroup, FleetAssignmentKind, FleetChargeConnectorState, FleetChargeInterface, FleetDamageEventType, FleetDamageKind, FleetDamageSeverity, FleetDamageStatus, FleetDocumentKind, FleetDueCategory, FleetFuelType, FleetInspectionKind, FleetInspectionResult, FleetLicenseCheckResult, FleetOdometerSource, FleetOperationalStatus, FleetOwnership, FleetSolarBatteryFlow, FleetSolarForecastState, FleetSolarState, FleetTaskGroup, FleetTaskKind, FleetUsageMode, FleetVehicleCategory, FleetWorkshopKind, GBTICKET_REMOTE_PROTOCOL, GERMAN_STATE_CODES, GermanStateCode, HELPDESK_DISPLAY_NAME, HELPDESK_EMPLOYEE_PERMISSIONS, HU_DEFAULTS, IMPACT_LEVEL_LABELS, IMPACT_URGENCY_PRIORITY, INCIDENT_SEVERITY_LABELS, INTRANET_ABSENCE_NOTE_END_BEFORE_START, INTRANET_ABSENCE_NOTE_MAX, INTRANET_ABSENCE_NOTE_TIME_REQUIRED, INTRANET_ABSENCE_NOTE_TOO_LONG, INTRANET_AI_AGENT_DESCRIPTION_MAX, INTRANET_AI_AGENT_FILES_MAX, INTRANET_AI_AGENT_NAME_MAX, INTRANET_AI_AGENT_TEMPLATES, INTRANET_AI_ASK_HISTORY_MAX, INTRANET_AI_ASK_PAGE_SIZE, INTRANET_AI_HTML_MAX, INTRANET_AI_INSTRUCTION_MAX, INTRANET_AI_QUESTION_MAX, INTRANET_APPEARANCE_TITLE_MAX, INTRANET_CATEGORY_ALL_USERS_MESSAGE, INTRANET_CATEGORY_PUBLICATION, INTRANET_CHANNEL_ICONS, INTRANET_CHAT_FORWARD_MAX, INTRANET_CHAT_LATITUDE_E7_MAX, INTRANET_CHAT_LATITUDE_E7_MIN, INTRANET_CHAT_LONGITUDE_E7_MAX, INTRANET_CHAT_LONGITUDE_E7_MIN, INTRANET_CHAT_MESSAGE_MAX, INTRANET_CHAT_PAGE_SIZE, INTRANET_CHAT_PREFERENCE_EMPTY_MESSAGE, INTRANET_CHAT_PREVIEW_MAX, INTRANET_CHAT_SEARCH_MAX, INTRANET_CHAT_SEARCH_QUERY_MAX, INTRANET_CHAT_SEARCH_TOO_LONG_MESSAGE, INTRANET_CHAT_SEARCH_TOO_SHORT_MESSAGE, INTRANET_COMMENT_GIF_MAX_BYTES, INTRANET_COMMENT_MAX, INTRANET_CONTENT_KIND_LABELS, INTRANET_FEEDBACK_NOTE_MAX, INTRANET_FILE_ACCEPT, INTRANET_FILE_ALLOWED_MIME, INTRANET_FILE_MAX_BYTES, INTRANET_GROUP_CHAT_ALL_TEAMS_MESSAGE, INTRANET_GROUP_CHAT_MIN_MEMBERS, INTRANET_GROUP_CHAT_NAME_MAX, INTRANET_HIGHLIGHT_TIMEZONE, INTRANET_IMAGE_ACCEPT, INTRANET_IMAGE_ALLOWED_MIME, INTRANET_IMAGE_ALT_MAX, INTRANET_IMAGE_MAX_BYTES, INTRANET_IMAGE_MAX_MB, INTRANET_IMAGE_MIN_BYTES, INTRANET_KNOWLEDGE_FILE_MAX_BYTES, INTRANET_KNOWLEDGE_FREQUENT_DAYS, INTRANET_KNOWLEDGE_RAIL_LIMIT, INTRANET_LIBRARY_KINDS, INTRANET_MAX_PINNED, INTRANET_MODULE_KIND_LABELS, INTRANET_NOTICE_TEXT_MAX, INTRANET_OCCASION_WINDOW_DAYS, INTRANET_PAGE_SIZE, INTRANET_POLL_OPTION_MAX, INTRANET_POLL_OPTION_MIN, INTRANET_REACTION_EMOJI, INTRANET_REACTION_KINDS, INTRANET_REACTION_LABEL, INTRANET_REACTION_QUICK_KINDS, INTRANET_READER_PERMISSIONS, INTRANET_SEARCH_MAX_RESULTS, INTRANET_SEARCH_MIN_QUERY, INTRANET_SHORTCUT_ICONS, INTRANET_VIDEO_ACCEPT, INTRANET_VIDEO_ALLOWED_MIME, INTRANET_VIDEO_MAX_BYTES, IT_ADMIN_EXCLUDED_PERMISSIONS, ImpactLevel, IncidentSeverity, IntranetActivityKind, IntranetContentKind, IntranetLinkTone, IntranetModuleKind, IntranetModuleReportStatus, IntranetOccasionKind, IntranetQuickAccessItemType, IntranetSearchHitType, IntranetSection, IntranetWidgetKind, KB_AI_PROPOSAL_KIND_LABELS, KB_AI_PROPOSAL_SOURCE_LABELS, KB_AI_PROPOSAL_STATUS_LABELS, KB_ARTICLE_STATUS_LABELS, KB_ASSISTANT_MAX_CONTENT, KB_EMBEDDING_DIMENSIONS, KB_HYBRID_RRF_K, KB_OPTIMIZE_LOOKBACK_DAYS, KB_OPTIMIZE_MAX_PROPOSALS, KB_SEARCH_CANDIDATE_LIMIT, KB_STALE_ARTICLE_DAYS, KB_VISIBILITY_LABELS, KB_WEAK_HIT_MAX_SCORE, KbAiProposalKind, KbAiProposalSource, KbAiProposalStatus, KbArticleStatus, KbSearchMode, KbVisibility, LEAVE_ABSENCE_KIND_HINTS, LEAVE_ABSENCE_KIND_LABELS, LEAVE_AREA_CODES, LEAVE_AREA_DEFAULTS, LEAVE_AREA_DEPENDENTS, LEAVE_AREA_DESCRIPTIONS, LEAVE_AREA_GROUP, LEAVE_AREA_GROUP_LABELS, LEAVE_AREA_HIDES, LEAVE_AREA_LABELS, LEAVE_AREA_SWITCHABLE, LEAVE_CALENDAR_AWAY_FIELDS, LEAVE_EMPLOYEE_PERMISSIONS, LEAVE_REQUESTS_PATH, LEAVE_SYSTEM_ABSENCE_TYPES, LEAVE_VACATION_BALANCE_KINDS, LEGACY_COPY_PATTERNS, LEGACY_PERMISSION_ALIASES, LeaveAbsenceColor, LeaveAbsenceKindCode, LeaveAreaCode, LeaveAreaGroup, LeaveCalculationError, LeaveCalendarKindCode, LeaveDayPartCode, LeaveExportFormatCode, LeaveExportKindCode, LeaveHolidayScope, LeavePatternSource, LeaveRequestStatusCode, MAIL_CONNECTION_ID, MAIL_POLICY_ID, MAIL_PROVIDER_LABELS, MAIL_TEMPLATE_DEFAULTS, MAIL_TEMPLATE_EVENT_SAMPLE, MAIL_TEMPLATE_PLACEHOLDERS, MAIL_TEMPLATE_PLACEHOLDER_HELP, MAIL_TEMPLATE_PLACEHOLDER_SAMPLE, MAINTENANCE_CHECKLIST_SCHEMA_VERSION, MAINTENANCE_INK_COLORS, MAINTENANCE_INK_DEFAULT_COLOR, MAINTENANCE_INK_DEFAULT_WIDTH, MAINTENANCE_INK_LEGACY_PDF_COLOR, MAINTENANCE_INK_LEGACY_PDF_DOT, MAINTENANCE_INK_LEGACY_PDF_THICKNESS, MAINTENANCE_INK_MIME, MAINTENANCE_INK_SCHEMA_VERSION, MAINTENANCE_INK_TEXT_FONT_RATIO, MAINTENANCE_INK_TEXT_MAX, MAINTENANCE_INK_WIDTHS, MAINTENANCE_PROTOCOL_ATTACHMENT_ALLOWED_MIME, MAINTENANCE_PROTOCOL_ATTACHMENT_MAX_BYTES, MAINTENANCE_PROTOCOL_SIGNATURE_FIELD_KEYS, MAINTENANCE_TECHNICIAN_PERMISSIONS, MAINTENANCE_TEMPLATE_PDF_MAX_BYTES, MAINTENANCE_TEMPLATE_PDF_MAX_PAGES, MAINTENANCE_TEMPLATE_PDF_MIME, MAINTENANCE_WORK_ORDER_CLOSED_STATUSES, MAINTENANCE_WORK_ORDER_DELETE_CANCELLED, MAINTENANCE_WORK_ORDER_DELETE_COMPLETED, MAINTENANCE_WORK_ORDER_DELETE_HAS_ENTRIES, MAINTENANCE_WORK_ORDER_DELETE_IN_PROGRESS, MAINTENANCE_WORK_ORDER_OPEN_STATUSES, MAINTENANCE_WORK_ORDER_TRANSITIONS, MailConnectionRulesError, MailProvider, MailTemplatePlaceholderError, ModuleInstallationStatus, ModuleStoreState, NOTIFICATION_EVENT_DESCRIPTIONS, NOTIFICATION_EVENT_EMAIL_DEFAULT, NOTIFICATION_EVENT_KEYS, NOTIFICATION_EVENT_LABELS, NOTIFICATION_EVENT_MODULE, NOTIFICATION_EVENT_PRODUCT, NOTIFICATION_INBOX_PAGE_SIZE, NOTIFICATION_PREFERENCE_EVENT_KEYS, NOTIFICATION_PRODUCT_LABELS, NotificationEventKey, NotificationProductCode, OLLAMA_CLASSIFY_CONFIDENCE_DEFAULT, ONBOARDING_CASE_TRANSITIONS, ONBOARDING_DOCUMENT_ALLOWED_MIME, ONBOARDING_DOCUMENT_MAX_BYTES, ONBOARDING_EMPLOYEE_PERMISSIONS, ONBOARDING_TEMPLATE_EXCHANGE_FORMAT, ONBOARDING_TEMPLATE_EXCHANGE_MAX_BYTES, ONBOARDING_TEMPLATE_EXCHANGE_MAX_PHASES, ONBOARDING_TEMPLATE_EXCHANGE_MAX_TASKS, ONBOARDING_TEMPLATE_EXCHANGE_VERSION, ONBOARDING_TEMPLATE_SCHEMA_VERSION, OVERVIEW_MAX_PATHS, OnboardingCaseKind, OnboardingCaseStatus, PARTNER_NUMBER_PATTERN, PERMISSION_GROUP, PERMISSION_LABELS, PORTAL_CHAT_DRAFT_STORAGE_KEY, PRIORITY_RANK, PROBLEM_LIFECYCLE_LABELS, PROBLEM_LIFECYCLE_TRANSITIONS, PRODUCT_CODES, PRODUCT_META, PRODUCT_META_LIST, PRODUCT_REQUIRES_PLATFORM, PROFILE_PHOTO_ALLOWED_MIME, PROFILE_PHOTO_MAX_BYTES, PROFILE_PHOTO_MAX_EDGE, PermissionCode, PermissionNamespace, ProblemLifecycle, ProductCode, ProductStatus, PushPlatformHint, PushTransport, REMOTE_CONNECT_GRANT_TTL_SECONDS, REMOTE_SESSION_CONSENT_TIMEOUT_SECONDS, REMOTE_SESSION_DEFAULT_VALID_MINUTES, REMOTE_SESSION_MAX_VALID_MINUTES, REMOTE_SESSION_REPORT_MAX_SKEW_SECONDS, REMOTE_SESSION_STATUS_LABELS, RESERVED_PERMISSION_NAMESPACES, ROLE_LABELS, ROLE_PERMISSIONS, RealtimeEvent, RemoteSessionStatus, RoleCode, SITE_DIARY_ACTIVITY_LABELS, SITE_DIARY_COLLEAGUES_HINT, SITE_DIARY_DEFAULT_LABEL, SITE_DIARY_DELIVERY_LABELS, SITE_DIARY_INCIDENT_HINT, SITE_DIARY_JOB_TITLE_HINT, SITE_DIARY_JOB_TITLE_LABELS, SITE_DIARY_MAIL_FAILED_MESSAGE, SITE_DIARY_MAIL_OFF_MESSAGE, SITE_DIARY_MAIL_PREF_OFF_MESSAGE, SITE_DIARY_MAX_PHOTOS, SITE_DIARY_NOTIFY_FAILED_MESSAGE, SITE_DIARY_NOTIFY_PREF_OFF_MESSAGE, SITE_DIARY_NO_RECIPIENT_MESSAGE, SITE_DIARY_PHOTO_HINT, SITE_DIARY_TEMPERATURE_HINT, SITE_DIARY_WEATHER_LABELS, SLA_DUE_SOON_MINUTES, STAFF_ROLE_CODES_FOR_MAIL, SYSTEM_AUDIT_ACTION_LABELS, SYSTEM_TYPE_DEFINITION_IDS, SiteDiaryActivity, SiteDiaryDelivery, SiteDiaryJobTitle, SiteDiaryLogoMode, SiteDiaryWeather, SystemAuditAction, TENANT_SUSPENDED_MESSAGE, TICKET_AI_ASSIST_QUESTION_MAX, TICKET_ATTACHMENT_ALLOWED_MIME, TICKET_ATTACHMENT_MAX_BYTES, TICKET_ATTACHMENT_MAX_FILES, TICKET_AUDIT_ACTION_LABELS, TICKET_AUTO_CLOSE_DAYS, TICKET_AUTO_CLOSE_REMINDER_BEFORE_DAYS, TICKET_CATALOG_SETTINGS_ID, TICKET_CATALOG_SOURCE_LABELS, TICKET_CREATE_SUGGEST_DEBOUNCE_MS, TICKET_CREATE_SUGGEST_LIMIT, TICKET_CREATE_SUGGEST_MIN_CHARS, TICKET_EMAIL_BRAND, TICKET_LINK_KIND_LABELS, TICKET_PRIORITY_LABELS, TICKET_REOPEN_WINDOW_DAYS, TICKET_SOURCE_LABELS, TICKET_STATUS_LABELS, TICKET_STATUS_TRANSITIONS, TICKET_TYPE_LABELS, TICKET_TYPE_MODULE, TenantStatus, TicketAiDecision, TicketAuditAction, TicketCatalogKeywordDecision, TicketCatalogKeywordKind, TicketCatalogPreviewDecision, TicketCatalogSource, TicketLinkKind, TicketPriority, TicketSlaRisk, TicketSource, TicketStatus, TicketType, UNASSIGNED_QUEUE_COLUMN, URGENCY_LEVEL_LABELS, UrgencyLevel, VOICE_COPY, WEBHOOK_DIRECTION_LABELS, WEBHOOK_EVENTS, WEBHOOK_EVENT_LABELS, WEBHOOK_FIELD_TARGETS, WEBHOOK_FIELD_TARGET_LABELS, WEEKDAY_LABELS, WebhookDirection, WebhookEvent, accentHexSchema, addBerlinDays, addCalendarDays2 as addCalendarDays, addCalendarMonths, addDays, addOnboardingCalendarDays, addTeamMemberRequestSchema, addressFormSchema, admitOnboardingCaseRequestSchema, aiChatMessageSchema, aiChatRequestSchema, aiChatResponseSchema, aiChatRoleSchema, aiChatSourceSchema, aiChatTicketDraftSchema, aiConnectionProbeSchema, aiConnectionSchema, aiHardwareProfileIdSchema, aiProviderSchema, aiProviderUsesApiKey, aiStatusSchema, allowedPriorityCodes, allowedProblemLifecycleTransitions, allowedTicketTransitions, apiErrorSchema, appModuleCatalogItemSchema, appModuleCatalogSchema, appModuleCodeSchema, appModuleGroupSchema, applyFieldMappings, approvalActionPreviewSchema, approvalActionResponseSchema, approvalDecisionValueSchema, approvalLookupsSchema, approvalModeSchema, approvalStateSchema, approvalStepApproverSchema, approvalStepSchema, approvalWorkflowListResponseSchema, approvalWorkflowSchema, articleDetailSchema, articleDocumentKindSchema, articleDocumentSchema, articleGroupListItemSchema, articleGroupListSchema, articleListItemSchema, articleListQuerySchema, articleListResponseSchema, articleListSortBySchema, articleNameSchema, articlePriceListSchema, articlePurchasePriceSchema, articleRequiresUnitConversion, articleSnapshotInputSchema, articleSupplierOptionListSchema, articleSupplierOptionSchema, articleSupplierSchema, articleTaxClassSchema, articleTypeSchema, articleUnitFactorSchema, assertArticleStockManaged, assertMailConnection, assertMailTemplatePlaceholders, assessDriverCompliance, assetDateSchema, assetDetailSchema, assetListItemSchema, assetListQuerySchema, assetListResponseSchema, assetListSortBySchema, assetLookupsSchema, assetOkSchema, assetPersonSchema, assetStatusSchema, assetTicketRefSchema, assetTypeSchema, assignIntranetKnowledgeManufacturerRequestSchema, assignIntranetLeadingCategoryRequestSchema, assignTicketRequestSchema, assignUserRolesBulkRequestSchema, assignUserRolesBulkResultSchema, assignUserRolesRequestSchema, assignmentRangesOverlap, attachmentScanStatusSchema, auditActionLabel, auditEntityTypeLabel, auditListQuerySchema, auditListResponseSchema, auditLogActorSchema, auditLogItemSchema, auditLookupsSchema, authUserSchema, automationActionSchema, automationActionsSchema, automationConditionSchema, automationConditionsSchema, automationDryRunRequestSchema, automationDryRunResponseSchema, automationLookupsSchema, automationNotifyRecipientSchema, automationRuleListResponseSchema, automationRuleRunListResponseSchema, automationRuleRunSchema, automationRuleSchema, automationTagSchema, automationTriggerSchema, berlinCalendarDate, berlinCalendarYear, berlinDateTime, berlinDayBounds, berlinInclusiveDayCount, berlinMonth, berlinWeek, broadcastIncidentRequestSchema, buildAppModuleCatalog, buildArticleSnapshot, buildChangeCollisionWarnings, buildChatTicketDraft, buildCreateTicketFormSchema, buildFleetAreaCatalog, buildFleetChargeLine, buildKbArticleDiff, buildLeaveAreaCatalog, buildMasterDataSnapshot, buildModuleStoreCatalog, businessCalendarListResponseSchema, businessCalendarSchema, businessIntervalSchema, businessPartnerAddressPurposeSchema, businessPartnerDetailSchema, businessPartnerListItemSchema, businessPartnerListSchema, businessPartnerRoleSchema, calendarHolidaySchema, calendarSpanDays, canReopenTicketStatus, canTransitionMaintenanceWorkOrderStatus, canTransitionOnboardingCase, canTransitionProblemLifecycle, canTransitionTicketStatus, canUserDecideStep, cancelMaintenanceWorkOrderRequestSchema, cancelOnboardingCaseRequestSchema, cancelOnboardingPhaseTasksRequestSchema, cancelOnboardingTaskRequestSchema, canonicalOverviewPath, canonicalPermissionCode, canonicalPermissionCodes, capCarryOverDays, capacityWarnings, catalogItemFor, catalogKeywordEntrySchema, centsToEuroInput, changeBerlinDayRange, changeCalendarItemSchema, changeCalendarQuerySchema, changeCalendarResponseSchema, changeCollisionReasonSchema, changeCollisionSeveritySchema, changeCollisionWarningSchema, changeRiskSchema, changeTicketStatusRequestSchema, changeWindowOverlapsBerlinDay, chatContextPlain, checkOnboardingTemplateDependencies, claimOnboardingTaskRequestSchema, clampConfidence, classifyPreviewRequestSchema, classifyPreviewResponseSchema, cleanPersonNamePart, closeFleetAssignmentRequestSchema, collectModuleInstallPlan, colorModeSchema, combineCatalogMatchText, commitOnboardingTemplateImportRequestSchema, completeOnboardingCaseRequestSchema, completeOnboardingTaskRequestSchema, completeSetupRequestSchema, computeLeaveBalance, connectedPayloadSchema, connectorDenyRequestSchema, connectorDeviceIdSchema, connectorDeviceListResponseSchema, connectorDeviceSchema, connectorEnrollmentCodeResponseSchema, connectorHeartbeatRequestSchema, connectorLaunchRequestSchema, connectorLaunchResponseSchema, connectorLaunchSigningString, connectorPendingResponseSchema, connectorPendingSessionSchema, connectorRegisterRequestSchema, connectorRegisterResponseSchema, connectorReportCredentialsRequestSchema, connectorReportSigningString, connectorSessionClosedPayloadSchema, consumeVacation, consumedDaysTotal, costCenterListSchema, createArticleGroupRequestSchema, createArticlePurchasePriceRequestSchema, createArticleRequestSchema, createArticleSupplierRequestSchema, createArticleUnitFactorRequestSchema, createAssetFormSchema, createAssetRequestSchema, createBusinessPartnerAddressRequestSchema, createBusinessPartnerBankAccountRequestSchema, createBusinessPartnerContactRequestSchema, createBusinessPartnerRequestSchema, createBusinessPartnerRoleRequestSchema, createBusinessPartnerSettlementRequestSchema, createBusinessPartnerTaxProfileRequestSchema, createCalendarHolidayRequestSchema, createCompanyHolidayRequestSchema, createCostCenterRequestSchema, createCurrencyRequestSchema, createDepartmentRequestSchema, createFleetAssignmentRequestSchema, createFleetChargeStationRequestSchema, createFleetDamageRequestSchema, createFleetFuelCardAssignmentRequestSchema, createFleetFuelCardRequestSchema, createFleetInspectionRequestSchema, createFleetInstructionRequestSchema, createFleetLicenseCheckRequestSchema, createFleetManualTaskRequestSchema, createFleetOdometerCorrectionRequestSchema, createFleetOdometerRequestSchema, createFleetPoolBookingRequestSchema, createFleetTelematicsConnectionRequestSchema, createFleetTelematicsConnectionResponseSchema, createFleetWorkshopVisitRequestSchema, createIntranetCategoryRequestSchema, createIntranetChatMessageRequestSchema, createIntranetCommentRequestSchema, createIntranetCommentWithFileRequestSchema, createIntranetContactRequestSchema, createIntranetContentRequestSchema, createIntranetGroupChatRequestSchema, createIntranetGroupChatResponseSchema, createIntranetKnowledgeCategoryRequestSchema, createIntranetKnowledgeManufacturerRequestSchema, createIntranetLinkRequestSchema, createIntranetModuleInstanceRequestSchema, createKbArticleFormSchema, createKbArticleRequestSchema, createKbCategoryRequestSchema, createLeaveAbsenceTypeRequestSchema, createLeaveBalanceAdjustmentRequestSchema, createLeaveOvertimeRequestSchema, createLeaveTimePunchSchema, createLocalUserRequestSchema, createMaintenanceCustomerRequestSchema, createMaintenanceInstallationRequestSchema, createMaintenanceProjectRequestSchema, createMaintenanceTemplateRequestSchema, createMaintenanceTemplateScopeRequestSchema, createMaintenanceWorkOrderRequestSchema, createOnboardingCaseRequestSchema, createOnboardingTemplateFromDescriptionRequestSchema, createOnboardingTemplateRequestSchema, createPaymentMethodRequestSchema, createPaymentTermRequestSchema, createQueueRequestSchema, createRoleRequestSchema, createSiteRequestSchema, createTeamRequestSchema, createTenantRequestSchema, createTicketCategoryRequestSchema, createTicketCommentRequestSchema, createTicketFormSchema, createTicketRequestSchema, createTicketTypeDefinitionRequestSchema, createUnitConversionRequestSchema, createUnitRequestSchema, csatActionPreviewSchema, csatAnalyticsLookupsSchema, csatAnalyticsQuerySchema, csatAnalyticsSchema, csatPendingListSchema, csatRatingSchema, csatSubmitResponseSchema, csatSurveySchema, csatTicketSurveySchema, currencyListSchema, currencyRoundingModeSchema, currentOdometerKm, currentRemoteSessionResponseSchema, currentSequentialApproverId, dashboardAssetExpiringKindSchema, dashboardAssetExpiringSchema, dashboardAssetStatusCountSchema, dashboardAssetTypeCountSchema, dashboardAssetsSummarySchema, dashboardDailyPointSchema, dashboardHelpdeskKpisSchema, dashboardHelpdeskSummarySchema, dashboardOpenByPrioritySchema, dashboardSecurityLinkSchema, dashboardServerAssetSchema, dashboardSummarySchema, dashboardTopProblemSchema, dashboardTopProblemsSummarySchema, dashboardTopQueueSchema, dataInvalidatedPayloadSchema, dataInvalidationScopeSchema, dayBefore, daysToHalf, decideApprovalRequestSchema, defaultLeaveWorkPattern, dependentsOf, designVariantSchema, directorySourceSchema, directoryStatusSchema, directorySyncQueuedSchema, directorySyncRequestSchema, discountMinorUnits, displayFontSchema, displayFontSizeSchema, eachDate, editableMailEventKeys, embeddingToSqlLiteral, employeePermissionsForNamespace, emptyMaintenanceChecklistDefinition, emptyOnboardingTemplateDefinition, enabledLiveProductCodes, enabledModulesFromMap, enabledModulesSchema, endOfMonth, entitlementHalfUnits, entraIntegrationStatusSchema, entraSyncResultSchema, escalationTriggerSchema, eurosToCents, evaluateDriverOdometer, evaluateVehicleRelease, excerptFromIntranetBody, excerptPlain, expireLicenseEvidence, finalizeMaintenanceWorkOrderRequestSchema, findMaintenanceChecklistField, flattenOnboardingTemplateTasks, fleetAreaCatalogItemSchema, fleetAreaCatalogSchema, fleetAreaCodeSchema, fleetAreaDependents, fleetAreaGroupSchema, fleetAssignmentKindSchema, fleetAssignmentListSchema, fleetAssignmentSchema, fleetAuthorizeStatus, fleetChargeAmounts, fleetChargeConnectorListSchema, fleetChargeConnectorSchema, fleetChargeConnectorStateSchema, fleetChargeIdentityFromSerial, fleetChargeInterfaceSchema, fleetChargeInvoiceSchema, fleetChargeLineInputSchema, fleetChargePasswordSchema, fleetChargeStationCreatedSchema, fleetChargeStationListSchema, fleetChargeStationSchema, fleetChargeTagLinkedSchema, fleetChargeTagSchema, fleetChargeVehicleOptionSchema, fleetChargeVehicleSchema, fleetCostMonthListSchema, fleetCostMonthQuerySchema, fleetCostMonthRowSchema, fleetDamageEventListSchema, fleetDamageEventSchema, fleetDamageEventTypeSchema, fleetDamageKindSchema, fleetDamageListQuerySchema, fleetDamageListSchema, fleetDamageReportSchema, fleetDamageSeveritySchema, fleetDamageStatusSchema, fleetDeadlineSchema, fleetDepartmentListSchema, fleetDepartmentOptionSchema, fleetDocumentKindSchema, fleetDocumentListSchema, fleetDocumentSchema, fleetDriverDetailSchema, fleetDriverListItemSchema, fleetDriverListQuerySchema, fleetDriverListSchema, fleetDriverProfileSchema, fleetDriverVehicleSchema, fleetEnergyCardSchema, fleetEnergyChoiceSchema, fleetEnergyCommandLabel, fleetEnergyCommandResponseSchema, fleetEnergyCommandResultSchema, fleetEnergyFirmwareRequestSchema, fleetEnergyGroupCreatedSchema, fleetEnergyGroupSchema, fleetEnergyOverviewSchema, fleetEnergyResetRequestSchema, fleetEnergyStartRequestSchema, fleetEnergyStationSchema, fleetEnergyStations, fleetEnergyVehicleSchema, fleetFirmwareStatusLabel, fleetFuelCardAssignmentSchema, fleetFuelCardListSchema, fleetFuelCardSchema, fleetFuelCardStatusSchema, fleetFuelImportResultSchema, fleetFuelIssuerSchema, fleetFuelMatchStatusSchema, fleetFuelProductSchema, fleetFuelTransactionListQuerySchema, fleetFuelTransactionListSchema, fleetFuelTransactionSchema, fleetFuelTypeSchema, fleetFuelUnitSchema, fleetInspectionKindSchema, fleetInspectionListSchema, fleetInspectionResultSchema, fleetInspectionSchema, fleetInstructionSessionSchema, fleetLicenseCheckResultSchema, fleetLicenseCheckSchema, fleetLicenseClassSchema, fleetManualTaskResultSchema, fleetMeAssignmentsQuerySchema, fleetMeComplianceSchema, fleetOdometerListQuerySchema, fleetOdometerListSchema, fleetOdometerReadingSchema, fleetOdometerSourceSchema, fleetOperationalStatusSchema, fleetOwnershipSchema, fleetPageQuerySchema, fleetPersonListSchema, fleetPersonSchema, fleetPolicySchema, fleetSepaFileSchema, fleetServiceDueSchema, fleetSolarAdviceSchema, fleetSolarBatteryFlowSchema, fleetSolarForecastStateSchema, fleetSolarGridLabelSchema, fleetSolarSchema, fleetSolarStateSchema, fleetSummarySchema, fleetSummaryTodayRowSchema, fleetTariffChipSchema, fleetTariffSettingsSchema, fleetTaskGroupSchema, fleetTaskKindSchema, fleetTaskListQuerySchema, fleetTaskListSchema, fleetTaskMatchesGroup, fleetTaskSchema, fleetTaskStatusSchema, fleetTelematicsConnectionListSchema, fleetTelematicsConnectionSchema, fleetTelematicsEventKindSchema, fleetTelematicsIngestRequestSchema, fleetTelematicsIngestResultSchema, fleetTelematicsIssueListSchema, fleetTelematicsIssueSchema, fleetTelematicsNormalizedEventSchema, fleetTelematicsPositionListSchema, fleetTelematicsPositionSchema, fleetTelematicsRawEventSchema, fleetTelematicsTripListQuerySchema, fleetTelematicsTripListSchema, fleetTelematicsTripSchema, fleetTelematicsWaypointSchema, fleetTripPurposeSchema, fleetUsageModeSchema, fleetVehicleCategorySchema, fleetVehicleCostDetailSchema, fleetVehicleCostLineSchema, fleetVehicleCostQuerySchema, fleetVehicleDeleteBlockedDetailsSchema, fleetVehicleDetailSchema, fleetVehicleListItemSchema, fleetVehicleListQuerySchema, fleetVehicleListSchema, fleetVehicleSchema, fleetVehicleWriteSchema, fleetWorkshopKindSchema, fleetWorkshopVisitListSchema, fleetWorkshopVisitSchema, forgotPasswordRequestSchema, formatArticleNumber, formatArticlePriceMinor, formatCentAsEuro, formatFleetKw, formatMailFrom, formatPartnerNumber, formatPersonName, formatSiteDiaryRunningNumber, forwardIntranetChatRequestSchema, forwardIntranetChatResponseSchema, forwardIntranetChatTargetSchema, fromChangeDatetimeLocalValue, fromCoordinateE7, fromZonedDatetimeLocalValue, fromZonedTime, fuelProductFitsVehicle, generateLeaveHolidaysQuerySchema, generateLeaveHolidaysResultSchema, germanStateCodeSchema, getByPath, halfToDays, halloCommaSalutation, halloSalutation, healthStatusSchema, htmlFromAiMarkdown, htmlToDiffText, huDueOn, impactLevelSchema, incidentSeveritySchema, installedProductAdminGroups, integrationsOverviewSchema, intranetAbsenceNoteOwnSchema, intranetAbsenceNoteProfileSchema, intranetAbsenceNoteResponseSchema, intranetAbsenceNoteViewSchema, intranetAcknowledgementPendingItemSchema, intranetAcknowledgementReportSchema, intranetAcknowledgementSchema, intranetAcknowledgementsPath, intranetActivityEventSchema, intranetActivityKindSchema, intranetActivitySchema, intranetAiAgentListSchema, intranetAiAgentMineItemSchema, intranetAiAgentMineSchema, intranetAiAgentSchema, intranetAiAgentWriteSchema, intranetAiAskDeleteAllQuerySchema, intranetAiAskDeleteAllResponseSchema, intranetAiAskHistoryItemSchema, intranetAiAskHistoryQuerySchema, intranetAiAskHistorySchema, intranetAiAskRequestSchema, intranetAiAskResponseSchema, intranetAiAskScopeSchema, intranetAiAskSourceSchema, intranetAiAssistantSchema, intranetAiDraftRequestSchema, intranetAiDraftResponseSchema, intranetAppearanceBackgroundPath, intranetAppearanceSchema, intranetBirthdayListResponseSchema, intranetBookmarkStateSchema, intranetCategoryImagePath, intranetCategoryListResponseSchema, intranetCategoryMembersOverviewSchema, intranetCategoryPublicationSchema, intranetCategorySchema, intranetChannelIconSchema, intranetChatAttachmentKindSchema, intranetChatAttachmentPath, intranetChatAttachmentSchema, intranetChatConversationListResponseSchema, intranetChatConversationSchema, intranetChatDegreeText, intranetChatDegrees, intranetChatForwardedFromSchema, intranetChatLocationE7, intranetChatLocationPair, intranetChatMapUrl, intranetChatMessageSchema, intranetChatMessagesQuerySchema, intranetChatMessagesResponseSchema, intranetChatPersonSchema, intranetChatPhotoSchema, intranetChatPhotosResponseSchema, intranetChatPreferenceRequestSchema, intranetChatPreferenceResponseSchema, intranetChatReplySchema, intranetChatSearchHitSchema, intranetChatSearchQuerySchema, intranetChatSearchResponseSchema, intranetColleagueListResponseSchema, intranetColleaguePath, intranetColleagueProfileSchema, intranetColleagueQuerySchema, intranetColleagueSchema, intranetCommentAttachmentKindSchema, intranetCommentAttachmentPath, intranetCommentAttachmentSchema, intranetCommentGifMaxBytesSchema, intranetCommentReplySchema, intranetCommentSchema, intranetContactListResponseSchema, intranetContactSchema, intranetContentDetailSchema, intranetContentImageMaxBytesSchema, intranetContentIssues, intranetContentKindSchema, intranetContentListQuerySchema, intranetContentListResponseSchema, intranetContentPath, intranetContentRevisionListResponseSchema, intranetContentRevisionSchema, intranetContentSiteSchema, intranetContentStateSchema, intranetContentSummarySchema, intranetConversationKindSchema, intranetCoverPath, intranetCoverUploadMetaSchema, intranetDepartmentOptionSchema, intranetDepartmentOptionsResponseSchema, intranetFeedItemSchema, intranetFeedbackCountsSchema, intranetFeedbackNoteSchema, intranetFeedbackNotesResponseSchema, intranetFileDownloadPath, intranetFileListResponseSchema, intranetFilePreviewPath, intranetFilePreviewSchema, intranetFileSchema, intranetHighlightSpanSchema, intranetHighlightUntil, intranetImageMaxBytesFromMb, intranetImageMaxBytesSchema, intranetImageMaxMb, intranetImageUploadMetaSchema, intranetKnowledgeAdoptionSchema, intranetKnowledgeCategoryBannerPath, intranetKnowledgeCategoryFilterItemSchema, intranetKnowledgeCategoryFilterResponseSchema, intranetKnowledgeCategoryListResponseSchema, intranetKnowledgeCategoryPageEntrySchema, intranetKnowledgeCategoryPageQuerySchema, intranetKnowledgeCategoryPageSchema, intranetKnowledgeCategoryParentSchema, intranetKnowledgeCategoryPath, intranetKnowledgeCategorySchema, intranetKnowledgeFileMaxBytesSchema, intranetKnowledgeHomeQuerySchema, intranetKnowledgeHomeSchema, intranetKnowledgeHomeTileSchema, intranetKnowledgeLibraryKindSchema, intranetKnowledgeManufacturerAssignmentSchema, intranetKnowledgeManufacturerListResponseSchema, intranetKnowledgeManufacturerRemovalSchema, intranetKnowledgeManufacturerSchema, intranetKnowledgeQuickAccessEntrySchema, intranetKnowledgeQuickAccessListSchema, intranetKnowledgeSearchHitSchema, intranetKnowledgeSearchQuerySchema, intranetKnowledgeSearchResponseSchema, intranetKnowledgeSitesResponseSchema, intranetLeadingCategoryAssignmentSchema, intranetLibraryPath, intranetLinkListResponseSchema, intranetLinkSchema, intranetLinkToneSchema, intranetModuleInstanceListResponseSchema, intranetModuleInstanceSchema, intranetModuleInstanceWriteSchema, intranetModuleKindListResponseSchema, intranetModuleKindOptionSchema, intranetModuleKindSchema, intranetModuleLogoPath, intranetModulePath, intranetModulePhotoPath, intranetModuleReportListResponseSchema, intranetModuleReportPhotoSchema, intranetModuleReportSchema, intranetModuleReportStatusSchema, intranetNewsPath, intranetNoticeListSchema, intranetNoticeSchema, intranetOccasionKindSchema, intranetOccasionSchema, intranetOwnAcknowledgementSchema, intranetOwnAcknowledgementsResponseSchema, intranetOwnFeedbackSchema, intranetPendingAcknowledgementsResponseSchema, intranetPollOptionSchema, intranetPollResponseSchema, intranetPollSchema, intranetQuickAccessItemSchema, intranetQuickAccessItemTypeSchema, intranetQuickAccessResponseSchema, intranetReactionCountSchema, intranetReactionKindSchema, intranetReactionListItemSchema, intranetReactionListSchema, intranetReactionStateSchema, intranetSearchHitSchema, intranetSearchHitTypeSchema, intranetSearchQuerySchema, intranetSearchResponseSchema, intranetSectionSchema, intranetShortcutIconSchema, intranetViewStateSchema, intranetWidgetKindSchema, intranetWidgetListResponseSchema, intranetWidgetSchema, isArticleNumber, isAssignedEmailRequired, isAutomationActionEnabled, isAutomationConditionEnabled, isDirectoryRoleCode, isFleetAreaEnabled, isFleetAreaLocked, isForbiddenOnboardingSecretKey, isHighClassifyConfidence, isIntranetChatMessageEvent, isIntranetHtmlBody, isIntranetLibraryKind, isIntranetModuleKind, isIntranetShortcutHref, isLeaveAreaEnabled, isLeaveAreaLocked, isLinkLocalAddress, isMetadataHostname, isModuleEnabledInMap, isNotificationEventVisible, isOrgEmailEnabledForRoles, isPartnerNumber, isPreviewableAttachment, isProductEnabledInMap, isPublicHttpsUrl, isPublicIpAddress, isStaffForMailPolicy, isTicketReopenWindowOpen, isTicketTypeEnabled, isValidChangeWindow, isValidIban, isValidVatId, isVoiceCopyId, kbAiProposalKindSchema, kbAiProposalListSchema, kbAiProposalSchema, kbAiProposalSourceSchema, kbAiProposalStatusSchema, kbArticleDiffSchema, kbArticleListItemSchema, kbArticleListQuerySchema, kbArticleListResponseSchema, kbArticleSchema, kbArticleStatusSchema, kbArticleVersionListSchema, kbArticleVersionSchema, kbAssistantChatRequestSchema, kbAssistantChatResponseSchema, kbCategorySchema, kbDiffLineSchema, kbDraftFromTicketRequestSchema, kbEmbeddingText, kbHelpfulResponseSchema, kbLookupsSchema, kbOptimizationDashboardSchema, kbOptimizationRunQueuedSchema, kbOptimizationRunSchema, kbSearchGapSchema, kbSearchHitSchema, kbSearchModeSchema, kbSearchQuerySchema, kbSearchResponseSchema, kbSimilarTicketHitSchema, kbSnippet, kbStaleArticleSchema, kbSuggestQuerySchema, kbSuggestResponseSchema, kbTagSchema, kbTicketGapSchema, kbTicketLinkSchema, kbVisibilitySchema, lastAssistantInstruction, lastUserQuestion, latestInspection, ldapIntegrationStatusSchema, ldapSyncResultSchema, leaveAbsenceColorSchema, leaveAbsenceKindSchema, leaveAbsenceMarkSchema, leaveAbsenceTypeListSchema, leaveAbsenceTypeSchema, leaveAdminSetupSchema, leaveApprovalDelegateSchema, leaveApprovalDelegateWriteSchema, leaveAreaCatalogItemSchema, leaveAreaCatalogSchema, leaveAreaCodeSchema, leaveAreaDependents, leaveAreaGroupSchema, leaveAreasTurnedOffWith, leaveBalanceAdjustmentSchema, leaveBalanceProjectionQuerySchema, leaveBalanceProjectionSchema, leaveBalanceQuerySchema, leaveBalanceSchema, leaveCalendarDaySchema, leaveCalendarDepartmentListSchema, leaveCalendarDepartmentSchema, leaveCalendarEntrySchema, leaveCalendarHolidaySchema, leaveCalendarKindSchema, leaveCalendarQuerySchema, leaveCalendarResponseSchema, leaveCalendarYearAbsenceSchema, leaveCalendarYearQuerySchema, leaveCalendarYearResponseSchema, leaveCalendarYearSchoolHolidaySchema, leaveCalendarYearTotalSchema, leaveCalendarYearTotalUnitSchema, leaveCapacityWarningSchema, leaveColleagueCalendarPersonSchema, leaveColleagueCalendarResponseSchema, leaveColleagueListSchema, leaveColleagueSchema, leaveCompanyClosureCancelSchema, leaveCompanyClosurePersonSchema, leaveCompanyClosurePreviewSchema, leaveCompanyClosureResultSchema, leaveCompanyClosureWriteSchema, leaveDayPartSchema, leaveDepartmentCapacityItemSchema, leaveDepartmentCapacityListSchema, leaveExportCountSchema, leaveExportFilterSchema, leaveExportFormatSchema, leaveExportKindSchema, leaveExportOptionsSchema, leaveExportQuerySchema, leaveHolidayListQuerySchema, leaveHolidayListSchema, leaveHolidaySchema, leaveHolidayScopeSchema, leaveInboxItemSchema, leaveInboxListSchema, leaveInboxOvertimeItemSchema, leaveInboxRequestItemSchema, leaveOvertimeEntrySchema, leaveOvertimeLimitWarningSchema, leaveOvertimeStatusSchema, leavePatternSourceSchema, leavePayrollExportSchema, leavePayrollSkippedSchema, leavePersonListSchema, leavePersonSchema, leavePolicySchema, leavePunchKindSchema, leaveRequestDecideSchema, leaveRequestDetailSchema, leaveRequestEventListSchema, leaveRequestEventSchema, leaveRequestListQuerySchema, leaveRequestListSchema, leaveRequestPath, leaveRequestResponseSchema, leaveRequestSchema, leaveRequestStatusSchema, leaveRequestWarningsSchema, leaveRequestWriteSchema, leaveTimePunchSchema, leaveWorkPatternSchema, leaveWorkingTimeModeSchema, leaveWorkingTimeStatusSchema, leaveWorkingTimeWarningSchema, lineDiff, linkAssetTicketRequestSchema, linkKbTicketRequestSchema, linkMajorIncidentChildRequestSchema, linkProblemIncidentRequestSchema, lockIntranetCommentsRequestSchema, loginOkResponseSchema, loginRequestSchema, loginResponseSchema, mailConnectionProbeSchema, mailConnectionSchema, mailConnectionTestResponseSchema, mailEventEmailDefault, mailEventLabel, mailInboundModeSchema, mailIntegrationStatusSchema, mailOutboundModeSchema, mailPasswordWillBeSet, mailPlaceholdersForEvent, mailPolicySchema, mailPollResultSchema, mailProviderSchema, mailSecretIntent, mailTemplateEventSample, mailTemplatePlaceholderViewSchema, mailTemplatePlaceholderViews, mailTemplatePlaceholdersList, mailTemplateSchema, mailTemplateTestRequestSchema, mailTemplateTestResponseSchema, mailTemplatesResponseSchema, maintenanceAcroformUnsupportedWidgetSchema, maintenanceAddressSchema, maintenanceChecklistAttachmentFieldSchema, maintenanceChecklistBooleanFieldSchema, maintenanceChecklistChoiceFieldSchema, maintenanceChecklistDateFieldSchema, maintenanceChecklistDecimalFieldSchema, maintenanceChecklistDefinitionSchema, maintenanceChecklistFieldSchema, maintenanceChecklistFieldTypeSchema, maintenanceChecklistInfoFieldSchema, maintenanceChecklistIntegerFieldSchema, maintenanceChecklistLongTextFieldSchema, maintenanceChecklistMeasurementFieldSchema, maintenanceChecklistSectionSchema, maintenanceChecklistShortTextFieldSchema, maintenanceChecklistSignatureSettingsSchema, maintenanceChecklistTimeFieldSchema, maintenanceCustomerListQuerySchema, maintenanceCustomerListResponseSchema, maintenanceCustomerSchema, maintenanceCustomerSnapshotSchema, maintenanceCustomerSummarySchema, maintenanceDateSchema, maintenanceDefinitionRequiresCustomerSignature, maintenanceInkColorSchema, maintenanceInkFieldKey, maintenanceInkHexRgb, maintenanceInkIsWinAnsiChar, maintenanceInkLayerListSchema, maintenanceInkLayerSchema, maintenanceInkPdfPaint, maintenanceInkPointSchema, maintenanceInkPreviewStyle, maintenanceInkSanitizeText, maintenanceInkStrokeSchema, maintenanceInkTextSchema, maintenanceInkWidthSchema, maintenanceInstallationHistoryItemSchema, maintenanceInstallationHistorySchema, maintenanceInstallationListQuerySchema, maintenanceInstallationListResponseSchema, maintenanceInstallationSchema, maintenanceInstallationSnapshotSchema, maintenancePdfPixelsToPlacement, maintenancePdfPlacementSchema, maintenancePdfPlacementToPixels, maintenanceProjectListQuerySchema, maintenanceProjectListResponseSchema, maintenanceProjectSchema, maintenanceProjectSnapshotSchema, maintenanceProtocolAnswerSchema, maintenanceProtocolArchiveDetailSchema, maintenanceProtocolAttachmentSchema, maintenanceProtocolDetailSchema, maintenanceProtocolEditorSchema, maintenanceProtocolEventTypeSchema, maintenanceProtocolFinalizeReadiness, maintenanceProtocolIssuesMessage, maintenanceProtocolRequiresCustomerSignature, maintenanceProtocolSignatureKindSchema, maintenanceProtocolSignatureSchema, maintenanceProtocolSnapshotSchema, maintenanceProtocolSummarySchema, maintenanceProtocolValidationResultSchema, maintenanceTemplateCleanupCutoff, maintenanceTemplateCleanupDecision, maintenanceTemplateDefinitionSchema, maintenanceTemplateDeleteBlock, maintenanceTemplateDetailSchema, maintenanceTemplateKindSchema, maintenanceTemplateListQuerySchema, maintenanceTemplateListResponseSchema, maintenanceTemplateRetentionMonths, maintenanceTemplateSchema, maintenanceTemplateScopeSchema, maintenanceTemplateSourcePdfUploadResponseSchema, maintenanceTemplateVersionSchema, maintenanceTemplateVersionStatusSchema, maintenanceUserSummarySchema, maintenanceWorkOrderDeleteBlock, maintenanceWorkOrderDetailSchema, maintenanceWorkOrderListItemSchema, maintenanceWorkOrderListQuerySchema, maintenanceWorkOrderListResponseSchema, maintenanceWorkOrderPrioritySchema, maintenanceWorkOrderSchema, maintenanceWorkOrderStatusSchema, managedDepartmentListSchema, managedDepartmentSchema, managedQueueListItemSchema, managedQueueListSchema, managedQueueSchema, managedRoleListSchema, managedRoleSchema, managedSiteListSchema, managedSiteSchema, managedTeamListSchema, managedTeamSchema, mappedString, mappedTicketPriority, mappedTicketStatus, mappedTicketType, markIntranetContentReviewedRequestSchema, markNotificationsForPathRequestSchema, masterDataAsOfQuerySchema, masterDataCsvRequestSchema, masterDataDateSchema, masterDataDuplicateCandidateSchema, masterDataDuplicateDetailsSchema, masterDataExportSchema, masterDataImportErrorSchema, masterDataImportPreviewSchema, masterDataImportResultSchema, masterDataIntervalsOverlap, masterDataListQuerySchema, masterDataSnapshotInputSchema, matchApprovalWorkflow, matchCatalogKeywords, matchFuelTransaction, matchLegacyCopy, matchingHardwareProfile, mayEditMaintenanceWorkOrder, mayFinalizeMaintenanceWorkOrder, mayStartMaintenanceWorkOrder, messageResponseSchema, moduleCompleteSetupResultSchema, moduleForTicketType, moduleInstallResultSchema, moduleInstallationSnapshotSchema, moduleInstallationStatusSchema, moduleInstallationsForTenantBackfill, moduleInstallationsFromProductFlagMap, moduleRemoveResultSchema, moduleStoreCardSchema, moduleStoreCatalogSchema, moduleStoreResponseSchema, moduleStoreStateSchema, moduleUpgradeResultSchema, moduleVersionIsNewer, moduleVersionSchema, modulesTurnedOffWith, modulesUpdatedPayloadSchema, nextInstructionDue, nextLicenseCheckDue, normalizeCommaPersonName, normalizeNotificationPath, normalizePlate, normalizeTelematicsBatch, normalizeVin, notApplicableOnboardingTaskRequestSchema, notificationActionResponseSchema, notificationChannelSchema, notificationDeleteAllResponseSchema, notificationEventKeySchema, notificationListQuerySchema, notificationListResponseSchema, notificationMatchesVisitedPath, notificationNewPayloadSchema, notificationPreferenceSchema, notificationPreferencesResponseSchema, notificationProductCodeSchema, notificationSchema, notificationStandardResponseSchema, okResponseSchema, onboardingAccountKindSchema, onboardingAssigneeKindSchema, onboardingAssigneeOptionsQuerySchema, onboardingAssigneeOptionsResponseSchema, onboardingAssigneeUserSchema, onboardingCancelCodeSchema, onboardingCaseDetailSchema, onboardingCaseEventSchema, onboardingCaseKindSchema, onboardingCaseListQuerySchema, onboardingCaseListResponseSchema, onboardingCaseRevisionBodySchema, onboardingCaseSchema, onboardingCaseStatusSchema, onboardingCaseTaskSchema, onboardingDataFieldSchema, onboardingDataFieldTypeSchema, onboardingDateSchema, onboardingDocumentListSchema, onboardingDocumentScanStatusSchema, onboardingDocumentSchema, onboardingDueAnchorSchema, onboardingPhaseStageSchema, onboardingSystemActionTargetSchema, onboardingSystemActionTargetsSchema, onboardingTaskKindSchema, onboardingTaskListItemSchema, onboardingTaskListQuerySchema, onboardingTaskListResponseSchema, onboardingTaskMutationResponseSchema, onboardingTaskStatusSchema, onboardingTemplateDefinitionSchema, onboardingTemplateDetailSchema, onboardingTemplateExchangeAssigneeRefSchema, onboardingTemplateExchangeDataFieldSchema, onboardingTemplateExchangeSchema, onboardingTemplateImportAssignmentSchema, onboardingTemplateImportPreviewPhaseSchema, onboardingTemplateImportPreviewReferenceSchema, onboardingTemplateImportPreviewSchema, onboardingTemplateImportPreviewTaskSchema, onboardingTemplateImportReferenceStatusSchema, onboardingTemplateListQuerySchema, onboardingTemplateListResponseSchema, onboardingTemplatePhaseSchema, onboardingTemplateRemoveBlockedDetailsSchema, onboardingTemplateSchema, onboardingTemplateTaskSchema, onboardingTemplateVersionSchema, onboardingTemplateVersionStatusSchema, openIntranetChatRequestSchema, openIntranetChatResponseSchema, operationalStatusAfterInspection, orgListQuerySchema, orgSlugSchema, orgUnitSchema, overviewLayoutSchema, overviewPathAllowed, ownProfileOptionSchema, ownProfileOptionsSchema, ownProfileSchema, paginatedSchema, paginationQuerySchema, parseAffectedServices, parseAiChatModelJson, parseBerlinDate, parseFleetAreas, parseFuelAmountToCents, parseFuelCsv, parseKbAssistantModelJson, parseKbDraftModelJson, parseLeaveAreas, parseOnboardingTemplateExchange, parseProblemRca, parseTagInput, parseTelematicsCsv, parseTelematicsDateTime, parseTicketAiAssistModelJson, parseTicketClassifyModelJson, parseTicketNumberInput, passwordSchema, patchArticleGroupRequestSchema, patchArticleRequestSchema, patchArticleSupplierRequestSchema, patchBusinessPartnerAddressRequestSchema, patchBusinessPartnerBankAccountRequestSchema, patchBusinessPartnerContactRequestSchema, patchBusinessPartnerRequestSchema, patchBusinessPartnerRoleRequestSchema, patchBusinessPartnerSettlementRequestSchema, patchBusinessPartnerTaxProfileRequestSchema, patchCostCenterRequestSchema, patchCurrencyRequestSchema, patchFleetAreasRequestSchema, patchFleetAreasResponseSchema, patchLeaveAreasRequestSchema, patchLeaveAreasResponseSchema, patchModulesRequestSchema, patchModulesResponseSchema, patchOnboardingCaseDatesRequestSchema, patchPaymentMethodRequestSchema, patchPaymentTermRequestSchema, patchUnitConversionRequestSchema, patchUnitRequestSchema, patternOn, paymentDueOn, paymentMethodCodeSchema, paymentMethodListSchema, paymentTermBaselineSchema, paymentTermDiscountSchema, paymentTermListSchema, permissionCodeSchema, permissionForTicketStatusChange, permissionNamespace, personDisplayName, personInitials, personNameOrderSchema, personNamePartsSchema, personnelNumberSchema, pinIntranetContentRequestSchema, plainTextFromHtml, platformSatisfies, presentOnDate, presentVoiceText, previewOnboardingTemplateImportRequestSchema, priorityFromImpactUrgency, problemLifecycleSchema, problemRcaSchema, productAdminLinkSchema, productCodeSchema, productEntry, productSettingItemSchema, productSettingsResponseSchema, productStatusSchema, profilePhotoUrlForUser, profilePhotoVersion, publicBrandSchema, publishIntranetContentRequestSchema, pushActionResponseSchema, pushDeviceListResponseSchema, pushPlatformHintSchema, pushSubscribeRequestSchema, pushSubscriptionSchema, pushTransportSchema, pushUnsubscribeCurrentRequestSchema, pushVapidPublicKeyResponseSchema, putFleetChargeTagRequestSchema, putIntranetModuleDraftRequestSchema, queueBoardColumnSchema, queueBoardSchema, queueBoardTicketSchema, reassignOnboardingTaskRequestSchema, reciprocalRankFusion, redactAssistPublicReplyLeak, reduceUnitFactor, remoteConnectLaunchResponseSchema, remoteConnectProtocolUrl, remoteSessionListResponseSchema, remoteSessionSchema, remoteSessionStatusSchema, remoteSessionUpdatedPayloadSchema, removeIntranetKnowledgeCategoryRequestSchema, removeIntranetKnowledgeManufacturerRequestSchema, renderNotificationText, renderVoiceCopy, reopenOnboardingCaseRequestSchema, reopenOnboardingTaskRequestSchema, reorderIntranetModulePhotosRequestSchema, reorderQueuesRequestSchema, replaceIntranetAiAgentsRequestSchema, replaceIntranetKnowledgeQuickAccessRequestSchema, replaceOnboardingTemplateDraftFromDescriptionRequestSchema, resetPasswordRequestSchema, resolveDirectoryPerson, resolveFleetAreaMap, resolveFleetFuelTransactionRequestSchema, resolveIncidentPriority, resolveLeaveAreaMap, resolveSessionProducts, roleAssignmentCeiling, roleAssignmentCodeSchema, roleAssignmentSchema, roleCodeSchema, roleMemberSchema, roleMembersSchema, roleOptionListSchema, roleOptionSchema, rolePermissionCeiling, roleSeededAtStartup, rotateFleetChargePasswordRequestSchema, rotateFleetTelematicsTokenResponseSchema, roundHalfUpMinor, runOnboardingSystemActionRequestSchema, rustdeskConnectUri, rustdeskIdSchema, rustdeskPasswordSchema, saveFleetEnergyGroupRequestSchema, saveFleetTariffChipSchema, saveFleetTariffSettingsSchema, saveIntranetAbsenceNoteRequestSchema, serializeProblemRca, serviceDueStatus, sessionProductsSchema, setFleetConnectorLimitRequestSchema, setFleetEnergyDepartureRequestSchema, setFleetTripPurposeRequestSchema, setIntranetReactionRequestSchema, setLocalPasswordRequestSchema, setPersonnelNumberSchema, setupCompanyRequestSchema, setupCompanyResponseSchema, setupCompleteResponseSchema, setupStatusResponseSchema, sharedAffectedServices, sharedAssetIds, shouldOpenChangePanel, siteDiaryActivitySchema, siteDiaryDeliveryMessage, siteDiaryDeliveryResultSchema, siteDiaryDeliverySchema, siteDiaryDraftSchema, siteDiaryJobTitleSchema, siteDiaryLogoModeSchema, siteDiaryReportSchema, siteDiarySettingsSchema, siteDiarySettingsViewSchema, siteDiaryStoredSettingsSchema, siteDiaryWeatherSchema, skipOnboardingTaskRequestSchema, slaEscalationSchema, slaLookupsSchema, slaPolicyListResponseSchema, slaPolicySchema, slugifyKbTitle, solarWebCompareUrl, solarWebPublicToken, ssoStatusResponseSchema, staffUserListSchema, staffUserSchema, startOnboardingCaseRequestSchema, stepOutcome, submitCsatRequestSchema, submitIntranetFeedbackRequestSchema, submitIntranetModuleReportRequestSchema, submitIntranetModuleReportResponseSchema, submitIntranetNoticeRequestSchema, suggestNextDueOn, suggestOrgSlug, suggestTenantSlug, summarizeTicketHistoryItem, teamLeadSummarySchema, teamMemberSchema, teamMembersResponseSchema, teamSummarySchema, templateDraftBody, tenantOperatorListSchema, tenantOperatorSchema, tenantSlugSchema, tenantStatusSchema, ticketAiAssistRequestSchema, ticketAiAssistResponseSchema, ticketAiAssistSimilarTicketSchema, ticketAiDecisionSchema, ticketAiFieldSchema, ticketAiStateSchema, ticketAiSuggestionActionSchema, ticketAiSuggestionSchema, ticketApprovalDecisionSchema, ticketApprovalSchema, ticketApprovalStepSchema, ticketAttachmentSchema, ticketAttachmentsResponseSchema, ticketAutoCloseAt, ticketAutoCloseReminderAt, ticketCatalogAdminResponseSchema, ticketCatalogKeywordDecisionSchema, ticketCatalogKeywordKindSchema, ticketCatalogKeywordListSchema, ticketCatalogPreviewDecisionSchema, ticketCatalogSettingsSchema, ticketCatalogSlugSchema, ticketCatalogSourceSchema, ticketCategoryAdminSchema, ticketCategoryNodeSchema, ticketCategoryRefSchema, ticketChangeAssetSchema, ticketChangeStateSchema, ticketCommentFormSchema, ticketCommentSchema, ticketCommentsResponseSchema, ticketCreateSuggestQuery, ticketDetailSchema, ticketHistoryItemSchema, ticketHistoryResponseSchema, ticketIncidentRefSchema, ticketIncidentStateSchema, ticketLifecycleRefSchema, ticketLifecycleSchema, ticketLinkKindSchema, ticketListItemSchema, ticketListQuerySchema, ticketListResponseSchema, ticketListSortBySchema, ticketLookupsSchema, ticketMailSubjectTag, ticketNumberFromPath, ticketNumberFromSubject, ticketPersonSchema, ticketPrioritySchema, ticketProblemKbProposalSchema, ticketProblemStateSchema, ticketQueueRefSchema, ticketReopenUntil, ticketSlaRiskSchema, ticketSlaSchema, ticketSourceSchema, ticketStatusActionLabel, ticketStatusSchema, ticketTypeDefinitionSchema, ticketTypeSchema, toAssetRequest, toChangeDatetimeLocalValue, toCoordinateE7, toSiteDiaryStoredSettings, toZonedDatetimeLocalValue, totpCodeSchema, totpEnableRequestSchema, totpRequiredResponseSchema, totpSetupRequiredResponseSchema, transitionsForActor, typographyFromUnknown, typographySchema, unclaimOnboardingTaskRequestSchema, unitConversionListSchema, unitDimensionSchema, unitListSchema, updateAiConnectionRequestSchema, updateAssetRequestSchema, updateBusinessCalendarRequestSchema, updateChangeRequestSchema, updateDepartmentRequestSchema, updateFleetChargeStationRequestSchema, updateFleetDamageStatusRequestSchema, updateFleetDriverProfileRequestSchema, updateFleetFuelCardRequestSchema, updateFleetPolicyRequestSchema, updateFleetPoolBookingRequestSchema, updateFleetTariffChipSchema, updateFleetVehicleRequestSchema, updateIncidentRequestSchema, updateIntranetAppearanceRequestSchema, updateIntranetCategoryRequestSchema, updateIntranetContactRequestSchema, updateIntranetContentRequestSchema, updateIntranetKnowledgeCategoryRequestSchema, updateIntranetKnowledgeManufacturerRequestSchema, updateIntranetLinkRequestSchema, updateIntranetModuleInstanceRequestSchema, updateIntranetWidgetRequestSchema, updateKbArticleRequestSchema, updateKbCategoryRequestSchema, updateLdapIntegrationRequestSchema, updateLeaveAbsenceTypeRequestSchema, updateLeavePolicyRequestSchema, updateMailConnectionRequestSchema, updateMailIntegrationRequestSchema, updateMailPolicyRequestSchema, updateMailTemplateRequestSchema, updateMaintenanceCustomerRequestSchema, updateMaintenanceInstallationRequestSchema, updateMaintenanceProjectRequestSchema, updateMaintenanceProtocolNotesRequestSchema, updateMaintenanceProtocolNotesResponseSchema, updateMaintenanceTemplateRequestSchema, updateMaintenanceTemplateVersionRequestSchema, updateMaintenanceWorkOrderRequestSchema, updateNotificationPreferencesRequestSchema, updateNotificationStandardRequestSchema, updateOnboardingTemplateRequestSchema, updateOverviewLayoutSchema, updateOwnBirthdayRequestSchema, updateOwnProfileRequestSchema, updateProblemRequestSchema, updateProductSettingRequestSchema, updateQueueRequestSchema, updateRoleRequestSchema, updateSiteRequestSchema, updateTeamRequestSchema, updateTenantAppearanceRequestSchema, updateTenantRequestSchema, updateTicketCatalogSettingsRequestSchema, updateTicketCategoryRequestSchema, updateTicketRequestSchema, updateTicketTypeDefinitionRequestSchema, updateTypographyRequestSchema, updateUserRequestSchema, updateVoiceRequestSchema, upsertApprovalStepRequestSchema, upsertApprovalWorkflowRequestSchema, upsertAutomationRuleRequestSchema, upsertIntranetPollRequestSchema, upsertLeaveDepartmentCapacityRequestSchema, upsertLeaveWorkPatternRequestSchema, upsertMaintenanceInkLayerRequestSchema, upsertMaintenanceInkLayerResponseSchema, upsertMaintenanceProtocolAnswerRequestSchema, upsertMaintenanceProtocolAnswerResponseSchema, upsertSlaEscalationRequestSchema, upsertSlaPolicyRequestSchema, upsertWebhookEndpointRequestSchema, urgencyLevelSchema, userCanAccessProduct, userDetailSchema, userListItemSchema, userListQuerySchema, userListResponseSchema, userListSortBySchema, userListStatusSchema, userLookupsQuerySchema, userLookupsSchema, userManagerSummarySchema, userQualifiesForProduct, userSalutationName, uvvDueOn, vacationDaysRestoredBySick, vacationsOverlap, validateIntranetGroupChatLicenses, validateMaintenanceChecklistDefinitionForPublish, validateMaintenanceChecklistFieldValue, validateMaintenanceProtocolAnswerUpsert, validateMaintenanceProtocolCompleteness, validateMaintenanceProtocolForFinalize, validateOnboardingTemplateDefinitionForPublish, voiceSchema, voteIntranetPollRequestSchema, webhookDirectionSchema, webhookEndpointCreatedSchema, webhookEndpointSchema, webhookEndpointSecretsSchema, webhookEventSchema, webhookFieldMappingSchema, webhookFieldTargetSchema, webhookFieldTransformSchema, weeklyHoursSchema, windowOverlapsRange, windowOverlapsZonedDay, windowsOverlap, zonedDateTimeParts, zonedDayRange };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map